import * as i0 from '@angular/core';
import { InjectionToken, signal, Injectable, Optional, Inject, computed, inject, DestroyRef, Injector, effect, untracked, LOCALE_ID, NgModule } from '@angular/core';
import * as ML from 'maplibre-gl';
import ML__default, { MapWheelEvent, MapMouseEvent, LngLat, Marker as Marker$2 } from 'maplibre-gl';
import { AuthService, AUTH_SERVICE_CONFIG_TOKEN, LocalStorageService, LocalStorageKey } from '@garmin-avcloud/avcloud-web-utils';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { getUnixTime, fromUnixTime, isEqual as isEqual$1, isDate, isValid } from 'date-fns';
import { ReplaySubject, BehaviorSubject, Subject, from, catchError, of, merge, switchMap, EMPTY, filter as filter$1, mergeMap, toArray, tap, map, lastValueFrom, mergeAll } from 'rxjs';
import area from '@turf/area';
import bbox from '@turf/bbox';
import bboxPolygon from '@turf/bbox-polygon';
import bearing from '@turf/bearing';
import centerOfMass from '@turf/center-of-mass';
import convex from '@turf/convex';
import destination from '@turf/destination';
import distance from '@turf/distance';
import greatCircle from '@turf/great-circle';
import { featureCollection, lineString, point } from '@turf/helpers';
import transformScale from '@turf/transform-scale';
import { DecimalPipe } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { lngLatToGoogle, googleToBBox } from 'global-mercator';
import Point from '@mapbox/point-geometry';
import * as twgl from 'twgl.js';
const AVC_MAPS_DISPLAY_CONFIG = new InjectionToken('AVC_MAPS_DISPLAY_CONFIG');
let OptionalAuthService = /*#__PURE__*/(() => {
  class OptionalAuthService {
    constructor(injector, authServiceConfig) {
      this._loggedIn = signal(false);
      this.loggedIn = this._loggedIn.asReadonly();
      if (authServiceConfig != null) {
        injector.get(AuthService).isAuthenticated().pipe(takeUntilDestroyed()).subscribe({
          next: value => this._loggedIn.set(value),
          error: error => console.error(error)
        });
      }
    }
    static {
      this.ɵfac = function OptionalAuthService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OptionalAuthService)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(AUTH_SERVICE_CONFIG_TOKEN, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OptionalAuthService,
        factory: OptionalAuthService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OptionalAuthService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var RadarType = /*#__PURE__*/function (RadarType) {
  RadarType["Base"] = "Base";
  RadarType["Composite"] = "Composite";
  return RadarType;
}(RadarType || {});
var MapAnimationStatus = /*#__PURE__*/function (MapAnimationStatus) {
  /**
   * The data is not applicable and the status should be ignored.
   */
  MapAnimationStatus[MapAnimationStatus["NotApplicable"] = 0] = "NotApplicable";
  /**
   * The data is either available and loaded or there's some reason to ignore
   * an invalid status. E.g., a user who doesn't have a subscription that includes
   * icing will get back OK rather than OutOfRange.
   */
  MapAnimationStatus[MapAnimationStatus["OK"] = 1] = "OK";
  /**
   * The data is in range but is being loaded.
   */
  MapAnimationStatus[MapAnimationStatus["Loading"] = 2] = "Loading";
  /**
   * The data is out of range, either due to time or altitude constraints.
   */
  MapAnimationStatus[MapAnimationStatus["OutOfRange"] = 3] = "OutOfRange";
  return MapAnimationStatus;
}(MapAnimationStatus || {});
const isWithinRange = (time, range) => {
  for (const [start, end] of range) {
    if (start <= time && time <= end) {
      return true;
    }
  }
  return false;
};
const createApplicableSignal = (availableSignal, applicableFunc) => {
  return computed(() => {
    const available = availableSignal();
    const applicable = applicableFunc();
    return available && applicable;
  });
};
const createAvailableSignal = (time, availableRange) => {
  return computed(() => {
    const t = time();
    const r = availableRange();
    return isWithinRange(t, r);
  });
};
const createLoadedSignal = (time, loadedRange) => {
  return computed(() => {
    const t = time();
    const r = loadedRange();
    return isWithinRange(t, r);
  });
};
const createStatusSignal = (applicableSignal, availableSignal, loadedSignal) => {
  return computed(() => {
    const applicable = applicableSignal();
    const available = availableSignal();
    const loaded = loadedSignal();
    let result = MapAnimationStatus.NotApplicable;
    if (applicable) {
      if (!available) {
        result = MapAnimationStatus.OutOfRange;
      } else if (!loaded) {
        result = MapAnimationStatus.Loading;
      } else {
        result = MapAnimationStatus.OK;
      }
    }
    return result;
  });
};
var MapInteractions = /*#__PURE__*/function (MapInteractions) {
  MapInteractions[MapInteractions["None"] = 0] = "None";
  MapInteractions[MapInteractions["RadialMenu"] = 1] = "RadialMenu";
  MapInteractions[MapInteractions["Click"] = 2] = "Click";
  MapInteractions[MapInteractions["ClickDebug"] = 3] = "ClickDebug"; // same as click but opens debug window instead of populating search signals
  return MapInteractions;
}(MapInteractions || {});
const cloneArray = value => {
  const result = [];
  for (const x of value) {
    result.push(cloneDeep(x));
  }
  return result;
};
const cloneObjectProperties = (target, source) => {
  for (const [k, v] of Object.entries(source)) {
    target[k] = cloneDeep(v);
  }
  return target;
};
const cloneObject = value => {
  if (value === null) {
    return null;
  }
  if (Array.isArray(value)) {
    return cloneArray(value);
  }
  const tag = getObjectTag(value);
  if (tag === '[object Object]') {
    let result;
    if (value.constructor === Object.prototype.constructor) {
      result = {};
    } else {
      result = Object.create(Object.getPrototypeOf(value));
    }
    cloneObjectProperties(result, value);
    return result;
  }
  return {};
};
const getObjectTag = value => {
  const hasOwn = Object.prototype.hasOwnProperty.call(value, Symbol.toStringTag);
  const tag = value[Symbol.toStringTag];
  // Overwrite Symbol.toStringTag temporarily to access object tag
  let unmasked = false;
  try {
    // set to undefined
    value[Symbol.toStringTag] = undefined;
    unmasked = true;
  } catch (e) {
    // nothing to do
  }
  const result = Object.prototype.toString.call(value);
  if (unmasked) {
    if (hasOwn) {
      // put the Symbol.toStringTag back
      value[Symbol.toStringTag] = tag;
    } else {
      // if Symbol.toStringTag didn't exist before, delete it now
      delete value[Symbol.toStringTag];
    }
  }
  return result;
};
/**
 * Tries to create a 1:1 deep clone of the given item. This does
 * have some limitations and may have unintended behavior.
 */
const cloneDeep = value => {
  switch (typeof value) {
    case 'string':
    case 'number':
    case 'bigint':
    case 'boolean':
    case 'undefined':
      // these types can't be meaningfully cloned
      return value;
    case 'object':
      return cloneObject(value);
    case 'function':
    case 'symbol':
      throw new Error(`unsupported value type: ${typeof value}`);
    default:
      throw new Error(`unknown value type: ${typeof value}`);
  }
};
class ChartsConfigHandler {
  constructor() {
    this.groups = signal([]);
    this.visible = signal(false);
  }
  save() {
    return {
      groups: cloneDeep(this.groups()),
      visible: this.visible()
    };
  }
  restore(state) {
    if (state?.groups != null) {
      this.groups.set(cloneDeep(state.groups));
    }
    if (state?.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
const MIN_OPACITY = 0.0;
const MAX_OPACITY = 1.0;
function isValidOpacity(opacity) {
  // Assume opacity as ML.ExpressionSpecification is properly defined.
  if (Array.isArray(opacity)) {
    return true;
  }
  return opacity >= MIN_OPACITY && opacity <= MAX_OPACITY;
}
function trySetLayerOpacity(map, layerId, opacity) {
  if (!isValidOpacity(opacity)) {
    throw new Error('Opacity is not valid.');
  }
  if (map != null && layerId != null) {
    map.setLayerOpacity(layerId, opacity);
  }
}
class AeronauticalConfigHandler {
  constructor() {
    this.airport = {
      present: signal(false),
      visible: signal(false),
      runways: signal(false),
      heliportsVisible: signal(false),
      airportsToIgnore: signal([])
    };
    this.airspace = {
      present: signal(false),
      visible: signal(false),
      atcVisible: signal(false),
      suaVisible: signal(false),
      ctrlVisible: signal(false)
    };
    this.airway = {
      followSetting: signal(true),
      highPresent: signal(false),
      highVisible: signal(false),
      lowPresent: signal(false),
      lowVisible: signal(false)
    };
    this.navaid = {
      present: signal(false),
      visible: signal(false),
      ndb: signal(false),
      vor: signal(false),
      wpt: signal(false)
    };
    this.opacity = signal(MAX_OPACITY);
    this.visible = signal(false);
  }
  save() {
    return {
      airport: {
        present: this.airport.present(),
        visible: this.airport.visible(),
        airportsToIgnore: this.airport.airportsToIgnore(),
        runways: this.airport.runways(),
        heliportsVisible: this.airport.heliportsVisible()
      },
      airspace: {
        present: this.airspace.present(),
        visible: this.airspace.visible(),
        atcVisible: this.airspace.atcVisible(),
        suaVisible: this.airspace.suaVisible(),
        ctrlVisible: this.airspace.ctrlVisible()
      },
      airway: {
        followSetting: this.airway.followSetting(),
        highPresent: this.airway.highPresent(),
        highVisible: this.airway.highVisible(),
        lowPresent: this.airway.lowPresent(),
        lowVisible: this.airway.lowVisible()
      },
      navaid: {
        present: this.navaid.present(),
        visible: this.navaid.visible(),
        ndb: this.navaid.ndb(),
        vor: this.navaid.vor(),
        wpt: this.navaid.wpt()
      },
      opacity: this.opacity(),
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.airport != null) {
      this.restoreAirport(state.airport);
    }
    if (state.airspace != null) {
      this.restoreAirspace(state.airspace);
    }
    if (state.airway != null) {
      this.restoreAirway(state.airway);
    }
    if (state.navaid != null) {
      this.restoreNavaid(state.navaid);
    }
    if (state.opacity != null) {
      this.opacity.set(state.opacity);
    }
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
  restoreAirport(state) {
    if (state.present != null) {
      this.airport.present.set(state.present);
    }
    if (state.visible != null) {
      this.airport.visible.set(state.visible);
    }
    if (state.runways != null) {
      this.airport.runways.set(state.runways);
    }
    if (state.heliportsVisible != null) {
      this.airport.heliportsVisible.set(state.heliportsVisible);
    }
    if (state.airportsToIgnore != null) {
      this.airport.airportsToIgnore.set(state.airportsToIgnore);
    }
  }
  restoreAirspace(state) {
    if (state.present != null) {
      this.airspace.present.set(state.present);
    }
    if (state.visible != null) {
      this.airspace.visible.set(state.visible);
    }
    if (state.atcVisible != null) {
      this.airspace.atcVisible.set(state.atcVisible);
    }
    if (state.suaVisible != null) {
      this.airspace.suaVisible.set(state.suaVisible);
    }
    if (state.ctrlVisible != null) {
      this.airspace.ctrlVisible.set(state.ctrlVisible);
    }
  }
  restoreAirway(state) {
    if (state.followSetting != null) {
      this.airway.followSetting.set(state.followSetting);
    }
    if (state.highPresent != null) {
      this.airway.highPresent.set(state.highPresent);
    }
    if (state.highVisible != null) {
      this.airway.highVisible.set(state.highVisible);
    }
    if (state.lowPresent != null) {
      this.airway.lowPresent.set(state.lowPresent);
    }
    if (state.lowVisible != null) {
      this.airway.lowVisible.set(state.lowVisible);
    }
  }
  restoreNavaid(state) {
    if (state.present != null) {
      this.navaid.present.set(state.present);
    }
    if (state.visible != null) {
      this.navaid.visible.set(state.visible);
    }
    if (state.ndb != null) {
      this.navaid.ndb.set(state.ndb);
    }
    if (state.vor != null) {
      this.navaid.vor.set(state.vor);
    }
    if (state.wpt != null) {
      this.navaid.wpt.set(state.wpt);
    }
  }
}
class BackgroundConfigHandler {
  constructor() {
    this.available = signal(true).asReadonly();
    this.visible = signal(false);
  }
  save() {
    return {
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class BaseMapConfigHandler {
  constructor() {
    this.visible = signal(false);
    this.cityNames = signal(false);
    this.smallCityMinZoom = signal(0);
    this.smallTownMinZoom = signal(0);
    this.countryBorders = signal(false);
    this.landUse = signal(false);
    this.parks = signal(false);
    this.railTracks = signal(false);
    this.roads = signal(false);
    this.stateBorders = signal(false);
    this.waterBodies = signal(false);
    this.waterWays = signal(false);
  }
  save() {
    return {
      visible: this.visible(),
      cityNames: this.cityNames(),
      smallCityMinZoom: this.smallCityMinZoom(),
      smallTownMinZoom: this.smallTownMinZoom(),
      countryBorders: this.countryBorders(),
      landUse: this.landUse(),
      parks: this.parks(),
      railTracks: this.railTracks(),
      roads: this.roads(),
      stateBorders: this.stateBorders(),
      waterBodies: this.waterBodies(),
      waterWays: this.waterWays()
    };
  }
  restore(state) {
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
    if (state?.cityNames != null) {
      this.cityNames.set(state.cityNames);
    }
    if (state?.smallCityMinZoom != null) {
      this.smallCityMinZoom.set(state.smallCityMinZoom);
    }
    if (state?.smallTownMinZoom != null) {
      this.smallTownMinZoom.set(state.smallTownMinZoom);
    }
    if (state?.countryBorders != null) {
      this.countryBorders.set(state.countryBorders);
    }
    if (state?.landUse != null) {
      this.landUse.set(state.landUse);
    }
    if (state?.parks != null) {
      this.parks.set(state.parks);
    }
    if (state?.railTracks != null) {
      this.railTracks.set(state.railTracks);
    }
    if (state?.roads != null) {
      this.roads.set(state.roads);
    }
    if (state?.stateBorders != null) {
      this.stateBorders.set(state.stateBorders);
    }
    if (state?.waterBodies != null) {
      this.waterBodies.set(state.waterBodies);
    }
    if (state?.waterWays != null) {
      this.waterWays.set(state.waterWays);
    }
  }
}
class GroundSatelliteConfigHandler {
  constructor() {
    this.visible = signal(false);
  }
  save() {
    return {
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class HillshadeConfigHandler {
  constructor() {
    this.visible = signal(false);
  }
  save() {
    return {
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class OpenStreetMapConfigHandler {
  constructor() {
    this.visible = signal(false);
  }
  save() {
    return {
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class SafeTaxiConfigHandler {
  constructor() {
    this.visible = signal(false);
  }
  save() {
    return {
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class TopographyConfigHandler {
  constructor() {
    this.visible = signal(false);
  }
  save() {
    return {
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class ContentConfigHandler {
  constructor() {
    this.aeronautical = new AeronauticalConfigHandler();
    this.background = new BackgroundConfigHandler();
    this.baseMap = new BaseMapConfigHandler();
    this.groundSatellite = new GroundSatelliteConfigHandler();
    this.hillshade = new HillshadeConfigHandler();
    this.openStreetMap = new OpenStreetMapConfigHandler();
    this.safeTaxi = new SafeTaxiConfigHandler();
    this.topography = new TopographyConfigHandler();
  }
  save() {
    return {
      aeronautical: this.aeronautical.save(),
      background: this.background.save(),
      baseMap: this.baseMap.save(),
      groundSatellite: this.groundSatellite.save(),
      hillshade: this.hillshade.save(),
      openStreetMap: this.openStreetMap.save(),
      safeTaxi: this.safeTaxi.save(),
      topography: this.topography.save()
    };
  }
  restore(state) {
    if (state.aeronautical != null) {
      this.aeronautical.restore(state.aeronautical);
    }
    if (state.background != null) {
      this.background.restore(state.background);
    }
    if (state.baseMap != null) {
      this.baseMap.restore(state.baseMap);
    }
    if (state.groundSatellite != null) {
      this.groundSatellite.restore(state.groundSatellite);
    }
    if (state.hillshade != null) {
      this.hillshade.restore(state.hillshade);
    }
    if (state.openStreetMap != null) {
      this.openStreetMap.restore(state.openStreetMap);
    }
    if (state.safeTaxi != null) {
      this.safeTaxi.restore(state.safeTaxi);
    }
    if (state.topography != null) {
      this.topography.restore(state.topography);
    }
  }
}
class LocationHighlightConfigHandler {
  constructor() {
    this.visible = signal(false);
  }
  save() {
    return {
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class HighlightConfigHandler {
  constructor() {
    this.location = new LocationHighlightConfigHandler();
  }
  save() {
    return {
      location: this.location.save()
    };
  }
  restore(state) {
    if (state.location != null) {
      this.location.restore(state.location);
    }
  }
}
class AirmetSigmetConfigHandler {
  constructor() {
    this.opacity = signal(MAX_OPACITY);
    this.options = {
      icing: signal(false),
      ifrMountain: signal(false),
      convective: signal(false),
      turbulence: signal(false),
      lowLevelWindShearAndSurfaceWinds: signal(false),
      volcanicAsh: signal(false),
      tropical: signal(false)
    };
    this.visible = signal(false);
  }
  save() {
    return {
      opacity: this.opacity(),
      options: {
        icing: this.options.icing(),
        ifrMountain: this.options.ifrMountain(),
        convective: this.options.convective(),
        turbulence: this.options.turbulence(),
        lowLevelWindShearAndSurfaceWinds: this.options.lowLevelWindShearAndSurfaceWinds(),
        volcanicAsh: this.options.volcanicAsh(),
        tropical: this.options.tropical()
      },
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.opacity != null) {
      this.opacity.set(state.opacity);
    }
    if (state.options != null) {
      if (state.options.icing != null) {
        this.options.icing.set(state.options.icing);
      }
      if (state.options.ifrMountain != null) {
        this.options.ifrMountain.set(state.options.ifrMountain);
      }
      if (state.options.convective != null) {
        this.options.convective.set(state.options.convective);
      }
      if (state.options.turbulence != null) {
        this.options.turbulence.set(state.options.turbulence);
      }
      if (state.options.lowLevelWindShearAndSurfaceWinds != null) {
        this.options.lowLevelWindShearAndSurfaceWinds.set(state.options.lowLevelWindShearAndSurfaceWinds);
      }
      if (state.options.volcanicAsh != null) {
        this.options.volcanicAsh.set(state.options.volcanicAsh);
      }
      if (state.options.tropical != null) {
        this.options.tropical.set(state.options.tropical);
      }
    }
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
var FuelPriceType = /*#__PURE__*/function (FuelPriceType) {
  FuelPriceType["Jet"] = "Jet";
  FuelPriceType["LowLead"] = "100LL";
  return FuelPriceType;
}(FuelPriceType || {});
class FuelPricesConfigHandler {
  constructor() {
    this.type = signal(FuelPriceType.LowLead);
    this.visible = signal(false);
  }
  save() {
    return {
      type: this.type(),
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.type != null) {
      this.type.set(state.type);
    }
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
var IcingType = /*#__PURE__*/function (IcingType) {
  IcingType["Severity"] = "Severity+SLD";
  IcingType["SeverityProb25"] = "Severity+SLD (>25% PRB)";
  IcingType["SeverityProb50"] = "Severity+SLD (>50% PRB)";
  IcingType["Probability"] = "Probability";
  return IcingType;
}(IcingType || {});
class IcingConfigHandler {
  constructor() {
    this.opacity = signal(MAX_OPACITY);
    this.type = signal(IcingType.Severity);
    this.visible = signal(false);
  }
  save() {
    return {
      opacity: this.opacity(),
      type: this.type(),
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.opacity != null) {
      this.opacity.set(state.opacity);
    }
    if (state.type != null) {
      this.type.set(state.type);
    }
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class InternetTrafficConfigHandler {
  constructor() {
    this.visible = signal(false);
    this.breadcrumbsVisible = signal(true);
  }
  save() {
    return {
      visible: this.visible(),
      breadcrumbsVisible: this.breadcrumbsVisible()
    };
  }
  restore(state) {
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
var MetarType = /*#__PURE__*/function (MetarType) {
  MetarType["FlightConditions"] = "Flight Conditions";
  return MetarType;
}(MetarType || {});
class MetarConfigHandler {
  constructor() {
    this.type = signal(MetarType.FlightConditions);
    this.visible = signal(false);
  }
  save() {
    return {
      type: this.type(),
      visible: this.visible()
    };
  }
  restore(state) {
    if (state?.type != null) {
      this.type.set(state.type);
    }
    if (state?.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class NotamConfigHandler {
  constructor() {
    this.opacity = signal(MAX_OPACITY);
    this.showNavaidNotams = signal(true);
    this.showObstacleNotams = signal(true);
    this.showTerminalNotams = signal(true);
    this.showUasNotams = signal(true);
    this.upcomingVisibilityHours = signal(48); // 2 days
    this.visible = signal(false);
  }
  save() {
    return {
      opacity: this.opacity(),
      showNavaidNotams: this.showNavaidNotams(),
      showObstacleNotams: this.showObstacleNotams(),
      showTerminalNotams: this.showTerminalNotams(),
      showUasNotams: this.showUasNotams(),
      upcomingVisibilityHours: this.upcomingVisibilityHours(),
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.opacity != null) {
      this.opacity.set(state.opacity);
    }
    if (state.showNavaidNotams != null) {
      this.showNavaidNotams.set(state.showNavaidNotams);
    }
    if (state.showObstacleNotams != null) {
      this.showObstacleNotams.set(state.showObstacleNotams);
    }
    if (state.showTerminalNotams != null) {
      this.showTerminalNotams.set(state.showTerminalNotams);
    }
    if (state.showUasNotams != null) {
      this.showUasNotams.set(state.showUasNotams);
    }
    if (state.upcomingVisibilityHours != null) {
      this.upcomingVisibilityHours.set(state.upcomingVisibilityHours);
    }
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class ObstacleConfigHandler {
  constructor() {
    this.opacity = signal(MAX_OPACITY);
    this.visible = signal(false);
  }
  save() {
    return {
      opacity: this.opacity(),
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.opacity != null) {
      this.opacity.set(state.opacity);
    }
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class PirepConfigHandler {
  constructor() {
    this.visible = signal(false);
  }
  save() {
    return {
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class RadarConfigHandler {
  constructor() {
    this.infraredClouds = signal(false);
    this.lightning = signal(false);
    this.opacity = signal(MAX_OPACITY);
    this.stormCells = signal(false);
    this.type = signal(RadarType.Composite);
    this.visible = signal(false);
  }
  save() {
    return {
      infraredClouds: this.infraredClouds(),
      lightning: this.lightning(),
      opacity: this.opacity(),
      stormCells: this.stormCells(),
      type: this.type(),
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.infraredClouds != null) {
      this.infraredClouds.set(state.infraredClouds);
    }
    if (state.lightning != null) {
      this.lightning.set(state.lightning);
    }
    if (state.opacity != null) {
      this.opacity.set(state.opacity);
    }
    if (state.stormCells != null) {
      this.stormCells.set(state.stormCells);
    }
    if (state.type != null) {
      this.type.set(state.type);
    }
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class TfrConfigHandler {
  constructor() {
    this.opacity = signal(MAX_OPACITY);
    this.visible = signal(false);
  }
  save() {
    return {
      opacity: this.opacity(),
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.opacity != null) {
      this.opacity.set(state.opacity);
    }
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
const DEFAULT_TILED_TEMPERATURE_OPACITY = 0.6;
class TiledTemperatureConfigHandler {
  constructor() {
    this.opacity = signal(DEFAULT_TILED_TEMPERATURE_OPACITY);
    this.visible = signal(false);
  }
  save() {
    return {
      opacity: this.opacity(),
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.opacity != null) {
      this.opacity.set(state.opacity);
    }
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class WindAnimationConfigHandler {
  constructor() {
    this.opacity = signal(MAX_OPACITY);
    this.visible = signal(false);
  }
  save() {
    return {
      opacity: this.opacity(),
      visible: this.visible()
    };
  }
  restore(state) {
    if (state.opacity != null) {
      this.opacity.set(state.opacity);
    }
    if (state.visible != null) {
      this.visible.set(state.visible);
    }
  }
}
class OverlayConfigHandler {
  constructor() {
    this.airmetSigmet = new AirmetSigmetConfigHandler();
    this.fuelPrices = new FuelPricesConfigHandler();
    this.icing = new IcingConfigHandler();
    this.internetTraffic = new InternetTrafficConfigHandler();
    this.metar = new MetarConfigHandler();
    this.notam = new NotamConfigHandler();
    this.obstacle = new ObstacleConfigHandler();
    this.pirep = new PirepConfigHandler();
    this.radar = new RadarConfigHandler();
    this.tfr = new TfrConfigHandler();
    this.tiledTemperature = new TiledTemperatureConfigHandler();
    this.windAnimation = new WindAnimationConfigHandler();
    this.altitude = signal(12500);
  }
  disableAll() {
    const overlays = {
      airmetSigmet: {
        visible: false
      },
      fuelPrices: {
        visible: false
      },
      icing: {
        visible: false
      },
      internetTraffic: {
        visible: false
      },
      metar: {
        visible: false
      },
      notam: {
        visible: false
      },
      obstacle: {
        visible: false
      },
      pirep: {
        visible: false
      },
      radar: {
        visible: false
      },
      tfr: {
        visible: false
      },
      tiledTemperature: {
        visible: false
      },
      windAnimation: {
        visible: false
      }
    };
    this.restore(overlays);
  }
  save() {
    return {
      airmetSigmet: this.airmetSigmet.save(),
      fuelPrices: this.fuelPrices.save(),
      icing: this.icing.save(),
      internetTraffic: this.internetTraffic.save(),
      metar: this.metar.save(),
      notam: this.notam.save(),
      obstacle: this.obstacle.save(),
      pirep: this.pirep.save(),
      radar: this.radar.save(),
      tfr: this.tfr.save(),
      tiledTemperature: this.tiledTemperature.save(),
      windAnimation: this.windAnimation.save(),
      altitude: this.altitude()
    };
  }
  restore(state) {
    if (state.airmetSigmet != null) {
      this.airmetSigmet.restore(state.airmetSigmet);
    }
    if (state.fuelPrices != null) {
      this.fuelPrices.restore(state.fuelPrices);
    }
    if (state.icing != null) {
      this.icing.restore(state.icing);
    }
    if (state.internetTraffic != null) {
      this.internetTraffic.restore(state.internetTraffic);
    }
    if (state.metar != null) {
      this.metar.restore(state.metar);
    }
    if (state.notam != null) {
      this.notam.restore(state.notam);
    }
    if (state.obstacle != null) {
      this.obstacle.restore(state.obstacle);
    }
    if (state.pirep != null) {
      this.pirep.restore(state.pirep);
    }
    if (state.radar != null) {
      this.radar.restore(state.radar);
    }
    if (state.tfr != null) {
      this.tfr.restore(state.tfr);
    }
    if (state.tiledTemperature != null) {
      this.tiledTemperature.restore(state.tiledTemperature);
    }
    if (state.windAnimation != null) {
      this.windAnimation.restore(state.windAnimation);
    }
    if (state.altitude != null) {
      this.altitude.set(state.altitude);
    }
  }
}
let MapConfigHandler = /*#__PURE__*/(() => {
  class MapConfigHandler {
    constructor() {
      this.charts = new ChartsConfigHandler();
      this.content = new ContentConfigHandler();
      this.overlay = new OverlayConfigHandler();
      this.highlight = new HighlightConfigHandler();
      this.interactions = signal(MapInteractions.None);
    }
    save() {
      return {
        charts: this.charts.save(),
        content: this.content.save(),
        overlay: this.overlay.save(),
        highlight: this.highlight.save(),
        interactions: this.interactions()
      };
    }
    restore(state) {
      if (state.charts != null) {
        this.charts.restore(state.charts);
      }
      if (state.content != null) {
        this.content.restore(state.content);
      }
      if (state.overlay != null) {
        this.overlay.restore(state.overlay);
      }
      if (state.highlight != null) {
        this.highlight.restore(state.highlight);
      }
      if (state.interactions != null) {
        this.interactions.set(state.interactions);
      }
    }
    static {
      this.ɵfac = function MapConfigHandler_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapConfigHandler)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapConfigHandler,
        factory: MapConfigHandler.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapConfigHandler;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class ChartsInfo {
  constructor() {
    // Internal setter signals
    this.setGroups = signal([]);
    // External getter signals
    this.groups = this.setGroups.asReadonly();
  }
}
class AeronauticalInfo {
  constructor(airportInfo, airspaceInfo, airwayInfo, navaidInfo) {
    this.airportInfo = airportInfo;
    this.airspaceInfo = airspaceInfo;
    this.airwayInfo = airwayInfo;
    this.navaidInfo = navaidInfo;
    this.available = computed(() => this.airportInfo.available() && this.airspaceInfo.available() && this.airwayInfo.available() && this.navaidInfo.available());
  }
}
class AirportInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class AirspaceInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class AirwayInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class BaseMapInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class GroundSatelliteInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class HillshadeInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class NavaidInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
var ContentFormat = /*#__PURE__*/function (ContentFormat) {
  ContentFormat[ContentFormat["None"] = 0] = "None";
  ContentFormat[ContentFormat["Image"] = 1] = "Image";
  ContentFormat[ContentFormat["MVT"] = 2] = "MVT";
  return ContentFormat;
}(ContentFormat || {});
const WorldBounds = {
  latMin: -90,
  lonMin: -180,
  latMax: 90,
  lonMax: 180
};
const EmptyContent = {
  type: '',
  domain: '',
  urlBase: '',
  bounds: WorldBounds,
  format: ContentFormat.None
};
class OpenStreetMapInfo {
  constructor() {
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = signal([{
      type: 'OpenStreetMap',
      domain: 'GLOBAL',
      urlBase: 'https://tile.openstreetmap.org',
      bounds: WorldBounds,
      format: ContentFormat.Image
    }]).asReadonly();
  }
}
class SafeTaxiInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class TopographyInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class ContentInfo {
  constructor() {
    this.airport = new AirportInfo();
    this.airspace = new AirspaceInfo();
    this.airway = new AirwayInfo();
    this.navaid = new NavaidInfo();
    this.baseMap = new BaseMapInfo();
    this.groundSatellite = new GroundSatelliteInfo();
    this.hillshade = new HillshadeInfo();
    this.openStreetMap = new OpenStreetMapInfo();
    this.safeTaxi = new SafeTaxiInfo();
    this.topography = new TopographyInfo();
    this.aeronautical = new AeronauticalInfo(this.airport, this.airspace, this.airway, this.navaid);
  }
}
class AirmetSigmetInfo {
  constructor() {
    // Internal setter signals
    this.setAirmetContent = signal([]);
    this.setGraphicalAirmetContent = signal([]);
    this.setSigmetContent = signal([]);
    // External getter signals
    this.airmetAvailable = computed(() => this.airmetContent().length > 0);
    this.airmetContent = this.setAirmetContent.asReadonly();
    this.graphicalAirmetAvailable = computed(() => this.graphicalAirmetContent().length > 0);
    this.graphicalAirmetContent = this.setGraphicalAirmetContent.asReadonly();
    this.sigmetAvailable = computed(() => this.sigmetContent().length > 0);
    this.sigmetContent = this.setSigmetContent.asReadonly();
  }
}
class CloudsInfo {
  constructor() {
    // Internal setter signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class FuelPricesInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class IcingInfo {
  constructor() {
    // Internal setter signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class InternetTrafficInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class LightningInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class MetarInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
var NotamClass$1 = /*#__PURE__*/function (NotamClass) {
  NotamClass[NotamClass["RUNWAY"] = 0] = "RUNWAY";
  NotamClass[NotamClass["TAXIWAY"] = 1] = "TAXIWAY";
  NotamClass[NotamClass["RAMP"] = 2] = "RAMP";
  NotamClass[NotamClass["APRON"] = 3] = "APRON";
  NotamClass[NotamClass["AERODROME"] = 4] = "AERODROME";
  NotamClass[NotamClass["OBSTACLE"] = 5] = "OBSTACLE";
  NotamClass[NotamClass["NAVIGATION"] = 6] = "NAVIGATION";
  NotamClass[NotamClass["COMMUNICATION"] = 7] = "COMMUNICATION";
  NotamClass[NotamClass["SERVICE"] = 8] = "SERVICE";
  NotamClass[NotamClass["AIRSPACE"] = 9] = "AIRSPACE";
  NotamClass[NotamClass["FDC"] = 10] = "FDC";
  NotamClass[NotamClass["OBSTACLE_DEPARTURE_PROCEDURE_ODP"] = 11] = "OBSTACLE_DEPARTURE_PROCEDURE_ODP";
  NotamClass[NotamClass["STANDARD_INSTRUMENT_PROCEDURE_SID"] = 12] = "STANDARD_INSTRUMENT_PROCEDURE_SID";
  NotamClass[NotamClass["STANDARD_TERMINAL_ARRIVAL_STAR"] = 13] = "STANDARD_TERMINAL_ARRIVAL_STAR";
  NotamClass[NotamClass["CHART"] = 14] = "CHART";
  NotamClass[NotamClass["DATA"] = 15] = "DATA";
  NotamClass[NotamClass["INSTRUMENT_APPROACH_PROCEDURE_IAP"] = 16] = "INSTRUMENT_APPROACH_PROCEDURE_IAP";
  NotamClass[NotamClass["VISUAL_FLIGHT_PROCEDURE_VFP"] = 17] = "VISUAL_FLIGHT_PROCEDURE_VFP";
  NotamClass[NotamClass["ROUTE"] = 18] = "ROUTE";
  NotamClass[NotamClass["SPECIAL"] = 19] = "SPECIAL";
  NotamClass[NotamClass["MILITARY"] = 20] = "MILITARY";
  NotamClass[NotamClass["INTERNATIONAL"] = 21] = "INTERNATIONAL";
  NotamClass[NotamClass["OTHER_AERONAUTICAL_INFO"] = 22] = "OTHER_AERONAUTICAL_INFO";
  NotamClass[NotamClass["UNKNOWN"] = 23] = "UNKNOWN";
  return NotamClass;
}(NotamClass$1 || {});
var NotamStatus$1 = /*#__PURE__*/function (NotamStatus) {
  NotamStatus[NotamStatus["NO_SPECIFIC_STATUS"] = 0] = "NO_SPECIFIC_STATUS";
  NotamStatus[NotamStatus["ACTIVATED"] = 1] = "ACTIVATED";
  NotamStatus[NotamStatus["DEACTIVATED"] = 2] = "DEACTIVATED";
  NotamStatus[NotamStatus["CLOSED"] = 3] = "CLOSED";
  NotamStatus[NotamStatus["UNUSABLE"] = 4] = "UNUSABLE";
  NotamStatus[NotamStatus["NOT_AVAILABLE"] = 5] = "NOT_AVAILABLE";
  NotamStatus[NotamStatus["UNSERVICEABLE"] = 6] = "UNSERVICEABLE";
  NotamStatus[NotamStatus["CHANGED"] = 7] = "CHANGED";
  NotamStatus[NotamStatus["ERECTED"] = 8] = "ERECTED";
  NotamStatus[NotamStatus["WITHDRAWN"] = 9] = "WITHDRAWN";
  return NotamStatus;
}(NotamStatus$1 || {});
class NotamInfo {
  constructor() {
    this.mapConfig = inject(MapConfigHandler);
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
  // eslint-disable-next-line complexity
  isMapDisplayable(notam) {
    const now = getUnixTime(new Date());
    const showNavaidNotams = this.mapConfig.overlay.notam.showNavaidNotams();
    const showObstacleNotams = this.mapConfig.overlay.notam.showObstacleNotams();
    const showTerminalNotams = this.mapConfig.overlay.notam.showTerminalNotams();
    const showUasNotams = this.mapConfig.overlay.notam.showUasNotams();
    const upcomingVisibilitySeconds = this.mapConfig.overlay.notam.upcomingVisibilityHours() * 3600;
    // only show graphical NOTAMs
    if (!notam.isGraphical) {
      return false;
    }
    // only show non-expired NOTAMs
    if (now >= notam.expireTime) {
      return false;
    }
    // only show upcoming NOTAMs within the configured time
    if (now < notam.effectiveTime - upcomingVisibilitySeconds) {
      return false;
    }
    switch (notam.notamClass) {
      // symbol layers
      case NotamClass$1.AERODROME:
        if (!showTerminalNotams) {
          return false;
        }
        break;
      case NotamClass$1.NAVIGATION:
        if (!showNavaidNotams) {
          return false;
        }
        break;
      case NotamClass$1.OBSTACLE:
        if (!showObstacleNotams) {
          return false;
        }
        break;
      // polygon layers
      case NotamClass$1.AIRSPACE:
        switch (notam.notamSubClass) {
          case 6:
            // 'UAS Operating Area'
            if (!showUasNotams) {
              return false;
            }
            break;
          default:
            break;
        }
        break;
      case NotamClass$1.APRON:
      case NotamClass$1.RAMP:
      case NotamClass$1.RUNWAY:
      case NotamClass$1.TAXIWAY:
        if (!showTerminalNotams) {
          return false;
        }
        if (notam.notamStatus !== NotamStatus$1.CLOSED) {
          return false;
        }
        break;
      case NotamClass$1.FDC:
      case NotamClass$1.OTHER_AERONAUTICAL_INFO:
      case NotamClass$1.UNKNOWN:
        break;
      default:
        return false;
    }
    return true;
  }
}

/**
 * These enumerations are based on the zoom levels for Garmin Pilot. The
 * enumeration names may not be accurate with regards to MapLibre. See
 * https://confluence.garmin.com/display/AVSERVICES/Data+Definitions
 */
var ZoomLevel = /*#__PURE__*/function (ZoomLevel) {
  ZoomLevel[ZoomLevel["All"] = 0] = "All";
  ZoomLevel[ZoomLevel["500NM"] = 1] = "500NM";
  ZoomLevel[ZoomLevel["300NM"] = 2] = "300NM";
  ZoomLevel[ZoomLevel["200NM"] = 3] = "200NM";
  ZoomLevel[ZoomLevel["100NM"] = 4] = "100NM";
  ZoomLevel[ZoomLevel["50NM"] = 5] = "50NM";
  ZoomLevel[ZoomLevel["20NM"] = 6] = "20NM";
  ZoomLevel[ZoomLevel["10NM"] = 7] = "10NM";
  ZoomLevel[ZoomLevel["5NM"] = 8] = "5NM";
  ZoomLevel[ZoomLevel["2NM"] = 9] = "2NM";
  ZoomLevel[ZoomLevel["1NM"] = 10] = "1NM";
  ZoomLevel[ZoomLevel["0.5NM"] = 11] = "0.5NM";
  ZoomLevel[ZoomLevel["0.2NM"] = 12] = "0.2NM";
  ZoomLevel[ZoomLevel["0.1NM"] = 13] = "0.1NM";
  ZoomLevel[ZoomLevel["500FT"] = 14] = "500FT";
  ZoomLevel[ZoomLevel["300FT"] = 15] = "300FT";
  ZoomLevel[ZoomLevel["200FT"] = 16] = "200FT";
  ZoomLevel[ZoomLevel["100FT"] = 17] = "100FT";
  ZoomLevel[ZoomLevel["50FT"] = 18] = "50FT";
  ZoomLevel[ZoomLevel["Off"] = 99] = "Off";
  return ZoomLevel;
}(ZoomLevel || {});
// Converts ZoomLevel enums to nautical miles and feet
const ZoomLevelTable = new Map([[ZoomLevel.All, [0, 'ERROR']], [ZoomLevel['500NM'], [500.0, 'NM']], [ZoomLevel['300NM'], [300.0, 'NM']], [ZoomLevel['200NM'], [200.0, 'NM']], [ZoomLevel['100NM'], [100.0, 'NM']], [ZoomLevel['50NM'], [50.0, 'NM']], [ZoomLevel['20NM'], [20.0, 'NM']], [ZoomLevel['10NM'], [10.0, 'NM']], [ZoomLevel['5NM'], [5.0, 'NM']], [ZoomLevel['2NM'], [2.0, 'NM']], [ZoomLevel['1NM'], [1.0, 'NM']], [ZoomLevel['0.5NM'], [0.5, 'NM']], [ZoomLevel['0.2NM'], [0.2, 'NM']], [ZoomLevel['0.1NM'], [0.1, 'NM']], [ZoomLevel['500FT'], [500.0, 'FT']], [ZoomLevel['300FT'], [300.0, 'FT']], [ZoomLevel['200FT'], [200.0, 'FT']], [ZoomLevel['100FT'], [100.0, 'FT']], [ZoomLevel['50FT'], [50.0, 'FT']], [ZoomLevel.Off, [0, 'ERROR']]]);
var ZoomLevelKM = /*#__PURE__*/function (ZoomLevelKM) {
  ZoomLevelKM[ZoomLevelKM["All"] = 0] = "All";
  ZoomLevelKM[ZoomLevelKM["1000KM"] = 1] = "1000KM";
  ZoomLevelKM[ZoomLevelKM["600KM"] = 2] = "600KM";
  ZoomLevelKM[ZoomLevelKM["400KM"] = 3] = "400KM";
  ZoomLevelKM[ZoomLevelKM["200KM"] = 4] = "200KM";
  ZoomLevelKM[ZoomLevelKM["100KM"] = 5] = "100KM";
  ZoomLevelKM[ZoomLevelKM["40KM"] = 6] = "40KM";
  ZoomLevelKM[ZoomLevelKM["20KM"] = 7] = "20KM";
  ZoomLevelKM[ZoomLevelKM["10KM"] = 8] = "10KM";
  ZoomLevelKM[ZoomLevelKM["4KM"] = 9] = "4KM";
  ZoomLevelKM[ZoomLevelKM["2KM"] = 10] = "2KM";
  ZoomLevelKM[ZoomLevelKM["1KM"] = 11] = "1KM";
  ZoomLevelKM[ZoomLevelKM["0.5KM"] = 12] = "0.5KM";
  ZoomLevelKM[ZoomLevelKM["200M"] = 13] = "200M";
  ZoomLevelKM[ZoomLevelKM["150M"] = 14] = "150M";
  ZoomLevelKM[ZoomLevelKM["90M"] = 15] = "90M";
  ZoomLevelKM[ZoomLevelKM["60M"] = 16] = "60M";
  ZoomLevelKM[ZoomLevelKM["30M"] = 17] = "30M";
  ZoomLevelKM[ZoomLevelKM["15M"] = 18] = "15M";
  ZoomLevelKM[ZoomLevelKM["Off"] = 99] = "Off";
  return ZoomLevelKM;
}(ZoomLevelKM || {});
// Convert ZoomLevelKM to kilometers and meters
const ZoomLevelTableKM = new Map([[ZoomLevelKM.All, [0, 'ERROR']], [ZoomLevelKM['1000KM'], [1000.0, 'KM']], [ZoomLevelKM['600KM'], [600.0, 'KM']], [ZoomLevelKM['400KM'], [400.0, 'KM']], [ZoomLevelKM['200KM'], [200.0, 'KM']], [ZoomLevelKM['100KM'], [100.0, 'KM']], [ZoomLevelKM['40KM'], [40.0, 'KM']], [ZoomLevelKM['20KM'], [20.0, 'KM']], [ZoomLevelKM['10KM'], [10.0, 'KM']], [ZoomLevelKM['4KM'], [4.0, 'KM']], [ZoomLevelKM['2KM'], [2.0, 'KM']], [ZoomLevelKM['1KM'], [1.0, 'KM']], [ZoomLevelKM['0.5KM'], [0.5, 'KM']], [ZoomLevelKM['200M'], [200.0, 'M']], [ZoomLevelKM['150M'], [150.0, 'M']], [ZoomLevelKM['90M'], [90.0, 'M']], [ZoomLevelKM['60M'], [60.0, 'M']], [ZoomLevelKM['30M'], [30.0, 'M']], [ZoomLevelKM['15M'], [15.0, 'M']], [ZoomLevelKM.Off, [0, 'ERROR']]]);
var ZoomLevelSM = /*#__PURE__*/function (ZoomLevelSM) {
  ZoomLevelSM[ZoomLevelSM["All"] = 0] = "All";
  ZoomLevelSM[ZoomLevelSM["500SM"] = 1] = "500SM";
  ZoomLevelSM[ZoomLevelSM["300SM"] = 2] = "300SM";
  ZoomLevelSM[ZoomLevelSM["200SM"] = 3] = "200SM";
  ZoomLevelSM[ZoomLevelSM["100SM"] = 4] = "100SM";
  ZoomLevelSM[ZoomLevelSM["50SM"] = 5] = "50SM";
  ZoomLevelSM[ZoomLevelSM["20SM"] = 6] = "20SM";
  ZoomLevelSM[ZoomLevelSM["10SM"] = 7] = "10SM";
  ZoomLevelSM[ZoomLevelSM["5SM"] = 8] = "5SM";
  ZoomLevelSM[ZoomLevelSM["2SM"] = 9] = "2SM";
  ZoomLevelSM[ZoomLevelSM["1SM"] = 10] = "1SM";
  ZoomLevelSM[ZoomLevelSM["0.5SM"] = 11] = "0.5SM";
  ZoomLevelSM[ZoomLevelSM["0.2SM"] = 12] = "0.2SM";
  ZoomLevelSM[ZoomLevelSM["0.1SM"] = 13] = "0.1SM";
  ZoomLevelSM[ZoomLevelSM["500FT"] = 14] = "500FT";
  ZoomLevelSM[ZoomLevelSM["300FT"] = 15] = "300FT";
  ZoomLevelSM[ZoomLevelSM["200FT"] = 16] = "200FT";
  ZoomLevelSM[ZoomLevelSM["100FT"] = 17] = "100FT";
  ZoomLevelSM[ZoomLevelSM["50FT"] = 18] = "50FT";
  ZoomLevelSM[ZoomLevelSM["Off"] = 99] = "Off";
  return ZoomLevelSM;
}(ZoomLevelSM || {});
// Convert ZoomLevelSM to statute miles and feet
const ZoomLevelTableSM = new Map([[ZoomLevelSM.All, [0, 'ERROR']], [ZoomLevelSM['500SM'], [500.0, 'SM']], [ZoomLevelSM['300SM'], [300.0, 'SM']], [ZoomLevelSM['200SM'], [200.0, 'SM']], [ZoomLevelSM['100SM'], [100.0, 'SM']], [ZoomLevelSM['50SM'], [50.0, 'SM']], [ZoomLevelSM['20SM'], [20.0, 'SM']], [ZoomLevelSM['10SM'], [10.0, 'SM']], [ZoomLevelSM['5SM'], [5.0, 'SM']], [ZoomLevelSM['2SM'], [2.0, 'SM']], [ZoomLevelSM['1SM'], [1.0, 'SM']], [ZoomLevelSM['0.5SM'], [0.5, 'SM']], [ZoomLevelSM['0.2SM'], [0.2, 'SM']], [ZoomLevelSM['0.1SM'], [0.1, 'SM']], [ZoomLevelSM['500FT'], [500.0, 'FT']], [ZoomLevelSM['300FT'], [300.0, 'FT']], [ZoomLevelSM['200FT'], [200.0, 'FT']], [ZoomLevelSM['100FT'], [100.0, 'FT']], [ZoomLevelSM['50FT'], [50.0, 'FT']], [ZoomLevelSM.Off, [0, 'ERROR']]]);
class ObstacleInfo {
  constructor() {
    // Internal setter signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
    this.zoomMax = signal(ZoomLevel['200FT']).asReadonly();
    this.zoomMin = signal(ZoomLevel['2NM']).asReadonly();
  }
}
class PirepInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class RadarInfo {
  constructor() {
    // Internal setter signals
    this.setBaseContent = signal([]);
    this.setCompositeContent = signal([]);
    // External getter signals
    this.baseAvailable = computed(() => this.baseContent().length > 0);
    this.baseContent = this.setBaseContent.asReadonly();
    this.compositeAvailable = computed(() => this.compositeContent().length > 0);
    this.compositeContent = this.setCompositeContent.asReadonly();
  }
}
class StormCellsInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class TfrInfo {
  constructor() {
    // Internal setting signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class TiledTemperatureInfo {
  constructor() {
    // Internal setter signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class WindAnimationInfo {
  constructor() {
    // Internal setter signals
    this.setContent = signal([]);
    // External getter signals
    this.available = computed(() => this.content().length > 0);
    this.content = this.setContent.asReadonly();
  }
}
class OverlayInfo {
  constructor() {
    this.airmetSigmet = new AirmetSigmetInfo();
    this.clouds = new CloudsInfo();
    this.fuelPrices = new FuelPricesInfo();
    this.icing = new IcingInfo();
    this.internetTraffic = new InternetTrafficInfo();
    this.lightning = new LightningInfo();
    this.metar = new MetarInfo();
    this.notam = new NotamInfo();
    this.obstacle = new ObstacleInfo();
    this.pirep = new PirepInfo();
    this.radar = new RadarInfo();
    this.stormCells = new StormCellsInfo();
    this.tfr = new TfrInfo();
    this.tiledTemperature = new TiledTemperatureInfo();
    this.windAnimation = new WindAnimationInfo();
  }
}
const isMapFlight = x => {
  return x?.id != null && x.points != null && Array.isArray(x.points);
};
const isMapPoint = x => {
  return x?.latitude != null && x.longitude != null;
};
const isMapRoute = x => {
  return x?.waypoints != null && Array.isArray(x.waypoints);
};
/**
 * Modifies the MapRoute properties according to the hierarchy.
 * For example, if a waypoint doesn't specify a color, the color is taken
 * from the route.
*/
const propagateMapRouteProperties = route => {
  for (const waypoint of route.waypoints) {
    // propagate color
    if (waypoint.color == null && route.color != null) {
      waypoint.color = route.color;
    }
    if (waypoint.previousLegColor == null && route.color != null) {
      waypoint.previousLegColor = route.color;
    }
  }
};
const isMapProcedure = x => {
  return x?.transitions != null && Array.isArray(x.transitions);
};
/**
 * Modifies the MapProcedure properties according to the hierarchy.
 * For example, if a transition doesn't specify a color, the color is taken
 * from the procedure.
*/
const propagateMapProcedureProperties = procedure => {
  for (const transition of procedure.transitions) {
    // propagate color
    if (transition.color == null && procedure.color != null) {
      transition.color = procedure.color;
    }
    propagateMapRouteProperties(transition);
  }
};
const isMapWaypoint = x => {
  return x?.type != null && x.latitude != null && x.longitude != null;
};
let turf = /*#__PURE__*/(() => {
  class turf {
    static {
      this.area = area;
    }
    static {
      this.bbox = bbox;
    }
    static {
      this.bboxPolygon = bboxPolygon;
    }
    static {
      this.bearing = bearing;
    }
    static {
      this.centerOfMass = centerOfMass;
    }
    static {
      this.convex = convex;
    }
    static {
      this.destination = destination;
    }
    static {
      this.distance = distance;
    }
    static {
      this.featureCollection = featureCollection;
    }
    static {
      this.greatCircle = greatCircle;
    }
    static {
      this.line = lineString;
    }
    static {
      this.point = point;
    }
    static {
      this.transformScale = transformScale;
    }
  }
  return turf;
})();
/**
 * Get the bounding box of a map element
 * @param elements The list of map elements
 * @returns A bounding box containing the list of elements
 */
const getBounds = elements => {
  const result = new ML__default.LngLatBounds();
  for (const element of elements) {
    if (isMapFlight(element)) {
      result.extend(getBounds(element.points));
    } else if (isMapProcedure(element)) {
      result.extend(getBounds(element.transitions));
    } else if (isMapRoute(element)) {
      result.extend(getBounds(element.waypoints));
    } else if (isMapWaypoint(element)) {
      result.extend([element.longitude, element.latitude]);
    } else if (isMapPoint(element)) {
      result.extend([element.longitude, element.latitude]);
    }
  }
  return result;
};
/**
 * Scale the given LngLatBounds object by the given factor. A factor > 1.0 increases
 * the size of the bounds and a factor < 1.0 decreases the size of the bounds.
 */
const scaleLngLatBounds = (bounds, factor) => {
  const bboxPolygon = turf.bboxPolygon([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]);
  const scaledBboxPolygon = turf.transformScale(bboxPolygon, factor);
  const [minX, minY, maxX, maxY] = turf.bbox(scaledBboxPolygon);
  return ML__default.LngLatBounds.convert([minX, minY, maxX, maxY]);
};
const clamp = (value, minimum, maximum) => {
  if (minimum > maximum) {
    throw new Error('unable to clamp value');
  }
  if (value < minimum) {
    return minimum;
  }
  if (value > maximum) {
    return maximum;
  }
  return value;
};

/**
 * This is a class for keeping track of layer and source IDs to help
 * ensure they are unique IDs
 */
class IdStorage {
  constructor() {
    this.ids = new Set();
  }
  add(id) {
    this.ids.add(id);
  }
  get(prefix) {
    const prefixMatches = [];
    for (const id of this.ids.values()) {
      if (id.startsWith(prefix)) {
        prefixMatches.push(id);
      }
    }
    return prefixMatches;
  }
  next(prefix) {
    const prefixMatches = this.get(prefix);
    let suffix = 1;
    let id = `${prefix}`;
    while (prefixMatches.some(x => x === id)) {
      suffix += 1;
      id = `${prefix}-${suffix}`;
    }
    return id;
  }
  remove(id) {
    this.ids.delete(id);
  }
  [Symbol.iterator]() {
    return this.ids[Symbol.iterator]();
  }
}

/**
 * Used to define the ordering of layers.
 * Any layers that have the same priority may be drawn in an undetermined order.
 */
var LayerPriority = /*#__PURE__*/function (LayerPriority) {
  LayerPriority["Background"] = "Priority-Background";
  LayerPriority["LowestPriority"] = "Priority-Lowest";
  LayerPriority["BaseMap"] = "Priority-BaseMap";
  LayerPriority["Topography"] = "Priority-Topography";
  LayerPriority["LandUse"] = "Priority-LandUse";
  LayerPriority["Hillshade"] = "Priority-Hillshade";
  LayerPriority["BaseMapWater"] = "Priority-BaseMap-Water";
  LayerPriority["BaseMapBordersAndRoads"] = "Priority-BaseMap-BordersAndRoads";
  LayerPriority["Charts"] = "Priority-Charts";
  LayerPriority["HelicopterOverlay"] = "Priority-HelicopterOverlay";
  LayerPriority["TiledTemperature"] = "Priority-TiledTemperature";
  LayerPriority["Icing"] = "Priority-Icing";
  LayerPriority["Clouds"] = "Priority-Clouds";
  LayerPriority["Radar"] = "Priority-Radar";
  LayerPriority["AirportRunways"] = "Priority-Airport-Runways";
  LayerPriority["AirwayLines"] = "Priority-AirwayLines";
  LayerPriority["Airways"] = "Priority-Airways";
  LayerPriority["NavigationAids_WPT"] = "Priority-NavigationAidsWPT";
  LayerPriority["NavigationAids_VRP"] = "Priority-NavigationAidsVRP";
  LayerPriority["NavigationAids_NDB"] = "Priority-NavigationAidsNDB";
  LayerPriority["NavigationAids_TACAN"] = "Priority-NavigationAidsTACAN";
  LayerPriority["NavigationAids_VOR"] = "Priority-NavigationAidsVOR";
  LayerPriority["NavigationAids_VOR_DME"] = "Priority-NavigationAidsVORDME";
  LayerPriority["SafeTaxi"] = "Priority-SafeTaxi";
  LayerPriority["SafeTaxiNotams"] = "Priority-SafeTaxi-Notams";
  LayerPriority["Airmet"] = "Priority-Airmet";
  LayerPriority["Sigmet"] = "Priority-Sigmet";
  LayerPriority["WindAnimation"] = "Priority-WindAnimation";
  LayerPriority["Airspaces"] = "Priority-Airspaces";
  LayerPriority["BaseMapPlaces"] = "Priority-BaseMap-Places";
  LayerPriority["Obstacles"] = "Priority-Obstacles";
  LayerPriority["ObstacleNotam"] = "Priority-Obstacles-Notams";
  LayerPriority["AirportIcons"] = "Priority-Airport-Icons";
  LayerPriority["AirportNotams"] = "Priority-Airport-Notams";
  LayerPriority["Notam"] = "Priority-Notam";
  LayerPriority["RunwayNotam"] = "Priority-Notam-Runway";
  LayerPriority["Tfr"] = "Priority-TFR";
  LayerPriority["InternetTraffic"] = "Priority-InternetTraffic";
  LayerPriority["InternetTrafficTrackedBreadcrumbs"] = "Priority-InternetTraffic-TrackedBreadcrumbs";
  LayerPriority["FuelPrices"] = "Priority-FuelPrices";
  LayerPriority["Lightning"] = "Priority-Lightning";
  LayerPriority["WeatherReport"] = "Priority-WeatherReport";
  LayerPriority["Pirep"] = "Priority-PIREP";
  LayerPriority["StormCells"] = "Priority-StormCells";
  LayerPriority["Waypoints"] = "Priority-Waypoints";
  LayerPriority["Routes"] = "Priority-Routes";
  LayerPriority["Flights"] = "Priority-Flights";
  LayerPriority["Procedures"] = "Priority-Procedures";
  LayerPriority["HighlightAirmet"] = "Priority-Highlight-Airmet";
  LayerPriority["HighlightSigmet"] = "Priority-Highlight-Sigmet";
  LayerPriority["HighlightAirspace"] = "Priority-Highlight-Airspace";
  LayerPriority["HighlightTfr"] = "Priority-Highlight-TFR";
  LayerPriority["HighlightNotam"] = "Priority-Highlight-NOTAM";
  LayerPriority["HighlightRunwayNotam"] = "Priority-Highlight-Runway-NOTAM";
  LayerPriority["HighlightAirways"] = "Priority-Highlight-Airways";
  LayerPriority["HighlightObstacle"] = "Priority-Highlight-Obstacle";
  LayerPriority["AirwaysText"] = "Priority-Airways-Text";
  LayerPriority["HighlightInternetTrafficCircle"] = "Priority-Highlight-InternetTraffic-Circle";
  LayerPriority["HighlightInternetTrafficAircraft"] = "Priority-Highlight-InternetTraffic-Aircraft";
  LayerPriority["HighlightLocation"] = "Priority-Highlight-Location";
  LayerPriority["HighestPriority"] = "Priority-Highest";
  return LayerPriority;
}(LayerPriority || {});
class LayerPriorityHandler {
  constructor() {
    this.layersByPriority = new Map();
  }
  /**
  * Given a priority, figure out the layer that must be in the `beforeId` argument
  * for the addLayer call. Also saves the given layer ID for future calls.
  * @param priority The priority for the layer being added
  * @param id The ID for the layer being added
  * @returns The `beforeId` to use when adding the layer
  */
  getNextLayer(priority, id) {
    // Get the current layer list for the specific priority
    const layersList = this.layersByPriority.get(priority) ?? [];
    // nextLayersList is the layer list to use to determine the 'beforeId' argument.
    // This may be the specified priority, if any layers are on it, or the next higher priority
    // that has any layers on that one.
    let nextLayersList = undefined;
    // Iterate through the priorities to find the ones higher than the specified priority
    let found = false;
    for (const x of Object.values(LayerPriority)) {
      if (found) {
        const d = this.layersByPriority.get(x);
        if (d != null) {
          nextLayersList = d;
          break;
        }
      }
      if (x === priority) {
        found = true;
      }
    }
    // If nextLayersList is undefined, then there is no higher priority with active layers
    // so just add it to the top of the map
    const nextLayer = nextLayersList != null ? nextLayersList[0] : '';
    // Save the current id to the maps active layers
    layersList.push(id);
    this.layersByPriority.set(priority, layersList);
    // Return the `beforeId` argument to use
    return nextLayer;
  }
  /**
   * Removes the given layer from priority tracking
   * @param id The layer ID being removed
   */
  removeLayer(id) {
    for (const key of this.layersByPriority.keys()) {
      const b = this.layersByPriority.get(key);
      if (b != null) {
        const modifiedB = b.filter(x => x !== id);
        if (modifiedB.length > 0) {
          this.layersByPriority.set(key, modifiedB);
        } else {
          this.layersByPriority.delete(key);
        }
      }
    }
  }
}
const MINIMUM_ZOOM = 2;
const MAXIMUM_ZOOM = 18;
var MarkerPriority = /*#__PURE__*/function (MarkerPriority) {
  // LowestPriority,
  MarkerPriority[MarkerPriority["StormCells"] = 0] = "StormCells";
  MarkerPriority[MarkerPriority["Flights"] = 1] = "Flights";
  MarkerPriority[MarkerPriority["RadialMenu"] = 2] = "RadialMenu";
  // HighestPriority
  return MarkerPriority;
}(MarkerPriority || {});
/**
 * Class for setting the priority/z-order of MapLibre Marker elements.
 *
 * Normally when markers are added to the map, they are simply appended to the
 * map canvas container. The priority/z-order is thus controlled by the order
 * in which the markers are added. This is because things that appear later in
 * the DOM are above things that appear earlier in the DOM (without that pesky
 * CSS getting in the way).
 *
 * This controls the priority/z-order by creating one div element per priority
 * and appending them to the map canvas container. Then the markers are appended
 * to these priority elements instead of to the map canvas container directly.
 */
class MarkerPriorityHandler {
  /**
   * @param container The map canvas container
   */
  constructor(container) {
    this.priorityMap = new Map(this.getEnumValues().map(p => [p, document.createElement('div')]));
    for (const p of this.getEnumValues()) {
      const element = this.priorityMap.get(p);
      if (element != null) {
        container.appendChild(element);
      }
    }
  }
  /**
   * Sets the marker priority by moving the marker element in the DOM
   * @param priority The marker priority
   * @param marker The marker
   */
  setMarkerPriority(priority, marker) {
    const element = this.priorityMap.get(priority);
    if (element != null) {
      // Append the marker element to the priority element
      // Note: If the element is already in the DOM, this will move
      //       the element instead of creating a copy.
      element.appendChild(marker.getElement());
    }
  }
  getEnumValues() {
    return Object.values(MarkerPriority).filter(p => typeof p === 'number').sort((a, b) => a - b);
  }
}

/* eslint-disable max-classes-per-file */
class PropertyMapIterator {
  constructor(values) {
    this.values = values;
    this.index = 0;
    this.done = false;
  }
  next() {
    if (this.done) {
      return {
        done: this.done,
        value: undefined
      };
    }
    if (this.index === this.values.length) {
      this.done = true;
      return {
        done: this.done,
        value: this.index
      };
    }
    const value = this.values[this.index];
    this.index += 1;
    return {
      done: false,
      value
    };
  }
}
/**
 * This is similar to a standard Map object but the key used is provided by a
 * subclass. The intent is that the key would be a property stored in the key object.
 * This is needed for mutable keys as any difference in properties may result in a
 * different key value.
 */
class PropertyMap {
  constructor() {
    this._keys = new Map();
    this._entries = new Map();
  }
  set(key, value) {
    const propertyKey = this.getPropertyKey(key);
    this._keys.set(propertyKey, key);
    this._entries.set(propertyKey, value);
  }
  get(key) {
    const propertyKey = this.getPropertyKey(key);
    return this._entries.get(propertyKey);
  }
  has(key) {
    const propertyKey = this.getPropertyKey(key);
    return this._keys.has(propertyKey);
  }
  clear() {
    this._keys.clear();
    this._entries.clear();
  }
  delete(key) {
    const propertyKey = this.getPropertyKey(key);
    this._keys.delete(propertyKey);
    this._entries.delete(propertyKey);
  }
  entries() {
    return this[Symbol.iterator]();
  }
  forEach(callbackfn) {
    const keys = [...this._keys.values()];
    const entries = [...this._entries.values()];
    for (let i = 0; i < keys.length; i++) {
      callbackfn(entries[i], keys[i], entries);
    }
  }
  keys() {
    return [...this._keys.values()];
  }
  get size() {
    return this._entries.size;
  }
  values() {
    return [...this._entries.values()];
  }
  [Symbol.iterator]() {
    const keys = [...this._keys.values()];
    const entries = [...this._entries.values()];
    const values = [];
    for (let i = 0; i < keys.length; i++) {
      values.push([keys[i], entries[i]]);
    }
    return new PropertyMapIterator(values);
  }
}
class ContentMap extends PropertyMap {
  getPropertyKey(key) {
    return `${key.type}${key.domain}`;
  }
}
class MultiLayerSource {
  constructor(map) {
    this.map = map;
    this.contentInfo = new ContentMap();
  }
  add(user, content) {
    const info = this.contentInfo.get(content) ?? {
      id: this.map.sourceIds.next(this.SOURCE),
      users: new Set()
    };
    if (info.users.size === 0) {
      this.map.addSource(info.id, {
        type: 'vector',
        tiles: [`${content.urlBase}/{z}/{x}/{y}.mvt`],
        scheme: 'tms',
        minzoom: this.MINZOOM,
        maxzoom: this.MAXZOOM
      });
    }
    info.users.add(user);
    this.contentInfo.set(content, info);
    return info.id;
  }
  getId(content) {
    return this.contentInfo.get(content)?.id;
  }
  remove(user, content) {
    const info = this.contentInfo.get(content);
    if (info != null) {
      info.users.delete(user);
      if (info.users.size === 0 && info.id != null) {
        this.map.removeSource(info.id);
      }
    }
  }
}
var MvtCycleContentUser = /*#__PURE__*/function (MvtCycleContentUser) {
  MvtCycleContentUser["Airport"] = "airport";
  MvtCycleContentUser["Airspace"] = "airspace";
  MvtCycleContentUser["Airways"] = "airways";
  MvtCycleContentUser["BaseMap"] = "base-map";
  MvtCycleContentUser["Navaids"] = "navaids";
  return MvtCycleContentUser;
}(MvtCycleContentUser || {});
var MvtCycleContentSourceLayer = /*#__PURE__*/function (MvtCycleContentSourceLayer) {
  // Airspaces
  MvtCycleContentSourceLayer["ARTCC"] = "airspace_fir_uir";
  MvtCycleContentSourceLayer["ControlledAirspaces"] = "airspace_ctrl";
  MvtCycleContentSourceLayer["SpecialUseAirspaces"] = "airspace_sua";
  // Airways
  MvtCycleContentSourceLayer["Airways"] = "airways";
  // Airports
  MvtCycleContentSourceLayer["Airports"] = "Airport";
  MvtCycleContentSourceLayer["Runways"] = "Runways";
  MvtCycleContentSourceLayer["RunwayPoints"] = "RunwayPoints";
  // Navaids
  MvtCycleContentSourceLayer["VOR"] = "VOR";
  MvtCycleContentSourceLayer["NDB"] = "NDB";
  MvtCycleContentSourceLayer["WPT"] = "WPT";
  // OpenStreetMap (BaseMap)
  // See https://openmaptiles.org/schema/
  MvtCycleContentSourceLayer["Boundaries"] = "boundary";
  MvtCycleContentSourceLayer["LandCover"] = "landcover";
  MvtCycleContentSourceLayer["LandUse"] = "landuse";
  MvtCycleContentSourceLayer["Places"] = "place";
  MvtCycleContentSourceLayer["Transportation"] = "transportation";
  MvtCycleContentSourceLayer["Water"] = "water";
  MvtCycleContentSourceLayer["WaterWay"] = "waterway";
  return MvtCycleContentSourceLayer;
}(MvtCycleContentSourceLayer || {});
class MvtCycleContent extends MultiLayerSource {
  constructor() {
    super(...arguments);
    this.SOURCE = 'mvt-cycle-content';
    this.MINZOOM = 0;
    this.MAXZOOM = 11;
  }
}
const now = () => {
  if (window?.performance != null) {
    return window.performance.now();
  }
  return Date.now();
};
class SourceLoadingMonitor {
  constructor() {
    this.measurements = new Map();
    this.onSourceData = event => {
      const sourceId = event.sourceId;
      const isSourceLoaded = event.isSourceLoaded;
      // get the source measurement or make a new one
      let measurement = this.measurements.get(sourceId);
      if (measurement == null) {
        measurement = {
          sourceId,
          timeStarted: 0,
          loadTime: 0,
          totalLoadTime: 0,
          isSourceLoaded: true
        };
        this.measurements.set(sourceId, measurement);
      }
      // save the loaded state
      measurement.isSourceLoaded = isSourceLoaded;
      // calculate stuff if the source is considered loaded now
      if (isSourceLoaded) {
        if (measurement.timeStarted !== 0) {
          measurement.loadTime = now() - measurement.timeStarted;
          measurement.totalLoadTime += measurement.loadTime;
          measurement.timeStarted = 0;
        }
      }
      // save timestamps if the source is being loaded
      else {
        if (measurement.timeStarted == 0) {
          measurement.timeStarted = now();
        }
      }
    };
  }
  delete(sourceId) {
    this.measurements.delete(sourceId);
  }
  getSourceIsLoaded(sourceId) {
    return this.measurements.get(sourceId)?.isSourceLoaded ?? true;
  }
  getTotalLoadTime(sourceId) {
    return this.measurements.get(sourceId)?.totalLoadTime ?? 0;
  }
  reset() {
    this.measurements.clear();
  }
  start(map) {
    map.on('sourcedata', this.onSourceData);
    map.on('sourcedataabort', this.onSourceData);
    map.on('sourcedataloading', this.onSourceData);
  }
  stop(map) {
    map.off('sourcedata', this.onSourceData);
    map.off('sourcedataabort', this.onSourceData);
    map.off('sourcedataloading', this.onSourceData);
  }
}
var SpriteID = /*#__PURE__*/function (SpriteID) {
  SpriteID["Airports"] = "airports";
  return SpriteID;
}(SpriteID || {});
var MapClickOrder = /*#__PURE__*/function (MapClickOrder) {
  MapClickOrder[MapClickOrder["First"] = 0] = "First";
  MapClickOrder[MapClickOrder["PopupClose"] = 1] = "PopupClose";
  MapClickOrder[MapClickOrder["PopupOpen"] = 2] = "PopupOpen";
  MapClickOrder[MapClickOrder["Last"] = 3] = "Last";
  return MapClickOrder;
}(MapClickOrder || {});
const DOUBLE_CLICK_TIMER = 200;
/**
 * This is wrapper around a MapLibre Map object to provide additional functionality
 */
class MapWrapper {
  constructor(_map) {
    this._map = _map;
    this.layerIds = new IdStorage();
    this.sourceIds = new IdStorage();
    this.mvtCycleContent = new MvtCycleContent(this);
    this.layerPriorityHandler = new LayerPriorityHandler();
    this.markerPriorityHandler = new MarkerPriorityHandler(this._map.getCanvasContainer());
    this.sourceLoadingMonitor = new SourceLoadingMonitor();
    this.mapClickHandlers = new Map();
    this.clickCount = 0;
    this.topElementCount = 0;
    this.centerOnTraffic = new ReplaySubject(1);
    this.controlsEnabled = true;
    // Increase the scroll wheel zoom speed (1/450 is default)
    _map.scrollZoom.setWheelZoomRate(1 / 225);
    // Increase the track pad zoom speed (1/100 is default)
    _map?.scrollZoom.setZoomRate(1 / 50);
    this.installClickHandler();
    // keep track of which sources are loaded and how long they take to load
    this.sourceLoadingMonitor.start(_map);
    // save the initial state of the event handlers
    this.initialControls = {
      boxZoom: this._map.boxZoom.isEnabled(),
      cooperativeGestures: this._map.cooperativeGestures.isEnabled(),
      doubleClickZoom: this._map.doubleClickZoom.isEnabled(),
      dragPan: this._map.dragPan.isEnabled(),
      dragRotate: this._map.dragRotate.isEnabled(),
      keyboard: this._map.keyboard.isEnabled(),
      scrollZoom: this._map.scrollZoom.isEnabled(),
      touchPitch: this._map.touchPitch.isEnabled(),
      touchZoomRotate: this._map.touchZoomRotate.isEnabled()
    };
  }
  dispatchEvent(e) {
    if (e instanceof WheelEvent) {
      this._map.fire(new MapWheelEvent(e.type, this._map, e));
    } else {
      this._map.fire(new MapMouseEvent(e.type, this._map, e));
    }
  }
  addImage(id, image, options) {
    if (!this._map.hasImage(id)) {
      this._map.addImage(id, image, options);
    }
    return this;
  }
  addLayer(priority, layer) {
    const id = layer.id;
    const beforeId = this.layerPriorityHandler.getNextLayer(priority, id);
    if (!this.hasLayer(id)) {
      if (this.hasLayer(beforeId)) {
        this._map.addLayer(layer, beforeId);
      } else {
        this._map.addLayer(layer);
      }
      this.layerIds.add(id);
    }
    return this;
  }
  addMarker(priority, marker) {
    // Call the original method to add markers so all the appropriate
    // styles and events get sets up. This appends the marker to the
    // map canvas.
    marker.addTo(this._map);
    // "Set the priority" by moving the marker to a different div
    this.markerPriorityHandler.setMarkerPriority(priority, marker);
    return this;
  }
  addSource(id, source) {
    if (!this.hasSource(id)) {
      this._map.addSource(id, source);
      this.sourceIds.add(id);
    }
    return this;
  }
  cameraForBounds(bounds, options) {
    return this._map.cameraForBounds(bounds, options);
  }
  easeTo(options, eventData) {
    this._map.easeTo(options, eventData);
    return this;
  }
  /**
   * Disables all map control handlers. These are the things that allow
   * panning, zooming, tilting, etc., by interacting with the map.
   */
  disableMapControls() {
    this._map.boxZoom.disable();
    this._map.cooperativeGestures.disable();
    this._map.doubleClickZoom.disable();
    this._map.dragPan.disable();
    this._map.dragRotate.disable();
    this._map.keyboard.disable();
    this._map.scrollZoom.disable();
    this._map.touchPitch.disable();
    this._map.touchZoomRotate.disable();
    this.controlsEnabled = false;
    this._map.getCanvasContainer().style.cursor = 'default';
    return this;
  }
  /**
   * Enables all map control handlers. These are the things that allow
   * panning, zooming, tilting, etc., by interacting with the map.
   *
   * NOTE: Only the control handlers that were enabled when the map
   * was created will be enabled.
   */
  enableMapControls() {
    if (this.initialControls.boxZoom) {
      this._map.boxZoom.enable();
    }
    if (this.initialControls.cooperativeGestures) {
      this._map.cooperativeGestures.enable();
    }
    if (this.initialControls.doubleClickZoom) {
      this._map.doubleClickZoom.enable();
    }
    if (this.initialControls.dragPan) {
      this._map.dragPan.enable();
    }
    if (this.initialControls.dragRotate) {
      this._map.dragRotate.enable();
    }
    if (this.initialControls.keyboard) {
      this._map.keyboard.enable();
    }
    if (this.initialControls.scrollZoom) {
      this._map.scrollZoom.enable();
    }
    if (this.initialControls.touchPitch) {
      this._map.touchPitch.enable();
    }
    if (this.initialControls.touchZoomRotate) {
      this._map.touchZoomRotate.enable();
    }
    this.controlsEnabled = true;
    this._map.getCanvasContainer().style.cursor = '';
    return this;
  }
  convertFitOptionsToFlyOptions(center, zoom, options) {
    const cappedZoom = clamp(zoom, options?.minZoom ?? MINIMUM_ZOOM, options?.maxZoom ?? MAXIMUM_ZOOM);
    return {
      center,
      zoom: cappedZoom,
      animate: options?.animate,
      duration: options?.duration
    };
  }
  fitBounds(bounds, options) {
    const scaledBounds = options?.padding != null ? scaleLngLatBounds(bounds, options.padding) : bounds;
    const camera = this._map.cameraForBounds(scaledBounds);
    const zoom = camera?.zoom ?? this.getZoom();
    this.flyTo(this.convertFitOptionsToFlyOptions(camera?.center, zoom, options));
    return this;
  }
  fitElements(elements, options) {
    if (elements != null) {
      this.fitBounds(getBounds(elements), options);
    }
    return this;
  }
  fitPoint(point, options) {
    this.flyTo(this.convertFitOptionsToFlyOptions(point, this.getZoom(), options));
    return this;
  }
  flyTo(options, eventData) {
    this._map.flyTo(options, eventData);
    return this;
  }
  getBearing() {
    return this._map.getBearing();
  }
  getBounds() {
    return this._map.getBounds();
  }
  getCanvas() {
    return this._map.getCanvas();
  }
  getCanvasContainer() {
    return this._map.getCanvasContainer();
  }
  getCenter() {
    return this._map.getCenter();
  }
  getId() {
    return this._map._getMapId();
  }
  getImage(id, spriteId) {
    const resolvedId = spriteId != null ? `${spriteId}:${id}` : id;
    if (this._map.hasImage(resolvedId)) {
      return this._map.style.getImage(resolvedId);
    }
    return undefined;
  }
  getSourceForLayerId(id) {
    if (id == null) {
      return {};
    }
    const layer = this._map.getLayer(id);
    return {
      source: layer?.source,
      sourceLayer: layer?.sourceLayer
    };
  }
  getLayerOpacity(id) {
    if (id == null) {
      return;
    }
    const layer = this._map.getLayer(id);
    if (layer == null) {
      return;
    }
    switch (layer.type) {
      case 'symbol':
        return this._map.getPaintProperty(id, 'icon-opacity');
      case 'background':
      case 'circle':
      case 'fill':
      case 'fill-extrusion':
      case 'heatmap':
      case 'hillshade':
      case 'line':
      case 'raster':
        return this._map.getPaintProperty(id, `${layer.type}-opacity`);
      default:
        console.error(`getLayerOpacity does not support "${layer.type}" layers`);
        break;
    }
    return;
  }
  /** @private @deprecated debug or development use only! */
  getMap() {
    return this._map;
  }
  getMaxBounds() {
    return this._map.getMaxBounds();
  }
  getMaxPitch() {
    return this._map.getMaxPitch();
  }
  getMaxZoom() {
    return this._map.getMaxZoom();
  }
  getMinPitch() {
    return this._map.getMinPitch();
  }
  getMinZoom() {
    return this._map.getMinZoom();
  }
  getPitch() {
    return this._map.getPitch();
  }
  getSourceIds() {
    return Array.from(this.sourceIds);
  }
  getZoom() {
    return this._map.getZoom();
  }
  hasImage(id) {
    return id != null && this._map.hasImage(id);
  }
  hasLayer(id) {
    return id != null && this._map.getLayer(id) != null;
  }
  hasSource(id) {
    return id != null && this._map.getSource(id) != null;
  }
  incrementalZoom(input, mouseEvent) {
    const options = {
      duration: 300,
      zoom: Math.round(this._map.getZoom() + (input === 'in' ? 1 : -1)),
      animate: true,
      essential: true
    };
    if (mouseEvent != null) {
      Object.assign(options, {
        around: mouseEvent.lngLat
      });
    }
    this._map.easeTo(options);
    //turn off traffic centering when zooming
    this.centerOnTraffic.next(false);
  }
  jumpTo(options, eventData) {
    this._map.jumpTo(options, eventData);
    return this;
  }
  listImages() {
    return this._map.style.listImages();
  }
  loaded() {
    return this._map.loaded();
  }
  off(type, listener) {
    this._map.off(type, listener);
    return this;
  }
  on(type, listener) {
    this._map.on(type, listener);
    return this;
  }
  once(type, listener) {
    const promise = this._map.once(type, listener);
    if (promise !== this._map) {
      return promise;
    } else {
      return this;
    }
  }
  project(lnglat) {
    return this._map.project(lnglat);
  }
  queryRenderedFeatures(geometryOrOptions, options) {
    return this._map.queryRenderedFeatures(geometryOrOptions, options);
  }
  querySourceFeatures(sourceId, parameters) {
    if (this.hasSource(sourceId)) {
      return this._map.querySourceFeatures(sourceId, parameters);
    }
    return [];
  }
  /**
   * Registers a mouse click handler with the underlying map.
   *
   * We do this because we're overriding the double click to zoom functionality,
   * so we need more control over the click events.
   * @param handler A function to be called when the map emits a click event.
   * @param order Optional ordering for map click handlers
   */
  registerMapClickHandler(handler, order) {
    const fixedOrder = order ?? MapClickOrder.First;
    const foundHandlers = this.mapClickHandlers.get(fixedOrder) ?? [];
    foundHandlers.push(handler);
    this.mapClickHandlers.set(fixedOrder, foundHandlers);
  }
  get repaint() {
    return this._map.repaint;
  }
  set repaint(value) {
    this._map.repaint = value;
  }
  remove() {
    this._map.remove();
  }
  removeImage(id) {
    if (this.hasImage(id)) {
      this._map.removeImage(id);
    }
    return this;
  }
  removeLayer(id) {
    if (this.hasLayer(id)) {
      this._map.removeLayer(id);
      this.layerPriorityHandler.removeLayer(id);
      this.layerIds.remove(id);
    }
    return this;
  }
  removeSource(id) {
    if (this.hasSource(id)) {
      this._map.removeSource(id);
      this.sourceIds.remove(id);
      this.sourceLoadingMonitor.delete(id);
    }
    return this;
  }
  resize(eventData) {
    this._map.resize(eventData);
    return this;
  }
  setBearing(bearing, eventData) {
    this._map.setBearing(bearing, eventData);
    return this;
  }
  setCenter(center, eventData) {
    this._map.setCenter(center, eventData);
    return this;
  }
  setGeoJsonSourceData(sourceId, data) {
    const source = this._map.getSource(sourceId);
    if (this.isGeoJsonSource(source)) {
      source.setData(data);
    }
    return this;
  }
  setLayerColor(layerId, color) {
    const layers = Array.isArray(layerId) ? layerId : [layerId];
    for (const id of layers) {
      const layer = this._map.getLayer(id);
      if (layer == null) {
        continue;
      }
      switch (layer.type) {
        case 'background':
        case 'circle':
        case 'fill':
        case 'line':
          this._map.setPaintProperty(id, `${layer.type}-color`, color);
          break;
        default:
          console.error(`setLayerColor does not support "${layer.type}" layers`);
          break;
      }
    }
    return this;
  }
  setLayerFilter(layerId, filter) {
    const layers = Array.isArray(layerId) ? layerId : [layerId];
    for (const id of layers) {
      const layer = this._map.getLayer(id);
      if (layer == null) {
        continue;
      }
      this._map.setFilter(id, filter);
    }
    return this;
  }
  /**
   * Sets the opacity of one or more layers
   * @param layerId The layer ID(s)
   * @param opacity The opacity
   */
  setLayerOpacity(layerId, opacity) {
    const layers = Array.isArray(layerId) ? layerId : [layerId];
    for (const id of layers) {
      const layer = this._map.getLayer(id);
      if (layer == null) {
        continue;
      }
      switch (layer.type) {
        case 'circle':
          this._map.setPaintProperty(id, `${layer.type}-opacity`, opacity);
          this._map.setPaintProperty(id, `${layer.type}-stroke-opacity`, opacity);
          break;
        case 'symbol':
          this._map.setPaintProperty(id, 'icon-opacity', opacity);
          this._map.setPaintProperty(id, 'text-opacity', opacity);
          break;
        case 'background':
        case 'fill':
        case 'fill-extrusion':
        case 'heatmap':
        case 'hillshade':
        case 'line':
        case 'raster':
          this._map.setPaintProperty(id, `${layer.type}-opacity`, opacity);
          break;
        default:
          console.error(`setLayerOpacity does not support "${layer.type}" layers`);
          break;
      }
    }
    return this;
  }
  setLayerPattern(layerId, pattern) {
    const layers = Array.isArray(layerId) ? layerId : [layerId];
    for (const id of layers) {
      const layer = this._map.getLayer(id);
      if (layer == null) {
        continue;
      }
      switch (layer.type) {
        case 'background':
        case 'fill':
        case 'fill-extrusion':
        case 'line':
          this._map.setPaintProperty(id, `${layer.type}-pattern`, pattern);
          break;
        default:
          console.error(`setLayerPattern does not support "${layer.type}" layers`);
      }
    }
    return this;
  }
  setLayerSortKey(layerId, sortKey) {
    const layers = Array.isArray(layerId) ? layerId : [layerId];
    for (const id of layers) {
      const layer = this._map.getLayer(id);
      if (layer == null) {
        continue;
      }
      switch (layer.type) {
        case 'circle':
        case 'fill':
        case 'line':
        case 'symbol':
          this._map.setLayoutProperty(id, `${layer.type}-sort-key`, sortKey);
          break;
        default:
          console.error(`setLayerSortKey does not support "${layer.type}" layers`);
      }
    }
    return this;
  }
  setLayerSymbolProperties(layerId, options) {
    const layers = Array.isArray(layerId) ? layerId : [layerId];
    for (const id of layers) {
      const layer = this._map.getLayer(id);
      if (layer == null) {
        continue;
      }
      switch (layer.type) {
        case 'symbol':
          if (options.iconColor != null) {
            this._map.setPaintProperty(id, 'icon-color', options.iconColor);
          }
          if (options.iconImage != null) {
            this._map.setLayoutProperty(id, 'icon-image', options.iconImage);
          }
          if (options.iconOpacity != null) {
            this._map.setPaintProperty(id, 'icon-opacity', options.iconOpacity);
          }
          if (options.iconRotate != null) {
            this._map.setLayoutProperty(id, 'icon-rotate', options.iconRotate);
          }
          if (options.textColor != null) {
            this._map.setPaintProperty(id, 'text-color', options.textColor);
          }
          if (options.textField != null) {
            this._map.setLayoutProperty(id, 'text-field', options.textField);
          }
          if (options.textHaloColor != null) {
            this._map.setPaintProperty(id, 'text-halo-color', options.textHaloColor);
          }
          if (options.textHaloWidth != null) {
            this._map.setPaintProperty(id, 'text-halo-width', options.textHaloWidth);
          }
          if (options.textSize != null) {
            this._map.setLayoutProperty(id, 'text-size', options.textSize);
          }
          if (options.textAllowOverlap != null) {
            this._map.setLayoutProperty(id, 'text-allow-overlap', options.textAllowOverlap);
          }
          break;
        default:
          console.error(`setLayerSymbolProperties does not support "${layer.type}" layers`);
          break;
      }
    }
    return this;
  }
  setLayerVisibility(layerId, visibility) {
    const layers = Array.isArray(layerId) ? layerId : [layerId];
    for (const id of layers) {
      const layer = this._map.getLayer(id);
      if (layer == null) {
        continue;
      }
      this._map.setLayoutProperty(id, 'visibility', visibility);
    }
    return this;
  }
  setLayerWidthProperties(layerId, width) {
    const layers = Array.isArray(layerId) ? layerId : [layerId];
    for (const id of layers) {
      const layer = this._map.getLayer(id);
      if (layer == null) {
        continue;
      }
      switch (layer.type) {
        case 'line':
          this._map.setPaintProperty(id, 'line-width', width);
          break;
        default:
          console.error(`setLayerWidthProperties does not support "${layer.type}" layers`);
          break;
      }
    }
    return this;
  }
  setMaxBounds(bounds) {
    this._map.setMaxBounds(bounds);
    return this;
  }
  setMaxPitch(maxPitch) {
    this._map.setMaxPitch(maxPitch);
    return this;
  }
  setMaxZoom(maxZoom) {
    this._map.setMaxZoom(maxZoom);
    return this;
  }
  setMinPitch(minPitch) {
    this._map.setMinPitch(minPitch);
    return this;
  }
  setMinZoom(minZoom) {
    this._map.setMinZoom(minZoom);
    return this;
  }
  setPitch(pitch, eventData) {
    this._map.setPitch(pitch, eventData);
    return this;
  }
  setSprite(spriteId, url) {
    if (this._map == null) {
      return false;
    }
    const sprites = this._map.getSprite();
    const present = sprites.find(x => x.id === spriteId) != null;
    const loaded = sprites.find(x => x.id === spriteId && x.url === url) != null;
    if (!loaded) {
      if (present) {
        this._map.removeSprite(spriteId);
      }
      this._map.addSprite(spriteId, url);
    }
    return loaded;
  }
  setTransformRequest(transformRequest) {
    this._map.setTransformRequest(transformRequest);
    return this;
  }
  setZoom(zoom, eventData) {
    this._map.setZoom(zoom, eventData);
    return this;
  }
  get showTileBoundaries() {
    return this._map.showTileBoundaries;
  }
  set showTileBoundaries(value) {
    this._map.showTileBoundaries = value;
  }
  /**
   * Given a list of layers for a given `LayerPriority` group, rearranges
   * the layers such that the last layer is on top and the first layer
   * is on bottom.
   * @note Any layers in the `priority` group that are not specified
   * in the `order` array will be placed below the specified layers.
   */
  sortLayers(priority, order) {
    // Remove the specified layers from the priority handler
    order.forEach(id => this.layerPriorityHandler.removeLayer(id));
    // Add them back in the specified order while moving them
    order.forEach(id => {
      const beforeId = this.layerPriorityHandler.getNextLayer(priority, id);
      this._map.moveLayer(id, beforeId);
    });
    return this;
  }
  triggerRepaint() {
    this._map.triggerRepaint();
  }
  unproject(point) {
    return this._map.unproject(point).wrap();
  }
  isGeoJsonSource(source) {
    return source != null && source.type === 'geojson';
  }
  unregisterMapClickHandler(handler) {
    for (const handlers of this.mapClickHandlers.values()) {
      const index = handlers.findIndex(x => handler === x);
      handlers.splice(index, 1);
    }
  }
  installClickHandler() {
    // Set up our own double-click to zoom functionality so we can _properly_ handle it. Ideally
    // MapLibre would have implemented this same behavior with an option to control the timing
    // threshold with something like `new Map({ clickEventThreshold: 200 })`, which would only fire
    // a 'click' event if no other 'click' events were triggered within that time.
    this._map.on('click', e => {
      if (this.topElementCount > 0) {
        // There is an element on top of the map that is presumably being closed ==> ignore click
        this.clickCount += 1;
        window.setTimeout(() => {
          this.clickCount = 0;
        }, DOUBLE_CLICK_TIMER); // double click within 200 ms, otherwise clear count
        return;
      }
      this.clickCount += 1;
      if (this.controlsEnabled && this.clickCount === 2) {
        this.incrementalZoom(e.originalEvent.ctrlKey ? 'out' : 'in', e);
        this.clickCount = 0;
      } else {
        window.setTimeout(() => {
          if (this.clickCount === 1) {
            this.clickCount = 0;
            const allHandlers = this.mapClickHandlers;
            for (let order = MapClickOrder.First; order <= MapClickOrder.Last; order++) {
              const handlers = allHandlers.get(order);
              if (handlers == null) {
                continue;
              }
              for (const handler of handlers) {
                handler(e);
              }
            }
          }
        }, DOUBLE_CLICK_TIMER); // double clicks must occur within 200ms
      }
    });
  }
}
let MapStateHandler = /*#__PURE__*/(() => {
  class MapStateHandler {
    constructor() {
      this._bearing = signal(0);
      this._center = signal(ML__default.LngLat.convert([0, 0]));
      this._pitch = signal(0);
      this._zoom = signal(0);
      this.bearing = this._bearing.asReadonly();
      this.center = this._center.asReadonly();
      this.pitch = this._pitch.asReadonly();
      this.zoom = this._zoom.asReadonly();
      this._maxBounds = signal(null);
      this._minPitch = signal(0);
      this._maxPitch = signal(0);
      this._minZoom = signal(0);
      this._maxZoom = signal(0);
      this.maxBounds = this._maxBounds.asReadonly();
      this.minPitch = this._minPitch.asReadonly();
      this.maxPitch = this._maxPitch.asReadonly();
      this.minZoom = this._minZoom.asReadonly();
      this.maxZoom = this._maxZoom.asReadonly();
      this.allowMapControls = signal(true);
      this.centerOnInternetTraffic = signal(false);
      this._height = signal(0);
      this._width = signal(0);
      this.height = this._height.asReadonly();
      this.width = this._width.asReadonly();
      this._loaded = signal(false);
      this.loaded = this._loaded.asReadonly();
      this.pendingOperations = [];
      this.destroyRef = inject(DestroyRef);
      this.injector = inject(Injector);
      this.RENDER_TIMEOUT_MS = 1000; // how quick to recheck when no render/idle events are fired
      this.LOADED_MINIMUM_TIME_MS = 1000; // must be loaded for at least this long before reporting loaded
    }
    cameraForBounds(bounds, options) {
      if (this.map == null) {
        return {};
      }
      try {
        return this.map.cameraForBounds(bounds, options);
      } catch {
        // An exception here usually means the bounds are invalid for the map settings.
        // Too big, too small, inverted, etc. So just do a best guess.
        return {
          center: ML__default.LngLatBounds.convert(bounds).getCenter(),
          zoom: Math.min(options?.zoom ?? this.map.getZoom(), options?.maxZoom ?? Infinity)
        };
      }
    }
    connectMap(map) {
      if (map == null) {
        return;
      }
      this.map = map;
      this.map.centerOnTraffic.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: center => {
          this.centerOnInternetTraffic.set(center);
        },
        error: error => {
          console.error(error);
        }
      });
      effect(() => {
        if (this.allowMapControls()) {
          this.map?.enableMapControls();
        } else {
          this.map?.disableMapControls();
        }
      }, {
        injector: this.injector
      });
      this._bearing.set(this.map.getBearing());
      this._center.set(this.map.getCenter());
      this._pitch.set(this.map.getPitch());
      this._zoom.set(this.map.getZoom());
      this._maxBounds.set(this.map.getMaxBounds());
      this._maxPitch.set(this.map.getMaxPitch());
      this._maxZoom.set(this.map.getMaxZoom());
      this._minPitch.set(this.map.getMinPitch());
      this._minZoom.set(this.map.getMinZoom());
      this._height.set(this.map.getCanvas().height);
      this._width.set(this.map.getCanvas().width);
      this.map.on('move', ev => {
        this._bearing.set(ev.target.getBearing());
        this._center.set(ev.target.getCenter());
        this._pitch.set(ev.target.getPitch());
        this._zoom.set(ev.target.getZoom());
      });
      this.map.on('resize', ev => {
        this._height.set(ev.target.getCanvas().height);
        this._width.set(ev.target.getCanvas().width);
      });
      this.setUpLoadMonitoring();
      this.pendingOperations?.forEach(fn => fn());
      this.pendingOperations = undefined;
    }
    fit(options) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.fit(options));
        return this;
      }
      if (this.verifyFitOptions(options)) {
        if (options.point != null) {
          this.map.fitPoint(ML__default.LngLat.convert([options.point.longitude, options.point.latitude]), this.convertApiFitOptionsToMapFitOptions(options));
        }
        if (options.bounds != null) {
          const minX = options.bounds.array?.at(0) ?? options.bounds.sw?.longitude;
          const minY = options.bounds.array?.at(1) ?? options.bounds.sw?.latitude;
          const maxX = options.bounds.array?.at(2) ?? options.bounds.ne?.longitude;
          const maxY = options.bounds.array?.at(3) ?? options.bounds.ne?.latitude;
          if (minX != null && minY != null && maxX != null && maxY != null) {
            this.map.fitBounds(ML__default.LngLatBounds.convert([minX, minY, maxX, maxY]), this.convertApiFitOptionsToMapFitOptions(options));
          }
        }
        if (options.elements != null) {
          this.map.fitElements(options.elements, this.convertApiFitOptionsToMapFitOptions(options));
        }
      }
      return this;
    }
    flyTo(options) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.flyTo(options));
        return this;
      }
      const flyToOptions = {
        animate: true,
        duration: 100
      };
      if (options.bearing != null) {
        flyToOptions.bearing = options.bearing;
      }
      if (options.center != null) {
        flyToOptions.center = options.center;
      }
      if (options.pitch != null) {
        flyToOptions.pitch = options.pitch;
      }
      if (options.zoom != null) {
        flyToOptions.zoom = options.zoom;
      }
      this.map.flyTo(flyToOptions);
      return this;
    }
    incrementZoom(direction, point) {
      if (this.map == null) {
        return this;
      }
      //turn off traffic centering when zooming
      this.centerOnInternetTraffic.set(false);
      const options = {
        animate: true,
        duration: 100,
        zoom: Math.round(this.map.getZoom() + (direction === 'in' ? 1 : -1)),
        around: point
      };
      this.map.easeTo(options);
      return this;
    }
    jumpTo(options) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.jumpTo(options));
        return this;
      }
      const jumpToOptions = {};
      if (options.bearing != null) {
        jumpToOptions.bearing = options.bearing;
      }
      if (options.center != null) {
        jumpToOptions.center = options.center;
      }
      if (options.pitch != null) {
        jumpToOptions.pitch = options.pitch;
      }
      if (options.zoom != null) {
        jumpToOptions.zoom = options.zoom;
      }
      this.map.jumpTo(jumpToOptions);
      return this;
    }
    setBearing(bearing) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.setBearing(bearing));
        return this;
      }
      this.map.setBearing(bearing);
      this._bearing.set(this.map.getBearing());
      return this;
    }
    setCenter(center) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.setCenter(center));
        return this;
      }
      this.map.setCenter(center);
      this._center.set(this.map.getCenter());
      return this;
    }
    setMaxBounds(bounds) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.setMaxBounds(bounds));
        return this;
      }
      this.map.setMaxBounds(bounds);
      this._maxBounds.set(this.map.getMaxBounds());
      return this;
    }
    setMaxPitch(maxPitch) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.setMaxPitch(maxPitch));
        return this;
      }
      this.map.setMaxPitch(maxPitch);
      this._maxPitch.set(this.map.getMaxPitch());
      return this;
    }
    setMaxZoom(maxZoom) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.setMaxZoom(maxZoom));
        return this;
      }
      this.map.setMaxZoom(maxZoom);
      this._maxZoom.set(this.map.getMaxZoom());
      return this;
    }
    setMinPitch(minPitch) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.setMinPitch(minPitch));
        return this;
      }
      this.map.setMinPitch(minPitch);
      this._minPitch.set(this.map.getMinPitch());
      return this;
    }
    setMinZoom(minZoom) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.setMinZoom(minZoom));
        return this;
      }
      this.map.setMinZoom(minZoom);
      this._minZoom.set(this.map.getMinZoom());
      return this;
    }
    setPitch(pitch) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.setPitch(pitch));
        return this;
      }
      this.map.setPitch(pitch);
      this._pitch.set(this.map.getPitch());
      return this;
    }
    setZoom(zoom) {
      if (this.map == null) {
        this.pendingOperations?.push(() => this.setZoom(zoom));
        return this;
      }
      this.map.setZoom(zoom);
      this._zoom.set(this.map.getZoom());
      return this;
    }
    project(lnglat) {
      if (this.map == null) {
        return null;
      }
      return this.map.project(lnglat);
    }
    save() {
      return {
        bearing: this.bearing(),
        center: this.center(),
        maxBounds: this.maxBounds(),
        maxPitch: this.maxPitch(),
        maxZoom: this.maxZoom(),
        minPitch: this.minPitch(),
        minZoom: this.minZoom(),
        pitch: this.pitch(),
        zoom: this.zoom()
      };
    }
    restore(values) {
      if (values?.bearing != null) {
        this.setBearing(values.bearing);
      }
      if (values?.center != null) {
        this.setCenter(values.center);
      }
      if (values?.maxBounds !== undefined) {
        // maxBounds is nullable
        this.setMaxBounds(values.maxBounds);
      }
      if (values?.maxPitch != null) {
        this.setMaxPitch(values.maxPitch);
      }
      if (values?.maxZoom != null) {
        this.setMaxZoom(values.maxZoom);
      }
      if (values?.minPitch != null) {
        this.setMinPitch(values.minPitch);
      }
      if (values?.minZoom != null) {
        this.setMinZoom(values.minZoom);
      }
      if (values?.pitch != null) {
        this.setPitch(values.pitch);
      }
      if (values?.zoom != null) {
        this.setZoom(values.zoom);
      }
    }
    unproject(point) {
      if (this.map == null) {
        return null;
      }
      return this.map.unproject(point);
    }
    convertApiFitOptionsToMapFitOptions(options) {
      if (options != null) {
        return {
          animate: options.animate ?? false,
          duration: options.duration,
          maxZoom: options.maxZoom,
          minZoom: options.minZoom,
          padding: options.padding
        };
      }
      return undefined;
    }
    setUpLoadMonitoring() {
      if (this.map == null) {
        return;
      }
      // The map should only report loaded=true when it has been
      // considered loaded for a minimum amount of time. That is
      // controlled by this timeout handler.
      const onLoadedSetTimeout = () => {
        this.loadedSetTimeoutHandle = undefined;
        this._loaded.set(true);
      };
      // Sometimes the idle event doesn't fire. No clue why.
      // So this timeout is used to check for a loaded state
      // when the render event hasn't been received for >1s.
      const onRenderTimeout = () => {
        this.renderTimeoutHandle = undefined;
        if (this.map == null) {
          return;
        }
        const loaded = this.map.loaded();
        if (loaded) {
          if (this.loadedSetTimeoutHandle == null) {
            this.loadedSetTimeoutHandle = window.setTimeout(onLoadedSetTimeout, this.LOADED_MINIMUM_TIME_MS);
          }
        } else {
          this.renderTimeoutHandle = window.setTimeout(onRenderTimeout, this.RENDER_TIMEOUT_MS);
        }
      };
      this.map.on('render', () => {
        this._loaded.set(false);
        if (this.loadedSetTimeoutHandle != null) {
          window.clearTimeout(this.loadedSetTimeoutHandle);
          this.loadedSetTimeoutHandle = undefined;
        }
        if (this.renderTimeoutHandle != null) {
          window.clearTimeout(this.renderTimeoutHandle);
          this.renderTimeoutHandle = undefined;
        }
        this.renderTimeoutHandle = window.setTimeout(onRenderTimeout, this.RENDER_TIMEOUT_MS);
      });
      this.map.on('idle', () => {
        if (this.loadedSetTimeoutHandle == null) {
          this.loadedSetTimeoutHandle = window.setTimeout(onLoadedSetTimeout, this.LOADED_MINIMUM_TIME_MS);
        }
        if (this.renderTimeoutHandle != null) {
          window.clearTimeout(this.renderTimeoutHandle);
          this.renderTimeoutHandle = undefined;
        }
      });
    }
    verifyFitOptions(options) {
      let valid = true;
      if (options.maxZoom != null && options.maxZoom > MAXIMUM_ZOOM) {
        console.error(`Setting maxZoom above the maximum of ${MAXIMUM_ZOOM}`);
        valid = false;
      }
      if (options.minZoom != null && options.minZoom < MINIMUM_ZOOM) {
        console.error(`Setting minZoom below the minimum of ${MINIMUM_ZOOM}`);
        valid = false;
      }
      if (options.bounds != null) {
        if (options.bounds.array != null && options.bounds.array.length !== 4) {
          console.error(`Bounds array must be exactly 4 items`);
          valid = false;
        }
        if (options.bounds.ne != null && options.bounds.sw == null || options.bounds.ne == null && options.bounds.sw != null) {
          console.error(`Both NE and SW bounds must be provided`);
          valid = false;
        }
      }
      if (options.padding != null) {
        if (options.padding < 1.0) {
          console.error(`Padding must be greater than or equal to 1.0`);
          valid = false;
        }
      }
      return valid;
    }
    static {
      this.ɵfac = function MapStateHandler_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapStateHandler)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapStateHandler,
        factory: MapStateHandler.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapStateHandler;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var MapThemePreset = /*#__PURE__*/function (MapThemePreset) {
  MapThemePreset["Blank"] = "Blank";
  MapThemePreset["Dark"] = "Dark";
  MapThemePreset["Green"] = "Green";
  MapThemePreset["OpenStreetMap"] = "OpenStreetMap";
  MapThemePreset["Satellite"] = "Satellite";
  MapThemePreset["Topography"] = "Topography";
  MapThemePreset["White"] = "White";
  return MapThemePreset;
}(MapThemePreset || {});
var FlightRule = /*#__PURE__*/function (FlightRule) {
  FlightRule["Vfr"] = "Vfr";
  FlightRule["Ifr"] = "Ifr";
  return FlightRule;
}(FlightRule || {});
const blendChannel = (x, y, weight) => {
  return Math.floor(x * (1 - weight) + y * weight);
};
class Color {
  static {
    this.Beige = new Color(198, 198, 187);
  }
  static {
    this.Black = new Color(0, 0, 0);
  }
  static {
    this.Blue = new Color(20, 20, 255);
  }
  static {
    this.Brown = new Color(64, 46, 31);
  }
  static {
    this.DarkBeige = new Color(140, 123, 84);
  }
  static {
    this.DarkBlue = new Color(32, 41, 47);
  }
  static {
    this.DarkPaleBlue = new Color(161, 179, 203);
  }
  static {
    this.DarkYellow = new Color(230, 215, 50);
  }
  static {
    this.Gray = new Color(125, 118, 125);
  }
  static {
    this.Green = new Color(0, 255, 0);
  }
  static {
    this.GreenBrown = new Color(76, 68, 45);
  }
  static {
    this.LightBlue = new Color(56, 160, 223);
  }
  static {
    this.LightBrown = new Color(88, 64, 47);
  }
  static {
    this.LightningYellow = new Color(255, 213, 0);
  }
  static {
    this.WaterBlue = new Color(58, 65, 69);
  }
  static {
    this.Magenta = new Color(255, 0, 254);
  }
  static {
    this.MorePaleWhite = new Color(237, 240, 245);
  }
  static {
    this.Orange = new Color(255, 140, 0);
  }
  static {
    this.OtherBlue = new Color(30, 76, 157);
  }
  static {
    this.OtherLightBrown = new Color(81, 58, 44);
  }
  static {
    this.PaleBlue = new Color(214, 222, 255);
  }
  static {
    this.PaleGreen = new Color(150, 153, 143);
  }
  static {
    this.PaleWhite = new Color(250, 250, 255);
  }
  static {
    this.PaleYellow = new Color(217, 215, 152);
  }
  static {
    this.PureRed = new Color(255, 0, 0);
  }
  static {
    this.Purple = new Color(162, 64, 159);
  }
  static {
    this.Red = new Color(255, 59, 48);
  }
  static {
    this.RedBrown = new Color(147, 81, 1);
  }
  static {
    this.Teal = new Color(0, 176, 176);
  }
  static {
    this.Transparent = new Color(0, 0, 0, 0);
  }
  static {
    this.White = new Color(255, 255, 255);
  }
  static {
    this.Yellow = new Color(230, 215, 50);
  }
  static {
    this.NotamYellow = new Color(255, 169, 81);
  }
  static {
    this.NotamRed = new Color(230, 215, 50);
  }
  static {
    this.NotamOrange = new Color(255, 131, 0);
  }
  static {
    this.Gray93 = new Color(238, 238, 238);
  }
  static {
    this.Gray91 = new Color(231, 231, 231);
  }
  static {
    this.Gray90 = new Color(229, 229, 229);
  }
  static {
    this.Gray87 = new Color(222, 222, 222);
  }
  static {
    this.Gray75 = new Color(191, 191, 191);
  }
  static {
    this.Gray66 = new Color(170, 170, 170);
  }
  static {
    this.Gray65 = new Color(167, 167, 167);
  }
  static {
    this.Gray60 = new Color(153, 153, 153);
  }
  static {
    this.Gray50 = new Color(127, 127, 127);
  }
  static {
    this.Gray40 = new Color(102, 102, 102);
  }
  static {
    this.Gray33 = new Color(85, 85, 85);
  }
  static {
    this.Gray20 = new Color(51, 51, 51);
  }
  static {
    this.Gray15 = new Color(38, 38, 38);
  }
  static {
    this.Gray10 = new Color(26, 26, 26);
  }
  constructor(r, g, b, a = 1) {
    this._r = 0;
    this._g = 0;
    this._b = 0;
    this._a = 0;
    this.rgba(r, g, b, a);
  }
  blendWith(other, weight = 0.3) {
    return new Color(blendChannel(this._a, other._a, weight), blendChannel(this._g, other._g, weight), blendChannel(this._b, other._b, weight), blendChannel(this._a, other._a, weight));
  }
  clone() {
    return new Color(this._r, this._g, this._b, this._a);
  }
  rgba(r, g, b, a) {
    this.r(r);
    this.g(g);
    this.b(b);
    this.a(a);
    return this;
  }
  rgb(r, g, b) {
    this.r(r);
    this.g(g);
    this.b(b);
    return this;
  }
  r(value) {
    if (value < 0 || value > 255) {
      throw new Error(`a value of ${value} is invalid [0..255]`);
    }
    this._r = value;
    return this;
  }
  g(value) {
    if (value < 0 || value > 255) {
      throw new Error(`a value of ${value} is invalid [0..255]`);
    }
    this._g = value;
    return this;
  }
  b(value) {
    if (value < 0 || value > 255) {
      throw new Error(`a value of ${value} is invalid [0..255]`);
    }
    this._b = value;
    return this;
  }
  a(value) {
    if (value < 0 || value > 1) {
      throw new Error(`a value of ${value} is invalid [0..1]`);
    }
    this._a = value;
    return this;
  }
  getR() {
    return this._r;
  }
  getG() {
    return this._g;
  }
  getB() {
    return this._b;
  }
  toString() {
    return `rgba(${this._r}, ${this._g}, ${this._b}, ${this._a})`;
  }
}
const BlankTheme = {
  airwayLineColor: Color.Transparent.toString(),
  airwayOutlineColor: Color.Transparent.toString(),
  airwayTextColor: Color.Transparent.toString(),
  airwayTextHaloColor: Color.Transparent.toString(),
  airportTextColor: Color.Transparent.toString(),
  navaidTextColor: Color.Transparent.toString(),
  background: Color.Transparent.toString(),
  textColor: Color.Transparent.toString(),
  textHaloColor: Color.Transparent.toString(),
  textHaloWidth: 0,
  countryBordersColor: Color.Transparent.toString(),
  landUseColor: Color.Transparent.toString(),
  parkColor: Color.Transparent.toString(),
  railColor: Color.Transparent.toString(),
  roadColor: Color.Transparent.toString(),
  stateBordersColor: Color.Transparent.toString(),
  waterColor: Color.Transparent.toString(),
  buildingColor: Color.Transparent.toString(),
  pavementColor: Color.Transparent.toString(),
  pavementClosedColor: Color.Transparent.toString(),
  pavementOutlineColor: Color.Transparent.toString(),
  runwayColor: Color.Transparent.toString(),
  runwayClosedColor: Color.Transparent.toString(),
  runwayLengthColor: Color.Transparent.toString(),
  runwayLineColor: Color.Transparent.toString(),
  hotspotColor: Color.Transparent.toString(),
  hotspotOutlineColor: Color.Transparent.toString(),
  paintedLineColor: Color.Transparent.toString()
};
const DarkTheme = {
  // Text labels
  textColor: Color.Gray75.toString(),
  textHaloColor: Color.Gray10.toString(),
  textHaloWidth: 1.5,
  airportTextColor: Color.Gray91.toString(),
  navaidTextColor: Color.Gray91.toString(),
  // Airways
  airwayLineColor: Color.Gray10.toString(),
  airwayOutlineColor: Color.Transparent.toString(),
  airwayTextColor: Color.Gray90.toString(),
  airwayTextHaloColor: Color.Gray10.toString(),
  // Basemap
  background: Color.Gray40.toString(),
  countryBordersColor: Color.Black.toString(),
  landUseColor: Color.Gray33.toString(),
  parkColor: Color.Transparent.toString(),
  railColor: Color.Gray20.toString(),
  roadColor: Color.Gray15.toString(),
  stateBordersColor: Color.Black.toString(),
  waterColor: Color.WaterBlue.toString(),
  // SafeTaxi
  buildingColor: Color.Brown.toString(),
  pavementColor: Color.Gray50.toString(),
  pavementClosedColor: Color.Gray33.toString(),
  pavementOutlineColor: Color.Gray60.toString(),
  runwayColor: Color.Gray66.toString(),
  runwayClosedColor: Color.Black.toString(),
  runwayLengthColor: Color.Gray90.toString(),
  runwayLineColor: Color.White.toString(),
  hotspotColor: Color.RedBrown.clone().a(0.25).toString(),
  hotspotOutlineColor: Color.RedBrown.toString(),
  paintedLineColor: Color.Yellow.toString()
};
const GreenTheme = {
  airportTextColor: Color.Gray91.toString(),
  navaidTextColor: Color.Gray91.toString(),
  airwayLineColor: Color.Black.toString(),
  airwayOutlineColor: Color.Black.toString(),
  airwayTextColor: Color.Gray10.toString(),
  airwayTextHaloColor: Color.Beige.toString(),
  background: Color.Beige.toString(),
  textColor: Color.Gray40.toString(),
  textHaloColor: Color.Beige.toString(),
  textHaloWidth: 1,
  countryBordersColor: Color.Black.toString(),
  landUseColor: Color.PaleYellow.toString(),
  parkColor: Color.Transparent.toString(),
  railColor: Color.Gray40.toString(),
  roadColor: Color.PaleGreen.toString(),
  stateBordersColor: Color.Black.toString(),
  waterColor: Color.DarkPaleBlue.toString(),
  buildingColor: Color.Brown.toString(),
  pavementColor: Color.Gray66.toString(),
  pavementClosedColor: Color.Gray75.toString(),
  pavementOutlineColor: Color.Gray60.toString(),
  runwayColor: Color.Gray50.toString(),
  runwayClosedColor: Color.Black.toString(),
  runwayLengthColor: Color.Gray10.toString(),
  runwayLineColor: Color.White.toString(),
  hotspotColor: Color.RedBrown.clone().a(0.25).toString(),
  hotspotOutlineColor: Color.RedBrown.toString(),
  paintedLineColor: Color.Yellow.toString()
};
const OpenStreetMapTheme = {
  airportTextColor: Color.Gray91.toString(),
  navaidTextColor: Color.Gray91.toString(),
  airwayLineColor: Color.Black.toString(),
  airwayOutlineColor: Color.White.toString(),
  airwayTextColor: Color.Black.toString(),
  airwayTextHaloColor: Color.White.toString(),
  background: Color.Gray60.toString(),
  textColor: Color.Gray10.toString(),
  textHaloColor: Color.White.toString(),
  textHaloWidth: 1,
  countryBordersColor: Color.Transparent.toString(),
  landUseColor: Color.Transparent.toString(),
  parkColor: Color.Transparent.toString(),
  railColor: Color.Transparent.toString(),
  roadColor: Color.Transparent.toString(),
  stateBordersColor: Color.Transparent.toString(),
  waterColor: Color.Transparent.toString(),
  buildingColor: Color.Brown.toString(),
  pavementColor: Color.Gray66.toString(),
  pavementClosedColor: Color.Gray75.toString(),
  pavementOutlineColor: Color.Gray60.toString(),
  runwayColor: Color.Gray50.toString(),
  runwayClosedColor: Color.Black.toString(),
  runwayLengthColor: Color.Black.toString(),
  runwayLineColor: Color.White.toString(),
  hotspotColor: Color.RedBrown.clone().a(0.25).toString(),
  hotspotOutlineColor: Color.RedBrown.toString(),
  paintedLineColor: Color.Yellow.toString()
};
const SatelliteTheme = {
  airportTextColor: Color.Gray91.toString(),
  navaidTextColor: Color.Gray91.toString(),
  airwayLineColor: Color.White.toString(),
  airwayOutlineColor: Color.Black.toString(),
  airwayTextColor: Color.White.toString(),
  airwayTextHaloColor: Color.Black.toString(),
  background: Color.White.toString(),
  textColor: Color.White.toString(),
  textHaloColor: Color.Black.toString(),
  textHaloWidth: 1,
  countryBordersColor: Color.White.toString(),
  landUseColor: Color.Transparent.toString(),
  parkColor: Color.Transparent.toString(),
  railColor: Color.Transparent.toString(),
  roadColor: Color.Transparent.toString(),
  stateBordersColor: Color.White.toString(),
  waterColor: Color.Transparent.toString(),
  buildingColor: Color.Brown.toString(),
  pavementColor: Color.Gray66.toString(),
  pavementClosedColor: Color.Gray75.toString(),
  pavementOutlineColor: Color.Gray60.toString(),
  runwayColor: Color.Gray50.toString(),
  runwayClosedColor: Color.Black.toString(),
  runwayLengthColor: Color.White.toString(),
  runwayLineColor: Color.White.toString(),
  hotspotColor: Color.RedBrown.clone().a(0.25).toString(),
  hotspotOutlineColor: Color.RedBrown.toString(),
  paintedLineColor: Color.Yellow.toString()
};
const TopographyTheme = {
  // Text labels
  textColor: Color.Gray75.toString(),
  textHaloColor: Color.Gray10.toString(),
  textHaloWidth: 1.5,
  airportTextColor: Color.Gray91.toString(),
  navaidTextColor: Color.Gray91.toString(),
  // Airways
  airwayLineColor: Color.Gray10.toString(),
  airwayOutlineColor: Color.Transparent.toString(),
  airwayTextColor: Color.Gray90.toString(),
  airwayTextHaloColor: Color.Gray10.toString(),
  // Basemap
  background: Color.Gray40.toString(),
  countryBordersColor: Color.Black.toString(),
  landUseColor: Color.DarkBeige.toString(),
  parkColor: Color.Transparent.toString(),
  railColor: Color.Gray20.toString(),
  roadColor: Color.Gray15.toString(),
  stateBordersColor: Color.Black.toString(),
  waterColor: Color.WaterBlue.toString(),
  // SafeTaxi
  buildingColor: Color.Brown.toString(),
  pavementColor: Color.Gray50.toString(),
  pavementClosedColor: Color.Gray33.toString(),
  pavementOutlineColor: Color.Gray60.toString(),
  runwayColor: Color.Gray66.toString(),
  runwayClosedColor: Color.Black.toString(),
  runwayLengthColor: Color.Gray90.toString(),
  runwayLineColor: Color.White.toString(),
  hotspotColor: Color.RedBrown.clone().a(0.25).toString(),
  hotspotOutlineColor: Color.RedBrown.toString(),
  paintedLineColor: Color.Yellow.toString()
};
const WhiteTheme = {
  airportTextColor: Color.Gray91.toString(),
  navaidTextColor: Color.Gray91.toString(),
  airwayLineColor: Color.Black.toString(),
  airwayOutlineColor: Color.Black.toString(),
  airwayTextColor: Color.Black.toString(),
  airwayTextHaloColor: Color.White.toString(),
  background: Color.White.toString(),
  textColor: Color.Gray40.toString(),
  textHaloColor: Color.White.toString(),
  textHaloWidth: 1,
  countryBordersColor: Color.Black.toString(),
  landUseColor: Color.PaleYellow.toString(),
  parkColor: Color.Transparent.toString(),
  railColor: Color.Gray87.toString(),
  roadColor: Color.MorePaleWhite.toString(),
  stateBordersColor: Color.Black.toString(),
  waterColor: Color.PaleBlue.toString(),
  buildingColor: Color.Brown.toString(),
  pavementColor: Color.Gray66.toString(),
  pavementClosedColor: Color.Gray75.toString(),
  pavementOutlineColor: Color.Gray60.toString(),
  runwayColor: Color.Gray50.toString(),
  runwayClosedColor: Color.Black.toString(),
  runwayLengthColor: Color.Gray10.toString(),
  runwayLineColor: Color.White.toString(),
  hotspotColor: Color.RedBrown.clone().a(0.25).toString(),
  hotspotOutlineColor: Color.RedBrown.toString(),
  paintedLineColor: Color.Yellow.toString()
};
let MapThemeHandler = /*#__PURE__*/(() => {
  class MapThemeHandler {
    constructor() {
      this.attributionBackgroundColor = signal(Color.Black.toString());
      this.attributionLinkColor = signal(Color.PaleBlue.toString());
      this.attributionTextColor = signal(Color.White.toString());
      this.airportTextColor = signal(Color.Transparent.toString());
      this.navaidTextColor = signal(Color.Transparent.toString());
      this.airwayLineColor = signal(Color.Transparent.toString());
      this.airwayOutlineColor = signal(Color.Transparent.toString());
      this.airwayTextColor = signal(Color.Transparent.toString());
      this.airwayTextHaloColor = signal(Color.Transparent.toString());
      this.background = signal(Color.Transparent.toString());
      this.textColor = signal(Color.Transparent.toString());
      this.textHaloColor = signal(Color.Transparent.toString());
      this.textHaloWidth = signal(0);
      this.countryBordersColor = signal(Color.Transparent.toString());
      this.landUseColor = signal(Color.Transparent.toString());
      this.parkColor = signal(Color.Transparent.toString());
      this.railColor = signal(Color.Transparent.toString());
      this.roadColor = signal(Color.Transparent.toString());
      this.stateBordersColor = signal(Color.Transparent.toString());
      this.waterColor = signal(Color.Transparent.toString());
      this.buildingColor = signal(Color.Transparent.toString());
      this.pavementColor = signal(Color.Transparent.toString());
      this.pavementClosedColor = signal(Color.Transparent.toString());
      this.pavementOutlineColor = signal(Color.Transparent.toString());
      this.runwayColor = signal(Color.Transparent.toString());
      this.runwayClosedColor = signal(Color.Transparent.toString());
      this.runwayLengthColor = signal(Color.Transparent.toString());
      this.runwayLineColor = signal(Color.Transparent.toString());
      this.hotspotColor = signal(Color.Transparent.toString());
      this.hotspotOutlineColor = signal(Color.Transparent.toString());
      this.paintedLineColor = signal(Color.Transparent.toString());
      this.flightRule = signal(FlightRule.Vfr);
    }
    apply(preset) {
      this.restore(this.get(preset));
    }
    get(preset) {
      let theme;
      switch (preset) {
        case MapThemePreset.Blank:
          theme = BlankTheme;
          break;
        case MapThemePreset.Dark:
          theme = DarkTheme;
          break;
        case MapThemePreset.Green:
          theme = GreenTheme;
          break;
        case MapThemePreset.OpenStreetMap:
          theme = OpenStreetMapTheme;
          break;
        case MapThemePreset.Satellite:
          theme = SatelliteTheme;
          break;
        case MapThemePreset.Topography:
          theme = TopographyTheme;
          break;
        case MapThemePreset.White:
        default:
          theme = WhiteTheme;
          break;
      }
      return cloneDeep(theme);
    }
    save() {
      return {
        attributionBackgroundColor: this.attributionBackgroundColor(),
        attributionLinkColor: this.attributionLinkColor(),
        attributionTextColor: this.attributionTextColor(),
        airportTextColor: this.airportTextColor(),
        navaidTextColor: this.navaidTextColor(),
        airwayLineColor: this.airwayLineColor(),
        airwayOutlineColor: this.airwayOutlineColor(),
        airwayTextColor: this.airwayTextColor(),
        airwayTextHaloColor: this.airwayTextHaloColor(),
        background: this.background(),
        textColor: this.textColor(),
        textHaloColor: this.textHaloColor(),
        textHaloWidth: this.textHaloWidth(),
        countryBordersColor: this.countryBordersColor(),
        landUseColor: this.landUseColor(),
        parkColor: this.parkColor(),
        railColor: this.railColor(),
        roadColor: this.roadColor(),
        stateBordersColor: this.stateBordersColor(),
        waterColor: this.waterColor(),
        buildingColor: this.buildingColor(),
        pavementColor: this.pavementColor(),
        pavementClosedColor: this.pavementClosedColor(),
        pavementOutlineColor: this.pavementOutlineColor(),
        runwayColor: this.runwayColor(),
        runwayClosedColor: this.runwayClosedColor(),
        runwayLengthColor: this.runwayLengthColor(),
        runwayLineColor: this.runwayLineColor(),
        hotspotColor: this.hotspotColor(),
        hotspotOutlineColor: this.hotspotOutlineColor(),
        paintedLineColor: this.paintedLineColor(),
        flightRule: this.flightRule()
      };
    }
    // eslint-disable-next-line complexity
    restore(values) {
      if (values?.attributionBackgroundColor != null) {
        this.attributionBackgroundColor.set(values.attributionBackgroundColor);
      }
      if (values?.attributionLinkColor != null) {
        this.attributionLinkColor.set(values.attributionLinkColor);
      }
      if (values?.attributionTextColor != null) {
        this.attributionTextColor.set(values.attributionTextColor);
      }
      if (values?.airportTextColor != null) {
        this.airportTextColor.set(values.airportTextColor);
      }
      if (values?.navaidTextColor != null) {
        this.navaidTextColor.set(values.navaidTextColor);
      }
      if (values?.airwayLineColor != null) {
        this.airwayLineColor.set(values.airwayLineColor);
      }
      if (values?.airwayOutlineColor != null) {
        this.airwayOutlineColor.set(values.airwayOutlineColor);
      }
      if (values?.airwayTextColor != null) {
        this.airwayTextColor.set(values.airwayTextColor);
      }
      if (values?.airwayTextHaloColor != null) {
        this.airwayTextHaloColor.set(values.airwayTextHaloColor);
      }
      if (values?.background != null) {
        this.background.set(values.background);
      }
      if (values?.textColor != null) {
        this.textColor.set(values.textColor);
      }
      if (values?.textHaloColor != null) {
        this.textHaloColor.set(values.textHaloColor);
      }
      if (values?.textHaloWidth != null) {
        this.textHaloWidth.set(values.textHaloWidth);
      }
      if (values?.countryBordersColor != null) {
        this.countryBordersColor.set(values.countryBordersColor);
      }
      if (values?.landUseColor != null) {
        this.landUseColor.set(values.landUseColor);
      }
      if (values?.parkColor != null) {
        this.parkColor.set(values.parkColor);
      }
      if (values?.railColor != null) {
        this.railColor.set(values.railColor);
      }
      if (values?.roadColor != null) {
        this.roadColor.set(values.roadColor);
      }
      if (values?.stateBordersColor != null) {
        this.stateBordersColor.set(values.stateBordersColor);
      }
      if (values?.waterColor != null) {
        this.waterColor.set(values.waterColor);
      }
      if (values?.buildingColor != null) {
        this.buildingColor.set(values.buildingColor);
      }
      if (values?.pavementColor != null) {
        this.pavementColor.set(values.pavementColor);
      }
      if (values?.pavementClosedColor != null) {
        this.pavementClosedColor.set(values.pavementClosedColor);
      }
      if (values?.pavementOutlineColor != null) {
        this.pavementOutlineColor.set(values.pavementOutlineColor);
      }
      if (values?.runwayColor != null) {
        this.runwayColor.set(values.runwayColor);
      }
      if (values?.runwayClosedColor != null) {
        this.runwayClosedColor.set(values.runwayClosedColor);
      }
      if (values?.runwayLengthColor != null) {
        this.runwayLengthColor.set(values.runwayLengthColor);
      }
      if (values?.runwayLineColor != null) {
        this.runwayLineColor.set(values.runwayLineColor);
      }
      if (values?.hotspotColor != null) {
        this.hotspotColor.set(values.hotspotColor);
      }
      if (values?.hotspotOutlineColor != null) {
        this.hotspotOutlineColor.set(values.hotspotOutlineColor);
      }
      if (values?.paintedLineColor != null) {
        this.paintedLineColor.set(values.paintedLineColor);
      }
      if (values?.flightRule != null) {
        this.flightRule.set(values.flightRule);
      }
    }
    static {
      this.ɵfac = function MapThemeHandler_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapThemeHandler)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapThemeHandler,
        factory: MapThemeHandler.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapThemeHandler;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let MapTracker = /*#__PURE__*/(() => {
  class MapTracker {
    constructor() {
      this.mapConfig = inject(MapConfigHandler);
      this.moduleConfig = inject(AVC_MAPS_DISPLAY_CONFIG);
      this.state = inject(MapStateHandler);
      this.theme = inject(MapThemeHandler);
      this.container = document.createElement('div');
      this.map = signal(undefined, {
        equal: (a, b) => {
          if (a != null && b != null) {
            return a.getId() == b.getId();
          }
          return a == null && b == null;
        }
      });
      this.updateAttributions = () => {
        // grab all the variables
        const baseMap = this.mapConfig.content.baseMap.visible();
        const openStreetMap = this.mapConfig.content.openStreetMap.visible();
        const groundSatellite = this.mapConfig.content.groundSatellite.visible();
        const backgroundColor = this.theme.attributionBackgroundColor();
        const linkColor = this.theme.attributionLinkColor();
        const textColor = this.theme.attributionTextColor();
        // add the elements
        if (openStreetMap || baseMap || groundSatellite) {
          // create the containing div
          if (this.attributionsElement != null && this.parentContainer?.contains(this.attributionsElement) === true) {
            this.parentContainer.removeChild(this.attributionsElement);
          }
          this.attributionsElement = document.createElement('div');
          this.attributionsElement.classList.add('avc-map-attributions');
          this.attributionsElement.style.cssText = `
        position: absolute;
        bottom: 0;
        left: 0;
        font-size: .625rem;
        padding: 0 .625rem;
        background-color: ${backgroundColor};
        color: ${textColor};
      `;
          this.parentContainer?.appendChild(this.attributionsElement);
          // add the attributions
          if (openStreetMap || baseMap) {
            const attrib = document.createElement('span');
            attrib.innerHTML = '&copy; ';
            const link = document.createElement('a');
            link.href = 'https://www.openstreetmap.org/copyright';
            link.text = 'OpenStreetMap';
            link.style.cssText = `
          color: ${linkColor};
        `;
            attrib.appendChild(link);
            this.attributionsElement.appendChild(attrib);
          }
          if (groundSatellite) {
            if (this.attributionsElement.childElementCount > 0) {
              const comma = document.createElement('span');
              comma.innerText = ', ';
              this.attributionsElement.appendChild(comma);
            }
            const currentYear = new Date().getFullYear();
            const attrib = document.createElement('span');
            attrib.innerHTML = `&copy; ${currentYear} Maxar Technologies Inc.`;
            this.attributionsElement.appendChild(attrib);
          }
        }
        // remove the element after it's not needed
        else if (this.attributionsElement != null) {
          this.parentContainer?.removeChild(this.attributionsElement);
          this.attributionsElement = undefined;
        }
      };
      effect(this.updateAttributions);
    }
    attachMap(container) {
      if (container == null) {
        return;
      }
      // Set the size of the map container
      this.container.style.width = '100%';
      this.container.style.height = '100%';
      // Append the map container before creating the map
      // so that it can figure out the correct size and
      // minimize visual changes
      container.appendChild(this.container);
      this.parentContainer = container;
      // Set 'contain: layout' on the container.
      // This helps the container actually contain everything
      // and properly position stuff like the attributions.
      this.parentContainer.style.contain = 'layout';
      // attributions is appended to the parent to control z-index
      this.updateAttributions();
      // Create the map if it doesn't exist
      if (this._map == null) {
        this._map = new MapWrapper(new ML__default.Map({
          container: this.container,
          style: {
            layers: [],
            sources: {},
            version: 8,
            glyphs: `${this.moduleConfig.mapAssetsPath}/fonts/{fontstack}/{range}.pbf`,
            transition: {
              delay: 0,
              duration: 0
            }
          },
          center: [0, 0],
          zoom: 2,
          minZoom: 2,
          maxZoom: 18,
          // default fade is 300ms so this is faster
          fadeDuration: 200,
          // Disabling rotate and tilt interactions
          dragRotate: false,
          minPitch: 0,
          maxPitch: 0,
          pitchWithRotate: false,
          touchPitch: false,
          // We have a custom click handler
          doubleClickZoom: false,
          // Enable refreshExpiredTiles for internet traffic
          refreshExpiredTiles: true
        }));
        this._map.once('load', () => {
          this.container.style.width = '100%';
          this.container.style.height = '100%';
          this.state.connectMap(this._map);
          this.map.set(this._map);
          // Sometimes the map container changes sizes before the monitoring for container
          // size changes is set up so just force a resize when the map is done loading.
          this._map?.resize();
        });
        this._map.once('remove', () => {
          if (this.parentContainer != null) {
            this.parentContainer.removeChild(this.container);
          }
          this.parentContainer = undefined;
          this.map.set(undefined);
        });
      }
    }
    static {
      this.ɵfac = function MapTracker_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapTracker)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapTracker,
        factory: MapTracker.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapTracker;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let MapInfo = /*#__PURE__*/(() => {
  class MapInfo {
    constructor() {
      this.charts = new ChartsInfo();
      this.content = new ContentInfo();
      this.overlay = new OverlayInfo();
      this.map = inject(MapTracker).map;
    }
    getMapImageData() {
      const map = this.map();
      return new Promise((resolve, reject) => {
        if (map == null) {
          return resolve(null);
        }
        map.once('render', () => {
          try {
            const canvas = map.getCanvas();
            return resolve(canvas.toDataURL('image/png'));
          } catch (error) {
            return reject(error);
          }
        });
        map.triggerRepaint();
      });
    }
    static {
      this.ɵfac = function MapInfo_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapInfo)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapInfo,
        factory: MapInfo.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapInfo;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let MapAnimationHandler = /*#__PURE__*/(() => {
  class MapAnimationHandler {
    constructor() {
      this.mapConfig = inject(MapConfigHandler);
      this.mapInfo = inject(MapInfo);
      this.setActive = signal(false);
      this.active = this.setActive.asReadonly();
      this.setProgress = signal(0);
      this.progress = this.setProgress.asReadonly();
      this.setTime = signal(getUnixTime(new Date()));
      this.time = this.setTime.asReadonly();
      this.date = computed(() => fromUnixTime(this.time()), {
        equal: (a, b) => isEqual$1(a, b)
      });
      this.options = signal(undefined);
      this.onTimer = () => {
        this.timer = undefined;
        const options = this.options();
        if (options == null) {
          return;
        }
        const time = this.setTime();
        const newTime = time + Math.floor(options.step / 1000);
        if (newTime > options.maximumTime) {
          this.startRepeatTimer(options.delayBeforeRepeat);
          this.setProgress.set(1); // reset to the latest one
        } else {
          this.setTime.set(newTime);
          this.startTimer(options.period);
          // update progress
          const progress = (newTime - options.minimumTime) / (options.maximumTime - options.minimumTime);
          this.setProgress.set(progress);
        }
      };
      this.onRepeatTimer = () => {
        this.repeatTimer = undefined;
        const options = this.options();
        if (options == null) {
          return;
        }
        this.setTime.set(options.minimumTime);
        this.startTimer(options.period);
      };
      /**
       * An array of tuples that indicates that ranges where data is expected
       * to exist, whether it's been loaded or not. E.g., [[123, 223]] indicates
       * data is expected to exist between timestamp 123 and timestamp 223.
       * @units UTC epoch in seconds
       */
      this.availableRange = {
        airmet: signal([]),
        clouds: signal([]),
        graphicalAirmet: signal([]),
        icing: signal([]),
        lightning: signal([]),
        radarBase: signal([]),
        radarComposite: signal([]),
        sigmet: signal([]),
        stormCells: signal([]),
        temps: signal([]),
        winds: signal([])
      };
      this.available = {
        airmet: createAvailableSignal(this.time, this.availableRange.airmet),
        clouds: createAvailableSignal(this.time, this.availableRange.clouds),
        graphicalAirmet: createAvailableSignal(this.time, this.availableRange.graphicalAirmet),
        icing: createAvailableSignal(this.time, this.availableRange.icing),
        lightning: createAvailableSignal(this.time, this.availableRange.lightning),
        radarBase: createAvailableSignal(this.time, this.availableRange.radarBase),
        radarComposite: createAvailableSignal(this.time, this.availableRange.radarComposite),
        sigmet: createAvailableSignal(this.time, this.availableRange.sigmet),
        stormCells: createAvailableSignal(this.time, this.availableRange.stormCells),
        temps: createAvailableSignal(this.time, this.availableRange.temps),
        winds: createAvailableSignal(this.time, this.availableRange.winds)
      };
      /**
       * An array of tuples that indicates the ranges that have loaded data.
       * E.g., [[123, 223]] indicates that all data between timestamp 123 and
       * timestamp 223 is loaded.
       * @units UTC epoch in seconds
       */
      this.loadedRange = {
        airmet: signal([]),
        clouds: signal([]),
        graphicalAirmet: signal([]),
        icing: signal([]),
        lightning: signal([]),
        radarBase: signal([]),
        radarComposite: signal([]),
        sigmet: signal([]),
        stormCells: signal([]),
        temps: signal([]),
        winds: signal([])
      };
      this.loaded = {
        airmet: createLoadedSignal(this.time, this.loadedRange.airmet),
        clouds: createLoadedSignal(this.time, this.loadedRange.clouds),
        graphicalAirmet: createLoadedSignal(this.time, this.loadedRange.graphicalAirmet),
        icing: createLoadedSignal(this.time, this.loadedRange.icing),
        lightning: createLoadedSignal(this.time, this.loadedRange.lightning),
        radarBase: createLoadedSignal(this.time, this.loadedRange.radarBase),
        radarComposite: createLoadedSignal(this.time, this.loadedRange.radarComposite),
        sigmet: createLoadedSignal(this.time, this.loadedRange.sigmet),
        stormCells: createLoadedSignal(this.time, this.loadedRange.stormCells),
        temps: createLoadedSignal(this.time, this.loadedRange.temps),
        winds: createLoadedSignal(this.time, this.loadedRange.winds)
      };
      this.applicable = {
        airmet: createApplicableSignal(this.mapInfo.overlay.airmetSigmet.airmetAvailable, this.mapConfig.overlay.airmetSigmet.visible),
        clouds: createApplicableSignal(this.mapInfo.overlay.clouds.available, computed(() => this.mapConfig.overlay.radar.visible() && this.mapConfig.overlay.radar.infraredClouds())),
        graphicalAirmet: createApplicableSignal(this.mapInfo.overlay.airmetSigmet.graphicalAirmetAvailable, this.mapConfig.overlay.airmetSigmet.visible),
        icing: createApplicableSignal(this.mapInfo.overlay.icing.available, this.mapConfig.overlay.icing.visible),
        lightning: createApplicableSignal(this.mapInfo.overlay.lightning.available, computed(() => this.mapConfig.overlay.radar.visible() && this.mapConfig.overlay.radar.lightning())),
        radarBase: createApplicableSignal(this.mapInfo.overlay.radar.baseAvailable, computed(() => this.mapConfig.overlay.radar.visible() && this.mapConfig.overlay.radar.type() === RadarType.Base)),
        radarComposite: createApplicableSignal(this.mapInfo.overlay.radar.compositeAvailable, computed(() => this.mapConfig.overlay.radar.visible() && this.mapConfig.overlay.radar.type() === RadarType.Composite)),
        sigmet: createApplicableSignal(this.mapInfo.overlay.airmetSigmet.sigmetAvailable, this.mapConfig.overlay.airmetSigmet.visible),
        stormCells: createApplicableSignal(this.mapInfo.overlay.stormCells.available, computed(() => this.mapConfig.overlay.radar.visible() && this.mapConfig.overlay.radar.stormCells())),
        temps: createApplicableSignal(this.mapInfo.overlay.tiledTemperature.available, this.mapConfig.overlay.tiledTemperature.visible),
        winds: createApplicableSignal(this.mapInfo.overlay.windAnimation.available, this.mapConfig.overlay.windAnimation.visible)
      };
      this.status = {
        airmet: createStatusSignal(this.applicable.airmet, this.available.airmet, this.loaded.airmet),
        clouds: createStatusSignal(this.applicable.clouds, this.available.clouds, this.loaded.clouds),
        graphicalAirmet: createStatusSignal(this.applicable.graphicalAirmet, this.available.graphicalAirmet, this.loaded.graphicalAirmet),
        icing: createStatusSignal(this.applicable.icing, this.available.icing, this.loaded.icing),
        lightning: createStatusSignal(this.applicable.lightning, this.available.lightning, this.loaded.lightning),
        radarBase: createStatusSignal(this.applicable.radarBase, this.available.radarBase, this.loaded.radarBase),
        radarComposite: createStatusSignal(this.applicable.radarComposite, this.available.radarComposite, this.loaded.radarComposite),
        sigmet: createStatusSignal(this.applicable.sigmet, this.available.sigmet, this.loaded.sigmet),
        stormCells: createStatusSignal(this.applicable.stormCells, this.available.stormCells, this.loaded.stormCells),
        temps: createStatusSignal(this.applicable.temps, this.available.temps, this.loaded.temps),
        winds: createStatusSignal(this.applicable.winds, this.available.winds, this.loaded.winds)
      };
    }
    configure(options) {
      untracked(() => {
        const isInvalid = x => typeof x !== 'number' || isNaN(x) || !isFinite(x);
        if (isInvalid(options.now)) {
          throw new Error('invalid now');
        }
        if (isInvalid(options.maximumTime)) {
          throw new Error('invalid maximumTime');
        }
        if (isInvalid(options.minimumTime)) {
          throw new Error('invalid minimumTime');
        }
        if (isInvalid(options.step) || options.step <= 0) {
          throw new Error('invalid step');
        }
        if (isInvalid(options.period) || options.period <= 0) {
          throw new Error('invalid period');
        }
        const oldOptions = this.options();
        if (oldOptions != null && this.active() && oldOptions.period !== options.period) {
          window.clearTimeout(this.timer);
          this.timer = window.setTimeout(this.onTimer, options.period);
        }
        this.options.set(options);
      });
    }
    seek(dateTime) {
      untracked(() => {
        this.stopRepeatTimer();
        const time = isDate(dateTime) ? getUnixTime(dateTime) : dateTime;
        this.setTime.set(time);
        const options = this.options();
        if (options != null) {
          const progress = (time - options.minimumTime) / (options.maximumTime - options.minimumTime);
          this.setProgress.set(progress);
        }
      });
    }
    start() {
      untracked(() => {
        const options = this.options();
        if (options == null) {
          throw new Error('animation options have not been configured');
        }
        this.startTimer(options.period);
        this.setActive.set(true);
      });
    }
    stop() {
      untracked(() => {
        this.stopTimer();
        this.stopRepeatTimer();
        this.setActive.set(false);
      });
    }
    startTimer(period) {
      if (this.timer == null) {
        this.timer = window.setTimeout(this.onTimer, period);
      }
    }
    stopTimer() {
      if (this.timer != null) {
        window.clearTimeout(this.timer);
        this.timer = undefined;
      }
    }
    startRepeatTimer(delay) {
      if (this.repeatTimer == null) {
        this.repeatTimer = window.setTimeout(this.onRepeatTimer, delay);
      }
    }
    stopRepeatTimer() {
      if (this.repeatTimer != null) {
        window.clearTimeout(this.repeatTimer);
        this.repeatTimer = undefined;
      }
    }
    static {
      this.ɵfac = function MapAnimationHandler_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapAnimationHandler)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapAnimationHandler,
        factory: MapAnimationHandler.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapAnimationHandler;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let MapDataHandler = /*#__PURE__*/(() => {
  class MapDataHandler {
    constructor() {
      this.flights = signal([]);
      this.procedures = signal([]);
      this.routes = signal([]);
      this.waypoints = signal([]);
    }
    save() {
      return {
        flights: cloneDeep(this.flights()),
        procedures: cloneDeep(this.procedures()),
        routes: cloneDeep(this.routes()),
        waypoints: cloneDeep(this.waypoints())
      };
    }
    restore(state) {
      if (state.flights != null) {
        this.flights.set(cloneDeep(state.flights));
      }
      if (state.procedures != null) {
        this.procedures.set(cloneDeep(state.procedures));
      }
      if (state.routes != null) {
        this.routes.set(cloneDeep(state.routes));
      }
      if (state.waypoints != null) {
        this.waypoints.set(cloneDeep(state.waypoints));
      }
    }
    static {
      this.ɵfac = function MapDataHandler_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapDataHandler)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapDataHandler,
        factory: MapDataHandler.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapDataHandler;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var PerformanceMode = /*#__PURE__*/function (PerformanceMode) {
  /**
   * Turn off performance measurements
   */
  PerformanceMode[PerformanceMode["Off"] = 0] = "Off";
  /**
   * Measures the raw frames per second on the screen.
   * This really only measures how long it takes for already cached data
   * to be drawn to the screen. This will not account for how long it takes
   * tiles to be downloaded or processed.
   */
  PerformanceMode[PerformanceMode["FramesPerSecond"] = 1] = "FramesPerSecond";
  /**
   * Measures the time each source is in the 'not loaded' state.
   */
  PerformanceMode[PerformanceMode["SourceLoading"] = 2] = "SourceLoading";
  return PerformanceMode;
}(PerformanceMode || {});
class MapDebug {
  constructor(mapService) {
    this.mapService = mapService;
    /** @private @deprecated debug or development use only! */
    this.map = computed(() => this.mapService.map()?.getMap());
    this.showTileBoundaries = signal(false);
    this.performanceMode = signal(PerformanceMode.Off);
    this.fpsReport = signal(undefined);
    this.sourceLoadReports = signal([]);
  }
}
let MapHighlight = /*#__PURE__*/(() => {
  class MapHighlight {
    constructor() {
      this.airmetSigmet = signal([]);
      this.airport = signal([]);
      this.airspace = signal([]);
      this.airway = signal([]);
      this.fuelPrice = signal([]);
      this.internetTraffic = signal([]);
      this.ndb = signal([]);
      this.notam = signal([]);
      this.obstacle = signal([]);
      this.pirep = signal([]);
      this.stormCell = signal([]);
      this.tfr = signal([]);
      this.vor = signal([]);
      this.weatherReport = signal([]);
      this.wpt = signal([]);
      this.location = signal([]);
    }
    static {
      this.ɵfac = function MapHighlight_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapHighlight)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapHighlight,
        factory: MapHighlight.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapHighlight;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var MapIcon = /*#__PURE__*/function (MapIcon) {
  MapIcon["Invalid"] = "Invalid";
  // Runway
  MapIcon["Runway"] = "Runway";
  MapIcon["Runway_Billboard"] = "Runway_Billboard";
  MapIcon["Runway_NOTAM_Billboard"] = "Runway_NOTAM_Billboard";
  MapIcon["Runway_NOTAM_Closed"] = "Runway_NOTAM_Closed";
  MapIcon["Runway_NOTAM_Conditional"] = "Runway_NOTAM_Conditional";
  MapIcon["Runway_NOTAM_Highlight_Billboard"] = "Runway_NOTAM_Highlight_Billboard";
  // Airspace
  MapIcon["Airspace_Air_Defense_Identification_Zone"] = "Airspace_Air_Defense_Identification_Zone";
  MapIcon["Airspace_Alert_Area"] = "Airspace_Alert_Area";
  MapIcon["Airspace_ARTCC_FIR"] = "Airspace_ARTCC_FIR";
  MapIcon["Airspace_Nature_Conservation_Area"] = "Airspace_Nature_Conservation_Area";
  MapIcon["Airspace_Military_Operations_Area"] = "Airspace_Military_Operations_Area";
  MapIcon["Airspace_Restricted_Area"] = "Airspace_Restricted_Area";
  MapIcon["Airspace_A_2"] = "Airspace_A_2";
  MapIcon["Airspace_ADVS_2"] = "Airspace_ADVS_2";
  MapIcon["Airspace_AERO_2"] = "Airspace_AERO_2";
  MapIcon["Airspace_ALRT_2"] = "Airspace_ALRT_2";
  MapIcon["Airspace_ATZ_2"] = "Airspace_ATZ_2";
  MapIcon["Airspace_B_2"] = "Airspace_B_2";
  MapIcon["Airspace_C_2"] = "Airspace_C_2";
  MapIcon["Airspace_CBA_2"] = "Airspace_CBA_2";
  MapIcon["Airspace_CFZ_2"] = "Airspace_CFZ_2";
  MapIcon["Airspace_CTR_2"] = "Airspace_CTR_2";
  MapIcon["Airspace_D_2"] = "Airspace_D_2";
  MapIcon["Airspace_E_2"] = "Airspace_E_2";
  MapIcon["Airspace_FIZ_2"] = "Airspace_FIZ_2";
  MapIcon["Airspace_FMC_2"] = "Airspace_FMC_2";
  MapIcon["Airspace_G_2"] = "Airspace_G_2";
  MapIcon["Airspace_M_2"] = "Airspace_M_2";
  MapIcon["Airspace_MATZ_2"] = "Airspace_MATZ_2";
  MapIcon["Airspace_MBZ_2"] = "Airspace_MBZ_2";
  MapIcon["Airspace_MCTR_2"] = "Airspace_MCTR_2";
  MapIcon["Airspace_MODC_2"] = "Airspace_MODC_2";
  MapIcon["Airspace_MTMA_2"] = "Airspace_MTMA_2";
  MapIcon["Airspace_NSA_2"] = "Airspace_NSA_2";
  MapIcon["Airspace_P_2"] = "Airspace_P_2";
  MapIcon["Airspace_PJA_2"] = "Airspace_PJA_2";
  MapIcon["Airspace_R_2"] = "Airspace_R_2";
  MapIcon["Airspace_RMZ_2"] = "Airspace_RMZ_2";
  MapIcon["Airspace_SR_2"] = "Airspace_SR_2";
  MapIcon["Airspace_T_2"] = "Airspace_T_2";
  MapIcon["Airspace_TFR_2"] = "Airspace_TFR_2";
  MapIcon["Airspace_TIA_2"] = "Airspace_TIA_2";
  MapIcon["Airspace_TIZ_2"] = "Airspace_TIZ_2";
  MapIcon["Airspace_TMA_2"] = "Airspace_TMA_2";
  MapIcon["Airspace_TMZ_2"] = "Airspace_TMZ_2";
  MapIcon["Airspace_W_2"] = "Airspace_W_2";
  MapIcon["Airspace_A_3"] = "Airspace_A_3";
  MapIcon["Airspace_ADVS_3"] = "Airspace_ADVS_3";
  MapIcon["Airspace_AERO_3"] = "Airspace_AERO_3";
  MapIcon["Airspace_ALRT_3"] = "Airspace_ALRT_3";
  MapIcon["Airspace_ATZ_3"] = "Airspace_ATZ_3";
  MapIcon["Airspace_B_3"] = "Airspace_B_3";
  MapIcon["Airspace_C_3"] = "Airspace_C_3";
  MapIcon["Airspace_CBA_3"] = "Airspace_CBA_3";
  MapIcon["Airspace_CFZ_3"] = "Airspace_CFZ_3";
  MapIcon["Airspace_CTR_3"] = "Airspace_CTR_3";
  MapIcon["Airspace_D_3"] = "Airspace_D_3";
  MapIcon["Airspace_E_3"] = "Airspace_E_3";
  MapIcon["Airspace_FIZ_3"] = "Airspace_FIZ_3";
  MapIcon["Airspace_FMC_3"] = "Airspace_FMC_3";
  MapIcon["Airspace_G_3"] = "Airspace_G_3";
  MapIcon["Airspace_M_3"] = "Airspace_M_3";
  MapIcon["Airspace_MATZ_3"] = "Airspace_MATZ_3";
  MapIcon["Airspace_MBZ_3"] = "Airspace_MBZ_3";
  MapIcon["Airspace_MCTR_3"] = "Airspace_MCTR_3";
  MapIcon["Airspace_MODC_3"] = "Airspace_MODC_3";
  MapIcon["Airspace_MTMA_3"] = "Airspace_MTMA_3";
  MapIcon["Airspace_NSA_3"] = "Airspace_NSA_3";
  MapIcon["Airspace_P_3"] = "Airspace_P_3";
  MapIcon["Airspace_PJA_3"] = "Airspace_PJA_3";
  MapIcon["Airspace_R_3"] = "Airspace_R_3";
  MapIcon["Airspace_RMZ_3"] = "Airspace_RMZ_3";
  MapIcon["Airspace_SR_3"] = "Airspace_SR_3";
  MapIcon["Airspace_T_3"] = "Airspace_T_3";
  MapIcon["Airspace_TFR_3"] = "Airspace_TFR_3";
  MapIcon["Airspace_TIA_3"] = "Airspace_TIA_3";
  MapIcon["Airspace_TIZ_3"] = "Airspace_TIZ_3";
  MapIcon["Airspace_TMA_3"] = "Airspace_TMA_3";
  MapIcon["Airspace_TMZ_3"] = "Airspace_TMZ_3";
  MapIcon["Airspace_W_3"] = "Airspace_W_3";
  // Airway
  MapIcon["Airway_Marker"] = "Airway_Marker";
  MapIcon["Airway_Marker_RNAV"] = "Airway_Marker_RNAV";
  MapIcon["Airway_Marker_West"] = "Airway_Marker_West";
  MapIcon["Airway_Marker_East"] = "Airway_Marker_East";
  MapIcon["Airway_Marker_East_West"] = "Airway_Marker_East_West";
  MapIcon["Airway_Marker_West_RNAV"] = "Airway_Marker_West_RNAV";
  MapIcon["Airway_Marker_East_RNAV"] = "Airway_Marker_East_RNAV";
  MapIcon["Airway_Marker_East_West_RNAV"] = "Airway_Marker_East_West_RNAV";
  // Base map icons
  MapIcon["BaseMap_CityDot"] = "base_map_city_dot";
  MapIcon["BaseMap_RailTracks"] = "base_map_rail_tracks";
  // Fuel price icons
  MapIcon["FuelPrices_Gray"] = "fuel_prices_gray";
  MapIcon["FuelPrices_Green"] = "fuel_prices_green";
  MapIcon["FuelPrices_Red"] = "fuel_prices_red";
  MapIcon["FuelPrices_Yellow"] = "fuel_prices_yellow";
  // Icing icons
  MapIcon["Icing_Empty"] = "empty_pattern";
  MapIcon["Icing_ProbLow"] = "icing_prob_low";
  MapIcon["Icing_ProbMedium"] = "icing_prob_medium";
  MapIcon["Icing_ProbHigh"] = "icing_prob_high";
  MapIcon["Icing_Trace"] = "icing_trace";
  MapIcon["Icing_Light"] = "icing_light";
  MapIcon["Icing_Moderate"] = "icing_moderate";
  MapIcon["Icing_Severe"] = "icing_severe";
  MapIcon["Icing_Extreme"] = "icing_extreme";
  MapIcon["Icing_TraceSolid"] = "icing_trace_sld";
  MapIcon["Icing_LightSolid"] = "icing_light_sld";
  MapIcon["Icing_ModerateSolid"] = "icing_moderate_sld";
  MapIcon["Icing_SevereSolid"] = "icing_severe_sld";
  MapIcon["Icing_ExtremeSolid"] = "icing_extreme_sld";
  MapIcon["Icing_Solid"] = "icing_sld";
  // InternetTraffic icons
  MapIcon["InternetTraffic_Biplane"] = "internet_traffic_biplane";
  MapIcon["InternetTraffic_BusinessJet"] = "internet_traffic_business_jet";
  MapIcon["InternetTraffic_CommercialDualEngine"] = "internet_traffic_commercial_dual_engine";
  MapIcon["InternetTraffic_CommercialFourEngine"] = "internet_traffic_commercial_four_engine";
  MapIcon["InternetTraffic_CommercialRearEngine"] = "internet_traffic_commercial_rear_engine";
  MapIcon["InternetTraffic_Fighter"] = "internet_traffic_fighter";
  MapIcon["InternetTraffic_FloatPlane"] = "internet_traffic_float_plane";
  MapIcon["InternetTraffic_Generic"] = "internet_traffic_generic";
  MapIcon["InternetTraffic_Helicopter"] = "internet_traffic_helicopter";
  MapIcon["InternetTraffic_Highlight"] = "internet_traffic_highlight";
  MapIcon["InternetTraffic_HighlightDetailBox"] = "internet_traffic_highlight_detail_box";
  MapIcon["InternetTraffic_HighWindTwin"] = "internet_traffic_high_wind_twin";
  MapIcon["InternetTraffic_HighWing"] = "internet_traffic_high_wing";
  MapIcon["InternetTraffic_LowWingTwin"] = "internet_traffic_low_wing_twin";
  MapIcon["InternetTraffic_LowWing"] = "internet_traffic_low_wing";
  MapIcon["InternetTraffic_MilitaryTransport"] = "internet_traffic_military_transport";
  MapIcon["InternetTraffic_Sr"] = "internet_traffic_sr";
  MapIcon["InternetTraffic_WarBird"] = "internet_traffic_war_bird";
  MapIcon["InternetTraffic_Xplane"] = "internet_traffic_x_plane";
  // Lightning icons
  MapIcon["Lightning"] = "lightning";
  // Navaid Icons (VFR)
  MapIcon["Navaid_DME"] = "NAVAID_DME";
  MapIcon["Navaid_NDB_Marker"] = "NAVAID_NDB_MARKER";
  MapIcon["Navaid_NDB"] = "NAVAID_NDB";
  MapIcon["Navaid_TACAN"] = "NAVAID_TACAN";
  MapIcon["Navaid_VOR"] = "NAVAID_VOR";
  MapIcon["Navaid_VOR_Compass"] = "NAVAID_VOR_COMPASS";
  MapIcon["Navaid_VOR_DME"] = "NAVAID_VOR_DME";
  MapIcon["Navaid_VORTAC"] = "NAVAID_VORTAC";
  MapIcon["Navaid_WPT"] = "NAVAID_WPT";
  MapIcon["Navaid_WPT_RNAV"] = "NAVAID_WPT_RNAV";
  // Navaid Icons (IFR)
  MapIcon["Navaid_IFR_DME"] = "NAVAID_IFR_DME";
  MapIcon["Navaid_IFR_NDB_Marker"] = "NAVAID_IFR_NDB_MARKER";
  MapIcon["Navaid_IFR_NDB"] = "NAVAID_IFR_NDB";
  MapIcon["Navaid_IFR_TACAN"] = "NAVAID_IFR_TACAN";
  MapIcon["Navaid_IFR_VOR"] = "NAVAID_IFR_VOR";
  MapIcon["Navaid_IFR_VOR_DME"] = "NAVAID_IFR_VOR_DME";
  MapIcon["Navaid_IFR_VORTAC"] = "NAVAID_IFR_VORTAC";
  MapIcon["Navaid_IFR_WPT"] = "NAVAID_IFR_WPT";
  MapIcon["Navaid_IFR_WPT_RNAV"] = "NAVAID_IFR_WPT_RNAV";
  // NOTAM icons (VFR)
  MapIcon["Notam_AdCaution"] = "notam_ad_caution";
  MapIcon["Notam_AdCautionTimed"] = "notam_ad_caution_timed";
  MapIcon["Notam_AdWarning"] = "notam_ad_warning";
  MapIcon["Notam_AdWarningTimed"] = "notam_ad_warning_timed";
  MapIcon["Notam_FillPatternBlank"] = "notam_pattern_blank";
  MapIcon["Notam_FillPatternDarkYellow"] = "notam_pattern_dark_yellow";
  MapIcon["Notam_FillPatternGray"] = "notam_pattern_gray";
  MapIcon["Notam_FillPatternOrange"] = "notam_pattern_orange";
  MapIcon["Notam_FillPatternPurple"] = "notam_pattern_purple";
  MapIcon["Notam_FillPatternRed"] = "notam_pattern_red";
  MapIcon["Notam_FillPatternTransparent"] = "notam_pattern_transparent";
  MapIcon["Notam_FillPatternYellow"] = "notam_pattern_yellow";
  MapIcon["Notam_NavDmeCaution"] = "notam_nav_dme_caution";
  MapIcon["Notam_NavDmeUpcoming"] = "notam_nav_dme_upcoming";
  MapIcon["Notam_NavDmeWarning"] = "notam_nav_dme_warning";
  MapIcon["Notam_NavNdbCaution"] = "notam_nav_ndb_caution";
  MapIcon["Notam_NavNdbUpcoming"] = "notam_nav_ndb_upcoming";
  MapIcon["Notam_NavNdbWarning"] = "notam_nav_ndb_warning";
  MapIcon["Notam_NavTacanCaution"] = "notam_nav_tacan_caution";
  MapIcon["Notam_NavTacanUpcoming"] = "notam_nav_tacan_upcoming";
  MapIcon["Notam_NavTacanWarning"] = "notam_nav_tacan_warning";
  MapIcon["Notam_NavVorCaution"] = "notam_nav_vor_caution";
  MapIcon["Notam_NavVorDmeCaution"] = "notam_nav_vor_dme_caution";
  MapIcon["Notam_NavVorDmeUpcoming"] = "notam_nav_vor_dme_upcoming";
  MapIcon["Notam_NavVorDmeWarning"] = "notam_nav_vor_dme_warning";
  MapIcon["Notam_NavVortacCaution"] = "notam_nav_vortac_caution";
  MapIcon["Notam_NavVortacUpcoming"] = "notam_nav_vortac_upcoming";
  MapIcon["Notam_NavVortacWarning"] = "notam_nav_vortac_warning";
  MapIcon["Notam_NavVorUpcoming"] = "notam_nav_vor_upcoming";
  MapIcon["Notam_NavVorWarning"] = "notam_nav_vor_warning";
  MapIcon["Notam_ObstShortLighted"] = "notam_obst_short_lighted";
  MapIcon["Notam_ObstShortLightedHighlighted"] = "notam_obst_short_lighted_highlighted";
  MapIcon["Notam_ObstShortLightedInverse"] = "notam_obst_short_lighted_inverse";
  MapIcon["Notam_ObstShortLightedInverseHighlighted"] = "notam_obst_short_lighted_inverse_highlighted";
  MapIcon["Notam_ObstShortUnlighted"] = "notam_obst_short_unlighted";
  MapIcon["Notam_ObstShortUnlightedHighlighted"] = "notam_obst_short_unlighted_highlighted";
  MapIcon["Notam_ObstShortUnlightedInverse"] = "notam_obst_short_unlighted_inverse";
  MapIcon["Notam_ObstShortUnlightedInverseHighlighted"] = "notam_obst_short_unlighted_inverse_highlighted";
  MapIcon["Notam_ObstTallLighted"] = "notam_obst_tall_lighted";
  MapIcon["Notam_ObstTallLightedHighlighted"] = "notam_obst_tall_lighted_highlighted";
  MapIcon["Notam_ObstTallLightedInverse"] = "notam_obst_tall_lighted_inverse";
  MapIcon["Notam_ObstTallLightedInverseHighlighted"] = "notam_obst_tall_lighted_inverse_highlighted";
  MapIcon["Notam_ObstTallUnlighted"] = "notam_obst_tall_unlighted";
  MapIcon["Notam_ObstTallUnlightedHighlighted"] = "notam_obst_tall_unlighted_highlighted";
  MapIcon["Notam_ObstTallUnlightedInverse"] = "notam_obst_tall_unlighted_inverse";
  MapIcon["Notam_ObstTallUnlightedInverseHighlighted"] = "notam_obst_tall_unlighted_inverse_highlighted";
  MapIcon["Notam_Unknown"] = "notam_unknown";
  // NOTAM icons (IFR)
  MapIcon["Notam_IFR_NavDmeCaution"] = "notam_IFR_nav_dme_caution";
  MapIcon["Notam_IFR_NavDmeUpcoming"] = "notam_IFR_nav_dme_upcoming";
  MapIcon["Notam_IFR_NavDmeWarning"] = "notam_IFR_nav_dme_warning";
  MapIcon["Notam_IFR_NavNdbCaution"] = "notam_IFR_nav_ndb_caution";
  MapIcon["Notam_IFR_NavNdbUpcoming"] = "notam_IFR_nav_ndb_upcoming";
  MapIcon["Notam_IFR_NavNdbWarning"] = "notam_IFR_nav_ndb_warning";
  MapIcon["Notam_IFR_NavTacanCaution"] = "notam_IFR_nav_tacan_caution";
  MapIcon["Notam_IFR_NavTacanUpcoming"] = "notam_IFR_nav_tacan_upcoming";
  MapIcon["Notam_IFR_NavTacanWarning"] = "notam_IFR_nav_tacan_warning";
  MapIcon["Notam_IFR_NavVorCaution"] = "notam_IFR_nav_vor_caution";
  MapIcon["Notam_IFR_NavVorDmeCaution"] = "notam_IFR_nav_vor_dme_caution";
  MapIcon["Notam_IFR_NavVorDmeUpcoming"] = "notam_IFR_nav_vor_dme_upcoming";
  MapIcon["Notam_IFR_NavVorDmeWarning"] = "notam_IFR_nav_vor_dme_warning";
  MapIcon["Notam_IFR_NavVortacCaution"] = "notam_IFR_nav_vortac_caution";
  MapIcon["Notam_IFR_NavVortacUpcoming"] = "notam_IFR_nav_vortac_upcoming";
  MapIcon["Notam_IFR_NavVortacWarning"] = "notam_IFR_nav_vortac_warning";
  MapIcon["Notam_IFR_NavVorUpcoming"] = "notam_IFR_nav_vor_upcoming";
  MapIcon["Notam_IFR_NavVorWarning"] = "notam_IFR_nav_vor_warning";
  // PIREP icons
  MapIcon["Pirep_Generic"] = "pirep_pirep";
  MapIcon["Pirep_Icing_Negative"] = "pirep_icing_neg";
  MapIcon["Pirep_Icing_Trace"] = "pirep_icing_tr";
  MapIcon["Pirep_Icing_TraceToLight"] = "pirep_icing_tr_lgt";
  MapIcon["Pirep_Icing_Light"] = "pirep_icing_lgt";
  MapIcon["Pirep_Icing_LightToModerate"] = "pirep_icing_lgt_mod";
  MapIcon["Pirep_Icing_Moderate"] = "pirep_icing_mod";
  MapIcon["Pirep_Icing_ModerateToSevere"] = "pirep_icing_mod_sev";
  MapIcon["Pirep_Icing_Severe"] = "pirep_icing_sev";
  MapIcon["Pirep_Sky_Unknown"] = "pirep_skycond_unknown";
  MapIcon["Pirep_Sky_Clear"] = "pirep_skycond_clr";
  MapIcon["Pirep_Sky_Few"] = "pirep_skycond_few";
  MapIcon["Pirep_Sky_Scattered"] = "pirep_skycond_sct";
  MapIcon["Pirep_Sky_Broken"] = "pirep_skycond_bkn";
  MapIcon["Pirep_Sky_Overcast"] = "pirep_skycond_ovc";
  MapIcon["Pirep_Sky_InstrumentConditions"] = "pirep_skycond_imc";
  MapIcon["Pirep_Turbulence_Negative"] = "pirep_turbulence_neg";
  MapIcon["Pirep_Turbulence_Light"] = "pirep_turbulence_lgt";
  MapIcon["Pirep_Turbulence_LightToModerate"] = "pirep_turbulence_lgt_mod";
  MapIcon["Pirep_Turbulence_Moderate"] = "pirep_turbulence_mod";
  MapIcon["Pirep_Turbulence_ModerateToSevere"] = "pirep_turbulence_mod_sev";
  MapIcon["Pirep_Turbulence_Severe"] = "pirep_turbulence_sev";
  MapIcon["Pirep_Turbulence_Extreme"] = "pirep_turbulence_ext";
  MapIcon["Pirep_Altitude_Generic"] = "pirep_pirep_alt";
  MapIcon["Pirep_Altitude_Icing_Negative"] = "pirep_icing_neg_alt";
  MapIcon["Pirep_Altitude_Icing_Trace"] = "pirep_icing_tr_alt";
  MapIcon["Pirep_Altitude_Icing_TraceToLight"] = "pirep_icing_tr_lgt_alt";
  MapIcon["Pirep_Altitude_Icing_Light"] = "pirep_icing_lgt_alt";
  MapIcon["Pirep_Altitude_Icing_LightToModerate"] = "pirep_icing_lgt_mod_alt";
  MapIcon["Pirep_Altitude_Icing_Moderate"] = "pirep_icing_mod_alt";
  MapIcon["Pirep_Altitude_Icing_ModerateToSevere"] = "pirep_icing_mod_sev_alt";
  MapIcon["Pirep_Altitude_Icing_Severe"] = "pirep_icing_sev_alt";
  MapIcon["Pirep_Altitude_Sky_Unknown"] = "pirep_skycond_unknown_alt";
  MapIcon["Pirep_Altitude_Sky_Clear"] = "pirep_skycond_clr_alt";
  MapIcon["Pirep_Altitude_Sky_Few"] = "pirep_skycond_few_alt";
  MapIcon["Pirep_Altitude_Sky_Scattered"] = "pirep_skycond_sct_alt";
  MapIcon["Pirep_Altitude_Sky_Broken"] = "pirep_skycond_bkn_alt";
  MapIcon["Pirep_Altitude_Sky_Overcast"] = "pirep_skycond_ovc_alt";
  MapIcon["Pirep_Altitude_Sky_InstrumentConditions"] = "pirep_skycond_imc_alt";
  MapIcon["Pirep_Altitude_Turbulence_Negative"] = "pirep_turbulence_neg_alt";
  MapIcon["Pirep_Altitude_Turbulence_Light"] = "pirep_turbulence_lgt_alt";
  MapIcon["Pirep_Altitude_Turbulence_LightToModerate"] = "pirep_turbulence_lgt_mod_alt";
  MapIcon["Pirep_Altitude_Turbulence_Moderate"] = "pirep_turbulence_mod_alt";
  MapIcon["Pirep_Altitude_Turbulence_ModerateToSevere"] = "pirep_turbulence_mod_sev_alt";
  MapIcon["Pirep_Altitude_Turbulence_Severe"] = "pirep_turbulence_sev_alt";
  MapIcon["Pirep_Altitude_Turbulence_Extreme"] = "pirep_turbulence_ext_alt";
  MapIcon["Pirep_NoAltitude_Generic"] = "pirep_pirep_no_alt";
  MapIcon["Pirep_NoAltitude_Icing_Negative"] = "pirep_icing_neg_no_alt";
  MapIcon["Pirep_NoAltitude_Icing_Trace"] = "pirep_icing_tr_no_alt";
  MapIcon["Pirep_NoAltitude_Icing_TraceToLight"] = "pirep_icing_tr_lgt_no_alt";
  MapIcon["Pirep_NoAltitude_Icing_Light"] = "pirep_icing_lgt_no_alt";
  MapIcon["Pirep_NoAltitude_Icing_LightToModerate"] = "pirep_icing_lgt_mod_no_alt";
  MapIcon["Pirep_NoAltitude_Icing_Moderate"] = "pirep_icing_mod_no_alt";
  MapIcon["Pirep_NoAltitude_Icing_ModerateToSevere"] = "pirep_icing_mod_sev_no_alt";
  MapIcon["Pirep_NoAltitude_Icing_Severe"] = "pirep_icing_sev_no_alt";
  MapIcon["Pirep_NoAltitude_Sky_Unknown"] = "pirep_skycond_unknown_no_alt";
  MapIcon["Pirep_NoAltitude_Sky_Clear"] = "pirep_skycond_clr_no_alt";
  MapIcon["Pirep_NoAltitude_Sky_Few"] = "pirep_skycond_few_no_alt";
  MapIcon["Pirep_NoAltitude_Sky_Scattered"] = "pirep_skycond_sct_no_alt";
  MapIcon["Pirep_NoAltitude_Sky_Broken"] = "pirep_skycond_bkn_no_alt";
  MapIcon["Pirep_NoAltitude_Sky_Overcast"] = "pirep_skycond_ovc_no_alt";
  MapIcon["Pirep_NoAltitude_Sky_InstrumentConditions"] = "pirep_skycond_imc_no_alt";
  MapIcon["Pirep_NoAltitude_Turbulence_Negative"] = "pirep_turbulence_neg_no_alt";
  MapIcon["Pirep_NoAltitude_Turbulence_Light"] = "pirep_turbulence_lgt_no_alt";
  MapIcon["Pirep_NoAltitude_Turbulence_LightToModerate"] = "pirep_turbulence_lgt_mod_no_alt";
  MapIcon["Pirep_NoAltitude_Turbulence_Moderate"] = "pirep_turbulence_mod_no_alt";
  MapIcon["Pirep_NoAltitude_Turbulence_ModerateToSevere"] = "pirep_turbulence_mod_sev_no_alt";
  MapIcon["Pirep_NoAltitude_Turbulence_Severe"] = "pirep_turbulence_sev_no_alt";
  MapIcon["Pirep_NoAltitude_Turbulence_Extreme"] = "pirep_turbulence_ext_no_alt";
  // Obstacle Icons
  MapIcon["Obstacle_Lighted_Short"] = "obstacle_lighted_short";
  MapIcon["Obstacle_Unlighted_Short"] = "obstacle_unlighted_short";
  MapIcon["Obstacle_Lighted_Tall"] = "obstacle_lighted_tall";
  MapIcon["Obstacle_Unlighted_Tall"] = "obstacle_unlighted_tall";
  // Weather Report icons
  MapIcon["WeatherReport_CircleBlueBroken"] = "weather_report_circle_blue_broken";
  MapIcon["WeatherReport_CircleBlueClear"] = "weather_report_circle_blue_clear";
  MapIcon["WeatherReport_CircleBlueFew"] = "weather_report_circle_blue_few";
  MapIcon["WeatherReport_CircleBlueMissing"] = "weather_report_circle_blue_missing";
  MapIcon["WeatherReport_CircleBlueObscured"] = "weather_report_circle_blue_obscured";
  MapIcon["WeatherReport_CircleBlueOvercast"] = "weather_report_circle_blue_overcast";
  MapIcon["WeatherReport_CircleBlueScattered"] = "weather_report_circle_blue_scattered";
  MapIcon["WeatherReport_CircleGrayMissing"] = "weather_report_circle_gray_missing";
  MapIcon["WeatherReport_CircleGreenBroken"] = "weather_report_circle_green_broken";
  MapIcon["WeatherReport_CircleGreenClear"] = "weather_report_circle_green_clear";
  MapIcon["WeatherReport_CircleGreenFew"] = "weather_report_circle_green_few";
  MapIcon["WeatherReport_CircleGreenMissing"] = "weather_report_circle_green_missing";
  MapIcon["WeatherReport_CircleGreenObscured"] = "weather_report_circle_green_obscured";
  MapIcon["WeatherReport_CircleGreenOvercast"] = "weather_report_circle_green_overcast";
  MapIcon["WeatherReport_CircleGreenScattered"] = "weather_report_circle_green_scattered";
  MapIcon["WeatherReport_CircleMagentaBroken"] = "weather_report_circle_magenta_broken";
  MapIcon["WeatherReport_CircleMagentaClear"] = "weather_report_circle_magenta_clear";
  MapIcon["WeatherReport_CircleMagentaFew"] = "weather_report_circle_magenta_few";
  MapIcon["WeatherReport_CircleMagentaMissing"] = "weather_report_circle_magenta_missing";
  MapIcon["WeatherReport_CircleMagentaObscured"] = "weather_report_circle_magenta_obscured";
  MapIcon["WeatherReport_CircleMagentaOvercast"] = "weather_report_circle_magenta_overcast";
  MapIcon["WeatherReport_CircleMagentaScattered"] = "weather_report_circle_magenta_scattered";
  MapIcon["WeatherReport_CircleRedBroken"] = "weather_report_circle_red_broken";
  MapIcon["WeatherReport_CircleRedClear"] = "weather_report_circle_red_clear";
  MapIcon["WeatherReport_CircleRedFew"] = "weather_report_circle_red_few";
  MapIcon["WeatherReport_CircleRedMissing"] = "weather_report_circle_red_missing";
  MapIcon["WeatherReport_CircleRedObscured"] = "weather_report_circle_red_obscured";
  MapIcon["WeatherReport_CircleRedOvercast"] = "weather_report_circle_red_overcast";
  MapIcon["WeatherReport_CircleRedScattered"] = "weather_report_circle_red_scattered";
  // Highlight Icons
  MapIcon["Highlight_Circle"] = "highlight_circle";
  return MapIcon;
}(MapIcon || {});
var IconPrefixType = /*#__PURE__*/function (IconPrefixType) {
  IconPrefixType[IconPrefixType["None"] = 0] = "None";
  IconPrefixType[IconPrefixType["Map"] = 1] = "Map";
  return IconPrefixType;
}(IconPrefixType || {});
const UPDATE_TICK_MS = 100;
const getIconSrc = (moduleConfig, iconSrc, iconPrefix) => {
  switch (iconPrefix) {
    case IconPrefixType.Map:
      return `${moduleConfig.mapAssetsPath}/${iconSrc}`;
    default:
      return iconSrc;
  }
};
let MapIconHandler = /*#__PURE__*/(() => {
  class MapIconHandler {
    constructor() {
      this.moduleConfig = inject(AVC_MAPS_DISPLAY_CONFIG);
      this.map = inject(MapTracker).map;
      this.icons = new Map();
      this.iconsToRemove = new Set();
      this.onTimer = () => {
        const map = this.map();
        let stillLoading = map == null;
        if (map != null) {
          const imagesBeforeRemove = map.listImages();
          for (const id of this.iconsToRemove) {
            if (imagesBeforeRemove.includes(id)) {
              map.removeImage(id);
            }
          }
          this.iconsToRemove.clear();
          // Add new and updated images
          const imagesBeforeAdd = map.listImages();
          for (const [id, icon] of this.icons) {
            if (icon.definition.complete === true && icon.image.width !== 0 && icon.image.height !== 0) {
              if (!imagesBeforeAdd.includes(id)) {
                const scaling = icon.definition.scaling ?? 1;
                if (scaling !== 1) {
                  icon.image.height *= scaling;
                  icon.image.width *= scaling;
                }
                map.addImage(id, icon.image, icon.definition.opts);
              }
            } else {
              stillLoading = true;
            }
          }
        }
        // Schedule the next update if something is still pending
        if (stillLoading) {
          this.timer = window.setTimeout(this.onTimer, UPDATE_TICK_MS);
        }
      };
    }
    /**
     * Adds one or more map icons
     * @param icon The icon definition(s)
     * @param onload A callback called when each icon is done loading
     */
    add(icons) {
      for (const icon of icons) {
        this.validateIcon(icon);
        icon.complete = false;
        this.icons.set(icon.id, {
          definition: icon,
          image: this.createImage(icon)
        });
      }
      this.updateTimer();
    }
    /**
     * Gets an HTML element of the specified icon
     * @param id The icon ID
     */
    get(id) {
      return this.icons.get(id)?.image;
    }
    getPath(id) {
      const icon = this.icons.get(id);
      if (icon?.definition.src == null) return undefined;
      return getIconSrc(this.moduleConfig, icon.definition.src, icon.definition.prefix);
    }
    /**
     * Gets an HTML element of the specified airport
     * @param feature The airport feature
     */
    getAirport(id, airportService) {
      return new Promise((resolve, reject) => {
        const map = this.map();
        if (map != null && airportService != null) {
          // Figure out the source ID to query
          const sourceId = airportService.getSource(map);
          if (sourceId == null) {
            reject('airport content not found on map');
            return;
          }
          // Query the map for the airport
          const features = map.querySourceFeatures(sourceId, {
            sourceLayer: MvtCycleContentSourceLayer.Airports,
            filter: ['==', ['get', 'airportId'], id.toUpperCase()]
          });
          if (features.length > 0) {
            // Construct the icon name based on the airport information from the map
            const name = `${features[0].properties.runwayIcon}`;
            // Get the icon from the map
            const styleImage = map.getImage(name, SpriteID.Airports);
            if (styleImage != null) {
              // Load the image in the browser again to get a portable HTMLImageElement
              const width = styleImage.data.width;
              const height = styleImage.data.height;
              const image = new Image(width, height);
              image.onload = () => resolve(image);
              image.onerror = () => reject('image failed to load');
              const canvas = document.createElement('canvas');
              canvas.width = width;
              canvas.height = height;
              const context = canvas.getContext('2d');
              const iconData = styleImage.data;
              const imageData = new ImageData(new Uint8ClampedArray(iconData.data), iconData.width, iconData.height);
              context?.putImageData(imageData, 0, 0);
              image.src = canvas.toDataURL();
            } else {
              reject(`airport icon (${name}) was not found`);
            }
          } else {
            reject(`airport (${id}) was not found`);
          }
        }
      });
    }
    /**
     * Does the specified icon already exist?
     * @param id The icon ID
     */
    has(id) {
      return this.icons.has(id);
    }
    /**
     * Returns a list of all icons
     */
    list() {
      return [...this.icons.keys()];
    }
    /**
     * Removes one of more map icons
     * @param icon The icon definition(s) or icon ID(s)
     */
    remove(icons) {
      for (const icon of icons) {
        this.iconsToRemove.add(icon.id);
        this.icons.delete(icon.id);
      }
      this.updateTimer();
    }
    /**
     * Updates one or more map icons by removing and adding the icon. This
     * is preferable to using separate `remove()` and `add()` calls as this
     * will delay the removal until the icon is fully loaded.
     */
    update(icons) {
      for (const icon of icons) {
        this.iconsToRemove.add(icon.id);
        this.validateIcon(icon);
        this.icons.set(icon.id, {
          definition: icon,
          image: this.createImage(icon)
        });
      }
      this.updateTimer();
    }
    validateIcon(icon) {
      if (icon == null) {
        throw new Error('icon definition not provided');
      }
      if (icon.id === MapIcon.Invalid) {
        throw new Error('icon flagged as invalid');
      }
      if (icon.element == null && icon.image == null && icon.src == null) {
        throw new Error('icon definitions did not define an image');
      }
    }
    createImage(icon) {
      if (icon.element != null) {
        const image = icon.element.cloneNode();
        icon.complete = true;
        return image;
      } else if (icon.image != null) {
        // Draw the provided image data onto a new canvas
        const canvas = document.createElement('canvas');
        canvas.width = icon.image.width;
        canvas.height = icon.image.height;
        const context = canvas.getContext('2d');
        const imageData = new ImageData(new Uint8ClampedArray(), icon.image.width, icon.image.height);
        context?.putImageData(imageData, 0, 0);
        // Create a new image and load the canvas onto it
        const image = new Image();
        image.onload = () => {
          icon.complete = true;
        };
        image.src = canvas.toDataURL();
        return image;
      } else if (icon.src != null) {
        // Create a new image and load the given URL
        const image = new Image();
        image.addEventListener('load', () => {
          icon.complete = true;
        });
        image.src = getIconSrc(this.moduleConfig, icon.src, icon.prefix);
        return image;
      }
      throw new Error('icon did not define element, image, or src properties');
    }
    updateTimer() {
      if (this.timer != null) {
        window.clearTimeout(this.timer);
      }
      this.onTimer();
    }
    static {
      this.ɵfac = function MapIconHandler_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapIconHandler)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapIconHandler,
        factory: MapIconHandler.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapIconHandler;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Performs a deep equivalency comparison of the given items.
 */
const isEqual = (a, b) => {
  if (typeof a != typeof b) {
    return false;
  }
  if (typeof a === 'object') {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return false;
      }
      for (let i = 0; i < a.length; ++i) {
        if (!isEqual(a[i], b[i])) {
          return false;
        }
      }
      return true;
    } else if (Array.isArray(a) || Array.isArray(b)) {
      return false;
    }
    const result = isEqual(Object.entries(a), Object.entries(b));
    return result;
  }
  const result = a === b;
  return result;
};
var NotamClass = /*#__PURE__*/function (NotamClass) {
  NotamClass[NotamClass["Runway"] = 0] = "Runway";
  NotamClass[NotamClass["Taxiway"] = 1] = "Taxiway";
  NotamClass[NotamClass["Ramp"] = 2] = "Ramp";
  NotamClass[NotamClass["Apron"] = 3] = "Apron";
  NotamClass[NotamClass["Aerodrome"] = 4] = "Aerodrome";
  NotamClass[NotamClass["Obstacle"] = 5] = "Obstacle";
  NotamClass[NotamClass["Navigation"] = 6] = "Navigation";
  NotamClass[NotamClass["Communication"] = 7] = "Communication";
  NotamClass[NotamClass["Service"] = 8] = "Service";
  NotamClass[NotamClass["Airspace"] = 9] = "Airspace";
  NotamClass[NotamClass["FDC"] = 10] = "FDC";
  NotamClass[NotamClass["ODP"] = 11] = "ODP";
  NotamClass[NotamClass["SID"] = 12] = "SID";
  NotamClass[NotamClass["STAR"] = 13] = "STAR";
  NotamClass[NotamClass["Chart"] = 14] = "Chart";
  NotamClass[NotamClass["Data"] = 15] = "Data";
  NotamClass[NotamClass["IAP"] = 16] = "IAP";
  NotamClass[NotamClass["VFP"] = 17] = "VFP";
  NotamClass[NotamClass["Route"] = 18] = "Route";
  NotamClass[NotamClass["Special"] = 19] = "Special";
  NotamClass[NotamClass["Military"] = 20] = "Military";
  NotamClass[NotamClass["International"] = 21] = "International";
  NotamClass[NotamClass["OtherAeronauticalInfo"] = 22] = "OtherAeronauticalInfo";
  NotamClass[NotamClass["Unknown"] = 23] = "Unknown";
  return NotamClass;
}(NotamClass || {});
var AerodromeSubClass = /*#__PURE__*/function (AerodromeSubClass) {
  AerodromeSubClass[AerodromeSubClass["Undefined"] = 0] = "Undefined";
  AerodromeSubClass[AerodromeSubClass["SNOWTAM"] = 1] = "SNOWTAM";
  AerodromeSubClass[AerodromeSubClass["Lighting"] = 2] = "Lighting";
  AerodromeSubClass[AerodromeSubClass["Unknown"] = 3] = "Unknown";
  return AerodromeSubClass;
}(AerodromeSubClass || {});
var AirspaceSubClass = /*#__PURE__*/function (AirspaceSubClass) {
  AirspaceSubClass[AirspaceSubClass["Undefined"] = 0] = "Undefined";
  AirspaceSubClass[AirspaceSubClass["Prohibited"] = 1] = "Prohibited";
  AirspaceSubClass[AirspaceSubClass["Restricted"] = 2] = "Restricted";
  AirspaceSubClass[AirspaceSubClass["MilitaryOperations"] = 3] = "MilitaryOperations";
  AirspaceSubClass[AirspaceSubClass["Alert"] = 4] = "Alert";
  AirspaceSubClass[AirspaceSubClass["Warning"] = 5] = "Warning";
  AirspaceSubClass[AirspaceSubClass["UAS"] = 6] = "UAS";
  AirspaceSubClass[AirspaceSubClass["TetheredBalloons"] = 7] = "TetheredBalloons";
  AirspaceSubClass[AirspaceSubClass["Gathering"] = 8] = "Gathering";
  AirspaceSubClass[AirspaceSubClass["Danger"] = 9] = "Danger";
  AirspaceSubClass[AirspaceSubClass["ParachuteJumping"] = 10] = "ParachuteJumping";
  AirspaceSubClass[AirspaceSubClass["ScheduledExercises"] = 11] = "ScheduledExercises";
  AirspaceSubClass[AirspaceSubClass["FormationTransit"] = 12] = "FormationTransit";
  AirspaceSubClass[AirspaceSubClass["Aerobatic"] = 13] = "Aerobatic";
  AirspaceSubClass[AirspaceSubClass["GliderFlying"] = 14] = "GliderFlying";
  AirspaceSubClass[AirspaceSubClass["BannerTowing"] = 15] = "BannerTowing";
  AirspaceSubClass[AirspaceSubClass["ASHTAM"] = 16] = "ASHTAM";
  AirspaceSubClass[AirspaceSubClass["Unknown"] = 17] = "Unknown";
  return AirspaceSubClass;
}(AirspaceSubClass || {});
var ApronSubClass = /*#__PURE__*/function (ApronSubClass) {
  ApronSubClass[ApronSubClass["Undefined"] = 0] = "Undefined";
  ApronSubClass[ApronSubClass["AircraftStand"] = 1] = "AircraftStand";
  ApronSubClass[ApronSubClass["ParkingArea"] = 2] = "ParkingArea";
  ApronSubClass[ApronSubClass["Lighting"] = 3] = "Lighting";
  ApronSubClass[ApronSubClass["FICON"] = 4] = "FICON";
  ApronSubClass[ApronSubClass["Access"] = 5] = "Access";
  ApronSubClass[ApronSubClass["Markings"] = 6] = "Markings";
  ApronSubClass[ApronSubClass["Unknown"] = 7] = "Unknown";
  return ApronSubClass;
}(ApronSubClass || {});
var NavigationSubClass = /*#__PURE__*/function (NavigationSubClass) {
  NavigationSubClass[NavigationSubClass["Undefined"] = 0] = "Undefined";
  NavigationSubClass[NavigationSubClass["NDB"] = 1] = "NDB";
  NavigationSubClass[NavigationSubClass["DECCA"] = 2] = "DECCA";
  NavigationSubClass[NavigationSubClass["FAN"] = 3] = "FAN";
  NavigationSubClass[NavigationSubClass["LOCATOR"] = 4] = "LOCATOR";
  NavigationSubClass[NavigationSubClass["DME"] = 5] = "DME";
  NavigationSubClass[NavigationSubClass["TACAN"] = 6] = "TACAN";
  NavigationSubClass[NavigationSubClass["VORTAC"] = 7] = "VORTAC";
  NavigationSubClass[NavigationSubClass["VOR"] = 8] = "VOR";
  NavigationSubClass[NavigationSubClass["DirectionFindingStation"] = 9] = "DirectionFindingStation";
  NavigationSubClass[NavigationSubClass["ILS"] = 10] = "ILS";
  NavigationSubClass[NavigationSubClass["VOR_DME"] = 11] = "VOR_DME";
  NavigationSubClass[NavigationSubClass["LDA"] = 12] = "LDA";
  NavigationSubClass[NavigationSubClass["GLS"] = 13] = "GLS";
  NavigationSubClass[NavigationSubClass["Unknown"] = 14] = "Unknown";
  return NavigationSubClass;
}(NavigationSubClass || {});
var ObstacleSubClass = /*#__PURE__*/function (ObstacleSubClass) {
  ObstacleSubClass[ObstacleSubClass["Undefined"] = 0] = "Undefined";
  ObstacleSubClass[ObstacleSubClass["Unlit"] = 1] = "Unlit";
  ObstacleSubClass[ObstacleSubClass["Lit"] = 2] = "Lit";
  ObstacleSubClass[ObstacleSubClass["Lighting"] = 3] = "Lighting";
  ObstacleSubClass[ObstacleSubClass["Unknown"] = 4] = "Unknown";
  return ObstacleSubClass;
}(ObstacleSubClass || {});
var RunwaySubClass = /*#__PURE__*/function (RunwaySubClass) {
  RunwaySubClass[RunwaySubClass["Undefined"] = 0] = "Undefined";
  RunwaySubClass[RunwaySubClass["Length"] = 1] = "Length";
  RunwaySubClass[RunwaySubClass["Threshold"] = 2] = "Threshold";
  RunwaySubClass[RunwaySubClass["Lights"] = 3] = "Lights";
  RunwaySubClass[RunwaySubClass["FICON"] = 4] = "FICON";
  RunwaySubClass[RunwaySubClass["Sign"] = 5] = "Sign";
  RunwaySubClass[RunwaySubClass["Markings"] = 6] = "Markings";
  RunwaySubClass[RunwaySubClass["Unknown"] = 7] = "Unknown";
  return RunwaySubClass;
}(RunwaySubClass || {});
var TaxiwaySubClass = /*#__PURE__*/function (TaxiwaySubClass) {
  TaxiwaySubClass[TaxiwaySubClass["Undefined"] = 0] = "Undefined";
  TaxiwaySubClass[TaxiwaySubClass["Length"] = 1] = "Length";
  TaxiwaySubClass[TaxiwaySubClass["Threshold"] = 2] = "Threshold";
  TaxiwaySubClass[TaxiwaySubClass["Lights"] = 3] = "Lights";
  TaxiwaySubClass[TaxiwaySubClass["FICON"] = 4] = "FICON";
  TaxiwaySubClass[TaxiwaySubClass["Sign"] = 5] = "Sign";
  TaxiwaySubClass[TaxiwaySubClass["Markings"] = 6] = "Markings";
  TaxiwaySubClass[TaxiwaySubClass["Unknown"] = 7] = "Unknown";
  return TaxiwaySubClass;
}(TaxiwaySubClass || {});
var NavQCode = /*#__PURE__*/function (NavQCode) {
  NavQCode["NDB"] = "QNB";
  NavQCode["DME"] = "QND";
  NavQCode["VOR_DME"] = "QNM";
  NavQCode["VOR"] = "QNV";
  NavQCode["VORTAC"] = "QNT";
  NavQCode["TACAN"] = "QNN";
  return NavQCode;
}(NavQCode || {});
var NotamStatus = /*#__PURE__*/function (NotamStatus) {
  NotamStatus[NotamStatus["NoStatus"] = 0] = "NoStatus";
  NotamStatus[NotamStatus["Activated"] = 1] = "Activated";
  NotamStatus[NotamStatus["Deactivated"] = 2] = "Deactivated";
  NotamStatus[NotamStatus["Closed"] = 3] = "Closed";
  NotamStatus[NotamStatus["Unusable"] = 4] = "Unusable";
  NotamStatus[NotamStatus["NotAvailable"] = 5] = "NotAvailable";
  NotamStatus[NotamStatus["OutOfService"] = 6] = "OutOfService";
  NotamStatus[NotamStatus["Changed"] = 7] = "Changed";
  NotamStatus[NotamStatus["Erected"] = 8] = "Erected";
  NotamStatus[NotamStatus["Withdrawn"] = 9] = "Withdrawn";
  NotamStatus[NotamStatus["Unknown"] = 10] = "Unknown";
  return NotamStatus;
}(NotamStatus || {});
var NotamSource = /*#__PURE__*/function (NotamSource) {
  NotamSource[NotamSource["Unknown"] = 0] = "Unknown";
  NotamSource[NotamSource["Domestic"] = 1] = "Domestic";
  NotamSource[NotamSource["FDC"] = 2] = "FDC";
  NotamSource[NotamSource["Military"] = 3] = "Military";
  NotamSource[NotamSource["International"] = 4] = "International";
  return NotamSource;
}(NotamSource || {});
const NotamWarningStatuses = [NotamStatus.Deactivated, NotamStatus.Closed, NotamStatus.Unusable, NotamStatus.NotAvailable, NotamStatus.OutOfService, NotamStatus.Withdrawn];
const NotamNonConditionalClosureQCodes = ["LC"];
const NotamConditionalClosureQCodes = ["LI", "LN", "LV", "LT"];
const NotamNonConditionalKeywords = ["EXC TAX", "EXC XNG"];
const NotamConditionalKeywords = ["DLY", "DAILY", "BETWEEN", "HOURS", "WEEKDAY", "WEEKEND", "MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN", "EXC", "PPR", "REQUEST", "PHONE", "TEL", "MAIL", "EMERG", "MEDEVAC", "HUMANITARIAN", "PROTECTION", "BRAKING", "TGL", "SEP", "CLSD TO", "CLSD FOR", "CLOSED TO", "CLOSED FOR", "STOP", "FST", "FIRST", "LST", "LAST", "WINGSPAN", "0FT"];

/**
 * Get the expression for getting the maximum altitude text.
 * @returns An `ExpressionSpecification`.
 */
const getMaxAltitudeText = () => {
  return ['concat', ['case', ['any', ['!', ['has', 'TimeAltitudeRange.MaxAltitude.altitude']], ['==', ['get', 'TimeAltitudeRange.MaxAltitude.altitude'], 0]], 'UNL', ['concat', ['to-string', ['get', 'TimeAltitudeRange.MaxAltitude.altitude']], ' FT', ['case', ['all', ['has', 'TimeAltitudeRange.MaxAltitude.isMSL'], ['get', 'TimeAltitudeRange.MaxAltitude.isMSL']], ' MSL', '']]]];
};
/**
 * Get the expression for getting the minimum altitude text.
 * @returns An `ExpressionSpecification`.
 */
const getMinAltitudeText = () => {
  return ['concat', ['case', ['any', ['!', ['has', 'TimeAltitudeRange.MinAltitude.altitude']], ['==', ['get', 'TimeAltitudeRange.MinAltitude.altitude'], 0]], 'SFC', ['concat', ['to-string', ['get', 'TimeAltitudeRange.MinAltitude.altitude']], ' FT', ['case', ['all', ['has', 'TimeAltitudeRange.MinAltitude.isMSL'], ['get', 'TimeAltitudeRange.MinAltitude.isMSL']], ' MSL', '']]]];
};
/**
 * Get the expression for whether a NOTAM is considered graphical.
 * @returns An `ExpressionSpecification`
 */
const isGraphical = () => {
  /**
   * The 'isGraphical' flag is set by the server based on whether it
   * thinks the map will display it. The exact logic for this is unknown
   * at the time of writing. I _think_ it has something to do with having
   * useful shape data, maybe??
   *
   * As the time of writing, the MVTs are explicitly removing all
   * NOTAMs where 'isGraphical' is false. This is essentially a guard
   * against that switch being turned back on at some point.
   *
   * If this changes, also modify src\lib\api\info\overlay\notam-info.ts!
   */
  return ['boolean', ['get', 'isGraphical'], false];
};
/**
 * Get the expression for checking if the NOTAM is expired.
 * @param now The time to check against the expire time.
 * @returns An `ExpressionSpecification`.
 */
const isExpired$1 = now => {
  return ['case', ['has', 'expireTime'], ['>=', now, ['to-number', ['get', 'expireTime']]], false];
};
/**
 * Get the expression for checking if the NOTAM is upcoming.
 * @param now The time to check against the effective time.
 * @returns An `ExpressionSpecification`.
 */
const isUpcoming = (now, upcomingVisibilitySeconds) => {
  return ['all', ['<', now, ['get', 'effectiveTime']], withinUpcomingVisibility(now, upcomingVisibilitySeconds)];
};
/**
 * Get the expression for checking if the NOTAM is active.
 * @param now The time to check against the effective time and expire time.
 * @returns An `ExpressionSpecification`.
 */
const isActive$1 = now => {
  return ['all', ['>=', now, ['get', 'effectiveTime']], ['!', isExpired$1(now)]];
};
/**
 * Get the expression for checking if the NOTAM is inactive.
 * @param now The time to check against the effective time and expire time.
 * @returns An `ExpressionSpecification`.
 */
const isInactive = (now, upcomingVisibilitySeconds) => {
  return ['all', ['<', now, ['get', 'effectiveTime']], ['!', withinUpcomingVisibility(now, upcomingVisibilitySeconds)], ['!', isExpired$1(now)]];
};
const withinUpcomingVisibility = (now, upcomingVisibilitySeconds) => {
  return ['>=', now, ['-', ['get', 'effectiveTime'], upcomingVisibilitySeconds]];
};
const isAirspaceNotam = (notamFilter, showUasNotams) => {
  return ['all', notamFilter, ['==', ['get', 'notamClass'], NotamClass.Airspace], ['any', ['boolean', showUasNotams], ['!=', ['get', 'notamSubClass'], AirspaceSubClass.UAS]]];
};
const isFdcOrUnknownNotam = notamFilter => {
  return ['all', notamFilter, ['match', ['get', 'notamClass'], [NotamClass.FDC, NotamClass.OtherAeronauticalInfo], true, ['any', ['>=', ['get', 'notamClass'], NotamClass.Unknown], ['coalesce', ['get', 'fdc'], false]]]];
};
/**
 * Get the expression for checking if the NOTAM belongs to the restricted
 * and danger category.
 * @returns An `ExpressionSpecification`.
 */
const isRestrictedAndDangerAirspace = () => {
  return ['any', ['==', ['get', 'notamSubClass'], AirspaceSubClass.Prohibited], ['==', ['get', 'notamSubClass'], AirspaceSubClass.Restricted], ['==', ['get', 'notamSubClass'], AirspaceSubClass.Danger]];
};
/**
 * Get the expression for checking if the NOTAM belongs to the military
 * and warning category.
 * @returns An `ExpressionSpecification`.
 */
const isMilitaryAndWarningAirspace = () => {
  return ['any', ['==', ['get', 'notamSubClass'], AirspaceSubClass.MilitaryOperations], ['==', ['get', 'notamSubClass'], AirspaceSubClass.ScheduledExercises], ['==', ['get', 'notamSubClass'], AirspaceSubClass.Warning]];
};
/**
 * Get the expression for checking if the NOTAM belongs to the alert category.
 * @returns An `ExpressionSpecification`.
 */
const isAlertAirspace = () => {
  return ['any', ['==', ['get', 'notamSubClass'], AirspaceSubClass.Alert], ['==', ['get', 'notamSubClass'], AirspaceSubClass.BannerTowing], ['==', ['get', 'notamSubClass'], AirspaceSubClass.GliderFlying], ['==', ['get', 'notamSubClass'], AirspaceSubClass.ParachuteJumping], ['==', ['get', 'notamSubClass'], AirspaceSubClass.UAS], ['==', ['get', 'notamSubClass'], AirspaceSubClass.TetheredBalloons], ['==', ['get', 'notamSubClass'], AirspaceSubClass.Gathering], ['==', ['get', 'notamSubClass'], AirspaceSubClass.Aerobatic], ['==', ['get', 'notamSubClass'], AirspaceSubClass.FormationTransit]];
};
/**
 * Creates an interpolate expression for zoom values
 * @param values The list of numbers per zoom level
 * @returns An `ExpressionSpecification`
 */
const interpolateZoom = values => {
  if (values.length < 1) {
    throw new Error('interpolateZoom requires at least one pair of values');
  }
  const zoomOpacityExpression = ['interpolate', ['linear'], ['zoom']];
  for (const pair of values) {
    zoomOpacityExpression.push(pair[0]);
    zoomOpacityExpression.push(pair[1]);
  }
  return zoomOpacityExpression;
};
// Typescript version of `isConditional()`
const featureIsConditional = feature => {
  const qcode = feature.properties['qcode'];
  if (qcode != null) {
    if (NotamNonConditionalClosureQCodes.includes(qcode)) {
      return false;
    }
    if (NotamConditionalClosureQCodes.includes(qcode)) {
      return true;
    }
  }
  const text = feature.properties['notamText'];
  if (text != null) {
    for (const keyword of NotamNonConditionalKeywords) {
      if (text.includes(keyword)) {
        return false;
      }
    }
    for (const keyword of NotamConditionalKeywords) {
      if (text.includes(keyword)) {
        return true;
      }
    }
  }
  return false;
};
// MapLibre version of `featureIsConditional()`
const isConditional = () => {
  return ['case',
  // Check the qCode for non-conditional closures
  // TODO: This feels wrong. Haven't found any examples to test yet.
  ['all', ['has', 'qcode'], ['in', ['get', 'qcode'], ['literal', NotamNonConditionalClosureQCodes]]], false,
  // Check the qCode for conditional closures
  // TODO: This feels wrong. Haven't found any examples to test yet.
  ['all', ['has', 'qcode'], ['in', ['get', 'qcode'], ['literal', NotamConditionalClosureQCodes]]], true,
  // If a non-conditional keyword is present, it's NOT conditional
  ['all', ['has', 'notamText'], ['any', ...NotamNonConditionalKeywords.map(x => ['in', x, ['get', 'notamText']])]], false,
  // If a conditional keyword is present, it's conditional
  ['all', ['has', 'notamText'], ['any', ...NotamConditionalKeywords.map(x => ['in', x, ['get', 'notamText']])]], true,
  // Assume anything else is non-conditional
  false];
};
const getObstructionIconForHeight = (lighted, lightedInverse, unlighted, unlightedInverse, showInverseIcons) => {
  return ['match', ['get', 'notamSubClass'],
  // A subclass of "Lighting" means this is a permanent obstacle and the NOTAM is
  // modifying the lighting of that obstacle. If the permanent obstacle is also
  // being shown, use inverse icons to indicate a change in lighting.
  ObstacleSubClass.Lighting, ['match', ['get', 'notamStatus'],
  // "Lighting: Unserviceable" means there were lights and the lights no longer work
  NotamWarningStatuses, showInverseIcons ? unlightedInverse : unlighted,
  // "Lighting: Erected" would mean lights were added
  showInverseIcons ? lightedInverse : unlighted],
  // A subclass of "Lights" means this is a new obstacle with lights.
  ObstacleSubClass.Lit, lighted,
  // Assume anything else is a new obstacle without lights.
  unlighted];
};
const getObstructionIconImage = (tall, tallInverse, short, shortInverse, tallLighted, tallLightedInverse, shortLighted, shortLightedInverse, obstaclesVisible) => {
  // If obstacles are also visible, some NOTAM icons are inversed to show
  // that they are acting on an already present NOTAM instead of creating a
  // new obstacle with a NOTAM.
  const showInverseIcons = obstaclesVisible;
  return ['let', 'obstHeight', ['case',
  // If the max altitude is AGL, just use the max altitude
  ['all', ['has', 'TimeAltitudeRange.MaxAltitude.isMSL'], ['!', ['boolean', ['get', 'TimeAltitudeRange.MaxAltitude.isMSL'], true]]], ['number', ['get', 'TimeAltitudeRange.MaxAltitude.altitude']],
  // If both min and max are MSL, subtract one from the other
  // NOTE: We expect all obstacles to be AGL. This is just some opportunistic logic.
  ['all', ['has', 'TimeAltitudeRange.MaxAltitude.isMSL'], ['has', 'TimeAltitudeRange.MinAltitude.isMSL'], ['boolean', ['get', 'TimeAltitudeRange.MaxAltitude.isMSL'], false], ['boolean', ['get', 'TimeAltitudeRange.MinAltitude.isMSL'], false]], ['-', ['number', ['get', 'TimeAltitudeRange.MaxAltitude.altitude']], ['number', ['get', 'TimeAltitudeRange.MinAltitude.altitude']]],
  // Fallback to tall obstacle if unable to determine obstacle height
  9999999], ['case',
  // Any obstacle over 1,000 ft in height is considered tall
  ['>=', ['var', 'obstHeight'], 1000], getObstructionIconForHeight(tallLighted, tallLightedInverse, tall, tallInverse, showInverseIcons), getObstructionIconForHeight(shortLighted, shortLightedInverse, short, shortInverse, showInverseIcons)]];
};
var FeatureType$1 = /*#__PURE__*/function (FeatureType) {
  FeatureType[FeatureType["AirmetsSigmets"] = 0] = "AirmetsSigmets";
  FeatureType[FeatureType["Airports"] = 1] = "Airports";
  FeatureType[FeatureType["Airspaces"] = 2] = "Airspaces";
  FeatureType[FeatureType["Airways"] = 3] = "Airways";
  FeatureType[FeatureType["Fuel"] = 4] = "Fuel";
  FeatureType[FeatureType["InternetTraffic"] = 5] = "InternetTraffic";
  FeatureType[FeatureType["NAVAIDs_NDB"] = 6] = "NAVAIDs_NDB";
  FeatureType[FeatureType["NAVAIDs_VOR"] = 7] = "NAVAIDs_VOR";
  FeatureType[FeatureType["NAVAIDs_WPT"] = 8] = "NAVAIDs_WPT";
  FeatureType[FeatureType["NOTAMs"] = 9] = "NOTAMs";
  FeatureType[FeatureType["Obstacles"] = 10] = "Obstacles";
  FeatureType[FeatureType["PIREPs"] = 11] = "PIREPs";
  FeatureType[FeatureType["StormCells"] = 12] = "StormCells";
  FeatureType[FeatureType["TFRs"] = 13] = "TFRs";
  FeatureType[FeatureType["UserWaypoints"] = 14] = "UserWaypoints";
  FeatureType[FeatureType["WeatherReport"] = 15] = "WeatherReport";
  FeatureType[FeatureType["MoreInfo"] = 16] = "MoreInfo";
  return FeatureType;
}(FeatureType$1 || {});
const AllFeatureTypes = [FeatureType$1.AirmetsSigmets, FeatureType$1.Airports, FeatureType$1.Airspaces, FeatureType$1.Airways, FeatureType$1.Fuel, FeatureType$1.InternetTraffic, FeatureType$1.MoreInfo, FeatureType$1.NAVAIDs_NDB, FeatureType$1.NAVAIDs_VOR, FeatureType$1.NAVAIDs_WPT, FeatureType$1.NOTAMs, FeatureType$1.Obstacles, FeatureType$1.PIREPs, FeatureType$1.StormCells, FeatureType$1.TFRs, FeatureType$1.UserWaypoints, FeatureType$1.WeatherReport];
var LayerType = /*#__PURE__*/function (LayerType) {
  LayerType[LayerType["Airports"] = 0] = "Airports";
  LayerType[LayerType["AirmetsSigmets"] = 1] = "AirmetsSigmets";
  LayerType[LayerType["Airspaces"] = 2] = "Airspaces";
  LayerType[LayerType["Airways"] = 3] = "Airways";
  LayerType[LayerType["FuelPrices"] = 4] = "FuelPrices";
  LayerType[LayerType["InternetTraffic"] = 5] = "InternetTraffic";
  LayerType[LayerType["Metars"] = 6] = "Metars";
  LayerType[LayerType["Navaids_NDB"] = 7] = "Navaids_NDB";
  LayerType[LayerType["Navaids_VOR"] = 8] = "Navaids_VOR";
  LayerType[LayerType["Navaids_WPT"] = 9] = "Navaids_WPT";
  LayerType[LayerType["RunwayNotams"] = 10] = "RunwayNotams";
  LayerType[LayerType["Notams"] = 11] = "Notams";
  LayerType[LayerType["Obstacles"] = 12] = "Obstacles";
  LayerType[LayerType["Pireps"] = 13] = "Pireps";
  LayerType[LayerType["StormCells"] = 14] = "StormCells";
  LayerType[LayerType["Tfrs"] = 15] = "Tfrs";
  LayerType[LayerType["UserWaypoints"] = 16] = "UserWaypoints";
  return LayerType;
}(LayerType || {});
const FeatureTypeLayerTypeMap = {
  [FeatureType$1.AirmetsSigmets]: [LayerType.AirmetsSigmets],
  [FeatureType$1.Airports]: [LayerType.Airports],
  [FeatureType$1.Airspaces]: [LayerType.Airspaces],
  [FeatureType$1.Airways]: [LayerType.Airways],
  [FeatureType$1.Fuel]: [LayerType.FuelPrices],
  [FeatureType$1.InternetTraffic]: [LayerType.InternetTraffic],
  [FeatureType$1.NAVAIDs_NDB]: [LayerType.Navaids_NDB],
  [FeatureType$1.NAVAIDs_VOR]: [LayerType.Navaids_VOR],
  [FeatureType$1.NAVAIDs_WPT]: [LayerType.Navaids_WPT],
  [FeatureType$1.NOTAMs]: [LayerType.Notams, LayerType.RunwayNotams],
  [FeatureType$1.Obstacles]: [LayerType.Obstacles],
  [FeatureType$1.PIREPs]: [LayerType.Pireps],
  [FeatureType$1.StormCells]: [LayerType.StormCells],
  [FeatureType$1.TFRs]: [LayerType.Tfrs],
  [FeatureType$1.WeatherReport]: [LayerType.Metars],
  [FeatureType$1.UserWaypoints]: [LayerType.UserWaypoints],
  [FeatureType$1.MoreInfo]: []
};
let RadialMenuMapService = /*#__PURE__*/(() => {
  class RadialMenuMapService {
    constructor() {
      this.RadialMenuLayerMap = new Map([[LayerType.Airports, []], [LayerType.Airspaces, []], [LayerType.AirmetsSigmets, []], [LayerType.Airways, []], [LayerType.FuelPrices, []], [LayerType.InternetTraffic, []], [LayerType.Metars, []], [LayerType.Navaids_VOR, []], [LayerType.Navaids_NDB, []], [LayerType.Navaids_WPT, []], [LayerType.Notams, []], [LayerType.Obstacles, []], [LayerType.Pireps, []], [LayerType.RunwayNotams, []], [LayerType.StormCells, []], [LayerType.Tfrs, []], [LayerType.UserWaypoints, []]]);
      this.radialMenuWatcher = new BehaviorSubject(this.RadialMenuLayerMap);
      this.radialMenuWatcher$ = this.radialMenuWatcher.asObservable();
    }
    addToMap(type, id) {
      this.RadialMenuLayerMap.get(type)?.push(id);
      this.radialMenuWatcher.next(this.RadialMenuLayerMap);
    }
    getLayerData(type) {
      return this.RadialMenuLayerMap.get(type) ?? [];
    }
    getLayerDataByFeature(type) {
      return FeatureTypeLayerTypeMap[type].map(layerType => this.getLayerData(layerType)).reduce((acc, curr) => acc.concat(curr), []);
    }
    getAllLayers() {
      return this.RadialMenuLayerMap;
    }
    removeFromMap(type, id) {
      const index = this.RadialMenuLayerMap.get(type)?.indexOf(id);
      if (index != null && index >= 0) {
        this.RadialMenuLayerMap.get(type)?.splice(index, 1);
        this.radialMenuWatcher.next(this.RadialMenuLayerMap);
      }
    }
    static {
      this.ɵfac = function RadialMenuMapService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RadialMenuMapService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: RadialMenuMapService,
        factory: RadialMenuMapService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return RadialMenuMapService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var AirwayLayerVisibility = /*#__PURE__*/function (AirwayLayerVisibility) {
  AirwayLayerVisibility[AirwayLayerVisibility["Off"] = 0] = "Off";
  AirwayLayerVisibility[AirwayLayerVisibility["Low"] = 1] = "Low";
  AirwayLayerVisibility[AirwayLayerVisibility["High"] = 2] = "High";
  AirwayLayerVisibility[AirwayLayerVisibility["Both"] = 3] = "Both";
  return AirwayLayerVisibility;
}(AirwayLayerVisibility || {});
var EnrouteState = /*#__PURE__*/function (EnrouteState) {
  EnrouteState[EnrouteState["Low"] = 0] = "Low";
  EnrouteState[EnrouteState["High"] = 1] = "High";
  return EnrouteState;
}(EnrouteState || {});
var MapOverlayLayer = /*#__PURE__*/function (MapOverlayLayer) {
  MapOverlayLayer[MapOverlayLayer["VFR"] = 0] = "VFR";
  MapOverlayLayer[MapOverlayLayer["IFR"] = 1] = "IFR";
  MapOverlayLayer[MapOverlayLayer["StreetMap"] = 2] = "StreetMap";
  MapOverlayLayer[MapOverlayLayer["Satellite"] = 3] = "Satellite";
  MapOverlayLayer[MapOverlayLayer["VFRSectional"] = 4] = "VFRSectional";
  MapOverlayLayer[MapOverlayLayer["IFREnroute"] = 5] = "IFREnroute";
  MapOverlayLayer[MapOverlayLayer["None"] = 6] = "None";
  return MapOverlayLayer;
}(MapOverlayLayer || {});
var TopographyState = /*#__PURE__*/function (TopographyState) {
  TopographyState[TopographyState["OFF"] = 0] = "OFF";
  TopographyState[TopographyState["SHADE"] = 1] = "SHADE";
  TopographyState[TopographyState["ON"] = 2] = "ON";
  return TopographyState;
}(TopographyState || {});
class ObstacleSettingsHandler {
  constructor() {
    this.minimumHeightFt = signal(0);
    this.minimumZoom = signal(ZoomLevel.All);
  }
  save() {
    return {
      minimumHeightFt: this.minimumHeightFt(),
      minimumZoom: this.minimumZoom()
    };
  }
  restore(state) {
    if (state.minimumHeightFt != null) {
      this.minimumHeightFt.set(state.minimumHeightFt);
    }
    if (state.minimumZoom != null) {
      this.minimumZoom.set(state.minimumZoom);
    }
  }
}
var AltimeterUnits = /*#__PURE__*/function (AltimeterUnits) {
  AltimeterUnits["InchesOfMercury"] = "inHg";
  AltimeterUnits["Millibars"] = "mb";
  AltimeterUnits["Hectopascals"] = "hPa";
  AltimeterUnits["Kilopascals"] = "kPa";
  AltimeterUnits["PoundsPerSquareInch"] = "psi";
  return AltimeterUnits;
}(AltimeterUnits || {});
var CoordinatesFormat = /*#__PURE__*/function (CoordinatesFormat) {
  CoordinatesFormat["Degrees"] = "hdd.dddd\u00B0";
  CoordinatesFormat["DegreesMinutes"] = "hddd\u00B0mm.mmm'";
  CoordinatesFormat["DegreesMinutesSeconds"] = "hddd\u00B0mm'ss.s";
  return CoordinatesFormat;
}(CoordinatesFormat || {});
var DistanceUnits = /*#__PURE__*/function (DistanceUnits) {
  DistanceUnits["NauticalMiles"] = "nm";
  DistanceUnits["Kilometers"] = "km";
  DistanceUnits["Miles"] = "mi";
  return DistanceUnits;
}(DistanceUnits || {});
var PressureUnits = /*#__PURE__*/function (PressureUnits) {
  PressureUnits["InchesOfMercury"] = "inHg";
  PressureUnits["Millibars"] = "mb";
  PressureUnits["Hectopascals"] = "hPa";
  PressureUnits["Kilopascals"] = "kPa";
  PressureUnits["PoundsPerSquareInch"] = "psi";
  return PressureUnits;
}(PressureUnits || {});
var RunwayLengthUnits = /*#__PURE__*/function (RunwayLengthUnits) {
  RunwayLengthUnits["Feet"] = "ft";
  RunwayLengthUnits["Meters"] = "m";
  return RunwayLengthUnits;
}(RunwayLengthUnits || {});
var TemperatureUnits = /*#__PURE__*/function (TemperatureUnits) {
  TemperatureUnits["Fahrenheit"] = "\u00B0F";
  TemperatureUnits["Celsius"] = "\u00B0C";
  return TemperatureUnits;
}(TemperatureUnits || {});
var TimeFormat = /*#__PURE__*/function (TimeFormat) {
  TimeFormat["TwelveHours"] = "12h";
  TimeFormat["TwentyFourHours"] = "24h";
  return TimeFormat;
}(TimeFormat || {});
var TimeZone = /*#__PURE__*/function (TimeZone) {
  TimeZone["UTC"] = "UTC";
  TimeZone["Local"] = "local";
  return TimeZone;
}(TimeZone || {});
var VelocityUnits = /*#__PURE__*/function (VelocityUnits) {
  VelocityUnits["Knots"] = "kt";
  VelocityUnits["KilometersPerHour"] = "kph";
  VelocityUnits["MilesPerHour"] = "mph";
  return VelocityUnits;
}(VelocityUnits || {});
var VisibilityUnits = /*#__PURE__*/function (VisibilityUnits) {
  VisibilityUnits["StatuteMiles"] = "sm";
  VisibilityUnits["Meters"] = "m";
  return VisibilityUnits;
}(VisibilityUnits || {});
var WindSpeedUnits = /*#__PURE__*/function (WindSpeedUnits) {
  WindSpeedUnits["Knots"] = "kt";
  WindSpeedUnits["KilometersPerHour"] = "kph";
  WindSpeedUnits["MilesPerHour"] = "mph";
  WindSpeedUnits["MetersPerSecond"] = "m/s";
  return WindSpeedUnits;
}(WindSpeedUnits || {});
class UnitSettingsHandler {
  constructor() {
    this.altimeter = signal(AltimeterUnits.InchesOfMercury);
    this.coordinates = signal(CoordinatesFormat.DegreesMinutesSeconds);
    this.distance = signal(DistanceUnits.NauticalMiles);
    this.pressure = signal(PressureUnits.InchesOfMercury);
    this.runwayLength = signal(RunwayLengthUnits.Feet);
    this.temperature = signal(TemperatureUnits.Fahrenheit);
    this.temperatureAloft = signal(TemperatureUnits.Fahrenheit);
    this.timeFormat = signal(TimeFormat.TwelveHours);
    this.timeZone = signal(TimeZone.Local);
    this.velocity = signal(VelocityUnits.Knots);
    this.visibility = signal(VisibilityUnits.StatuteMiles);
    this.windSpeed = signal(WindSpeedUnits.Knots);
  }
  save() {
    return {
      altimeter: this.altimeter(),
      coordinates: this.coordinates(),
      distance: this.distance(),
      pressure: this.pressure(),
      runwayLength: this.runwayLength(),
      temperature: this.temperature(),
      temperatureAloft: this.temperatureAloft(),
      timeFormat: this.timeFormat(),
      timeZone: this.timeZone(),
      velocity: this.velocity(),
      visibility: this.visibility(),
      windSpeed: this.windSpeed()
    };
  }
  restore(state) {
    if (state.altimeter != null) {
      this.altimeter.set(state.altimeter);
    }
    if (state.coordinates != null) {
      this.coordinates.set(state.coordinates);
    }
    if (state.distance != null) {
      this.distance.set(state.distance);
    }
    if (state.pressure != null) {
      this.pressure.set(state.pressure);
    }
    if (state.runwayLength != null) {
      this.runwayLength.set(state.runwayLength);
    }
    if (state.temperature != null) {
      this.temperature.set(state.temperature);
    }
    if (state.temperatureAloft != null) {
      this.temperatureAloft.set(state.temperatureAloft);
    }
    if (state.timeFormat != null) {
      this.timeFormat.set(state.timeFormat);
    }
    if (state.timeZone != null) {
      this.timeZone.set(state.timeZone);
    }
    if (state.velocity != null) {
      this.velocity.set(state.velocity);
    }
    if (state.visibility != null) {
      this.visibility.set(state.visibility);
    }
    if (state.windSpeed != null) {
      this.windSpeed.set(state.windSpeed);
    }
  }
}
let MapSettingsHandler = /*#__PURE__*/(() => {
  class MapSettingsHandler {
    constructor() {
      this.activeMapLayer = signal(MapOverlayLayer.None);
      this.airwayVisibility = signal(AirwayLayerVisibility.Off);
      this.ifrEnrouteState = signal(EnrouteState.Low);
      this.ifrTopographyState = signal(TopographyState.OFF);
      this.vfrTopographyState = signal(TopographyState.ON);
      this.obstacles = new ObstacleSettingsHandler();
      this.units = new UnitSettingsHandler();
    }
    save() {
      return {
        activeMapLayer: this.activeMapLayer(),
        airwayVisibility: this.airwayVisibility(),
        ifrEnrouteState: this.ifrEnrouteState(),
        ifrTopographyState: this.ifrTopographyState(),
        vfrTopographyState: this.vfrTopographyState(),
        obstacles: this.obstacles.save(),
        units: this.units.save()
      };
    }
    restore(state) {
      if (state.activeMapLayer != null) {
        this.activeMapLayer.set(state.activeMapLayer);
      }
      if (state.airwayVisibility != null) {
        this.airwayVisibility.set(state.airwayVisibility);
      }
      if (state.ifrTopographyState != null) {
        this.ifrTopographyState.set(state.ifrTopographyState);
      }
      if (state.vfrTopographyState != null) {
        this.vfrTopographyState.set(state.vfrTopographyState);
      }
      if (state.obstacles != null) {
        this.obstacles.restore(state.obstacles);
      }
      if (state.units != null) {
        this.units.restore(state.units);
      }
    }
    static {
      this.ɵfac = function MapSettingsHandler_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapSettingsHandler)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapSettingsHandler,
        factory: MapSettingsHandler.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapSettingsHandler;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class Layer {
  constructor(injector, radialMenuMapService, content = EmptyContent) {
    this.injector = injector;
    this.radialMenuMapService = radialMenuMapService;
    this.content = content;
    this.map = injector.get(MapTracker).map;
    this.mapAnimation = injector.get(MapAnimationHandler);
    this.mapConfig = injector.get(MapConfigHandler);
    this.mapData = injector.get(MapDataHandler);
    this.mapInfo = injector.get(MapInfo);
    this.mapHighlight = injector.get(MapHighlight);
    this.mapSettings = injector.get(MapSettingsHandler);
    this.mapTheme = injector.get(MapThemeHandler);
  }
  setOpacity(opacity) {
    trySetLayerOpacity(this.map(), this.layerId, opacity);
  }
}
const resolveShouldBeVisible = shouldBeVisible => {
  if (shouldBeVisible == null) {
    // everything should be visible unless specified otherwise
    return true;
  }
  if (typeof shouldBeVisible === 'function') {
    return shouldBeVisible();
  }
  return shouldBeVisible;
};
/**
 * A layer group handles one or more sub-layer objects, providing
 * a single point-of-entry for the sub-layers as a group.
 */
class LayerGroup extends Layer {
  hide() {
    const map = this.map();
    if (map != null) {
      for (const info of this.groupInfo) {
        info.layer.hide(map);
      }
      this.removeSource(map);
    }
  }
  show() {
    const map = this.map();
    if (map != null) {
      const sourceId = this.addSource(map);
      for (const info of this.groupInfo) {
        const visible = resolveShouldBeVisible(info.shouldBeVisible);
        if (visible) {
          info.layer.show(map, sourceId);
        }
      }
    }
  }
  update() {
    const map = this.map();
    if (map != null) {
      const sourceId = this.getSource(map);
      if (sourceId != null) {
        for (const info of this.groupInfo) {
          const visible = resolveShouldBeVisible(info.shouldBeVisible);
          this.updateSubLayer(info.layer, visible, map, sourceId);
        }
      }
    }
  }
  visible() {
    const map = this.map();
    if (map != null) {
      for (const info of this.groupInfo) {
        if (info.layer.visible(map)) {
          return true;
        }
      }
    }
    return false;
  }
  setOpacity(opacity) {
    for (const info of this.groupInfo) {
      const visible = resolveShouldBeVisible(info.shouldBeVisible);
      if (visible) {
        info.layer.setOpacity(opacity);
      }
    }
  }
  updateSubLayer(subLayer, shouldBeVisible, map, sourceId) {
    const visible = subLayer.visible(map);
    if (visible && shouldBeVisible) {
      subLayer.update(map);
    } else if (shouldBeVisible) {
      subLayer.show(map, sourceId);
    } else if (visible) {
      subLayer.hide(map);
    }
  }
}
class SubLayer {
  constructor(injector, radialMenuMapService, sourceLayerId, layerType) {
    this.injector = injector;
    this.radialMenuMapService = radialMenuMapService;
    this.sourceLayerId = sourceLayerId;
    this.layerType = layerType;
    this.layer = {
      radialLayerType: undefined
    };
    this.map = injector.get(MapTracker).map;
    this.mapHighlight = injector.get(MapHighlight);
    this.mapConfig = injector.get(MapConfigHandler);
    this.mapTheme = injector.get(MapThemeHandler);
    this.mapSettings = injector.get(MapSettingsHandler);
    this.mapAnimation = injector.get(MapAnimationHandler);
    this.layer.radialLayerType = layerType;
  }
  hide(map) {
    if (this.layerId != null) {
      if (this.layer.radialLayerType != null) {
        this.radialMenuMapService.removeFromMap(this.layer.radialLayerType, this.layerId);
      }
      map.removeLayer(this.layerId);
      this.layerId = undefined;
      this.sourceId = undefined;
    }
  }
  show(map, sourceId) {
    this.sourceId = sourceId;
    this.layerId = map.layerIds.next(this.LAYER_PREFIX);
    this._show(map);
    if (this.layer.radialLayerType != null) {
      this.radialMenuMapService.addToMap(this.layer.radialLayerType, this.layerId);
    }
  }
  update(map) {
    this._update(map);
  }
  visible(map) {
    return map.hasLayer(this.layerId) ?? false;
  }
  setOpacity(opacity) {
    trySetLayerOpacity(this.map(), this.layerId, opacity);
  }
}
const NOTAM_MAX_RADIUS = 260; // nm
class NotamSubLayer extends SubLayer {
  constructor(injector, sourceLayerId, layerType, radialMenuMapService, highlight) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.highlight = highlight;
  }
  getNotamPriority() {
    return this.highlight ? LayerPriority.HighlightNotam : LayerPriority.Notam;
  }
  getNotamFilter() {
    if (this.highlight) {
      const highlights = this.mapHighlight.notam();
      if (highlights != null && highlights.length > 0) {
        return ['match', ['get', 'notamId'], highlights.flatMap(highlight => highlight.id), true, false];
      }
      return ['boolean', false];
    }
    const time = getUnixTime(new Date());
    const upcomingVisibilitySeconds = this.mapConfig.overlay.notam.upcomingVisibilityHours() * 3600;
    return ['all', ['case', ['has', 'radius'], ['<', ['get', 'radius'], NOTAM_MAX_RADIUS], true], isGraphical(), ['!', isExpired$1(time)],
    // Show only upcoming/active NOTAMs
    ['any', isUpcoming(time, upcomingVisibilitySeconds), isActive$1(time)]];
  }
  getRenderingOrder() {
    const time = getUnixTime(new Date());
    const upcomingVisibilitySeconds = this.mapConfig.overlay.notam.upcomingVisibilityHours() * 3600;
    // Higher order is render above lower order
    return ['case', isActive$1(time), ['case', isRestrictedAndDangerAirspace(), 6, isMilitaryAndWarningAirspace(), 5, isAlertAirspace(), 4, 3], isUpcoming(time, upcomingVisibilitySeconds), ['case', isRestrictedAndDangerAirspace(), 2, 1], 0];
  }
}
class NotamAirportSymbolLayer extends NotamSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'notam-airport-symbol';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const theme = this.mapTheme;
      map.addLayer(LayerPriority.AirportNotams, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(),
        'paint': {
          'text-color': theme.textColor(),
          'text-halo-color': theme.textHaloColor(),
          'text-halo-width': theme.textHaloWidth()
        },
        'layout': {
          'icon-anchor': 'center',
          'icon-image': this.getIconImage(),
          'icon-size': 0.7,
          'symbol-sort-key': 1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerFilter(this.layerId, this.getFilter());
      map.setLayerSymbolProperties(this.layerId, {
        textColor: theme.textColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
  getFilter() {
    const config = this.mapConfig.overlay.notam;
    return ['all', this.getNotamFilter(), ['match', ['get', 'notamClass'], NotamClass.Aerodrome, ['all', ['boolean', config.showTerminalNotams()], ['>=', ['zoom'], ZoomLevel['10NM']]], false]];
  }
  getIconImage() {
    return ['case', ['==', ['get', 'notamClass'], NotamClass.Aerodrome], ['concat', 'airports:', ['get', 'icaoNotamLocation']], ''];
  }
}

/**
 * NOTAM classes handled here:
 *   - Airspace
 *   - Apron
 *   - Ramp
 *   - Taxiway
 *   - FDC
 *   - Other
 *   - Unknown (>= known class values)
 *
 * Note that "Other" is a very specific class. Any classes not explicitly
 * mentioned are not handled or handled elsewhere.
 */
class NotamFillPatternLayer extends NotamSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'notam-fill-pattern';
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('NotamFillPatternLayer only supports numeric opacity.');
    }
    trySetLayerOpacity(this.map(), this.layerId, this.getFillOpacity(opacity));
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.getNotamPriority(), {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'fill',
        'filter': this.getFilter(),
        'paint': {
          'fill-pattern': this.getFillPattern(),
          'fill-outline-color': Color.Transparent.toString()
        },
        'layout': {
          'fill-sort-key': this.getRenderingOrder()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
      map.setLayerPattern(this.layerId, this.getFillPattern());
    }
  }
  getFilter() {
    const config = this.mapConfig.overlay.notam;
    return ['all', this.getNotamFilter(), ['match', ['get', 'notamClass'],
    // All airspace NOTAMs are shown except UAS when not enabled
    NotamClass.Airspace, ['match', ['get', 'notamSubClass'], AirspaceSubClass.UAS, ['boolean', config.showUasNotams()], true],
    // Terminal NOTAMs are only shown when enabled and the NOTAM is for a closure
    [NotamClass.Apron, NotamClass.Ramp, NotamClass.Taxiway], ['all', ['boolean', config.showTerminalNotams()], ['==', ['get', 'notamStatus'], NotamStatus.Closed]],
    // FDC, Other, and Unknown NOTAMs are always shown
    [NotamClass.FDC, NotamClass.OtherAeronauticalInfo], true, ['>=', ['get', 'notamClass'], NotamClass.Unknown]]];
  }
  getFillOpacity(opacityScaleFactor) {
    return ['let', 'initialOpacity', ['match', ['get', 'notamClass'], [NotamClass.Apron, NotamClass.Ramp, NotamClass.Taxiway], 0.3 * (opacityScaleFactor ?? 1.0), 0.25 * (opacityScaleFactor ?? 1.0)], ['let', 'middleOpacity', ['match', ['get', 'notamClass'], [NotamClass.Apron, NotamClass.Ramp, NotamClass.Taxiway], 0.3 * (opacityScaleFactor ?? 1.0), 0.15 * (opacityScaleFactor ?? 1.0)], ['let', 'finalOpacity', ['match', ['get', 'notamClass'], [NotamClass.Apron, NotamClass.Ramp, NotamClass.Taxiway], 0.3 * (opacityScaleFactor ?? 1.0), 0], ['interpolate', ['linear'], ['zoom'], 0, ['var', 'initialOpacity'], 9, ['var', 'middleOpacity'], 10, ['var', 'finalOpacity']]]]];
  }
  getAirspaceFillPattern(time, upcomingVisibilitySeconds) {
    return ['case', isInactive(time, upcomingVisibilitySeconds), MapIcon.Notam_FillPatternGray, isUpcoming(time, upcomingVisibilitySeconds), ['case', isRestrictedAndDangerAirspace(), MapIcon.Notam_FillPatternYellow, MapIcon.Notam_FillPatternDarkYellow], ['case', isRestrictedAndDangerAirspace(), MapIcon.Notam_FillPatternRed, isMilitaryAndWarningAirspace(), MapIcon.Notam_FillPatternOrange, isAlertAirspace(), MapIcon.Notam_FillPatternPurple, MapIcon.Notam_FillPatternGray]];
  }
  getFdcOrUnknownFillPattern(time, upcomingVisibilitySeconds) {
    return ['case', isInactive(time, upcomingVisibilitySeconds), MapIcon.Notam_FillPatternTransparent, isUpcoming(time, upcomingVisibilitySeconds), MapIcon.Notam_FillPatternDarkYellow, MapIcon.Notam_FillPatternGray];
  }
  getTerminalFillPattern(time, upcomingVisibilitySeconds) {
    return ['case', isInactive(time, upcomingVisibilitySeconds), MapIcon.Notam_FillPatternTransparent, isUpcoming(time, upcomingVisibilitySeconds), MapIcon.Notam_FillPatternYellow, ['case', isConditional(), MapIcon.Notam_FillPatternOrange, MapIcon.Notam_FillPatternRed]];
  }
  getFillPattern() {
    const time = getUnixTime(new Date());
    const upcomingVisibilitySeconds = this.mapConfig.overlay.notam.upcomingVisibilityHours() * 3600;
    return ['match', ['get', 'notamClass'], NotamClass.Airspace, this.getAirspaceFillPattern(time, upcomingVisibilitySeconds), [NotamClass.Apron, NotamClass.Ramp, NotamClass.Taxiway], this.getTerminalFillPattern(time, upcomingVisibilitySeconds), [NotamClass.FDC, NotamClass.OtherAeronauticalInfo], this.getFdcOrUnknownFillPattern(time, upcomingVisibilitySeconds), ['case', ['>=', ['get', 'notamClass'], NotamClass.Unknown], this.getFdcOrUnknownFillPattern(time, upcomingVisibilitySeconds), MapIcon.Notam_FillPatternBlank]];
  }
}
class NotamHighlightShapeLayer extends NotamSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'notam-highlight-shape';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.getNotamPriority(), {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': this.getFilter(),
        'paint': {
          'line-color': 'white',
          'line-width': interpolateZoom([[0, 6.0], [9, 12.0]])
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  getFilter() {
    return ['all', this.getNotamFilter(), ['match', ['get', 'notamClass'],
    // Runways are handled elsewhere
    NotamClass$1.RUNWAY, false, true]];
  }
}
class NotamHighlightSymbolLayer extends NotamSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'notam-highlight-point';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.getNotamPriority(), {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getNotamFilter(),
        'layout': {
          'icon-allow-overlap': true,
          'icon-anchor': this.getIconAnchor(),
          'icon-image': this.getIconImage()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getNotamFilter());
    }
  }
  getIconAnchor() {
    return ['match', ['get', 'notamClass'], NotamClass.Obstacle, 'bottom', 'center'];
  }
  getIconImage() {
    return ['match', ['get', 'notamClass'], NotamClass.Obstacle, getObstructionIconImage(MapIcon.Notam_ObstTallUnlightedHighlighted, MapIcon.Notam_ObstTallUnlightedInverseHighlighted, MapIcon.Notam_ObstShortUnlightedHighlighted, MapIcon.Notam_ObstShortUnlightedInverseHighlighted, MapIcon.Notam_ObstTallLightedHighlighted, MapIcon.Notam_ObstTallLightedInverseHighlighted, MapIcon.Notam_ObstShortLightedHighlighted, MapIcon.Notam_ObstShortLightedInverseHighlighted, this.mapConfig.overlay.obstacle.visible()), ''];
  }
}
class NotamOutlineLayer extends NotamSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'notam-outline';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.getNotamPriority(), {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': this.getFilter(),
        'paint': {
          'line-color': this.getLineColor(),
          'line-width': interpolateZoom([[0, 0.125], [9, 2.0]])
        },
        'layout': {
          'line-join': 'round',
          'line-sort-key': this.getRenderingOrder()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
      map.setLayerColor(this.layerId, this.getLineColor());
    }
  }
  getFilter() {
    const config = this.mapConfig.overlay.notam;
    return ['all', this.getNotamFilter(), ['match', ['get', 'notamClass'],
    // All airspace NOTAMs are shown except UAS when not enabled
    NotamClass.Airspace, ['match', ['get', 'notamSubClass'], AirspaceSubClass.UAS, ['boolean', config.showUasNotams()], true],
    // FDC, Other, and Unknown NOTAMs are always shown
    [NotamClass.FDC, NotamClass.OtherAeronauticalInfo], true, ['>=', ['get', 'notamClass'], NotamClass.Unknown]]];
  }
  getActiveAirspaceColor() {
    return ['case', isRestrictedAndDangerAirspace(), Color.Red.toString(), isMilitaryAndWarningAirspace(), Color.Orange.toString(), isAlertAirspace(), Color.Purple.toString(), Color.Gray.toString()];
  }
  getLineColor() {
    const time = getUnixTime(new Date());
    const upcomingVisibilitySeconds = this.mapConfig.overlay.notam.upcomingVisibilityHours() * 3600;
    return ['case', isInactive(time, upcomingVisibilitySeconds), Color.Gray.toString(), isUpcoming(time, upcomingVisibilitySeconds), ['case', isRestrictedAndDangerAirspace(), Color.Yellow.toString(), Color.DarkYellow.toString()], ['match', ['get', 'notamClass'], NotamClass.Airspace, this.getActiveAirspaceColor(), [NotamClass.FDC, NotamClass.OtherAeronauticalInfo], Color.Gray.toString(), ['case', ['>=', ['get', 'notamClass'], NotamClass.Unknown], Color.Gray.toString(), Color.Transparent.toString()]]];
  }
}
class NotamSymbolLayer extends NotamSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'notam-symbol';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const theme = this.mapTheme;
      map.addLayer(this.getNotamPriority(), {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(),
        'paint': {
          'text-color': theme.textColor(),
          'text-halo-color': theme.textHaloColor(),
          'text-halo-width': theme.textHaloWidth()
        },
        'layout': {
          'icon-anchor': this.getIconAnchor(),
          'icon-image': this.getIconImage(),
          'icon-size': this.getIconSize(),
          'symbol-sort-key': this.getRenderingOrder(),
          'text-field': this.getTextField(),
          'text-font': ['Roboto'],
          'text-offset': [0, -1.5],
          'text-letter-spacing': 0.1
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerFilter(this.layerId, this.getFilter());
      map.setLayerSymbolProperties(this.layerId, {
        textColor: theme.textColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
  getRenderingOrder() {
    return ['match', ['get', 'notamClass'], NotamClass.Aerodrome, 1, 2];
  }
  getFilter() {
    const config = this.mapConfig.overlay.notam;
    return ['all', this.getNotamFilter(), ['match', ['get', 'notamClass'], NotamClass.Aerodrome, ['all', ['boolean', config.showTerminalNotams()], ['>=', ['zoom'], ZoomLevel['10NM']]], NotamClass.Navigation, ['all', ['boolean', config.showNavaidNotams()], ['>=', ['zoom'], ZoomLevel['5NM']]], NotamClass.Obstacle, ['all', ['boolean', config.showObstacleNotams()], ['>=', ['zoom'], ZoomLevel['2NM']]], false]];
  }
  getIconAnchor() {
    return ['match', ['get', 'notamClass'], NotamClass.Obstacle, 'bottom', 'center'];
  }
  getAerodromeIconImage() {
    return ['case', ['boolean', isConditional()], ['match', ['get', 'notamStatus'], NotamWarningStatuses, MapIcon.Notam_AdWarningTimed, MapIcon.Notam_AdCautionTimed], ['match', ['get', 'notamStatus'], NotamWarningStatuses, MapIcon.Notam_AdWarning, MapIcon.Notam_AdCaution]];
  }
  getNavigationIconImage() {
    return ['case', this.getCaseForNavSubClass(NavigationSubClass.NDB, NavQCode.NDB), this.getIconForNavSubClass(MapIcon.Notam_NavNdbUpcoming, MapIcon.Notam_NavNdbCaution, MapIcon.Notam_NavNdbWarning, MapIcon.Notam_IFR_NavNdbUpcoming, MapIcon.Notam_IFR_NavNdbCaution, MapIcon.Notam_IFR_NavNdbWarning), this.getCaseForNavSubClass(NavigationSubClass.DME, NavQCode.DME), this.getIconForNavSubClass(MapIcon.Notam_NavDmeUpcoming, MapIcon.Notam_NavDmeCaution, MapIcon.Notam_NavDmeWarning, MapIcon.Notam_IFR_NavDmeUpcoming, MapIcon.Notam_IFR_NavDmeCaution, MapIcon.Notam_IFR_NavDmeWarning), this.getCaseForNavSubClass(NavigationSubClass.TACAN, NavQCode.TACAN), this.getIconForNavSubClass(MapIcon.Notam_NavTacanUpcoming, MapIcon.Notam_NavTacanCaution, MapIcon.Notam_NavTacanWarning, MapIcon.Notam_IFR_NavTacanUpcoming, MapIcon.Notam_IFR_NavTacanCaution, MapIcon.Notam_IFR_NavTacanWarning), this.getCaseForNavSubClass(NavigationSubClass.VORTAC, NavQCode.VORTAC), this.getIconForNavSubClass(MapIcon.Notam_NavVortacUpcoming, MapIcon.Notam_NavVortacCaution, MapIcon.Notam_NavVortacWarning, MapIcon.Notam_IFR_NavVortacUpcoming, MapIcon.Notam_IFR_NavVortacCaution, MapIcon.Notam_IFR_NavVortacWarning), this.getCaseForNavSubClass(NavigationSubClass.VOR, NavQCode.VOR), this.getIconForNavSubClass(MapIcon.Notam_NavVorUpcoming, MapIcon.Notam_NavVorCaution, MapIcon.Notam_NavVorWarning, MapIcon.Notam_IFR_NavVorUpcoming, MapIcon.Notam_IFR_NavVorCaution, MapIcon.Notam_IFR_NavVorWarning), this.getCaseForNavSubClass(NavigationSubClass.VOR_DME, NavQCode.VOR_DME), this.getIconForNavSubClass(MapIcon.Notam_NavVorDmeUpcoming, MapIcon.Notam_NavVorDmeCaution, MapIcon.Notam_NavVorDmeWarning, MapIcon.Notam_IFR_NavVorDmeUpcoming, MapIcon.Notam_IFR_NavVorDmeCaution, MapIcon.Notam_IFR_NavVorDmeWarning), ''];
  }
  getCaseForNavSubClass(subClass, qCode) {
    if (qCode.length != 3) {
      console.error('invalid qCode', qCode);
      return ['boolean', false];
    }
    return ['any', ['all', ['has', 'qCode'], ['==', ['slice', ['get', 'qCode'], 0, 2], qCode]], ['==', ['get', 'notamSubClass'], subClass]];
  }
  getIconForNavSubClass(upcomingVfr, cautionVfr, warningVfr, upcomingIfr, cautionIfr, warningIfr) {
    const time = getUnixTime(new Date());
    const upcomingVisibilitySeconds = this.mapConfig.overlay.notam.upcomingVisibilityHours() * 3600;
    const useIfr = this.mapTheme.flightRule() === FlightRule.Ifr;
    return ['case', isInactive(time, upcomingVisibilitySeconds), '', isUpcoming(time, upcomingVisibilitySeconds), useIfr ? upcomingIfr : upcomingVfr, ['match', ['get', 'notamStatus'], NotamWarningStatuses, useIfr ? warningIfr : warningVfr, useIfr ? cautionIfr : cautionVfr]];
  }
  getIconImage() {
    return ['match', ['get', 'notamClass'], NotamClass.Aerodrome, this.getAerodromeIconImage(), NotamClass.Navigation, this.getNavigationIconImage(), NotamClass.Obstacle, getObstructionIconImage(MapIcon.Notam_ObstTallUnlighted, MapIcon.Notam_ObstTallUnlightedInverse, MapIcon.Notam_ObstShortUnlighted, MapIcon.Notam_ObstShortUnlightedInverse, MapIcon.Notam_ObstTallLighted, MapIcon.Notam_ObstTallLightedInverse, MapIcon.Notam_ObstShortLighted, MapIcon.Notam_ObstShortLightedInverse, this.mapConfig.overlay.obstacle.visible()), ''];
  }
  getIconSize() {
    return ['match', ['get', 'notamClass'], NotamClass.Obstacle, 0.5, 0.7];
  }
  getTextField() {
    return ['match', ['get', 'notamClass'], NotamClass.Obstacle, '', ['coalesce', ['get', 'icaoNotamLocation'], 'UNK']];
  }
}
class NotamTextLayer extends NotamSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'notam-text';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const theme = this.mapTheme;
      map.addLayer(this.getNotamPriority(), {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(),
        'paint': {
          'text-color': theme.textColor(),
          'text-halo-color': theme.textHaloColor(),
          'text-halo-width': theme.textHaloWidth()
        },
        'layout': {
          'symbol-placement': 'line',
          'text-anchor': 'bottom',
          'text-font': ['Roboto'],
          'text-size': 14,
          'text-field': this.getTextField(),
          'symbol-sort-key': this.getRenderingOrder()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerFilter(this.layerId, this.getFilter());
      map.setLayerSymbolProperties(this.layerId, {
        textColor: theme.textColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
  getFilter() {
    const config = this.mapConfig.overlay.notam;
    return ['all', this.getNotamFilter(), ['match', ['get', 'notamClass'], NotamClass.Airspace, ['match', ['get', 'notamSubClass'], AirspaceSubClass.UAS, ['boolean', config.showUasNotams()], true], [NotamClass.FDC, NotamClass.OtherAeronauticalInfo], true, ['>=', ['get', 'notamClass'], NotamClass.Unknown]]];
  }
  getTextField() {
    const time = getUnixTime(new Date());
    const upcomingVisibilitySeconds = this.mapConfig.overlay.notam.upcomingVisibilityHours() * 3600;
    return ['step', ['zoom'], '', 7, ['concat', ['match', ['get', 'notamClass'], NotamClass.Airspace, ['match', ['get', 'notamSubClass'], AirspaceSubClass.Prohibited, `Prohibited Area`, AirspaceSubClass.Restricted, `Restricted Area`, AirspaceSubClass.Danger, `Danger Area`, AirspaceSubClass.MilitaryOperations, `Military Operations Area`, AirspaceSubClass.ScheduledExercises, `Scheduled Exercises`, AirspaceSubClass.Warning, `Warning Area`, AirspaceSubClass.Alert, `Alert Area`, AirspaceSubClass.BannerTowing, `Banner Towing`, AirspaceSubClass.GliderFlying, `Glider Flying Area`, AirspaceSubClass.ParachuteJumping, `Parachute Jumping Area`, AirspaceSubClass.UAS, `UAS Operating Area`, AirspaceSubClass.TetheredBalloons, `Tethered Balloons/Kits`, AirspaceSubClass.Gathering, `Gathering of Aircraft`, AirspaceSubClass.Aerobatic, `Aerobatic Area`, AirspaceSubClass.FormationTransit, `Formation Transit Flight`, `Miscellaneous`], NotamClass.FDC, `FDC`, `Miscellaneous`], ['case', isInactive(time, upcomingVisibilitySeconds), '', isUpcoming(time, upcomingVisibilitySeconds), ' Upcoming', isActive$1(time), ' Activated', ''], ': ', getMinAltitudeText(), ' - ', getMaxAltitudeText()]];
  }
}
const NOTAM_SOURCE_LAYER_POINT = 'PointNotam';
const NOTAM_SOURCE_LAYER_POLYGON = 'ShapeNotam';
class NotamLayer extends LayerGroup {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'notam';
    this.groupInfo = this.createGroupInfo();
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      minzoom: 0,
      maxzoom: 11
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
  createGroupInfo() {
    const info = [];
    this.addNotamLayers(info);
    this.addNotamHighlightLayers(info);
    return info;
  }
  addNotamLayers(list) {
    list.push({
      layer: new NotamFillPatternLayer(this.injector, NOTAM_SOURCE_LAYER_POLYGON, LayerType.Notams, this.radialMenuMapService, false),
      shouldBeVisible: undefined
    });
    list.push({
      layer: new NotamOutlineLayer(this.injector, NOTAM_SOURCE_LAYER_POLYGON, LayerType.Notams, this.radialMenuMapService, false),
      shouldBeVisible: undefined
    });
    list.push({
      layer: new NotamTextLayer(this.injector, NOTAM_SOURCE_LAYER_POLYGON, LayerType.Notams, this.radialMenuMapService, false),
      shouldBeVisible: undefined
    });
    list.push({
      layer: new NotamSymbolLayer(this.injector, NOTAM_SOURCE_LAYER_POINT, LayerType.Notams, this.radialMenuMapService, false),
      shouldBeVisible: undefined
    });
    list.push({
      layer: new NotamAirportSymbolLayer(this.injector, NOTAM_SOURCE_LAYER_POINT, LayerType.Notams, this.radialMenuMapService, false),
      shouldBeVisible: undefined
    });
  }
  addNotamHighlightLayers(list) {
    const shouldBeVisible = () => this.mapHighlight.notam() != null;
    list.push({
      layer: new NotamHighlightShapeLayer(this.injector, NOTAM_SOURCE_LAYER_POLYGON, LayerType.Notams, this.radialMenuMapService, true),
      shouldBeVisible
    });
    list.push({
      layer: new NotamHighlightSymbolLayer(this.injector, NOTAM_SOURCE_LAYER_POINT, LayerType.Notams, this.radialMenuMapService, true),
      shouldBeVisible
    });
    // Highlighted layer has its own fill, outline, and text to overlay on top of all layers
    list.push({
      layer: new NotamFillPatternLayer(this.injector, NOTAM_SOURCE_LAYER_POLYGON, LayerType.Notams, this.radialMenuMapService, true),
      shouldBeVisible
    });
    list.push({
      layer: new NotamOutlineLayer(this.injector, NOTAM_SOURCE_LAYER_POLYGON, LayerType.Notams, this.radialMenuMapService, true),
      shouldBeVisible
    });
    list.push({
      layer: new NotamTextLayer(this.injector, NOTAM_SOURCE_LAYER_POLYGON, LayerType.Notams, this.radialMenuMapService, true),
      shouldBeVisible
    });
  }
}
const NoOptions = {};
const FuelPriceIconOptions = {
  content: [25, 0, 31, 24],
  stretchX: [[26, 30]]
};
const PirepIconWithAltOptions = {
  content: [26, 0, 32, 28],
  stretchX: [[26, 32]]
};
const HighlightDetailBoxIconOptions = {
  content: [4, 8, 146, 56],
  stretchX: [[4, 146]],
  stretchY: [[8, 56]]
};
var PirepWeatherCondition = /*#__PURE__*/function (PirepWeatherCondition) {
  PirepWeatherCondition["PIREP"] = "pirep";
  PirepWeatherCondition["ICING_NEG"] = "icing_neg";
  PirepWeatherCondition["ICING_TR"] = "icing_tr";
  PirepWeatherCondition["ICING_TR_LGT"] = "icing_tr_lgt";
  PirepWeatherCondition["ICING_LGT"] = "icing_lgt";
  PirepWeatherCondition["ICING_LGT_MOD"] = "icing_lgt_mod";
  PirepWeatherCondition["ICING_MOD"] = "icing_mod";
  PirepWeatherCondition["ICING_MOD_SEV"] = "icing_mod_sev";
  PirepWeatherCondition["ICING_SEV"] = "icing_sev";
  PirepWeatherCondition["SKYCOND_UNK"] = "skycond_unknown";
  PirepWeatherCondition["SKYCOND_CLR"] = "skycond_clr";
  PirepWeatherCondition["SKYCOND_FEW"] = "skycond_few";
  PirepWeatherCondition["SKYCOND_SCT"] = "skycond_sct";
  PirepWeatherCondition["SKYCOND_BKN"] = "skycond_bkn";
  PirepWeatherCondition["SKYCOND_OVC"] = "skycond_ovc";
  PirepWeatherCondition["SKYCOND_IMC"] = "skycond_imc";
  PirepWeatherCondition["TURBULENCE_NEG"] = "turbulence_neg";
  PirepWeatherCondition["TURBULENCE_LGT"] = "turbulence_lgt";
  PirepWeatherCondition["TURBULENCE_LGT_MOD"] = "turbulence_lgt_mod";
  PirepWeatherCondition["TURBULENCE_MOD"] = "turbulence_mod";
  PirepWeatherCondition["TURBULENCE_MOD_SEV"] = "turbulence_mod_sev";
  PirepWeatherCondition["TURBULENCE_SEV"] = "turbulence_sev";
  PirepWeatherCondition["TURBULENCE_EXT"] = "turbulence_ext";
  return PirepWeatherCondition;
}(PirepWeatherCondition || {});
const defineIcon = (id, src, opts, scaling) => {
  return [id, {
    id,
    src,
    prefix: IconPrefixType.Map,
    opts,
    scaling
  }];
};
class MapIconDictionary extends Map {
  get(key) {
    const value = super.get(key);
    if (value == null) {
      console.error('unknown icon definition:', key);
      return {
        id: MapIcon.Invalid
      };
    }
    return value;
  }
}
const MapIconDefinitions = new MapIconDictionary([defineIcon(MapIcon.Airspace_ARTCC_FIR, 'airspace/artcc-fir.png'), defineIcon(MapIcon.Airspace_Air_Defense_Identification_Zone, 'airspace/ADIZ.png'), defineIcon(MapIcon.Airspace_Military_Operations_Area, 'airspace/MOA.png'), defineIcon(MapIcon.Airspace_Nature_Conservation_Area, 'airspace/conservation-area.png'), defineIcon(MapIcon.Airspace_Alert_Area, 'airspace/alert-area.png'), defineIcon(MapIcon.Airspace_Restricted_Area, 'airspace/restricted.png'), defineIcon(MapIcon.Airspace_A_2, 'airspace/a2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_ADVS_2, 'airspace/advs2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_AERO_2, 'airspace/aero2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_ALRT_2, 'airspace/alrt2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_ATZ_2, 'airspace/atz2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_B_2, 'airspace/b2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_C_2, 'airspace/c2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_CBA_2, 'airspace/cba2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_CFZ_2, 'airspace/cfz2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_CTR_2, 'airspace/ctr2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_D_2, 'airspace/d2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_E_2, 'airspace/e2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_FIZ_2, 'airspace/fiz2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_FMC_2, 'airspace/fmc2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_G_2, 'airspace/g2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_M_2, 'airspace/m2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_MATZ_2, 'airspace/matz2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_MBZ_2, 'airspace/mbz2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_MCTR_2, 'airspace/mctr2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_MODC_2, 'airspace/modc2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_MTMA_2, 'airspace/mtma2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_NSA_2, 'airspace/nsa2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_P_2, 'airspace/p2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_PJA_2, 'airspace/pja2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_R_2, 'airspace/r2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_RMZ_2, 'airspace/rmz2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_SR_2, 'airspace/sr2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_T_2, 'airspace/t2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_TFR_2, 'airspace/tfr2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_TIA_2, 'airspace/tia2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_TIZ_2, 'airspace/tiz2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_TMA_2, 'airspace/tma2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_TMZ_2, 'airspace/tmz2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_W_2, 'airspace/w2.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_A_3, 'airspace/a3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_ADVS_3, 'airspace/advs3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_AERO_3, 'airspace/aero3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_ALRT_3, 'airspace/alrt3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_ATZ_3, 'airspace/atz3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_B_3, 'airspace/b3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_C_3, 'airspace/c3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_CBA_3, 'airspace/cba3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_CFZ_3, 'airspace/cfz3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_CTR_3, 'airspace/ctr3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_D_3, 'airspace/d3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_E_3, 'airspace/e3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_FIZ_3, 'airspace/fiz3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_FMC_3, 'airspace/fmc3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_G_3, 'airspace/g3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_M_3, 'airspace/m3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_MATZ_3, 'airspace/matz3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_MBZ_3, 'airspace/mbz3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_MCTR_3, 'airspace/mctr3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_MODC_3, 'airspace/modc3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_MTMA_3, 'airspace/mtma3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_NSA_3, 'airspace/nsa3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_P_3, 'airspace/p3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_PJA_3, 'airspace/pja3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_R_3, 'airspace/r3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_RMZ_3, 'airspace/rmz3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_SR_3, 'airspace/sr3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_T_3, 'airspace/t3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_TFR_3, 'airspace/tfr3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_TIA_3, 'airspace/tia3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_TIZ_3, 'airspace/tiz3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_TMA_3, 'airspace/tma3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_TMZ_3, 'airspace/tmz3.svg', NoOptions, 2), defineIcon(MapIcon.Airspace_W_3, 'airspace/w3.svg', NoOptions, 2), defineIcon(MapIcon.FuelPrices_Gray, 'fuel-price/circle-gray.svg', FuelPriceIconOptions), defineIcon(MapIcon.FuelPrices_Green, 'fuel-price/circle-green.svg', FuelPriceIconOptions), defineIcon(MapIcon.FuelPrices_Red, 'fuel-price/circle-red.svg', FuelPriceIconOptions), defineIcon(MapIcon.FuelPrices_Yellow, 'fuel-price/circle-yellow.svg', FuelPriceIconOptions), defineIcon(MapIcon.Icing_Empty, 'icing/empty-pattern.svg'), defineIcon(MapIcon.Icing_ProbLow, 'icing/probability-low.svg'), defineIcon(MapIcon.Icing_ProbMedium, 'icing/probability-medium.svg'), defineIcon(MapIcon.Icing_ProbHigh, 'icing/probability-high.svg'), defineIcon(MapIcon.Icing_Trace, 'icing/trace.svg'), defineIcon(MapIcon.Icing_Light, 'icing/light.svg'), defineIcon(MapIcon.Icing_Moderate, 'icing/moderate.svg'), defineIcon(MapIcon.Icing_Severe, 'icing/severe.svg'), defineIcon(MapIcon.Icing_Extreme, 'icing/extreme.svg'), defineIcon(MapIcon.Icing_TraceSolid, 'icing/trace-sld.svg'), defineIcon(MapIcon.Icing_LightSolid, 'icing/light-sld.svg'), defineIcon(MapIcon.Icing_ModerateSolid, 'icing/moderate-sld.svg'), defineIcon(MapIcon.Icing_SevereSolid, 'icing/severe-sld.svg'), defineIcon(MapIcon.Icing_ExtremeSolid, 'icing/extreme-sld.svg'), defineIcon(MapIcon.Icing_Solid, 'icing/sld.svg'), defineIcon(MapIcon.InternetTraffic_Biplane, 'internet-traffic/biplane.svg'), defineIcon(MapIcon.InternetTraffic_BusinessJet, 'internet-traffic/business-jet.svg'), defineIcon(MapIcon.InternetTraffic_CommercialDualEngine, 'internet-traffic/commercial-dual-engine.svg'), defineIcon(MapIcon.InternetTraffic_CommercialFourEngine, 'internet-traffic/commercial-four-engine.svg'), defineIcon(MapIcon.InternetTraffic_CommercialRearEngine, 'internet-traffic/commercial-rear-engine.svg'), defineIcon(MapIcon.InternetTraffic_Fighter, 'internet-traffic/fighter.svg'), defineIcon(MapIcon.InternetTraffic_FloatPlane, 'internet-traffic/float-plane.svg'), defineIcon(MapIcon.InternetTraffic_Generic, 'internet-traffic/generic.svg'), defineIcon(MapIcon.InternetTraffic_Helicopter, 'internet-traffic/helicopter.svg'), defineIcon(MapIcon.InternetTraffic_Highlight, 'internet-traffic/highlight.svg'), defineIcon(MapIcon.InternetTraffic_HighlightDetailBox, `internet-traffic/highlight-aircraft-detail.svg`, HighlightDetailBoxIconOptions), defineIcon(MapIcon.InternetTraffic_HighWindTwin, 'internet-traffic/high-wind-twin.svg'), defineIcon(MapIcon.InternetTraffic_HighWing, 'internet-traffic/high-wing.svg'), defineIcon(MapIcon.InternetTraffic_LowWingTwin, 'internet-traffic/low-wing-twin.svg'), defineIcon(MapIcon.InternetTraffic_LowWing, 'internet-traffic/low-wing.svg'), defineIcon(MapIcon.InternetTraffic_MilitaryTransport, 'internet-traffic/military-transport.svg'), defineIcon(MapIcon.InternetTraffic_Sr, 'internet-traffic/sr.svg'), defineIcon(MapIcon.InternetTraffic_WarBird, 'internet-traffic/warbird.svg'), defineIcon(MapIcon.InternetTraffic_Xplane, 'internet-traffic/x-plane.svg'), defineIcon(MapIcon.Lightning, 'lightning/lightning.png', {
  sdf: true
}), defineIcon(MapIcon.Navaid_DME, 'navaid/vfr/small/dme_16.svg'), defineIcon(MapIcon.Navaid_NDB_Marker, 'navaid/vfr/ndb-marker.svg'), defineIcon(MapIcon.Navaid_NDB, 'navaid/vfr/ndb.svg'), defineIcon(MapIcon.Navaid_TACAN, 'navaid/vfr/small/tacan_16.svg'), defineIcon(MapIcon.Navaid_VOR, 'navaid/vfr/small/vor_16.svg'), defineIcon(MapIcon.Navaid_VOR_Compass, 'navaid/compass_rose.svg'), defineIcon(MapIcon.Navaid_VOR_DME, 'navaid/vfr/small/vor-dme_16.svg'), defineIcon(MapIcon.Navaid_VORTAC, 'navaid/vfr/small/vortac_16.svg'), defineIcon(MapIcon.Navaid_WPT, 'navaid/vfr/wpt.svg'), defineIcon(MapIcon.Navaid_WPT_RNAV, 'navaid/vfr/wpt-rnav.svg'), defineIcon(MapIcon.Navaid_IFR_DME, 'navaid/ifr/small/dme_16.svg'), defineIcon(MapIcon.Navaid_IFR_NDB_Marker, 'navaid/ifr/ndb-marker.svg'), defineIcon(MapIcon.Navaid_IFR_NDB, 'navaid/ifr/ndb.svg'), defineIcon(MapIcon.Navaid_IFR_TACAN, 'navaid/ifr/small/tacan_16.svg'), defineIcon(MapIcon.Navaid_IFR_VOR, 'navaid/ifr/small/vor_16.svg'), defineIcon(MapIcon.Navaid_IFR_VOR_DME, 'navaid/ifr/small/vor-dme_16.svg'), defineIcon(MapIcon.Navaid_IFR_VORTAC, 'navaid/ifr/small/vortac_16.svg'), defineIcon(MapIcon.Navaid_IFR_WPT, 'navaid/ifr/wpt.svg'), defineIcon(MapIcon.Navaid_IFR_WPT_RNAV, 'navaid/ifr/wpt-rnav.svg'), defineIcon(MapIcon.Notam_AdCaution, `notam/ad-caution.svg`), defineIcon(MapIcon.Notam_AdCautionTimed, `notam/ad-caution-timed.svg`), defineIcon(MapIcon.Notam_AdWarning, `notam/ad-warning.svg`), defineIcon(MapIcon.Notam_AdWarningTimed, `notam/ad-warning-timed.svg`), defineIcon(MapIcon.Notam_FillPatternBlank, `notam/fill_pattern_blank.svg`), defineIcon(MapIcon.Notam_FillPatternDarkYellow, `notam/fill_pattern_dark_yellow.svg`), defineIcon(MapIcon.Notam_FillPatternGray, `notam/fill_pattern_gray.svg`), defineIcon(MapIcon.Notam_FillPatternOrange, `notam/fill_pattern_orange.svg`), defineIcon(MapIcon.Notam_FillPatternPurple, `notam/fill_pattern_purple.svg`), defineIcon(MapIcon.Notam_FillPatternRed, `notam/fill_pattern_red.svg`), defineIcon(MapIcon.Notam_FillPatternTransparent, `notam/fill_pattern_transparent.svg`), defineIcon(MapIcon.Notam_FillPatternYellow, `notam/fill_pattern_yellow.svg`), defineIcon(MapIcon.Notam_NavDmeCaution, `notam/vfr/dme-caution.svg`), defineIcon(MapIcon.Notam_NavDmeUpcoming, `notam/vfr/dme-upcoming.svg`), defineIcon(MapIcon.Notam_NavDmeWarning, `notam/vfr/dme-warning.svg`), defineIcon(MapIcon.Notam_NavNdbCaution, `notam/vfr/ndb-caution.svg`), defineIcon(MapIcon.Notam_NavNdbUpcoming, `notam/vfr/ndb-upcoming.svg`), defineIcon(MapIcon.Notam_NavNdbWarning, `notam/vfr/ndb-warning.svg`), defineIcon(MapIcon.Notam_NavTacanCaution, `notam/vfr/tacan-caution.svg`), defineIcon(MapIcon.Notam_NavTacanUpcoming, `notam/vfr/tacan-upcoming.svg`), defineIcon(MapIcon.Notam_NavTacanWarning, `notam/vfr/tacan-warning.svg`), defineIcon(MapIcon.Notam_NavVorCaution, `notam/vfr/vor-caution.svg`), defineIcon(MapIcon.Notam_NavVorUpcoming, `notam/vfr/vor-upcoming.svg`), defineIcon(MapIcon.Notam_NavVorWarning, `notam/vfr/vor-warning.svg`), defineIcon(MapIcon.Notam_NavVorDmeCaution, `notam/vfr/vor-dme-caution.svg`), defineIcon(MapIcon.Notam_NavVorDmeUpcoming, `notam/vfr/vor-dme-upcoming.svg`), defineIcon(MapIcon.Notam_NavVorDmeWarning, `notam/vfr/vor-dme-warning.svg`), defineIcon(MapIcon.Notam_NavVortacCaution, `notam/vfr/vortac-caution.svg`), defineIcon(MapIcon.Notam_NavVortacUpcoming, `notam/vfr/vortac-upcoming.svg`), defineIcon(MapIcon.Notam_NavVortacWarning, `notam/vfr/vortac-warning.svg`), defineIcon(MapIcon.Notam_IFR_NavDmeCaution, `notam/ifr/dme-caution.svg`), defineIcon(MapIcon.Notam_IFR_NavDmeUpcoming, `notam/ifr/dme-upcoming.svg`), defineIcon(MapIcon.Notam_IFR_NavDmeWarning, `notam/ifr/dme-warning.svg`), defineIcon(MapIcon.Notam_IFR_NavNdbCaution, `notam/ifr/ndb-caution.svg`), defineIcon(MapIcon.Notam_IFR_NavNdbUpcoming, `notam/ifr/ndb-upcoming.svg`), defineIcon(MapIcon.Notam_IFR_NavNdbWarning, `notam/ifr/ndb-warning.svg`), defineIcon(MapIcon.Notam_IFR_NavTacanCaution, `notam/ifr/tacan-caution.svg`), defineIcon(MapIcon.Notam_IFR_NavTacanUpcoming, `notam/ifr/tacan-upcoming.svg`), defineIcon(MapIcon.Notam_IFR_NavTacanWarning, `notam/ifr/tacan-warning.svg`), defineIcon(MapIcon.Notam_IFR_NavVorCaution, `notam/ifr/vor-caution.svg`), defineIcon(MapIcon.Notam_IFR_NavVorUpcoming, `notam/ifr/vor-upcoming.svg`), defineIcon(MapIcon.Notam_IFR_NavVorWarning, `notam/ifr/vor-warning.svg`), defineIcon(MapIcon.Notam_IFR_NavVorDmeCaution, `notam/ifr/vor-dme-caution.svg`), defineIcon(MapIcon.Notam_IFR_NavVorDmeUpcoming, `notam/ifr/vor-dme-upcoming.svg`), defineIcon(MapIcon.Notam_IFR_NavVorDmeWarning, `notam/ifr/vor-dme-warning.svg`), defineIcon(MapIcon.Notam_IFR_NavVortacCaution, `notam/ifr/vortac-caution.svg`), defineIcon(MapIcon.Notam_IFR_NavVortacUpcoming, `notam/ifr/vortac-upcoming.svg`), defineIcon(MapIcon.Notam_IFR_NavVortacWarning, `notam/ifr/vortac-warning.svg`), defineIcon(MapIcon.Notam_ObstShortLighted, `notam/obst-short-lighted.svg`), defineIcon(MapIcon.Notam_ObstShortLightedInverse, `notam/obst-short-lighted-inverse.svg`), defineIcon(MapIcon.Notam_ObstShortUnlighted, `notam/obst-short-unlighted.svg`), defineIcon(MapIcon.Notam_ObstShortUnlightedInverse, `notam/obst-short-lighted-inverse.svg`), defineIcon(MapIcon.Notam_ObstTallLighted, `notam/obst-tall-lighted.svg`), defineIcon(MapIcon.Notam_ObstTallLightedInverse, `notam/obst-tall-lighted-inverse.svg`), defineIcon(MapIcon.Notam_ObstTallUnlighted, `notam/obst-tall-unlighted.svg`), defineIcon(MapIcon.Notam_ObstTallUnlightedInverse, `notam/obst-tall-unlighted-inverse.svg`), defineIcon(MapIcon.Notam_ObstShortLightedHighlighted, `notam/obst-short-lighted-highlighted.svg`), defineIcon(MapIcon.Notam_ObstShortLightedInverseHighlighted, `notam/obst-short-lighted-inverse-highlighted.svg`), defineIcon(MapIcon.Notam_ObstShortUnlightedHighlighted, `notam/obst-short-unlighted-highlighted.svg`), defineIcon(MapIcon.Notam_ObstShortUnlightedInverseHighlighted, `notam/obst-short-lighted-inverse-highlighted.svg`), defineIcon(MapIcon.Notam_ObstTallLightedHighlighted, `notam/obst-tall-lighted-highlighted.svg`), defineIcon(MapIcon.Notam_ObstTallLightedInverseHighlighted, `notam/obst-tall-lighted-inverse-highlighted.svg`), defineIcon(MapIcon.Notam_ObstTallUnlightedHighlighted, `notam/obst-tall-unlighted-highlighted.svg`), defineIcon(MapIcon.Notam_ObstTallUnlightedInverseHighlighted, `notam/obst-tall-unlighted-inverse-highlighted.svg`), defineIcon(MapIcon.Notam_Unknown, 'notam/unknown.svg'), defineIcon(MapIcon.Obstacle_Lighted_Short, 'obstacles/lighted-short.svg'), defineIcon(MapIcon.Obstacle_Lighted_Tall, 'obstacles/lighted-tall.svg'), defineIcon(MapIcon.Obstacle_Unlighted_Short, 'obstacles/unlighted-short.svg'), defineIcon(MapIcon.Obstacle_Unlighted_Tall, 'obstacles/unlighted-tall.svg'), defineIcon(MapIcon.Pirep_Generic, `pirep/icons/${PirepWeatherCondition.PIREP}.svg`), defineIcon(MapIcon.Pirep_Icing_Negative, `pirep/icons/${PirepWeatherCondition.ICING_NEG}.svg`), defineIcon(MapIcon.Pirep_Icing_Trace, `pirep/icons/${PirepWeatherCondition.ICING_TR}.svg`), defineIcon(MapIcon.Pirep_Icing_TraceToLight, `pirep/icons/${PirepWeatherCondition.ICING_TR_LGT}.svg`), defineIcon(MapIcon.Pirep_Icing_Light, `pirep/icons/${PirepWeatherCondition.ICING_LGT}.svg`), defineIcon(MapIcon.Pirep_Icing_LightToModerate, `pirep/icons/${PirepWeatherCondition.ICING_LGT_MOD}.svg`), defineIcon(MapIcon.Pirep_Icing_Moderate, `pirep/icons/${PirepWeatherCondition.ICING_MOD}.svg`), defineIcon(MapIcon.Pirep_Icing_ModerateToSevere, `pirep/icons/${PirepWeatherCondition.ICING_MOD_SEV}.svg`), defineIcon(MapIcon.Pirep_Icing_Severe, `pirep/icons/${PirepWeatherCondition.ICING_SEV}.svg`), defineIcon(MapIcon.Pirep_Sky_Unknown, `pirep/icons/${PirepWeatherCondition.SKYCOND_UNK}.svg`), defineIcon(MapIcon.Pirep_Sky_Clear, `pirep/icons/${PirepWeatherCondition.SKYCOND_CLR}.svg`), defineIcon(MapIcon.Pirep_Sky_Few, `pirep/icons/${PirepWeatherCondition.SKYCOND_FEW}.svg`), defineIcon(MapIcon.Pirep_Sky_Scattered, `pirep/icons/${PirepWeatherCondition.SKYCOND_SCT}.svg`), defineIcon(MapIcon.Pirep_Sky_Broken, `pirep/icons/${PirepWeatherCondition.SKYCOND_BKN}.svg`), defineIcon(MapIcon.Pirep_Sky_Overcast, `pirep/icons/${PirepWeatherCondition.SKYCOND_OVC}.svg`), defineIcon(MapIcon.Pirep_Sky_InstrumentConditions, `pirep/icons/${PirepWeatherCondition.SKYCOND_IMC}.svg`), defineIcon(MapIcon.Pirep_Turbulence_Negative, `pirep/icons/${PirepWeatherCondition.TURBULENCE_NEG}.svg`), defineIcon(MapIcon.Pirep_Turbulence_Light, `pirep/icons/${PirepWeatherCondition.TURBULENCE_LGT}.svg`), defineIcon(MapIcon.Pirep_Turbulence_LightToModerate, `pirep/icons/${PirepWeatherCondition.TURBULENCE_LGT_MOD}.svg`), defineIcon(MapIcon.Pirep_Turbulence_Moderate, `pirep/icons/${PirepWeatherCondition.TURBULENCE_MOD}.svg`), defineIcon(MapIcon.Pirep_Turbulence_ModerateToSevere, `pirep/icons/${PirepWeatherCondition.TURBULENCE_MOD_SEV}.svg`), defineIcon(MapIcon.Pirep_Turbulence_Severe, `pirep/icons/${PirepWeatherCondition.TURBULENCE_SEV}.svg`), defineIcon(MapIcon.Pirep_Turbulence_Extreme, `pirep/icons/${PirepWeatherCondition.TURBULENCE_EXT}.svg`), defineIcon(MapIcon.Pirep_Altitude_Generic, `pirep/icons-w-alt/${PirepWeatherCondition.PIREP}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Icing_Negative, `pirep/icons-w-alt/${PirepWeatherCondition.ICING_NEG}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Icing_Trace, `pirep/icons-w-alt/${PirepWeatherCondition.ICING_TR}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Icing_TraceToLight, `pirep/icons-w-alt/${PirepWeatherCondition.ICING_TR_LGT}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Icing_Light, `pirep/icons-w-alt/${PirepWeatherCondition.ICING_LGT}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Icing_LightToModerate, `pirep/icons-w-alt/${PirepWeatherCondition.ICING_LGT_MOD}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Icing_Moderate, `pirep/icons-w-alt/${PirepWeatherCondition.ICING_MOD}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Icing_ModerateToSevere, `pirep/icons-w-alt/${PirepWeatherCondition.ICING_MOD_SEV}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Icing_Severe, `pirep/icons-w-alt/${PirepWeatherCondition.ICING_SEV}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Sky_Unknown, `pirep/icons-w-alt/${PirepWeatherCondition.SKYCOND_UNK}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Sky_Clear, `pirep/icons-w-alt/${PirepWeatherCondition.SKYCOND_CLR}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Sky_Few, `pirep/icons-w-alt/${PirepWeatherCondition.SKYCOND_FEW}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Sky_Scattered, `pirep/icons-w-alt/${PirepWeatherCondition.SKYCOND_SCT}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Sky_Broken, `pirep/icons-w-alt/${PirepWeatherCondition.SKYCOND_BKN}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Sky_Overcast, `pirep/icons-w-alt/${PirepWeatherCondition.SKYCOND_OVC}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Sky_InstrumentConditions, `pirep/icons-w-alt/${PirepWeatherCondition.SKYCOND_IMC}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Turbulence_Negative, `pirep/icons-w-alt/${PirepWeatherCondition.TURBULENCE_NEG}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Turbulence_Light, `pirep/icons-w-alt/${PirepWeatherCondition.TURBULENCE_LGT}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Turbulence_LightToModerate, `pirep/icons-w-alt/${PirepWeatherCondition.TURBULENCE_LGT_MOD}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Turbulence_Moderate, `pirep/icons-w-alt/${PirepWeatherCondition.TURBULENCE_MOD}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Turbulence_ModerateToSevere, `pirep/icons-w-alt/${PirepWeatherCondition.TURBULENCE_MOD_SEV}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Turbulence_Severe, `pirep/icons-w-alt/${PirepWeatherCondition.TURBULENCE_SEV}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_Altitude_Turbulence_Extreme, `pirep/icons-w-alt/${PirepWeatherCondition.TURBULENCE_EXT}.svg`, PirepIconWithAltOptions), defineIcon(MapIcon.Pirep_NoAltitude_Generic, `pirep/icons-wo-alt/${PirepWeatherCondition.PIREP}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Icing_Negative, `pirep/icons-wo-alt/${PirepWeatherCondition.ICING_NEG}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Icing_Trace, `pirep/icons-wo-alt/${PirepWeatherCondition.ICING_TR}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Icing_TraceToLight, `pirep/icons-wo-alt/${PirepWeatherCondition.ICING_TR_LGT}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Icing_Light, `pirep/icons-wo-alt/${PirepWeatherCondition.ICING_LGT}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Icing_LightToModerate, `pirep/icons-wo-alt/${PirepWeatherCondition.ICING_LGT_MOD}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Icing_Moderate, `pirep/icons-wo-alt/${PirepWeatherCondition.ICING_MOD}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Icing_ModerateToSevere, `pirep/icons-wo-alt/${PirepWeatherCondition.ICING_MOD_SEV}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Icing_Severe, `pirep/icons-wo-alt/${PirepWeatherCondition.ICING_SEV}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Sky_Unknown, `pirep/icons-wo-alt/${PirepWeatherCondition.SKYCOND_UNK}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Sky_Clear, `pirep/icons-wo-alt/${PirepWeatherCondition.SKYCOND_CLR}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Sky_Few, `pirep/icons-wo-alt/${PirepWeatherCondition.SKYCOND_FEW}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Sky_Scattered, `pirep/icons-wo-alt/${PirepWeatherCondition.SKYCOND_SCT}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Sky_Broken, `pirep/icons-wo-alt/${PirepWeatherCondition.SKYCOND_BKN}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Sky_Overcast, `pirep/icons-wo-alt/${PirepWeatherCondition.SKYCOND_OVC}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Sky_InstrumentConditions, `pirep/icons-wo-alt/${PirepWeatherCondition.SKYCOND_IMC}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Turbulence_Negative, `pirep/icons-wo-alt/${PirepWeatherCondition.TURBULENCE_NEG}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Turbulence_Light, `pirep/icons-wo-alt/${PirepWeatherCondition.TURBULENCE_LGT}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Turbulence_LightToModerate, `pirep/icons-wo-alt/${PirepWeatherCondition.TURBULENCE_LGT_MOD}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Turbulence_Moderate, `pirep/icons-wo-alt/${PirepWeatherCondition.TURBULENCE_MOD}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Turbulence_ModerateToSevere, `pirep/icons-wo-alt/${PirepWeatherCondition.TURBULENCE_MOD_SEV}.svg`), defineIcon(MapIcon.Pirep_NoAltitude_Turbulence_Severe, `pirep/icons-wo-alt/${PirepWeatherCondition.TURBULENCE_SEV}.svg`), defineIcon(MapIcon.Runway, 'runway.svg'), defineIcon(MapIcon.Pirep_NoAltitude_Turbulence_Extreme, `pirep/icons-wo-alt/${PirepWeatherCondition.TURBULENCE_EXT}.svg`), defineIcon(MapIcon.Runway_NOTAM_Closed, 'safetaxi/runway-closure.svg'), defineIcon(MapIcon.Runway_NOTAM_Conditional, 'safetaxi/conditional-closure.svg'), defineIcon(MapIcon.WeatherReport_CircleBlueBroken, 'weather-report/circle-blue-broken.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleBlueClear, 'weather-report/circle-blue-clear.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleBlueFew, 'weather-report/circle-blue-few.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleBlueMissing, 'weather-report/circle-blue-missing.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleBlueObscured, 'weather-report/circle-blue-obscured.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleBlueOvercast, 'weather-report/circle-blue-overcast.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleBlueScattered, 'weather-report/circle-blue-scattered.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleGrayMissing, 'weather-report/circle-gray-missing.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleGreenBroken, 'weather-report/circle-green-broken.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleGreenClear, 'weather-report/circle-green-clear.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleGreenFew, 'weather-report/circle-green-few.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleGreenMissing, 'weather-report/circle-green-missing.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleGreenObscured, 'weather-report/circle-green-obscured.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleGreenOvercast, 'weather-report/circle-green-overcast.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleGreenScattered, 'weather-report/circle-green-scattered.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleMagentaBroken, 'weather-report/circle-magenta-broken.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleMagentaClear, 'weather-report/circle-magenta-clear.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleMagentaFew, 'weather-report/circle-magenta-few.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleMagentaMissing, 'weather-report/circle-magenta-missing.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleMagentaObscured, 'weather-report/circle-magenta-obscured.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleMagentaOvercast, 'weather-report/circle-magenta-overcast.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleMagentaScattered, 'weather-report/circle-magenta-scattered.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleRedBroken, 'weather-report/circle-red-broken.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleRedClear, 'weather-report/circle-red-clear.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleRedFew, 'weather-report/circle-red-few.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleRedMissing, 'weather-report/circle-red-missing.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleRedObscured, 'weather-report/circle-red-obscured.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleRedOvercast, 'weather-report/circle-red-overcast.svg', NoOptions), defineIcon(MapIcon.WeatherReport_CircleRedScattered, 'weather-report/circle-red-scattered.svg', NoOptions), defineIcon(MapIcon.Highlight_Circle, `highlight/highlight-circle.svg`, NoOptions, 2)]);
class ContentObserver {
  constructor() {
    this.present = false;
    this.presentContent = [];
    this.visible = false;
    this.visibleContent = [];
  }
  update(content, visible, present) {
    const fixedPresent = present ?? visible;
    const fixedVisible = fixedPresent && visible;
    const changes = this.getChanges(content, fixedPresent, fixedVisible);
    this.present = fixedPresent;
    this.visible = fixedVisible;
    if (this.present) {
      this.presentContent = cloneDeep(content);
    } else {
      this.presentContent = [];
    }
    if (this.visible) {
      this.visibleContent = cloneDeep(content);
    } else {
      this.visibleContent = [];
    }
    return changes;
  }
  getChanges(content, present, visible) {
    const changes = {
      added: [],
      hidden: [],
      removed: [],
      shown: [],
      unchanged: []
    };
    const unchanged = new Set();
    if (present) {
      changes.added = this.getAddedChanges(content, this.presentContent);
      changes.removed = this.getRemovedChanges(content, this.presentContent);
      for (const change of this.getUnchangedChanges(content, this.presentContent)) {
        unchanged.add(change);
      }
    } else if (this.present) {
      changes.removed = [...this.presentContent];
    }
    if (visible) {
      changes.shown = this.getAddedChanges(content, this.visibleContent);
      changes.hidden = this.getRemovedChanges(content, this.visibleContent);
      for (const change of this.getUnchangedChanges(content, this.visibleContent)) {
        unchanged.add(change);
      }
    } else if (this.visible) {
      changes.hidden = [...this.visibleContent];
    }
    for (const c of [...changes.added, ...changes.hidden, ...changes.removed, ...changes.shown]) {
      for (const u of unchanged.values()) {
        if (isEqual(u, c)) {
          unchanged.delete(u);
        }
      }
    }
    changes.unchanged = [...unchanged.values()];
    return changes;
  }
  getAddedChanges(content, previous) {
    return content.filter(x => !previous.some(y => isEqual(x, y)));
  }
  getRemovedChanges(content, previous) {
    return previous.filter(x => !content.some(y => isEqual(x, y)));
  }
  getUnchangedChanges(content, previous) {
    return content.filter(x => previous.some(y => isEqual(x, y)));
  }
}

/**
 * This is a template for a service that handles a single layer.
 * The layer is created when it should be visible and is destroyed
 * when it should be hidden.
 */
class LayerService {
  constructor() {
    this.injector = inject(Injector);
    this.mapAnimation = inject(MapAnimationHandler);
    this.mapConfig = inject(MapConfigHandler);
    this.mapData = inject(MapDataHandler);
    this.mapHighlight = inject(MapHighlight);
    this.mapIcon = inject(MapIconHandler);
    this.mapInfo = inject(MapInfo);
    this.mapSettings = inject(MapSettingsHandler);
    this.mapState = inject(MapStateHandler);
    this.mapTheme = inject(MapThemeHandler);
    this.moduleConfig = inject(AVC_MAPS_DISPLAY_CONFIG);
    this.contentObserver = new ContentObserver();
    this.radialMenuMapService = inject(RadialMenuMapService);
    this.map = inject(MapTracker).map;
    this.layers = new ContentMap();
  }
  updateLayers() {
    const changes = this.contentObserver.update(this.getContent(), this.getVisible());
    const opacity = this.getOpacity();
    for (const c of changes.hidden) {
      this.layers.get(c)?.hide();
      this.layers.delete(c);
    }
    for (const c of changes.shown) {
      const layer = this.createLayer(c);
      layer.show();
      layer.setOpacity(opacity);
      this.layers.set(c, layer);
    }
    for (const c of changes.unchanged) {
      let layer = this.layers.get(c);
      if (layer == null) {
        layer = this.createLayer(c);
        layer.show();
        this.layers.set(c, layer);
      } else if (!layer.visible()) {
        layer.show();
      } else {
        layer.update();
      }
      layer.setOpacity(opacity);
    }
  }
  getOpacity() {
    return MAX_OPACITY;
  }
}
let NotamService = /*#__PURE__*/(() => {
  class NotamService extends LayerService {
    constructor() {
      super();
      this.images = [MapIcon.Notam_AdCaution, MapIcon.Notam_AdCautionTimed, MapIcon.Notam_AdWarning, MapIcon.Notam_AdWarningTimed, MapIcon.Notam_FillPatternBlank, MapIcon.Notam_FillPatternDarkYellow, MapIcon.Notam_FillPatternGray, MapIcon.Notam_FillPatternOrange, MapIcon.Notam_FillPatternPurple, MapIcon.Notam_FillPatternRed, MapIcon.Notam_FillPatternTransparent, MapIcon.Notam_FillPatternYellow, MapIcon.Notam_NavDmeCaution, MapIcon.Notam_NavDmeUpcoming, MapIcon.Notam_NavDmeWarning, MapIcon.Notam_NavNdbCaution, MapIcon.Notam_NavNdbUpcoming, MapIcon.Notam_NavNdbWarning, MapIcon.Notam_NavTacanCaution, MapIcon.Notam_NavTacanUpcoming, MapIcon.Notam_NavTacanWarning, MapIcon.Notam_NavVorCaution, MapIcon.Notam_NavVorDmeCaution, MapIcon.Notam_NavVorDmeUpcoming, MapIcon.Notam_NavVorDmeWarning, MapIcon.Notam_NavVortacCaution, MapIcon.Notam_NavVortacUpcoming, MapIcon.Notam_NavVortacWarning, MapIcon.Notam_NavVorUpcoming, MapIcon.Notam_NavVorWarning, MapIcon.Notam_IFR_NavDmeCaution, MapIcon.Notam_IFR_NavDmeUpcoming, MapIcon.Notam_IFR_NavDmeWarning, MapIcon.Notam_IFR_NavNdbCaution, MapIcon.Notam_IFR_NavNdbUpcoming, MapIcon.Notam_IFR_NavNdbWarning, MapIcon.Notam_IFR_NavTacanCaution, MapIcon.Notam_IFR_NavTacanUpcoming, MapIcon.Notam_IFR_NavTacanWarning, MapIcon.Notam_IFR_NavVorCaution, MapIcon.Notam_IFR_NavVorDmeCaution, MapIcon.Notam_IFR_NavVorDmeUpcoming, MapIcon.Notam_IFR_NavVorDmeWarning, MapIcon.Notam_IFR_NavVortacCaution, MapIcon.Notam_IFR_NavVortacUpcoming, MapIcon.Notam_IFR_NavVortacWarning, MapIcon.Notam_IFR_NavVorUpcoming, MapIcon.Notam_IFR_NavVorWarning, MapIcon.Notam_ObstShortLighted, MapIcon.Notam_ObstShortLightedHighlighted, MapIcon.Notam_ObstShortLightedInverse, MapIcon.Notam_ObstShortLightedInverseHighlighted, MapIcon.Notam_ObstShortUnlighted, MapIcon.Notam_ObstShortUnlightedHighlighted, MapIcon.Notam_ObstShortUnlightedInverse, MapIcon.Notam_ObstShortUnlightedInverseHighlighted, MapIcon.Notam_ObstTallLighted, MapIcon.Notam_ObstTallLightedHighlighted, MapIcon.Notam_ObstTallLightedInverse, MapIcon.Notam_ObstTallLightedInverseHighlighted, MapIcon.Notam_ObstTallUnlighted, MapIcon.Notam_ObstTallUnlightedHighlighted, MapIcon.Notam_ObstTallUnlightedInverse, MapIcon.Notam_ObstTallUnlightedInverseHighlighted, MapIcon.Notam_Unknown].map(x => MapIconDefinitions.get(x));
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
    }
    getSourceIds(map) {
      const sourceIds = [];
      for (const [_content, layer] of this.layers) {
        const id = layer.getSource(map);
        if (id != null) {
          sourceIds.push(id);
        }
      }
      return sourceIds;
    }
    createLayer(content) {
      return new NotamLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.overlay.notam.content();
    }
    getVisible() {
      return this.mapConfig.overlay.notam.visible();
    }
    getOpacity() {
      return this.mapConfig.overlay.notam.opacity();
    }
    updateImages() {
      const config = this.mapConfig.overlay.notam;
      const info = this.mapInfo.overlay.notam;
      if (info.available() && config.visible()) {
        this.mapIcon.add(this.images);
      } else {
        this.mapIcon.remove(this.images);
      }
    }
    static {
      this.ɵfac = function NotamService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NotamService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NotamService,
        factory: NotamService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return NotamService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const DELAYED_PROCESSING_TIMEOUT = 200;
var RunwayNotamStatus = /*#__PURE__*/function (RunwayNotamStatus) {
  // order denotes priority!
  RunwayNotamStatus[RunwayNotamStatus["Active"] = 0] = "Active";
  RunwayNotamStatus[RunwayNotamStatus["Conditional"] = 1] = "Conditional";
  RunwayNotamStatus[RunwayNotamStatus["Upcoming"] = 2] = "Upcoming";
  return RunwayNotamStatus;
}(RunwayNotamStatus || {});
/**
 * This service is responsible for monitoring changes to NOTAM sources and publishing
 * NOTAM data related to runways.
 */
let NotamRunwayService = /*#__PURE__*/(() => {
  class NotamRunwayService {
    isRunwayNotam(feature) {
      return this.layerIds.has(feature.layer.id);
    }
    getNotam(feature) {
      const airport = feature.properties['airportId'];
      if (airport == null) {
        return undefined;
      }
      let runway = feature.properties['runwayId'];
      if (runway == null) {
        runway = feature.properties['RunwayAttrs.runwayId'];
      }
      if (runway == null) {
        return undefined;
      }
      for (const runwayNotam of this.runwayNotams()) {
        if (runwayNotam.airport === airport && runwayNotam.runway === runway) {
          return runwayNotam.notam;
        }
      }
      return undefined;
    }
    constructor() {
      this.runwayNotams = signal([]);
      this.layerIds = new Set();
      this.notamService = inject(NotamService);
      this.highlight = inject(MapHighlight);
      this.mapConfig = inject(MapConfigHandler);
      this.mapInfo = inject(MapInfo);
      this.map = inject(MapTracker).map;
      this.sourceIds = [];
      this.highlightedNotamList = computed(() => {
        const highlights = [];
        const runwayNotams = this.runwayNotams();
        const notamHighlights = this.highlight.notam();
        for (const runwayNotam of runwayNotams) {
          const notamId = runwayNotam.notam.properties['notamId'];
          if (notamId != null && notamHighlights.some(x => x.id === notamId)) {
            const combinedId = `${runwayNotam.airport}_${runwayNotam.runway}`;
            highlights.push(combinedId);
          }
        }
        return highlights;
      });
      this.runwayNotamLists = computed(() => {
        const notams = this.runwayNotams();
        const active = [];
        const conditional = [];
        const upcoming = [];
        const notPresent = (lists, id) => {
          return lists.every(list => list.findIndex(x => x === id) === -1);
        };
        // Get NOTAMs and sort by status (active then conditional then upcoming)
        notams.sort((a, b) => a.status - b.status);
        for (const notam of notams) {
          const combinedId = `${notam.airport}_${notam.runway}`;
          switch (notam.status) {
            case RunwayNotamStatus.Active:
            default:
              // only use if no similar active NOTAM
              if (notPresent([active], combinedId)) {
                active.push(combinedId);
              }
              break;
            case RunwayNotamStatus.Conditional:
              // only use if no similar active or conditional
              if (notPresent([active, conditional], combinedId)) {
                conditional.push(combinedId);
              }
              break;
            case RunwayNotamStatus.Upcoming:
              // only use if no similar active, conditional, or upcoming NOTAM
              if (notPresent([active, conditional, upcoming], combinedId)) {
                upcoming.push(combinedId);
              }
              break;
          }
        }
        // The lists can't be empty because MapLibre
        if (active.length === 0) {
          active.push('_A_');
        }
        if (conditional.length === 0) {
          conditional.push('_C_');
        }
        if (upcoming.length === 0) {
          upcoming.push('_B_');
        }
        return {
          active,
          conditional,
          upcoming
        };
      });
      this.showRunwayNotams = computed(() => {
        const map = this.map();
        const notamsAvailable = this.mapInfo.overlay.notam.available();
        const notamsVisible = this.mapConfig.overlay.notam.visible();
        const runwayAvailable = this.mapInfo.content.airport.available();
        const showTerminalNotams = this.mapConfig.overlay.notam.showTerminalNotams();
        return map != null && runwayAvailable && notamsAvailable && notamsVisible && showTerminalNotams;
      });
      this.onSourceData = event => {
        const map = this.map();
        if (map != null) {
          this.sourceIds = this.notamService.getSourceIds(map);
          for (const id of this.sourceIds) {
            if (id === event.sourceId) {
              this.triggerDelayedProcessing();
              return;
            }
          }
        }
      };
      this.onTimeout = () => {
        const map = this.map();
        const now = getUnixTime(new Date());
        if (map != null) {
          const runwayNotams = [];
          for (const sourceId of this.sourceIds) {
            const features = map.querySourceFeatures(sourceId, {
              sourceLayer: NOTAM_SOURCE_LAYER_POLYGON,
              filter: ['==', ['get', 'notamClass'], NotamClass$1.RUNWAY]
            });
            for (const feature of features) {
              // ignore expired NOTAMs
              const expireTime = feature.properties['expireTime'];
              if (expireTime != null && expireTime < now) {
                continue;
              }
              // we only care about "CLOSED" runways
              const status = feature.properties['notamStatus'];
              if (status !== NotamStatus$1.CLOSED) {
                continue;
              }
              // Determine the notam status
              const effectiveTime = feature.properties['effectiveTime'];
              const upcomingVisibilitySeconds = this.mapConfig.overlay.notam.upcomingVisibilityHours() * 3600;
              let runwayNotamStatus;
              if (effectiveTime != null) {
                if (effectiveTime < now) {
                  if (featureIsConditional(feature)) {
                    runwayNotamStatus = RunwayNotamStatus.Conditional;
                  } else {
                    runwayNotamStatus = RunwayNotamStatus.Active;
                  }
                } else if (now < effectiveTime && now >= effectiveTime - upcomingVisibilitySeconds) {
                  runwayNotamStatus = RunwayNotamStatus.Upcoming;
                } else {
                  // no need to list inactive NOTAMs
                  continue;
                }
              } else {
                // no effective time means no NOTAMs
                continue;
              }
              const airport = feature.properties['icaoNotamLocation'];
              const runway = feature.properties['notamLocation'];
              if (airport != null && runway != null) {
                const splitRunways = runway.split('/');
                for (const splitRunway of splitRunways) {
                  const newRunwayNotam = {
                    airport,
                    runway: splitRunway,
                    status: runwayNotamStatus,
                    notam: feature
                  };
                  const found = runwayNotams.findIndex(x => isEqual(x, newRunwayNotam)) !== -1;
                  if (!found) {
                    runwayNotams.push(newRunwayNotam);
                  }
                }
              }
            }
          }
          untracked(() => this.runwayNotams.set(runwayNotams));
        }
      };
      effect(() => {
        if (this.showRunwayNotams()) {
          this.create();
        } else {
          this.reset();
        }
      });
    }
    create() {
      // If the NOTAM content changes, reset everything
      const _content = this.mapInfo.overlay.notam.content();
      this.reset();
      const map = this.map();
      if (map != null) {
        // Connect callback
        map.on('sourcedata', this.onSourceData);
        // Immediately query sources
        this.sourceIds = this.notamService.getSourceIds(map);
        this.onTimeout();
      }
    }
    triggerDelayedProcessing() {
      if (this.timer != null) {
        window.clearTimeout(this.timer);
      }
      this.timer = window.setTimeout(this.onTimeout, DELAYED_PROCESSING_TIMEOUT);
    }
    stopDelayedProcessing() {
      window.clearTimeout(this.timer);
      this.timer = undefined;
    }
    reset() {
      untracked(() => {
        this.stopDelayedProcessing();
        const map = this.map();
        if (map != null) {
          map.off('sourcedata', this.onSourceData);
        }
        this.sourceIds = [];
        this.runwayNotams.set([]);
      });
    }
    static {
      this.ɵfac = function NotamRunwayService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NotamRunwayService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NotamRunwayService,
        factory: NotamRunwayService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return NotamRunwayService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// • = \u{2022}
// — = \u{2014}
const conversionTable = new Map([['A', '• —'], ['B', '— • • •'], ['C', '— • — •'], ['D', '— • •'], ['E', '•'], ['F', '• • — •'], ['G', '— — •'], ['H', '• • • •'], ['I', '• •'], ['J', '• — — —'], ['K', '— • —'], ['L', '• — • •'], ['M', '— —'], ['N', '— •'], ['O', '— — —'], ['P', '• — — •'], ['Q', '— — • —'], ['R', '• — •'], ['S', '• • •'], ['T', '—'], ['U', '• • —'], ['V', '• • • —'], ['W', '• — —'], ['X', '— • • —'], ['Y', '— • — —'], ['Z', '— — • •'], ['0', '— — — — —'], ['1', '• — — — —'], ['2', '• • — — —'], ['3', '• • • — —'], ['4', '• • • • —'], ['5', '• • • • •'], ['6', '— • • • •'], ['7', '— — • • •'], ['8', '— — — • •'], ['9', '— — — — •']]);
const convertWordToMorseCharArray = word => {
  const morseCharArray = [];
  for (const char of word.trim().toUpperCase()) {
    const morseCharEncoding = conversionTable.get(char);
    if (morseCharEncoding != null) {
      morseCharArray.push(morseCharEncoding);
    } else {
      console.error(`Unsupported character, '${char}', found when converting "${word}" to a morse code character array`);
    }
  }
  return morseCharArray;
};
const SIZE = 160;
const ORIGIN = {
  x: SIZE / 2,
  y: SIZE / 2
};
const RADIUS_BTN_OUTER = SIZE / 2 - 1;
const RADIUS_BTN_INNER = SIZE / 4 + 2;
const RADIUS_APERTURE = SIZE / 6.7 - 1;
const RADIUS_THINKING = RADIUS_APERTURE + 3;
const RADIUS_INFO_OUTER = RADIUS_BTN_OUTER;
const RADIUS_INFO_INNER = RADIUS_APERTURE;
const RADIUS_INFO_TEXT = RADIUS_THINKING + 6; // the 6 is roughly (RADIUS_BTN_INNER - RADIUS_THINKING) / 2 - 1;
const RADIUS_IMAGE = RADIUS_BTN_INNER + (RADIUS_BTN_OUTER - RADIUS_BTN_INNER) / 2;
const RADIUS_LABEL = RADIUS_BTN_OUTER + 7;
const TO_RAD = Math.PI / 180;
const TO_DEG = 180 / Math.PI;
const ARC_LEN_BTN_STATIC = 2.25; // radians - looks good for now
const ARC_LEN_BTN_GAP = Math.PI / 32; // looks good for now
const ARC_LEN_BTN_DYN_MAX = 2 * Math.PI - ARC_LEN_BTN_STATIC - 2 * ARC_LEN_BTN_GAP;
const ARC_LEN_THINKING = ARC_LEN_BTN_STATIC / 3 * 1.25;
const IMG_SCALE = (RADIUS_BTN_OUTER - RADIUS_BTN_INNER) / SIZE - 0.07; // buffered percentage of the SVG size
const IMG_SHIFT = SIZE * IMG_SCALE / 2;
const FONT_LBL_SCALE = 0.09;
const FONT_INFO_SCALE = 0.08;
const SVG$2 = 'http://www.w3.org/2000/svg';
const COLOR_INFO = '#eee';
/**
 * Creates a `Point` from ORIGIN (in svg-constants.ts) to the `radius` at the
 * given `angle`.
 */
const makePoint = (angle, radius) => {
  return {
    x: Math.cos(angle) * radius + ORIGIN.x,
    y: Math.sin(angle) * radius + ORIGIN.y
  };
};

/**
 * This class provides a convenient API for building SVG paths defined around some center point like
 * donuts and segments of donuts.
 */
class SvgRadialPath {
  constructor() {
    this.d = '';
    this.angle = 0;
    this.is_donut = false;
  }
  /**
   * Clears the internals of this path so this object can be used to build another path.
   */
  clear() {
    this.d = '';
    this.angle = 0;
    this.is_donut = false;
  }
  /**
   * Generates a 'move' command.
   * @param angle The angle to point at.
   * @param radius The distance away from the center point.
   */
  move(angle, radius) {
    const point = makePoint(angle, radius);
    this.d += `M${point.x} ${point.y}`;
    this.angle = angle;
  }
  /**
   * Generates an 'arc' command.
   * @param distance The arc distance.
   * @param radius The distance away from the center point.
   */
  arc(distance, radius) {
    const point = makePoint(this.angle + distance, radius);
    const large = Math.abs(this.angle + distance - this.angle) > Math.PI ? 1 : 0; // assuming no negative angles
    this.d += `A${radius} ${radius} 0 ${large} ${distance > 0 ? 1 : 0} ${point.x} ${point.y}`;
    this.angle += distance;
  }
  /**
   * Generates a 'line' command.
   * @param radius The distance away from the center point.
   */
  line(radius) {
    const point = makePoint(this.angle, radius);
    this.d += `L${point.x} ${point.y}`;
  }
  /**
   * Closes the path.
   */
  close() {
    this.d += 'z';
  }
  /**
   * Generates a set of commands to create a donut (i.e., a circle with a hole in the middle).
   * @param ro The radius of the outer circle.
   * @param ri The radius of the inner circle.
   */
  donut(ro, ri) {
    this.move(0, ro);
    this.arc(Math.PI, ro);
    this.arc(Math.PI, ro);
    this.close();
    this.move(0, ri);
    this.arc(Math.PI, ri);
    this.arc(Math.PI, ri);
    this.close();
    this.is_donut = true;
  }
  /**
   * Creates an SVGPathElement with the given set of attributes and the current set of commands
   * generated with this path.
   * @param attributes General set of SvgPathElementAttributes.
   * @returns The SVGPathElement represented by the current state of this path.
   */
  element(attributes) {
    const path = document.createElementNS(SVG$2, 'path');
    path.setAttribute('d', this.d);
    if (attributes.classes !== undefined) {
      path.classList.add(...attributes.classes);
    }
    if (attributes.fill !== undefined) {
      path.setAttribute('fill', attributes.fill);
    }
    if (attributes.fill_rule !== undefined) {
      path.setAttribute('fill-rule', attributes.fill_rule);
    } else if (this.is_donut) {
      path.setAttribute('fill-rule', 'evenodd');
    }
    if (attributes.filter !== undefined) {
      path.setAttribute('filter', attributes.filter);
    }
    if (attributes.id !== undefined) {
      path.id = attributes.id;
    }
    if (attributes.stroke !== undefined) {
      path.setAttribute('stroke', attributes.stroke);
    }
    if (attributes.stroke_width !== undefined) {
      path.setAttribute('stroke-width', `${attributes.stroke_width}`);
    }
    return path;
  }
}

/**
 * This class represents a single Radial Menu SVG button.
 */
class SvgButton {
  constructor(def, clickable = true) {
    this.morseCodeElementContentPairs = null;
    this.def = def;
    this.group = document.createElementNS(SVG$2, 'g');
    this.group.classList.add('radial-menu-button-group');
    this.group.setAttribute('visibility', 'visible');
    this.onClick = e => {
      if (!clickable || this.isEnabled != null && !this.isEnabled()) {
        // we don't want any parent container to get click events
        e.stopPropagation();
      } else {
        this.unpress();
        if (this.onclick !== undefined) {
          this.onclick({
            feature_type: this.def.feature_type,
            features: this.features
          });
        }
      }
    };
    this.group.addEventListener('click', this.onClick);
    if (clickable && (this.isEnabled == null || this.isEnabled())) {
      this.onMouseDown = e => {
        if (e.button === 0) {
          // we only want the primary mouse button
          this.press();
        }
      };
      this.group.addEventListener('mousedown', this.onMouseDown);
      this.onUnclick = () => {
        this.unpress();
      };
      this.group.addEventListener('unclick', this.onUnclick);
    }
    this.onContextMenu = e => {
      e.preventDefault(); // we don't want a context menu when you right-click
    };
    this.group.addEventListener('contextmenu', this.onContextMenu);
    this.path = new SvgRadialPath().element({
      classes: ['radial-menu-button']
    });
    this.group.appendChild(this.path);
    if (this.def.img !== undefined) {
      this.image = document.createElementNS(SVG$2, 'image');
      if (typeof this.def.img === 'string') {
        this.image.setAttribute('href', this.def.img);
      }
      this.image.setAttribute('width', `${IMG_SCALE * 100}%`);
      this.image.setAttribute('height', `${IMG_SCALE * 100}%`);
      this.group.appendChild(this.image);
    }
    if (this.def.lbl !== undefined) {
      this.label = document.createElementNS(SVG$2, 'text');
      this.label.classList.add('radial-menu-button-label');
      this.label.setAttribute('letter-spacing', '1.2');
      this.label.setAttribute('font-size', `${Math.ceil(SIZE * FONT_LBL_SCALE)}px`);
    }
    if (this.def.morseCodeLbl != null) {
      // only create the morse code label if the button label is defined, since it's complementary
      if (this.def.lbl != null) {
        this.morseCodeLabel = document.createElementNS(SVG$2, 'text');
        this.morseCodeLabel.classList.add('radial-menu-button-label');
        this.morseCodeLabel.setAttribute('font-size', `${Math.ceil(SIZE * FONT_LBL_SCALE * 1.5)}px`);
        this.morseCodeLabel.setAttribute('letter-spacing', '1.2');
      } else {
        throw new Error('Unsupported Radial Menu SVG button configuration: morse code labels require a text lbl');
      }
    }
    if (this.def.shape != null) {
      this.reshape(this.def.shape.angle, this.def.shape.arc_len);
    } else {
      this.hide();
    }
  }
  destroy() {
    this.group.removeEventListener('click', this.onClick);
    if (this.onMouseDown != null) {
      this.group.removeEventListener('mousedown', this.onMouseDown);
    }
    if (this.onUnclick != null) {
      this.group.removeEventListener('unclick', this.onUnclick);
    }
    this.group.removeEventListener('contextmenu', this.onContextMenu);
  }
  /**
   * Sets the styles and attributes to display this button.
   */
  show() {
    if (this.isEnabled != null && !this.isEnabled()) {
      return;
    }
    // icon
    if (typeof this.def.img === 'function') {
      const onload = features => {
        if (this.image != null) {
          this.image.setAttribute('href', this.def.img(features));
        }
      };
      if (typeof this.def.loadImg !== 'function' || !this.def.loadImg(this.features, onload)) {
        onload(this.features);
      }
    }
    // label
    if (this.def.lbl !== undefined && this.label != null) {
      if (typeof this.def.lbl === 'string') {
        // if we have a static string label we set it in `show` instead of in our constructor because we remove the
        // string when `hide` is called so that its size is not considered when auto-toggling the radial menu off
        // when it goes off screen
        this.label.textContent = this.def.lbl;
      } else {
        this.label.textContent = this.def.lbl(this.features);
      }
      this.label.setAttribute('visibility', 'visible');
    }
    // morse code label
    if (this.morseCodeLabel != null && this.morseCodeElementContentPairs != null) {
      // Set the text content of each morseCodeLabel element
      for (const morseCodeElementContentPair of this.morseCodeElementContentPairs) {
        morseCodeElementContentPair.element.textContent = morseCodeElementContentPair.content;
        morseCodeElementContentPair.element.setAttribute('visibility', 'visible');
      }
      this.morseCodeLabel.setAttribute('visibility', 'visible');
    }
    this.group.setAttribute('visibility', 'visible');
  }
  /**
   * Sets the styles to hide this button.
   */
  hide() {
    this.features = undefined;
    this.group.setAttribute('visibility', 'hidden');
    if (this.label != null) {
      this.label.textContent = '';
      this.label.setAttribute('visibility', 'hidden');
    }
    if (this.morseCodeLabel != null && this.morseCodeElementContentPairs != null) {
      for (const morseCodeElementContentPair of this.morseCodeElementContentPairs) {
        morseCodeElementContentPair.element.textContent = '';
        morseCodeElementContentPair.element.setAttribute('visibility', 'hidden');
      }
      this.morseCodeLabel.setAttribute('visible', 'hidden');
    }
  }
  /**
   * Updates this button's current set of features based on the given latest set of features.
   * @param features All of the latest features found within the Radial Menu aperture.
   * @returns `true` if any of the latest features indicate a change in this button's current set of
   * features and `false` otherwise.
   */
  updateFeatures(features) {
    // the 'more info' button should always be cleared out
    if (this.def.feature_type === FeatureType$1.MoreInfo) {
      if (this.features != null) {
        this.features = undefined;
        return true;
      }
      return false;
    }
    let did_update = false;
    // this button only cares about features that its definition specifies
    let matched_features = features.filter(f => this.def.layer_ids?.includes(f.layer.id)).filter(f => this.def.layer_sub_id == null || f.properties[this.def.layer_sub_id.key] === this.def.layer_sub_id.value);
    if (this.def.filter_fn != null) {
      matched_features = this.def.filter_fn(matched_features);
    }
    if (matched_features.length > 0) {
      // we don't have any features at all so just take the new ones
      if (this.features === undefined) {
        this.features = matched_features;
        did_update = true;
      } else if (this.def.use_feature_id === true) {
        const ids = this.features.map(f => f.id);
        const new_ids = matched_features.map(f => f.id);
        if (JSON.stringify(ids) !== JSON.stringify(new_ids)) {
          this.features = matched_features;
          did_update = true;
        }
      }
      // use the property id to match against new features
      else if (this.def.prop_id !== undefined) {
        if (this.def.prop_id != null) {
          const ids = this.features.map(f => f.properties[this.def.prop_id]);
          const new_ids = matched_features.map(f => f.properties[this.def.prop_id]);
          if (JSON.stringify(ids) !== JSON.stringify(new_ids)) {
            this.features = matched_features;
            did_update = true;
          }
        }
      }
      // use all properties to match against new features
      else {
        const old = this.features.some(f => matched_features.every(m => JSON.stringify(f.properties) !== JSON.stringify(m.properties)));
        if (old) {
          this.features = matched_features;
          did_update = true;
        }
      }
    } else {
      did_update = this.features !== undefined; // if we have features, we're about to delete them
      this.features = undefined;
    }
    if (did_update && this.features != null) {
      // dedup
      const ids = [];
      this.features = this.features.filter(f => {
        const id = JSON.stringify(this.def.use_feature_id === true ? f.id : this.def.prop_id !== undefined ? f.properties[this.def.prop_id] : f.properties);
        if (!ids.includes(id)) {
          ids.push(id);
          return true;
        } else {
          return false;
        }
      });
      // sort
      if (this.def.sort_fn != null) {
        this.features.sort(this.def.sort_fn);
      }
      if (this.morseCodeLabel != null && this.def.morseCodeLbl != null) {
        // the morse code subtext is created in `updateFeatures` instead of the
        // constructor because it requires the features to be defined in order to
        // get the morse code content and create a <tspan> for each encoded character
        let morseCodeCharArray;
        if (Array.isArray(this.def.morseCodeLbl)) {
          morseCodeCharArray = this.def.morseCodeLbl;
        } else {
          morseCodeCharArray = this.def.morseCodeLbl(this.features);
        }
        // remove the spaces from the morse code characters to make the overall representation more compact
        morseCodeCharArray = morseCodeCharArray.map(morseChar => morseChar.replaceAll(' ', ''));
        this.morseCodeLabel.textContent = '';
        this.morseCodeElementContentPairs = [];
        for (const morseCodeChar of morseCodeCharArray) {
          const tspan = document.createElementNS(SVG$2, 'tspan');
          this.morseCodeLabel.appendChild(tspan);
          // zip the created tspan elements with their associated morse code characters so that
          // we can easily access and set their text content when we `show` the button
          this.morseCodeElementContentPairs.push({
            element: tspan,
            content: morseCodeChar
          });
        }
      }
    }
    return did_update;
  }
  /**
   * Sets the 'pressed' style.
   */
  press() {
    this.path.classList.add('pressed');
  }
  /**
   * 'Unsets' the 'pressed' style.
   */
  unpress() {
    this.path.classList.remove('pressed');
  }
  /**
   * Reshapes this button. This is called when the button needs to be drawn in a new location with a
   * new size on the Radial Menu.
   * @param angle The starting angle of this button's arc.
   * @param arc_len The arc length of this button's arc.
   */
  reshape(angle, arc_len) {
    const path = new SvgRadialPath();
    path.move(angle, RADIUS_BTN_OUTER);
    path.arc(arc_len, RADIUS_BTN_OUTER);
    path.line(RADIUS_BTN_INNER);
    path.arc(-arc_len, RADIUS_BTN_INNER);
    path.close();
    this.path.setAttribute('d', path.d);
    // ---- image and text
    // this angle is in the middle of the start and end angles
    const middle = angle + arc_len / 2;
    if (this.image !== undefined) {
      // this position needs to be shifted to the left and up because the image
      // anchor is at its top-left corner while these coordinates are where we want
      // the center of the image to be
      const ip = makePoint(middle, RADIUS_IMAGE); // desired image center coordinates
      ip.x -= IMG_SHIFT; // shift left
      ip.y -= IMG_SHIFT; // shift up
      this.image.setAttribute('x', `${ip.x}`);
      this.image.setAttribute('y', `${ip.y}`);
    }
    if (this.label !== undefined) {
      const tp = makePoint(middle, RADIUS_LABEL);
      this.label.setAttribute('x', `${tp.x}`);
      this.label.setAttribute('y', `${tp.y}`);
      this.label.setAttribute('dominant-baseline', tp.y > SIZE / 2 ? 'hanging' : 'auto');
      this.label.setAttribute('text-anchor', tp.x < SIZE / 2 ? 'end' : 'start');
      if (this.morseCodeLabel != null && this.morseCodeElementContentPairs != null) {
        const LABEL_GAP_PX = Math.ceil(SIZE * FONT_LBL_SCALE * .5);
        const MORSE_CODE_LABEL_VERTICAL_OFFSET_PX = Math.ceil(SIZE * FONT_LBL_SCALE * .125);
        const labelBbox = this.label.getBBox();
        const morseCodeXPos = labelBbox.x + labelBbox.width + LABEL_GAP_PX;
        const morseCodeYPos = labelBbox.y - MORSE_CODE_LABEL_VERTICAL_OFFSET_PX;
        this.morseCodeLabel.setAttribute('x', `${morseCodeXPos}`);
        this.morseCodeLabel.setAttribute('y', `${morseCodeYPos}`);
        this.morseCodeLabel.setAttribute('dominant-baseline', 'hanging');
        for (let i = 0; i < this.morseCodeElementContentPairs.length; i++) {
          const morseCodeElement = this.morseCodeElementContentPairs[i].element;
          morseCodeElement.setAttribute('x', `${morseCodeXPos}`);
          morseCodeElement.setAttribute('y', `${morseCodeYPos + Math.ceil(SIZE * FONT_LBL_SCALE * .75 * i)}`);
          // the dominant-baseline attribute does not get inherited from the parent in safari
          morseCodeElement.setAttribute('dominant-baseline', 'hanging');
        }
      }
    }
  }
}
const getNavaidIconType = feature => {
  const props = feature.properties;
  switch (props.type) {
    case 'VOR':
      return MapIcon.Navaid_VOR;
    case 'DME':
      return MapIcon.Navaid_DME;
    case 'TACAN':
      return MapIcon.Navaid_TACAN;
    case 'VOR/DME':
      return MapIcon.Navaid_VOR_DME;
    case 'VORTAC':
      return MapIcon.Navaid_VORTAC;
    case 'ILS/DME':
      return MapIcon.Navaid_VOR;
    case 'NDB':
      return MapIcon.Navaid_NDB;
    case 'Marker NDB':
      return MapIcon.Navaid_NDB_Marker;
    default:
      {
        const wptProps = feature.properties;
        return wptProps.is_on_rnav ? MapIcon.Navaid_WPT_RNAV : MapIcon.Navaid_WPT;
      }
  }
};
const getObstacleIconType = feature => {
  const props = feature.properties;
  switch (props.obstacleIcon) {
    case 'LightedShort':
      return MapIcon.Obstacle_Lighted_Short;
    case 'LightedTall':
      return MapIcon.Obstacle_Lighted_Tall;
    case 'UnlightedShort':
      return MapIcon.Obstacle_Unlighted_Short;
    case 'UnlightedTall':
      return MapIcon.Obstacle_Unlighted_Tall;
    default:
      return MapIcon.Obstacle_Lighted_Short;
  }
};
const getPirepsIcon = feature => {
  const icon = feature.properties.icon;
  switch (icon) {
    case 'pirep':
      return MapIcon.Pirep_Generic;
    case 'icing_lgt':
      return MapIcon.Pirep_Icing_Light;
    case 'icing_lgt_mod':
      return MapIcon.Pirep_Icing_LightToModerate;
    case 'icing_mod':
      return MapIcon.Pirep_Icing_Moderate;
    case 'icing_tr':
      return MapIcon.Pirep_Icing_Trace;
    case 'icing_tr_lgt':
      return MapIcon.Pirep_Icing_TraceToLight;
    case 'skycond_bkn':
      return MapIcon.Pirep_Sky_Broken;
    case 'skycond_clr':
      return MapIcon.Pirep_Sky_Clear;
    case 'skycond_ovc':
      return MapIcon.Pirep_Sky_Overcast;
    case 'skycond_sct':
      return MapIcon.Pirep_Sky_Scattered;
    case 'icing_neg':
      return MapIcon.Pirep_Icing_Negative;
    case 'turbulence_neg':
      return MapIcon.Pirep_Turbulence_Negative;
    case 'skycond_unknown':
      return MapIcon.Pirep_Sky_Unknown;
    case 'turbulence_lgt':
      return MapIcon.Pirep_Turbulence_Light;
    case 'turbulence_lgt_mod':
      return MapIcon.Pirep_Turbulence_LightToModerate;
    case 'turbulence_mod':
      return MapIcon.Pirep_Turbulence_Moderate;
    case 'turbulence_mod_sev':
      return MapIcon.Pirep_Turbulence_ModerateToSevere;
    default:
      console.error('Unknown PIREP icon', icon);
      return MapIcon.Pirep_Generic;
  }
};
const ID_INFO_GRADIENT = 'info-ring-gradient';
const ID_INFO_BOTTOM = 'elevation';
const ID_INFO_TOP_LEFT = 'distance';
const ID_INFO_TOP_RIGHT = 'bearing';
const DEAD_SEA_ELEVATION_METERS = -450; // Lowest point on the Earth
/**
 * This class represents the inner information ring of the Radial Menu and displays information
 * related to the location of the Radial Menu on the map and (if available) the user's location.
 */
class SvgInfoRing {
  constructor(svg, terrainService) {
    this.terrainService = terrainService;
    this.group = document.createElementNS(SVG$2, 'g');
    this.bottom = createTextPath(ID_INFO_BOTTOM);
    this.top_left = createTextPath(ID_INFO_TOP_LEFT);
    this.top_right = createTextPath(ID_INFO_TOP_RIGHT);
    this.elevation = undefined;
    this.position = new LngLat(0, 0);
    this.group.classList.add('radial-menu-info-hide');
    this.group.id = 'radial-menu-info-ring';
    this.createBackground();
    this.createDefs();
    this.createTextElements();
    svg.appendChild(this.group);
  }
  /**
   * Toggles the info ring on and off.
   * @param show Whether to show the info ring.
   */
  toggle(show) {
    if (show) {
      this.group.classList.replace('radial-menu-info-hide', 'radial-menu-info-show');
    } else {
      this.group.classList.replace('radial-menu-info-show', 'radial-menu-info-hide');
    }
  }
  /**
   * Tells the info ring about updates to the Marker location.
   * @param mkr The current Marker location.
   */
  update(mkr, map) {
    this.position = mkr;
    // Displays the longitude and latitude at the center of the of the Radial Menu.
    this.top_left.textContent = `${Math.abs(this.position.lng).toFixed(2)} ${this.position.lng >= 0 ? 'E' : 'W'}`;
    this.top_right.textContent = `${Math.abs(this.position.lat).toFixed(2)} ${this.position.lat >= 0 ? 'N' : 'S'}`;
    this.updateElevation(map);
  }
  /**
   * Displays the elevation info at the center of the Radial Menu aperture.
   */
  updateElevation(map) {
    if (this.terrainService.available()) {
      this.terrainService.getElevation(this.position, map).then(value => {
        // Verify that the value we are getting back corresponds to a real world value
        // The Dead Sea is the lowest point on Earth, any result back should be greater than it
        if (value > DEAD_SEA_ELEVATION_METERS) {
          this.elevation = Math.round(value * 3.28084); // convert meters to feet
          this.bottom.textContent = `${this.elevation} Ft`;
        } else {
          console.error('invalid elevation', this.position);
          this.elevation = undefined;
          this.bottom.textContent = '--- Ft';
        }
      }).catch(error => {
        console.error('terrain error', error);
        this.elevation = undefined;
        this.bottom.textContent = '--- Ft';
      });
    } else {
      this.elevation = undefined;
      this.bottom.textContent = '--- Ft';
    }
  }
  /**
   * Creates the background and aperture outline of the info ring.
   */
  createBackground() {
    const p = new SvgRadialPath();
    p.donut(RADIUS_INFO_OUTER, RADIUS_INFO_INNER);
    this.group.appendChild(p.element({
      fill: `url(#${ID_INFO_GRADIENT})`
    }));
    // ---- the aperture outline
    const aperture_outline = document.createElementNS(SVG$2, 'circle');
    aperture_outline.id = 'aperture-outline';
    aperture_outline.setAttribute('cx', `${ORIGIN.x}`);
    aperture_outline.setAttribute('cy', `${ORIGIN.y}`);
    aperture_outline.setAttribute('r', `${RADIUS_APERTURE}`);
    this.group.appendChild(aperture_outline);
  }
  /**
   * Creates all of the SVG definitions for the info circle including the text paths and background
   * gradient.
   */
  createDefs() {
    // info circle text path definitions
    const gap = Math.PI / 32;
    const arc_len = (2 * Math.PI - 3 * gap) / 3;
    const defs = document.createElementNS(SVG$2, 'defs');
    const angle = Math.PI / 2 + arc_len / 2;
    // ---- bottom info text
    const path = new SvgRadialPath();
    // pad the radius since the middle baseline isn't actually in the middle for Roboto - it's slightly towards the bottom
    path.move(angle, RADIUS_INFO_TEXT + 2);
    path.arc(-arc_len, RADIUS_INFO_TEXT + 2); // negative = counterclockwise (i.e. left-to-right for the text)
    defs.appendChild(path.element({
      id: ID_INFO_BOTTOM
    }));
    // this.group.appendChild(path.element({ fill: 'none', stroke: 'red' })); // debugging
    // ---- top left info text
    path.clear();
    path.move(angle + gap, RADIUS_INFO_TEXT);
    path.arc(arc_len, RADIUS_INFO_TEXT);
    defs.appendChild(path.element({
      id: ID_INFO_TOP_LEFT
    }));
    // this.group.appendChild(path.element({ fill: 'none', stroke: 'green' })); // debugging
    // ---- top right info text
    path.clear();
    path.move(angle + arc_len + 2 * gap, RADIUS_INFO_TEXT);
    path.arc(arc_len, RADIUS_INFO_TEXT);
    defs.appendChild(path.element({
      id: ID_INFO_TOP_RIGHT
    }));
    // this.group.appendChild(path.element({ fill: 'none', stroke: 'blue' })); // debugging
    // ---- radial gradient coloring for info ring
    const gradient = document.createElementNS(SVG$2, 'radialGradient');
    gradient.id = ID_INFO_GRADIENT;
    defs.appendChild(gradient);
    const stop1 = document.createElementNS(SVG$2, 'stop');
    stop1.setAttribute('offset', '47%');
    stop1.setAttribute('stop-color', COLOR_INFO);
    gradient.appendChild(stop1);
    const stop2 = document.createElementNS(SVG$2, 'stop');
    stop2.setAttribute('offset', '100%');
    stop2.setAttribute('stop-color', 'transparent');
    gradient.appendChild(stop2);
    this.group.appendChild(defs);
  }
  /**
   * Creates the three text elements on the info ring.
   */
  createTextElements() {
    this.group.appendChild(createText(this.bottom));
    this.group.appendChild(createText(this.top_left));
    this.group.appendChild(createText(this.top_right));
  }
}
// ---- helpers
/**
 * Creates an SVGTextPathElement references the given path definition ID.
 * @param href The path definition ID for this text.
 * @returns An SVGTextPathElement.
 */
const createTextPath = href => {
  const text_path = document.createElementNS(SVG$2, 'textPath');
  text_path.setAttribute('href', `#${href}`);
  text_path.setAttribute('startOffset', '50%');
  text_path.setAttribute('text-anchor', 'middle');
  return text_path;
};
/**
 * Wraps the given text path in an SVGTextElement.
 * @param text_path The SVGTextPathElement to wrap.
 * @returns An SVGTextElement.
 */
const createText = text_path => {
  const text = document.createElementNS(SVG$2, 'text');
  text.setAttribute('dominant-baseline', 'middle');
  text.setAttribute('font-size', `${Math.ceil(SIZE * FONT_INFO_SCALE)}px`);
  text.appendChild(text_path);
  return text;
};

/**
 * This class provides a convenient API for building SVG filters.
 */
class SvgFilter {
  constructor(options) {
    this.filter = document.createElementNS(SVG$2, 'filter');
    this.filter.id = options.id;
    if (options.pos !== undefined) {
      this.filter.setAttribute('x', options.pos.x);
      this.filter.setAttribute('y', options.pos.y);
    }
    if (options.size !== undefined) {
      this.filter.setAttribute('width', options.size.w);
      this.filter.setAttribute('height', options.size.h);
    }
  }
  /**
   * Adds a Gaussian blur effect.
   * @param intensity The intensity (i.e. standard deviation) of the blur.
   * @param options General effect options.
   */
  feGaussianBlur(intensity, options) {
    const b = document.createElementNS(SVG$2, 'feGaussianBlur');
    b.setAttribute('stdDeviation', `${intensity}`);
    setEffectOptions(b, options);
    this.filter.appendChild(b);
  }
  /**
   * Applies a composition operator to one or two inputs.
   * @param operator The composition operator to apply.
   * @param options General effect options.
   */
  feComposite(operator, options) {
    const c = document.createElementNS(SVG$2, 'feComposite');
    c.setAttribute('operator', operator);
    setEffectOptions(c, options);
    this.filter.appendChild(c);
  }
  /**
   * Applies a morph operator to one or two inputs.
   * @param operator The morph operator to apply.
   * @param radius The radius of the morph.
   * @param options General effect options.
   */
  feMorphology(operator, radius, options) {
    const m = document.createElementNS(SVG$2, 'feMorphology');
    m.setAttribute('operator', operator);
    m.setAttribute('radius', radius);
    setEffectOptions(m, options);
    this.filter.appendChild(m);
  }
  /**
   * Merges the given set of nodes/effects (IDs) together.
   * @param nodes The set of nodes/effects (IDs) to merge together.
   */
  feMerge(nodes) {
    const m = document.createElementNS(SVG$2, 'feMerge');
    for (const node of nodes) {
      const n = document.createElementNS(SVG$2, 'feMergeNode');
      n.setAttribute('in', node);
      m.appendChild(n);
    }
    this.filter.appendChild(m);
  }
}
/**
 * A convenience function for setting options on an SVG element.
 * @param elem The element to set options for.
 * @param opts The set of options.
 */
const setEffectOptions = (elem, opts) => {
  if (opts.id !== undefined) {
    elem.id = opts.id;
  }
  if (opts.in !== undefined) {
    elem.setAttribute('in', opts.in);
  }
  if (opts.in2 !== undefined) {
    elem.setAttribute('in2', opts.in2);
  }
  if (opts.result !== undefined) {
    elem.setAttribute('result', opts.result);
  }
};
const ID_NORMAL_SHADOW = 'normal-shadow-filter';
const ID_DRAG_SHADOW = 'drag-shadow-filter';
/**
 * Creates the filters and path elements for the Radial Menu shadows.
 * @param svg The SVGSVGElement to add these shadows to.
 */
const addShadows = svg => {
  // normal shadow filter
  const normal = new SvgFilter({
    id: ID_NORMAL_SHADOW
  });
  normal.feGaussianBlur(6, {
    result: 'blur'
  });
  normal.feComposite('out', {
    in: 'blur',
    in2: 'SourceGraphic'
  });
  svg.appendChild(normal.filter);
  // dragging shadow filter
  const dragging = new SvgFilter({
    id: ID_DRAG_SHADOW
  });
  dragging.feGaussianBlur(6, {});
  svg.appendChild(dragging.filter);
  // normal shadow path
  const path = new SvgRadialPath();
  path.move(ARC_LEN_BTN_DYN_MAX / 2, RADIUS_BTN_INNER);
  path.arc(ARC_LEN_BTN_GAP, RADIUS_BTN_INNER);
  path.line(RADIUS_BTN_OUTER);
  path.arc(ARC_LEN_BTN_STATIC, RADIUS_BTN_OUTER);
  path.line(RADIUS_BTN_INNER);
  path.arc(ARC_LEN_BTN_GAP, RADIUS_BTN_INNER);
  path.line(RADIUS_BTN_OUTER);
  path.arc(ARC_LEN_BTN_DYN_MAX, RADIUS_BTN_OUTER);
  path.close();
  svg.appendChild(path.element({
    filter: `url(#${ID_NORMAL_SHADOW})`
  }));
  // dragging shadow path
  path.clear();
  path.donut(85, RADIUS_APERTURE);
  svg.appendChild(path.element({
    id: 'drag-shadow',
    filter: `url(#${ID_DRAG_SHADOW})`
  }));
};
const KEYFRAMES = [{
  transform: 'rotate(0deg)'
}, {
  transform: 'rotate(360deg)'
}];
const OPTIONS = {
  duration: 4000,
  iterations: Infinity
};
/**
 * This class exists to manage the orbiting animation of the "thinking" element.
 * The reason for this is that the CSS version seems to remove animations when
 * the element is not visible, thus losing the playback location.
 */
class SvgThinking {
  constructor(svg) {
    const p = new SvgRadialPath();
    p.move(0, RADIUS_THINKING);
    p.arc(ARC_LEN_THINKING, RADIUS_THINKING);
    this.path = p.element({
      id: 'thinking'
    });
    this.animation = this.path.animate(KEYFRAMES, OPTIONS);
    this.animation.pause();
    svg.appendChild(this.path);
  }
  /**
   * Starts the 'thinking' animation.
   */
  start() {
    this.animation.play();
  }
  /**
   * Stops the 'thinking' animation.
   */
  stop() {
    this.animation.pause();
  }
}
const BUTTONS_HIDE = 'radial-menu-buttons-hide';
const BUTTONS_SHOW = 'radial-menu-buttons-show';
const RADIAL_MENU_HIDE = 'radial-menu-hide';
const RADIAL_MENU_SHOW = 'radial-menu-show';
const RADIAL_MENU_DRAG = 'radial-menu-drag';
const MAX_DYN_BTNS = 7;
/**
 * This class represents the entire visual/interactive elements of the Radial Menu as an SVG. There
 * are only a few functions related to changing the styles for different scenarios (like showing and
 * hiding the SVG, and dragging it). The more interesting functions are for updating the info ring
 * and the dynamic buttons as the SVG is dragged around the map.
 */
class RadialSvg {
  constructor(map, mapService, airportService, stormCellService, terrainService, radialMenuMapService, radialMenuService) {
    this.radialMenuMapService = radialMenuMapService;
    this.radialMenuService = radialMenuService;
    this.visible = false;
    this.static_buttons = [];
    this.dynamic_buttons = [];
    this.empty_button = new SvgButton({
      shape: {
        angle: Math.PI + 0.5 * ARC_LEN_BTN_STATIC + ARC_LEN_BTN_GAP,
        arc_len: ARC_LEN_BTN_DYN_MAX
      }
    }, false);
    // create top-level elements
    this.svg = document.createElementNS(SVG$2, 'svg');
    this.svg.id = 'radial-menu';
    this.svg.setAttribute('width', `${SIZE}`);
    this.svg.setAttribute('height', `${SIZE}`);
    this.svg.setAttribute('viewPort', `0 0 ${SIZE} ${SIZE}`);
    this.svg.setAttribute('overflow', 'visible');
    this.container = document.createElement('div');
    this.container.classList.add(RADIAL_MENU_HIDE);
    this.container.appendChild(this.svg);
    addShadows(this.svg);
    this.info_circle = new SvgInfoRing(this.svg, terrainService);
    this.buttons_group = document.createElementNS(SVG$2, 'g');
    this.buttons_group.classList.add(BUTTONS_HIDE);
    this.buttons_group.setAttribute('cursor', 'default');
    this.onClick = e => {
      e.stopPropagation();
      this.toggle();
    };
    this.buttons_group.addEventListener('click', this.onClick);
    this.svg.appendChild(this.buttons_group);
    const assetsPath = mapService.moduleConfig.mapAssetsPath;
    const btnDefs = staticDefs(assetsPath, this.radialMenuMapService);
    if (map != null) {
      btnDefs.push(...dynamicDefs(assetsPath, airportService, stormCellService, mapService.icon, this.radialMenuMapService));
    }
    for (const def of btnDefs) {
      const btn = new SvgButton(def);
      this.buttons_group.appendChild(btn.group);
      if (btn.label != null) {
        this.svg.appendChild(btn.label);
      }
      if (btn.morseCodeLabel != null) {
        this.svg.appendChild(btn.morseCodeLabel);
      }
      if (btn.def.isStatic ?? false) {
        this.static_buttons.push(btn);
      } else {
        this.dynamic_buttons.push(btn);
      }
    }
    if (this.empty_button != null) {
      this.buttons_group.appendChild(this.empty_button.group);
    }
    this.thinking = new SvgThinking(this.svg);
    this.radialMenuMapService.radialMenuWatcher$.pipe().subscribe({
      next: () => {
        this.updateButtonLayerIds();
      },
      error: error => {
        console.error(error);
      }
    });
  }
  destroy() {
    this.buttons_group.removeEventListener('click', this.onClick);
    this.empty_button?.destroy();
    delete this.empty_button;
    for (const btn of this.static_buttons) {
      btn.destroy();
    }
    this.static_buttons = [];
    for (const btn of this.dynamic_buttons) {
      btn.destroy();
    }
    this.dynamic_buttons = [];
  }
  /**
   * Connects each Radial Menu button's click functionality to the PopupService.
   * @param popupService The service that should be notified when a RadialMenu button is clicked.
   * @param getRadialMenuPosition A callback to get the position of the radial menu.
   */
  popupConnect(popupService, getRadialMenuPosition) {
    for (const btn of this.dynamic_buttons) {
      btn.onclick = data => {
        if (data.feature_type != null) {
          popupService.showFeatures(data.feature_type, data.features ?? [], getRadialMenuPosition != null ? getRadialMenuPosition() : undefined);
        }
      };
    }
  }
  /**
   * Connects each Radial Menu button's click functionality to the PopupService.
   * @param popupService The service that should be notified when a RadialMenu button is clicked.
   * @param getRadialMenuPosition A callback to get the position of the radial menu.
   */
  waypointConnect(popupService, getRadialMenuPosition, isEnabled) {
    for (const btn of this.static_buttons) {
      if (btn.def.feature_type === FeatureType$1.UserWaypoints) {
        btn.isEnabled = isEnabled;
        btn.onclick = data => {
          popupService.showFeatures(FeatureType$1.UserWaypoints, data.features ?? [], getRadialMenuPosition != null ? getRadialMenuPosition() : undefined);
        };
      }
    }
  }
  /**
   * Toggles the SVG on and off by changing styles.
   */
  toggle() {
    this.visible = !this.visible;
    this.radialMenuService.setIsOpen(this.visible);
    if (this.visible) {
      this.container.classList.replace(RADIAL_MENU_HIDE, RADIAL_MENU_SHOW);
      this.buttons_group.classList.replace(BUTTONS_HIDE, BUTTONS_SHOW);
    } else {
      this.container.classList.replace(RADIAL_MENU_SHOW, RADIAL_MENU_HIDE);
      this.buttons_group.classList.replace(BUTTONS_SHOW, BUTTONS_HIDE);
    }
    this.info_circle.toggle(this.visible);
  }
  /**
   * Dims the SVG, 'unclicks' all buttons, and starts the 'thinking' animation.
   */
  dragstart() {
    this.container.classList.replace(RADIAL_MENU_SHOW, RADIAL_MENU_DRAG);
    for (const btn of this.static_buttons) {
      btn.group.dispatchEvent(new Event('unclick'));
    }
    for (const btn of this.dynamic_buttons) {
      btn.group.dispatchEvent(new Event('unclick'));
    }
    this.thinking.start();
  }
  /**
   * 'Undims' the SVG and stops the 'thinking' animation.
   */
  dragend() {
    this.container.classList.replace(RADIAL_MENU_DRAG, RADIAL_MENU_SHOW);
    this.thinking.stop();
  }
  updateButtonLayerIds() {
    this.dynamic_buttons.forEach(btn => {
      btn.def.layer_ids = btn.def.feature_type == null ? [] : this.radialMenuMapService.getLayerDataByFeature(btn.def.feature_type);
    });
  }
  /**
   * Updates the dynamic SVG buttons based on the current set of map features found within the Radial
   * Menu's aperture.
   * @param features The set of map features currently found within the Radial Menu aperture.
   */
  updateDynamicButtons(features) {
    // update all of the dynamic buttons with the latest features
    let did_update = false;
    this.dynamic_buttons.forEach(btn => {
      did_update = btn.updateFeatures(features) || did_update;
    });
    // if no updates occurred, then don't waste anymore time
    if (!did_update) {
      return;
    }
    // figure out how many buttons we need to show
    const feature_count = this.dynamic_buttons.map(btn => btn.features != null ? 1 : 0).reduce((p, c) => p + c, 0);
    // no features to show
    if (feature_count === 0) {
      this.showEmptyButton();
      return;
    }
    // too many features to show
    if (feature_count > MAX_DYN_BTNS) {
      const takeFeatures = btn => {
        let buttonFeatures;
        if (btn.features != null) {
          buttonFeatures = btn.features;
        }
        btn.features = undefined;
        if (buttonFeatures !== undefined) {
          return buttonFeatures;
        } else {
          return [];
        }
      };
      // If we've reached our button count limit, then we need to remove `(feature_count - MAX_DYN_BTNS) + 1` features
      // since we're adding the 'more' button as well.
      const moreButton = this.dynamic_buttons.find(btn => btn.def.feature_type === FeatureType$1.MoreInfo);
      if (moreButton != null) {
        moreButton.features = [];
      }
      let new_feature_count = feature_count;
      // TODO: decide which features types go into the 'more' button - AIR/SIGMETs and Obstacles are what I see in GP
      let button = this.dynamic_buttons.find(btn => btn.def.feature_type === FeatureType$1.AirmetsSigmets);
      if (button != null) {
        if (button.features !== undefined) {
          if (moreButton != null) {
            if (moreButton.features != null) {
              moreButton.features = moreButton.features.concat(takeFeatures(button));
            }
          }
          new_feature_count -= 1;
        }
      }
      button = this.dynamic_buttons.find(btn => btn.def.feature_type === FeatureType$1.Obstacles);
      if (button != null) {
        if (button.features != null) {
          if (moreButton != null) {
            if (moreButton.features != null) {
              moreButton.features = moreButton.features.concat(takeFeatures(button));
            }
          }
          new_feature_count -= 1;
        }
      }
      if (new_feature_count > MAX_DYN_BTNS) {
        console.warn('radial menu feature count exceeds limit');
      }
    }
    // show/hide the buttons for the latest features
    const arc_len = ARC_LEN_BTN_DYN_MAX / Math.min(feature_count, MAX_DYN_BTNS);
    let angle = Math.PI + 0.5 * ARC_LEN_BTN_STATIC + ARC_LEN_BTN_GAP;
    this.empty_button?.hide();
    for (const btn of this.dynamic_buttons) {
      if (btn.features != null) {
        btn.show();
        btn.reshape(angle, arc_len);
        angle += arc_len;
      } else {
        btn.hide();
      }
    }
  }
  /**
   * Updates the static SVG buttons based on the current set of map features found within the Radial
   * Menu's aperture.
   * @param features The set of map features currently found within the Radial Menu aperture.
   */
  updateStaticButtons(features) {
    this.static_buttons.forEach(btn => {
      btn.updateFeatures(features);
      btn.show();
    });
  }
  /**
   * Tells the SVG to only the empty button.
   */
  showEmptyButton() {
    // first hide all dynamic buttons
    for (const btn of this.dynamic_buttons) {
      btn.hide();
    }
    this.empty_button?.show();
  }
}
// ---- Button Definitions
const STATIC_START_ANGLE = Math.PI - 0.5 * ARC_LEN_BTN_STATIC;
/**
 * Generates an array of definitions used to construct all of the static buttons for the Radial
 * Menu. A static button is a button that is always visible in the radial menu.
 * @param assetsPath The path to static icon assets.
 * @returns An array of SvgButtonDefs for the static Radial Menu buttons.
 */
const staticDefs = (assetsPath, radialMenuMapService) => [{
  feature_type: FeatureType$1.UserWaypoints,
  layer_ids: radialMenuMapService.getLayerData(LayerType.UserWaypoints),
  prop_id: 'data',
  shape: {
    angle: STATIC_START_ANGLE,
    arc_len: ARC_LEN_BTN_STATIC
  },
  img: f => {
    const t = f != null && f.length > 0 ? 'edit-waypoint' : 'user-waypoint';
    return `${assetsPath}/radial-menu/${t}.svg`;
  },
  lbl: f => {
    return f != null ? f[0].properties.id : '';
  },
  isStatic: true
}];
/**
 * Creates an SvgButtonDef for the given NAVAID layer.
 * @param layerId A NAVAID layer ID.
 * @param iconService The IconService.
 * @returns An SvgButtonDef for the given NAVAID layer.
 */
const navaidDef = (layerId, iconHandler, type) => {
  const navaidButtonDef = {
    feature_type: getNavaidsType(type),
    layer_ids: layerId,
    prop_id: 'identifier',
    img: f => {
      const navaidIcon = iconHandler.get(getNavaidIconType(f[0]));
      if (navaidIcon != undefined) {
        return navaidIcon.currentSrc;
      } else {
        return '';
      }
    },
    lbl: f => {
      const props = f[0].properties;
      const frequency = 'frequency' in props ? `(${parseFloat(props.frequency.toFixed(2))})` : '';
      return `${props.identifier}${frequency}`;
    }
  };
  if (navaidButtonDef.feature_type === FeatureType$1.NAVAIDs_VOR || navaidButtonDef.feature_type === FeatureType$1.NAVAIDs_NDB) {
    navaidButtonDef.morseCodeLbl = f => {
      const props = f[0].properties;
      return convertWordToMorseCharArray(props.identifier);
    };
  }
  return navaidButtonDef;
};
const getNavaidsType = type => {
  switch (type) {
    case 'vor':
      return FeatureType$1.NAVAIDs_VOR;
    case 'ndb':
      return FeatureType$1.NAVAIDs_NDB;
    case 'wpt':
      return FeatureType$1.NAVAIDs_WPT;
    default:
      return undefined;
  }
};
const getWeatherConditionIconPath = (assetsPath, airfieldRating) => {
  if (airfieldRating == null) {
    return `${assetsPath}/radial-menu/wx-na.svg`;
  }
  return `${assetsPath}/radial-menu/wx-${airfieldRating.toLowerCase()}.svg`;
};
/**
 * Pluralizes the given label if the feature count is > 1.
 * @param label The label to pluralize.
 * @param featureCount The number of features associated with this label.
 * @returns The pluralized label (if the feature count is > 1).
 */
const pluralize = (label, featureCount) => `${label}${featureCount > 1 ? 's' : ''}`;
/**
 * Generates an array of definitions used to construct most of the dynamic buttons for the Radial
 * Menu. The order of these is based on Garmin Pilot.
 * @param assetsPath The path to static icon assets.
 * @returns An array of SvgButtonDefs for most the dynamic Radial Menu buttons.
 */
const dynamicDefs = (assetsPath, airportService, stormCellService, iconHandler, radialMenuMapService) => [{
  feature_type: FeatureType$1.Airports,
  layer_ids: radialMenuMapService.getLayerData(LayerType.Airports),
  prop_id: 'airportId',
  loadImg: (f, onload) => {
    if (f != null) {
      const airportId = f[0].properties.airportId;
      iconHandler.getAirport(airportId, airportService).then(value => {
        f[0].properties.loadedAirportImage = value;
        if (onload != null) {
          onload(f);
        }
      });
      return true;
    }
    return false;
  },
  img: f => f[0].properties.loadedAirportImage.currentSrc,
  lbl: f => f[0].properties.airportId
}, navaidDef(radialMenuMapService.getLayerData(LayerType.Navaids_VOR), iconHandler, 'vor'), navaidDef(radialMenuMapService.getLayerData(LayerType.Navaids_NDB), iconHandler, 'ndb'), {
  feature_type: FeatureType$1.Airspaces,
  layer_ids: radialMenuMapService.getLayerData(LayerType.Airspaces),
  use_feature_id: true,
  img: `${assetsPath}/radial-menu/airspace.svg`,
  lbl: f => pluralize('Airspace', f.length)
}, {
  feature_type: FeatureType$1.Airways,
  layer_ids: radialMenuMapService.getLayerData(LayerType.Airways),
  img: `${assetsPath}/radial-menu/airway.svg`,
  lbl: f => pluralize('Airway', f.length)
}, navaidDef(radialMenuMapService.getLayerData(LayerType.Navaids_WPT), iconHandler, 'wpt'), {
  feature_type: FeatureType$1.TFRs,
  layer_ids: radialMenuMapService.getLayerData(LayerType.Tfrs),
  prop_id: 'ident',
  img: `${assetsPath}/radial-menu/tfr-red.svg`,
  lbl: f => pluralize('TFR', f.length)
}, {
  feature_type: FeatureType$1.NOTAMs,
  layer_ids: radialMenuMapService.getLayerData(LayerType.Notams).concat(radialMenuMapService.getLayerData(LayerType.RunwayNotams)),
  prop_id: 'notamId',
  img: `${assetsPath}/radial-menu/notam.svg`,
  lbl: f => pluralize('NOTAM', f.length)
}, {
  feature_type: FeatureType$1.AirmetsSigmets,
  layer_ids: radialMenuMapService.getLayerData(LayerType.AirmetsSigmets),
  use_feature_id: true,
  img: f => {
    const t = f[0].properties.sigmetId != null ? 'sigmet' : 'airmet';
    return `${assetsPath}/radial-menu/${t}.svg`;
  },
  lbl: f => {
    const t = f[0].properties.sigmetId != null ? 'SIGMET' : 'AIRMET';
    return pluralize(t, f.length);
  }
}, {
  feature_type: FeatureType$1.Fuel,
  layer_ids: radialMenuMapService.getLayerData(LayerType.FuelPrices),
  prop_id: 'stationId',
  img: `${assetsPath}/radial-menu/fuel.svg`,
  lbl: 'Fuel'
}, {
  feature_type: FeatureType$1.InternetTraffic,
  layer_ids: radialMenuMapService.getLayerData(LayerType.InternetTraffic),
  prop_id: 'id',
  img: `${assetsPath}/radial-menu/internet-traffic.svg`,
  lbl: features => {
    if (features.length > 1) {
      return 'Traffic';
    } else {
      const id = features[0]?.properties?.id;
      return id ?? 'Traffic';
    }
  }
}, {
  feature_type: FeatureType$1.Obstacles,
  layer_ids: radialMenuMapService.getLayerData(LayerType.Obstacles),
  sort_fn: (a, b) => {
    return b.properties.mslPlusAgl - a.properties.mslPlusAgl;
  },
  img: f => {
    const obstacleIcon = iconHandler.get(getObstacleIconType(f[0]));
    if (obstacleIcon !== undefined) {
      return obstacleIcon.currentSrc;
    } else {
      return '';
    }
  },
  lbl: f => `${f[0].properties.mslPlusAgl}FT MSL`
}, {
  feature_type: FeatureType$1.PIREPs,
  layer_ids: radialMenuMapService.getLayerData(LayerType.Pireps),
  prop_id: 'pirepId',
  img: f => {
    const pirepIcon = iconHandler.get(getPirepsIcon(f[0]));
    if (pirepIcon !== undefined) {
      return pirepIcon.currentSrc;
    } else {
      return '';
    }
  },
  lbl: f => pluralize('PIREP', f.length)
}, {
  feature_type: FeatureType$1.StormCells,
  layer_ids: radialMenuMapService.getLayerData(LayerType.StormCells),
  img: `${assetsPath}/radial-menu/severe-weather.svg`,
  lbl: 'Storm Cells',
  filter_fn: f => stormCellService?.filterFeaturesByVisibility(f) ?? []
}, {
  feature_type: FeatureType$1.WeatherReport,
  layer_ids: radialMenuMapService.getLayerData(LayerType.Metars),
  prop_id: 'station',
  img: features => {
    return getWeatherConditionIconPath(assetsPath, features[0]?.properties?.airfieldRating);
  },
  lbl: features => {
    const stationID = features.at(0)?.properties.station;
    return stationID ?? 'Unknown';
  },
  weatherSource: 'METAR'
}, {
  feature_type: FeatureType$1.MoreInfo,
  img: `${assetsPath}/radial-menu/more-info.svg`,
  lbl: 'More Info'
}];
const CONTEXT_TIMEOUT = 10; // number of seconds to delete context after no accesses
const IMAGE_SIZE = 256; // size of image in pixels
/**
 * This is a class to retrieve and parse TerrainRGB PNGs in order
 * to access terrain elevation data
 */
class TerrainRGB {
  constructor() {
    this.destroyRef = inject(DestroyRef);
    this.http = inject(HttpClient);
  }
  /**
   * @param baseUrl URL with '{x}', '{y}', and '{z}' tile ID replacements
   * @param minZoom Minimum zoom level to clamp to
   * @param maxZoom Maximum zoom level to clamp to
   */
  configure(baseUrl, minZoom, maxZoom) {
    this.baseUrl = baseUrl;
    this.minZoom = minZoom;
    this.maxZoom = maxZoom;
  }
  /**
   * Gets the terrain elevation at a given location
   * @param pos The location
   * @returns A promise to provide the elevation in meters (or NaN for some errors)
   */
  getElevation(pos, mapDisplay) {
    return new Promise((resolve, reject) => {
      const zoom = this.getZoom(mapDisplay);
      const tile = lngLatToGoogle(pos.toArray(), zoom);
      const [pixelX, pixelY] = this.convertPositionToPixel(pos, tile);
      const url = this.getUrl(tile);
      if (this.previousUrl !== url) {
        this.previousUrl = url;
        this.http.get(url, {
          responseType: 'blob'
        }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: blob => {
            const imageUrl = URL.createObjectURL(blob);
            const image = new Image();
            image.onload = () => {
              this.drawImage(image);
              const elevation = this.processImage(pixelX, pixelY);
              if (elevation != null) {
                resolve(elevation);
              } else {
                reject(new Error(`image processing failed: ${url}, ${pos}`));
              }
            };
            image.onabort = () => {
              reject(new Error(`image loading aborted: ${url}, ${pos}`));
            };
            image.onerror = () => {
              reject(new Error(`image loading failed: ${url}, ${pos}`));
            };
            image.crossOrigin = 'use-credentials';
            image.src = imageUrl;
          },
          error: error => {
            console.error(error);
            reject(error);
          }
        });
      } else {
        const elevation = this.processImage(pixelX, pixelY);
        if (elevation != null) {
          resolve(elevation);
        } else {
          reject(new Error(`image processing failed: ${url}, ${pos}`));
        }
      }
    });
  }
  /**
   * Converts a pixel color to terrain elevation in meters
   */
  convertRgbToElevation(r, g, b) {
    return -10000 + (r * 256 * 256 + g * 256 + b) * 0.1;
  }
  /**
   * Converts a position and tile to an X and Y pixel value
   */
  convertPositionToPixel(pos, tile) {
    const [west, north, east, south] = googleToBBox(tile);
    const width = Math.abs(east - west);
    const height = Math.abs(north - south);
    const widthRatio = IMAGE_SIZE / width;
    const heightRatio = IMAGE_SIZE / height;
    const x = Math.floor((pos.lng - west) * widthRatio);
    const y = Math.floor((south - pos.lat) * heightRatio);
    return [x, y];
  }
  /**
   * Gets the URL for a tile
   */
  getUrl(tile) {
    return this.baseUrl?.replace(/{x}/g, `${tile[0]}`).replace(/{y}/g, `${tile[1]}`).replace(/{z}/g, `${tile[2]}`) ?? '';
  }
  /**
   * Gets the current zoom clamped to the minimum zoom and maximum zoom
   */
  getZoom(mapDisplay) {
    const zoom = mapDisplay.getZoom() ?? -1;
    const roundedZoom = Math.round(zoom);
    if (this.minZoom != null && roundedZoom < this.minZoom) {
      return this.minZoom;
    }
    if (this.maxZoom != null && roundedZoom > this.maxZoom) {
      return this.maxZoom;
    }
    return roundedZoom;
  }
  /**
   * Draws the images to the canvas
   * @param image
   */
  drawImage(image) {
    const context = this.getContext();
    context?.drawImage(image, 0, 0, image.width, image.height);
  }
  /**
   * Retrieves the value of the specified pixel, and returns the elevation
   * associated with that pixel
   */
  processImage(x, y) {
    const context = this.getContext();
    if (context == null) {
      console.error('Unable to get canvas context');
      this.deleteContext();
      return null;
    }
    const imageData = context.getImageData(x, y, 1, 1);
    if (imageData.colorSpace !== 'srgb') {
      console.error('Unsupported color space:', imageData.colorSpace);
      this.deleteContext();
      return null;
    }
    const elevation = this.convertRgbToElevation(imageData.data[0], imageData.data[1], imageData.data[2]);
    return elevation;
  }
  createContext() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = IMAGE_SIZE;
    this.canvas.height = IMAGE_SIZE;
    this.context = this.canvas.getContext('2d', {
      willReadFrequently: true
    }) ?? undefined;
    if (this.context == null) {
      console.error('Unable to get canvas context');
    }
  }
  deleteContext() {
    if (this.canvas != null) {
      this.canvas.width = 1;
      this.canvas.height = 1;
    }
    if (this.context != null) {
      this.context.clearRect(0, 0, 1, 1);
    }
    this.context = undefined;
    this.canvas = undefined;
    // with the canvas and context destroyed, we need to download
    // the image again to start the process over
    this.previousUrl = undefined;
  }
  getContext() {
    // Reset the timeout if it's active
    window.clearTimeout(this.timeout);
    // Create a new timeout
    this.timeout = window.setTimeout(() => this.deleteContext(), CONTEXT_TIMEOUT * 1000);
    // Create a canvas and context if it doesn't exist
    if (this.context == null) {
      this.createContext();
    }
    // Return the context
    return this.context;
  }
}
var AirwayType$1 = /*#__PURE__*/function (AirwayType) {
  AirwayType[AirwayType["Unknown"] = 0] = "Unknown";
  AirwayType[AirwayType["EnrouteLow"] = 1] = "EnrouteLow";
  AirwayType[AirwayType["EnrouteHigh"] = 2] = "EnrouteHigh";
  AirwayType[AirwayType["EnrouteBoth"] = 3] = "EnrouteBoth";
  return AirwayType;
}(AirwayType$1 || {});
const MAXIMUM_AIRWAYS_PER_FEATURE = 20;
const MAXIMUM_AIRWAY_IDENTIFIER_LABEL_LENGTH = 10;
const buildAirspaceFrequencyDescription = (properties, clickPos) => {
  if (properties == null) {
    return '';
  }
  const controllingAgency = properties.controllingAgency;
  let parsedList = [];
  if (properties.FrequencyList != null) {
    parsedList = JSON.parse(properties.FrequencyList);
  } else {
    const propId = i => `FrequencyList[${i}].FrequencyInfo`;
    let i = 0;
    let frequency = properties[`${propId(i)}.frequency`];
    while (frequency != null) {
      const lat = properties[`${propId(i)}.lat`];
      const lon = properties[`${propId(i)}.lon`];
      parsedList.push({
        frequency,
        lat,
        lon
      });
      i += 1;
      frequency = properties[`${propId(i)}.frequency`];
    }
  }
  if (parsedList.length < 1) {
    return '';
  }
  let frequencies;
  if (clickPos == null) {
    // Select the first 3 frequencies
    frequencies = parsedList.map(freqObj => {
      if (freqObj.frequency == null) {
        return freqObj;
      }
      return freqObj.frequency;
    }).slice(0, 3).join(', ');
  } else {
    // Select the 3 closest frequencies based on position
    frequencies = parsedList.map(freqObj => {
      return {
        frequency: freqObj.frequency,
        distance: turf.distance([clickPos.lng, clickPos.lat], [freqObj.lon, freqObj.lat])
      };
    }).sort((freq1, freq2) => freq1.distance - freq2.distance).slice(0, 3).map(freqObj => freqObj.frequency).join(', ');
  }
  const agency = controllingAgency != null && controllingAgency.length > 0 ? `${controllingAgency} - ` : '';
  return `${agency}${frequencies}`;
};
const buildAirspaceName = (name, icao) => {
  if (name == null || name.length < 1) {
    return '';
  }
  const icaoCode = icao != null && icao.length > 0 ? `(${icao})` : '';
  return `${name} ${icaoCode}`;
};
const buildAirspaceAltitudeDescription = (decimalPipe, alt, isSfc, isUnl, isNotam, isAgl) => {
  if (alt == null) {
    return '';
  }
  const altDec = `${decimalPipe.transform(alt, '0.0-0')}`;
  if (isSfc != null && isSfc) {
    return 'Surface';
  } else if (isUnl != null && isUnl) {
    return 'Unlimited';
  } else if (isNotam != null && isNotam) {
    return 'NOTAM';
  }
  const aglDescription = isAgl != null && isAgl ? ' AGL' : '';
  return `${altDec}FT${aglDescription}`;
};
const deduplicateNotams = notams => {
  return notams.filter((notam, index, array) => {
    return array.findIndex(p => p.id === notam.id && p.effectiveTime === notam.effectiveTime) === index;
  });
};
const deduplicateTfrs = tfrs => {
  return tfrs.filter((tfr, index, array) => {
    return array.findIndex(p => p.notam_ident === tfr.notam_ident && p.start_time === tfr.start_time) === index;
  });
};
const packAirmetSigmet = feature => {
  return {
    id: feature.properties['sigmetId'] ?? feature.properties['airmetId'],
    hazard: feature.properties['hazard'],
    isSigmet: feature.properties['sigmetId'] != null,
    isOutlook: feature.properties['isOutlook'],
    bounds: packBounds(feature.properties['featureMinLat'], feature.properties['featureMinLon'], feature.properties['featureMaxLat'], feature.properties['featureMaxLon']),
    reportText: feature.properties['reportText'],
    description: feature.properties['header'],
    effectiveTime: feature.properties['effectiveTime'] ?? feature.properties['validAtTime'],
    expiresTime: feature.properties['expireTime'] ?? feature.properties['validUntilTime'],
    minimumAltitude: feature.properties['AltitudeRange.MinAltitude.altitude'],
    minimumAltitudeMSL: feature.properties['AltitudeRange.MinAltitude.isMSL'],
    maximumAltitude: feature.properties['AltitudeRange.MaxAltitude.altitude'],
    maximumAltitudeMSL: feature.properties['AltitudeRange.MaxAltitude.isMSL']
  };
};
const packAirport = feature => {
  return {
    airportId: feature.properties['airportId'],
    name: feature.properties['name'],
    elevationFt: feature.properties['elevationFt'],
    longestRunwayLength: feature.properties['longestRunwayLength']
  };
};
const packMetar = (feature, currentPosition) => {
  return {
    stationId: feature.properties['station'],
    // lnglat of the selected station give more accurate results, there is a slight variation from currentPosition.
    lngLat: feature?.geometry?.type === 'Point' ? new LngLat(feature.geometry.coordinates[0], feature.geometry.coordinates[1]) : currentPosition,
    clouds: feature.properties['clouds'],
    cloudLayers: feature.properties['cloudLayers'],
    dewPointC: feature.properties['dewPointC'],
    expireTime: feature.properties['expireTime'],
    issueTime: feature.properties['issueTime'],
    pressure: feature.properties['pressure'],
    rawReport: feature.properties['rawReport'],
    tempC: feature.properties['tempC'],
    visibility: feature.properties['visibility'],
    windDirection: feature.properties['windDirection'],
    windSpeed: feature.properties['windSpeed'],
    wxDescription: feature.properties['wxDescription'],
    airfieldRating: feature.properties['airfrieldRating'],
    ceilingHeight: feature.properties['ceilingHeight'],
    ceilingType: feature.properties['ceilingType'],
    windLightVar: feature.properties['windLightVar'],
    windGust: feature.properties['windGust'],
    windDir: feature.properties['windDirection']
  };
};
const packAirspace = (feature, currentPosition, allNotams, decimalPipe) => {
  const id = feature.id;
  const type = feature.properties.classDescription;
  const isARTCC = type === 'ARTCC / FIR';
  const frequencyList = buildAirspaceFrequencyDescription(feature.properties, isARTCC ? currentPosition : undefined);
  const name = buildAirspaceName(feature.properties.name, isARTCC ? feature.properties.icao : '');
  const showAltitude = !isARTCC;
  const minAlt = buildAirspaceAltitudeDescription(decimalPipe, feature.properties['MinAltitude.altitude'], feature.properties['MinAltitude.isSfc'], feature.properties['MinAltitude.isUnl'], feature.properties['MinAltitude.isNotam'], feature.properties['MinAltitude.isAgl']);
  const maxAlt = buildAirspaceAltitudeDescription(decimalPipe, feature.properties['MaxAltitude.altitude'], feature.properties['MaxAltitude.isSfc'], feature.properties['MaxAltitude.isUnl'], feature.properties['MaxAltitude.isNotam'], feature.properties['MaxAltitude.isAgl']);
  const bounds = packBounds(feature.properties['featureMinLat'], feature.properties['featureMinLon'], feature.properties['featureMaxLat'], feature.properties['featureMaxLon']);
  // Find any NOTAMs that match this airspace
  const airspaceName = feature.properties.name;
  const relevantNotams = allNotams.filter(notam => {
    const notamLocation = notam.properties.notamLocation;
    if (airspaceName != null && notamLocation != null) {
      return notamLocation.toUpperCase() === airspaceName.toUpperCase();
    }
    return false;
  });
  const packedNotams = relevantNotams.map(x => packNotam(x));
  const notams = deduplicateNotams(packedNotams);
  const caspcClass = feature.properties.caspcClass;
  const suaClass = feature.properties.suaClass;
  return {
    id,
    type,
    isARTCC,
    frequencyList,
    name,
    showAltitude,
    minAlt,
    maxAlt,
    bounds,
    notams,
    caspcClass,
    suaClass
  };
};
const packAirway = feature => {
  let type;
  switch (feature.properties['level']) {
    case 'L':
      type = AirwayType$1.EnrouteLow;
      break;
    case 'H':
      type = AirwayType$1.EnrouteHigh;
      break;
    case 'B':
      type = AirwayType$1.EnrouteBoth;
      break;
    default:
      type = AirwayType$1.Unknown;
      break;
  }
  // Backwards compatibility: If the airwayId property exists, then we must be using the
  // old airway MVT schema. In that case, pack the airway info using the old properties.
  // Otherwise, pack the airway info with all found merged airways in the new schema
  if (feature.properties['airwayId'] != null) {
    return [{
      type,
      id: feature.id,
      airwayId: feature.properties['airwayId'],
      name: feature.properties['ident'],
      mea: feature.properties['mea'],
      directionRestrict: feature.properties['directionRestrict'],
      segment: feature.properties['endPoints'],
      distance: feature.properties['distance']
    }];
  }
  const airwayInfo = [];
  for (let i = 0; i < MAXIMUM_AIRWAYS_PER_FEATURE; i++) {
    if (feature.properties[`Airways[${i}].Airways.airwayId`] != null) {
      airwayInfo.push({
        type,
        id: feature.id,
        airwayId: feature.properties[`Airways[${i}].Airways.airwayId`],
        name: feature.properties[`Airways[${i}].Airways.ident`],
        mea: feature.properties[`Airways[${i}].Airways.mea`],
        directionRestrict: feature.properties[`Airways[${i}].Airways.directionRestrict`],
        segment: feature.properties['endPoints'],
        distance: feature.properties['distance']
      });
    }
  }
  return airwayInfo;
};
const packFuel = feature => {
  return {
    stationId: feature.properties['stationId'],
    avLowestPrice: feature.properties['avLowestPrice'],
    jetLowestPrice: feature.properties['jetLowestPrice'],
    autoLowestPrice: feature.properties['autoLowestPrice'],
    avLowestFboName: feature.properties['avFboName'],
    jetLowestFboName: feature.properties['jetFboName'],
    autoLowestFboName: feature.properties['autoFboName'],
    avLowestIssueTime: feature.properties['avIssueTime'],
    jetLowestIssueTime: feature.properties['jetIssueTime'],
    autoLowestIssueTime: feature.properties['autoIssueTime']
  };
};
const packInternetTraffic = feature => {
  const point = feature.geometry;
  const internetTrafficInfo = {
    timestamp: feature.properties['timestamp'],
    position: {
      ts: feature.properties['ts'],
      lat: feature.properties['lat'],
      lon: feature.properties['lon'],
      speed: feature.properties['speed'],
      altitude: feature.properties['altitude'],
      heading: feature.properties['heading'],
      ground: feature.properties['ground']
    },
    id: feature.properties['id'],
    reg: feature.properties['reg'],
    callsign: feature.properties['callsign'],
    aircraft_type: feature.properties['aircraft_type'],
    aircraft_desc: feature.properties['aircraft_desc'],
    operator: feature.properties['operator'],
    origin: feature.properties['origin'],
    origin_name: feature.properties['origin_name'],
    destination: feature.properties['destination'],
    destination_name: feature.properties['destination'],
    takeoff_time: {
      t: feature.properties['takeoff_time.t'] ?? feature.properties['takeoff_time_t'],
      tz: feature.properties['takeoff_time.tz'] ?? feature.properties['takeoff_time_tz'],
      offset: feature.properties['takeoff_time.offset'] ?? feature.properties['takeoff_time_offset']
    },
    landing_time: {
      t: feature.properties['landing_time.t'] ?? feature.properties['landing_time_t'],
      tz: feature.properties['landing_time.tz'] ?? feature.properties['landing_time_tz'],
      offset: feature.properties['landing_time.offset'] ?? feature.properties['landing_time_offset']
    },
    estimated_departure_time: {
      t: feature.properties['estimated_departure_time.t'] ?? feature.properties['estimated_departure_time_t'],
      tz: feature.properties['estimated_departure_time.tz'] ?? feature.properties['estimated_departure_time_tz'],
      offset: feature.properties['estimated_departure_time.offset'] ?? feature.properties['estimated_departure_time_offset']
    },
    estimated_arrival_time: {
      t: feature.properties['estimated_arrival_time.t'] ?? feature.properties['estimated_arrival_time_t'],
      tz: feature.properties['estimated_arrival_time.tz'] ?? feature.properties['estimated_arrival_time_tz'],
      offset: feature.properties['estimated_arrival_time.offset'] ?? feature.properties['estimated_arrival_time_offset']
    },
    point: packPoint(point.coordinates[1], point.coordinates[0]),
    //TODO: remove these fields after Pilot Web is updated
    lat: feature.properties['lat'],
    lon: feature.properties['lon'],
    speed: feature.properties['speed'],
    altitude: feature.properties['altitude'],
    heading: feature.properties['heading'],
    ground: feature.properties['ground']
  };
  return internetTrafficInfo;
};
const packNavaid = feature => {
  const point = feature.geometry;
  return {
    identifier: feature.properties['identifier'],
    name: feature.properties['name'],
    type: feature.properties['type'],
    point: packPoint(point.coordinates[1], point.coordinates[0]),
    is_on_rnav: feature.properties['is_on_rnav'],
    frequency: feature.properties['frequency'],
    magVariance: feature.properties['mag_var']
  };
};
const packNotam = feature => {
  const notam = {
    class: feature.properties['notamClass'],
    subClass: feature.properties['notamSubClass'],
    status: feature.properties['notamStatus'],
    identifier: feature.properties['notamIdent'],
    text: feature.properties['notamText'],
    icaoNotamLocation: feature.properties['icaoNotamLocation'],
    notamLocation: feature.properties['notamLocation'],
    effectiveTime: feature.properties['effectiveTime'],
    expireTime: feature.properties['expireTime'],
    id: feature.properties['notamId'],
    altitudeMaximum: feature.properties['TimeAltitudeRange.MaxAltitude.altitude'],
    altitudeMaximumMSL: feature.properties['TimeAltitudeRange.MaxAltitude.isMSL'],
    altitudeMinimum: feature.properties['TimeAltitudeRange.MinAltitude.altitude'],
    altitudeMinimumMSL: feature.properties['TimeAltitudeRange.MinAltitude.isMSL']
  };
  if (feature.geometry.type === 'Point') {
    notam.point = packPoint(feature.geometry.coordinates[1], feature.geometry.coordinates[0]);
  } else {
    notam.bounds = packBounds(feature.properties['featureMinLat'], feature.properties['featureMinLon'], feature.properties['featureMaxLat'], feature.properties['featureMaxLon']);
  }
  return notam;
};
const packObstacle = feature => {
  const point = feature.geometry;
  return {
    id: feature.id,
    altitude: feature.properties['mslPlusAgl'],
    height: feature.properties['agl'],
    tall: !['LightedShort', 'UnlightedShort'].includes(feature.properties['obstacleIcon']),
    lighted: !['UnlightedShort', 'UnlightedTall'].includes(feature.properties['obstacleIcon']),
    point: packPoint(point.coordinates[1], point.coordinates[0])
  };
};
const packPirep = feature => {
  // Locations
  const locations = JSON.parse(feature.properties.PirepLocationList);
  const dir = locations[0].direction;
  const dist = locations[0].distance;
  const pirepInfo = {
    icon: feature.properties['icon'],
    urgency: feature.properties['isUrgent'],
    location: locations[0].station,
    locationDir: dir,
    locationDist: dist,
    time: feature.properties['time'],
    altitude: feature.properties['altitude'],
    aircraft: feature.properties['airCraftType'],
    sky: feature.properties['skyCover'],
    weather: feature.properties['weather'],
    temperature: feature.properties['temperature'],
    windDir: feature.properties['windDir'],
    windSpeed: feature.properties['windSpeed'],
    turbulence: feature.properties['turbulence'],
    icing: feature.properties['icing'],
    remarks: feature.properties['remarks'],
    code: feature.properties['rawReport']
  };
  if (feature.geometry.type === 'Point') {
    pirepInfo.point = packPoint(feature.geometry.coordinates[1], feature.geometry.coordinates[0]);
  } else {
    pirepInfo.bounds = packBounds(feature.properties['featureMinLat'], feature.properties['featureMinLon'], feature.properties['featureMaxLat'], feature.properties['featureMaxLon']);
  }
  return pirepInfo;
};
const packStormCell = feature => {
  return {
    id: feature.id,
    time: feature.properties['issueTime'],
    speed: feature.properties['CellTrackingInfo.speed'],
    direction: feature.properties['CellTrackingInfo.dir'],
    hail: feature.properties['HailIndex.probHail'],
    tornadic: feature.properties['isTornadic'],
    echoTop: feature.properties['EchoTop.echoTopFeetMSL']
  };
};
const packTfr = feature => {
  const tfrInfo = {
    id: feature.properties['tfr_ident'],
    end_time: feature.properties['end_time'],
    ident: feature.properties['ident'],
    notam_ident: feature.properties['notam_ident'],
    report_text: feature.properties['report_text'],
    restriction_type: feature.properties['restriction_type'],
    start_time: feature.properties['start_time'],
    stadium_display_location: feature.properties['stadium_display_location'],
    stadium_name: feature.properties['stadium_name'],
    stadium_event_title: feature.properties['stadium_event_title'],
    stadium_event_short_title: feature.properties['stadium_event_short_title'],
    stadium_event_type: feature.properties['stadium_event_type'],
    stadium_event_estimated_start_time: feature.properties['stadium_event_estimated_start_time'],
    restriction_subtype: feature.properties['restriction_subtype'],
    minAltitude: feature.properties['minAltitude'],
    min_is_msl: feature.properties['min_is_msl'],
    maxAltitude: feature.properties['maxAltitude'],
    max_is_msl: feature.properties['max_is_msl']
  };
  if (feature.geometry.type === 'Point') {
    tfrInfo.point = packPoint(feature.geometry.coordinates[1], feature.geometry.coordinates[0]);
  } else {
    tfrInfo.bounds = packBounds(feature.properties['featureMinLat'], feature.properties['featureMinLon'], feature.properties['featureMaxLat'], feature.properties['featureMaxLon']);
  }
  return tfrInfo;
};
const packUserWaypoint = feature => {
  let point = undefined;
  if (feature.geometry.type === 'Point') {
    point = packPoint(feature.geometry.coordinates[1], feature.geometry.coordinates[0]);
  }
  return {
    exists: true,
    point,
    name: feature.properties['name'],
    data: feature.properties['data']
  };
};
const packBounds = (minLat, minLon, maxLat, maxLon) => {
  if (minLat != null && minLon != null && maxLat != null && maxLon != null) {
    return {
      sw: {
        latitude: minLat,
        longitude: minLon
      },
      ne: {
        latitude: maxLat,
        longitude: maxLon
      }
    };
  }
  console.error('packing a feature with invalid bounds');
  return {
    sw: {
      latitude: 0,
      longitude: 0
    },
    ne: {
      latitude: 0,
      longitude: 0
    }
  };
};
const packPoint = (lat, lon) => {
  if (lat != null && lon != null) {
    return {
      latitude: lat,
      longitude: lon
    };
  }
  console.error('packing a feature with invalid point');
  return {
    latitude: 0,
    longitude: 0
  };
};
const packFeatures = (featureType, features, currentPosition, notams, decimalPipe) => {
  switch (featureType) {
    case FeatureType$1.AirmetsSigmets:
      return {
        type: featureType,
        info: features.map(packAirmetSigmet)
      };
    case FeatureType$1.Airspaces:
      return {
        type: featureType,
        info: features.map(feature => packAirspace(feature, currentPosition, notams, decimalPipe))
      };
    case FeatureType$1.Airports:
      return {
        type: featureType,
        info: features.map(packAirport)
      };
    case FeatureType$1.Airways:
      return {
        type: featureType,
        info: features.map(packAirway)
      };
    case FeatureType$1.Fuel:
      return {
        type: featureType,
        info: features.map(packFuel)
      };
    case FeatureType$1.InternetTraffic:
      return {
        type: featureType,
        info: features.map(packInternetTraffic)
      };
    case FeatureType$1.NAVAIDs_NDB:
    case FeatureType$1.NAVAIDs_VOR:
    case FeatureType$1.NAVAIDs_WPT:
      return {
        type: featureType,
        info: features.map(packNavaid)
      };
    case FeatureType$1.NOTAMs:
      return {
        type: featureType,
        info: features.map(packNotam)
      };
    case FeatureType$1.Obstacles:
      return {
        type: featureType,
        info: features.map(packObstacle)
      };
    case FeatureType$1.PIREPs:
      return {
        type: featureType,
        info: features.map(packPirep)
      };
    case FeatureType$1.StormCells:
      return {
        type: featureType,
        info: features.map(packStormCell)
      };
    case FeatureType$1.TFRs:
      return {
        type: featureType,
        info: features.map(packTfr)
      };
    case FeatureType$1.UserWaypoints:
      if (features.length >= 1) {
        return {
          type: featureType,
          info: features.map(packUserWaypoint)
        };
      } else if (currentPosition != null) {
        return {
          type: featureType,
          info: [{
            exists: false,
            point: packPoint(currentPosition.lat, currentPosition.lng)
          }]
        };
      } else {
        return {
          type: featureType,
          info: []
        };
      }
      break;
    case FeatureType$1.WeatherReport:
      return {
        type: featureType,
        info: features.map(feature => packMetar(feature, currentPosition))
      };
    default:
      throw new Error(`Unrecognized FeatureType: ${featureType}`);
  }
};
const HoverFeaturePriority = [FeatureType$1.InternetTraffic, FeatureType$1.Airports, FeatureType$1.Fuel, FeatureType$1.WeatherReport, FeatureType$1.PIREPs, FeatureType$1.NAVAIDs_VOR, FeatureType$1.NAVAIDs_NDB, FeatureType$1.NAVAIDs_WPT, FeatureType$1.Obstacles, FeatureType$1.NOTAMs];
const ClickFeaturePriority = [FeatureType$1.InternetTraffic, FeatureType$1.Airports, FeatureType$1.Fuel, FeatureType$1.WeatherReport, FeatureType$1.PIREPs, FeatureType$1.NAVAIDs_VOR, FeatureType$1.NAVAIDs_NDB, FeatureType$1.NAVAIDs_WPT, FeatureType$1.Obstacles];
// Everything that's not a point target that would be clicked on
const ClickContextPriority = [FeatureType$1.AirmetsSigmets, FeatureType$1.Airspaces, FeatureType$1.Airways, FeatureType$1.NOTAMs, FeatureType$1.StormCells, FeatureType$1.TFRs, FeatureType$1.MoreInfo];
// In the event of mutual dependencies, you should only need to define those later in the priority list (or not in it),
// since higher priority items would already have been searched for.
const FeatureDependencies = {
  [FeatureType$1.AirmetsSigmets]: [],
  [FeatureType$1.Airports]: [FeatureType$1.Fuel, FeatureType$1.WeatherReport],
  [FeatureType$1.Airspaces]: [],
  [FeatureType$1.Airways]: [],
  [FeatureType$1.Fuel]: [FeatureType$1.WeatherReport],
  [FeatureType$1.InternetTraffic]: [],
  [FeatureType$1.NAVAIDs_NDB]: [FeatureType$1.NOTAMs],
  [FeatureType$1.NAVAIDs_VOR]: [FeatureType$1.NOTAMs],
  [FeatureType$1.NAVAIDs_WPT]: [FeatureType$1.NOTAMs],
  [FeatureType$1.NOTAMs]: [],
  [FeatureType$1.Obstacles]: [FeatureType$1.NOTAMs],
  [FeatureType$1.PIREPs]: [],
  [FeatureType$1.StormCells]: [],
  [FeatureType$1.TFRs]: [],
  [FeatureType$1.WeatherReport]: [],
  [FeatureType$1.UserWaypoints]: [],
  [FeatureType$1.MoreInfo]: []
};
/**
 * This service is responsible for handling direct clicks on the map,
 * searching the area around the click for map features, and populating
 * the search API with those features.
 */
class MapInteractionHandler {
  constructor(mapService) {
    this.mapService = mapService;
    this.destroyRef = inject(DestroyRef);
    this.notamRunwayService = inject(NotamRunwayService);
    this.radialMenuMapService = inject(RadialMenuMapService);
    this._locale = inject(LOCALE_ID);
    this.hoverEvents = new Subject();
    this.contextEvents = new Subject();
    this.clickEvents = new Subject();
    this.dragStartEvents = new Subject();
    this.dragEndEvents = new Subject();
    this.zoomStartEvents = new Subject();
    this.zoomEndEvents = new Subject();
    this.lastHoveredFeatureMap = null;
    this.activeHoverMove$ = null;
    this.trgb = new TerrainRGB();
    this.decimal = new DecimalPipe(this._locale);
    this.hoverButtonDefs = [];
    this.buttonDefs = [];
    this.gatherNotams = features => {
      // NOTAMs can come from many different layers. This turns them into actual usable NOTAM features.
      const notams = [];
      for (const feature of features) {
        let notam;
        // Runway NOTAMs
        if (this.notamRunwayService.isRunwayNotam(feature)) {
          notam = this.notamRunwayService.getNotam(feature);
        }
        // normal NOTAMs
        if (notam == null) {
          notam = feature;
        }
        notams.push(notam);
      }
      return notams;
    };
    this.filterNotams = features => {
      return features.filter(notam => [0, 1, 5].includes(notam.properties['notamClass']));
    };
    effect(() => {
      const content = this.mapService.info.content.hillshade.content()[0];
      if (content != null) {
        this.trgb.configure(`${content.urlBase}/{z}/{x}/{y}.webp`, 1, 10);
      }
    });
    effect(() => {
      if (mapService.map() != null) {
        this.registerEventHandlers();
        this.hoverButtonDefs = dynamicDefs(this.mapService.moduleConfig.mapAssetsPath, null, null, this.mapService.icon, this.radialMenuMapService);
        this.buttonDefs = dynamicDefs(this.mapService.moduleConfig.mapAssetsPath, null, null, this.mapService.icon, this.radialMenuMapService);
      }
    });
    this.radialMenuMapService.radialMenuWatcher$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.updateButtonLayerIds();
      },
      error: error => {
        console.error(error);
      }
    });
  }
  dispatchEvent(e) {
    this.mapService.map()?.dispatchEvent(e);
  }
  updateButtonLayerIds() {
    this.hoverButtonDefs.forEach(def => {
      def.layer_ids = def.feature_type == null ? [] : this.radialMenuMapService.getLayerDataByFeature(def.feature_type);
      // We exclude some specific layers for hover to avoid clutter
      if (def.feature_type === FeatureType$1.Airports) {
        def.layer_ids = def.layer_ids.filter(id => !id.includes('safetaxi') && !id.includes('safe-taxi') && !id.includes('runway-fill') && !id.includes('runway-line') && !id.includes('runway-notam') && !id.includes('runway-symbol'));
      } else if (def.feature_type === FeatureType$1.NAVAIDs_VOR) {
        def.layer_ids = def.layer_ids.filter(id => id !== 'navaids-vor-compass');
      }
    });
    this.buttonDefs.forEach(def => {
      def.layer_ids = def.feature_type == null ? [] : this.radialMenuMapService.getLayerDataByFeature(def.feature_type);
      if (def.feature_type === FeatureType$1.NAVAIDs_VOR) {
        def.layer_ids = def.layer_ids.filter(id => id !== 'navaids-vor-compass');
      }
    });
  }
  registerEventHandlers() {
    const map = this.mapService.map();
    if (map != null) {
      map.on('mousemove', event => {
        const features = map.queryRenderedFeatures(event.point);
        const newFeatureMap = this.sortFeatures(HoverFeaturePriority, features, this.hoverButtonDefs, true);
        if (newFeatureMap.size > 0 && (this.lastHoveredFeatureMap == null || !this.featuresMatch(this.lastHoveredFeatureMap, newFeatureMap))) {
          this.lastHoveredFeatureMap = newFeatureMap;
          this.activeHoverMove$ = new BehaviorSubject(event.point);
          this.hoverEvents.next({
            mapPoint: packPoint(event.lngLat.lat, event.lngLat.lng),
            point: event.point,
            moveEvents: this.activeHoverMove$,
            info: this.convertFeaturesToEventData(newFeatureMap, HoverFeaturePriority, event.lngLat, 'hover')
          });
        } else if (this.lastHoveredFeatureMap != null && newFeatureMap.size > 0) {
          this.activeHoverMove$?.next(event.point);
        } else if (this.lastHoveredFeatureMap != null) {
          this.activeHoverMove$?.complete();
          this.activeHoverMove$ = null;
          this.hoverEvents.next(null);
          this.lastHoveredFeatureMap = null;
        }
      });
      map.registerMapClickHandler(event => this.clickHandler(map, event));
      const genericHandler = (event, emitter) => {
        if (event.originalEvent != null) {
          // TouchEvent is undefined on desktop-based Firefox browsers.
          // Check if window.TouchEvent is defined before checking instanceof.
          if (window.TouchEvent != null && event.originalEvent instanceof TouchEvent) {
            if (event.originalEvent.touches.length > 0) {
              emitter.next({
                point: {
                  x: event.originalEvent.touches[0].clientX,
                  y: event.originalEvent.touches[0].clientY
                },
                info: []
              });
            }
            return;
          }
          if (window.MouseEvent != null && event.originalEvent instanceof MouseEvent || window.WheelEvent != null && event.originalEvent instanceof WheelEvent) {
            emitter.next({
              point: {
                x: event.originalEvent.clientX,
                y: event.originalEvent.clientY
              },
              info: []
            });
          }
        }
      };
      map.on('dragstart', event => genericHandler(event, this.dragStartEvents));
      map.on('dragend', event => genericHandler(event, this.dragEndEvents));
      map.on('zoomstart', event => genericHandler(event, this.zoomStartEvents));
      map.on('zoomend', event => genericHandler(event, this.zoomEndEvents));
    }
  }
  clickHandler(map, event) {
    const features = map.queryRenderedFeatures(event.point);
    const newFeatureMap = this.sortFeatures(ClickFeaturePriority, features, this.buttonDefs, true);
    if (newFeatureMap.size > 0) {
      this.clickEvents.next({
        mapPoint: packPoint(event.lngLat.lat, event.lngLat.lng),
        point: event.point,
        info: this.convertFeaturesToEventData(newFeatureMap, ClickFeaturePriority, event.lngLat, 'click')
      });
    } else {
      const contextFeatureMap = this.sortFeatures(ClickContextPriority, features, this.buttonDefs, false);
      from(this.trgb.getElevation(event.lngLat, map)).pipe(catchError(err => {
        console.error('terrain error', err);
        return of(null);
      }), takeUntilDestroyed(this.destroyRef)).subscribe({
        next: elevation => {
          this.contextEvents.next({
            mapPoint: packPoint(event.lngLat.lat, event.lngLat.lng),
            point: event.point,
            info: this.convertFeaturesToEventData(contextFeatureMap, ClickContextPriority, event.lngLat, 'click'),
            elevation: elevation
          });
        },
        error: error => {
          console.error(error);
        }
      });
    }
  }
  convertFeaturesToEventData(featureMap, priority, lngLat, eventType) {
    const notamDef = this.hoverButtonDefs.find(d => d.feature_type === FeatureType$1.NOTAMs);
    return [...featureMap.entries()].sort(([def, _features], [def2, _features2]) => {
      const defIndex = priority.findIndex(d => d === def.feature_type);
      const defIndex2 = priority.findIndex(d => d === def2.feature_type);
      return defIndex < 0 ? defIndex2 : defIndex2 < 0 ? defIndex : defIndex - defIndex2;
    }).map(([def, featureList]) => {
      const gatheredList = def.feature_type === FeatureType$1.NOTAMs ? this.gatherNotams(featureList) : featureList;
      const filteredList = def.feature_type === FeatureType$1.NOTAMs && eventType === 'hover' ? this.filterNotams(gatheredList) : gatheredList;
      return packFeatures(def.feature_type ?? FeatureType$1.MoreInfo, filteredList, lngLat, notamDef != null ? featureMap.get(notamDef) ?? [] : [], this.decimal);
    });
  }
  sortFeatures(priority, features, buttonDefs, findFirst = false) {
    const eventData = new Map();
    for (const featureType of priority) {
      //TODO: feature->def lookup?
      const def = buttonDefs.find(d => d.feature_type === featureType);
      if (def != null && !eventData.has(def)) {
        const matched_features = this.filterFeatures(features, def);
        if (matched_features.length > 0) {
          eventData.set(def, matched_features);
          FeatureDependencies[featureType].map(dep => {
            const dependendDef = buttonDefs.find(d => d.feature_type === dep);
            if (dependendDef != null) {
              eventData.set(dependendDef, this.filterFeatures(features, dependendDef));
            }
          });
          if (findFirst) {
            break;
          }
        }
      }
    }
    return eventData;
  }
  filterFeatures(features, def) {
    // this button only cares about features that its definition specifies
    let matched_features = features.filter(f => def.layer_ids?.includes(f.layer.id)).filter(f => def.layer_sub_id == null || f.properties[def.layer_sub_id.key] === def.layer_sub_id.value);
    if (def.filter_fn != null) {
      matched_features = def.filter_fn(matched_features);
    }
    if (matched_features != null) {
      // dedup
      const ids = [];
      matched_features = matched_features.filter(f => {
        const id = JSON.stringify(def.use_feature_id === true ? f.id : def.prop_id !== undefined ? f.properties[def.prop_id] : f.properties);
        if (!ids.includes(id)) {
          ids.push(id);
          return true;
        } else {
          return false;
        }
      });
      // sort
      if (def.sort_fn != null) {
        matched_features.sort(def.sort_fn);
      }
    }
    return matched_features;
  }
  featuresMatch(oldFeatures, newFeatures) {
    if (oldFeatures.size !== newFeatures.size) {
      return false;
    }
    for (const def of this.hoverButtonDefs) {
      const oldList = oldFeatures.get(def);
      const newList = newFeatures.get(def);
      if (oldList?.length !== newList?.length) {
        return false;
      }
      for (let i = 0; i < (oldList?.length ?? 0); i++) {
        if (def.use_feature_id === true) {
          const ids = oldList.map(f => f.id);
          const new_ids = newList.map(f => f.id);
          if (JSON.stringify(ids) !== JSON.stringify(new_ids)) {
            return false;
          }
        }
        // use the property id to match against new features
        else if (def.prop_id != null) {
          const ids = oldList.map(f => f.properties[def.prop_id]);
          const new_ids = newList.map(f => f.properties[def.prop_id]);
          if (JSON.stringify(ids) !== JSON.stringify(new_ids)) {
            return false;
          }
        }
        // use all properties to match against new features
        else {
          const old = oldList.some(f => newList.every(m => JSON.stringify(f.properties) !== JSON.stringify(m.properties)));
          if (old) {
            return false;
          }
        }
      }
    }
    return true;
  }
}
var MapPreset = /*#__PURE__*/function (MapPreset) {
  MapPreset["Blank"] = "Blank";
  MapPreset["VFR"] = "VFR";
  MapPreset["IFR"] = "IFR";
  MapPreset["Satellite"] = "Satellite";
  MapPreset["OpenStreetMap"] = "OpenStreetMap";
  return MapPreset;
}(MapPreset || {});
const BlankPreset = {
  charts: {
    groups: [],
    visible: false
  },
  content: {
    background: {
      visible: false
    },
    baseMap: {
      visible: false,
      cityNames: false,
      countryBorders: false,
      landUse: false,
      parks: false,
      railTracks: false,
      smallCityMinZoom: ZoomLevel.Off,
      smallTownMinZoom: ZoomLevel.Off,
      stateBorders: false,
      roads: false,
      waterBodies: false,
      waterWays: false
    },
    groundSatellite: {
      visible: false
    },
    hillshade: {
      visible: false
    },
    openStreetMap: {
      visible: false
    },
    safeTaxi: {
      visible: false
    },
    topography: {
      visible: false
    }
  },
  highlight: {
    location: {
      visible: true
    }
  }
};
const blankPresetIsAvailable = _mapService => true;
const IfrPreset = {
  charts: {
    groups: [],
    visible: false
  },
  content: {
    background: {
      visible: true
    },
    baseMap: {
      visible: true,
      cityNames: true,
      countryBorders: true,
      landUse: false,
      parks: false,
      railTracks: false,
      smallCityMinZoom: ZoomLevel.Off,
      smallTownMinZoom: ZoomLevel.Off,
      stateBorders: false,
      roads: false,
      waterBodies: true,
      waterWays: true
    },
    groundSatellite: {
      visible: false
    },
    hillshade: {
      visible: false // controlled by settings.ifrTopographyState
    },
    openStreetMap: {
      visible: false
    },
    safeTaxi: {
      visible: true
    },
    topography: {
      visible: false // controlled by settings.ifrTopographyState
    }
  },
  highlight: {
    location: {
      visible: true
    }
  }
};
const ifrPresetIsAvailable = mapService => {
  return mapService.info.content.airport.available() || mapService.info.content.airspace.available() || mapService.info.content.airway.available() || mapService.info.content.baseMap.available() || mapService.info.content.navaid.available() || mapService.info.content.safeTaxi.available();
};
const OpenStreetMapPreset = {
  charts: {
    groups: [],
    visible: false
  },
  content: {
    background: {
      visible: true
    },
    baseMap: {
      visible: false,
      cityNames: false,
      countryBorders: false,
      landUse: false,
      parks: false,
      railTracks: false,
      roads: false,
      smallCityMinZoom: ZoomLevel.Off,
      smallTownMinZoom: ZoomLevel.Off,
      stateBorders: false,
      waterBodies: false,
      waterWays: false
    },
    groundSatellite: {
      visible: false
    },
    hillshade: {
      visible: false
    },
    openStreetMap: {
      visible: true
    },
    safeTaxi: {
      visible: true
    },
    topography: {
      visible: false
    }
  },
  highlight: {
    location: {
      visible: true
    }
  }
};
const openStreetMapPresetIsAvailable = mapService => {
  return mapService.info.content.airport.available() || mapService.info.content.airspace.available() || mapService.info.content.navaid.available() || mapService.info.content.safeTaxi.available();
};
const SatellitePreset = {
  charts: {
    groups: [],
    visible: false
  },
  content: {
    background: {
      visible: true
    },
    baseMap: {
      visible: true,
      cityNames: false,
      countryBorders: true,
      landUse: false,
      parks: false,
      railTracks: false,
      roads: false,
      smallCityMinZoom: ZoomLevel.Off,
      smallTownMinZoom: ZoomLevel.Off,
      stateBorders: true,
      waterBodies: false,
      waterWays: false
    },
    groundSatellite: {
      visible: true
    },
    hillshade: {
      visible: false
    },
    openStreetMap: {
      visible: false
    },
    safeTaxi: {
      visible: true
    },
    topography: {
      visible: false
    }
  },
  highlight: {
    location: {
      visible: true
    }
  }
};
const satellitePresetIsAvailable = mapService => {
  return mapService.info.content.airport.available() || mapService.info.content.airspace.available() || mapService.info.content.groundSatellite.available() || mapService.info.content.navaid.available() || mapService.info.content.safeTaxi.available();
};
const VfrPreset = {
  charts: {
    groups: [],
    visible: false
  },
  content: {
    background: {
      visible: true
    },
    baseMap: {
      visible: true,
      cityNames: true,
      countryBorders: true,
      landUse: true,
      parks: false,
      railTracks: true,
      smallCityMinZoom: ZoomLevel['0.5NM'],
      smallTownMinZoom: ZoomLevel['0.2NM'],
      stateBorders: true,
      roads: true,
      waterBodies: true,
      waterWays: true
    },
    groundSatellite: {
      visible: false
    },
    hillshade: {
      visible: false // controlled by settings.vfrTopographyState
    },
    openStreetMap: {
      visible: false
    },
    safeTaxi: {
      visible: true
    },
    topography: {
      visible: false // controlled by settings.vfrTopographyState
    }
  },
  highlight: {
    location: {
      visible: true
    }
  }
};
const vfrPresetIsAvailable = mapService => {
  return mapService.info.content.airport.available() || mapService.info.content.airspace.available() || mapService.info.content.baseMap.available() || mapService.info.content.hillshade.available() || mapService.info.content.safeTaxi.available() || mapService.info.content.topography.available();
};
class MapPresetHandler {
  constructor(mapService) {
    this.mapService = mapService;
  }
  apply(preset) {
    const config = this.get(preset);
    this.mapService.config.restore(config);
  }
  get(preset) {
    let config;
    switch (preset) {
      case MapPreset.Blank:
        config = BlankPreset;
        break;
      case MapPreset.VFR:
        config = VfrPreset;
        break;
      case MapPreset.IFR:
        config = IfrPreset;
        break;
      case MapPreset.Satellite:
        config = SatellitePreset;
        break;
      case MapPreset.OpenStreetMap:
        config = OpenStreetMapPreset;
        break;
      default:
        config = VfrPreset;
        break;
    }
    return cloneDeep(config);
  }
  isAvailable(preset) {
    switch (preset) {
      case MapPreset.Blank:
        return blankPresetIsAvailable(this.mapService);
      case MapPreset.VFR:
        return vfrPresetIsAvailable(this.mapService);
      case MapPreset.IFR:
        return ifrPresetIsAvailable(this.mapService);
      case MapPreset.Satellite:
        return satellitePresetIsAvailable(this.mapService);
      case MapPreset.OpenStreetMap:
        return openStreetMapPresetIsAvailable(this.mapService);
      default:
        return false;
    }
  }
}
class MapSearch {
  constructor(mapService) {
    this.mapService = mapService;
    this.airmetsSigmets = signal([]);
    this.airports = signal([]);
    this.airspaces = signal([]);
    this.airways = signal([]);
    this.fuelPrices = signal([]);
    this.internetTraffic = signal([]);
    this.metars = signal([]);
    this.ndbs = signal([]);
    this.notams = signal([]);
    this.obstacles = signal([]);
    this.pireps = signal([]);
    this.stormCells = signal([]);
    this.tfrs = signal([]);
    this.vors = signal([]);
    this.weatherReports = signal([]);
    this.wpts = signal([]);
    this.enableEditUserWaypoints = signal(false);
    this.userWaypoints = signal([]);
  }
  changeTopElementCount(val) {
    const map = this.mapService.map();
    if (map != null) {
      map.topElementCount += val;
    }
  }
  clearSearch() {
    this.airmetsSigmets.set([]);
    this.airports.set([]);
    this.airspaces.set([]);
    this.airways.set([]);
    this.fuelPrices.set([]);
    this.internetTraffic.set([]);
    this.ndbs.set([]);
    this.notams.set([]);
    this.obstacles.set([]);
    this.pireps.set([]);
    this.stormCells.set([]);
    this.tfrs.set([]);
    this.vors.set([]);
    this.weatherReports.set([]);
    this.wpts.set([]);
    this.userWaypoints.set([]);
  }
}

/**
 * This is the internal service for the API. To avoid circular dependencies, any internal code
 * should be referencing this service instead of AvcMapsDisplayService.
 */
let MapService = /*#__PURE__*/(() => {
  class MapService {
    constructor() {
      this.mapTracker = inject(MapTracker);
      // Wondering why "this" is being passed into child objects? Something in MapService
      // that tries to inject MapService is creating a circular dependency. Angular can't
      // resolve these and just throws a runtime error. This is a consequence of trying
      // to cram absolutely everything into a single service for the Map API.
      this.animation = inject(MapAnimationHandler);
      this.config = inject(MapConfigHandler);
      this.data = inject(MapDataHandler);
      this.debug = new MapDebug(this);
      this.highlight = inject(MapHighlight);
      this.icon = inject(MapIconHandler);
      this.info = inject(MapInfo);
      this.interactions = new MapInteractionHandler(this);
      this.preset = new MapPresetHandler(this);
      this.search = new MapSearch(this);
      this.settings = inject(MapSettingsHandler);
      this.state = inject(MapStateHandler);
      this.theme = inject(MapThemeHandler);
      this.map = this.mapTracker.map;
      this.moduleConfig = inject(AVC_MAPS_DISPLAY_CONFIG);
      this.addCredentialsFilter = url => {
        return this.moduleConfig.capsUrl != null && url.startsWith(this.moduleConfig.capsUrl) || this.moduleConfig.chartsUrl != null && url.startsWith(this.moduleConfig.chartsUrl);
      };
      this.isLoggedIn = inject(OptionalAuthService).loggedIn;
      this.localStorageService = inject(LocalStorageService);
      this.updateUrlTransform = () => {
        const isLoggedIn = this.isLoggedIn(); // refetch when auth state changes
        const map = this.map();
        if (map != null) {
          map.setTransformRequest(this.createTransform(isLoggedIn));
        }
      };
      effect(this.updateUrlTransform);
    }
    ngOnDestroy() {
      this.map()?.remove();
    }
    attachMap(container) {
      this.mapTracker.attachMap(container);
    }
    createTransform(isLoggedIn) {
      let oAuthResponse = null;
      if (isLoggedIn) {
        oAuthResponse = this.localStorageService.get(LocalStorageKey.OAuthTokenResponse);
      }
      return url => {
        if (this.addCredentialsFilter(url)) {
          const headers = {
            ['X-Garmin-Client-Id']: this.moduleConfig.clientId
          };
          if (oAuthResponse?.customerId != null) {
            headers['X-Garmin-Actor-Id'] = oAuthResponse.customerId;
            headers['X-Garmin-Customer-Id'] = oAuthResponse.customerId;
          }
          if (oAuthResponse?.accessToken != null) {
            headers['Authorization'] = `Bearer ${oAuthResponse.accessToken}`;
          }
          return {
            url,
            credentials: 'include',
            headers
          };
        }
        return {
          url
        };
      };
    }
    static {
      this.ɵfac = function MapService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapService,
        factory: MapService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class ChartLayer {
  constructor(map, id, url) {
    this.sourceId = map.sourceIds.next(`${id}`);
    this.layerId = map.layerIds.next(`${id}`);
    this.addSource(map, url);
    this.addLayer(map, url);
  }
  remove(map) {
    this.removeLayer(map);
    this.removeSource(map);
  }
  addSource(map, url) {
    map.addSource(this.sourceId, {
      type: 'raster',
      bounds: url.bounds,
      maxzoom: url.maxZoom,
      minzoom: url.minZoom,
      tiles: [`${url.url}/{z}/{x}/{y}.png`],
      scheme: 'tms',
      tileSize: 256
    });
  }
  addLayer(map, url) {
    map.addLayer(LayerPriority.Charts, {
      id: this.layerId,
      source: this.sourceId,
      minzoom: url.minZoom,
      type: 'raster'
    });
  }
  removeSource(map) {
    map.removeSource(this.sourceId);
  }
  removeLayer(map) {
    map.removeLayer(this.layerId);
  }
}
const createKey = (group, metadata, chart) => {
  return `${group.id}--${metadata.id}--${chart.name}`;
};
const forEachVisibleChart = (config, callbackfn) => {
  for (const group of config) {
    if (!group.visible) {
      continue;
    }
    for (const metadata of group.children) {
      if (!metadata.visible) {
        continue;
      }
      for (const chart of metadata.children) {
        if (!chart.visible) {
          continue;
        }
        callbackfn(group, metadata, chart);
      }
    }
  }
};
let ChartsService = /*#__PURE__*/(() => {
  class ChartsService {
    constructor() {
      this.layers = new Map();
      this.mapService = inject(MapService);
      effect(() => this.update());
    }
    update() {
      const map = this.mapService.map();
      const config = this.mapService.config.charts.groups();
      const allVisible = this.mapService.config.charts.visible();
      if (map != null) {
        this.addCharts(map, allVisible, config);
        this.removeCharts(map, allVisible, config);
        this.sortCharts(map, config);
      }
    }
    /**
     * Iterates through the specified chart groups to find charts that
     * should be visible and add them to the map as needed.
     */
    addCharts(map, allVisible, config) {
      if (!allVisible) {
        return;
      }
      forEachVisibleChart(config, (group, metadata, chart) => {
        this.addSingleChart(map, group, metadata, chart);
      });
    }
    /**
     * Adds the specified chart to the map if it's not already present
     */
    addSingleChart(map, group, metadata, chart) {
      const key = createKey(group, metadata, chart);
      if (this.layers.get(key) == null) {
        const urls = this.getUrls(group.id, metadata.id, chart.name);
        const layers = urls.map(url => new ChartLayer(map, key, url));
        this.layers.set(key, layers);
      }
    }
    /**
     * Returns the URL information associated with the specified chart
     */
    getUrls(groupId, metadataId, chartId) {
      const info = this.mapService.info.charts.groups();
      const results = [];
      for (const group of info.filter(x => x.id === groupId)) {
        for (const metadata of group.children.filter(x => x.id === metadataId)) {
          for (const chart of metadata.children.filter(x => x.name === chartId)) {
            results.push(chart);
          }
        }
      }
      return results;
    }
    /**
     * Iterates through the charts on the map, checks to see if they
     * should still be visible, and removes them from the map as needed.
     */
    removeCharts(map, allVisible, config) {
      for (const [key, layers] of this.layers) {
        let found = false;
        if (allVisible) {
          forEachVisibleChart(config, (group, metadata, chart) => {
            const searchKey = createKey(group, metadata, chart);
            if (key == searchKey) {
              found = true;
            }
          });
        }
        if (!found) {
          layers.forEach(layer => layer.remove(map));
          this.layers.delete(key);
        }
      }
    }
    /**
     * Iterates through the charts on the map and ensures they are conforming
     * to the expected layer order from the UI
     */
    sortCharts(map, config) {
      const order = [];
      forEachVisibleChart(config, (group, metadata, chart) => {
        const layers = this.layers.get(createKey(group, metadata, chart)) ?? [];
        for (const layer of layers) {
          order.push(layer.layerId);
        }
      });
      map.sortLayers(LayerPriority.Charts, order);
    }
    static {
      this.ɵfac = function ChartsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ChartsService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ChartsService,
        factory: ChartsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ChartsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Base class of a SVG element. Subclasses can extend this class to
 * dynamically create SVGs.
 */
let BaseSVG = /*#__PURE__*/(() => {
  class BaseSVG {
    static {
      this.svgNamespace = 'http://www.w3.org/2000/svg';
    }
    constructor() {
      this.svg = document.createElementNS(BaseSVG.svgNamespace, 'svg');
    }
    /**
     * Rasterize the SVG data.
     * @returns The rasterized SVG data.
     */
    rasterize() {
      const svgString = new XMLSerializer().serializeToString(this.svg);
      const svgBlob = new Blob([svgString], {
        type: 'image/svg+xml;charset=utf-8'
      });
      return window.URL.createObjectURL(svgBlob);
    }
  }
  return BaseSVG;
})();
class Rectangle extends BaseSVG {
  constructor(width, height, fillColor, cornerRadius = 0, strokeColor = 'black', strokeWidth = 0.5) {
    super();
    this.width = width;
    this.height = height;
    this.fillColor = fillColor;
    this.cornerRadius = cornerRadius;
    this.strokeColor = strokeColor;
    this.strokeWidth = strokeWidth;
    const totalWidth = width + strokeWidth * 2;
    const totalHeight = height + strokeWidth * 2;
    this.svg.setAttribute('width', `${totalWidth}`);
    this.svg.setAttribute('height', `${totalHeight}`);
    this.svg.setAttribute('viewPort', `0 0 ${totalWidth} ${totalHeight}`);
    const rect = document.createElementNS(BaseSVG.svgNamespace, 'rect');
    rect.setAttribute('x', `${strokeWidth}`);
    rect.setAttribute('y', `${strokeWidth}`);
    rect.setAttribute('width', `${width}`);
    rect.setAttribute('height', `${height}`);
    if (cornerRadius !== 0) {
      rect.setAttribute('rx', `${cornerRadius}`);
    }
    rect.setAttribute('fill', `${fillColor}`);
    rect.setAttribute('stroke', `${strokeColor}`);
    if (strokeWidth !== 1) {
      rect.setAttribute('stroke-width', `${strokeWidth}`);
    }
    this.svg.appendChild(rect);
  }
}
class ChartSearchableLayerService extends LayerService {
  updateLayers() {
    const changes = this.contentObserver.update(this.getContent(), this.getVisible(), this.getPresent());
    const opacity = this.getOpacity();
    for (const c of changes.hidden) {
      this.layers.get(c)?.hide();
      this.layers.get(c)?.update();
    }
    for (const c of changes.removed) {
      this.layers.get(c)?.remove();
      this.layers.delete(c);
    }
    for (const c of changes.added) {
      const layer = this.createLayer(c);
      this.layers.set(c, layer);
      layer.add(opacity);
    }
    for (const c of changes.shown) {
      this.layers.get(c)?.update();
      this.layers.get(c)?.setOpacity(opacity);
    }
    for (const c of changes.unchanged) {
      let layer = this.layers.get(c);
      if (this.getPresent()) {
        if (layer == null) {
          layer = this.createLayer(c);
          this.layers.set(c, layer);
          layer.add(opacity);
        } else if (!layer.present()) {
          layer.add(opacity);
        }
      }
      if (layer != null) {
        if (this.getVisible()) {
          if (!layer.visible()) {
            layer.show();
          }
          layer.update();
          layer.setOpacity(opacity);
        } else if (layer.visible()) {
          layer.hide();
        } else {
          layer.update();
          layer.setOpacity(opacity);
        }
      }
    }
  }
}
const RUNWAY_SHOW_LABELS_ZOOM = ZoomLevel['0.1NM'];
const RUNWAY_BILLBOARD_WIDTH = 20;
const RUNWAY_BILLBOARD_HEIGHT = 20;
const RUNWAY_BILLBOARD_RADIUS = 1;
const RUNWAY_BILLBOARD_COLOR = Color.Gray20.toString();
var RunwayPointType = /*#__PURE__*/function (RunwayPointType) {
  RunwayPointType[RunwayPointType["RunwayEnd"] = 0] = "RunwayEnd";
  RunwayPointType[RunwayPointType["RunwayNotamMarking"] = 1] = "RunwayNotamMarking";
  return RunwayPointType;
}(RunwayPointType || {});
var AirportSize = /*#__PURE__*/function (AirportSize) {
  AirportSize[AirportSize["Small"] = 0] = "Small";
  AirportSize[AirportSize["Medium"] = 1] = "Medium";
  AirportSize[AirportSize["Large"] = 2] = "Large";
  return AirportSize;
}(AirportSize || {});
var AirportDdcCategory = /*#__PURE__*/function (AirportDdcCategory) {
  AirportDdcCategory[AirportDdcCategory["Public"] = 0] = "Public";
  AirportDdcCategory[AirportDdcCategory["Military"] = 1] = "Military";
  AirportDdcCategory[AirportDdcCategory["Private"] = 2] = "Private";
  AirportDdcCategory[AirportDdcCategory["Joint"] = 3] = "Joint"; // Public && Military
  return AirportDdcCategory;
}(AirportDdcCategory || {});
var AirportDdcType = /*#__PURE__*/function (AirportDdcType) {
  AirportDdcType[AirportDdcType["Airport"] = 0] = "Airport";
  AirportDdcType[AirportDdcType["Heliport"] = 1] = "Heliport";
  AirportDdcType[AirportDdcType["Gliderport"] = 2] = "Gliderport";
  AirportDdcType[AirportDdcType["SeaplaneBase"] = 3] = "SeaplaneBase";
  AirportDdcType[AirportDdcType["UltralightFlightBase"] = 4] = "UltralightFlightBase";
  return AirportDdcType;
}(AirportDdcType || {});
class ChartSearchableLayer extends Layer {}
const resolveShouldBeArg = shouldBeArg => {
  if (shouldBeArg == null) {
    // everything should be visible/present unless specified otherwise
    return true;
  }
  if (typeof shouldBeArg === 'function') {
    return shouldBeArg();
  }
  return shouldBeArg;
};
/**
 * A layer group handles one or more sub-layer objects, providing
 * a single point-of-entry for the sub-layers as a group.
 */
class ChartSearchableLayerGroup extends ChartSearchableLayer {
  add(opacity) {
    const map = this.map();
    if (map != null) {
      const sourceId = this.addSource(map);
      for (const info of this.groupInfo) {
        const present = resolveShouldBeArg(info.shouldBePresent);
        if (present) {
          info.layer.add(map, sourceId);
          const visible = resolveShouldBeArg(info.shouldBeVisible);
          if (visible) {
            info.layer.show(map);
            info.layer.setOpacity(opacity ?? MAX_OPACITY);
          } else {
            info.layer.hide(map);
          }
        }
      }
    }
  }
  hide() {
    const map = this.map();
    if (map != null) {
      for (const info of this.groupInfo) {
        info.layer.hide(map);
      }
    }
  }
  remove() {
    const map = this.map();
    if (map != null) {
      for (const info of this.groupInfo) {
        info.layer.hide(map);
        info.layer.remove(map);
      }
      this.removeSource(map);
    }
  }
  show() {
    const map = this.map();
    if (map != null) {
      for (const info of this.groupInfo) {
        const visible = resolveShouldBeArg(info.shouldBeVisible);
        if (visible) {
          info.layer.show(map);
        }
      }
    }
  }
  update() {
    const map = this.map();
    if (map != null) {
      const sourceId = this.getSource(map);
      if (sourceId != null) {
        for (const info of this.groupInfo) {
          const present = resolveShouldBeArg(info.shouldBePresent);
          const visible = resolveShouldBeArg(info.shouldBeVisible);
          this.updateSubLayer(info.layer, present, visible, map, sourceId);
        }
      }
    }
  }
  present() {
    const map = this.map();
    if (map != null) {
      for (const info of this.groupInfo) {
        if (info.layer.isPresent(map)) {
          return true;
        }
      }
    }
    return false;
  }
  visible() {
    const map = this.map();
    if (map != null) {
      for (const info of this.groupInfo) {
        if (info.layer.isVisible(map)) {
          return true;
        }
      }
    }
    return false;
  }
  setOpacity(opacity) {
    for (const info of this.groupInfo) {
      const visible = resolveShouldBeArg(info.shouldBeVisible);
      if (visible) {
        info.layer.setOpacity(opacity);
      }
    }
  }
  updateSubLayer(subLayer, shouldBePresent, shouldBeVisible, map, sourceId) {
    const present = subLayer.isPresent(map);
    const visible = subLayer.isVisible(map);
    if (present && shouldBePresent) {
      if (visible && shouldBeVisible) {
        subLayer.update(map);
      } else if (shouldBeVisible) {
        subLayer.show(map);
      } else if (visible) {
        subLayer.hide(map);
      }
    } else if (shouldBePresent) {
      subLayer.add(map, sourceId);
      if (shouldBeVisible) {
        subLayer.show(map);
      } else {
        subLayer.hide(map);
      }
    } else if (present) {
      if (visible) {
        subLayer.hide(map);
      }
      subLayer.remove(map);
    }
  }
}
class ChartSearchableSubLayer {
  constructor(injector, radialMenuMapService, sourceLayerId, layerType) {
    this.injector = injector;
    this.radialMenuMapService = radialMenuMapService;
    this.sourceLayerId = sourceLayerId;
    this.layerType = layerType;
    this.layer = {
      radialLayerType: undefined
    };
    this.map = injector.get(MapTracker).map;
    this.mapConfig = injector.get(MapConfigHandler);
    this.mapHighlight = injector.get(MapHighlight);
    this.mapSettings = injector.get(MapSettingsHandler);
    this.mapTheme = injector.get(MapThemeHandler);
    this.layer.radialLayerType = layerType;
  }
  add(map, sourceId) {
    this.sourceId = sourceId;
    this.layerId = map.layerIds.next(this.LAYER_PREFIX);
    if (this.layer.radialLayerType != null) {
      this.radialMenuMapService.addToMap(this.layer.radialLayerType, this.layerId);
    }
  }
  hide(map) {
    if (this.layerId != null) {
      this.setOpacity(0);
      this.update(map);
    }
  }
  remove(map) {
    if (this.layerId != null) {
      map.removeLayer(this.layerId);
      if (this.layer.radialLayerType != null) {
        this.radialMenuMapService.removeFromMap(this.layer.radialLayerType, this.layerId);
      }
      this.layerId = undefined;
      this.sourceId = undefined;
    }
  }
  show(map) {
    if (this.layerId != null) {
      this.update(map);
    }
  }
  update(_map) {
    // NO-OP.
  }
  setOpacity(opacity) {
    trySetLayerOpacity(this.map(), this.layerId, opacity);
  }
  isPresent(map) {
    return map.hasLayer(this.layerId) ?? false;
  }
  isVisible(map) {
    const layerOpacity = map.getLayerOpacity(this.layerId);
    return this.isPresent(map) && layerOpacity != null && (typeof layerOpacity !== 'number' || layerOpacity > 0);
  }
}

// minimum zoom should match default VFR setting for GP iOS
const MIN_ZOOM_LARGE = ZoomLevel['20NM'];
const MIN_ZOOM_MEDIUM = ZoomLevel['10NM'];
const MIN_ZOOM_SMALL = ZoomLevel['5NM'];
const MIN_ZOOM_MILITARY = ZoomLevel['10NM'];
const MIN_ZOOM_PRIVATE = ZoomLevel['1NM'];
const MIN_ZOOM_HELIPORT = ZoomLevel['0.5NM'];
const MIN_ZOOM_SEAPLANE_BASE = ZoomLevel['0.5NM'];
const MIN_ZOOM_OTHER = ZoomLevel['0.5NM'];
class AirportSymbolLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airport-symbol';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      const theme = this.mapTheme;
      map.addLayer(LayerPriority.AirportIcons, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(),
        'paint': {
          'icon-opacity': 0,
          'text-opacity': 0,
          'text-color': this.getTextColor(),
          'text-halo-color': theme.textHaloColor(),
          'text-halo-width': theme.textHaloWidth()
        },
        'layout': {
          'icon-allow-overlap': true,
          'icon-size': 0.85,
          'icon-image': ['case', ['has', 'runwayIcon'], ['concat', 'airports:', ['get', 'runwayIcon']], ''],
          'symbol-sort-key': this.getSymbolSortKey(),
          'text-field': ['get', 'airportId'],
          'text-anchor': 'top',
          'text-font': ['Roboto'],
          'text-size': this.getAirportTextSize(),
          'text-optional': true,
          'text-offset': [0, -2.25],
          'text-letter-spacing': 0.15,
          'text-allow-overlap': this.getTextOverlap()
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        textColor: this.getTextColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth(),
        textAllowOverlap: this.getTextOverlap()
      });
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  getTextColor() {
    if (this.mapSettings.activeMapLayer() === MapOverlayLayer.VFR && this.mapSettings.vfrTopographyState() === TopographyState.ON) {
      return this.mapTheme.airportTextColor();
    }
    return this.mapTheme.textColor();
  }
  getTextOverlap() {
    if (this.mapConfig.overlay.fuelPrices.visible()) {
      return true;
    }
    return false;
  }
  getFilter() {
    return ['all', this.notIgnoredAirportFilter(), this.withinMinZoomFilter(), this.heliportFilter()];
  }
  withinMinZoomFilter() {
    return ['let', 'minZoom', ['case', ['==', ['get', 'ddcCategory'], AirportDdcCategory.Military], MIN_ZOOM_MILITARY, ['match', ['get', 'ddcType'], AirportDdcType.Airport, this.getAirportMinZoom(), AirportDdcType.Heliport, MIN_ZOOM_HELIPORT, AirportDdcType.SeaplaneBase, MIN_ZOOM_SEAPLANE_BASE, MIN_ZOOM_OTHER // all unknown or unhandled airport types
    ]], ['>=', ['zoom'], ['var', 'minZoom']]];
  }
  notIgnoredAirportFilter() {
    const ignored = this.mapConfig.content.aeronautical.airport.airportsToIgnore();
    if (ignored.length > 0) {
      return ['!', ['in', ['concat', ['get', 'airportId'], ['get', 'countryCode']], ['literal', ignored]]];
    }
    return ['boolean', true];
  }
  heliportFilter() {
    return ['match', ['get', 'ddcType'], AirportDdcType.Heliport, this.mapConfig.content.aeronautical.airport.heliportsVisible(), this.mapConfig.content.aeronautical.airport.visible()];
  }
  getAirportMinZoom() {
    return ['match', ['get', 'ddcCategory'], [AirportDdcCategory.Public, AirportDdcCategory.Joint], ['match', ['get', 'size'], AirportSize.Large, MIN_ZOOM_LARGE, AirportSize.Medium, MIN_ZOOM_MEDIUM, AirportSize.Small, MIN_ZOOM_SMALL, MIN_ZOOM_OTHER // invalid or unknown size
    ], AirportDdcCategory.Private, MIN_ZOOM_PRIVATE, MIN_ZOOM_OTHER // invalid or unknown category
    ];
  }
  getSymbolSortKey() {
    return ['let', 'priorityOffset', ['case', ['==', ['get', 'hasTower'], true], 0, 1000], ['-', ['var', 'priorityOffset'], ['number', ['get', 'runwayCount'], 0]]];
  }
  getAirportTextSize() {
    return ['match', ['get', 'ddcCategory'], [AirportDdcCategory.Public, AirportDdcCategory.Joint, AirportDdcCategory.Military], 13, 10];
  }
}
class RunwayFillLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'runway-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      const theme = this.mapTheme;
      map.addLayer(LayerPriority.AirportRunways, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'fill',
        'paint': {
          'fill-opacity': 0,
          'fill-color': theme.runwayColor()
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerColor(this.layerId, theme.runwayColor());
    }
  }
}
class RunwayLineLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'runway-line';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      const theme = this.mapTheme;
      map.addLayer(LayerPriority.AirportRunways, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': ['==', ['geometry-type'], 'LineString'],
        'paint': {
          'line-opacity': 0,
          'line-color': theme.runwayLineColor(),
          'line-width': this.getLineWidth(),
          'line-dasharray': [3, 3]
        },
        'layout': {
          'line-cap': 'butt'
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerColor(this.layerId, theme.runwayLineColor());
    }
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('RunwayLineLayer only supports numeric opacity.');
    }
    return super.setOpacity(this.getLineOpacity(opacity));
  }
  getLineOpacity(opacityScaleFactor) {
    return ['step', ['zoom'], 0.8 * opacityScaleFactor, RUNWAY_SHOW_LABELS_ZOOM, 1 * opacityScaleFactor];
  }
  getLineWidth() {
    return ['step', ['zoom'], 1, RUNWAY_SHOW_LABELS_ZOOM, 3];
  }
}
const OPACITY_SCALE_FACTOR$6 = 0.75;
class RunwayNotamFillLayer extends ChartSearchableSubLayer {
  constructor(injector, radialMenuMapService, sourceLayerId, layerType, notamRunwayService) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.notamRunwayService = notamRunwayService;
    this.LAYER_PREFIX = 'runway-notam-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      const notamLists = this.notamRunwayService.runwayNotamLists();
      map.addLayer(LayerPriority.RunwayNotam, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'fill',
        'filter': this.getFilter(notamLists),
        'paint': {
          'fill-color': this.getFillColor(notamLists)
        }
      });
      this.notamRunwayService.layerIds.add(this.layerId);
    }
  }
  remove(map) {
    if (this.layerId != null) {
      this.notamRunwayService.layerIds.delete(this.layerId);
    }
    super.remove(map);
  }
  update(map) {
    if (this.layerId != null) {
      const notamLists = this.notamRunwayService.runwayNotamLists();
      map.setLayerColor(this.layerId, this.getFillColor(notamLists));
      map.setLayerFilter(this.layerId, this.getFilter(notamLists));
    }
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('RunwayNotamFillLayer only supports numeric opacity.');
    }
    return super.setOpacity(opacity * OPACITY_SCALE_FACTOR$6);
  }
  getFillColor({
    active,
    conditional,
    upcoming
  }) {
    return ['let', 'combinedId', ['concat', ['get', 'airportId'], '_', ['get', 'RunwayAttrs.runwayId']], ['match', ['var', 'combinedId'], active, Color.Red.toString(), conditional, Color.Orange.toString(), upcoming, Color.Yellow.toString(), Color.Black.toString()]];
  }
  getFilter({
    active,
    conditional,
    upcoming
  }) {
    return ['let', 'combinedId', ['concat', ['get', 'airportId'], '_', ['get', 'RunwayAttrs.runwayId']], ['match', ['var', 'combinedId'], active, true, conditional, true, upcoming, true, false // don't show inactive
    ]];
  }
}
const OPACITY_SCALE_FACTOR$5 = 0.75;
class RunwayNotamHighlightLineLayer extends ChartSearchableSubLayer {
  constructor(injector, radialMenuMapService, sourceLayerId, layerType, notamRunwayService) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.notamRunwayService = notamRunwayService;
    this.LAYER_PREFIX = 'runway-notam-highlight-line';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      const highlighted = this.notamRunwayService.highlightedNotamList();
      map.addLayer(LayerPriority.HighlightRunwayNotam, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': this.getFilter(highlighted),
        'paint': {
          'line-color': Color.White.toString(),
          'line-width': 2
        }
      });
      this.notamRunwayService.layerIds.add(this.layerId);
    }
  }
  remove(map) {
    if (this.layerId != null) {
      this.notamRunwayService.layerIds.delete(this.layerId);
    }
    super.remove(map);
  }
  update(map) {
    if (this.layerId != null) {
      const highlighted = this.notamRunwayService.highlightedNotamList();
      map.setLayerFilter(this.layerId, this.getFilter(highlighted));
    }
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('RunwayNotamHighlightLineLayer only supports numeric opacity.');
    }
    return super.setOpacity(opacity * OPACITY_SCALE_FACTOR$5);
  }
  getFilter(highlighted) {
    if (highlighted.length > 0) {
      return ['let', 'combinedId', ['concat', ['get', 'airportId'], '_', ['get', 'RunwayAttrs.runwayId']], ['match', ['var', 'combinedId'], highlighted, true, false]];
    } else {
      return false;
    }
  }
}
class RunwayNotamHighlightSymbolIdLayer extends ChartSearchableSubLayer {
  constructor(injector, radialMenuMapService, sourceLayerId, layerType, notamRunwayService) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.notamRunwayService = notamRunwayService;
    this.LAYER_PREFIX = 'runway-notam-highlight-symbol-id';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      const highlighted = this.notamRunwayService.highlightedNotamList();
      map.addLayer(LayerPriority.HighlightRunwayNotam, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(highlighted),
        'minzoom': RUNWAY_SHOW_LABELS_ZOOM,
        'paint': {
          'text-color': Color.Black.toString()
        },
        'layout': {
          'icon-allow-overlap': true,
          'icon-image': MapIcon.Runway_NOTAM_Highlight_Billboard,
          'icon-text-fit': 'both',
          'icon-text-fit-padding': [4, 4, 4, 4],
          'text-allow-overlap': true,
          'text-font': ['Roboto'],
          'text-field': ['get', 'runwayId'],
          'text-size': this.getFontSize()
        }
      });
      this.notamRunwayService.layerIds.add(this.layerId);
    }
  }
  remove(map) {
    if (this.layerId != null) {
      this.notamRunwayService.layerIds.delete(this.layerId);
    }
    super.remove(map);
  }
  update(map) {
    if (this.layerId != null) {
      const highlighted = this.notamRunwayService.highlightedNotamList();
      map.setLayerFilter(this.layerId, this.getFilter(highlighted));
    }
  }
  getFilter(highlighted) {
    if (highlighted.length > 0) {
      return ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'pointType'], RunwayPointType.RunwayEnd], ['let', 'combinedId', ['concat', ['get', 'airportId'], '_', ['get', 'runwayId']], ['match', ['var', 'combinedId'], highlighted, true, false]]];
    } else {
      return false;
    }
  }
  getFontSize() {
    return ['interpolate', ['linear'], ['zoom'], ZoomLevel['0.1NM'], 16, ZoomLevel['100FT'], 26];
  }
}
class RunwayNotamSymbolBadgesLayer extends ChartSearchableSubLayer {
  constructor(injector, radialMenuMapService, sourceLayerId, layerType, notamRunwayService) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.notamRunwayService = notamRunwayService;
    this.LAYER_PREFIX = 'runway-notam-symbol-badges';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      const notamLists = this.notamRunwayService.runwayNotamLists();
      map.addLayer(LayerPriority.RunwayNotam, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(notamLists),
        'minzoom': RUNWAY_SHOW_LABELS_ZOOM,
        'layout': {
          'icon-allow-overlap': true,
          'icon-image': this.getIconImage(notamLists),
          'icon-rotate': this.getIconRotation(notamLists),
          'icon-size': 0.5
        }
      });
      this.notamRunwayService.layerIds.add(this.layerId);
    }
  }
  remove(map) {
    if (this.layerId != null) {
      this.notamRunwayService.layerIds.delete(this.layerId);
    }
    super.remove(map);
  }
  update(map) {
    if (this.layerId != null) {
      const notamLists = this.notamRunwayService.runwayNotamLists();
      map.setLayerSymbolProperties(this.layerId, {
        iconImage: this.getIconImage(notamLists),
        iconRotate: this.getIconRotation(notamLists)
      });
      map.setLayerFilter(this.layerId, this.getFilter(notamLists));
    }
  }
  getFilter({
    active,
    conditional,
    upcoming
  }) {
    return ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'pointType'], RunwayPointType.RunwayNotamMarking], ['let', 'combinedId', ['concat', ['get', 'airportId'], '_', ['get', 'runwayId']], ['match', ['var', 'combinedId'], active, true, conditional, true, upcoming,
    // nothing to show for upcoming
    false, false // don't show inactive
    ]]];
  }
  getIconImage({
    active,
    conditional
  }) {
    return ['let', 'combinedId', ['concat', ['get', 'airportId'], '_', ['get', 'runwayId']], ['match', ['var', 'combinedId'], active, MapIcon.Runway_NOTAM_Closed, conditional, MapIcon.Runway_NOTAM_Conditional, MapIcon.Invalid // should be filtered out
    ]];
  }
  getIconRotation({
    active
  }) {
    return ['let', 'combinedId', ['concat', ['get', 'airportId'], '_', ['get', 'runwayId']], ['match', ['var', 'combinedId'], active, ['get', 'trueBearing'], 0]];
  }
}
class RunwayNotamSymbolIdLayer extends ChartSearchableSubLayer {
  constructor(injector, radialMenuMapService, sourceLayerId, layerType, notamRunwayService) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.notamRunwayService = notamRunwayService;
    this.LAYER_PREFIX = 'runway-notam-symbol-id';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      const notamLists = this.notamRunwayService.runwayNotamLists();
      map.addLayer(LayerPriority.RunwayNotam, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(notamLists),
        'minzoom': RUNWAY_SHOW_LABELS_ZOOM,
        'paint': {
          'text-color': Color.Black.toString()
        },
        'layout': {
          'icon-allow-overlap': true,
          'icon-image': MapIcon.Runway_NOTAM_Billboard,
          'icon-text-fit': 'both',
          'icon-text-fit-padding': [2, 2, 2, 2],
          'text-allow-overlap': true,
          'text-font': ['Roboto'],
          'text-field': ['get', 'runwayId'],
          'text-size': this.getFontSize()
        }
      });
      this.notamRunwayService.layerIds.add(this.layerId);
    }
  }
  remove(map) {
    if (this.layerId != null) {
      this.notamRunwayService.layerIds.delete(this.layerId);
    }
    super.remove(map);
  }
  update(map) {
    if (this.layerId != null) {
      const notamLists = this.notamRunwayService.runwayNotamLists();
      map.setLayerFilter(this.layerId, this.getFilter(notamLists));
    }
  }
  getFilter({
    active,
    conditional,
    upcoming
  }) {
    return ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'pointType'], RunwayPointType.RunwayEnd], ['let', 'combinedId', ['concat', ['get', 'airportId'], '_', ['get', 'runwayId']], ['match', ['var', 'combinedId'], active, true, conditional, true, upcoming, true, false // don't show inactive
    ]]];
  }
  getFontSize() {
    return ['interpolate', ['linear'], ['zoom'], ZoomLevel['0.1NM'], 16, ZoomLevel['100FT'], 26];
  }
}
class RunwaySymbolIdLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'runway-symbol-id';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      const theme = this.mapTheme;
      map.addLayer(LayerPriority.AirportRunways, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'pointType'], RunwayPointType.RunwayEnd]],
        'minzoom': RUNWAY_SHOW_LABELS_ZOOM,
        'paint': {
          'text-color': theme.runwayLineColor()
        },
        'layout': {
          'icon-allow-overlap': true,
          'icon-image': MapIcon.Runway_Billboard,
          'icon-text-fit': 'both',
          'icon-text-fit-padding': [2, 2, 2, 2],
          'text-allow-overlap': true,
          'text-font': ['Roboto'],
          'text-field': ['get', 'runwayId'],
          'text-size': this.getFontSize()
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        textColor: theme.runwayLineColor()
      });
    }
  }
  getFontSize() {
    return ['interpolate', ['linear'], ['zoom'], ZoomLevel['0.1NM'], 16, ZoomLevel['100FT'], 26];
  }
}
class RunwaySymbolLengthLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'runway-symbol-length';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      const theme = this.mapTheme;
      map.addLayer(LayerPriority.AirportRunways, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': ['==', ['geometry-type'], 'LineString'],
        'minzoom': RUNWAY_SHOW_LABELS_ZOOM,
        'paint': {
          'text-color': theme.runwayLengthColor()
        },
        'layout': {
          'text-allow-overlap': true,
          'text-font': ['Roboto'],
          'text-field': this.getTextField(),
          'symbol-placement': 'line-center',
          'text-anchor': 'top',
          'text-offset': this.getTextOffset(),
          'text-size': 12
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        textColor: theme.runwayLengthColor(),
        textField: this.getTextField()
      });
    }
  }
  convertFeetToMeters(value) {
    return ['round', ['/', value, 3.281]];
  }
  getTextField() {
    const units = this.mapSettings.units.runwayLength();
    switch (units) {
      case RunwayLengthUnits.Feet:
        return ['concat', ['get', 'length'], ' FT'];
      case RunwayLengthUnits.Meters:
        return ['concat', this.convertFeetToMeters(['get', 'length']), ' M'];
      default:
        return '';
    }
  }
  getTextOffset() {
    return ['step', ['zoom'], ['literal', [0, 0.5]], ZoomLevel['300FT'], ['literal', [0, 3]], ZoomLevel['100FT'], ['literal', [0, 6]]];
  }
}
class AirportLayer extends ChartSearchableLayerGroup {
  constructor(injector, radialMenuMapService, content, notamRunwayService) {
    super(injector, radialMenuMapService, content);
    this.notamRunwayService = notamRunwayService;
    this.runwayShouldBeVisible = () => {
      const runwayVisible = this.mapConfig.content.aeronautical.airport.runways();
      const safeTaxiAvailable = this.mapInfo.content.safeTaxi.available();
      const safeTaxiVisible = this.mapConfig.content.safeTaxi.visible();
      const groundSatelliteVisible = this.mapConfig.content.groundSatellite.visible();
      return runwayVisible && !(safeTaxiAvailable && safeTaxiVisible) && !groundSatelliteVisible;
    };
    this.runwayNotamsShouldBeVisible = () => {
      const airportsAvailable = this.mapInfo.content.airport.available();
      const notamsAvailable = this.mapInfo.overlay.notam.available();
      const notamsVisible = this.mapConfig.overlay.notam.visible();
      const showTerminalNotams = this.mapConfig.overlay.notam.showTerminalNotams();
      return airportsAvailable && notamsAvailable && notamsVisible && showTerminalNotams;
    };
    this.runwayNotamHighlightsShouldBeVisible = () => {
      const highlights = this.mapHighlight.notam();
      const runwayNotams = this.notamRunwayService.runwayNotams();
      return runwayNotams.some(runwayNotam => {
        const notamId = runwayNotam.notam.properties['notamId'];
        return notamId != null && highlights.some(highlight => notamId === highlight.id);
      });
    };
    this.groupInfo = [{
      layer: new RunwayFillLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.Runways, LayerType.Airports),
      shouldBeVisible: this.runwayShouldBeVisible
    }, {
      layer: new RunwayLineLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.RunwayPoints, LayerType.Airports),
      shouldBeVisible: this.runwayShouldBeVisible
    }, {
      layer: new RunwaySymbolIdLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.RunwayPoints, LayerType.Airports),
      shouldBeVisible: this.runwayShouldBeVisible
    }, {
      layer: new RunwaySymbolLengthLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.RunwayPoints, LayerType.Airports),
      shouldBeVisible: this.runwayShouldBeVisible
    }, {
      layer: new AirportSymbolLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.Airports, LayerType.Airports),
      shouldBeVisible: () => this.mapConfig.content.aeronautical.airport.visible() || this.mapConfig.content.aeronautical.airport.heliportsVisible()
    }, {
      layer: new RunwayNotamFillLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.Runways, LayerType.RunwayNotams, this.notamRunwayService),
      shouldBeVisible: this.runwayNotamsShouldBeVisible
    }, {
      layer: new RunwayNotamSymbolBadgesLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.RunwayPoints, LayerType.RunwayNotams, this.notamRunwayService),
      shouldBeVisible: this.runwayNotamsShouldBeVisible
    }, {
      layer: new RunwayNotamSymbolIdLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.RunwayPoints, LayerType.RunwayNotams, this.notamRunwayService),
      shouldBeVisible: this.runwayNotamsShouldBeVisible
    }, {
      layer: new RunwayNotamHighlightLineLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.Runways, LayerType.RunwayNotams, this.notamRunwayService),
      shouldBePresent: this.runwayNotamHighlightsShouldBeVisible,
      shouldBeVisible: this.runwayNotamHighlightsShouldBeVisible
    }, {
      layer: new RunwayNotamHighlightSymbolIdLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.RunwayPoints, LayerType.Notams, this.notamRunwayService),
      shouldBePresent: this.runwayNotamHighlightsShouldBeVisible,
      shouldBeVisible: this.runwayNotamHighlightsShouldBeVisible
    }];
  }
  addSource(map) {
    return map.mvtCycleContent.add(MvtCycleContentUser.Airport, this.content);
  }
  getSource(map) {
    return map.mvtCycleContent.getId(this.content);
  }
  removeSource(map) {
    map.mvtCycleContent.remove(MvtCycleContentUser.Airport, this.content);
  }
}
let AirportService = /*#__PURE__*/(() => {
  class AirportService extends ChartSearchableLayerService {
    constructor() {
      super();
      this.runwayNotamService = inject(NotamRunwayService);
      effect(() => this.updateLayers());
      effect(() => this.updateImages());
      effect(() => this.updateSprite());
    }
    getSource(map) {
      for (const [_content, layer] of this.layers) {
        return layer.getSource(map);
      }
      return undefined;
    }
    getSourceIds(map) {
      const sourceIds = [];
      for (const [_content, layer] of this.layers) {
        const id = layer.getSource(map);
        if (id != null) {
          sourceIds.push(id);
        }
      }
      return sourceIds;
    }
    createLayer(content) {
      return new AirportLayer(this.injector, this.radialMenuMapService, content, this.runwayNotamService);
    }
    getContent() {
      return this.mapInfo.content.airport.content();
    }
    getPresent() {
      return this.mapConfig.content.aeronautical.airport.present();
    }
    getVisible() {
      const aeronauticalVisible = this.mapConfig.content.aeronautical.visible();
      const airportsVisible = this.mapConfig.content.aeronautical.airport.visible();
      const heliportsVisible = this.mapConfig.content.aeronautical.airport.heliportsVisible();
      return aeronauticalVisible && (airportsVisible || heliportsVisible);
    }
    getOpacity() {
      return this.mapConfig.content.aeronautical.opacity();
    }
    createBillboardImage(id, fillColor, strokeColor, strokeWidth) {
      return {
        id,
        opts: {
          stretchX: [[RUNWAY_BILLBOARD_RADIUS, RUNWAY_BILLBOARD_WIDTH - RUNWAY_BILLBOARD_RADIUS]],
          stretchY: [[RUNWAY_BILLBOARD_RADIUS, RUNWAY_BILLBOARD_HEIGHT - RUNWAY_BILLBOARD_RADIUS]]
        },
        src: new Rectangle(RUNWAY_BILLBOARD_WIDTH, RUNWAY_BILLBOARD_HEIGHT, fillColor, RUNWAY_BILLBOARD_RADIUS, strokeColor, strokeWidth).rasterize()
      };
    }
    updateImages() {
      const icons = [MapIcon.Runway_NOTAM_Closed, MapIcon.Runway_NOTAM_Conditional].map(x => MapIconDefinitions.get(x)).concat(this.createBillboardImage(MapIcon.Runway_Billboard, RUNWAY_BILLBOARD_COLOR, this.mapTheme.runwayLineColor()), this.createBillboardImage(MapIcon.Runway_NOTAM_Billboard, Color.Yellow.toString(), Color.Black.toString(), 1), this.createBillboardImage(MapIcon.Runway_NOTAM_Highlight_Billboard, Color.Yellow.toString(), Color.White.toString()));
      this.mapIcon.update(icons);
    }
    updateSprite() {
      const map = this.map();
      if (map != null) {
        const spriteType = this.mapTheme.flightRule() === FlightRule.Vfr ? 'vfr' : 'ifr';
        for (const content of this.getContent()) {
          try {
            const spriteUrl = `${content.urlBase}/sprites/${spriteType}`;
            map.setSprite(SpriteID.Airports, spriteUrl);
          } catch (err) {
            console.error(err);
          }
        }
      }
    }
    static {
      this.ɵfac = function AirportService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AirportService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AirportService,
        factory: AirportService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AirportService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var ArtccAirspace = /*#__PURE__*/function (ArtccAirspace) {
  ArtccAirspace["ARTCC_FIR"] = "ARTCC / FIR";
  return ArtccAirspace;
}(ArtccAirspace || {});
var CtrlAirspace = /*#__PURE__*/function (CtrlAirspace) {
  CtrlAirspace["Air_Traffic_Zone"] = "Air Traffic Zone";
  CtrlAirspace["Airport_Radar_Service_Area"] = "Airport Radar Service Area";
  CtrlAirspace["Airway_Airspace"] = "Airway Airspace";
  CtrlAirspace["Class_A"] = "Class A";
  CtrlAirspace["Class_B"] = "Class B";
  CtrlAirspace["Class_C"] = "Class C";
  CtrlAirspace["Class_D"] = "Class D";
  CtrlAirspace["Class_E"] = "Class E";
  CtrlAirspace["Class_F"] = "Class F";
  CtrlAirspace["Class_G"] = "Class G";
  CtrlAirspace["Control_Area"] = "Control Area";
  CtrlAirspace["Control_Zone"] = "Control Zone";
  CtrlAirspace["Helicopter_Protected_Zone"] = "Helicopter Protected Zone";
  CtrlAirspace["Helicopter_Traffic_Zone"] = "Helicopter Traffic Zone";
  CtrlAirspace["Military_Air_Traffic_Zone"] = "Military Air Traffic Zone";
  CtrlAirspace["Military_Control_Zone"] = "Military Control Zone";
  CtrlAirspace["Military_Terminal_Area"] = "Military Terminal Area";
  CtrlAirspace["Oceanic_Control_Area"] = "Oceanic Control Area";
  CtrlAirspace["Positive_Control_Area"] = "Positive Control Area";
  CtrlAirspace["Radar_Zone"] = "Radar Zone";
  CtrlAirspace["Special_Rules"] = "Special Rules Airspace";
  CtrlAirspace["Special_Rules_Zone"] = "Special Rules Zone";
  CtrlAirspace["Terminal_Area"] = "Terminal Area";
  CtrlAirspace["Terminal_Control_Area"] = "Terminal Control Area";
  CtrlAirspace["Terminal_Radar_Service_Area"] = "Terminal Radar Service Area";
  CtrlAirspace["Traffic_Information_Area"] = "Traffic Information Area";
  CtrlAirspace["Traffic_Information_Zone"] = "Traffic Information Zone";
  CtrlAirspace["Upper_Control_Area"] = "Upper Control Area";
  return CtrlAirspace;
}(CtrlAirspace || {});
// Special Use Airspace
var SuaAirspace = /*#__PURE__*/function (SuaAirspace) {
  SuaAirspace["Advisory_Area"] = "Advisory Area";
  SuaAirspace["Aerobatic_Practice_Area"] = "Aerobatic Practice Area";
  SuaAirspace["Air_Defense_Identification_Zone"] = "Air Defense Identification Zone";
  SuaAirspace["Alert_Area"] = "Alert Area";
  SuaAirspace["Avoid_Overflying_Area"] = "Avoid Overflying Area";
  SuaAirspace["Buffer_Zone"] = "Buffer Zone";
  SuaAirspace["Caution_Area"] = "Caution Area";
  SuaAirspace["Common_Frequency_Zone"] = "Common Frequency Zone";
  SuaAirspace["Control_Mode_C_Airspace"] = "Control Mode C Airspace";
  SuaAirspace["Cross_Border_Area"] = "Cross Border Area";
  SuaAirspace["Danger_Area"] = "Danger Area";
  SuaAirspace["Flight_Information_Service"] = "Flight Information Service";
  SuaAirspace["Flight_Information_Zone"] = "Flight Information Zone";
  SuaAirspace["Frequency_Monitoring_Code"] = "Frequency Monitoring Code";
  SuaAirspace["Longterm_Temporary_Flight_Restriction"] = "Long-term Temporary Flight Restriction";
  SuaAirspace["Mandatory_Broadcast_Zone"] = "Mandatory Broadcast Zone";
  SuaAirspace["Military_Operations_Area"] = "Military Operations Area";
  SuaAirspace["National_Security_Area"] = "National Security Area";
  SuaAirspace["Nature_Conservation_Area"] = "Nature Conservation Area";
  SuaAirspace["Parachute_Jump_Area"] = "Parachute Jump Area";
  SuaAirspace["Prohibited_Area"] = "Prohibited Area";
  SuaAirspace["Radio_Mandatory_Zone"] = "Radio Mandatory Zone";
  SuaAirspace["Restricted_Area"] = "Restricted Area";
  SuaAirspace["Temporary_Reserve_Area"] = "Temporary Reserve Area";
  SuaAirspace["Temporary_Segregated_Area"] = "Temporary Segregated Area";
  SuaAirspace["Training_Area"] = "Training Area";
  SuaAirspace["Transponder_Mandatory_Zone"] = "Transponder Mandatory Zone";
  SuaAirspace["Unspecified_or_Unknown_Area"] = "Unspecified or Unknown Area";
  SuaAirspace["Warning_Area"] = "Warning Area";
  return SuaAirspace;
}(SuaAirspace || {});
const BaseColors = {
  BLUE: new Color(49, 147, 240),
  // #319ef0,
  GIFD3_DARK_ORANGE_2: new Color(238, 118, 0),
  // #ee7600
  GIFD3_MAGENTA: new Color(139, 0, 139),
  // #8b008b
  GIFD3_RED_3: new Color(205, 0, 0),
  // #cd0000
  GREY: new Color(45, 54, 54),
  // #2d3636
  UICOLOR_BLUE: new Color(0, 0, 255),
  // #0000ff,
  UICOLOR_WHITE: new Color(255, 255, 255) // #ffffff
};
const ModifiedBaseColors = {
  DARK_BLUE_ALPHA2: BaseColors.BLUE.clone().a(0.9),
  DARK_GREY: BaseColors.GREY.clone().a(0.8)
};
const AirspaceColor = {
  ARTCC: BaseColors.UICOLOR_BLUE,
  ATZ: BaseColors.BLUE,
  CLASS_C: BaseColors.GIFD3_MAGENTA,
  CONTROLLED: new Color(65, 190, 240),
  // #41bef0
  DEFAULT: new Color(120, 120, 120),
  // #787878
  FIS: new Color(220, 220, 220),
  // #dcdcdc
  FMC: ModifiedBaseColors.DARK_GREY,
  MATZ: new Color(17, 34, 170),
  // #1122aa
  MISC: new Color(180, 0, 180),
  // #b400b4
  MOA: new Color(183, 0, 255),
  // #b700ff
  PARACHUTE: new Color(189, 189, 57),
  PROHIBITED: BaseColors.GIFD3_RED_3,
  RESTRICTED: BaseColors.GIFD3_DARK_ORANGE_2,
  RMZ: ModifiedBaseColors.DARK_BLUE_ALPHA2,
  TMZ: ModifiedBaseColors.DARK_GREY,
  TRSA: new Color(104, 100, 100) // #686464
};
const DASHED_LINE_LAYERS = [SuaAirspace.Air_Defense_Identification_Zone, SuaAirspace.Parachute_Jump_Area, CtrlAirspace.Air_Traffic_Zone, CtrlAirspace.Military_Air_Traffic_Zone, CtrlAirspace.Class_D];
// These airspaces are provided to us but we don't want
// to show them for some reason
const EXCLUDED_AIRSPACES = [SuaAirspace.Aerobatic_Practice_Area];
// We don't want to show labels for these airspaces to
// help clean up some clutter
const NO_LABEL_AIRSPACES = [SuaAirspace.Training_Area, SuaAirspace.Temporary_Segregated_Area, SuaAirspace.Temporary_Reserve_Area, SuaAirspace.Caution_Area, SuaAirspace.Danger_Area, SuaAirspace.Air_Defense_Identification_Zone];
const PATTERN_LINE_AIRSPACES = [ArtccAirspace.ARTCC_FIR, SuaAirspace.Air_Defense_Identification_Zone, SuaAirspace.Alert_Area, SuaAirspace.Military_Operations_Area, SuaAirspace.Nature_Conservation_Area, SuaAirspace.Restricted_Area];
const getHighlightLineWidth = type => {
  if (type === 'artcc') {
    return getLineWidthHelper([[1, 6.0], [6, 12.0]]);
  }
  return getLineWidthHelper([[4, 6.0], [12, 12.0]]);
};
const getLineWidth = (type, borderWidth) => {
  const border = borderWidth ?? 0;
  if (type === 'artcc') {
    return getLineWidthHelper([[1, 0.25 + border], [5, 0.5 + border * 2], [6, 1.0 + border * 4]], border);
  }
  return getLineWidthHelper([[4, 0.125 + border], [9, 1.5 + border * 2], [13, 3.0 + border * 4]], border);
};
/**
 * Get the expression of the line width. If dynamic rendering is enabled, the
 * line width is de-emphasized and made thinner.
 * @param zoomWidths The list of widths per zoom level when dynamic rendering
 * is not applied.
 * @returns An `ExpressionSpecification` or number.
 */
const getLineWidthHelper = (zoomWidths, borderWidth) => {
  if (zoomWidths.length < 1) {
    return 0.25;
  }
  const border = borderWidth ?? 0;
  const zoomWidthExpression = ['interpolate', ['linear'], ['zoom'],
  // line width at ZL=1
  4, ['match', ['get', 'classDescription'], [CtrlAirspace.Class_B, CtrlAirspace.Class_C], 0.75 + border, [SuaAirspace.Prohibited_Area, SuaAirspace.Restricted_Area, SuaAirspace.Military_Operations_Area, ArtccAirspace.ARTCC_FIR, SuaAirspace.Air_Defense_Identification_Zone, SuaAirspace.Alert_Area, SuaAirspace.Nature_Conservation_Area], 0.5 + border, [CtrlAirspace.Control_Area, CtrlAirspace.Control_Zone, CtrlAirspace.Terminal_Area], ['match', ['get', 'caspcClass'], [1,
  // Class B
  2 // Class C
  ], 0.75 + border, 0.125 + border // default border
  ], 0.125 + border // everything else
  ],
  // line width at ZL=5
  13, ['match', ['get', 'classDescription'], [CtrlAirspace.Class_B, CtrlAirspace.Class_C], 7 + border * 2, [SuaAirspace.Prohibited_Area, SuaAirspace.Restricted_Area, SuaAirspace.Military_Operations_Area], 5 + border * 2, [CtrlAirspace.Control_Area, CtrlAirspace.Control_Zone, CtrlAirspace.Terminal_Area], ['match', ['get', 'caspcClass'], [1,
  // Class B
  2 // Class C
  ], 7 + border * 2, 3 + border * 2 // default border
  ], 3 + border * 2 // everything else
  ]];
  return zoomWidthExpression;
};
const getAltitudeTextHelper = type => {
  const prop = type === 'min' ? 'MinAltitude' : 'MaxAltitude';
  return ['case', ['get', `${prop}.isUnl`], 'UNL', ['get', `${prop}.isSfc`], 'SFC', ['==', ['get', `${prop}.altitude`], 0], type === 'min' ? 'SFC' : 'UNL', ['let', 'converted', ['round', ['/', ['get', `${prop}.altitude`], 100]], ['concat',
  // Add a dash if the altitude was rounded up by at least 0.1 feet.
  // Note: The extra 0.1 is to prevent any floating point shenanigans.
  // We expect the altitude to be given in whole numbers, e.g., 7499 or
  // 7500 and not 7499.9.
  ['case', ['<', ['get', `${prop}.altitude`], ['-', ['*', ['var', 'converted'], 100], 0.1]], '\u{2012}', ''],
  // Add zeros to left-pad
  ['case', ['<', ['var', 'converted'], 10], '00', ['<', ['var', 'converted'], 100], '0', ''], ['var', 'converted']]]];
};
/**
 * Get the expression to get the altitude text for an airspace which includes
 * max and min altitude information.
 * @returns An `ExpressionSpecification`.
 */
const getAltitudeText = type => {
  switch (type) {
    case 'ctrl':
    case 'sua':
      return ['format', getAltitudeTextHelper('max'), '\n', getAltitudeTextHelper('min'), ['case', showFrequency(), ['concat', '\n', ['string', ['get', 'FrequencyList[0].FrequencyInfo.frequency'], 'UNK']], ''], {
        'font-scale': 0.8
      }];
    case 'artcc':
      throw new Error('ARTCC text is not supported');
    default:
      throw new Error('unknown airspace type');
  }
};
const getArtccLineOpacity = opacityScaleFactor => {
  return ['interpolate', ['linear'], ['zoom'], 1, 0.02 * (opacityScaleFactor ?? 1.0), 5, 0.1 * (opacityScaleFactor ?? 1.0), 6, 1.0 * (opacityScaleFactor ?? 1.0)];
};
const getHighlightFilter = id => {
  if (id != null) {
    return ['==', ['id'], id];
  }
  return ['boolean', false];
};
const getZoomFilter = type => {
  switch (type) {
    case 'ctrl':
      return ['match', ['get', 'classDescription'], [CtrlAirspace.Oceanic_Control_Area, CtrlAirspace.Upper_Control_Area], ['>=', ['zoom'], ZoomLevel['10NM']], [CtrlAirspace.Class_A, CtrlAirspace.Class_B], ['>=', ['zoom'], ZoomLevel['100NM']], [CtrlAirspace.Class_C, CtrlAirspace.Class_D, CtrlAirspace.Terminal_Control_Area], ['>=', ['zoom'], ZoomLevel['50NM']], [CtrlAirspace.Class_G], ['>=', ['zoom'], ZoomLevel['20NM']], [CtrlAirspace.Control_Area, CtrlAirspace.Control_Zone, CtrlAirspace.Terminal_Area], ['match', ['get', 'caspcClass'], [0,
      // Class A
      1 // Class B
      ], ['>=', ['zoom'], ZoomLevel['100NM']], [2,
      // Class C
      3 // Class D
      ], ['>=', ['zoom'], ZoomLevel['50NM']], [6 // Class G
      ], ['>=', ['zoom'], ZoomLevel['20NM']], ['>=', ['zoom'], ZoomLevel['10NM']]], ['>=', ['zoom'], ZoomLevel['10NM']]];
    case 'sua':
      return ['match', ['get', 'classDescription'], [SuaAirspace.Military_Operations_Area], ['>=', ['zoom'], ZoomLevel['100NM']], [SuaAirspace.Air_Defense_Identification_Zone, SuaAirspace.Warning_Area, SuaAirspace.Restricted_Area, SuaAirspace.Prohibited_Area], ['>=', ['zoom'], ZoomLevel['50NM']], [SuaAirspace.Parachute_Jump_Area, SuaAirspace.Danger_Area], ['>=', ['zoom'], ZoomLevel['10NM']], [SuaAirspace.Nature_Conservation_Area, SuaAirspace.Temporary_Reserve_Area, SuaAirspace.Temporary_Segregated_Area, SuaAirspace.Caution_Area, SuaAirspace.Alert_Area, SuaAirspace.Radio_Mandatory_Zone, SuaAirspace.Transponder_Mandatory_Zone, SuaAirspace.Frequency_Monitoring_Code, SuaAirspace.Flight_Information_Service], ['>=', ['zoom'], ZoomLevel['5NM']], [SuaAirspace.Avoid_Overflying_Area], ['>=', ['zoom'], ZoomLevel['2NM']], ['>=', ['zoom'], ZoomLevel['5NM']] // Other
      ];
    default:
      return true;
  }
};
const getAirspaceFilter = type => {
  let airspaceClasses;
  switch (type) {
    case 'artcc':
      airspaceClasses = Object.values(ArtccAirspace).filter(x => !EXCLUDED_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
    case 'ctrl':
      airspaceClasses = Object.values(CtrlAirspace).filter(x => !EXCLUDED_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
    case 'sua':
    default:
      airspaceClasses = Object.values(SuaAirspace).filter(x => !EXCLUDED_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
  }
  return ['match', ['get', 'classDescription'], airspaceClasses, getZoomFilter(type), false];
};
const getTextAirspaceFilter = type => {
  let airspaceClasses;
  switch (type) {
    case 'artcc':
      airspaceClasses = Object.values(ArtccAirspace).filter(x => !EXCLUDED_AIRSPACES.includes(x)).filter(x => !NO_LABEL_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
    case 'ctrl':
      airspaceClasses = Object.values(CtrlAirspace).filter(x => !EXCLUDED_AIRSPACES.includes(x)).filter(x => !NO_LABEL_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
    case 'sua':
    default:
      airspaceClasses = Object.values(SuaAirspace).filter(x => !EXCLUDED_AIRSPACES.includes(x)).filter(x => !NO_LABEL_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
  }
  return ['all', ['any', ["==", ["geometry-type"], "Point"], ['==', ["geometry-type"], "MultiPoint"]], ['match', ['get', 'classDescription'], airspaceClasses, true, false]];
};
const getDashedAirspaceFilter = type => {
  let airspaceClasses;
  switch (type) {
    case 'artcc':
      airspaceClasses = Object.values(ArtccAirspace).filter(x => DASHED_LINE_LAYERS.includes(x)).filter(x => !EXCLUDED_AIRSPACES.includes(x)).filter(x => !PATTERN_LINE_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
    case 'ctrl':
      airspaceClasses = Object.values(CtrlAirspace).filter(x => DASHED_LINE_LAYERS.includes(x)).filter(x => !EXCLUDED_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
    case 'sua':
    default:
      airspaceClasses = Object.values(SuaAirspace).filter(x => DASHED_LINE_LAYERS.includes(x)).filter(x => !EXCLUDED_AIRSPACES.includes(x)).filter(x => !PATTERN_LINE_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
  }
  return ['all', ['any', ["==", ["geometry-type"], "Polygon"], ['==', ["geometry-type"], "MultiPolygon"]], ['match', ['get', 'classDescription'], airspaceClasses, getZoomFilter(type), false]];
};
const getNonDashedAirspaceFilter = type => {
  let airspaceClasses;
  switch (type) {
    case 'artcc':
      airspaceClasses = Object.values(ArtccAirspace).filter(x => !DASHED_LINE_LAYERS.includes(x)).filter(x => !EXCLUDED_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
    case 'ctrl':
      airspaceClasses = Object.values(CtrlAirspace).filter(x => !DASHED_LINE_LAYERS.includes(x)).filter(x => !EXCLUDED_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
    case 'sua':
    default:
      airspaceClasses = Object.values(SuaAirspace).filter(x => !DASHED_LINE_LAYERS.includes(x)).filter(x => !EXCLUDED_AIRSPACES.includes(x)).filter(x => !PATTERN_LINE_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
  }
  return ['all', ['any', ["==", ["geometry-type"], "Polygon"], ['==', ["geometry-type"], "MultiPolygon"]], ['match', ['get', 'classDescription'], airspaceClasses, getZoomFilter(type), false]];
};
const getPatternedAirspaceFilter = type => {
  let airspaceClasses;
  switch (type) {
    case 'artcc':
      airspaceClasses = Object.values(ArtccAirspace).filter(x => !EXCLUDED_AIRSPACES.includes(x)).filter(x => PATTERN_LINE_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
    case 'sua':
    default:
      airspaceClasses = Object.values(SuaAirspace).filter(x => !EXCLUDED_AIRSPACES.includes(x)).filter(x => PATTERN_LINE_AIRSPACES.includes(x)).flatMap(x => x.toString());
      break;
  }
  return ['all', ['any', ["==", ["geometry-type"], "Polygon"], ['==', ["geometry-type"], "MultiPolygon"]], ['match', ['get', 'classDescription'], airspaceClasses, getZoomFilter(type), false]];
};
const getAirspaceColor = type => {
  switch (type) {
    case 'artcc':
      return ['match', ['get', 'classDescription'], ArtccAirspace.ARTCC_FIR, AirspaceColor.ARTCC.toString(), AirspaceColor.DEFAULT.toString()];
    case 'ctrl':
      return ['match', ['get', 'classDescription'], CtrlAirspace.Class_C, AirspaceColor.CLASS_C.toString(), CtrlAirspace.Terminal_Radar_Service_Area, AirspaceColor.TRSA.toString(), [CtrlAirspace.Class_A, CtrlAirspace.Class_B, CtrlAirspace.Class_D, CtrlAirspace.Class_E, CtrlAirspace.Class_G], AirspaceColor.CONTROLLED.toString(), [CtrlAirspace.Control_Area, CtrlAirspace.Control_Zone, CtrlAirspace.Terminal_Area], ['match', ['get', 'caspcClass'], [2 // Class C
      ], AirspaceColor.CLASS_C.toString(), [0,
      // Class A
      1,
      // Class B
      3,
      // Class D
      4,
      // Class E
      6 // Class G
      ], AirspaceColor.CONTROLLED.toString(), AirspaceColor.DEFAULT.toString()], [CtrlAirspace.Airway_Airspace, CtrlAirspace.Radar_Zone, CtrlAirspace.Special_Rules, CtrlAirspace.Terminal_Control_Area, CtrlAirspace.Military_Terminal_Area, CtrlAirspace.Positive_Control_Area], AirspaceColor.MISC.toString(), [CtrlAirspace.Air_Traffic_Zone, CtrlAirspace.Traffic_Information_Zone, CtrlAirspace.Traffic_Information_Area], AirspaceColor.ATZ.toString(), [CtrlAirspace.Military_Air_Traffic_Zone, CtrlAirspace.Military_Control_Zone], AirspaceColor.MATZ.toString(), AirspaceColor.DEFAULT.toString()];
    case 'sua':
      return ['match', ['get', 'classDescription'], SuaAirspace.Flight_Information_Service, AirspaceColor.FIS.toString(), SuaAirspace.Prohibited_Area, AirspaceColor.PROHIBITED.toString(), [SuaAirspace.Military_Operations_Area, SuaAirspace.Air_Defense_Identification_Zone], AirspaceColor.MOA.toString(), [SuaAirspace.Alert_Area, SuaAirspace.Caution_Area, SuaAirspace.Training_Area, SuaAirspace.Warning_Area, SuaAirspace.Danger_Area], AirspaceColor.CONTROLLED.toString(), [SuaAirspace.Restricted_Area, SuaAirspace.National_Security_Area], AirspaceColor.RESTRICTED.toString(), [SuaAirspace.Parachute_Jump_Area, SuaAirspace.Aerobatic_Practice_Area, SuaAirspace.Advisory_Area], AirspaceColor.PARACHUTE.toString(), [SuaAirspace.Temporary_Reserve_Area, SuaAirspace.Cross_Border_Area, SuaAirspace.Longterm_Temporary_Flight_Restriction, SuaAirspace.Temporary_Segregated_Area], AirspaceColor.MISC.toString(), [SuaAirspace.Common_Frequency_Zone, SuaAirspace.Radio_Mandatory_Zone], AirspaceColor.RMZ.toString(), SuaAirspace.Flight_Information_Zone, AirspaceColor.ATZ.toString(), SuaAirspace.Frequency_Monitoring_Code, AirspaceColor.FMC.toString(), SuaAirspace.Transponder_Mandatory_Zone, AirspaceColor.TMZ.toString(), AirspaceColor.DEFAULT.toString()];
    default:
      throw new Error('must select airspace type');
  }
};
const getAirspaceIcon = type => {
  switch (type) {
    case 'ctrl':
      return ['case', showFrequency(), ['match', ['get', 'classDescription'], CtrlAirspace.Class_A, MapIcon.Airspace_A_3, CtrlAirspace.Class_B, MapIcon.Airspace_B_3, CtrlAirspace.Class_C, MapIcon.Airspace_C_3, CtrlAirspace.Class_D, MapIcon.Airspace_D_3, CtrlAirspace.Class_E, MapIcon.Airspace_E_3, CtrlAirspace.Class_G, MapIcon.Airspace_G_3, CtrlAirspace.Control_Zone, MapIcon.Airspace_CTR_3, CtrlAirspace.Air_Traffic_Zone, MapIcon.Airspace_ATZ_3, CtrlAirspace.Traffic_Information_Zone, MapIcon.Airspace_TIZ_3, CtrlAirspace.Terminal_Radar_Service_Area, MapIcon.Airspace_T_3, CtrlAirspace.Traffic_Information_Area, MapIcon.Airspace_TIA_3, CtrlAirspace.Military_Control_Zone, MapIcon.Airspace_MCTR_3, CtrlAirspace.Military_Terminal_Area, MapIcon.Airspace_MTMA_3, CtrlAirspace.Military_Air_Traffic_Zone, MapIcon.Airspace_MATZ_3, CtrlAirspace.Terminal_Control_Area, MapIcon.Airspace_TMA_3, CtrlAirspace.Special_Rules, MapIcon.Airspace_SR_3, ''], ['match', ['get', 'classDescription'], CtrlAirspace.Class_A, MapIcon.Airspace_A_2, CtrlAirspace.Class_B, MapIcon.Airspace_B_2, CtrlAirspace.Class_C, MapIcon.Airspace_C_2, CtrlAirspace.Class_D, MapIcon.Airspace_D_2, CtrlAirspace.Class_E, MapIcon.Airspace_E_2, CtrlAirspace.Class_G, MapIcon.Airspace_G_2, CtrlAirspace.Control_Zone, MapIcon.Airspace_CTR_2, CtrlAirspace.Air_Traffic_Zone, MapIcon.Airspace_ATZ_2, CtrlAirspace.Traffic_Information_Zone, MapIcon.Airspace_TIZ_2, CtrlAirspace.Terminal_Radar_Service_Area, MapIcon.Airspace_T_2, CtrlAirspace.Traffic_Information_Area, MapIcon.Airspace_TIA_2, CtrlAirspace.Military_Control_Zone, MapIcon.Airspace_MCTR_2, CtrlAirspace.Military_Terminal_Area, MapIcon.Airspace_MTMA_2, CtrlAirspace.Military_Air_Traffic_Zone, MapIcon.Airspace_MATZ_2, CtrlAirspace.Terminal_Control_Area, MapIcon.Airspace_TMA_2, CtrlAirspace.Special_Rules, MapIcon.Airspace_SR_2, '']];
    case 'sua':
      return ['case', showFrequency(), ['match', ['get', 'classDescription'], SuaAirspace.Alert_Area, MapIcon.Airspace_ALRT_3, SuaAirspace.Advisory_Area, MapIcon.Airspace_ADVS_3, SuaAirspace.National_Security_Area, MapIcon.Airspace_NSA_3, SuaAirspace.Parachute_Jump_Area, MapIcon.Airspace_PJA_3, SuaAirspace.Restricted_Area, MapIcon.Airspace_R_3, SuaAirspace.Prohibited_Area, MapIcon.Airspace_P_3, SuaAirspace.Warning_Area, MapIcon.Airspace_W_3, SuaAirspace.Military_Operations_Area, MapIcon.Airspace_M_3, SuaAirspace.Longterm_Temporary_Flight_Restriction, MapIcon.Airspace_TFR_3, SuaAirspace.Aerobatic_Practice_Area, MapIcon.Airspace_AERO_3, SuaAirspace.Cross_Border_Area, MapIcon.Airspace_CBA_3, SuaAirspace.Common_Frequency_Zone, MapIcon.Airspace_CFZ_3, SuaAirspace.Flight_Information_Zone, MapIcon.Airspace_FIZ_3, SuaAirspace.Frequency_Monitoring_Code, MapIcon.Airspace_FMC_3, SuaAirspace.Mandatory_Broadcast_Zone, MapIcon.Airspace_MBZ_3, SuaAirspace.Control_Mode_C_Airspace, MapIcon.Airspace_MODC_3, SuaAirspace.Radio_Mandatory_Zone, MapIcon.Airspace_RMZ_3, SuaAirspace.Transponder_Mandatory_Zone, MapIcon.Airspace_TMZ_3, ''], ['match', ['get', 'classDescription'], SuaAirspace.Alert_Area, MapIcon.Airspace_ALRT_2, SuaAirspace.Advisory_Area, MapIcon.Airspace_ADVS_2, SuaAirspace.National_Security_Area, MapIcon.Airspace_NSA_2, SuaAirspace.Parachute_Jump_Area, MapIcon.Airspace_PJA_2, SuaAirspace.Restricted_Area, MapIcon.Airspace_R_2, SuaAirspace.Prohibited_Area, MapIcon.Airspace_P_2, SuaAirspace.Warning_Area, MapIcon.Airspace_W_2, SuaAirspace.Military_Operations_Area, MapIcon.Airspace_M_2, SuaAirspace.Longterm_Temporary_Flight_Restriction, MapIcon.Airspace_TFR_2, SuaAirspace.Aerobatic_Practice_Area, MapIcon.Airspace_AERO_2, SuaAirspace.Cross_Border_Area, MapIcon.Airspace_CBA_2, SuaAirspace.Common_Frequency_Zone, MapIcon.Airspace_CFZ_2, SuaAirspace.Flight_Information_Zone, MapIcon.Airspace_FIZ_2, SuaAirspace.Frequency_Monitoring_Code, MapIcon.Airspace_FMC_2, SuaAirspace.Mandatory_Broadcast_Zone, MapIcon.Airspace_MBZ_2, SuaAirspace.Control_Mode_C_Airspace, MapIcon.Airspace_MODC_2, SuaAirspace.Radio_Mandatory_Zone, MapIcon.Airspace_RMZ_2, SuaAirspace.Transponder_Mandatory_Zone, MapIcon.Airspace_TMZ_2, '']];
    case 'artcc':
      throw new Error('ARTCC text is not supported');
    default:
      throw new Error('unknown airspace type');
  }
};
const getPatternIcon = () => {
  return ['match', ['get', 'classDescription'], [ArtccAirspace.ARTCC_FIR], MapIcon.Airspace_ARTCC_FIR, [SuaAirspace.Air_Defense_Identification_Zone], MapIcon.Airspace_Air_Defense_Identification_Zone, [SuaAirspace.Alert_Area], MapIcon.Airspace_Alert_Area, [SuaAirspace.Military_Operations_Area], MapIcon.Airspace_Military_Operations_Area, [SuaAirspace.Nature_Conservation_Area], MapIcon.Airspace_Nature_Conservation_Area, [SuaAirspace.Restricted_Area], MapIcon.Airspace_Restricted_Area, ''];
};
const isHighlighted$2 = highlights => {
  if (highlights == null || highlights.length == 0) {
    return ['boolean', false];
  }
  const expr = ['match', ['id'], highlights.flatMap(airspace => Number(airspace.id)), true, false];
  return expr;
};
const showFrequency = () => {
  // We only want to show the frequency when we have a single one listed
  return ['all', ['has', 'FrequencyList[0].FrequencyInfo.frequency'], ['!', ['has', 'FrequencyList[1].FrequencyInfo.frequency']]];
};
const getTextOffset = () => {
  return ['case', showFrequency(), ['literal', [0.25, 0.1]], ['literal', [0.5, -0.05]]];
};
class AirspacesArtccFillLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-artcc-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ARTCC,
        'type': 'fill',
        'filter': getNonDashedAirspaceFilter('artcc'),
        'paint': {
          // This layer is only used for searching the map for airspaces
          // and should never be visible
          'fill-opacity': 0,
          'fill-outline-color': Color.Transparent.toString()
        }
      });
    }
  }
  setOpacity(_opacity) {
    // NO-OP. Invisible layer. Opacity should always be 0.
  }
}
const OPACITY_SCALE_FACTOR$4 = 0.15;
class AirspacesArtccHighlightFillLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-artcc-highlight-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightAirspace, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ARTCC,
        'type': 'fill',
        'filter': isHighlighted$2(this.mapHighlight.airspace()),
        'paint': {
          'fill-color': getAirspaceColor('artcc')
        }
      });
    }
  }
  show(map, opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesArtccLineBorderLayer only supports numeric opacity.');
    }
    super.show(map);
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, isHighlighted$2(this.mapHighlight.airspace()));
    }
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesArtccLineBorderLayer only supports numeric opacity.');
    }
    super.setOpacity(opacity * OPACITY_SCALE_FACTOR$4);
  }
}
class AirspacesArtccHighlightLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-artcc-highlight';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightAirspace, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ARTCC,
        'type': 'line',
        'filter': isHighlighted$2(this.mapHighlight.airspace()),
        'paint': {
          'line-color': Color.White.toString(),
          'line-width': getHighlightLineWidth('artcc')
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, isHighlighted$2(this.mapHighlight.airspace()));
    }
  }
}
class AirspacesArtccLineBorderLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-artcc-line-border';
    this.LINE_BORDER_WIDTH = 0.25;
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ARTCC,
        'type': 'line',
        'filter': getNonDashedAirspaceFilter('artcc'),
        'paint': {
          'line-color': Color.Black.toString(),
          'line-width': getLineWidth('artcc', this.LINE_BORDER_WIDTH)
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
  show(map, opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesArtccLineBorderLayer only supports numeric opacity.');
    }
    super.show(map);
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesArtccLineBorderLayer only supports numeric opacity.');
    }
    super.setOpacity(getArtccLineOpacity(opacity));
  }
}
class AirspacesArtccLineLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-artcc-line';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ARTCC,
        'type': 'line',
        'filter': getNonDashedAirspaceFilter('artcc'),
        'paint': {
          'line-color': getAirspaceColor('artcc'),
          'line-width': getLineWidth('artcc')
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
  show(map, opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesArtccLineLayer only supports numeric opacity.');
    }
    super.show(map);
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesCtrlHighlightFillLayer only supports numeric opacity.');
    }
    super.setOpacity(getArtccLineOpacity(opacity));
  }
}
class AirspacesArtccPatternedFillLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-artcc-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ARTCC,
        'type': 'fill',
        'filter': getPatternedAirspaceFilter('artcc'),
        'paint': {
          // This layer is only used for searching the map for airspaces
          // and should never be visible
          'fill-opacity': 0,
          'fill-outline-color': Color.Transparent.toString()
        }
      });
    }
  }
  setOpacity(_opacity) {
    // NO-OP. Invisible layer. Opacity should always be 0.
  }
}
class AirspacesArtccPatternedLineLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-artcc-line';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ARTCC,
        'type': 'line',
        'filter': getPatternedAirspaceFilter('artcc'),
        'paint': {
          'line-color': getAirspaceColor('artcc'),
          'line-width': getLineWidth('artcc', 2),
          'line-pattern': getPatternIcon()
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
  show(map, opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesArtccLineLayer only supports numeric opacity.');
    }
    super.show(map);
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesCtrlHighlightFillLayer only supports numeric opacity.');
    }
    super.setOpacity(getArtccLineOpacity(opacity));
  }
}
class AirspacesCtrlDashedFillLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-ctrl-dashed-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ControlledAirspaces,
        'type': 'fill',
        'filter': getDashedAirspaceFilter('ctrl'),
        'paint': {
          'fill-opacity': 0,
          'fill-color': Color.Transparent.toString()
        }
      });
    }
  }
}
class AirspacesCtrlDashedLineLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-ctrl-dashed-line';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ControlledAirspaces,
        'type': 'line',
        'filter': getDashedAirspaceFilter('ctrl'),
        'layout': {
          'line-join': 'round'
        },
        'paint': {
          'line-color': getAirspaceColor('ctrl'),
          'line-width': getLineWidth('ctrl', 0.125),
          'line-dasharray': [3, 0.3]
        }
      });
    }
  }
}
class AirspacesCtrlFillLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-ctrl-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ControlledAirspaces,
        'type': 'fill',
        'filter': getNonDashedAirspaceFilter('ctrl'),
        'paint': {
          // This layer is only used for searching the map for airspaces
          // and should never be visible
          'fill-opacity': 0,
          'fill-outline-color': Color.Transparent.toString()
        }
      });
    }
  }
  setOpacity(_opacity) {
    // NO-OP. Invisible layer. Opacity should always be 0.
  }
}
const OPACITY_SCALE_FACTOR$3 = 0.15;
class AirspacesCtrlHighlightFillLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-ctrl-highlight-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightAirspace, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ControlledAirspaces,
        'type': 'fill',
        'filter': ['boolean', true],
        //AirspaceExpressions.isHighlighted(this.mapHighlight.airspace()),
        'paint': {
          'fill-color': getAirspaceColor('ctrl'),
          'fill-opacity': 0.15
        }
      });
    }
  }
  show(map, opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesCtrlHighlightFillLayer only supports numeric opacity.');
    }
    super.show(map);
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, isHighlighted$2(this.mapHighlight.airspace()));
    }
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesCtrlHighlightFillLayer only supports numeric opacity.');
    }
    return super.setOpacity(opacity * OPACITY_SCALE_FACTOR$3);
  }
}
class AirspacesCtrlHighlightLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-ctrl-highlight';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightAirspace, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ControlledAirspaces,
        'type': 'line',
        'filter': isHighlighted$2(this.mapHighlight.airspace()),
        'paint': {
          'line-color': Color.White.toString(),
          'line-width': getHighlightLineWidth('ctrl')
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, isHighlighted$2(this.mapHighlight.airspace()));
    }
  }
}
class AirspacesCtrlLineBorderLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-ctrl-line-border';
    this.LINE_BORDER_WIDTH = 0.125;
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ControlledAirspaces,
        'type': 'line',
        'filter': getNonDashedAirspaceFilter('ctrl'),
        'paint': {
          'line-color': Color.Black.toString(),
          'line-width': getLineWidth('ctrl', this.LINE_BORDER_WIDTH)
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
}
class AirspacesCtrlLineLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-ctrl-line';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ControlledAirspaces,
        'type': 'line',
        'filter': getNonDashedAirspaceFilter('ctrl'),
        'paint': {
          'line-color': getAirspaceColor('ctrl'),
          'line-width': getLineWidth('ctrl')
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
}
class AirspacesCtrlTextLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-ctrl-text';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.ControlledAirspaces,
        'type': 'symbol',
        'filter': getTextAirspaceFilter('ctrl'),
        'minzoom': ZoomLevel['2NM'],
        'paint': {
          'text-color': Color.White.toString(),
          'text-halo-color': getAirspaceColor('ctrl'),
          'text-halo-width': 1
        },
        'layout': {
          'icon-image': getAirspaceIcon('ctrl'),
          'text-field': getAltitudeText('ctrl'),
          'text-offset': getTextOffset(),
          'text-font': ['Roboto'],
          'text-letter-spacing': 0.1,
          'text-line-height': 1.4,
          'icon-size': 0.31,
          'text-size': 11
        }
      });
    }
  }
}
class AirspacesSuaDashedFillLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-sua-dashed-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.SpecialUseAirspaces,
        'type': 'fill',
        'filter': getDashedAirspaceFilter('sua'),
        'paint': {
          // This layer is only used for searching the map for airspaces
          // and should never be visible
          'fill-opacity': 0,
          'fill-outline-color': Color.Transparent.toString()
        }
      });
    }
  }
  setOpacity(_opacity) {
    // NO-OP. Invisible layer. Opacity should always be 0.
  }
}
class AirspacesSuaDashedLineLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-sua-dashed-line';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.SpecialUseAirspaces,
        'type': 'line',
        'filter': getDashedAirspaceFilter('sua'),
        'paint': {
          'line-color': getAirspaceColor('sua'),
          'line-dasharray': [5, 5],
          'line-width': getLineWidth('sua')
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
}
class AirspacesSuaFillLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-sua-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.SpecialUseAirspaces,
        'type': 'fill',
        'filter': getNonDashedAirspaceFilter('sua'),
        'paint': {
          // This layer is only used for searching the map for airspaces
          // and should never be visible
          'fill-opacity': 0,
          'fill-outline-color': Color.Transparent.toString()
        }
      });
    }
  }
  setOpacity(_opacity) {
    // NO-OP. Invisible layer. Opacity should always be 0.
  }
}
const OPACITY_SCALE_FACTOR$2 = 0.15;
class AirspacesSuaHighlightFillLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-sua-highlight-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightAirspace, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.SpecialUseAirspaces,
        'type': 'fill',
        'filter': ['boolean', true],
        //AirspaceExpressions.isHighlighted(this.mapHighlight.airspace()),
        'paint': {
          'fill-color': getAirspaceColor('sua'),
          'fill-opacity': 0.15
        }
      });
    }
  }
  show(map, opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesSuaHighlightFillLayer only supports numeric opacity.');
    }
    super.show(map);
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, isHighlighted$2(this.mapHighlight.airspace()));
    }
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('AirspacesSuaHighlightFillLayer only supports numeric opacity.');
    }
    super.setOpacity(opacity * OPACITY_SCALE_FACTOR$2);
  }
}
class AirspacesSuaHighlightLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-sua-highlight';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightAirspace, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.SpecialUseAirspaces,
        'type': 'line',
        'filter': isHighlighted$2(this.mapHighlight.airspace()),
        'paint': {
          'line-color': Color.White.toString(),
          'line-width': getHighlightLineWidth('sua')
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, isHighlighted$2(this.mapHighlight.airspace()));
    }
  }
}
class AirspacesSuaLineBorderLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-sua-line-border';
    this.LINE_BORDER_WIDTH = 0.125;
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.SpecialUseAirspaces,
        'type': 'line',
        'filter': getNonDashedAirspaceFilter('sua'),
        'paint': {
          'line-color': Color.Black.toString(),
          'line-width': getLineWidth('sua', this.LINE_BORDER_WIDTH)
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
}
class AirspacesSuaLineLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-sua-line';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.SpecialUseAirspaces,
        'type': 'line',
        'filter': getNonDashedAirspaceFilter('sua'),
        'paint': {
          'line-color': getAirspaceColor('sua'),
          'line-width': getLineWidth('sua')
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
}
class AirspacesSuaPatternedFillLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-sua-fill';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.SpecialUseAirspaces,
        'type': 'fill',
        'filter': getPatternedAirspaceFilter('sua'),
        'paint': {
          // This layer is only used for searching the map for airspaces
          // and should never be visible
          'fill-opacity': 0,
          'fill-outline-color': Color.Transparent.toString()
        }
      });
    }
  }
  setOpacity(_opacity) {
    // NO-OP. Invisible layer. Opacity should always be 0.
  }
}
class AirspacesSuaPatternedLineLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-sua-line';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.SpecialUseAirspaces,
        'type': 'line',
        'filter': getPatternedAirspaceFilter('sua'),
        'paint': {
          'line-color': getAirspaceColor('sua'),
          'line-width': getLineWidth('sua', 2),
          'line-pattern': getPatternIcon()
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
}
class AirspacesSuaTextLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airspaces-sua-text';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airspaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.SpecialUseAirspaces,
        'type': 'symbol',
        'filter': getTextAirspaceFilter('sua'),
        'minzoom': ZoomLevel['2NM'],
        'paint': {
          'text-color': Color.White.toString(),
          'text-halo-color': getAirspaceColor('sua'),
          'text-halo-width': 1
        },
        'layout': {
          'icon-image': getAirspaceIcon('sua'),
          'text-field': getAltitudeText('sua'),
          'text-offset': getTextOffset(),
          'text-font': ['Roboto'],
          'text-letter-spacing': 0.1,
          'text-line-height': 1.4,
          'icon-size': 0.31,
          'text-size': 11
        }
      });
    }
  }
}
class AirspacesLayer extends ChartSearchableLayerGroup {
  constructor() {
    super(...arguments);
    this.artccShouldBeVisible = () => {
      const config = this.mapConfig.content.aeronautical.airspace;
      return config.atcVisible();
    };
    this.ctrlShouldBeVisible = () => {
      const config = this.mapConfig.content.aeronautical.airspace;
      return config.visible() || config.ctrlVisible();
    };
    this.suaShouldBeVisible = () => {
      const config = this.mapConfig.content.aeronautical.airspace;
      return config.visible() || config.suaVisible();
    };
    this.groupInfo = [{
      layer: new AirspacesArtccHighlightLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBePresent: () => this.mapHighlight.airspace().length > 0
    }, {
      layer: new AirspacesArtccHighlightFillLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBePresent: () => this.mapHighlight.airspace().length > 0
    }, {
      layer: new AirspacesCtrlHighlightLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBePresent: () => this.mapHighlight.airspace().length > 0
    }, {
      layer: new AirspacesCtrlHighlightFillLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBePresent: () => this.mapHighlight.airspace().length > 0
    }, {
      layer: new AirspacesSuaHighlightLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBePresent: () => this.mapHighlight.airspace().length > 0
    }, {
      layer: new AirspacesSuaHighlightFillLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBePresent: () => this.mapHighlight.airspace().length > 0
    }, {
      layer: new AirspacesArtccLineBorderLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.artccShouldBeVisible
    }, {
      layer: new AirspacesArtccLineLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.artccShouldBeVisible
    }, {
      layer: new AirspacesArtccPatternedLineLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.artccShouldBeVisible
    }, {
      layer: new AirspacesCtrlDashedLineLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.ctrlShouldBeVisible
    }, {
      layer: new AirspacesCtrlLineBorderLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.ctrlShouldBeVisible
    }, {
      layer: new AirspacesCtrlLineLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.ctrlShouldBeVisible
    }, {
      layer: new AirspacesCtrlTextLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.ctrlShouldBeVisible
    }, {
      layer: new AirspacesSuaDashedLineLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.suaShouldBeVisible
    }, {
      layer: new AirspacesSuaLineBorderLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.suaShouldBeVisible
    }, {
      layer: new AirspacesSuaLineLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.suaShouldBeVisible
    }, {
      layer: new AirspacesSuaPatternedLineLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.suaShouldBeVisible
    }, {
      layer: new AirspacesSuaTextLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: this.suaShouldBeVisible
    },
    // These layers are only used for searching for airspaces on the map
    // and should never be visible
    {
      layer: new AirspacesArtccFillLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: false
    }, {
      layer: new AirspacesArtccPatternedFillLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: false
    }, {
      layer: new AirspacesCtrlFillLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: false
    }, {
      layer: new AirspacesCtrlDashedFillLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: false
    }, {
      layer: new AirspacesSuaFillLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: false
    }, {
      layer: new AirspacesSuaDashedFillLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: false
    }, {
      layer: new AirspacesSuaPatternedFillLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Airspaces),
      shouldBeVisible: false
    }];
  }
  addSource(map) {
    return map.mvtCycleContent.add(MvtCycleContentUser.Airspace, this.content);
  }
  getSource(map) {
    return map.mvtCycleContent.getId(this.content);
  }
  removeSource(map) {
    map.mvtCycleContent.remove(MvtCycleContentUser.Airspace, this.content);
  }
}
let AirspacesService = /*#__PURE__*/(() => {
  class AirspacesService extends ChartSearchableLayerService {
    constructor() {
      super();
      this.images = [MapIcon.Airspace_ARTCC_FIR, MapIcon.Airspace_Air_Defense_Identification_Zone, MapIcon.Airspace_Alert_Area, MapIcon.Airspace_Military_Operations_Area, MapIcon.Airspace_Nature_Conservation_Area, MapIcon.Airspace_Restricted_Area, MapIcon.Airspace_A_2, MapIcon.Airspace_ADVS_2, MapIcon.Airspace_AERO_2, MapIcon.Airspace_ALRT_2, MapIcon.Airspace_ATZ_2, MapIcon.Airspace_B_2, MapIcon.Airspace_C_2, MapIcon.Airspace_CBA_2, MapIcon.Airspace_CFZ_2, MapIcon.Airspace_CTR_2, MapIcon.Airspace_D_2, MapIcon.Airspace_E_2, MapIcon.Airspace_FIZ_2, MapIcon.Airspace_FMC_2, MapIcon.Airspace_G_2, MapIcon.Airspace_M_2, MapIcon.Airspace_MATZ_2, MapIcon.Airspace_MBZ_2, MapIcon.Airspace_MCTR_2, MapIcon.Airspace_MODC_2, MapIcon.Airspace_MTMA_2, MapIcon.Airspace_NSA_2, MapIcon.Airspace_P_2, MapIcon.Airspace_PJA_2, MapIcon.Airspace_R_2, MapIcon.Airspace_RMZ_2, MapIcon.Airspace_SR_2, MapIcon.Airspace_T_2, MapIcon.Airspace_TFR_2, MapIcon.Airspace_TIA_2, MapIcon.Airspace_TIZ_2, MapIcon.Airspace_TMA_2, MapIcon.Airspace_TMZ_2, MapIcon.Airspace_W_2, MapIcon.Airspace_A_3, MapIcon.Airspace_ADVS_3, MapIcon.Airspace_AERO_3, MapIcon.Airspace_ALRT_3, MapIcon.Airspace_ATZ_3, MapIcon.Airspace_B_3, MapIcon.Airspace_C_3, MapIcon.Airspace_CBA_3, MapIcon.Airspace_CFZ_3, MapIcon.Airspace_CTR_3, MapIcon.Airspace_D_3, MapIcon.Airspace_E_3, MapIcon.Airspace_FIZ_3, MapIcon.Airspace_FMC_3, MapIcon.Airspace_G_3, MapIcon.Airspace_M_3, MapIcon.Airspace_MATZ_3, MapIcon.Airspace_MBZ_3, MapIcon.Airspace_MCTR_3, MapIcon.Airspace_MODC_3, MapIcon.Airspace_MTMA_3, MapIcon.Airspace_NSA_3, MapIcon.Airspace_P_3, MapIcon.Airspace_PJA_3, MapIcon.Airspace_R_3, MapIcon.Airspace_RMZ_3, MapIcon.Airspace_SR_3, MapIcon.Airspace_T_3, MapIcon.Airspace_TFR_3, MapIcon.Airspace_TIA_3, MapIcon.Airspace_TIZ_3, MapIcon.Airspace_TMA_3, MapIcon.Airspace_TMZ_3, MapIcon.Airspace_W_3].map(x => MapIconDefinitions.get(x));
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new AirspacesLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.content.airspace.content();
    }
    getPresent() {
      return this.mapConfig.content.aeronautical.airspace.present();
    }
    getVisible() {
      const aeronauticalVisible = this.mapConfig.content.aeronautical.visible();
      const airspacesVisible = this.mapConfig.content.aeronautical.airspace.visible();
      const atcVisible = this.mapConfig.content.aeronautical.airspace.atcVisible();
      const ctrlVisible = this.mapConfig.content.aeronautical.airspace.ctrlVisible();
      const suaVisible = this.mapConfig.content.aeronautical.airspace.suaVisible();
      return aeronauticalVisible && (airspacesVisible || atcVisible || ctrlVisible || suaVisible);
    }
    getOpacity() {
      return this.mapConfig.content.aeronautical.opacity();
    }
    updateImages() {
      const info = this.mapInfo.content.airspace;
      if (info.available() && this.getVisible()) {
        this.mapIcon.add(this.images);
      } else {
        this.mapIcon.remove(this.images);
      }
    }
    static {
      this.ɵfac = function AirspacesService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AirspacesService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AirspacesService,
        factory: AirspacesService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AirspacesService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * An airway label SVG. This is a rectangle
 * with optional triangles on each side depicting
 * directionality.
 */
class AirwayLabel extends BaseSVG {
  constructor(width, height, fillColor, strokeColor, direction) {
    super();
    this.width = width;
    this.height = height;
    this.fillColor = fillColor;
    this.strokeColor = strokeColor;
    this.direction = direction;
    // Calculate actual width (plus some padding for borders)
    this.actualWidth = width + 1 + 2 * height;
    // Calculate actual height (plus some padding for borders)
    this.actualHeight = height + 1;
    this.svg.setAttribute('width', `${this.actualWidth}`);
    this.svg.setAttribute('height', `${this.actualHeight}`);
    this.svg.setAttribute('viewPort', `0 0 ${this.actualWidth} ${this.actualHeight}`);
    const path = document.createElementNS(BaseSVG.svgNamespace, 'path');
    path.setAttribute('d', this.createPathString());
    path.setAttribute('fill', fillColor);
    path.setAttribute('stroke', strokeColor);
    this.svg.appendChild(path);
  }
  createPathString() {
    //     1    2
    //     ______
    //  6 /      \   3
    //    \______/
    //     5    4
    const pathCommands = [];
    const halfHeight = this.height / 2;
    // Set the initial position (point 1)
    let positionX = 0.5 + this.height;
    let positionY = 0.5;
    pathCommands.push(`M ${positionX} ${positionY}`);
    // Create a horizontal line to the right (point 2)
    positionX += this.width;
    pathCommands.push(`L ${positionX} ${positionY}`);
    // Create the right arrow if needed (points 3 and 4)
    if (this.direction && ['both', 'east'].includes(this.direction)) {
      positionX += this.height;
      positionY += halfHeight;
      pathCommands.push(`L ${positionX} ${positionY}`);
      positionX -= this.height;
      positionY += halfHeight;
      pathCommands.push(`L ${positionX} ${positionY}`);
    }
    // Otherwise just create a vertical line (point 4)
    else {
      positionY += this.height;
      pathCommands.push(`L ${positionX} ${positionY}`);
    }
    // Create a horizontal line to the left (point 5)
    positionX -= this.width;
    pathCommands.push(`L ${positionX} ${positionY}`);
    // Create the left arrow if needed
    if (this.direction && ['both', 'west'].includes(this.direction)) {
      positionX -= this.height;
      positionY -= halfHeight;
      pathCommands.push(`L ${positionX} ${positionY}`);
      positionX += this.height;
      positionY -= halfHeight;
      pathCommands.push(`L ${positionX} ${positionY}`);
    }
    // Otherwise just create a vertical line (point 1)
    else {
      positionY -= this.height;
      pathCommands.push(`L ${positionX} ${positionY}`);
    }
    // Close the path
    pathCommands.push('Z');
    const result = pathCommands.join('');
    return result;
  }
}
var AirwayType = /*#__PURE__*/function (AirwayType) {
  AirwayType[AirwayType["Low"] = 0] = "Low";
  AirwayType[AirwayType["High"] = 1] = "High";
  return AirwayType;
}(AirwayType || {});
const getAirwayFilter = (airwayType, followSetting, setting, highPresent, lowPresent) => {
  let showHigh = highPresent;
  let showLow = lowPresent;
  if (followSetting) {
    switch (setting) {
      case AirwayLayerVisibility.Low:
        showHigh = false;
        showLow = true;
        break;
      case AirwayLayerVisibility.High:
        showHigh = true;
        showLow = false;
        break;
      case AirwayLayerVisibility.Both:
        showHigh = true;
        showLow = true;
        break;
      default:
        showHigh = false;
        showLow = false;
    }
  }
  if (showHigh && airwayType === AirwayType.High) {
    return ['in', ['get', 'level'], ['literal', ['H', 'B']]];
  }
  if (showLow && airwayType === AirwayType.Low) {
    return ['in', ['get', 'level'], ['literal', ['L', 'B']]];
  }
  return ['boolean', false];
};
const defineByAirwayLineType = (normal, rnav) => {
  return ['case', isRnavAirwayLine(), rnav, normal];
};
const getAirwayLineWidth = () => {
  return ['let', 'minWidth', defineByAirwayLineType(1, 1), ['let', 'maxWidth', defineByAirwayLineType(3, 3), ['interpolate', ['linear'], ['zoom'], ZoomLevel['20NM'], ['var', 'minWidth'], ZoomLevel['0.1NM'], ['var', 'maxWidth']]]];
};
const isHighlighted$1 = highlights => {
  // Backwards compatibility: Check to see if any highlighted airways match either the
  // airwayId property (old schema) or any of the merged airwayIds (new schema)
  if (highlights == null || highlights.length == 0) {
    return ['boolean', false];
  }
  const expr = ['any', ...highlights.flatMap(val => {
    return [['==', ['get', 'airwayId'], val.airwayId], ...getMergedAirwayPropertyNames('airwayId').map(mergedAirwayId => {
      return ['==', ['get', mergedAirwayId], val.airwayId];
    })];
  })];
  return expr;
};
const isRnavAirwayLine = () => {
  // If the merged airway line type is RNAV, then treat the overall airway line as RNAV
  return ['all', ['has', 'lineType'], ['==', ['get', 'lineType'], 1]];
};
const isSegmentHighlighted = highlights => {
  // Backwards compatibility: Check to see if any of the highlighted airways match the
  // airwayId property (old schema) or any of the merged airwayIds (new schema)
  if (highlights == null || highlights.length == 0) {
    return ['boolean', false];
  }
  const expr = ['any', ...highlights.map(val => {
    return ['all', ['==', ['id'], val.id ?? ''], ['any', ['==', ['get', 'airwayId'], val.airwayId], ...getMergedAirwayPropertyNames('airwayId').map(mergedAirwayId => {
      return ['==', ['get', mergedAirwayId], val.airwayId];
    })]];
  })];
  return expr;
};
const createIconDefs = theme => {
  return [{
    id: MapIcon.Airway_Marker,
    src: makeIcon(theme.airwayLineColor(), theme.airwayOutlineColor())
  }, {
    id: MapIcon.Airway_Marker_East,
    src: makeIcon(theme.airwayLineColor(), theme.airwayOutlineColor(), 'east')
  }, {
    id: MapIcon.Airway_Marker_West,
    src: makeIcon(theme.airwayLineColor(), theme.airwayOutlineColor(), 'west')
  }, {
    id: MapIcon.Airway_Marker_East_West,
    src: makeIcon(theme.airwayLineColor(), theme.airwayOutlineColor(), 'both')
  }, {
    id: MapIcon.Airway_Marker_RNAV,
    src: makeIcon(Color.LightBlue.toString(), theme.airwayOutlineColor())
  }, {
    id: MapIcon.Airway_Marker_East_RNAV,
    src: makeIcon(Color.LightBlue.toString(), theme.airwayOutlineColor(), 'east')
  }, {
    id: MapIcon.Airway_Marker_West_RNAV,
    src: makeIcon(Color.LightBlue.toString(), theme.airwayOutlineColor(), 'west')
  }, {
    id: MapIcon.Airway_Marker_East_West_RNAV,
    src: makeIcon(Color.LightBlue.toString(), theme.airwayOutlineColor(), 'both')
  }];
};
const makeIcon = (fillColor, strokeColor, direction) => {
  return new AirwayLabel(28, 10, fillColor, strokeColor, direction).rasterize();
};
const getIconImage$1 = () => {
  // Since MapLibre rotates the icons for us according to the bearing of the line, East is Forward and West is Reverse
  return ['case', ['has', 'directionRestrict'], ['case', ['==', ['get', 'directionRestrict'], 'B'], defineByAirwayLineType(MapIcon.Airway_Marker_East_West, MapIcon.Airway_Marker_East_West_RNAV), ['==', ['get', 'directionRestrict'], 'F'], defineByAirwayLineType(MapIcon.Airway_Marker_East, MapIcon.Airway_Marker_East_RNAV), defineByAirwayLineType(MapIcon.Airway_Marker_West, MapIcon.Airway_Marker_West_RNAV)], defineByAirwayLineType(MapIcon.Airway_Marker, MapIcon.Airway_Marker_RNAV)];
};
const getAirwayIdentifierLabel = () => {
  return ['let', 'mergedName', getAirwayMergedName(), ['case', ['>', ['length', ['var', 'mergedName']], MAXIMUM_AIRWAY_IDENTIFIER_LABEL_LENGTH],
  // If the length of the merged airway name exceeds the maximum supported label length,
  // then clip it after the last airway identifier ending before the maximum length and
  // concatenate '...'
  ['concat', ['slice', ['var', 'mergedName'], 0,
  // The index following the last valid airway identifier can be found by finding the largest
  // index of an airway delimiter character ('-' or ',') before the cutoff point. Assuming airway
  // identifiers have at most 2 characters followed by 3 numbers, then the last delimiter must be
  // within 6 characters of the cutoff position.
  ['max', getIndexIfAirwayIdentifierDelimiterIsAt(MAXIMUM_AIRWAY_IDENTIFIER_LABEL_LENGTH), getIndexIfAirwayIdentifierDelimiterIsAt(MAXIMUM_AIRWAY_IDENTIFIER_LABEL_LENGTH - 1), getIndexIfAirwayIdentifierDelimiterIsAt(MAXIMUM_AIRWAY_IDENTIFIER_LABEL_LENGTH - 2), getIndexIfAirwayIdentifierDelimiterIsAt(MAXIMUM_AIRWAY_IDENTIFIER_LABEL_LENGTH - 3), getIndexIfAirwayIdentifierDelimiterIsAt(MAXIMUM_AIRWAY_IDENTIFIER_LABEL_LENGTH - 4), getIndexIfAirwayIdentifierDelimiterIsAt(MAXIMUM_AIRWAY_IDENTIFIER_LABEL_LENGTH - 5), getIndexIfAirwayIdentifierDelimiterIsAt(MAXIMUM_AIRWAY_IDENTIFIER_LABEL_LENGTH - 6), 0]], '...'], ['var', 'mergedName']]];
};
const getIndexIfAirwayIdentifierDelimiterIsAt = index => {
  return ['let', 'airwayMergedName', getAirwayMergedName(), ['case', [
  // Ensure the index is within the bounds of the merged airway label
  'all', ['<', index, ['length', ['var', 'airwayMergedName']]], ['>=', index, 0]], ['let', 'characterAtIndex', ['coalesce', ['slice', ['var', 'airwayMergedName'], index, index + 1], ''], ['case', ['any', ['==', ['var', 'characterAtIndex'], '-'], ['==', ['var', 'characterAtIndex'], ',']], index, -1]], -1]];
};
const getAirwayMergedName = () => {
  // Backwards compatibility: Attempt to get the merged name property.
  // If it doesn't exist, then we must be using the old feature schema, so fallback to the ident property
  return ['coalesce', ['get', 'mergedName'], ['get', 'ident'], 'UNK'];
};
const getMergedAirwayPropertyNames = property => {
  const mergedPropertyNames = [];
  for (let i = 0; i < MAXIMUM_AIRWAYS_PER_FEATURE; i++) {
    mergedPropertyNames.push(`Airways[${i}].Airways.${property}`);
  }
  return mergedPropertyNames;
};
class AirwayBlackSegmentLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airway-black-segment';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightAirways, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': isSegmentHighlighted(this.mapHighlight.airway()),
        'paint': {
          'line-color': Color.Black.toString(),
          'line-width': 12
        },
        'layout': {
          'line-cap': 'round'
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, isSegmentHighlighted(this.mapHighlight.airway()));
    }
  }
}
class AirwayHighlightSegmentLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airway-highlight-segment';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightAirways, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': isSegmentHighlighted(this.mapHighlight.airway()),
        'paint': {
          'line-color': Color.Magenta.toString(),
          'line-width': 5
        },
        'layout': {
          'line-cap': 'round'
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, isSegmentHighlighted(this.mapHighlight.airway()));
    }
  }
}
class AirwayHighlightLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airway-highlight';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightAirways, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': isHighlighted$1(this.mapHighlight.airway()),
        'paint': {
          'line-color': Color.Magenta.toString(),
          'line-width': 5
        },
        'layout': {
          'line-cap': 'round'
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, isHighlighted$1(this.mapHighlight.airway()));
    }
  }
}
class AirwayLineLayer extends ChartSearchableSubLayer {
  constructor(injector, radialMenuMapService, sourceLayerId, layerType, airwayType) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.airwayType = airwayType;
    this.LAYER_PREFIX = 'airway-line';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Airways, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': this.getFilter(),
        'paint': {
          'line-opacity': 0,
          'line-color': defineByAirwayLineType(this.mapTheme.airwayLineColor(), Color.LightBlue.toString()),
          'line-width': getAirwayLineWidth()
        },
        'layout': {
          'line-cap': 'round',
          'line-sort-key': defineByAirwayLineType(0, 1)
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, defineByAirwayLineType(this.mapTheme.airwayLineColor(), Color.LightBlue.toString()));
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  getFilter() {
    const config = this.mapConfig.content.aeronautical.airway;
    const setting = this.mapSettings.airwayVisibility;
    return getAirwayFilter(this.airwayType, config.followSetting(), setting(), config.highPresent(), config.lowPresent());
  }
}
class AirwayTextLayer extends ChartSearchableSubLayer {
  constructor(injector, radialMenuMapService, sourceLayerId, layerType, airwayType) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.airwayType = airwayType;
    this.LAYER_PREFIX = 'airway-text';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      const theme = this.mapTheme;
      map.addLayer(LayerPriority.AirwaysText, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(),
        'minzoom': ZoomLevel['5NM'],
        'paint': {
          'text-color': theme.textColor(),
          'text-halo-color': theme.textHaloColor(),
          'text-halo-width': 1
        },
        'layout': {
          'icon-image': getIconImage$1(),
          'icon-text-fit': 'width',
          'icon-text-fit-padding': [0, 22, 0, 22],
          'text-max-width': Infinity,
          'text-field': ['format',
          // a hack to offset the text using a conditional newline instead of 'text-offset' and
          // 'text-anchor', as the text and icon are linked when `icon-text-fit` is set
          ['case', ['has', 'mea'], '\n', ''], getAirwayIdentifierLabel(), '\n', ['coalesce', ['get', 'mea'], ''], {
            'font-scale': 0.9
          }],
          'text-font': ['Roboto'],
          'text-transform': 'uppercase',
          'text-line-height': 1.4,
          'text-size': 8,
          'symbol-placement': 'line'
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerSymbolProperties(this.layerId, {
        textHaloColor: this.mapTheme.airwayTextHaloColor(),
        textColor: this.mapTheme.airwayTextColor()
      });
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  getFilter() {
    const config = this.mapConfig.content.aeronautical.airway;
    const setting = this.mapSettings.airwayVisibility;
    return getAirwayFilter(this.airwayType, config.followSetting(), setting(), config.highPresent(), config.lowPresent());
  }
}
class AirwayWhiteSegmentLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airway-white-segment';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightAirways, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': isSegmentHighlighted(this.mapHighlight.airway()),
        'paint': {
          'line-color': Color.White.toString(),
          'line-width': 9
        },
        'layout': {
          'line-cap': 'round'
        }
      });
    }
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, isSegmentHighlighted(this.mapHighlight.airway()));
    }
  }
}
const AIRWAY_SOURCE_LAYER = 'airways';
class AirwayLayer extends ChartSearchableLayerGroup {
  constructor() {
    super(...arguments);
    this.highAirwayShouldBeVisible = () => {
      const config = this.mapConfig.content.aeronautical.airway;
      const setting = this.mapSettings.airwayVisibility;
      if (config.followSetting()) {
        switch (setting()) {
          case AirwayLayerVisibility.High:
          case AirwayLayerVisibility.Both:
            return true;
          default:
            return false;
        }
      }
      return config.highVisible();
    };
    this.highAirwayShouldBePresent = () => {
      const config = this.mapConfig.content.aeronautical.airway;
      const setting = this.mapSettings.airwayVisibility;
      if (config.followSetting()) {
        switch (setting()) {
          case AirwayLayerVisibility.High:
          case AirwayLayerVisibility.Both:
            return true;
          default:
            return false;
        }
      }
      return config.highPresent();
    };
    this.lowAirwayShouldBeVisible = () => {
      const config = this.mapConfig.content.aeronautical.airway;
      const setting = this.mapSettings.airwayVisibility;
      if (config.followSetting()) {
        switch (setting()) {
          case AirwayLayerVisibility.Low:
          case AirwayLayerVisibility.Both:
            return true;
          default:
            return false;
        }
      }
      return config.lowVisible();
    };
    this.lowAirwayShouldBePresent = () => {
      const config = this.mapConfig.content.aeronautical.airway;
      const setting = this.mapSettings.airwayVisibility;
      if (config.followSetting()) {
        switch (setting()) {
          case AirwayLayerVisibility.Low:
          case AirwayLayerVisibility.Both:
            return true;
          default:
            return false;
        }
      }
      return config.lowPresent();
    };
    this.groupInfo = [{
      layer: new AirwayHighlightLayer(this.injector, this.radialMenuMapService, AIRWAY_SOURCE_LAYER, LayerType.Airways),
      shouldBeVisible: () => this.mapHighlight.airway().length > 0
    }, {
      layer: new AirwayBlackSegmentLayer(this.injector, this.radialMenuMapService, AIRWAY_SOURCE_LAYER, LayerType.Airways),
      shouldBeVisible: () => this.mapHighlight.airway().length > 0
    }, {
      layer: new AirwayWhiteSegmentLayer(this.injector, this.radialMenuMapService, AIRWAY_SOURCE_LAYER, LayerType.Airways),
      shouldBeVisible: () => this.mapHighlight.airway().length > 0
    }, {
      layer: new AirwayHighlightSegmentLayer(this.injector, this.radialMenuMapService, AIRWAY_SOURCE_LAYER, LayerType.Airways),
      shouldBeVisible: () => this.mapHighlight.airway().length > 0
    }, {
      layer: new AirwayLineLayer(this.injector, this.radialMenuMapService, AIRWAY_SOURCE_LAYER, LayerType.Airways, AirwayType.Low),
      shouldBeVisible: this.lowAirwayShouldBeVisible,
      shouldBePresent: this.lowAirwayShouldBePresent
    }, {
      layer: new AirwayLineLayer(this.injector, this.radialMenuMapService, AIRWAY_SOURCE_LAYER, LayerType.Airways, AirwayType.High),
      shouldBeVisible: this.highAirwayShouldBeVisible,
      shouldBePresent: this.highAirwayShouldBePresent
    }, {
      layer: new AirwayTextLayer(this.injector, this.radialMenuMapService, AIRWAY_SOURCE_LAYER, LayerType.Airways, AirwayType.Low),
      shouldBeVisible: this.lowAirwayShouldBeVisible,
      shouldBePresent: this.lowAirwayShouldBePresent
    }, {
      layer: new AirwayTextLayer(this.injector, this.radialMenuMapService, AIRWAY_SOURCE_LAYER, LayerType.Airways, AirwayType.High),
      shouldBeVisible: this.highAirwayShouldBeVisible,
      shouldBePresent: this.highAirwayShouldBePresent
    }];
  }
  addSource(map) {
    return map.mvtCycleContent.add(MvtCycleContentUser.Airways, this.content);
  }
  getSource(map) {
    return map.mvtCycleContent.getId(this.content);
  }
  removeSource(map) {
    return map.mvtCycleContent.remove(MvtCycleContentUser.Airways, this.content);
  }
}
let AirwayService = /*#__PURE__*/(() => {
  class AirwayService extends ChartSearchableLayerService {
    constructor() {
      super();
      effect(() => this.updateLayers());
      effect(() => this.updateImages());
    }
    createLayer(content) {
      return new AirwayLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.content.airway.content();
    }
    getPresent() {
      const config = this.mapConfig.content.aeronautical.airway;
      return config.followSetting() ? this.mapSettings.airwayVisibility() !== AirwayLayerVisibility.Off : config.highPresent() || config.lowPresent();
    }
    getVisible() {
      const config = this.mapConfig.content.aeronautical;
      return config.airway.followSetting() ? this.mapSettings.airwayVisibility() !== AirwayLayerVisibility.Off : config.visible() && (config.airway.highVisible() || config.airway.lowVisible());
    }
    getOpacity() {
      return this.mapConfig.content.aeronautical.opacity();
    }
    updateImages() {
      const info = this.mapInfo.content.airway;
      const icons = createIconDefs(this.mapTheme);
      if (info.available() && this.getPresent()) {
        this.mapIcon.update(icons);
      } else {
        this.mapIcon.remove(icons);
      }
    }
    static {
      this.ɵfac = function AirwayService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AirwayService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AirwayService,
        factory: AirwayService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AirwayService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const LAYER_PREFIX$5 = 'background';
class BackgroundLayer extends Layer {
  constructor() {
    super(...arguments);
    this.theme = this.mapTheme;
  }
  hide() {
    const map = this.map();
    if (map != null) {
      if (this.layerId != null) {
        map.removeLayer(this.layerId);
        this.layerId = undefined;
      }
    }
  }
  show() {
    const map = this.map();
    if (map != null) {
      this.layerId = map.layerIds.next(LAYER_PREFIX$5);
      map.addLayer(LayerPriority.Background, {
        id: this.layerId,
        type: 'background',
        paint: {
          'background-color': this.theme.background()
        }
      });
    }
  }
  update() {
    const map = this.map();
    if (map != null && this.layerId != null) {
      map.setLayerColor(this.layerId, this.theme.background());
    }
  }
  visible() {
    return this.map()?.hasLayer(this.layerId) ?? false;
  }
}
let BackgroundService = /*#__PURE__*/(() => {
  class BackgroundService {
    constructor() {
      this.injector = inject(Injector);
      this.mapConfig = inject(MapConfigHandler);
      this.radialMenuMapService = inject(RadialMenuMapService);
      const backgroundConfig = this.mapConfig.content.background;
      effect(() => {
        if (backgroundConfig.available()) {
          if (this.layer == null) {
            this.layer = new BackgroundLayer(this.injector, this.radialMenuMapService);
          }
          if (backgroundConfig.visible()) {
            if (!this.layer.visible()) {
              this.layer.show();
            } else {
              this.layer.update();
            }
          } else {
            if (this.layer.visible()) {
              this.layer.hide();
            }
          }
        } else {
          this.layer = undefined;
        }
      });
    }
    static {
      this.ɵfac = function BackgroundService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BackgroundService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BackgroundService,
        factory: BackgroundService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BackgroundService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * A circle SVG.
 */
class Circle extends BaseSVG {
  constructor(dim, radius, fillColor, strokeColor) {
    super();
    this.dim = dim;
    this.radius = radius;
    this.fillColor = fillColor;
    this.strokeColor = strokeColor;
    this.svg.setAttribute('width', `${dim}`);
    this.svg.setAttribute('height', `${dim}`);
    this.svg.setAttribute('viewPort', `0 0 ${dim} ${dim}`);
    const circle = document.createElementNS(BaseSVG.svgNamespace, 'circle');
    const center = Math.floor(dim / 2);
    circle.setAttribute('cy', `${center}`);
    circle.setAttribute('cx', `${center}`);
    circle.setAttribute('r', `${radius}`);
    circle.setAttribute('fill', fillColor);
    circle.setAttribute('stroke', strokeColor);
    this.svg.appendChild(circle);
  }
}

/**
 * Class to draw railroad tracks
 */
class RailTrack extends BaseSVG {
  constructor(color) {
    super();
    // Set the height and width
    const width = 16;
    const height = width;
    this.svg.setAttribute('width', `${width}`);
    this.svg.setAttribute('height', `${height}`);
    this.svg.setAttribute('viewPort', `0 0 ${width} ${height}`);
    // Create the path
    const path = document.createElementNS(BaseSVG.svgNamespace, 'path');
    path.setAttribute('d', this.createPathString(width, height));
    path.setAttribute('fill', color);
    path.setAttribute('stroke', color);
    this.svg.appendChild(path);
  }
  createPathString(width, height) {
    // This is drawing a rail track that can be repeated
    const pathCommands = [];
    // Set the initial position (left side)
    pathCommands.push(`M 0 ${height / 2}`);
    // Draw the left part of the line
    pathCommands.push(`h ${width / 2}`);
    // Draw the line up
    pathCommands.push(`v ${height / 2}`);
    // Draw the line down
    pathCommands.push(`v -${height}`);
    // Draw the line back up to the middle
    pathCommands.push(`v ${height / 2}`);
    // Draw the right part of the line
    pathCommands.push(`h ${width / 2}`);
    // Close the path
    pathCommands.push(`Z`);
    const result = pathCommands.join('');
    return result;
  }
}

/**
 * Observes one or more signals to provide an indication the signal has changed.
 * This can be useful when many signals are used in a single `effect` and you
 * need to take different actions depending on what signals changed.
 *
 * @example
 *
 * const alpha = signal(0);
 * const bravo = signal(false);
 * const observer = new SignalObserver(alpha, bravo);
 * effect(() => {
 *   if (observer.changed()) {
 *     // take this action when alpha or bravo signals have changed
 *   }
 * })
 */
class SignalObserver {
  constructor(...signals) {
    this.info = [];
    for (const signal of signals) {
      this.info.push({
        signal,
        previous: signal()
      });
    }
  }
  /**
   * Returns true if any of the signals has changed since the last
   * `changed` call.
   */
  changed() {
    let result = false;
    for (const info of this.info) {
      const now = info.signal();
      if (now != info.previous) {
        info.previous = now;
        result = true;
      }
    }
    return result;
  }
}
class CityNamesLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'base-map-cities';
    this.config = this.mapConfig.content.baseMap;
    this.theme = this.mapTheme;
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.BaseMapPlaces, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.Places,
        'type': 'symbol',
        'filter': this.getFilter(),
        'paint': {
          'text-color': this.theme.textColor(),
          'text-halo-color': this.theme.textHaloColor(),
          'text-halo-width': this.theme.textHaloWidth()
        },
        'layout': {
          'icon-image': MapIcon.BaseMap_CityDot,
          'symbol-sort-key': ['get', 'rank'],
          'symbol-z-order': 'source',
          'text-anchor': 'bottom-left',
          'text-field': ['get', 'name_en'],
          'text-font': ['Roboto'],
          'text-justify': 'left',
          'text-letter-spacing': 0.2,
          'text-offset': [0.3, 0],
          'text-transform': 'uppercase',
          'text-size': 10
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
      map.setLayerSymbolProperties(this.layerId, {
        textColor: this.theme.textColor(),
        textHaloColor: this.theme.textHaloColor(),
        textHaloWidth: this.theme.textHaloWidth()
      });
    }
  }
  getFilter() {
    return ['case', CityNamesLayer.isLargeCity(), true,
    // large cities are always visible
    CityNamesLayer.isMediumCity(), ['>=', ['zoom'], ZoomLevel['100NM']], CityNamesLayer.isSmallCity(), ['>=', ['zoom'], this.config.smallCityMinZoom()], CityNamesLayer.isSmallTown(), ['>=', ['zoom'], this.config.smallTownMinZoom()], false // don't show anything else
    ];
  }
  static isLargeCity() {
    return ['all', ['==', ['get', 'class'], 'city'], ['<=', ['get', 'rank'], 4]];
  }
  static isMediumCity() {
    return ['all', ['==', ['get', 'class'], 'city'], ['>', ['get', 'rank'], 5], ['<=', ['get', 'rank'], 8]];
  }
  static isSmallCity() {
    return ['==', ['get', 'class'], 'town'];
  }
  static isSmallTown() {
    return ['==', ['get', 'class'], 'village'];
  }
}
class CountryBordersLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'base-map-country-borders';
    this.theme = this.mapTheme;
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.BaseMapBordersAndRoads, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.Boundaries,
        'type': 'line',
        'filter': ['all', ['==', ['get', 'admin_level'], 2], ['!=', ['get', 'maritime'], 1]],
        'paint': {
          'line-color': this.theme.countryBordersColor()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, this.theme.countryBordersColor());
    }
  }
}
class LandUseLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'base-map-land-use';
    this.theme = this.mapTheme;
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.LandUse, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.LandUse,
        'type': 'fill',
        'minzoom': ZoomLevel['50NM'],
        'paint': {
          'fill-color': this.theme.landUseColor()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, this.theme.landUseColor());
    }
  }
}
class ParksLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'base-map-parks';
    this.theme = this.mapTheme;
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.BaseMapBordersAndRoads, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.LandCover,
        'type': 'fill',
        'minzoom': ZoomLevel['50NM'],
        'paint': {
          'fill-color': this.theme.parkColor()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, this.theme.parkColor());
    }
  }
}
class RailTracksLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'base-map-rail-tracks';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.BaseMapBordersAndRoads, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.Transportation,
        'type': 'line',
        'filter': ['==', ['get', 'class'], 'rail'],
        'paint': {
          'line-pattern': MapIcon.BaseMap_RailTracks,
          'line-width': 5
        }
      });
    }
  }
  _update(_map) {
    // no action needed
  }
}
class RoadsLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'base-map-roads';
    this.theme = this.mapTheme;
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.BaseMapBordersAndRoads, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.Transportation,
        'type': 'line',
        'filter': this.getFilter(),
        'paint': {
          'line-color': this.theme.roadColor(),
          'line-width': 0.5
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, this.theme.roadColor());
    }
  }
  getFilter() {
    return ['match', ['get', 'class'], ['motorway', 'trunk'], ['>=', ['zoom'], ZoomLevel['20NM']], false];
  }
}
class StateBordersLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'base-map-state-borders';
    this.theme = this.mapTheme;
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.BaseMapBordersAndRoads, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.Boundaries,
        'type': 'line',
        'filter': ['all', ['==', ['get', 'admin_level'], 4], ['!=', ['get', 'maritime'], 1]],
        'minzoom': ZoomLevel['200NM'],
        'paint': {
          'line-color': this.theme.stateBordersColor(),
          'line-dasharray': [6, 6],
          'line-width': 0.5
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, this.theme.stateBordersColor());
    }
  }
}
class WaterBodiesLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'base-map-water-bodies';
    this.theme = this.mapTheme;
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.BaseMapWater, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.Water,
        'type': 'fill',
        'filter': this.getFilter(),
        'paint': {
          'fill-color': this.theme.waterColor()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, this.theme.waterColor());
    }
  }
  getFilter() {
    return ['match', ['get', 'class'], ['pond'], false, true];
  }
}
class WaterWaysLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'base-map-water-ways';
    this.theme = this.mapTheme;
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.BaseMapWater, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': MvtCycleContentSourceLayer.WaterWay,
        'type': 'line',
        'minzoom': ZoomLevel['10NM'],
        'paint': {
          'line-color': this.theme.waterColor()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, this.theme.waterColor());
    }
  }
}
class BaseMapLayer extends LayerGroup {
  constructor() {
    super(...arguments);
    this.config = this.mapConfig.content.baseMap;
    this.groupInfo = [{
      layer: new CityNamesLayer(this.injector, this.radialMenuMapService),
      shouldBeVisible: this.config.cityNames
    }, {
      layer: new CountryBordersLayer(this.injector, this.radialMenuMapService),
      shouldBeVisible: this.config.countryBorders
    }, {
      layer: new LandUseLayer(this.injector, this.radialMenuMapService),
      shouldBeVisible: this.config.landUse
    }, {
      layer: new ParksLayer(this.injector, this.radialMenuMapService),
      shouldBeVisible: this.config.parks
    }, {
      layer: new RailTracksLayer(this.injector, this.radialMenuMapService),
      shouldBeVisible: this.config.railTracks
    }, {
      layer: new RoadsLayer(this.injector, this.radialMenuMapService),
      shouldBeVisible: this.config.roads
    }, {
      layer: new StateBordersLayer(this.injector, this.radialMenuMapService),
      shouldBeVisible: this.config.stateBorders
    }, {
      layer: new WaterBodiesLayer(this.injector, this.radialMenuMapService),
      shouldBeVisible: this.config.waterBodies
    }, {
      layer: new WaterWaysLayer(this.injector, this.radialMenuMapService),
      shouldBeVisible: this.config.waterWays
    }];
  }
  addSource(map) {
    return map.mvtCycleContent.add(MvtCycleContentUser.BaseMap, this.content);
  }
  getSource(map) {
    return map.mvtCycleContent.getId(this.content);
  }
  removeSource(map) {
    map.mvtCycleContent.remove(MvtCycleContentUser.BaseMap, this.content);
  }
}
let BaseMapService = /*#__PURE__*/(() => {
  class BaseMapService extends LayerService {
    constructor() {
      super();
      this.config = this.mapConfig.content.baseMap;
      this.info = this.mapInfo.content.baseMap;
      this.theme = this.mapTheme;
      this.cityDotObserver = new SignalObserver(this.theme.textColor, this.theme.textHaloColor);
      this.railTrackObserver = new SignalObserver(this.theme.railColor);
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new BaseMapLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.info.content();
    }
    getVisible() {
      return this.config.visible();
    }
    updateImageCityDot() {
      const id = MapIcon.BaseMap_CityDot;
      if (this.info.available() && this.config.visible() && this.config.cityNames()) {
        if (this.cityDotObserver.changed() || !this.mapIcon.has(id)) {
          const svg = new Circle(9, 3, this.theme.textHaloColor(), this.theme.textColor());
          const src = svg.rasterize();
          this.mapIcon.update([{
            id,
            src
          }]);
        }
      } else {
        this.mapIcon.remove([{
          id
        }]);
      }
    }
    updateImageRailTracks() {
      const id = MapIcon.BaseMap_RailTracks;
      if (this.info.available() && this.config.visible() && this.config.railTracks()) {
        if (this.railTrackObserver.changed() || !this.mapIcon.has(id)) {
          const svg = new RailTrack(this.theme.railColor());
          const src = svg.rasterize();
          this.mapIcon.update([{
            id,
            src
          }]);
        }
      }
    }
    updateImages() {
      this.updateImageCityDot();
      this.updateImageRailTracks();
    }
    static {
      this.ɵfac = function BaseMapService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BaseMapService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BaseMapService,
        factory: BaseMapService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BaseMapService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * This is a class with a single source and a single layer
 * that is either visible or not visible
 */
class SimpleLayer extends Layer {
  constructor(injector, radialMenuMapService, content, radialMenuLayer) {
    super(injector, radialMenuMapService, content);
    this.layerType = {
      radialLayerType: undefined
    };
    this.layerType.radialLayerType = radialMenuLayer;
  }
  hide() {
    const map = this.map();
    if (map != null) {
      if (this.layerId != null) {
        if (this.layerType.radialLayerType != null) {
          this.radialMenuMapService.removeFromMap(this.layerType.radialLayerType, this.layerId);
        }
        map.removeLayer(this.layerId);
        this.layerId = undefined;
      }
      if (this.sourceId != null) {
        map.removeSource(this.sourceId);
        this.sourceId = undefined;
      }
    }
  }
  show() {
    const map = this.map();
    if (map != null) {
      this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
      this.addSource(map, this.content, this.sourceId);
      this.layerId = map.layerIds.next(this.LAYER_PREFIX);
      this.addLayer(map, this.sourceId, this.layerId);
      if (this.layerType.radialLayerType != null) {
        this.radialMenuMapService.addToMap(this.layerType.radialLayerType, this.layerId);
      }
    }
  }
  update() {
    // no action required
  }
  visible() {
    return this.map()?.hasLayer(this.layerId) ?? false;
  }
}
class GroundSatelliteLayer extends SimpleLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'ground-satellite';
    this.SOURCE_PREFIX = 'ground-satellite';
  }
  addSource(map, content, sourceId) {
    map.addSource(sourceId, {
      type: 'raster',
      tiles: [`${content.urlBase}/{z}/{x}/{y}.jpg`],
      scheme: 'xyz',
      minzoom: 0,
      maxzoom: 19,
      tileSize: 256
    });
  }
  addLayer(map, sourceId, layerId) {
    map.addLayer(LayerPriority.BaseMap, {
      id: layerId,
      source: sourceId,
      type: 'raster'
    });
  }
}
let GroundSatelliteService = /*#__PURE__*/(() => {
  class GroundSatelliteService extends LayerService {
    constructor() {
      super();
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new GroundSatelliteLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.content.groundSatellite.content();
    }
    getVisible() {
      return this.mapConfig.content.groundSatellite.visible();
    }
    static {
      this.ɵfac = function GroundSatelliteService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GroundSatelliteService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GroundSatelliteService,
        factory: GroundSatelliteService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GroundSatelliteService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class MRUCache {
  constructor(limit) {
    this.limit = limit;
    this.map = new Map();
    this.order = [];
    if (limit <= 0) {
      throw new Error(`MRUCache with a limit of ${limit} is useless!`);
    }
  }
  get(key) {
    if (!this.map.has(key)) {
      return undefined;
    }
    this.tagKey(key);
    return this.map.get(key);
  }
  set(key, value) {
    // Update existing keys
    if (this.map.has(key)) {
      this.map.set(key, value);
      this.tagKey(key);
      return;
    }
    // Check the limit and remove old stuff if needed
    if (this.map.size >= this.limit) {
      const removedKey = this.order.shift();
      if (removedKey != null) {
        this.map.delete(removedKey);
      }
    }
    // Save the new thing
    this.map.set(key, value);
    this.order.push(key);
  }
  get size() {
    return this.map.size;
  }
  tagKey(key) {
    // Move key to back of order array
    this.order = this.order.filter(k => k !== key);
    this.order.push(key);
  }
}
class ColorGradient {
  constructor(startColor, endColor, minValue, maxValue) {
    this.startColor = startColor;
    this.endColor = endColor;
    this.range = [minValue, maxValue];
  }
  colorAt(index) {
    if (index < this.range[0]) {
      return this.startColor;
    } else if (index > this.range[1]) {
      return this.endColor;
    }
    const range = this.range[1] - this.range[0];
    const color = new Color((this.endColor.getR() - this.startColor.getR()) / range * (index - this.range[0]) + this.startColor.getR(), (this.endColor.getG() - this.startColor.getG()) / range * (index - this.range[0]) + this.startColor.getG(), (this.endColor.getB() - this.startColor.getB()) / range * (index - this.range[0]) + this.startColor.getB());
    return color;
  }
}
const METERS_TO_FEET = 3.28084;
const BASE_HEIGHT = -10000;
const MAX_CACHE_LENGTH = 50;
const MIN_HEIGHT = -2000;
const MAX_HEIGHT = 30000;
class TopographyProtocol {
  static {
    this.TOPO_NAME = 'topography';
  }
  static {
    this.TOPO_PREFIX = `${this.TOPO_NAME}://`;
  }
  static {
    this.TERRAIN_NAME = 'terrain';
  }
  static {
    this.TERRAIN_PREFIX = `${this.TERRAIN_NAME}://`;
  }
  async downloadImage(url) {
    let downloadedImage = this.imageCache.get(url);
    if (downloadedImage == null) {
      downloadedImage = new Promise((resolve, reject) => {
        this.http.get(url, {
          responseType: 'blob'
        }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: blob => {
            const imageUrl = URL.createObjectURL(blob);
            const image = new Image();
            image.crossOrigin = 'anonymous'; // needed to avoid "Tainted canvases may not be exported."
            image.onabort = () => {
              reject(`image loading aborted: ${url}`);
            };
            image.onerror = () => {
              reject(`image loading failed: ${url}`);
            };
            image.onload = () => {
              resolve(image);
            };
            image.src = imageUrl;
          },
          error: error => {
            console.error(error);
            reject(error);
          }
        });
      });
      this.imageCache.set(url, downloadedImage);
    }
    return downloadedImage;
  }
  async prepareImage(url) {
    let rawImageData = this.rawImageCache.get(url);
    if (rawImageData == null) {
      rawImageData = new Promise((resolve, reject) => {
        this.downloadImage(url).then(image => {
          if (this.context == null) {
            reject('unable to get canvas context');
            return;
          }
          this.canvas.width = image.width;
          this.canvas.height = image.height;
          this.context.drawImage(image, 0, 0);
          const imageData = this.context.getImageData(0, 0, this.canvas.width, this.canvas.height);
          this.canvas.toBlob(blob => {
            if (blob == null) {
              reject('unable to convert canvas to blob');
              return;
            }
            blob.arrayBuffer().then(arrayBuffer => resolve({
              imageData,
              arrayBuffer
            })).catch(reason => reject(reason));
          });
        }).catch(reason => reject(reason));
      });
      this.rawImageCache.set(url, rawImageData);
    }
    return rawImageData;
  }
  async modifyImage(url) {
    let modImageData = this.modImageCache.get(url);
    if (modImageData == null) {
      modImageData = new Promise((resolve, reject) => {
        this.prepareImage(url).then(({
          imageData: rawImageData
        }) => {
          if (this.context == null) {
            reject('unable to get canvas context');
            return;
          }
          const imageData = new ImageData(rawImageData.width, rawImageData.height);
          for (let offset = 0; offset < rawImageData.data.length; offset += 4) {
            const height = this.getHeight(rawImageData.data, offset);
            const color = this.getColor(height);
            imageData.data[offset] = color.getR();
            imageData.data[offset + 1] = color.getG();
            imageData.data[offset + 2] = color.getB();
            imageData.data[offset + 3] = 255; // alpha
          }
          this.context.putImageData(imageData, 0, 0);
          this.canvas.toBlob(blob => {
            if (blob == null) {
              reject('unable to convert canvas to blob');
              return;
            }
            blob.arrayBuffer().then(value => resolve(value)).catch(reason => reject(reason));
          });
        }).catch(reason => reject(reason));
      });
      this.modImageCache.set(url, modImageData);
    }
    return modImageData;
  }
  constructor() {
    this.destroyRef = inject(DestroyRef);
    this.http = inject(HttpClient);
    this.TOPO_GRADIENTS = this.createGradients();
    this.canvas = document.createElement('canvas');
    this.context = this.canvas.getContext('2d', {
      willReadFrequently: true
    });
    this.currentGradientIndex = 0;
    this.currentGradient = this.TOPO_GRADIENTS[this.currentGradientIndex];
    this.imageCache = new MRUCache(MAX_CACHE_LENGTH);
    this.rawImageCache = new MRUCache(MAX_CACHE_LENGTH);
    this.modImageCache = new MRUCache(MAX_CACHE_LENGTH);
    this.terrainProtocol = params => {
      const url = params.url.replace(TopographyProtocol.TERRAIN_PREFIX, '');
      return new Promise((resolve, reject) => {
        this.prepareImage(url).then(({
          arrayBuffer
        }) => resolve({
          data: arrayBuffer
        })).catch(reason => reject(reason));
      });
    };
    this.topoProtocol = params => {
      const url = params.url.replace(TopographyProtocol.TOPO_PREFIX, '');
      return new Promise((resolve, reject) => {
        this.modifyImage(url).then(arrayBuffer => resolve({
          data: arrayBuffer
        })).catch(reason => reject(reason));
      });
    };
    // Set the default canvas size. Note that this may change when images are downloaded.
    this.canvas.width = 256;
    this.canvas.height = 256;
    ML__default.addProtocol(TopographyProtocol.TOPO_NAME, this.topoProtocol);
    ML__default.addProtocol(TopographyProtocol.TERRAIN_NAME, this.terrainProtocol);
  }
  destroy() {
    ML__default.removeProtocol(TopographyProtocol.TOPO_NAME);
    ML__default.removeProtocol(TopographyProtocol.TERRAIN_NAME);
  }
  getHeight(data, offset) {
    // This is reading the height value encoded in the TerrainRGB file,
    // converting from 0.1m increments to 1m increments, and converting from
    // meters to feet.
    // https://docs.mapbox.com/data/tilesets/reference/mapbox-terrain-dem-v1/
    return (BASE_HEIGHT + (data[offset] * 65536 + data[offset + 1] * 256 + data[offset + 2]) * 0.1) * METERS_TO_FEET;
  }
  getColor(height) {
    // don't assume the data is valid
    if (height <= MIN_HEIGHT) {
      this.currentGradientIndex = 0;
      this.currentGradient = this.TOPO_GRADIENTS[this.currentGradientIndex];
    } else if (height >= MAX_HEIGHT) {
      this.currentGradientIndex = this.TOPO_GRADIENTS.length - 1;
      this.currentGradient = this.TOPO_GRADIENTS[this.currentGradientIndex];
    } else {
      // heights next to each other are probably using the same gradient so
      // use some temporal caching
      while (height > this.currentGradient.max) {
        this.currentGradientIndex += 1;
        this.currentGradient = this.TOPO_GRADIENTS[this.currentGradientIndex];
      }
      while (height < this.currentGradient.min) {
        this.currentGradientIndex -= 1;
        this.currentGradient = this.TOPO_GRADIENTS[this.currentGradientIndex];
      }
    }
    return this.currentGradient.gradient.colorAt(height);
  }
  createGradients() {
    /* Colors are average between GIFD2 and GIFD3 theme with 70% leaning towards GIFD3 */
    const definitions = [{
      height: MIN_HEIGHT,
      color: new Color(0x8C, 0xAB, 0x55)
    }, {
      height: -500,
      color: new Color(0x8C, 0xAB, 0x55)
    }, {
      height: 500,
      color: new Color(0x45, 0x62, 0x23)
    }, {
      height: 2000,
      color: new Color(0xB8, 0xA8, 0x5E)
    }, {
      height: 3000,
      color: new Color(0xB4, 0x92, 0x58)
    }, {
      height: 6000,
      color: new Color(0x8A, 0x66, 0x3A)
    }, {
      height: 8000,
      color: new Color(0x81, 0x52, 0x2D)
    }, {
      height: 10500,
      color: new Color(0x80, 0x4E, 0x29)
    }, {
      height: 27000,
      color: new Color(0x9C, 0x9C, 0x9C)
    }, {
      height: MAX_HEIGHT,
      color: new Color(0xCC, 0xCC, 0xCC)
    }];
    const gradients = [];
    for (let i = 0; i < definitions.length - 1; i++) {
      const min = definitions[i];
      const max = definitions[i + 1];
      const gradient = new ColorGradient(min.color, max.color, min.height, max.height);
      gradients.push({
        min: min.height,
        max: max.height,
        gradient
      });
    }
    return gradients;
  }
}
const LAYER_PREFIX$4 = 'hillshade';
const SOURCE_PREFIX$5 = 'hillshade';
class HillshadeLayer extends Layer {
  hide() {
    const map = this.map();
    if (map != null) {
      if (this.layerId != null) {
        map.removeLayer(this.layerId);
        this.layerId = undefined;
      }
      if (this.sourceId != null) {
        map.removeSource(this.sourceId);
        this.sourceId = undefined;
      }
    }
  }
  show() {
    const map = this.map();
    if (map != null) {
      this.sourceId = map.sourceIds.next(SOURCE_PREFIX$5);
      map.addSource(this.sourceId, {
        type: 'raster-dem',
        tiles: [`${TopographyProtocol.TERRAIN_PREFIX}${this.content.urlBase}/{z}/{x}/{y}.webp`],
        minzoom: 2,
        maxzoom: 9,
        // The terrain tiles are provided as a 256x256 image
        // (at the time of writing). We're purposefully using 512 instead.
        // This results in MapLibre using a more zoomed out tile but stretching
        // it to cover the same geographic area. This results in a slightly
        // blurrier image but with drastically fewer network requests.
        tileSize: 512
      });
      this.layerId = map.layerIds.next(LAYER_PREFIX$4);
      map.addLayer(LayerPriority.Hillshade, {
        id: this.layerId,
        source: this.sourceId,
        type: 'hillshade',
        paint: {
          'hillshade-accent-color': Color.Transparent.toString(),
          'hillshade-highlight-color': Color.Transparent.toString(),
          'hillshade-shadow-color': 'black',
          'hillshade-exaggeration': ['interpolate', ['linear'], ['zoom'],
          // Zoomed out, terrain is not visible
          ZoomLevel['500NM'], 0.0,
          // It stays pretty faded out for a long time
          ZoomLevel['100NM'], 0.2,
          // Zoomed in, terrain is at full strength
          ZoomLevel['5NM'], 0.5]
        }
      });
    }
  }
  update() {
    // no action required
  }
  visible() {
    return this.map()?.hasLayer(this.layerId) ?? false;
  }
  setOpacity(_opacity) {
    // NO-OP: Opacity should always be 1.0.
  }
}
let HillshadeService = /*#__PURE__*/(() => {
  class HillshadeService extends LayerService {
    constructor() {
      super();
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new HillshadeLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.content.hillshade.content();
    }
    getVisible() {
      const visible = this.mapConfig.content.hillshade.visible();
      const activeMapLayer = this.mapSettings.activeMapLayer();
      const vfrTopo = this.mapSettings.vfrTopographyState();
      const ifrTopo = this.mapSettings.ifrTopographyState();
      return visible || activeMapLayer === MapOverlayLayer.IFR && ifrTopo !== TopographyState.OFF || activeMapLayer === MapOverlayLayer.VFR && vfrTopo !== TopographyState.OFF;
    }
    static {
      this.ɵfac = function HillshadeService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || HillshadeService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: HillshadeService,
        factory: HillshadeService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return HillshadeService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var NavAidsType = /*#__PURE__*/function (NavAidsType) {
  NavAidsType["NAMED"] = "Named";
  NavAidsType["RNAV"] = "RNAV";
  NavAidsType["VFR"] = "VFR";
  NavAidsType["CNF"] = "CNF";
  NavAidsType["NDB"] = "NDB";
  NavAidsType["MARKER"] = "Marker";
  NavAidsType["NRS"] = "NRS";
  NavAidsType["COMBINED"] = "Combined";
  return NavAidsType;
}(NavAidsType || {});
const AIRWAY_INCLUSION_ZOOM_LEVEL_10NM = ZoomLevel['10NM'];
const AIRWAY_INCLUSION_ZOOM_LEVEL_5NM = ZoomLevel['5NM'];
const IFR_ENROUTE_LOW_ZOOM_LEVEL = ZoomLevel['50NM'];
const ENROUTE_INTERSECTION_VISIBILITY_ZOOM_LEVEL = ZoomLevel['2NM'];
const TERMINAL_INTERSECTION_VISIBILITY_ZOOM_LEVEL = ZoomLevel['1NM'];
const getFilterVORCompass = (config, settings) => {
  return ['all', ['!=', ['get', 'type'], 'DME'], getFilterVOR(config, settings), ['has', 'mag_var']];
};
const getFilterVORDME = (config, settings) => {
  return ['all', ['any', ['==', ['get', 'type'], 'DME'], ['==', ['get', 'type'], 'VOR/DME']], getFilterVOR(config, settings)];
};
const getFilterVORVORTAC = (config, settings) => {
  return ['all', ['!=', ['get', 'type'], 'DME'], ['!=', ['get', 'type'], 'TACAN'], ['!=', ['get', 'type'], 'VOR/DME'], getFilterVOR(config, settings)];
};
const getFilterVORTACAN = (config, settings) => {
  return ['all', ['==', ['get', 'type'], 'TACAN'], getFilterVOR(config, settings)];
};
const getFilterVOR = (config, settings) => {
  const areLowAirwaysVisible = config.content.aeronautical.airway.followSetting() ? settings.airwayVisibility() === AirwayLayerVisibility.Low : config.content.aeronautical.airway.lowVisible();
  if (areLowAirwaysVisible) {
    return ['all', ['!=', ['get', 'range'], 'Terminal Transmitter'], ['any', ['all', ['==', ['get', 'is_on_enrt_low'], true], ['>=', ['zoom'], AIRWAY_INCLUSION_ZOOM_LEVEL_10NM], ['>=', ['zoom'], IFR_ENROUTE_LOW_ZOOM_LEVEL]], ['>=', ['zoom'], AIRWAY_INCLUSION_ZOOM_LEVEL_5NM]]];
  }
  return ['all', ['!=', ['get', 'range'], 'Terminal Transmitter'], ['>=', ['zoom'], AIRWAY_INCLUSION_ZOOM_LEVEL_5NM]];
};
const getFilterNDB = (config, settings) => {
  const areLowAirwaysVisible = config.content.aeronautical.airway.followSetting() ? settings.airwayVisibility() === AirwayLayerVisibility.Low : config.content.aeronautical.airway.lowVisible();
  if (areLowAirwaysVisible) {
    return ['any', ['all', ['==', ['get', 'is_on_enrt_low'], true], ['>=', ['zoom'], AIRWAY_INCLUSION_ZOOM_LEVEL_10NM], ['>=', ['zoom'], IFR_ENROUTE_LOW_ZOOM_LEVEL]], ['>=', ['zoom'], AIRWAY_INCLUSION_ZOOM_LEVEL_5NM]];
  }
  return ['>=', ['zoom'], AIRWAY_INCLUSION_ZOOM_LEVEL_5NM];
};
const getFilterWPT = (config, settings) => {
  const areHighAirwaysVisible = config.content.aeronautical.airway.followSetting() ? settings.airwayVisibility() === AirwayLayerVisibility.High : config.content.aeronautical.airway.highVisible();
  const areLowAirwaysVisible = config.content.aeronautical.airway.followSetting() ? settings.airwayVisibility() === AirwayLayerVisibility.Low : config.content.aeronautical.airway.lowVisible();
  if (areHighAirwaysVisible) {
    return ['any',
    // Waypoints are visible if
    //  1. The waypoint is on a high airway and the high airway visibility is met OR
    //  2. The waypoint is on a low airway and the low airway visibility is met OR
    //  3. The waypoint is on a high airway and the enroute intersection visibility is met OR
    //  4. The waypoint is on a low airway and the enroute intersection visibility is met OR
    //  5. The waypoint is "on terminal" and the terminal intersection visibility is met
    ['all', ['==', ['get', 'is_on_awy_high'], true], ['>=', ['zoom'], AIRWAY_INCLUSION_ZOOM_LEVEL_10NM], ['>=', ['zoom'], ENROUTE_INTERSECTION_VISIBILITY_ZOOM_LEVEL]], ['all', ['==', ['get', 'is_on_terminal'], true], ['>=', ['zoom'], AIRWAY_INCLUSION_ZOOM_LEVEL_10NM], ['>=', ['zoom'], TERMINAL_INTERSECTION_VISIBILITY_ZOOM_LEVEL]]];
  } else if (areLowAirwaysVisible) {
    return ['any',
    // Waypoints are visible if
    //  1. The waypoint is on a high airway and the high airway visibility is met OR
    //  2. The waypoint is on a low airway and the low airway visibility is met OR
    //  3. The waypoint is on a high airway and the enroute intersection visibility is met OR
    //  4. The waypoint is on a low airway and the enroute intersection visibility is met OR
    //  5. The waypoint is "on terminal" and the terminal intersection visibility is met
    ['all', ['==', ['get', 'is_on_awy_low'], true], ['>=', ['zoom'], AIRWAY_INCLUSION_ZOOM_LEVEL_10NM], ['any', ['>=', ['zoom'], IFR_ENROUTE_LOW_ZOOM_LEVEL], ['>=', ['zoom'], ENROUTE_INTERSECTION_VISIBILITY_ZOOM_LEVEL]]], ['all', ['==', ['get', 'is_on_terminal'], true], ['>=', ['zoom'], AIRWAY_INCLUSION_ZOOM_LEVEL_10NM], ['>=', ['zoom'], TERMINAL_INTERSECTION_VISIBILITY_ZOOM_LEVEL]]];
  }
  return ['any',
  // Waypoints are visible if
  //  1. The waypoint is on a high airway and the high airway visibility is met OR
  //  2. The waypoint is on a low airway and the low airway visibility is met OR
  //  3. The waypoint is on a high airway and the enroute intersection visibility is met OR
  //  4. The waypoint is on a low airway and the enroute intersection visibility is met OR
  //  5. The waypoint is "on terminal" and the terminal intersection visibility is met
  ['all', ['==', ['get', 'is_on_terminal'], true], ['>=', ['zoom'], AIRWAY_INCLUSION_ZOOM_LEVEL_10NM], ['>=', ['zoom'], TERMINAL_INTERSECTION_VISIBILITY_ZOOM_LEVEL]]];
};
const isIntersectionNavAid = () => {
  return ['any', ['boolean', ['==', ['get', 'type'], NavAidsType.NAMED], false], ['boolean', ['==', ['get', 'type'], NavAidsType.COMBINED], false]];
};
class NavaidsNdbLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'navaids-ndb';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.NavigationAids_NDB, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': getFilterNDB(this.mapConfig, this.mapSettings),
        'paint': {
          'icon-opacity': 0,
          // transparent by default
          'text-opacity': 0 // transparent by default
        },
        'layout': {
          'icon-allow-overlap': true,
          'icon-image': this.getIconImage(),
          'icon-size': 0.8,
          'text-field': ['coalesce', ['get', 'identifier'], 'UNK'],
          'text-font': ['Roboto'],
          'text-size': 10,
          'text-optional': true,
          'text-offset': [0, -1.5],
          'text-letter-spacing': 0.1
        }
      });
    }
  }
  update(map) {
    this.updateLayers(map);
    this.updateTheme(map);
  }
  getTextColor() {
    if (this.mapSettings.activeMapLayer() === MapOverlayLayer.VFR && this.mapSettings.vfrTopographyState() === TopographyState.ON) {
      return this.mapTheme.airportTextColor();
    }
    return this.mapTheme.textColor();
  }
  getIconImage() {
    const flightRule = this.mapTheme.flightRule();
    if (flightRule === FlightRule.Ifr) {
      return ['match', ['get', 'type'], 'Marker NDB', MapIcon.Navaid_IFR_NDB_Marker, MapIcon.Navaid_IFR_NDB // NDB is default
      ];
    } else {
      return ['match', ['get', 'type'], 'Marker NDB', MapIcon.Navaid_NDB_Marker, MapIcon.Navaid_NDB // NDB is default
      ];
    }
  }
  updateLayers(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, getFilterNDB(this.mapConfig, this.mapSettings));
    }
  }
  updateTheme(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        iconImage: this.getIconImage(),
        textColor: this.getTextColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
}
class NavaidsVorCompassLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'navaids-vor-compass';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.NavigationAids_VOR, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'minzoom': ZoomLevel['20NM'],
        'maxzoom': ZoomLevel['0.1NM'],
        'filter': getFilterVORCompass(this.mapConfig, this.mapSettings),
        'paint': {
          'icon-opacity': 0 // transparent by default
        },
        'layout': {
          'icon-allow-overlap': true,
          'icon-image': MapIcon.Navaid_VOR_Compass,
          'icon-rotate': ['get', 'mag_var'],
          'icon-size': ['interpolate', ['linear'], ['zoom'],
          // Starts tiny when zoomed out
          ZoomLevel['20NM'], 0.05,
          // Grows in size when zoomed in
          ZoomLevel['0.1NM'], 1.0]
        }
      });
    }
  }
  update(map) {
    this.updateLayers(map);
    this.updateTheme(map);
  }
  updateLayers(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, getFilterVORCompass(this.mapConfig, this.mapSettings));
    }
  }
  updateTheme(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        textColor: this.getTextColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
  getTextColor() {
    if (this.mapSettings.activeMapLayer() === MapOverlayLayer.VFR && this.mapSettings.vfrTopographyState() === TopographyState.ON) {
      return this.mapTheme.airportTextColor();
    }
    return this.mapTheme.textColor();
  }
}
class NavaidsVorDmeLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'navaids-vor';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.NavigationAids_VOR_DME, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'minzoom': ZoomLevel['10NM'],
        'filter': getFilterVORDME(this.mapConfig, this.mapSettings),
        'paint': {
          'icon-opacity': 0,
          // transparent by default
          'text-opacity': 0 // transparent by default
        },
        'layout': {
          'icon-allow-overlap': true,
          'icon-image': this.getIconImage(),
          'icon-size': 0.8,
          'text-field': ['coalesce', ['get', 'identifier'], 'UNK'],
          'text-font': ['Roboto'],
          'text-size': 10,
          'text-optional': true,
          'text-offset': [0, -1.7],
          'text-letter-spacing': 0.1
        }
      });
    }
  }
  update(map) {
    this.updateLayers(map);
    this.updateTheme(map);
  }
  getIconImage() {
    const flightRule = this.mapTheme.flightRule();
    if (flightRule === FlightRule.Ifr) {
      return ['match', ['get', 'type'], 'DME', MapIcon.Navaid_IFR_DME, 'VOR/DME', MapIcon.Navaid_IFR_VOR_DME, MapIcon.Navaid_IFR_VOR // VOR is default
      // NOTE: ILS-DME uses the VOR icon
      ];
    } else {
      return ['match', ['get', 'type'], 'DME', MapIcon.Navaid_DME, 'VOR/DME', MapIcon.Navaid_VOR_DME, MapIcon.Navaid_VOR // VOR is default
      // NOTE: ILS-DME uses the VOR icon
      ];
    }
  }
  updateLayers(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, getFilterVOR(this.mapConfig, this.mapSettings));
    }
  }
  updateTheme(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        iconImage: this.getIconImage(),
        textColor: this.getTextColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
  getTextColor() {
    if (this.mapSettings.activeMapLayer() === MapOverlayLayer.VFR && this.mapSettings.vfrTopographyState() === TopographyState.ON) {
      return this.mapTheme.airportTextColor();
    }
    return this.mapTheme.textColor();
  }
}
class NavaidsVorTacanLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'navaids-vor';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.NavigationAids_TACAN, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'minzoom': ZoomLevel['10NM'],
        'filter': getFilterVORTACAN(this.mapConfig, this.mapSettings),
        'paint': {
          'icon-opacity': 0,
          // transparent by default
          'text-opacity': 0 // transparent by default
        },
        'layout': {
          'icon-allow-overlap': true,
          'icon-image': this.getIconImage(),
          'icon-size': 0.8,
          'text-field': ['coalesce', ['get', 'identifier'], 'UNK'],
          'text-font': ['Roboto'],
          'text-size': 10,
          'text-optional': true,
          'text-offset': [0, -1.5],
          'text-letter-spacing': 0.1
        }
      });
    }
  }
  update(map) {
    this.updateLayers(map);
    this.updateTheme(map);
  }
  getIconImage() {
    const flightRule = this.mapTheme.flightRule();
    if (flightRule === FlightRule.Ifr) {
      return ['match', ['get', 'type'], 'TACAN', MapIcon.Navaid_IFR_TACAN, MapIcon.Navaid_IFR_VOR // VOR is default
      // NOTE: ILS-DME uses the VOR icon
      ];
    } else {
      return ['match', ['get', 'type'], 'TACAN', MapIcon.Navaid_TACAN, MapIcon.Navaid_VOR // VOR is default
      // NOTE: ILS-DME uses the VOR icon
      ];
    }
  }
  updateLayers(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, getFilterVOR(this.mapConfig, this.mapSettings));
    }
  }
  updateTheme(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        iconImage: this.getIconImage(),
        textColor: this.getTextColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
  getTextColor() {
    if (this.mapSettings.activeMapLayer() === MapOverlayLayer.VFR && this.mapSettings.vfrTopographyState() === TopographyState.ON) {
      return this.mapTheme.airportTextColor();
    }
    return this.mapTheme.textColor();
  }
}
class NavaidsVorVortacLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'navaids-vor';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.NavigationAids_VOR, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'minzoom': ZoomLevel['10NM'],
        'filter': getFilterVORVORTAC(this.mapConfig, this.mapSettings),
        'paint': {
          'icon-opacity': 0,
          // transparent by default
          'text-opacity': 0 // transparent by default
        },
        'layout': {
          'icon-allow-overlap': true,
          'icon-image': this.getIconImage(),
          'icon-size': 0.8,
          'text-field': ['coalesce', ['get', 'identifier'], 'UNK'],
          'text-font': ['Roboto'],
          'text-size': 10,
          'text-optional': true,
          'text-offset': [0, -1.5],
          'text-letter-spacing': 0.1
        }
      });
    }
  }
  update(map) {
    this.updateLayers(map);
    this.updateTheme(map);
  }
  getIconImage() {
    const flightRule = this.mapTheme.flightRule();
    if (flightRule === FlightRule.Ifr) {
      return ['match', ['get', 'type'], 'VORTAC', MapIcon.Navaid_IFR_VORTAC, MapIcon.Navaid_IFR_VOR // VOR is default
      // NOTE: ILS-DME uses the VOR icon
      ];
    } else {
      return ['match', ['get', 'type'], 'VORTAC', MapIcon.Navaid_VORTAC, MapIcon.Navaid_VOR // VOR is default
      // NOTE: ILS-DME uses the VOR icon
      ];
    }
  }
  updateLayers(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, getFilterVOR(this.mapConfig, this.mapSettings));
    }
  }
  updateTheme(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        iconImage: this.getIconImage(),
        textColor: this.getTextColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
  getTextColor() {
    if (this.mapSettings.activeMapLayer() === MapOverlayLayer.VFR && this.mapSettings.vfrTopographyState() === TopographyState.ON) {
      return this.mapTheme.airportTextColor();
    }
    return this.mapTheme.textColor();
  }
}
class NavaidsWptLayer extends ChartSearchableSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'navaids-wpt';
  }
  add(map, sourceId) {
    super.add(map, sourceId);
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.NavigationAids_WPT, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': getFilterWPT(this.mapConfig, this.mapSettings),
        'paint': {
          'icon-opacity': 0,
          // transparent by default
          'text-opacity': 0 // transparent by default
        },
        'layout': {
          'icon-allow-overlap': true,
          'icon-image': this.getIconImage(),
          'icon-size': this.getIconSize(),
          'text-field': ['coalesce', ['get', 'identifier'], 'UNK'],
          'text-font': ['Roboto'],
          'text-optional': true,
          'text-offset': [0, -1.3],
          'text-letter-spacing': 0.1,
          'text-size': 10
        }
      });
    }
  }
  update(map) {
    this.updateLayers(map);
    this.updateTheme(map);
  }
  getIconImage() {
    const flightRule = this.mapTheme.flightRule();
    if (flightRule === FlightRule.Ifr) {
      return ['case', isIntersectionNavAid(), MapIcon.Navaid_IFR_WPT, MapIcon.Navaid_IFR_WPT_RNAV];
    } else {
      return ['case', isIntersectionNavAid(), MapIcon.Navaid_WPT, MapIcon.Navaid_WPT_RNAV];
    }
  }
  getIconSize() {
    const flightRule = this.mapTheme.flightRule();
    if (flightRule === FlightRule.Ifr) {
      return ['case', isIntersectionNavAid(), 0.4, 0.6];
    } else {
      return ['case', isIntersectionNavAid(), 0.4, 0.6];
    }
  }
  updateLayers(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, getFilterWPT(this.mapConfig, this.mapSettings));
    }
  }
  updateTheme(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        iconImage: this.getIconImage(),
        textColor: this.getTextColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
  getTextColor() {
    if (this.mapSettings.activeMapLayer() === MapOverlayLayer.VFR && this.mapSettings.vfrTopographyState() === TopographyState.ON) {
      return this.mapTheme.airportTextColor();
    }
    return this.mapTheme.textColor();
  }
}
class NavaidsLayer extends ChartSearchableLayerGroup {
  constructor() {
    super(...arguments);
    this.shouldNdbBeVisible = () => {
      const config = this.mapConfig.content.aeronautical.navaid;
      return config.visible() || config.ndb();
    };
    this.shouldVorBeVisible = () => {
      const config = this.mapConfig.content.aeronautical.navaid;
      return config.visible() || config.vor();
    };
    this.shouldWptBeVisible = () => {
      const config = this.mapConfig.content.aeronautical.navaid;
      return config.visible() || config.wpt();
    };
    this.groupInfo = [{
      layer: new NavaidsNdbLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.NDB, LayerType.Navaids_NDB),
      shouldBeVisible: this.shouldNdbBeVisible
    }, {
      layer: new NavaidsVorCompassLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.VOR, LayerType.Navaids_VOR),
      shouldBeVisible: this.shouldVorBeVisible,
      shouldBePresent: () => this.shouldNavaidBePresent(this.shouldVorBeVisible)
    }, {
      layer: new NavaidsVorDmeLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.VOR, LayerType.Navaids_VOR),
      shouldBeVisible: this.shouldVorBeVisible
    }, {
      layer: new NavaidsVorTacanLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.VOR, LayerType.Navaids_VOR),
      shouldBeVisible: this.shouldVorBeVisible
    }, {
      layer: new NavaidsVorVortacLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.VOR, LayerType.Navaids_VOR),
      shouldBeVisible: this.shouldVorBeVisible,
      shouldBePresent: () => this.shouldNavaidBePresent(this.shouldVorBeVisible)
    }, {
      layer: new NavaidsWptLayer(this.injector, this.radialMenuMapService, MvtCycleContentSourceLayer.WPT, LayerType.Navaids_WPT),
      shouldBeVisible: this.shouldWptBeVisible,
      shouldBePresent: () => this.shouldNavaidBePresent(this.shouldWptBeVisible)
    }];
  }
  addSource(map) {
    return map.mvtCycleContent.add(MvtCycleContentUser.Navaids, this.content);
  }
  getSource(map) {
    return map.mvtCycleContent.getId(this.content);
  }
  removeSource(map) {
    map.mvtCycleContent.remove(MvtCycleContentUser.Navaids, this.content);
  }
  shouldNavaidBePresent(navaidVisible) {
    const activeLayer = this.mapSettings.activeMapLayer();
    const chartActive = activeLayer === MapOverlayLayer.IFREnroute || activeLayer === MapOverlayLayer.VFRSectional;
    if (!navaidVisible() && !chartActive) {
      return false;
    }
    return true;
  }
}
let NavaidsService = /*#__PURE__*/(() => {
  class NavaidsService extends ChartSearchableLayerService {
    constructor() {
      super();
      this.images = [MapIcon.Navaid_DME, MapIcon.Navaid_NDB_Marker, MapIcon.Navaid_NDB, MapIcon.Navaid_TACAN, MapIcon.Navaid_VOR, MapIcon.Navaid_VOR_Compass, MapIcon.Navaid_VOR_DME, MapIcon.Navaid_VORTAC, MapIcon.Navaid_WPT, MapIcon.Navaid_WPT_RNAV, MapIcon.Navaid_IFR_DME, MapIcon.Navaid_IFR_NDB_Marker, MapIcon.Navaid_IFR_NDB, MapIcon.Navaid_IFR_TACAN, MapIcon.Navaid_IFR_VOR, MapIcon.Navaid_IFR_VOR_DME, MapIcon.Navaid_IFR_VORTAC, MapIcon.Navaid_IFR_WPT, MapIcon.Navaid_IFR_WPT_RNAV].map(x => MapIconDefinitions.get(x));
      effect(() => this.updateLayers());
      effect(() => this.updateImages());
    }
    createLayer(content) {
      return new NavaidsLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.content.navaid.content();
    }
    getPresent() {
      return this.mapConfig.content.aeronautical.navaid.present();
    }
    getVisible() {
      const aeronauticalVisible = this.mapConfig.content.aeronautical.visible();
      const navaidsVisible = this.mapConfig.content.aeronautical.navaid.visible();
      const vorVisible = this.mapConfig.content.aeronautical.navaid.vor();
      const ndbVisible = this.mapConfig.content.aeronautical.navaid.ndb();
      const wptVisible = this.mapConfig.content.aeronautical.navaid.wpt();
      return aeronauticalVisible && (navaidsVisible || vorVisible || ndbVisible || wptVisible);
    }
    getOpacity() {
      return this.mapConfig.content.aeronautical.opacity();
    }
    updateImages() {
      const info = this.mapInfo.content.navaid;
      if (info.available() && this.getVisible()) {
        this.mapIcon.add(this.images);
      } else {
        this.mapIcon.remove(this.images);
      }
    }
    static {
      this.ɵfac = function NavaidsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NavaidsService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NavaidsService,
        factory: NavaidsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return NavaidsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class OpenStreetMapLayer extends SimpleLayer {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'open-street-map';
    this.LAYER_PREFIX = 'open-street-map';
  }
  addSource(map, content, sourceId) {
    map.addSource(sourceId, {
      type: 'raster',
      tiles: [`${content.urlBase}/{z}/{x}/{y}.png`],
      scheme: 'xyz',
      minzoom: 0,
      maxzoom: 19,
      tileSize: 256
    });
  }
  addLayer(map, sourceId, layerId) {
    map.addLayer(LayerPriority.BaseMap, {
      id: layerId,
      source: sourceId,
      type: 'raster',
      minzoom: 0,
      maxzoom: 24
    });
  }
}
let OpenStreetMapService = /*#__PURE__*/(() => {
  class OpenStreetMapService extends LayerService {
    constructor() {
      super();
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new OpenStreetMapLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.content.openStreetMap.content();
    }
    getVisible() {
      return this.mapConfig.content.openStreetMap.visible();
    }
    static {
      this.ɵfac = function OpenStreetMapService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OpenStreetMapService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OpenStreetMapService,
        factory: OpenStreetMapService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OpenStreetMapService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var FeatureType = /*#__PURE__*/function (FeatureType) {
  FeatureType[FeatureType["Area"] = 0] = "Area";
  FeatureType[FeatureType["Line"] = 1] = "Line";
  FeatureType[FeatureType["Point"] = 2] = "Point";
  return FeatureType;
}(FeatureType || {});
var AreaFeatureClass = /*#__PURE__*/function (AreaFeatureClass) {
  AreaFeatureClass[AreaFeatureClass["AirportDiagram"] = 14] = "AirportDiagram";
  return AreaFeatureClass;
}(AreaFeatureClass || {});
var AreaFeatureSubclass = /*#__PURE__*/function (AreaFeatureSubclass) {
  AreaFeatureSubclass[AreaFeatureSubclass["Taxiway"] = 0] = "Taxiway";
  AreaFeatureSubclass[AreaFeatureSubclass["TaxiwayIntersection"] = 1] = "TaxiwayIntersection";
  AreaFeatureSubclass[AreaFeatureSubclass["ClosedOrAbandonedTaxiway"] = 2] = "ClosedOrAbandonedTaxiway";
  AreaFeatureSubclass[AreaFeatureSubclass["ClosedRunway"] = 3] = "ClosedRunway";
  AreaFeatureSubclass[AreaFeatureSubclass["RunwayUnderConstruction"] = 4] = "RunwayUnderConstruction";
  AreaFeatureSubclass[AreaFeatureSubclass["PavedAircraftParking"] = 5] = "PavedAircraftParking";
  AreaFeatureSubclass[AreaFeatureSubclass["UnpavedAircraftParking"] = 6] = "UnpavedAircraftParking";
  AreaFeatureSubclass[AreaFeatureSubclass["AirportBuildingStructure"] = 7] = "AirportBuildingStructure";
  AreaFeatureSubclass[AreaFeatureSubclass["AirportExtents"] = 8] = "AirportExtents";
  AreaFeatureSubclass[AreaFeatureSubclass["Runway"] = 9] = "Runway";
  AreaFeatureSubclass[AreaFeatureSubclass["DisplacedThreshold"] = 10] = "DisplacedThreshold";
  AreaFeatureSubclass[AreaFeatureSubclass["Overrun"] = 11] = "Overrun";
  AreaFeatureSubclass[AreaFeatureSubclass["Clearway"] = 12] = "Clearway";
  AreaFeatureSubclass[AreaFeatureSubclass["Stopway"] = 13] = "Stopway";
  AreaFeatureSubclass[AreaFeatureSubclass["BlastPad"] = 14] = "BlastPad";
  AreaFeatureSubclass[AreaFeatureSubclass["UnpavedRunway"] = 15] = "UnpavedRunway";
  AreaFeatureSubclass[AreaFeatureSubclass["AirportWater"] = 16] = "AirportWater";
  AreaFeatureSubclass[AreaFeatureSubclass["Hotspot"] = 17] = "Hotspot";
  AreaFeatureSubclass[AreaFeatureSubclass["Emas"] = 18] = "Emas";
  AreaFeatureSubclass[AreaFeatureSubclass["Tower"] = 19] = "Tower";
  AreaFeatureSubclass[AreaFeatureSubclass["VerticalOrHorizontalTanks"] = 20] = "VerticalOrHorizontalTanks";
  AreaFeatureSubclass[AreaFeatureSubclass["RunUpPad"] = 21] = "RunUpPad";
  AreaFeatureSubclass[AreaFeatureSubclass["RadarReflector"] = 22] = "RadarReflector";
  AreaFeatureSubclass[AreaFeatureSubclass["TaxiwayShoulder"] = 23] = "TaxiwayShoulder";
  AreaFeatureSubclass[AreaFeatureSubclass["RunwayShoulder"] = 24] = "RunwayShoulder";
  AreaFeatureSubclass[AreaFeatureSubclass["UnpavedTaxiway"] = 25] = "UnpavedTaxiway";
  AreaFeatureSubclass[AreaFeatureSubclass["UnpavedTaxiwayIntersection"] = 26] = "UnpavedTaxiwayIntersection";
  return AreaFeatureSubclass;
}(AreaFeatureSubclass || {});
var PointFeatureClass = /*#__PURE__*/function (PointFeatureClass) {
  PointFeatureClass[PointFeatureClass["AirportDiagram"] = 325] = "AirportDiagram";
  return PointFeatureClass;
}(PointFeatureClass || {});
var PointFeatureSubclass = /*#__PURE__*/function (PointFeatureSubclass) {
  PointFeatureSubclass[PointFeatureSubclass["Helipad"] = 0] = "Helipad";
  PointFeatureSubclass[PointFeatureSubclass["AirportBeacon"] = 1] = "AirportBeacon";
  PointFeatureSubclass[PointFeatureSubclass["RunwayNumberPoint"] = 2] = "RunwayNumberPoint";
  return PointFeatureSubclass;
}(PointFeatureSubclass || {});
var LineFeatureClass = /*#__PURE__*/function (LineFeatureClass) {
  LineFeatureClass[LineFeatureClass["AirportDiagram"] = 13] = "AirportDiagram";
  return LineFeatureClass;
}(LineFeatureClass || {});
var LineFeatureSubclass = /*#__PURE__*/function (LineFeatureSubclass) {
  LineFeatureSubclass[LineFeatureSubclass["PaintLine"] = 0] = "PaintLine";
  LineFeatureSubclass[LineFeatureSubclass["AirportRoad"] = 1] = "AirportRoad";
  LineFeatureSubclass[LineFeatureSubclass["ThresholdLine"] = 2] = "ThresholdLine";
  LineFeatureSubclass[LineFeatureSubclass["LahsoLine"] = 3] = "LahsoLine";
  LineFeatureSubclass[LineFeatureSubclass["ConstructionAreaBorder"] = 4] = "ConstructionAreaBorder";
  LineFeatureSubclass[LineFeatureSubclass["HotspotAreaBorder"] = 5] = "HotspotAreaBorder";
  LineFeatureSubclass[LineFeatureSubclass["IlsHoldLine"] = 6] = "IlsHoldLine";
  LineFeatureSubclass[LineFeatureSubclass["TaxiwayHoldLine"] = 7] = "TaxiwayHoldLine";
  LineFeatureSubclass[LineFeatureSubclass["ClosedPavementLine"] = 8] = "ClosedPavementLine";
  LineFeatureSubclass[LineFeatureSubclass["MovementAreaLine"] = 9] = "MovementAreaLine";
  LineFeatureSubclass[LineFeatureSubclass["RunwayHoldLine"] = 10] = "RunwayHoldLine";
  LineFeatureSubclass[LineFeatureSubclass["JetBarrier"] = 11] = "JetBarrier";
  LineFeatureSubclass[LineFeatureSubclass["VisionScreen"] = 12] = "VisionScreen";
  LineFeatureSubclass[LineFeatureSubclass["WaterRunway"] = 13] = "WaterRunway";
  LineFeatureSubclass[LineFeatureSubclass["Fence"] = 14] = "Fence";
  LineFeatureSubclass[LineFeatureSubclass["BridgeOrTunnel"] = 15] = "BridgeOrTunnel";
  LineFeatureSubclass[LineFeatureSubclass["Hardstand"] = 16] = "Hardstand";
  LineFeatureSubclass[LineFeatureSubclass["TaxiLane"] = 17] = "TaxiLane";
  LineFeatureSubclass[LineFeatureSubclass["ParkingLimits"] = 18] = "ParkingLimits";
  LineFeatureSubclass[LineFeatureSubclass["RunwaySafetyBoundary"] = 19] = "RunwaySafetyBoundary";
  LineFeatureSubclass[LineFeatureSubclass["TaxiwayCenterLine"] = 20] = "TaxiwayCenterLine";
  LineFeatureSubclass[LineFeatureSubclass["DistanceRemaining"] = 21] = "DistanceRemaining";
  LineFeatureSubclass[LineFeatureSubclass["DashedCenterLine"] = 22] = "DashedCenterLine";
  LineFeatureSubclass[LineFeatureSubclass["PowerLine"] = 23] = "PowerLine";
  LineFeatureSubclass[LineFeatureSubclass["HangarOutline"] = 24] = "HangarOutline";
  LineFeatureSubclass[LineFeatureSubclass["TerminalOutline"] = 25] = "TerminalOutline";
  LineFeatureSubclass[LineFeatureSubclass["CustomsOutline"] = 26] = "CustomsOutline";
  LineFeatureSubclass[LineFeatureSubclass["FboOutline"] = 27] = "FboOutline";
  LineFeatureSubclass[LineFeatureSubclass["FireStationOutline"] = 28] = "FireStationOutline";
  LineFeatureSubclass[LineFeatureSubclass["ArrestingGear"] = 29] = "ArrestingGear";
  return LineFeatureSubclass;
}(LineFeatureSubclass || {});
const areaFillColor = (pavementColor, pavementClosedColor, runwayColor, runwayClosedColor, buildingColor, paintedLineColor, waterColor, hotspotColor) => {
  return ['match', ['get', 'subClass'], [AreaFeatureSubclass.Taxiway, AreaFeatureSubclass.TaxiwayIntersection, AreaFeatureSubclass.DisplacedThreshold], pavementColor, [AreaFeatureSubclass.ClosedOrAbandonedTaxiway, AreaFeatureSubclass.RunwayUnderConstruction, AreaFeatureSubclass.PavedAircraftParking, AreaFeatureSubclass.RunUpPad, AreaFeatureSubclass.Overrun, AreaFeatureSubclass.Emas], pavementClosedColor, [AreaFeatureSubclass.Runway, AreaFeatureSubclass.BlastPad, AreaFeatureSubclass.UnpavedRunway], runwayColor, [AreaFeatureSubclass.ClosedRunway], runwayClosedColor, [AreaFeatureSubclass.AirportBuildingStructure, AreaFeatureSubclass.Tower, AreaFeatureSubclass.VerticalOrHorizontalTanks], buildingColor, [AreaFeatureSubclass.AirportWater], waterColor, [AreaFeatureSubclass.Hotspot], hotspotColor, [AreaFeatureSubclass.UnpavedAircraftParking, AreaFeatureSubclass.AirportExtents, AreaFeatureSubclass.Clearway, AreaFeatureSubclass.Stopway], Color.Transparent.toString(), paintedLineColor];
};
const areaPriority = () => {
  return ['match', ['get', 'subClass'], AreaFeatureSubclass.Hotspot, 100, AreaFeatureSubclass.Runway, 99, AreaFeatureSubclass.TaxiwayIntersection, 98, AreaFeatureSubclass.Taxiway, 97, 0];
};
const areaOutlineColor = (hotspotOutlineColor, pavementOutlineColor) => {
  return ['match', ['get', 'subClass'], AreaFeatureSubclass.AirportExtents, Color.Transparent.toString(), AreaFeatureSubclass.Hotspot, hotspotOutlineColor, pavementOutlineColor];
};
const lineColor = (hotspotOutlineColor, paintedLineColor) => {
  return ['match', ['get', 'subClass'], LineFeatureSubclass.HotspotAreaBorder, hotspotOutlineColor, paintedLineColor];
};
const areaClassFilter = () => {
  return ['all', ['==', ['get', 'featureClass'], AreaFeatureClass.AirportDiagram], ['!=', ['get', 'subClass'], AreaFeatureSubclass.RadarReflector]];
};
const lineClassFilter = () => {
  return ['==', ['get', 'featureClass'], LineFeatureClass.AirportDiagram];
};
const pointClassFilter = () => {
  return ['==', ['get', 'featureClass'], PointFeatureClass.AirportDiagram];
};
class SafeTaxiAreaOutlineLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'safe-taxi-area-outline';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.SafeTaxi, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': areaClassFilter(),
        'paint': {
          'line-color': this.getLineColor()
        },
        'layout': {
          'line-sort-key': areaPriority()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, this.getLineColor());
    }
  }
  getLineColor() {
    const theme = this.mapTheme;
    return areaOutlineColor(theme.hotspotOutlineColor(), theme.pavementOutlineColor());
  }
}
class SafeTaxiAreaPolygonLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'safe-taxi-area-polygons';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.SafeTaxi, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'fill',
        'filter': areaClassFilter(),
        'paint': {
          'fill-color': this.getFillColor()
        },
        'layout': {
          'fill-sort-key': areaPriority()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, this.getFillColor());
    }
  }
  getFillColor() {
    const theme = this.mapTheme;
    return areaFillColor(theme.pavementColor(), theme.pavementClosedColor(), theme.runwayColor(), theme.runwayClosedColor(), theme.buildingColor(), theme.paintedLineColor(), theme.waterColor(), theme.hotspotColor());
  }
}
class SafeTaxiAreaTextLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'safe-taxi-area-text';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const theme = this.mapTheme;
      map.addLayer(LayerPriority.SafeTaxi, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(),
        'minzoom': ZoomLevel['0.2NM'],
        'paint': {
          'text-color': theme.textColor(),
          'text-halo-color': theme.textHaloColor(),
          'text-halo-width': theme.textHaloWidth()
        },
        'layout': {
          'text-font': ['Roboto'],
          'text-size': 10,
          'text-letter-spacing': 0.1,
          'text-field': ['get', 'label'],
          'text-padding': 10,
          'symbol-sort-key': areaPriority()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        textColor: theme.textColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
  getFilter() {
    return ['all', areaClassFilter(), ['match', ['get', 'subClass'], AreaFeatureSubclass.Taxiway, ['case',
    // Hide intersection Taxiways, e.g., "D9" until zoom level 15
    ['>', ['length', ['to-string', ['get', 'label']]], 1], ['>=', ['zoom'], 15], true], AreaFeatureSubclass.TaxiwayIntersection, true, AreaFeatureSubclass.PavedAircraftParking, true, AreaFeatureSubclass.AirportBuildingStructure, true, AreaFeatureSubclass.Tower, true, AreaFeatureSubclass.RunUpPad, true, AreaFeatureSubclass.Emas, true, false]];
  }
}
class SafeTaxiLineLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'safe-taxi-line';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.SafeTaxi, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': lineClassFilter(),
        'paint': {
          'line-color': this.getLineColor(),
          'line-width': ['interpolate', ['linear'], ['zoom'],
          // 1 pixel from ZL=0 to ZL=14
          0, 1, 14, 1,
          // 8 pixels at ZL=18
          18, 8]
        },
        'layout': {
          'line-cap': 'round'
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, this.getLineColor());
    }
  }
  getLineColor() {
    const theme = this.mapTheme;
    return lineColor(theme.hotspotOutlineColor(), theme.paintedLineColor());
  }
}
class SafeTaxiPointLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'safe-taxi-point';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const theme = this.mapTheme;
      map.addLayer(LayerPriority.SafeTaxi, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': pointClassFilter(),
        'minzoom': ZoomLevel['0.1NM'],
        'paint': {
          'text-color': theme.textColor(),
          'text-halo-color': theme.textHaloColor(),
          'text-halo-width': theme.textHaloWidth()
        },
        'layout': {
          'text-font': ['Roboto'],
          'text-size': 10,
          'text-letter-spacing': 0.1,
          'text-field': ['match', ['get', 'subClass'], PointFeatureSubclass.RunwayNumberPoint, ['get', 'label'], ''],
          'text-allow-overlap': true
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        textColor: theme.textColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
}
const SOURCE_LAYER_AREAS = 'safetaxi-areas';
const SOURCE_LAYER_LINES = 'safetaxi-lines';
const SOURCE_LAYER_POINTS = 'safetaxi-points';
class SafeTaxiLayer extends LayerGroup {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'safe-taxi';
    this.groupInfo = [{
      layer: new SafeTaxiAreaPolygonLayer(this.injector, this.radialMenuMapService, SOURCE_LAYER_AREAS)
    }, {
      layer: new SafeTaxiAreaOutlineLayer(this.injector, this.radialMenuMapService, SOURCE_LAYER_AREAS)
    }, {
      layer: new SafeTaxiLineLayer(this.injector, this.radialMenuMapService, SOURCE_LAYER_LINES)
    }, {
      layer: new SafeTaxiAreaTextLayer(this.injector, this.radialMenuMapService, SOURCE_LAYER_AREAS)
    }, {
      layer: new SafeTaxiPointLayer(this.injector, this.radialMenuMapService, SOURCE_LAYER_POINTS)
    }];
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      minzoom: ZoomLevel['0.5NM'],
      maxzoom: 13
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
}
const safeTaxiImages = [];
let SafetaxiService = /*#__PURE__*/(() => {
  class SafetaxiService extends LayerService {
    constructor() {
      super();
      this.config = this.mapConfig.content.safeTaxi;
      this.satelliteConfig = this.mapConfig.content.groundSatellite;
      this.info = this.mapInfo.content.safeTaxi;
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new SafeTaxiLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.info.content();
    }
    getVisible() {
      return this.config.visible() && !this.satelliteConfig.visible();
    }
    updateImages() {
      if (this.info.available() && this.config.visible()) {
        this.mapIcon.add(safeTaxiImages);
      } else {
        this.mapIcon.remove(safeTaxiImages);
      }
    }
    static {
      this.ɵfac = function SafetaxiService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SafetaxiService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SafetaxiService,
        factory: SafetaxiService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SafetaxiService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const LAYER_PREFIX$3 = 'topography';
const SOURCE_PREFIX$4 = 'topography';
class TopographyLayer extends Layer {
  hide() {
    const map = this.map();
    if (map != null) {
      if (this.layerId != null) {
        map.removeLayer(this.layerId);
        this.layerId = undefined;
      }
      if (this.sourceId != null) {
        map.removeSource(this.sourceId);
        this.sourceId = undefined;
      }
    }
  }
  show() {
    const map = this.map();
    if (map != null) {
      this.sourceId = map.sourceIds.next(SOURCE_PREFIX$4);
      map.addSource(this.sourceId, {
        type: 'raster',
        tiles: [`${TopographyProtocol.TOPO_PREFIX}${this.content.urlBase}/{z}/{x}/{y}.webp`],
        minzoom: 1,
        maxzoom: 10,
        // The terrain tiles are provided as a 256x256 image
        // (at the time of writing). We're purposefully using 512 instead.
        // This results in MapLibre using a more zoomed out tile but stretching
        // it to cover the same geographic area. This results in a slightly
        // blurrier image but with drastically fewer network requests.
        tileSize: 512
      });
      this.layerId = map.layerIds.next(LAYER_PREFIX$3);
      map.addLayer(LayerPriority.Topography, {
        id: this.layerId,
        source: this.sourceId,
        type: 'raster'
      });
    }
  }
  update() {
    // no action required
  }
  visible() {
    return this.map()?.hasLayer(this.layerId) ?? false;
  }
}
let TopographyService = /*#__PURE__*/(() => {
  class TopographyService extends LayerService {
    constructor() {
      super();
      this.topographyProtocol = new TopographyProtocol();
      effect(() => this.updateLayers());
    }
    ngOnDestroy() {
      this.topographyProtocol.destroy();
    }
    createLayer(content) {
      return new TopographyLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.content.topography.content();
    }
    getVisible() {
      const visible = this.mapConfig.content.hillshade.visible();
      const activeMapLayer = this.mapSettings.activeMapLayer();
      const vfrTopo = this.mapSettings.vfrTopographyState();
      const ifrTopo = this.mapSettings.ifrTopographyState();
      return visible || activeMapLayer === MapOverlayLayer.IFR && ifrTopo === TopographyState.ON || activeMapLayer === MapOverlayLayer.VFR && vfrTopo === TopographyState.ON;
    }
    static {
      this.ɵfac = function TopographyService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TopographyService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TopographyService,
        factory: TopographyService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TopographyService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Performs a linear interpolation between two steps.
 */
const linearInterpolate = (value, min, minOutput, max, maxOutput) => {
  if (value < min) return minOutput;
  if (value > max) return maxOutput;
  const denom = max - min;
  if (denom == 0) throw new Error('would divide by zero');
  return (minOutput * (max - value) + maxOutput * (value - min)) / denom;
};
const aircraftMappingTable = {
  // Commercial Dual Engine
  A20N: MapIcon.InternetTraffic_CommercialDualEngine,
  A21N: MapIcon.InternetTraffic_CommercialDualEngine,
  A319: MapIcon.InternetTraffic_CommercialDualEngine,
  A320: MapIcon.InternetTraffic_CommercialDualEngine,
  A321: MapIcon.InternetTraffic_CommercialDualEngine,
  A333: MapIcon.InternetTraffic_CommercialDualEngine,
  A359: MapIcon.InternetTraffic_CommercialDualEngine,
  A35K: MapIcon.InternetTraffic_CommercialDualEngine,
  B38M: MapIcon.InternetTraffic_CommercialDualEngine,
  B39M: MapIcon.InternetTraffic_CommercialDualEngine,
  B737: MapIcon.InternetTraffic_CommercialDualEngine,
  B738: MapIcon.InternetTraffic_CommercialDualEngine,
  B739: MapIcon.InternetTraffic_CommercialDualEngine,
  B752: MapIcon.InternetTraffic_CommercialDualEngine,
  B753: MapIcon.InternetTraffic_CommercialDualEngine,
  B763: MapIcon.InternetTraffic_CommercialDualEngine,
  B764: MapIcon.InternetTraffic_CommercialDualEngine,
  B772: MapIcon.InternetTraffic_CommercialDualEngine,
  B77W: MapIcon.InternetTraffic_CommercialDualEngine,
  B788: MapIcon.InternetTraffic_CommercialDualEngine,
  BCS1: MapIcon.InternetTraffic_CommercialDualEngine,
  BCS3: MapIcon.InternetTraffic_CommercialDualEngine,
  E170: MapIcon.InternetTraffic_CommercialDualEngine,
  E190: MapIcon.InternetTraffic_CommercialDualEngine,
  E295: MapIcon.InternetTraffic_CommercialDualEngine,
  E75L: MapIcon.InternetTraffic_CommercialDualEngine,
  E75S: MapIcon.InternetTraffic_CommercialDualEngine,
  // Commercial Rear Engine
  B712: MapIcon.InternetTraffic_CommercialRearEngine,
  E145: MapIcon.InternetTraffic_CommercialRearEngine,
  E45X: MapIcon.InternetTraffic_CommercialRearEngine,
  // Commercial (assuming Four Engine for B748)
  B748: MapIcon.InternetTraffic_CommercialFourEngine,
  // Generic
  AC90: MapIcon.InternetTraffic_Generic,
  D328: MapIcon.InternetTraffic_Generic,
  E120: MapIcon.InternetTraffic_Generic,
  E135: MapIcon.InternetTraffic_Generic,
  SF34: MapIcon.InternetTraffic_Generic,
  SF50: MapIcon.InternetTraffic_Generic,
  SW4: MapIcon.InternetTraffic_Generic,
  // Business Jet
  ASTR: MapIcon.InternetTraffic_BusinessJet,
  C25A: MapIcon.InternetTraffic_BusinessJet,
  C25B: MapIcon.InternetTraffic_BusinessJet,
  C25C: MapIcon.InternetTraffic_BusinessJet,
  C25M: MapIcon.InternetTraffic_BusinessJet,
  C501: MapIcon.InternetTraffic_BusinessJet,
  C510: MapIcon.InternetTraffic_BusinessJet,
  C525: MapIcon.InternetTraffic_BusinessJet,
  C550: MapIcon.InternetTraffic_BusinessJet,
  C55B: MapIcon.InternetTraffic_BusinessJet,
  C560: MapIcon.InternetTraffic_BusinessJet,
  C56X: MapIcon.InternetTraffic_BusinessJet,
  C650: MapIcon.InternetTraffic_BusinessJet,
  C680: MapIcon.InternetTraffic_BusinessJet,
  C68A: MapIcon.InternetTraffic_BusinessJet,
  C700: MapIcon.InternetTraffic_BusinessJet,
  C750: MapIcon.InternetTraffic_BusinessJet,
  CL30: MapIcon.InternetTraffic_BusinessJet,
  CL35: MapIcon.InternetTraffic_BusinessJet,
  CL60: MapIcon.InternetTraffic_BusinessJet,
  E50P: MapIcon.InternetTraffic_BusinessJet,
  E545: MapIcon.InternetTraffic_BusinessJet,
  E550: MapIcon.InternetTraffic_BusinessJet,
  E55P: MapIcon.InternetTraffic_BusinessJet,
  F2TH: MapIcon.InternetTraffic_BusinessJet,
  F900: MapIcon.InternetTraffic_BusinessJet,
  G280: MapIcon.InternetTraffic_BusinessJet,
  GA5C: MapIcon.InternetTraffic_BusinessJet,
  GA6C: MapIcon.InternetTraffic_BusinessJet,
  GALX: MapIcon.InternetTraffic_BusinessJet,
  GL5T: MapIcon.InternetTraffic_BusinessJet,
  GLEX: MapIcon.InternetTraffic_BusinessJet,
  GLF5: MapIcon.InternetTraffic_BusinessJet,
  GLF6: MapIcon.InternetTraffic_BusinessJet,
  H25B: MapIcon.InternetTraffic_BusinessJet,
  HDJT: MapIcon.InternetTraffic_BusinessJet,
  LJ35: MapIcon.InternetTraffic_BusinessJet,
  LJ60: MapIcon.InternetTraffic_BusinessJet,
  LJ75: MapIcon.InternetTraffic_BusinessJet,
  PC24: MapIcon.InternetTraffic_BusinessJet,
  PRM1: MapIcon.InternetTraffic_BusinessJet,
  // Low Wing Twin
  B350: MapIcon.InternetTraffic_LowWingTwin,
  BE10: MapIcon.InternetTraffic_LowWingTwin,
  BE20: MapIcon.InternetTraffic_LowWingTwin,
  BE30: MapIcon.InternetTraffic_LowWingTwin,
  BE40: MapIcon.InternetTraffic_LowWingTwin,
  BE9L: MapIcon.InternetTraffic_LowWingTwin,
  DA62: MapIcon.InternetTraffic_LowWingTwin,
  // Low Wing
  EPIC: MapIcon.InternetTraffic_LowWing,
  LNP4: MapIcon.InternetTraffic_LowWing,
  M20T: MapIcon.InternetTraffic_LowWing,
  M600: MapIcon.InternetTraffic_LowWing,
  P46T: MapIcon.InternetTraffic_LowWing,
  PA46: MapIcon.InternetTraffic_LowWing,
  PC12: MapIcon.InternetTraffic_LowWing,
  S22T: MapIcon.InternetTraffic_LowWing,
  TBM7: MapIcon.InternetTraffic_LowWing,
  TBM8: MapIcon.InternetTraffic_LowWing,
  TBM9: MapIcon.InternetTraffic_LowWing,
  // Military Transport
  C130: MapIcon.InternetTraffic_MilitaryTransport,
  C30J: MapIcon.InternetTraffic_MilitaryTransport,
  // Fighter
  FA50: MapIcon.InternetTraffic_Fighter,
  // High Wing Twin
  MU2: MapIcon.InternetTraffic_HighWindTwin,
  // Military (assuming generic military icon)
  TEX2: MapIcon.InternetTraffic_Generic,
  // For types not explicitly mapped, default to Generic
  CRJ2: MapIcon.InternetTraffic_Generic,
  CRJ7: MapIcon.InternetTraffic_Generic,
  CRJ9: MapIcon.InternetTraffic_Generic,
  DC91: MapIcon.InternetTraffic_Generic
};
const aircraftImages = [MapIcon.InternetTraffic_Biplane, MapIcon.InternetTraffic_BusinessJet, MapIcon.InternetTraffic_CommercialDualEngine, MapIcon.InternetTraffic_CommercialFourEngine, MapIcon.InternetTraffic_CommercialRearEngine, MapIcon.InternetTraffic_Fighter, MapIcon.InternetTraffic_FloatPlane, MapIcon.InternetTraffic_Generic, MapIcon.InternetTraffic_Helicopter, MapIcon.InternetTraffic_Highlight, MapIcon.InternetTraffic_HighlightDetailBox, MapIcon.InternetTraffic_HighWindTwin, MapIcon.InternetTraffic_HighWing, MapIcon.InternetTraffic_LowWingTwin, MapIcon.InternetTraffic_LowWing, MapIcon.InternetTraffic_MilitaryTransport, MapIcon.InternetTraffic_Sr, MapIcon.InternetTraffic_WarBird, MapIcon.InternetTraffic_Xplane].map(x => MapIconDefinitions.get(x));
const getFlightDataByPosition = (flight, position) => {
  let bestDist = Infinity;
  let bestIndex = -1;
  let bestFlightPoint = undefined;
  let bestPoint = undefined;
  for (let i = 0; i < flight.points.length; ++i) {
    const flightPoint = flight.points[i];
    const point = new ML__default.LngLat(flightPoint.longitude, flightPoint.latitude);
    const dist = position.distanceTo(point);
    if (dist < bestDist) {
      bestIndex = i;
      bestFlightPoint = flightPoint;
      bestDist = dist;
      bestPoint = point;
    }
  }
  if (bestIndex >= 0 && bestFlightPoint != null && bestPoint != null) {
    const data = {
      flight,
      position: bestPoint,
      time: bestFlightPoint.timestamp,
      rotation: bestFlightPoint.rotation
    };
    if (bestFlightPoint.rotation == null) {
      data.track = calculateTrack(flight.points, bestIndex);
    }
    return data;
  }
  return undefined;
};
const getFlightDataByTime = (flight, time) => {
  let bestTimeDiff = Infinity;
  let bestIndex = -1;
  let bestFlightPoint = undefined;
  for (let i = 0; i < flight.points.length; ++i) {
    const point = flight.points[i];
    const diff = Math.abs(point.timestamp - time);
    if (diff < bestTimeDiff) {
      bestIndex = i;
      bestFlightPoint = point;
      bestTimeDiff = diff;
    }
  }
  if (bestIndex >= 0 && bestFlightPoint != null) {
    const data = {
      flight,
      position: new ML__default.LngLat(bestFlightPoint.longitude, bestFlightPoint.latitude),
      time: bestFlightPoint.timestamp,
      rotation: bestFlightPoint.rotation
    };
    if (bestFlightPoint.rotation == null) {
      data.track = calculateTrack(flight.points, bestIndex);
    }
    return data;
  }
  return undefined;
};
/**
   * Given the "current" position of the aircraft, calculate a track based on the
   * positions around it. If a point further in the flight exists, point towards
   * it. If not, point away from the previous point.
   * If somehow there's only one point or fewer, return 0.
   */
const calculateTrack = (points, index) => {
  let track = 0;
  const current = points[index];
  if (current != null) {
    const currentTurf = turf.point([current.longitude, current.latitude]);
    const next = points[index + 1];
    if (next != null) {
      const nextTurf = turf.point([next.longitude, next.latitude]);
      track = turf.bearing(currentTurf, nextTurf);
    } else {
      const previous = points[index - 1];
      if (previous != null) {
        const previousTurf = turf.point([previous.longitude, previous.latitude]);
        track = turf.bearing(previousTurf, currentTurf);
      }
    }
  }
  return track;
};
const DEFAULT_ICON_MIN_ZOOM = 2;
const DEFAULT_ICON_MIN_SCALE = 0.25;
const DEFAULT_ICON_MAX_ZOOM = 8;
const DEFAULT_ICON_MAX_SCALE = 1.0;
class FlightMarker {
  constructor(moduleConfig, map, aircraftModel, updateAnimationOnDrag) {
    this.map = map;
    this.updateAnimationOnDrag = updateAnimationOnDrag;
    this.existsOnMap = false;
    this.onDrag = () => {
      if (this.flight == null) return;
      const position = this.marker.getLngLat().wrap();
      const data = getFlightDataByPosition(this.flight, position);
      this.update(data);
      this.updateAnimationOnDrag(data?.time);
    };
    this.updateScaleOnZoom = () => {
      const element = this.marker.getElement();
      if (this.originalWidth == null) {
        if (element.clientWidth != 0) {
          this.originalWidth = element.clientWidth;
        }
      }
      if (this.originalWidth != null) {
        const zoom = this.map.getZoom();
        const scale = linearInterpolate(zoom, this.flight?.aircraftIconScaling?.minZoom ?? DEFAULT_ICON_MIN_ZOOM, this.flight?.aircraftIconScaling?.minScale ?? DEFAULT_ICON_MIN_SCALE, this.flight?.aircraftIconScaling?.maxZoom ?? DEFAULT_ICON_MAX_ZOOM, this.flight?.aircraftIconScaling?.maxZoom ?? DEFAULT_ICON_MAX_SCALE);
        element.style.width = `${Math.round(this.originalWidth * scale)}px`;
      }
    };
    const aircraftIcon = aircraftMappingTable[aircraftModel ?? ''] ?? MapIcon.InternetTraffic_Generic;
    const definition = MapIconDefinitions.get(aircraftIcon);
    const options = {
      draggable: true
    };
    if (definition.src != null) {
      const image = document.createElement('img');
      image.addEventListener('load', this.updateScaleOnZoom);
      image.src = getIconSrc(moduleConfig, definition.src, definition.prefix);
      options.element = image;
    }
    this.marker = new ML__default.Marker(options);
  }
  remove() {
    this.marker.remove();
    this.map.off('zoomend', this.updateScaleOnZoom);
    this.marker.off('dragstart', this.onDrag);
    this.marker.off('drag', this.onDrag);
    this.marker.off('dragend', this.onDrag);
    this.existsOnMap = false;
  }
  update(data) {
    if (data != null) {
      this.flight = data.flight;
      this.marker.setLngLat(data.position);
      this.marker.setRotation(data.rotation ?? data.track ?? 0);
      if (!this.existsOnMap) {
        this.map.addMarker(MarkerPriority.Flights, this.marker);
        this.map.on('zoomend', this.updateScaleOnZoom);
        this.marker.on('drag', this.onDrag);
        this.marker.on('dragstart', this.onDrag);
        this.marker.on('dragend', this.onDrag);
        this.updateScaleOnZoom();
        this.existsOnMap = true;
      }
    } else if (this.existsOnMap) {
      this.remove();
    }
  }
}
class FlightMarkerManager {
  constructor(moduleConfig, map, mapAnimation) {
    this.moduleConfig = moduleConfig;
    this.map = map;
    this.mapAnimation = mapAnimation;
    this.markers = new Map();
    this.updateAnimationOnDrag = time => {
      if (time != null) {
        this.mapAnimation.seek(time);
      }
    };
  }
  destroy() {
    for (const [_id, marker] of this.markers) {
      marker.remove();
    }
    this.markers.clear();
  }
  update(flights, date) {
    this.deleteOldMarkers(flights);
    this.createNewMarkers(flights);
    this.updateMarkers(flights, date);
  }
  createNewMarkers(flights) {
    for (const flight of flights) {
      let marker = this.markers.get(flight.id);
      if (marker == null) {
        marker = new FlightMarker(this.moduleConfig, this.map, flight.aircraftModel, this.updateAnimationOnDrag);
        this.markers.set(flight.id, marker);
      }
    }
  }
  deleteOldMarkers(flights) {
    for (const [id, marker] of this.markers) {
      const missing = flights.find(f => f.id === id) == null;
      if (missing) {
        marker.remove();
        this.markers.delete(id);
      }
    }
  }
  hideAllMarkers() {
    for (const [_id, marker] of this.markers) {
      marker.remove();
    }
  }
  updateMarkers(flights, date) {
    // if the date is invalid, remove all markers
    if (!isValid(date)) {
      console.error('invalid date');
      this.hideAllMarkers();
      return;
    }
    const time = getUnixTime(date);
    for (const flight of flights) {
      const marker = this.markers.get(flight.id);
      if (marker != null) {
        // find the best place to put the marker
        // if null, the marker should not be displayed
        const data = getFlightDataByTime(flight, time);
        marker.update(data);
      } else {
        console.error(`No marker exists for flight ${flight.id}`);
      }
    }
  }
}
class FlightLineLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'flights-line';
  }
  _show(map) {
    if (this.layerId != null && this.sourceId != null) {
      map.addLayer(LayerPriority.Flights, {
        id: this.layerId,
        source: this.sourceId,
        type: 'line',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 3
        },
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        }
      });
    }
  }
  _update(_map) {
    // nothing to do
  }
}
class FlightOutlineLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'flights-outline';
  }
  _show(map) {
    if (this.layerId != null && this.sourceId != null) {
      map.addLayer(LayerPriority.Flights, {
        id: this.layerId,
        source: this.sourceId,
        type: 'line',
        paint: {
          'line-color': ['get', 'borderColor'],
          'line-width': 5
        },
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        }
      });
    }
  }
  _update(_map) {
    // nothing to do
  }
}
class FlightLayer extends LayerGroup {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'flights';
    this.groupInfo = [{
      layer: new FlightOutlineLayer(this.injector, this.radialMenuMapService)
    }, {
      layer: new FlightLineLayer(this.injector, this.radialMenuMapService)
    }];
  }
  update() {
    const map = this.map();
    if (map != null && this.sourceId != null) {
      map.setGeoJsonSourceData(this.sourceId, this.getData());
    }
    super.update();
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'geojson',
      data: this.getData()
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
  getData() {
    const features = [];
    const flights = this.mapData.flights();
    for (const flight of flights) {
      features.push(this.createFlightFeature(flight));
    }
    return turf.featureCollection(features);
  }
  createFlightFeature(flight) {
    const coordinates = [];
    for (const point of flight.points) {
      coordinates.push([point.longitude, point.latitude]);
    }
    return turf.line(coordinates, this.createFlightProperties(flight));
  }
  createFlightProperties(flight) {
    return {
      color: flight.color ?? Color.White.toString(),
      borderColor: flight.borderColor ?? Color.Black.toString()
    };
  }
}
let FlightService = /*#__PURE__*/(() => {
  class FlightService extends LayerService {
    constructor() {
      super();
      this.updateMarkers = () => {
        const map = this.map();
        const flights = this.mapData.flights();
        if (map != null) {
          if (this.flightMarkerManager == null) {
            this.flightMarkerManager = new FlightMarkerManager(this.moduleConfig, map, this.mapAnimation);
          }
          this.flightMarkerManager.update(flights, this.mapAnimation.date());
        } else {
          this.flightMarkerManager?.destroy();
          this.flightMarkerManager = undefined;
        }
      };
      effect(() => this.updateLayers());
      effect(this.updateMarkers);
    }
    createLayer(_content) {
      return new FlightLayer(this.injector, this.radialMenuMapService);
    }
    getContent() {
      return [{
        ...EmptyContent,
        type: 'FLIGHTS',
        domain: 'GLOBAL'
      }];
    }
    getVisible() {
      const flights = this.mapData.flights();
      for (const flight of flights) {
        if (flight.points.length > 0) {
          return true;
        }
      }
      return false;
    }
    static {
      this.ɵfac = function FlightService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlightService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FlightService,
        factory: FlightService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FlightService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Given an array of unknown `MapElement` objects, grab all the `MapWaypoint`
 * objects available. This includes any children inside the `MapElement` objects.
 */
const extractMapWaypoints = elements => {
  const waypoints = [];
  for (const element of elements) {
    if (isMapProcedure(element)) {
      waypoints.push(...extractMapWaypoints(element.transitions));
    } else if (isMapRoute(element)) {
      waypoints.push(...extractMapWaypoints(element.waypoints));
    } else if (isMapWaypoint(element)) {
      waypoints.push(element);
    } else {
      throw new Error('unknown element type');
    }
  }
  return waypoints;
};
/**
 * Given an array of unknown `MapElement` objects, return an array of all of those
 * objects plus their children, recursive.
 */
const flattenMapElements = elements => {
  const results = [];
  for (const element of elements) {
    if (isMapProcedure(element)) {
      results.push(element);
      results.push(...flattenMapElements(element.transitions));
    } else if (isMapRoute(element)) {
      results.push(element);
      results.push(...flattenMapElements(element.waypoints));
    } else if (isMapWaypoint(element)) {
      results.push(element);
    } else {
      throw new Error('unknown element type');
    }
  }
  return results;
};
const DEFAULT_BACKGROUND_COLOR = Color.White.toString();
const DEFAULT_BORDER_COLOR = Color.Black.toString();
const BILLBOARD_WIDTH = 18;
const BILLBOARD_HEIGHT = 8;
const BILLBOARD_OPTIONS = {
  content: [3, 3, 17, 8],
  stretchX: [[3, 17]],
  stretchY: [[3, 8]]
};
class TextBillboardManager {
  constructor(imagePrefix) {
    this.imagePrefix = imagePrefix;
    this.mapService = inject(MapService);
    this.imagePrefix += '--';
  }
  generateId(element) {
    let id = this.imagePrefix;
    id += `${element.backgroundColor ?? DEFAULT_BACKGROUND_COLOR}-`;
    id += `${element.borderColor ?? DEFAULT_BORDER_COLOR}`;
    return this.sanitize(id);
  }
  update(elements) {
    const allElements = flattenMapElements(elements);
    this.addNewImages(allElements);
    this.removeOldImages(allElements);
  }
  addNewImages(elements) {
    const newImages = new Map();
    for (const element of elements) {
      const id = this.generateId(element);
      if (!newImages.has(id) && !this.mapService.icon.has(id)) {
        newImages.set(id, this.createImage(id, element));
      }
    }
    if (newImages.size > 0) {
      this.mapService.icon.add([...newImages.values()]);
    }
  }
  createImage(id, element) {
    return {
      id,
      opts: BILLBOARD_OPTIONS,
      src: new Rectangle(BILLBOARD_WIDTH, BILLBOARD_HEIGHT, element.backgroundColor ?? DEFAULT_BACKGROUND_COLOR, undefined, element.borderColor ?? DEFAULT_BORDER_COLOR).rasterize()
    };
  }
  removeOldImages(elements) {
    const oldImages = [];
    for (const id of this.mapService.icon.list()) {
      if (id.startsWith(this.imagePrefix)) {
        const found = elements.some(element => {
          const elementId = this.generateId(element);
          return elementId === id;
        });
        if (!found && !oldImages.includes(id)) {
          oldImages.push(id);
        }
      }
    }
    this.mapService.icon.remove(oldImages.map(x => {
      return {
        id: x
      };
    }));
  }
  sanitize(x) {
    // ':' in an image name denotes a sprite sheet
    return x.replace(':', '_');
  }
}
var MapWaypointType = /*#__PURE__*/function (MapWaypointType) {
  MapWaypointType["Circle"] = "Circle";
  MapWaypointType["CustomImage"] = "CustomImage";
  MapWaypointType["CustomElement"] = "CustomElement";
  MapWaypointType["Icon"] = "Icon";
  MapWaypointType["Pin"] = "Pin";
  MapWaypointType["Rectangle"] = "Rectangle";
  return MapWaypointType;
}(MapWaypointType || {});
let Marker$1 = class Marker extends BaseSVG {
  constructor(options) {
    super();
    this._color = options?.color ?? '#FF0000';
    const height = 41;
    const width = 27;
    this.svg.setAttribute('display', 'block');
    this.svg.setAttribute('height', `${height}px`);
    this.svg.setAttribute('width', `${width}px`);
    this.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    const page1 = document.createElementNS(BaseSVG.svgNamespace, 'g');
    page1.setAttribute('fill-rule', 'nonzero');
    const shadow = document.createElementNS(BaseSVG.svgNamespace, 'g');
    shadow.setAttribute('transform', 'translate(3.0, 29.0)');
    shadow.setAttribute('fill', '#000000');
    const ellipses = [{
      rx: '10.5',
      ry: '5.25002273'
    }, {
      rx: '10.5',
      ry: '5.25002273'
    }, {
      rx: '9.5',
      ry: '4.77275007'
    }, {
      rx: '8.5',
      ry: '4.29549936'
    }, {
      rx: '7.5',
      ry: '3.81822308'
    }, {
      rx: '6.5',
      ry: '3.34094679'
    }, {
      rx: '5.5',
      ry: '2.86367051'
    }, {
      rx: '4.5',
      ry: '2.38636864'
    }];
    for (const data of ellipses) {
      const ellipse = document.createElementNS(BaseSVG.svgNamespace, 'ellipse');
      ellipse.setAttribute('opacity', '0.04');
      ellipse.setAttribute('cx', '10.5');
      ellipse.setAttribute('cy', '5.80029008');
      ellipse.setAttribute('rx', data['rx']);
      ellipse.setAttribute('ry', data['ry']);
      shadow.appendChild(ellipse);
    }
    const background = document.createElementNS(BaseSVG.svgNamespace, 'g');
    background.setAttribute('fill', this._color);
    const bgPath = document.createElementNS(BaseSVG.svgNamespace, 'path');
    // eslint-disable-next-line max-len
    bgPath.setAttribute('d', 'M27,13.5 C27,19.074644 20.250001,27.000002 14.75,34.500002 C14.016665,35.500004 12.983335,35.500004 12.25,34.500002 C6.7499993,27.000002 0,19.222562 0,13.5 C0,6.0441559 6.0441559,0 13.5,0 C20.955844,0 27,6.0441559 27,13.5 Z');
    background.appendChild(bgPath);
    const border = document.createElementNS(BaseSVG.svgNamespace, 'g');
    border.setAttribute('opacity', '0.25');
    border.setAttribute('fill', '#000000');
    const borderPath = document.createElementNS(BaseSVG.svgNamespace, 'path');
    // eslint-disable-next-line max-len
    borderPath.setAttribute('d', 'M13.5,0 C6.0441559,0 0,6.0441559 0,13.5 C0,19.222562 6.7499993,27 12.25,34.5 C13,35.522727 14.016664,35.500004 14.75,34.5 C20.250001,27 27,19.074644 27,13.5 C27,6.0441559 20.955844,0 13.5,0 Z M13.5,1 C20.415404,1 26,6.584596 26,13.5 C26,15.898657 24.495584,19.181431 22.220703,22.738281 C19.945823,26.295132 16.705119,30.142167 13.943359,33.908203 C13.743445,34.180814 13.612715,34.322738 13.5,34.441406 C13.387285,34.322738 13.256555,34.180814 13.056641,33.908203 C10.284481,30.127985 7.4148684,26.314159 5.015625,22.773438 C2.6163816,19.232715 1,15.953538 1,13.5 C1,6.584596 6.584596,1 13.5,1 Z');
    border.appendChild(borderPath);
    const maki = document.createElementNS(BaseSVG.svgNamespace, 'g');
    maki.setAttribute('transform', 'translate(6.0, 7.0)');
    maki.setAttribute('fill', '#FFFFFF');
    const circleContainer = document.createElementNS(BaseSVG.svgNamespace, 'g');
    circleContainer.setAttribute('transform', 'translate(8.0, 8.0)');
    const circle1 = document.createElementNS(BaseSVG.svgNamespace, 'circle');
    circle1.setAttribute('fill', '#000000');
    circle1.setAttribute('opacity', '0.25');
    circle1.setAttribute('cx', '5.5');
    circle1.setAttribute('cy', '5.5');
    circle1.setAttribute('r', '5.4999962');
    const circle2 = document.createElementNS(BaseSVG.svgNamespace, 'circle');
    circle2.setAttribute('fill', '#FFFFFF');
    circle2.setAttribute('cx', '5.5');
    circle2.setAttribute('cy', '5.5');
    circle2.setAttribute('r', '5.4999962');
    circleContainer.appendChild(circle1);
    circleContainer.appendChild(circle2);
    page1.appendChild(shadow);
    page1.appendChild(background);
    page1.appendChild(border);
    page1.appendChild(maki);
    page1.appendChild(circleContainer);
    this.svg.appendChild(page1);
  }
};
const DEFAULT_ICON = MapIcon.Invalid;
const DEFAULT_CIRCLE_COLOR = Color.White.toString();
const DEFAULT_CIRCLE_SIZE = 4;
const DEFAULT_PIN_COLOR = Color.White.toString();
const DEFAULT_RECTANGLE_COLOR = Color.White.toString();
const DEFAULT_RECTANGLE_SIZE = 16;
class WaypointImageManager {
  constructor(imagePrefix) {
    this.imagePrefix = imagePrefix;
    this.mapService = inject(MapService);
    this.imagePrefix += `--`;
  }
  generateId(waypoint) {
    let id = this.imagePrefix;
    switch (waypoint.type) {
      case MapWaypointType.Circle:
        id += `circle-`;
        id += `${waypoint.color ?? DEFAULT_CIRCLE_COLOR}-`;
        id += `${waypoint.size ?? DEFAULT_CIRCLE_SIZE}`;
        break;
      case MapWaypointType.CustomImage:
        id += `customImage-`;
        id += `${waypoint.customIcon}`;
        break;
      case MapWaypointType.CustomElement:
        id += `customElement-`;
        id += `${waypoint.customElement?.src}`;
        break;
      case MapWaypointType.Icon:
        id += `icon-${waypoint.icon ?? DEFAULT_ICON}`;
        break;
      case MapWaypointType.Pin:
        id += `pin-${waypoint.color ?? DEFAULT_PIN_COLOR}`;
        break;
      case MapWaypointType.Rectangle:
        id += `rectangle-`;
        id += `${waypoint.color ?? DEFAULT_RECTANGLE_COLOR}-`;
        id += `${waypoint.size ?? DEFAULT_RECTANGLE_SIZE}`;
        break;
      default:
        throw new Error(`Unsupported waypoint type`);
    }
    return this.sanitize(id);
  }
  update(waypoints) {
    this.addNewImages(waypoints);
    this.removeOldImages(waypoints);
  }
  addNewImages(waypoints) {
    const newImages = new Map();
    for (const waypoint of waypoints) {
      const id = this.generateId(waypoint);
      if (!newImages.has(id) && !this.mapService.icon.has(id)) {
        newImages.set(id, this.createImage(id, waypoint));
      }
    }
    if (newImages.size > 0) {
      this.mapService.icon.add([...newImages.values()]);
    }
  }
  createImage(id, waypoint) {
    switch (waypoint.type) {
      case MapWaypointType.Circle:
        return this.createCircleImage(id, waypoint.color ?? DEFAULT_CIRCLE_COLOR, waypoint.size ?? DEFAULT_CIRCLE_SIZE);
      case MapWaypointType.CustomImage:
        if (waypoint.customIcon == null) {
          throw new Error('CustomImage waypoints must provide a customIcon property');
        }
        return this.createCustomImage(id, waypoint.customIcon);
      case MapWaypointType.CustomElement:
        if (waypoint.customElement == null) {
          throw new Error('CustomElement waypoints must provide a customElement property');
        }
        return this.createCustom2Image(id, waypoint.customElement);
        break;
      case MapWaypointType.Icon:
        return this.createIconImage(id, waypoint.icon ?? DEFAULT_ICON);
      case MapWaypointType.Pin:
        return this.createPinImage(id, waypoint.color ?? DEFAULT_PIN_COLOR);
      case MapWaypointType.Rectangle:
        return this.createRectangleImage(id, waypoint.color ?? DEFAULT_RECTANGLE_COLOR, waypoint.size ?? DEFAULT_RECTANGLE_SIZE);
      default:
        throw new Error(`Unsupported waypoint type`);
    }
  }
  createCircleImage(id, color, size) {
    return {
      id,
      src: new Circle((size + 1) * 2, size, color, 'black').rasterize()
    };
  }
  createCustomImage(id, icon) {
    return {
      id,
      src: icon
    };
  }
  createCustom2Image(id, element) {
    return {
      id,
      element
    };
  }
  createIconImage(id, icon) {
    const definition = MapIconDefinitions.get(icon);
    if (definition != null) {
      return {
        ...definition,
        id
      };
    }
    return {
      id: MapIcon.Invalid
    };
  }
  createPinImage(id, color) {
    return {
      id,
      src: new Marker$1({
        color
      }).rasterize()
    };
  }
  createRectangleImage(id, color, size) {
    return {
      id,
      src: new Rectangle(size, size, color, 4).rasterize()
    };
  }
  removeOldImages(waypoints) {
    const oldImages = new Set();
    for (const id of this.mapService.icon.list()) {
      if (id.startsWith(this.imagePrefix)) {
        const found = waypoints.some(waypoint => {
          const waypointId = this.generateId(waypoint);
          return waypointId === id;
        });
        if (!found && !oldImages.has(id)) {
          oldImages.add(id);
        }
      }
    }
    this.mapService.icon.remove([...oldImages.values()].map(x => {
      return {
        id: x
      };
    }));
  }
  sanitize(x) {
    // ':' in an image name denotes a sprite sheet
    return x.replace(':', '_');
  }
}
class RouteLinesLayer extends SubLayer {
  constructor(injector, radialMenuMapService, priority) {
    super(injector, radialMenuMapService, undefined, undefined);
    this.priority = priority;
    this.LAYER_PREFIX = 'route-lines';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.priority, {
        id: this.layerId,
        source: this.sourceId,
        type: 'line',
        filter: ['==', ['get', 'layerType'], 'routeLine'],
        paint: {
          'line-color': this.getLineColor(),
          'line-width': 4
        },
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerColor(this.layerId, this.getLineColor());
    }
  }
  getLineColor() {
    const activeLayer = this.mapSettings.activeMapLayer();
    const useAltLineColor = activeLayer === MapOverlayLayer.VFRSectional || activeLayer === MapOverlayLayer.IFREnroute || activeLayer === MapOverlayLayer.StreetMap;
    return ['coalesce', ['get', 'color'], useAltLineColor ? Color.Teal.toString() : Color.White.toString()];
  }
}
class RouteOutlinesLayer extends SubLayer {
  constructor(injector, radialMenuMapService, priority) {
    super(injector, radialMenuMapService, undefined, undefined);
    this.priority = priority;
    this.LAYER_PREFIX = 'route-outlines';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.priority, {
        id: this.layerId,
        source: this.sourceId,
        type: 'line',
        filter: ['==', ['get', 'layerType'], 'routeLine'],
        paint: {
          'line-color': Color.Gray10.toString(),
          'line-width': 6
        },
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        }
      });
    }
  }
  _update(_map) {
    // nothing to update
  }
}
class WaypointIconLayer extends SubLayer {
  constructor(injector, radialMenuMapService, priority) {
    super(injector, radialMenuMapService, undefined, LayerType.UserWaypoints);
    this.priority = priority;
    this.LAYER_PREFIX = 'waypoints-icon';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.priority, {
        id: this.layerId,
        source: this.sourceId,
        type: 'symbol',
        filter: ['all', ['==', ['get', 'layerType'], 'waypoint'], ['>=', ['zoom'], ['number', ['coalesce', ['get', 'iconMinZoom'], ZoomLevel.All]]]],
        layout: {
          'icon-allow-overlap': true,
          'icon-image': this.getIconImage(),
          'icon-size': .75
        }
      });
    }
  }
  _update(_map) {
    // nothing to update
  }
  getIconImage() {
    return ['get', 'icon'];
  }
}
class WaypointTextLayer extends SubLayer {
  constructor(injector, radialMenuMapService, priority) {
    super(injector, radialMenuMapService, undefined, LayerType.UserWaypoints);
    this.priority = priority;
    this.LAYER_PREFIX = 'waypoints-text';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.priority, {
        id: this.layerId,
        source: this.sourceId,
        type: 'symbol',
        filter: ['all', ['has', 'name'], ['==', ['get', 'layerType'], 'waypoint'], ['>=', ['zoom'], ['number', ['coalesce', ['get', 'nameMinZoom'], ZoomLevel.All]]]],
        paint: {
          'text-color': this.getTextColor()
        },
        layout: {
          'icon-text-fit': 'both',
          'icon-image': this.getIconImage(),
          'text-field': this.getTextField(),
          'text-size': 12,
          'text-font': ['Roboto'],
          'text-offset': [0, -1.5],
          'text-optional': true
        }
      });
    }
  }
  _update(_map) {
    // nothing to update
  }
  getIconImage() {
    return ['get', 'billboard'];
  }
  getTextColor() {
    return ['get', 'textColor'];
  }
  getTextField() {
    return ['get', 'name'];
  }
  getTextSize() {
    return ['get', 'textSize'];
  }
}
class ProcedurePolygonFillLayer extends SubLayer {
  constructor(injector, radialMenuMapService, priority) {
    super(injector, radialMenuMapService, undefined, undefined);
    this.priority = priority;
    this.LAYER_PREFIX = 'procedure-polygon-fill';
  }
  setOpacity(_opacity) {
    // NO-OP. Opacity is set directly when this layer is added (shown).
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.priority, {
        id: this.layerId,
        source: this.sourceId,
        type: 'fill',
        filter: this.getFilter(),
        paint: {
          'fill-color': this.getFillColor(),
          'fill-opacity': 0.25
        }
      });
    }
  }
  _update(_map) {
    // nothing to update
  }
  getFilter() {
    return ['all', ['==', ['get', 'layerType'], 'procedurePolygon'], ['has', 'color']];
  }
  getFillColor() {
    return ['get', 'color'];
  }
}
class ProcedurePolygonOutlineFillLayer extends SubLayer {
  constructor(injector, radialMenuMapService, priority) {
    super(injector, radialMenuMapService, undefined, undefined);
    this.priority = priority;
    this.LAYER_PREFIX = 'procedure-polygon-outline';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.priority, {
        id: this.layerId,
        source: this.sourceId,
        type: 'line',
        filter: this.getFilter(),
        paint: {
          'line-color': Color.Gray10.toString()
        }
      });
    }
  }
  _update(_map) {
    // nothing to update
  }
  getFilter() {
    return ['all', ['==', ['get', 'layerType'], 'procedurePolygon'], ['has', 'color']];
  }
}
class ProcedurePolygonTextLayer extends SubLayer {
  constructor(injector, radialMenuMapService, priority, name) {
    super(injector, radialMenuMapService, undefined, undefined);
    this.priority = priority;
    this.name = name;
    this.LAYER_PREFIX = 'procedure-polygon-text';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.priority, {
        id: this.layerId,
        source: this.sourceId,
        type: 'symbol',
        filter: this.getFilter(),
        paint: {
          'text-color': this.getTextColor()
        },
        layout: {
          'icon-allow-overlap': true,
          'text-allow-overlap': true,
          'icon-text-fit': 'both',
          'icon-image': this.getIconImage(),
          'text-field': this.getTextField(),
          'text-font': ['Roboto'],
          'text-size': 12
        }
      });
    }
  }
  _update(_map) {
    // nothing to update
  }
  getFilter() {
    return ['all', ['==', ['get', 'layerType'], 'procedureLabel'], ['has', 'name'], ['==', ['get', 'name'], this.name]];
  }
  getIconImage() {
    return ['get', 'billboard'];
  }
  getTextColor() {
    return ['get', 'textColor'];
  }
  getTextField() {
    return ['get', 'name'];
  }
}
const DEFAULT_ICON_VISIBILITY$2 = ZoomLevel.All;
const DEFAULT_NAME_VISIBILITY$2 = ZoomLevel.All;
const DEFAULT_TEXT_COLOR$2 = 'black';
const DEFAULT_TEXT_SIZE$2 = 14;
class ProcedureLayer extends LayerGroup {
  constructor(injector, radialMenuMapService, imageManager, billboardManager) {
    super(injector, radialMenuMapService);
    this.imageManager = imageManager;
    this.billboardManager = billboardManager;
    this.SOURCE_PREFIX = 'procedures';
    this.procedureTextLayers = new Map();
    this.groupInfo = [{
      layer: new ProcedurePolygonOutlineFillLayer(this.injector, this.radialMenuMapService, LayerPriority.Procedures)
    }, {
      layer: new ProcedurePolygonFillLayer(this.injector, this.radialMenuMapService, LayerPriority.Procedures)
    }, {
      layer: new RouteOutlinesLayer(this.injector, this.radialMenuMapService, LayerPriority.Procedures)
    }, {
      layer: new RouteLinesLayer(this.injector, this.radialMenuMapService, LayerPriority.Procedures)
    }, {
      layer: new WaypointIconLayer(this.injector, this.radialMenuMapService, LayerPriority.Procedures)
    }, {
      layer: new WaypointTextLayer(this.injector, this.radialMenuMapService, LayerPriority.Procedures)
    }];
  }
  updateProcedureTextLayers() {
    const map = this.map();
    const procedures = this.mapData.procedures();
    const presentLayers = [];
    if (map != null) {
      // Adding new layers
      if (this.sourceId != null) {
        for (const procedure of procedures) {
          const name = procedure.name;
          if (name != null) {
            presentLayers.push(name);
            const layer = this.procedureTextLayers.get(name);
            if (layer != null) {
              layer.update(map);
            } else {
              const newLayer = new ProcedurePolygonTextLayer(this.injector, this.radialMenuMapService, LayerPriority.Procedures, name);
              newLayer.show(map, this.sourceId);
              this.procedureTextLayers.set(name, newLayer);
            }
          }
        }
      }
      // Removing old layers
      for (const [name, layer] of this.procedureTextLayers) {
        if (!presentLayers.includes(name)) {
          layer.hide(map);
          this.procedureTextLayers.delete(name);
        }
      }
    }
  }
  hide() {
    const map = this.map();
    if (map != null) {
      for (const [_name, layer] of this.procedureTextLayers) {
        layer.hide(map);
      }
    }
    this.procedureTextLayers.clear();
    super.hide();
  }
  show() {
    super.show();
    this.updateProcedureTextLayers();
  }
  update() {
    const map = this.map();
    if (map != null && this.sourceId != null) {
      map.setGeoJsonSourceData(this.sourceId, this.getData());
    }
    super.update();
    this.updateProcedureTextLayers();
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'geojson',
      data: this.getData()
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
  getData() {
    const features = [];
    const procedures = this.mapData.procedures();
    for (const procedure of procedures) {
      propagateMapProcedureProperties(procedure);
      features.push(...this.createProcedureFeatures(procedure));
      for (const transition of procedure.transitions) {
        features.push(...this.createTransitionFeatures(transition));
        for (const waypoint of transition.waypoints) {
          features.push(this.createWaypointFeature(waypoint));
        }
      }
    }
    return turf.featureCollection(features);
  }
  createProcedureFeatures(procedure) {
    const features = [];
    if (procedure.color != null || procedure.name != null) {
      // Create a polygon that encompasses all waypoints
      const points = [];
      for (const transition of procedure.transitions) {
        for (const waypoint of transition.waypoints) {
          points.push(turf.point([waypoint.longitude, waypoint.latitude]));
        }
      }
      const polygon = turf.convex(turf.featureCollection(points));
      if (polygon != null) {
        // Show the polygon with the specified color
        if (procedure.color != null) {
          polygon.properties = this.createProcedurePolygonProperties(procedure);
          features.push(polygon);
        }
        // Create a point at the center of mass of the polygon for a text label
        if (procedure.name != null) {
          const center = turf.centerOfMass(polygon);
          center.properties = this.createProcedureLabelProperties(procedure);
          features.push(center);
        }
      }
    }
    return features;
  }
  createProcedurePolygonProperties(procedure) {
    const properties = {
      layerType: 'procedurePolygon'
    };
    if (procedure.color != null) {
      properties.color = procedure.color;
    }
    return properties;
  }
  createProcedureLabelProperties(procedure) {
    const properties = {
      layerType: 'procedureLabel',
      billboard: this.billboardManager.generateId(procedure),
      textColor: procedure.textColor ?? DEFAULT_TEXT_COLOR$2
    };
    if (procedure.name != null) {
      properties.name = procedure.name;
    }
    return properties;
  }
  createTransitionFeatures(transition) {
    const features = [];
    // Create lines between each waypoint on the route
    for (let i = 0; i < transition.waypoints.length - 1; i++) {
      const line = turf.line([[transition.waypoints[i].longitude, transition.waypoints[i].latitude], [transition.waypoints[i + 1].longitude, transition.waypoints[i + 1].latitude]], this.createTransitionLineProperties(transition.waypoints[i + 1]));
      features.push(line);
    }
    return features;
  }
  createTransitionLineProperties(toWaypoint) {
    const properties = {
      layerType: 'routeLine'
    };
    if (toWaypoint.previousLegColor != null) {
      properties.color = toWaypoint.previousLegColor;
    }
    return properties;
  }
  createWaypointFeature(waypoint) {
    return turf.point([waypoint.longitude, waypoint.latitude], this.createWaypointProperties(waypoint));
  }
  createWaypointProperties(waypoint) {
    const properties = {
      layerType: 'waypoint',
      icon: this.imageManager.generateId(waypoint),
      iconMinZoom: waypoint.iconMinZoom ?? DEFAULT_ICON_VISIBILITY$2,
      billboard: this.billboardManager.generateId(waypoint),
      textColor: waypoint.textColor ?? DEFAULT_TEXT_COLOR$2,
      textSize: waypoint.textSize ?? DEFAULT_TEXT_SIZE$2,
      nameMinZoom: waypoint.nameMinZoom ?? DEFAULT_NAME_VISIBILITY$2,
      data: waypoint.data
    };
    if (waypoint.type != null) {
      properties.type = waypoint.type;
    }
    if (waypoint.name != null) {
      properties.name = waypoint.name;
    }
    if (waypoint.color != null) {
      properties.color = waypoint.color;
    }
    return properties;
  }
}
let ProcedureService = /*#__PURE__*/(() => {
  class ProcedureService extends LayerService {
    constructor() {
      super();
      this.billboardManager = new TextBillboardManager('procedureBillboard');
      this.imageManager = new WaypointImageManager('procedureImage');
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
    }
    createLayer(_content) {
      return new ProcedureLayer(this.injector, this.radialMenuMapService, this.imageManager, this.billboardManager);
    }
    getContent() {
      return [{
        ...EmptyContent,
        type: 'PROCEDURES',
        domain: 'GLOBAL'
      }];
    }
    getVisible() {
      const procedures = this.mapData.procedures();
      for (const procedure of procedures) {
        if (isMapProcedure(procedure)) {
          for (const transition of procedure.transitions) {
            if (isMapRoute(transition)) {
              for (const waypoint of transition.waypoints) {
                if (isMapWaypoint(waypoint)) {
                  return true;
                }
              }
            }
          }
        }
      }
      return false;
    }
    updateImages() {
      const procedures = this.mapData.procedures();
      for (const procedure of procedures) {
        propagateMapProcedureProperties(procedure);
      }
      this.billboardManager.update(procedures);
      this.imageManager.update(extractMapWaypoints(procedures));
    }
    static {
      this.ɵfac = function ProcedureService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProcedureService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProcedureService,
        factory: ProcedureService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProcedureService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class WaypointAirportIconLayer extends SubLayer {
  constructor(injector, radialMenuMapService, priority) {
    super(injector, radialMenuMapService, undefined, LayerType.Airports);
    this.priority = priority;
    this.LAYER_PREFIX = 'waypoints-airport-icon';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.priority, {
        id: this.layerId,
        source: this.sourceId,
        type: 'symbol',
        filter: ['all', ['==', ['get', 'layerType'], 'airport-waypoint'], ['>=', ['zoom'], ['number', ['coalesce', ['get', 'iconMinZoom'], ZoomLevel.All]]]],
        layout: {
          'icon-allow-overlap': true,
          'icon-image': this.getIconImage()
        }
      });
    }
  }
  _update(_map) {
    // nothing to update
  }
  getIconImage() {
    return ['get', 'icon'];
  }
}
class WaypointAirportTextLayer extends SubLayer {
  constructor(injector, radialMenuMapService, priority) {
    super(injector, radialMenuMapService, undefined, LayerType.Airports);
    this.priority = priority;
    this.LAYER_PREFIX = 'waypoints-airport-text';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.priority, {
        id: this.layerId,
        source: this.sourceId,
        type: 'symbol',
        filter: ['all', ['has', 'airportId'], ['==', ['get', 'layerType'], 'airport-waypoint'], ['>=', ['zoom'], ['number', ['coalesce', ['get', 'nameMinZoom'], ZoomLevel.All]]]],
        paint: {
          'text-color': this.getTextColor()
        },
        layout: {
          'icon-text-fit': 'both',
          'icon-image': this.getIconImage(),
          'text-field': this.getTextField(),
          'text-size': this.getTextSize(),
          'text-font': ['Roboto'],
          'text-offset': [0, -2],
          'text-optional': true
        }
      });
    }
  }
  _update(_map) {
    // nothing to update
  }
  getIconImage() {
    return ['get', 'billboard'];
  }
  getTextColor() {
    return ['get', 'textColor'];
  }
  getTextField() {
    return ['get', 'airportId'];
  }
  getTextSize() {
    return ['get', 'textSize'];
  }
}
const DEFAULT_ICON_VISIBILITY$1 = ZoomLevel.All;
const DEFAULT_NAME_VISIBILITY$1 = ZoomLevel.All;
const DEFAULT_TEXT_COLOR$1 = 'black';
const DEFAULT_TEXT_SIZE$1 = 14;
class RouteLayer extends LayerGroup {
  constructor(injector, radialMenuMapService, imageManager, billboardManager) {
    super(injector, radialMenuMapService);
    this.imageManager = imageManager;
    this.billboardManager = billboardManager;
    this.SOURCE_PREFIX = 'routes';
    this.groupInfo = [{
      layer: new RouteOutlinesLayer(this.injector, this.radialMenuMapService, LayerPriority.Routes)
    }, {
      layer: new RouteLinesLayer(this.injector, this.radialMenuMapService, LayerPriority.Routes)
    }, {
      layer: new WaypointIconLayer(this.injector, this.radialMenuMapService, LayerPriority.Routes)
    }, {
      layer: new WaypointAirportIconLayer(this.injector, this.radialMenuMapService, LayerPriority.Routes)
    }, {
      layer: new WaypointAirportTextLayer(this.injector, this.radialMenuMapService, LayerPriority.Routes)
    }, {
      layer: new WaypointTextLayer(this.injector, this.radialMenuMapService, LayerPriority.Routes)
    }];
  }
  update() {
    const map = this.map();
    if (map != null && this.sourceId != null) {
      map.setGeoJsonSourceData(this.sourceId, this.getData());
    }
    super.update();
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'geojson',
      data: this.getData()
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
  getData() {
    const features = [];
    const routes = this.mapData.routes();
    for (const route of routes) {
      features.push(...this.createRouteFeatures(route));
      for (const waypoint of route.waypoints) {
        features.push(this.createWaypointFeature(waypoint));
      }
    }
    return turf.featureCollection(features);
  }
  createRouteFeatures(route) {
    const features = [];
    // Create lines between each waypoint on the route
    for (let i = 0; i < route.waypoints.length - 1; i++) {
      const line = turf.greatCircle([route.waypoints[i].longitude, route.waypoints[i].latitude], [route.waypoints[i + 1].longitude, route.waypoints[i + 1].latitude], {
        properties: this.createRouteLineProperties(route.waypoints[i + 1])
      });
      features.push(line);
    }
    return features;
  }
  createRouteLineProperties(toWaypoint) {
    const properties = {
      layerType: 'routeLine'
    };
    if (toWaypoint.previousLegColor != null) {
      properties.color = toWaypoint.previousLegColor;
    }
    return properties;
  }
  createWaypointFeature(waypoint) {
    return turf.point([waypoint.longitude, waypoint.latitude], this.createWaypointProperties(waypoint));
  }
  createWaypointProperties(waypoint) {
    const properties = {
      layerType: 'waypoint',
      icon: this.imageManager.generateId(waypoint),
      iconMinZoom: waypoint.iconMinZoom ?? DEFAULT_ICON_VISIBILITY$1,
      billboard: this.billboardManager.generateId(waypoint),
      textColor: waypoint.textColor ?? DEFAULT_TEXT_COLOR$1,
      textSize: waypoint.textSize ?? DEFAULT_TEXT_SIZE$1,
      nameMinZoom: waypoint.nameMinZoom ?? DEFAULT_NAME_VISIBILITY$1,
      data: waypoint.data,
      ...waypoint.properties
    };
    if (waypoint.type != null) {
      properties.type = waypoint.type;
    }
    if (waypoint.name != null) {
      properties.name = waypoint.name;
    }
    if (waypoint.color != null) {
      properties.color = waypoint.color;
    }
    if (waypoint.id != null) {
      properties.airportId = waypoint.id;
      properties.layerType = 'airport-waypoint';
    }
    return properties;
  }
}
let RouteService = /*#__PURE__*/(() => {
  class RouteService extends LayerService {
    constructor() {
      super();
      this.billboardManager = new TextBillboardManager('routeBillboard');
      this.imageManager = new WaypointImageManager('routeImage');
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
    }
    createLayer(_content) {
      return new RouteLayer(this.injector, this.radialMenuMapService, this.imageManager, this.billboardManager);
    }
    getContent() {
      return [{
        ...EmptyContent,
        type: 'ROUTES',
        domain: 'GLOBAL'
      }];
    }
    getVisible() {
      const routes = this.mapData.routes();
      for (const route of routes) {
        if (route.waypoints.length > 0) {
          return true;
        }
      }
      return false;
    }
    updateImages() {
      const routes = this.mapData.routes();
      this.billboardManager.update(routes);
      this.imageManager.update(extractMapWaypoints(routes));
    }
    static {
      this.ɵfac = function RouteService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RouteService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: RouteService,
        factory: RouteService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return RouteService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const DEFAULT_ICON_VISIBILITY = ZoomLevel.All;
const DEFAULT_NAME_VISIBILITY = ZoomLevel.All;
const DEFAULT_TEXT_COLOR = 'black';
const DEFAULT_TEXT_SIZE = 14;
class WaypointLayer extends LayerGroup {
  constructor(injector, radialMenuMapService, imageManager, billboardManager) {
    super(injector, radialMenuMapService);
    this.imageManager = imageManager;
    this.billboardManager = billboardManager;
    this.SOURCE_PREFIX = 'waypoints';
    this.groupInfo = [{
      layer: new WaypointIconLayer(this.injector, this.radialMenuMapService, LayerPriority.Waypoints)
    }, {
      layer: new WaypointTextLayer(this.injector, this.radialMenuMapService, LayerPriority.Waypoints)
    }];
  }
  update() {
    const map = this.map();
    if (map != null && this.sourceId != null) {
      map.setGeoJsonSourceData(this.sourceId, this.getData());
    }
    super.update();
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'geojson',
      data: this.getData()
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
  getData() {
    const waypoints = this.mapData.waypoints();
    return {
      type: 'FeatureCollection',
      features: waypoints.flatMap(waypoint => {
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [waypoint.longitude, waypoint.latitude]
          },
          properties: this.createProperties(waypoint)
        };
      })
    };
  }
  createProperties(waypoint) {
    const properties = {
      layerType: 'waypoint',
      icon: this.imageManager.generateId(waypoint),
      iconMinZoom: waypoint.iconMinZoom ?? DEFAULT_ICON_VISIBILITY,
      billboard: this.billboardManager.generateId(waypoint),
      textColor: waypoint.textColor ?? DEFAULT_TEXT_COLOR,
      textSize: waypoint.textSize ?? DEFAULT_TEXT_SIZE,
      nameMinZoom: waypoint.nameMinZoom ?? DEFAULT_NAME_VISIBILITY,
      data: waypoint.data
    };
    if (waypoint.type != null) {
      properties.type = waypoint.type;
    }
    if (waypoint.name != null) {
      properties.name = waypoint.name;
    }
    if (waypoint.color != null) {
      properties.color = waypoint.color;
    }
    return properties;
  }
}
let WaypointService = /*#__PURE__*/(() => {
  class WaypointService extends LayerService {
    constructor() {
      super();
      this.billboardManager = new TextBillboardManager('waypointBillboard');
      this.imageManager = new WaypointImageManager('waypointImage');
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
    }
    createLayer(_content) {
      return new WaypointLayer(this.injector, this.radialMenuMapService, this.imageManager, this.billboardManager);
    }
    getContent() {
      return [{
        ...EmptyContent,
        type: 'WAYPOINTS',
        domain: 'GLOBAL'
      }];
    }
    getVisible() {
      const waypoints = this.mapData.waypoints();
      return waypoints.length > 0;
    }
    updateImages() {
      const waypoints = this.mapData.waypoints();
      this.billboardManager.update(waypoints);
      this.imageManager.update(waypoints);
    }
    static {
      this.ɵfac = function WaypointService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || WaypointService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: WaypointService,
        factory: WaypointService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return WaypointService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class LocationHighlightLayer extends SimpleLayer {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'location-highlight';
    this.LAYER_PREFIX = 'location-highlight';
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'geojson',
      data: this.getData()
    });
    return this.sourceId;
  }
  addLayer(map, sourceId, layerId) {
    map.addLayer(LayerPriority.HighlightLocation, {
      id: layerId,
      source: sourceId,
      type: 'symbol',
      layout: {
        'icon-image': MapIcon.Highlight_Circle,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': 0.5
      }
    });
  }
  update() {
    const map = this.map();
    if (map != null && this.sourceId != null) {
      map.setGeoJsonSourceData(this.sourceId, this.getData());
    }
    super.update();
  }
  getData() {
    const locations = this.mapHighlight.location();
    return {
      type: 'FeatureCollection',
      features: locations.map(location => {
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [location.longitude, location.latitude]
          },
          properties: []
        };
      })
    };
  }
}
let LocationHighlightService = /*#__PURE__*/(() => {
  class LocationHighlightService extends LayerService {
    constructor() {
      super();
      this.highlightImages = [MapIcon.Highlight_Circle].map(x => MapIconDefinitions.get(x));
      effect(() => {
        this.updateLayers();
        this.updateImages();
      });
    }
    createLayer(_content) {
      return new LocationHighlightLayer(this.injector, this.radialMenuMapService);
    }
    getContent() {
      return [{
        ...EmptyContent,
        type: 'LOCATION_HIGHLIGHT',
        domain: 'GLOBAL'
      }];
    }
    getVisible() {
      return this.mapConfig.highlight.location.visible() && this.mapHighlight.location().length > 0;
    }
    updateImages() {
      if (this.mapHighlight.location().length > 0) {
        this.mapIcon.add(this.highlightImages);
      } else {
        this.mapIcon.remove(this.highlightImages);
      }
    }
    static {
      this.ɵfac = function LocationHighlightService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LocationHighlightService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LocationHighlightService,
        factory: LocationHighlightService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LocationHighlightService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const AIRMET_SIGMET_SCALE_OPACITY = 0.4;
class AnimationLayerGroup extends LayerGroup {
  constructor() {
    super(...arguments);
    this.loaded = false;
  }
  subscribeOnSourceData(map, sourceId) {
    if (this.onSourceData != null) {
      map.off('sourcedata', this.onSourceData);
    }
    this.onSourceData = ev => {
      if (ev.sourceId === sourceId) {
        if (this.loaded !== ev.isSourceLoaded) {
          this.loaded = ev.isSourceLoaded;
          untracked(() => this.updateLoadedRange());
        }
      }
    };
    map.on('sourcedata', this.onSourceData);
  }
  addSource(map) {
    const sourceId = this.addAnimationSource(map);
    this.subscribeOnSourceData(map, sourceId);
    untracked(() => this.updateAvailableRange());
    return sourceId;
  }
  removeSource(map) {
    this.removeAnimationSource(map);
    if (this.onSourceData != null) {
      map.off('sourcedata', this.onSourceData);
      this.onSourceData = undefined;
    }
    untracked(() => this.updateAvailableRange());
    untracked(() => this.updateLoadedRange());
  }
}
const GRAPHICAL_AIRMET_DURATION_HOURS = 3;
var AirmetSigmetType = /*#__PURE__*/function (AirmetSigmetType) {
  AirmetSigmetType[AirmetSigmetType["Airmet"] = 0] = "Airmet";
  AirmetSigmetType[AirmetSigmetType["Sigmet"] = 1] = "Sigmet";
  AirmetSigmetType[AirmetSigmetType["GraphicalAirmet"] = 2] = "GraphicalAirmet";
  return AirmetSigmetType;
}(AirmetSigmetType || {});
var AirmetSigmetClassification = /*#__PURE__*/function (AirmetSigmetClassification) {
  AirmetSigmetClassification[AirmetSigmetClassification["Cancellation"] = 0] = "Cancellation";
  AirmetSigmetClassification[AirmetSigmetClassification["Convective"] = 1] = "Convective";
  AirmetSigmetClassification[AirmetSigmetClassification["Dust"] = 2] = "Dust";
  AirmetSigmetClassification[AirmetSigmetClassification["Icing"] = 3] = "Icing";
  AirmetSigmetClassification[AirmetSigmetClassification["IFR"] = 4] = "IFR";
  AirmetSigmetClassification[AirmetSigmetClassification["MountainObscuration"] = 5] = "MountainObscuration";
  AirmetSigmetClassification[AirmetSigmetClassification["TropicalCyclone"] = 6] = "TropicalCyclone";
  AirmetSigmetClassification[AirmetSigmetClassification["Turbulence"] = 7] = "Turbulence";
  AirmetSigmetClassification[AirmetSigmetClassification["VolcanicAsh"] = 8] = "VolcanicAsh";
  AirmetSigmetClassification[AirmetSigmetClassification["StrongSurfaceWinds"] = 9] = "StrongSurfaceWinds";
  AirmetSigmetClassification[AirmetSigmetClassification["LowLevelWindShear"] = 10] = "LowLevelWindShear";
  AirmetSigmetClassification[AirmetSigmetClassification["FreezingLevel"] = 11] = "FreezingLevel";
  AirmetSigmetClassification[AirmetSigmetClassification["MultipleFreezingLevels"] = 12] = "MultipleFreezingLevels";
  AirmetSigmetClassification[AirmetSigmetClassification["TurbulenceHigh"] = 13] = "TurbulenceHigh";
  AirmetSigmetClassification[AirmetSigmetClassification["TurbulenceLow"] = 14] = "TurbulenceLow";
  AirmetSigmetClassification[AirmetSigmetClassification["Thunderstorm"] = 15] = "Thunderstorm";
  return AirmetSigmetClassification;
}(AirmetSigmetClassification || {});
const AirmetSigmetColors = {
  Yellow: new Color(234, 236, 35),
  Orange: new Color(208, 85, 35),
  LightOrange: new Color(227, 175, 53),
  DarkBlue: new Color(36, 27, 169),
  Green: new Color(110, 167, 67),
  LightGreen: new Color(162, 239, 68),
  Gray: new Color(195, 195, 195),
  DarkGray: new Color(30, 30, 30)
};
const outlookBlendColors = baseColor => {
  return ['case', ['boolean', ['get', 'isOutlook'], false], baseColor.blendWith(Color.White).toString(), baseColor.toString()];
};
const airmetColor = () => {
  return ['match', ['get', 'classification'], [AirmetSigmetClassification.Convective, AirmetSigmetClassification.Turbulence, AirmetSigmetClassification.TurbulenceHigh, AirmetSigmetClassification.TurbulenceLow], outlookBlendColors(AirmetSigmetColors.Yellow), [AirmetSigmetClassification.Dust, AirmetSigmetClassification.StrongSurfaceWinds, AirmetSigmetClassification.LowLevelWindShear, AirmetSigmetClassification.VolcanicAsh], outlookBlendColors(AirmetSigmetColors.Orange), [AirmetSigmetClassification.Icing, AirmetSigmetClassification.FreezingLevel, AirmetSigmetClassification.MultipleFreezingLevels], outlookBlendColors(AirmetSigmetColors.DarkBlue), AirmetSigmetClassification.IFR, outlookBlendColors(AirmetSigmetColors.LightGreen), AirmetSigmetClassification.MountainObscuration, outlookBlendColors(AirmetSigmetColors.Green), AirmetSigmetClassification.TropicalCyclone, outlookBlendColors(AirmetSigmetColors.Gray), outlookBlendColors(AirmetSigmetColors.DarkGray)];
};
const sigmetColor = () => {
  return ['case', ['boolean', ['get', 'isOutlook'], false], AirmetSigmetColors.Yellow.toString(), AirmetSigmetColors.Orange.toString()];
};
const airmetSigmetColor = type => {
  switch (type) {
    case AirmetSigmetType.Sigmet:
      return sigmetColor();
    case AirmetSigmetType.Airmet:
    case AirmetSigmetType.GraphicalAirmet:
    default:
      return airmetColor();
  }
};
const airmetSigmetIsValid = (type, time) => {
  if (type === AirmetSigmetType.GraphicalAirmet) {
    // graphical airmets use validAtTime and validUntilTime
    return ['all', ['>=', time, ['get', 'validAtTime']], ['<=', time, ['get', 'validUntilTime']]];
  }
  // airmets and sigmets use effectiveTime and expireTime
  return ['all', ['>=', time, ['get', 'effectiveTime']], ['<=', time, ['get', 'expireTime']]];
};
const airmetSigmetFilter = (type, options, time) => {
  return ['all', airmetSigmetOptionsFilter(options), airmetSigmetIsValid(type, time)];
};
const airmetSigmetHighlightFilter = (type, highlights) => {
  const idProperty = type !== AirmetSigmetType.Sigmet ? 'airmetId' : 'sigmetId';
  return ['match', ['get', idProperty], highlights.flatMap(x => x.id), true, false];
};
const airmetSigmetOptionsFilter = options => {
  const shownTypes = [];
  if (options?.icing === true) {
    shownTypes.push(AirmetSigmetClassification.Icing);
  }
  if (options?.ifrMountain === true) {
    shownTypes.push(AirmetSigmetClassification.IFR, AirmetSigmetClassification.MountainObscuration);
  }
  if (options?.convective === true) {
    shownTypes.push(AirmetSigmetClassification.Convective);
  }
  if (options?.turbulence === true) {
    shownTypes.push(AirmetSigmetClassification.Turbulence, AirmetSigmetClassification.TurbulenceHigh, AirmetSigmetClassification.TurbulenceLow);
  }
  if (options?.lowLevelWindShearAndSurfaceWinds === true) {
    shownTypes.push(AirmetSigmetClassification.LowLevelWindShear, AirmetSigmetClassification.StrongSurfaceWinds);
  }
  if (options?.volcanicAsh === true) {
    shownTypes.push(AirmetSigmetClassification.VolcanicAsh);
  }
  if (options?.tropical === true) {
    shownTypes.push(AirmetSigmetClassification.TropicalCyclone);
  }
  if (shownTypes.length === 0) {
    return ['boolean', false];
  }
  return ['match', ['get', 'classification'], shownTypes, true, false];
};
class AirmetSigmetSubLayer extends SubLayer {
  constructor(injector, airmetSigmetType, radialMenuMapService, sourceLayerId, layerType) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.airmetSigmetType = airmetSigmetType;
  }
  getPriority(isHighlight) {
    switch (this.airmetSigmetType) {
      case AirmetSigmetType.Sigmet:
        return isHighlight ? LayerPriority.HighlightSigmet : LayerPriority.Sigmet;
      case AirmetSigmetType.GraphicalAirmet:
      case AirmetSigmetType.Airmet:
      default:
        return isHighlight ? LayerPriority.HighlightAirmet : LayerPriority.Airmet;
    }
  }
}
class AirmetSigmetFillLayer extends AirmetSigmetSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airmet-sigmet-fill';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.getPriority(false), {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'fill',
        'filter': this.getFilter(),
        'paint': {
          'fill-color': this.getFillColor()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
      map.setLayerColor(this.layerId, this.getFillColor());
    }
  }
  getFilter() {
    return airmetSigmetFilter(this.airmetSigmetType, {
      icing: this.mapConfig.overlay.airmetSigmet.options.icing(),
      ifrMountain: this.mapConfig.overlay.airmetSigmet.options.ifrMountain(),
      convective: this.mapConfig.overlay.airmetSigmet.options.convective(),
      turbulence: this.mapConfig.overlay.airmetSigmet.options.turbulence(),
      lowLevelWindShearAndSurfaceWinds: this.mapConfig.overlay.airmetSigmet.options.lowLevelWindShearAndSurfaceWinds(),
      volcanicAsh: this.mapConfig.overlay.airmetSigmet.options.volcanicAsh(),
      tropical: this.mapConfig.overlay.airmetSigmet.options.tropical()
    }, this.mapAnimation.time());
  }
  getFillColor() {
    return airmetSigmetColor(this.airmetSigmetType);
  }
}
class AirmetSigmetHighlightLayer extends AirmetSigmetSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airmet-sigmet-highlight';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.getPriority(true), {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': this.getFilter(),
        'paint': {
          'line-color': Color.White.toString(),
          'line-width': 6
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  getFilter() {
    return airmetSigmetHighlightFilter(this.airmetSigmetType, this.mapHighlight.airmetSigmet());
  }
}
class AirmetSigmetLineLayer extends AirmetSigmetSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'airmet-sigmet-line';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.getPriority(false), {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': this.getFilter(),
        'paint': {
          'line-color': this.getLineColor()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
      map.setLayerColor(this.layerId, this.getLineColor());
    }
  }
  getFilter() {
    return airmetSigmetFilter(this.airmetSigmetType, {
      icing: this.mapConfig.overlay.airmetSigmet.options.icing(),
      ifrMountain: this.mapConfig.overlay.airmetSigmet.options.ifrMountain(),
      convective: this.mapConfig.overlay.airmetSigmet.options.convective(),
      turbulence: this.mapConfig.overlay.airmetSigmet.options.turbulence(),
      lowLevelWindShearAndSurfaceWinds: this.mapConfig.overlay.airmetSigmet.options.lowLevelWindShearAndSurfaceWinds(),
      volcanicAsh: this.mapConfig.overlay.airmetSigmet.options.volcanicAsh(),
      tropical: this.mapConfig.overlay.airmetSigmet.options.tropical()
    }, this.mapAnimation.time());
  }
  getLineColor() {
    return airmetSigmetColor(this.airmetSigmetType);
  }
}
class AirmetLayer extends AnimationLayerGroup {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'airmet';
    this.SOURCE_LAYER = 'airmet';
    this.type = AirmetSigmetType.Airmet;
    this.groupInfo = [{
      layer: new AirmetSigmetFillLayer(this.injector, this.type, this.radialMenuMapService, this.SOURCE_LAYER, LayerType.AirmetsSigmets)
    }, {
      layer: new AirmetSigmetLineLayer(this.injector, this.type, this.radialMenuMapService, this.SOURCE_LAYER, LayerType.AirmetsSigmets)
    }, {
      layer: new AirmetSigmetHighlightLayer(this.injector, this.type, this.radialMenuMapService, this.SOURCE_LAYER, LayerType.AirmetsSigmets),
      shouldBeVisible: () => this.mapHighlight.airmetSigmet().length > 0
    }];
  }
  addAnimationSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      minzoom: 1,
      maxzoom: 16
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeAnimationSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
  updateAvailableRange() {
    const range = [];
    if (this.sourceId != null) {
      // If the source is loaded, we have all the data the is possible
      // to exist therefore do -inf to inf.
      range.push([-Infinity, Infinity]);
    }
    this.mapAnimation.availableRange.airmet.set(range);
  }
  updateLoadedRange() {
    const range = [];
    if (this.loaded) {
      // If the source is loaded, we have all the data the is possible
      // to exist therefore do -inf to inf.
      range.push([-Infinity, Infinity]);
    }
    this.mapAnimation.loadedRange.airmet.set(range);
  }
}
let AirmetService = /*#__PURE__*/(() => {
  class AirmetService extends LayerService {
    constructor() {
      super();
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new AirmetLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.overlay.airmetSigmet.airmetContent();
    }
    getVisible() {
      return this.mapConfig.overlay.airmetSigmet.visible();
    }
    getOpacity() {
      return this.mapConfig.overlay.airmetSigmet.opacity() * AIRMET_SIGMET_SCALE_OPACITY;
    }
    static {
      this.ɵfac = function AirmetService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AirmetService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AirmetService,
        factory: AirmetService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AirmetService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const SOURCE_PREFIX$3 = 'graphical-airmet';
const SOURCE_LAYER$1 = 'gairmet';
class GraphicalAirmetLayer extends AnimationLayerGroup {
  constructor() {
    super(...arguments);
    this.type = AirmetSigmetType.GraphicalAirmet;
    this.groupInfo = [{
      layer: new AirmetSigmetFillLayer(this.injector, this.type, this.radialMenuMapService, SOURCE_LAYER$1, LayerType.AirmetsSigmets)
    }, {
      layer: new AirmetSigmetLineLayer(this.injector, this.type, this.radialMenuMapService, SOURCE_LAYER$1, LayerType.AirmetsSigmets)
    }, {
      layer: new AirmetSigmetHighlightLayer(this.injector, this.type, this.radialMenuMapService, SOURCE_LAYER$1, LayerType.AirmetsSigmets),
      shouldBeVisible: () => this.mapHighlight.airmetSigmet().length > 0
    }];
  }
  addAnimationSource(map) {
    this.sourceId = map.sourceIds.next(SOURCE_PREFIX$3);
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      minzoom: 1,
      maxzoom: 16
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeAnimationSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
  updateAvailableRange() {
    const range = [];
    if (this.sourceId != null) {
      // If the source is loaded, we have all the data the is possible
      // to exist therefore do -inf to inf.
      range.push([-Infinity, Infinity]);
    }
    this.mapAnimation.availableRange.graphicalAirmet.set(range);
  }
  updateLoadedRange() {
    const range = [];
    if (this.loaded) {
      // If the source is loaded, we have all the data the is possible
      // to exist therefore do -inf to inf.
      range.push([-Infinity, Infinity]);
    }
    this.mapAnimation.loadedRange.graphicalAirmet.set(range);
  }
}
let GraphicalAirmetService = /*#__PURE__*/(() => {
  class GraphicalAirmetService extends LayerService {
    constructor() {
      super();
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new GraphicalAirmetLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.overlay.airmetSigmet.graphicalAirmetContent();
    }
    getVisible() {
      return this.mapConfig.overlay.airmetSigmet.visible();
    }
    getOpacity() {
      return this.mapConfig.overlay.airmetSigmet.opacity() * AIRMET_SIGMET_SCALE_OPACITY;
    }
    static {
      this.ɵfac = function GraphicalAirmetService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GraphicalAirmetService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GraphicalAirmetService,
        factory: GraphicalAirmetService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GraphicalAirmetService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class SigmetLayer extends AnimationLayerGroup {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'sigmet';
    this.SOURCE_LAYER = 'sigmet';
    this.type = AirmetSigmetType.Sigmet;
    this.groupInfo = [{
      layer: new AirmetSigmetFillLayer(this.injector, this.type, this.radialMenuMapService, this.SOURCE_LAYER, LayerType.AirmetsSigmets)
    }, {
      layer: new AirmetSigmetLineLayer(this.injector, this.type, this.radialMenuMapService, this.SOURCE_LAYER, LayerType.AirmetsSigmets)
    }, {
      layer: new AirmetSigmetHighlightLayer(this.injector, this.type, this.radialMenuMapService, this.SOURCE_LAYER, LayerType.AirmetsSigmets),
      shouldBeVisible: () => this.mapHighlight.airmetSigmet().length > 0
    }];
  }
  addAnimationSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      minzoom: 1,
      maxzoom: 16
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeAnimationSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
  updateAvailableRange() {
    const range = [];
    if (this.sourceId != null) {
      // If the source is loaded, we have all the data the is possible
      // to exist therefore do -inf to inf.
      range.push([-Infinity, Infinity]);
    }
    this.mapAnimation.availableRange.sigmet.set(range);
  }
  updateLoadedRange() {
    const range = [];
    if (this.loaded) {
      // If the source is loaded, we have all the data the is possible
      // to exist therefore do -inf to inf.
      range.push([-Infinity, Infinity]);
    }
    this.mapAnimation.loadedRange.sigmet.set(range);
  }
}
let SigmetService = /*#__PURE__*/(() => {
  class SigmetService extends LayerService {
    constructor() {
      super();
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new SigmetLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.overlay.airmetSigmet.sigmetContent();
    }
    getVisible() {
      return this.mapConfig.overlay.airmetSigmet.visible();
    }
    getOpacity() {
      return this.mapConfig.overlay.airmetSigmet.opacity() * AIRMET_SIGMET_SCALE_OPACITY;
    }
    static {
      this.ɵfac = function SigmetService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SigmetService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SigmetService,
        factory: SigmetService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SigmetService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class SnapshotMap extends PropertyMap {
  getPropertyKey(key) {
    return key.directory;
  }
}

/**
 * Gets the "available" range for an overlay based on the Content
 * and Snapshots provided.
 * @param content The contents and snapshots
 * @param now The current time
 * @param maximumAge The maximum number of seconds allowed before `now`
 * @param dataValidity The number of seconds a snapshot is considered valid
 */
const getSnapshotAvailableRange = (content, now, maximumAge, dataValidity, onlyPast) => {
  const range = [];
  const oldestAllowedTime = maximumAge != null ? now - maximumAge : 0;
  for (const c of content) {
    let minTime = undefined;
    let maxTime = undefined;
    for (const s of c.snapshots ?? []) {
      const minSnapshotTime = s.time;
      let maxSnapshotTime = s.time + dataValidity;
      if (onlyPast) {
        if (minSnapshotTime > now) {
          continue;
        }
        if (maxSnapshotTime > now) {
          maxSnapshotTime = now;
        }
      }
      if (minTime == null || maxTime == null) {
        minTime = minSnapshotTime;
        maxTime = maxSnapshotTime;
      } else {
        minTime = Math.min(minTime, minSnapshotTime);
        maxTime = Math.max(maxTime, maxSnapshotTime);
      }
    }
    if (minTime != null && maxTime != null) {
      // limit the time based on specified layer parameters
      minTime = Math.max(minTime, oldestAllowedTime);
      maxTime = Math.max(maxTime, oldestAllowedTime);
      range.push([minTime, maxTime]);
    }
  }
  return range;
};
/**
 * Gets the "loaded" range for an overlay based on the provided
 * `loaded` dictionary.
 * @param loaded A dictionary for the loaded status of each relevant layer
 * @param now The current time
 * @param dataValidity The number of seconds a snapshot is considered valid
 */
const getSnapshotLoadedRange = (loaded, now, dataValidity) => {
  const range = [];
  const loadedEntries = Object.entries(loaded).sort(([a, _aLoaded], [b, _bLoaded]) => +a - +b);
  let newRangeStart = undefined;
  let maxTime = 0;
  let prevTime = undefined;
  for (let i = 0; i < loadedEntries.length; i++) {
    const [timeStr, isLoaded] = loadedEntries[i];
    const time = +timeStr;
    maxTime = Math.max(time, maxTime);
    if (newRangeStart == null) {
      if (isLoaded) {
        newRangeStart = time;
        if (time <= now && prevTime != null) {
          newRangeStart = prevTime + 1;
        }
      }
    } else if (!isLoaded) {
      let rangeEnd = time - 1;
      if (time <= now) {
        rangeEnd = prevTime ?? newRangeStart;
      }
      range.push([newRangeStart, rangeEnd]);
      newRangeStart = undefined;
    }
    prevTime = time;
  }
  if (newRangeStart != null) {
    range.push([newRangeStart, maxTime + dataValidity]);
  }
  return range;
};
/**
 * Given a list of snapshots, pick the "correct" one for the situation.
 * @param snapshots
 * @param target
 * @param dataValidity
 */
const getSnapshotByTime = (snapshots, target, now, maximumAge, dataValidity) => {
  const isFutureSearch = target > now;
  let result = undefined;
  const oldestAllowedTime = now - maximumAge;
  if (target < oldestAllowedTime) {
    // the target is outside the allowed range so short-circuit to nothing
    return result;
  }
  const pastCandidates = [];
  for (const snapshot of snapshots) {
    if (snapshot.time === target) {
      // always use an exact match
      result = snapshot;
      break;
    }
    if (snapshot.time < oldestAllowedTime) {
      // don't use data that is too old compared to now
      continue;
    }
    if (isFutureSearch && snapshot.time >= now && snapshot.time <= target) {
      // use the last applicable snapshot for future searches
      result = snapshot;
    } else if (!isFutureSearch && snapshot.time <= now && snapshot.time + dataValidity >= target) {
      pastCandidates.push(snapshot);
    }
  }
  if (result == null && pastCandidates.length > 0) {
    // pick the first candidate >= target
    const sortedCandidates = pastCandidates.sort((a, b) => a.time - b.time);
    for (const candidate of sortedCandidates) {
      if (candidate.time >= target) {
        result = candidate;
        break;
      }
    }
    // if there's still nothing, pick the last one
    if (result == null) {
      result = sortedCandidates.at(-1);
    }
  }
  if (result != null && result.time + dataValidity < target) {
    // don't use data that is too old compared to the target
    result = undefined;
  }
  return result;
};
class RasterAnimationLayerService {
  constructor() {
    this.layers = new ContentMap();
    this.contentObserver = new ContentObserver();
    this.mapService = inject(MapService);
    this.mapAnimation = inject(MapAnimationHandler);
    this._updateDisplay = effect(() => {
      const map = this.mapService.map();
      if (map == null) {
        return;
      }
      const target = this.mapService.animation.time();
      this.display(map, target);
    });
    this._updateLayers = effect(() => {
      const map = this.mapService.map();
      if (map == null) {
        return;
      }
      const content = this.getContent();
      const target = this.mapService.animation.time();
      const changes = this.contentObserver.update(content, this.getVisible());
      for (const c of changes.removed) {
        this.deleteLayers(map, c);
      }
      for (const c of changes.added) {
        this.addLayers(map, c);
      }
      this.updateAvailableRange();
      this.subscribeOnSourceData(map);
      this.subscribeOnMapMoveStart(map);
      this.subscribeOnMapMoveEnd(map);
      this.display(map, target);
    });
  }
  getOpacity() {
    return MAX_OPACITY;
  }
  addLayers(map, content) {
    if (content.snapshots == null) return;
    for (const snapshot of content.snapshots) {
      const layers = this.layers.get(content) ?? new SnapshotMap();
      if (!layers.has(snapshot)) {
        const layer = this.createLayer(content, snapshot);
        layer.add(map);
        layers.set(snapshot, layer);
      }
      this.layers.set(content, layers);
    }
  }
  deleteLayers(map, content) {
    for (const snapshot of content.snapshots ?? []) {
      const layers = this.layers.get(content);
      if (layers != null) {
        const layer = layers.get(snapshot);
        if (layer != null) {
          layer.remove(map);
        }
        layers.delete(snapshot);
        if (layers.size == 0) {
          this.layers.delete(content);
        }
      }
    }
  }
  subscribeOnMapMoveStart(map) {
    if (this.onMapMoveStart != null) {
      map.off('movestart', this.onMapMoveStart);
    }
    this.onMapMoveStart = () => {
      const target = this.mapService.animation.time();
      for (const [_c, sMap] of this.layers) {
        const [_targetSnapshot, targetLayer] = this.getTargetSnapshot(sMap, target);
        for (const [_s, layer] of sMap) {
          if (layer !== targetLayer) {
            layer.unload(map);
          }
        }
      }
    };
    map.on('movestart', this.onMapMoveStart);
  }
  subscribeOnMapMoveEnd(map) {
    if (this.onMapMoveEnd != null) {
      map.off('moveend', this.onMapMoveEnd);
    }
    this.onMapMoveEnd = () => {
      const target = this.mapService.animation.time();
      this.display(map, target);
    };
    map.on('moveend', this.onMapMoveEnd);
  }
  subscribeOnSourceData(map) {
    if (this.onSourceData != null) {
      map.off('sourcedata', this.onSourceData);
    }
    if (this.layers.size !== 0) {
      const sourceList = {};
      for (const [_c, sMap] of this.layers) {
        for (const [_s, layer] of sMap) {
          const id = layer.getSourceId();
          if (id != null) {
            sourceList[id] = layer;
          }
        }
      }
      this.onSourceData = ev => {
        const layer = sourceList[ev.sourceId];
        if (layer != null) {
          layer.loaded = layer.allowedToLoad && ev.isSourceLoaded;
          untracked(() => this.updateLoadedRange());
        }
      };
      map.on('sourcedata', this.onSourceData);
    }
  }
  getTargetSnapshot(snapshotMap, target) {
    const snapshot = getSnapshotByTime(snapshotMap.keys(), target, this.mapAnimation.options()?.now ?? 0, this.MAXIMUM_AGE_SECONDS, this.DATA_VALIDITY_SECONDS);
    if (snapshot != null) {
      const layer = snapshotMap.get(snapshot);
      if (layer != null) {
        return [snapshot, layer];
      }
    }
    return [undefined, undefined];
  }
  display(map, target) {
    const opacity = this.getOpacity();
    for (const [_content, snapshotMap] of this.layers) {
      // show the snapshot closest to the target time
      const [_targetSnapshot, targetLayer] = this.getTargetSnapshot(snapshotMap, target);
      targetLayer?.load(map);
      targetLayer?.show(map);
      targetLayer?.setOpacity(opacity);
      // hide other snapshots
      for (const [_snapshot, layer] of snapshotMap) {
        if (layer !== targetLayer) {
          layer.hide(map);
        }
      }
    }
    const loadAllSnapshots = this.mapService.animation.active();
    if (loadAllSnapshots) {
      for (const [_content, snapshotMap] of this.layers) {
        for (const [_snapshot, layer] of snapshotMap) {
          layer.load(map);
        }
      }
    }
  }
  updateAvailableRange() {
    const range = getSnapshotAvailableRange(this.getContent(), this.mapService.animation.options()?.now ?? 0, this.MAXIMUM_AGE_SECONDS, this.DATA_VALIDITY_SECONDS, this.ONLY_PAST);
    untracked(() => this.setAvailableRange(range));
  }
  updateLoadedRange() {
    const now = this.mapAnimation.options()?.now ?? 0;
    const loaded = {};
    for (const [_c, sMap] of this.layers) {
      for (const [s, layer] of sMap) {
        loaded[s.time] = (loaded[s.time] ?? true) && layer.loaded;
      }
    }
    const range = getSnapshotLoadedRange(loaded, now, this.DATA_VALIDITY_SECONDS);
    untracked(() => this.setLoadedRange(range));
  }
}
class AnimationLayer {
  constructor(mapService, content, snapshot) {
    this.mapService = mapService;
    this.content = content;
    this.snapshot = snapshot;
  }
  getSourceId() {
    return this.sourceId;
  }
  setOpacity(opacity) {
    trySetLayerOpacity(this.mapService.map(), this.layerId, opacity);
  }
  /**
   * Sets the layer opacity to 0.
   */
  hide(_map) {
    // No-operation - can be overridden by child classes
  }
  /**
   * Sets the layer visibility to visible.
   */
  load(_map) {
    // No-operation - can be overridden by child classes
  }
  /**
   * Sets the layer visibililty to none.
   */
  unload(_map) {
    // No-operation - can be overridden by child classes
  }
}
class RasterAnimationLayer extends AnimationLayer {
  constructor() {
    super(...arguments);
    this.loaded = false;
    this.allowedToLoad = false;
    this.visible = false;
  }
  add(map) {
    if (this.snapshot == null) {
      return;
    }
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'raster',
      tiles: [`${this.content.urlBase}/${this.snapshot.directory}/{z}/{x}/{y}.png`],
      scheme: 'tms',
      minzoom: this.MIN_ZOOM,
      maxzoom: this.MAX_ZOOM,
      // The radar and cloud tiles are provided as a 256x256 image
      // (at the time of writing). We're purposefully using 512 instead.
      // This results in MapLibre using a more zoomed out tile but stretching
      // it to cover the same geographic area. This results in a slightly
      // blurrier image but with drastically fewer network requests.
      tileSize: 512
    });
    this.layerId = map.layerIds.next(this.LAYER_PREFIX);
    map.addLayer(this.LAYER_PRIORITY, {
      id: this.layerId,
      source: this.sourceId,
      type: 'raster',
      paint: {
        'raster-opacity': 0
      },
      layout: {
        visibility: 'none'
      }
    });
  }
  hide(map) {
    if (this.visible) {
      if (this.layerId != null) {
        map.setLayerOpacity(this.layerId, 0);
      }
      this.visible = false;
    }
  }
  load(map) {
    if (!this.allowedToLoad) {
      if (this.layerId != null) {
        map.setLayerVisibility(this.layerId, 'visible');
      }
      this.allowedToLoad = true;
    }
  }
  remove(map) {
    if (this.layerId != null) {
      map.removeLayer(this.layerId);
      this.layerId = undefined;
    }
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
    this.visible = false;
    this.allowedToLoad = false;
  }
  show(map) {
    if (!this.visible) {
      if (this.layerId != null) {
        map.setLayerOpacity(this.layerId, 0.8);
      }
      this.visible = true;
    }
  }
  unload(map) {
    if (this.allowedToLoad) {
      if (this.layerId != null) {
        map.setLayerVisibility(this.layerId, 'none');
      }
      this.allowedToLoad = false;
    }
  }
}
class CloudsLayer extends RasterAnimationLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'clouds';
    this.LAYER_PRIORITY = LayerPriority.Clouds;
    this.SOURCE_PREFIX = 'clouds';
    this.MIN_ZOOM = 0;
    this.MAX_ZOOM = 9;
  }
}
let CloudsService = /*#__PURE__*/(() => {
  class CloudsService extends RasterAnimationLayerService {
    constructor() {
      super(...arguments);
      this.MAXIMUM_AGE_SECONDS = 2 * 60 * 60;
      this.DATA_VALIDITY_SECONDS = 30 * 60;
      this.ONLY_PAST = true;
    }
    createLayer(content, snapshot) {
      return new CloudsLayer(this.mapService, content, snapshot);
    }
    getContent() {
      return this.mapService.info.overlay.clouds.content();
    }
    getVisible() {
      return this.mapService.config.overlay.radar.visible() && this.mapService.config.overlay.radar.infraredClouds();
    }
    getOpacity() {
      return this.mapService.config.overlay.radar.opacity();
    }
    setAvailableRange(range) {
      this.mapAnimation.availableRange.clouds.set(range);
    }
    setLoadedRange(range) {
      this.mapAnimation.loadedRange.clouds.set(range);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵCloudsService_BaseFactory;
        return function CloudsService_Factory(__ngFactoryType__) {
          return (ɵCloudsService_BaseFactory || (ɵCloudsService_BaseFactory = i0.ɵɵgetInheritedFactory(CloudsService)))(__ngFactoryType__ || CloudsService);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CloudsService,
        factory: CloudsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CloudsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class FuelPricesLayer extends SimpleLayer {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'fuel-prices';
    this.LAYER_PREFIX = 'fuel-prices';
    this.SOURCE_LAYER = 'fuelPrice';
    this.config = this.mapConfig.overlay.fuelPrices;
  }
  addSource(map, content, sourceId) {
    map.addSource(sourceId, {
      type: 'vector',
      tiles: [`${content.urlBase}/index/{z}/{x}/{y}.mvt`],
      scheme: 'tms'
    });
  }
  addLayer(map, sourceId, layerId) {
    map.addLayer(LayerPriority.FuelPrices, {
      'id': layerId,
      'source': sourceId,
      'source-layer': this.SOURCE_LAYER,
      'type': 'symbol',
      'paint': {
        'text-color': 'white'
      },
      'layout': {
        'icon-image': this.fuelPriceIcon(),
        'icon-text-fit': 'width',
        'text-field': this.fuelPriceText(),
        'text-font': ['Roboto'],
        'text-size': 12,
        'symbol-sort-key': this.getSymbolSortKey()
      }
    });
  }
  update() {
    const map = this.map();
    if (map != null && this.layerId != null) {
      map.setLayerSymbolProperties(this.layerId, {
        iconImage: this.fuelPriceIcon(),
        textField: this.fuelPriceText()
      });
    }
  }
  getSymbolSortKey() {
    // Overlapping is disabled therefore lowest value wins.
    // airportPriority is lowest value wins.
    // Therefore use the priority directly with a default of 1000.
    return ['number', ['get', 'airportPriority'], 1000];
  }
  fuelPriceIcon() {
    const rankProp = this.config.type() === FuelPriceType.Jet ? 'jetPriceRank' : 'avPriceRank';
    return ['match', ['get', rankProp], 0, MapIcon.FuelPrices_Green, 1, MapIcon.FuelPrices_Yellow, 2, MapIcon.FuelPrices_Red, MapIcon.FuelPrices_Gray];
  }
  fuelPriceText() {
    const priceProp = this.config.type() === FuelPriceType.Jet ? 'jetLowestPrice' : 'avLowestPrice';
    return ['case', ['has', priceProp], ['concat', '$', ['get', priceProp]], 'N/A'];
  }
}
let FuelPricesService = /*#__PURE__*/(() => {
  class FuelPricesService extends LayerService {
    constructor() {
      super();
      this.config = this.mapConfig.overlay.fuelPrices;
      this.info = this.mapInfo.overlay.fuelPrices;
      this.images = [MapIcon.FuelPrices_Gray, MapIcon.FuelPrices_Green, MapIcon.FuelPrices_Red, MapIcon.FuelPrices_Yellow].map(x => MapIconDefinitions.get(x));
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new FuelPricesLayer(this.injector, this.radialMenuMapService, content, LayerType.FuelPrices);
    }
    getContent() {
      return this.mapInfo.overlay.fuelPrices.content();
    }
    getVisible() {
      return this.mapConfig.overlay.fuelPrices.visible();
    }
    updateImages() {
      if (this.info.available() && this.config.visible()) {
        this.mapIcon.add(this.images);
      } else {
        this.mapIcon.remove(this.images);
      }
    }
    static {
      this.ɵfac = function FuelPricesService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FuelPricesService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FuelPricesService,
        factory: FuelPricesService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FuelPricesService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * This is an LayerService to handle layers that rely on a
 * Snapshot that contains modelRunTime, elevation, and offset
 * tiles.
 */
class SnapshotLayerService {
  constructor() {
    this.injector = inject(Injector);
    this.map = inject(MapTracker).map;
    this.mapAnimation = inject(MapAnimationHandler);
    this.mapConfig = inject(MapConfigHandler);
    this.mapIcon = inject(MapIconHandler);
    this.mapInfo = inject(MapInfo);
    this.layers = new ContentMap();
    this.oldestAllowedTime = computed(() => {
      const now = this.mapAnimation.options()?.now;
      if (now != null && this.MAXIMUM_AGE_SECONDS != null) {
        return now - this.MAXIMUM_AGE_SECONDS;
      }
      return 0;
    });
    this.DISABLE_ON_SOURCE_DATA = false;
    this._updateAvailableRange = effect(() => {
      let range = [];
      if (this.MINIMUM_ALTITUDE != null || this.MAXIMUM_ALTITUDE != null) {
        const altitude = this.mapConfig.overlay.altitude();
        if ((this.MINIMUM_ALTITUDE == null || altitude >= this.MINIMUM_ALTITUDE) && (this.MAXIMUM_ALTITUDE == null || altitude <= this.MAXIMUM_ALTITUDE)) {
          range = getSnapshotAvailableRange(this.getContent(), this.mapAnimation.options()?.now ?? 0, this.MAXIMUM_AGE_SECONDS, this.DATA_VALIDITY_SECONDS, this.ONLY_PAST);
        }
      }
      untracked(() => this.setAvailableRange(range));
    });
    this._updateLayers = effect(() => {
      const map = this.map();
      if (map == null) {
        return;
      }
      const content = this.getContent();
      const visible = this.getVisible();
      const targetAltitude = this.mapConfig.overlay.altitude();
      const targetTime = this.mapAnimation.time();
      const opacity = this.getOpacity();
      if (visible) {
        // add new layers or update existing layers
        for (const c of content) {
          const info = this.getInfo(c, targetAltitude, targetTime);
          let layer = this.layers.get(c);
          if (info != null) {
            if (layer == null) {
              layer = this.createLayer(info);
              layer.add(map);
              this.layers.set(c, layer);
            } else if (!isEqual(info, layer.info)) {
              layer.remove(map);
              layer = this.createLayer(info);
              layer.add(map);
              this.layers.set(c, layer);
            } else {
              layer.update(map);
            }
            layer.setOpacity(opacity);
          }
          // target is out of range so remove if it exists
          else if (layer != null) {
            layer.remove(map);
            this.layers.delete(c);
          }
        }
        // remove outdated layers
        for (const [c, layer] of this.layers) {
          const found = content.some(x => isEqual(x, c));
          if (!found) {
            layer.remove(map);
            this.layers.delete(c);
          }
        }
      } else {
        for (const [_c, layer] of this.layers) {
          layer.remove(map);
        }
        this.layers.clear();
      }
      this.subscribeOnSourceData(map);
    });
  }
  getInfo(content, targetAltitude, targetTime) {
    if (this.MINIMUM_ALTITUDE != null && targetAltitude < this.MINIMUM_ALTITUDE || this.MAXIMUM_ALTITUDE != null && targetAltitude > this.MAXIMUM_ALTITUDE || targetTime < this.oldestAllowedTime()) {
      return undefined;
    }
    let modelRunTime = undefined;
    let elevation = undefined;
    let offset = undefined;
    let bestElevationDiff = Infinity;
    let bestTimeDiff = Infinity;
    const altitudesMap = new Map();
    // Group together Snapshots by Altitude
    for (const s of content.snapshots ?? []) {
      const c = altitudesMap.get(s.elevation);
      if (c === undefined) {
        altitudesMap.set(s.elevation, [s]);
      } else {
        c.push(s);
      }
    }
    // Determine which Snapshot to use
    for (const a of altitudesMap.keys()) {
      const elevationDiff = Math.abs(a - targetAltitude);
      if (elevationDiff < bestElevationDiff) {
        elevation = a;
        bestElevationDiff = elevationDiff;
      }
    }
    if (elevation == null) {
      return undefined;
    }
    const timeStamps = altitudesMap.get(elevation);
    if (timeStamps != null) {
      for (const t of timeStamps) {
        // get the closest time offset + model run time
        if (t.time < this.oldestAllowedTime()) {
          continue;
        }
        const timeDiff = Math.abs(t.time - targetTime);
        if (timeDiff < bestTimeDiff) {
          modelRunTime = t.time - t.offset;
          offset = t.offset;
          bestTimeDiff = timeDiff;
        }
      }
    }
    if (modelRunTime == null || offset == null) {
      return undefined;
    }
    return {
      baseUrl: content.urlBase,
      bounds: content.bounds,
      modelRunTime,
      elevation,
      offset
    };
  }
  getOpacity() {
    return MAX_OPACITY;
  }
  subscribeOnSourceData(map) {
    if (this.DISABLE_ON_SOURCE_DATA) {
      return;
    }
    if (this.onSourceData != null) {
      map.off('sourcedata', this.onSourceData);
    }
    if (this.layers.size !== 0) {
      const sourceList = {};
      for (const [_c, layer] of this.layers) {
        const id = layer.getSourceId();
        if (id != null) {
          sourceList[id] = layer;
        }
      }
      this.onSourceData = ev => {
        const layer = sourceList[ev.sourceId];
        if (layer != null) {
          layer.loaded = ev.isSourceLoaded;
          untracked(() => this.updateLoadedRange());
        }
      };
      map.on('sourcedata', this.onSourceData);
    }
  }
  updateLoadedRange() {
    const now = this.mapAnimation.options()?.now ?? 0;
    const loaded = {};
    for (const [_c, layer] of this.layers) {
      const id = layer.getSourceId();
      if (id == null) {
        continue;
      }
      const time = layer.info.modelRunTime + layer.info.offset;
      loaded[time] = (loaded[time] ?? true) && layer.loaded;
    }
    const range = getSnapshotLoadedRange(loaded, now, this.DATA_VALIDITY_SECONDS);
    untracked(() => this.setLoadedRange(range));
  }
}

/**
 * This is an Layer to handle layers that rely on a
 * Snapshot that contains modelRunTime, elevation, and offset
 * tiles.
 */
class SnapshotLayer {
  constructor(injector, info) {
    this.injector = injector;
    this.info = info;
    this.map = this.injector.get(MapTracker).map;
    this.mapConfig = this.injector.get(MapConfigHandler);
    this.mapSettings = this.injector.get(MapSettingsHandler);
    this.loaded = false;
  }
  getSourceId() {
    return this.sourceId;
  }
  setOpacity(opacity) {
    trySetLayerOpacity(this.map(), this.layerId, opacity);
  }
}
var ProbabilityLevel = /*#__PURE__*/function (ProbabilityLevel) {
  ProbabilityLevel[ProbabilityLevel["None"] = 0] = "None";
  ProbabilityLevel[ProbabilityLevel["Low"] = 1] = "Low";
  ProbabilityLevel[ProbabilityLevel["Medium"] = 2] = "Medium";
  ProbabilityLevel[ProbabilityLevel["High"] = 3] = "High";
  ProbabilityLevel[ProbabilityLevel["Unknown"] = 7] = "Unknown";
  return ProbabilityLevel;
}(ProbabilityLevel || {});
var SeverityLevel = /*#__PURE__*/function (SeverityLevel) {
  SeverityLevel[SeverityLevel["None"] = 0] = "None";
  SeverityLevel[SeverityLevel["Trace"] = 1] = "Trace";
  SeverityLevel[SeverityLevel["Light"] = 2] = "Light";
  SeverityLevel[SeverityLevel["Moderate"] = 3] = "Moderate";
  SeverityLevel[SeverityLevel["Severe"] = 4] = "Severe";
  SeverityLevel[SeverityLevel["Extreme"] = 5] = "Extreme";
  SeverityLevel[SeverityLevel["Unknown"] = 7] = "Unknown";
  return SeverityLevel;
}(SeverityLevel || {});
var SupercooledLargeDroplet = /*#__PURE__*/function (SupercooledLargeDroplet) {
  SupercooledLargeDroplet[SupercooledLargeDroplet["False"] = 0] = "False";
  SupercooledLargeDroplet[SupercooledLargeDroplet["True"] = 1] = "True";
  SupercooledLargeDroplet[SupercooledLargeDroplet["Unknown"] = 3] = "Unknown";
  return SupercooledLargeDroplet;
}(SupercooledLargeDroplet || {});
const getIcingDataProperty = prop => {
  return ['number', ['get', `${prop}`], 0];
};
const getNoIcing = icingOption => {
  const icingOptionFilter = ['any', ['>=', getIcingDataProperty('prob'), ProbabilityLevel.Unknown]];
  switch (icingOption) {
    case IcingType.SeverityProb25:
      icingOptionFilter.push(['>=', ProbabilityLevel.Low, getIcingDataProperty('prob')]);
      break;
    case IcingType.SeverityProb50:
      icingOptionFilter.push(['>=', ProbabilityLevel.Medium, getIcingDataProperty('prob')]);
      break;
    default:
      icingOptionFilter.push(['>=', ProbabilityLevel.None, getIcingDataProperty('prob')]);
      break;
  }
  return icingOptionFilter;
};
const getSeveritySldIcingPattern = icingOption => {
  return ['case', getNoIcing(icingOption), 'empty-pattern', ['all', ['==', SeverityLevel.Trace, getIcingDataProperty('sev')], ['==', SupercooledLargeDroplet.True, getIcingDataProperty('sipd')]], 'icing_trace_sld', ['all', ['==', SeverityLevel.Light, getIcingDataProperty('sev')], ['==', SupercooledLargeDroplet.True, getIcingDataProperty('sipd')]], 'icing_light_sld', ['all', ['==', SeverityLevel.Moderate, getIcingDataProperty('sev')], ['==', SupercooledLargeDroplet.True, getIcingDataProperty('sipd')]], 'icing_moderate_sld', ['all', ['==', SeverityLevel.Severe, getIcingDataProperty('sev')], ['==', SupercooledLargeDroplet.True, getIcingDataProperty('sipd')]], 'icing_severe_sld', ['all', ['==', SeverityLevel.Extreme, getIcingDataProperty('sev')], ['==', SupercooledLargeDroplet.True, getIcingDataProperty('sipd')]], 'icing_extreme_sld', ['all', ['==', SupercooledLargeDroplet.True, getIcingDataProperty('sipd')], ['any', ['<', getIcingDataProperty('sev'), SeverityLevel.Light], ['>', getIcingDataProperty('sev'), SeverityLevel.Extreme]]], 'icing_sld', ['==', SeverityLevel.Trace, getIcingDataProperty('sev')], 'icing_trace', ['==', SeverityLevel.Light, getIcingDataProperty('sev')], 'icing_light', ['==', SeverityLevel.Moderate, getIcingDataProperty('sev')], 'icing_moderate', ['==', SeverityLevel.Severe, getIcingDataProperty('sev')], 'icing_severe', ['==', SeverityLevel.Extreme, getIcingDataProperty('sev')], 'icing_extreme', 'empty_pattern'];
};
const getProbabilityIcingPattern = () => {
  return ['case', ['==', ProbabilityLevel.Low, getIcingDataProperty('prob')], 'icing_prob_low', ['==', ProbabilityLevel.Medium, getIcingDataProperty('prob')], 'icing_prob_medium', ['==', ProbabilityLevel.High, getIcingDataProperty('prob')], 'icing_prob_high', 'empty_pattern'];
};
const getIcingPattern = icingOption => {
  return ['match', icingOption, [IcingType.Severity, IcingType.SeverityProb25, IcingType.SeverityProb50], getSeveritySldIcingPattern(icingOption), getProbabilityIcingPattern()];
};
const SOURCE_PREFIX$2 = 'icing';
const LAYER_PREFIX$2 = 'icing';
const SOURCE_LAYER = 'icing';
const LAYER_PRIORITY$2 = LayerPriority.Icing;
class IcingLayer extends SnapshotLayer {
  add(map) {
    this.sourceId = map.sourceIds.next(SOURCE_PREFIX$2);
    const tileUrl = `${this.info.baseUrl}/${this.info.modelRunTime}/${this.info.elevation}/${this.info.offset}/{z}/{x}/{y}.mvt`;
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [tileUrl],
      scheme: 'tms',
      bounds: [this.info.bounds.lonMin, this.info.bounds.latMin, this.info.bounds.lonMax, this.info.bounds.latMax],
      minzoom: 2,
      maxzoom: 4
    });
    this.layerId = map.layerIds.next(LAYER_PREFIX$2);
    map.addLayer(LAYER_PRIORITY$2, {
      'id': this.layerId,
      'source': this.sourceId,
      'source-layer': SOURCE_LAYER,
      'type': 'fill',
      'paint': {
        'fill-pattern': getIcingPattern(this.mapConfig.overlay.icing.type())
      }
    });
  }
  remove(map) {
    if (this.layerId != null) {
      map.removeLayer(this.layerId);
      this.layerId = undefined;
    }
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
  update(map) {
    if (this.layerId != null) {
      map.setLayerPattern(this.layerId, getIcingPattern(this.mapConfig.overlay.icing.type()));
    }
  }
}
let IcingLayerService = /*#__PURE__*/(() => {
  class IcingLayerService extends SnapshotLayerService {
    constructor() {
      super(...arguments);
      this.images = [MapIcon.Icing_Empty, MapIcon.Icing_ProbLow, MapIcon.Icing_ProbMedium, MapIcon.Icing_ProbHigh, MapIcon.Icing_Trace, MapIcon.Icing_Light, MapIcon.Icing_Moderate, MapIcon.Icing_Severe, MapIcon.Icing_Extreme, MapIcon.Icing_TraceSolid, MapIcon.Icing_LightSolid, MapIcon.Icing_ModerateSolid, MapIcon.Icing_SevereSolid, MapIcon.Icing_ExtremeSolid, MapIcon.Icing_Solid].map(x => MapIconDefinitions.get(x));
      this.MAXIMUM_AGE_SECONDS = 1 * 60 * 60;
      this.MAXIMUM_ALTITUDE = 30000;
      this.MINIMUM_ALTITUDE = 1000;
      this.DATA_VALIDITY_SECONDS = 3 * 60 * 60;
      this.ONLY_PAST = false;
      this._updateImages = effect(() => {
        const config = this.mapConfig.overlay.icing;
        const info = this.mapInfo.overlay.icing;
        if (info.available() && config.visible()) {
          this.mapIcon.add(this.images);
        } else {
          this.mapIcon.remove(this.images);
        }
      });
    }
    createLayer(info) {
      return new IcingLayer(this.injector, info);
    }
    getContent() {
      return this.mapInfo.overlay.icing.content();
    }
    getVisible() {
      return this.mapConfig.overlay.icing.visible();
    }
    getOpacity() {
      return this.mapConfig.overlay.icing.opacity();
    }
    setAvailableRange(range) {
      this.mapAnimation.availableRange.icing.set(range);
    }
    setLoadedRange(range) {
      this.mapAnimation.loadedRange.icing.set(range);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵIcingLayerService_BaseFactory;
        return function IcingLayerService_Factory(__ngFactoryType__) {
          return (ɵIcingLayerService_BaseFactory || (ɵIcingLayerService_BaseFactory = i0.ɵɵgetInheritedFactory(IcingLayerService)))(__ngFactoryType__ || IcingLayerService);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: IcingLayerService,
        factory: IcingLayerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return IcingLayerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const getIconImage = () => {
  return ['case', ['has', 'aircraft_type'], ['coalesce', ['get', ['to-string', ['get', 'aircraft_type']], ['literal', aircraftMappingTable]], 'internet_traffic_generic'], 'internet_traffic_generic' // Show the generic aircraft if no match is found
  ];
};
const getTextField = speedUnit => {
  return ["format", ['concat', ['coalesce', ['get', 'id'], '--'], ' (', ['coalesce', ['get', 'aircraft_type'], '--'], ')'], {
    "font-scale": 1
  }, "\n", {}, getOriginAndDest(), {
    "font-scale": 0.8
  }, "\n", {}, getAltitudeAndSpeed(speedUnit), {
    "font-scale": 0.8
  }];
};
const getOriginAndDest = () => {
  return ['case', ['all', ['!=', ['coalesce', ['get', 'origin'], ''], ''], ['!=', ['coalesce', ['get', 'destination'], ''], '']], ['concat', ['get', 'origin'], ' - ', ['get', 'destination']], ['coalesce', ['get', 'origin'], ['get', 'destination'], '--']];
};
const getAltitudeAndSpeed = speedUnit => {
  return ['concat', ['case', ['all', ['has', 'altitude'], ['>', ['get', 'altitude'], 0]], ['concat', ['number-format', ['get', 'altitude'], {
    'locale': 'en-US',
    'max-fraction-digits': 0
  }], 'FT  '], '--  '], ['case', ['all', ['has', 'speed'], ['>', ['get', 'speed'], 0]], ['concat', displaySpeed(speedUnit), `${speedUnit.toUpperCase()}`], '--']];
};
const displaySpeed = speedUnit => {
  let value = ['get', 'speed'];
  if (speedUnit === VelocityUnits.KilometersPerHour) {
    value = convertTempKtsToKph();
  } else if (speedUnit === VelocityUnits.MilesPerHour) {
    value = convertTempKtsToMph();
  }
  return ['number-format', value, {
    'locale': 'en-US',
    'max-fraction-digits': 0
  }];
};
const convertTempKtsToKph = () => {
  const KPH_PER_KNOT = 1.852;
  return ['round', ['/', ['*', ['get', 'speed'], KPH_PER_KNOT], 1]];
};
const convertTempKtsToMph = () => {
  const MPH_PER_KNOT = 1.151;
  return ['round', ['/', ['*', ['get', 'speed'], MPH_PER_KNOT], 1]];
};

// This layer visualizes the icons of tracked aircraft
class InternetTrafficHighlightAircraftLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'internet-traffic-tracked-aircraft';
  }
  setOpacity(_opacity) {
    // NO-OP. Opacity is set directly when this layer is added (shown).
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightInternetTrafficAircraft, {
        id: this.layerId,
        source: this.sourceId,
        type: 'symbol',
        layout: {
          'icon-image': getIconImage(),
          'icon-size': ['interpolate', ['linear'], ['zoom'], 0, 0.1, 4, 0.25, 9, 0.4, 12, 0.6],
          'icon-allow-overlap': true,
          'icon-rotate': ['get', 'heading']
        },
        paint: {
          'icon-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0.7, 6, 1]
        }
      });
    }
  }
  _update(_map) {
    //no action needed
  }
}

// This layer visualizes the background icons of tracked aircraft
class InternetTrafficHighlightCircleLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'internet-traffic-tracked-aircraft';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightInternetTrafficCircle, {
        id: this.layerId,
        source: this.sourceId,
        type: 'symbol',
        layout: {
          'icon-allow-overlap': true,
          'icon-anchor': 'center',
          'icon-image': MapIcon.InternetTraffic_Highlight,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 0, 0.4, 4, 0.5, 9, 0.9, 12, 1.2]
        }
      });
    }
  }
  _update(_map) {
    // no action needed
  }
}
const INTERNET_TRAFFIC_HIGHLIGHT_SOURCE_ID = 'internet-traffic-highlight';
class InternetTrafficHighlightLayer extends LayerGroup {
  constructor(injector, radialMenuMapService, content = EmptyContent, internetTrafficHighlightService) {
    super(injector, radialMenuMapService, content);
    this.injector = injector;
    this.radialMenuMapService = radialMenuMapService;
    this.content = content;
    this.internetTrafficHighlightService = internetTrafficHighlightService;
    this.SOURCE_LAYER = 'internetTrafficHighlight';
    this.groupInfo = [{
      layer: new InternetTrafficHighlightAircraftLayer(this.injector, this.radialMenuMapService, this.SOURCE_LAYER, LayerType.InternetTraffic)
    }, {
      layer: new InternetTrafficHighlightCircleLayer(this.injector, this.radialMenuMapService, this.SOURCE_LAYER)
    }];
  }
  addSource(map) {
    this.sourceId = INTERNET_TRAFFIC_HIGHLIGHT_SOURCE_ID;
    map.addSource(this.sourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [] // Initialize with an empty array
      }
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
}

// This layer visualizes aircraft using symbols based on data from internet traffic tiles
class InternetTrafficSymbolLayer extends SubLayer {
  constructor(injector, radialMenuMapService, internetTrafficService, sourceLayerId, layerType) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.injector = injector;
    this.radialMenuMapService = radialMenuMapService;
    this.internetTrafficService = internetTrafficService;
    this.sourceLayerId = sourceLayerId;
    this.layerType = layerType;
    this.LAYER_PREFIX = 'internet-traffic-symbol';
  }
  setOpacity(_opacity) {
    // NO-OP. Opacity is set directly when this layer is added (shown).
  }
  _show(map) {
    if (this.sourceId != null) {
      this.layerId = map.layerIds.next(this.LAYER_PREFIX);
      map.addLayer(LayerPriority.InternetTraffic, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': INTERNET_TRAFFIC_SOURCE_LAYER,
        'type': 'symbol',
        'filter': this.getFilter(),
        'layout': {
          'icon-image': getIconImage(),
          'icon-size': ['interpolate', ['linear'], ['zoom'], 0, 0.1, 4, 0.25, 9, 0.4, 12, 0.6],
          'icon-allow-overlap': true,
          'text-optional': true,
          'text-allow-overlap': true,
          'icon-rotate': ['get', 'heading'],
          'text-anchor': 'center',
          'text-justify': 'center',
          'text-font': ['Roboto'],
          'text-size': 18,
          'text-ignore-placement': true
        },
        'paint': {
          'icon-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0.7, 6, 1],
          'text-color': '#02ebdb',
          'text-halo-color': '#000000',
          'text-halo-width': 1
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  getFilter() {
    const highlight = this.mapHighlight.internetTraffic();
    const trackedIds = highlight.map(aircraft => aircraft.id).filter(id => id !== undefined);
    return ['all', ['any', ['all', ['>', ['zoom'], 13], ['==', ['get', 'ground'], true]], ['!=', ['get', 'ground'], true]], ['!', ['in', ['get', 'id'], ['literal', trackedIds]]]];
  }
}
const INTERNET_TRAFFIC_SOURCE_LAYER = 'traffic-targets';
const INTERNET_TRAFFIC_SOURCE_ID = 'internet-traffic';
class InternetTrafficLayer extends LayerGroup {
  constructor(injector, radialMenuMapService, content = EmptyContent, internetTrafficService) {
    super(injector, radialMenuMapService, content);
    this.injector = injector;
    this.radialMenuMapService = radialMenuMapService;
    this.content = content;
    this.internetTrafficService = internetTrafficService;
    this.SOURCE_LAYER = 'InternetTraffic';
    this.groupInfo = [
    // Keep this for future features
    {
      layer: new InternetTrafficSymbolLayer(this.injector, this.radialMenuMapService, this.internetTrafficService, this.SOURCE_LAYER, LayerType.InternetTraffic)
    }];
  }
  addSource(map) {
    this.sourceId = INTERNET_TRAFFIC_SOURCE_ID;
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/current/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      maxzoom: 11,
      minzoom: 1,
      volatile: true
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
}
let InternetTrafficHighlightService = /*#__PURE__*/(() => {
  class InternetTrafficHighlightService extends LayerService {
    constructor() {
      super();
      this.LAYER_INFO = 'InternetTrafficHighlight';
      this.lastPositionUri = null;
      this.lastUpdateTime = 0;
      this.config = this.mapConfig.overlay.internetTraffic;
      this.info = this.mapInfo.overlay.internetTraffic;
      this.existingFeatures = new Map();
      this.http = inject(HttpClient);
      this.updateSubject$ = new Subject();
      this.dependentSourceId = INTERNET_TRAFFIC_SOURCE_ID;
      this.internetTrafficChanges$ = toObservable(this.mapHighlight.internetTraffic);
      this.showHighlights = computed(() => {
        return this.map() != null && this.mapInfo.overlay.internetTraffic.available() && this.mapConfig.overlay.internetTraffic.visible();
      });
      this.centerOnAircraft = computed(() => {
        return this.map() != null && this.mapInfo.overlay.internetTraffic.available() && this.mapConfig.overlay.internetTraffic.visible() && this.mapState.centerOnInternetTraffic();
      });
      this.onMoveStart = ev => {
        if (ev.originalEvent != null) {
          this.mapState.centerOnInternetTraffic.set(false);
        }
      };
      this.onSourceData = ev => {
        const minUpdateInterval = 500; // ms
        if (ev.sourceId === this.dependentSourceId && Date.now() - this.lastUpdateTime > minUpdateInterval) {
          this.lastUpdateTime = Date.now();
          this.updateSubject$.next();
        }
      };
      merge(this.internetTrafficChanges$, this.updateSubject$).pipe(switchMap(() => this.update()), catchError(error => {
        console.error("Failure to get aircraft last position:", error);
        return EMPTY;
      })).subscribe({
        next: () => {
          if (this.centerOnAircraft()) {
            this.centerFocusedAircrafts();
          }
        },
        error: error => {
          console.error(error);
        }
      });
      effect(() => {
        this.subscribeToSourceDataAndMoveStart(this.showHighlights());
      });
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
      effect(() => {
        if (this.centerOnAircraft()) {
          untracked(() => this.centerFocusedAircrafts());
        }
      });
    }
    subscribeToSourceDataAndMoveStart(isSubscribed) {
      const map = this.map();
      if (map != null) {
        if (isSubscribed) {
          map.on('sourcedata', this.onSourceData);
          map.on('movestart', this.onMoveStart);
        } else {
          map.off('sourcedata', this.onSourceData);
          map.off('movestart', this.onMoveStart);
        }
      }
    }
    createLayer(content) {
      return new InternetTrafficHighlightLayer(this.injector, this.radialMenuMapService, content, this);
    }
    getContent() {
      return this.info.content();
    }
    getVisible() {
      return this.config.visible();
    }
    updateImages() {
      if (this.info.available() && this.config.visible()) {
        this.mapIcon.add(aircraftImages);
      } else {
        this.mapIcon.remove(aircraftImages);
      }
    }
    update() {
      // Clear the source if it's not visible or no tracked aircraft
      if (!this.config.visible() || this.mapHighlight.internetTraffic().length == 0) {
        this.clearSource();
        return of(undefined);
      }
      const trackedAircraft = this.mapHighlight.internetTraffic();
      const currentIds = new Set(trackedAircraft.map(a => a.id).filter(id => id != null));
      // Remove outdated features
      this.existingFeatures.forEach((feature, id) => {
        if (!currentIds.has(id)) {
          this.existingFeatures.delete(id);
        }
      });
      const concurrentNumber = 3;
      this.lastPositionUri = this.getAircraftTrackUri();
      return from(trackedAircraft).pipe(filter$1(input => input.id != null), mergeMap(input => this.getAircraftLastPosition(input.id), concurrentNumber), toArray(), tap(() => this.updateSource()), map(() => undefined), catchError(error => {
        console.error("Error in update process:", error);
        return of(undefined);
      }));
    }
    getAircraftTrackUri() {
      const content = this.getContent();
      if (content.length === 0 || !content[0].urlBase) {
        console.error('Invalid content');
        return null;
      }
      return `${content[0].urlBase}/position/last`;
    }
    getAircraftLastPosition(id) {
      const url = `${this.lastPositionUri}/${id}`;
      return this.http.get(url, {
        withCredentials: true
      }).pipe(switchMap(data => {
        if (data != null && data.position != null) {
          if (this.existingFeatures.has(id)) {
            // update the existing feature
            const existingFeature = this.existingFeatures.get(id);
            if (existingFeature != null) {
              this.updateFeature(existingFeature, data);
            }
          } else {
            // create the new feature
            const newFeature = this.createFeature(data);
            if (newFeature != null) {
              this.existingFeatures.set(id, newFeature);
            }
          }
        }
        return of(undefined);
      }), catchError(error => {
        console.error('Error fetching aircraft data:', error);
        return of(undefined);
      }));
    }
    updateSource() {
      const map = this.map();
      if (map != null) {
        map.setGeoJsonSourceData(INTERNET_TRAFFIC_HIGHLIGHT_SOURCE_ID, {
          type: 'FeatureCollection',
          features: Array.from(this.existingFeatures.values())
        });
      }
    }
    updateFeature(feature, data) {
      if (data.position != null && feature.properties != null) {
        const position = data.position;
        if (feature.geometry.type === 'Point') {
          feature.geometry.coordinates = [position.lon, position.lat];
        }
        feature.properties.altitude = position.altitude ?? -1;
        feature.properties.heading = position.heading;
        feature.properties.speed = position.speed ?? -1;
        feature.properties.timestamp = position.timestamp;
        feature.properties.ground = position.ground;
      }
    }
    createFeature(aircraft) {
      if (aircraft.position != null) {
        const position = aircraft.position;
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [position.lon, position.lat]
          },
          properties: {
            id: aircraft.id,
            reg: aircraft.reg,
            callsign: aircraft.callsign,
            aircraft_type: aircraft.aircraft_type,
            aircraft_desc: aircraft.aircraft_desc,
            operator: aircraft.operator,
            origin: aircraft.origin,
            origin_name: aircraft.origin_name,
            destination: aircraft.destination,
            destination_name: aircraft.destination_name,
            timestamp: position.timestamp,
            ts: position.ts,
            heading: position.heading,
            altitude: position.altitude ?? -1,
            // Set an invalid value if not found
            speed: position.speed ?? -1,
            ground: position.ground,
            takeoff_time_t: aircraft.takeoff_time != null ? aircraft.takeoff_time.t : null,
            takeoff_time_tz: aircraft.takeoff_time != null ? aircraft.takeoff_time.tz : null,
            takeoff_time_offset: aircraft.takeoff_time != null ? aircraft.takeoff_time.offset : null,
            landing_time_t: aircraft.landing_time != null ? aircraft.landing_time.t : null,
            landing_time_tz: aircraft.landing_time != null ? aircraft.landing_time.tz : null,
            landing_time_offset: aircraft.landing_time != null ? aircraft.landing_time.offset : null,
            estimated_departure_time_t: aircraft.estimated_departure_time != null ? aircraft.estimated_departure_time.t : null,
            estimated_departure_time_tz: aircraft.estimated_departure_time != null ? aircraft.estimated_departure_time.tz : null,
            estimated_departure_time_offset: aircraft.estimated_departure_time != null ? aircraft.estimated_departure_time.offset : null,
            estimated_arrival_time_t: aircraft.estimated_arrival_time != null ? aircraft.estimated_arrival_time.t : null,
            estimated_arrival_time_tz: aircraft.estimated_arrival_time != null ? aircraft.estimated_arrival_time.tz : null,
            estimated_arrival_time_offset: aircraft.estimated_arrival_time != null ? aircraft.estimated_arrival_time.offset : null
          }
        };
      }
      return null;
    }
    clearSource() {
      this.existingFeatures.clear();
      this.updateSource();
    }
    centerFocusedAircrafts() {
      const centroid = [0, 0];
      const featureLongitudes = [];
      const featureLatitudes = [];
      this.existingFeatures.forEach(feature => {
        if (feature.geometry.type === 'Point') {
          //coordinates = [lon, lat]
          centroid[0] += feature.geometry.coordinates[0];
          centroid[1] += feature.geometry.coordinates[1];
          featureLongitudes.push(feature.geometry.coordinates[0]);
          featureLatitudes.push(feature.geometry.coordinates[1]);
        }
      });
      if (featureLongitudes.length > 0) {
        centroid[0] = centroid[0] / featureLongitudes.length;
        centroid[1] = centroid[1] / featureLatitudes.length;
        //To match Garmin Pilot behavior, if centering on only one aircraft, do not
        //use bounding box or adjust zoom level. If centering on more than one aircraft,
        //adjust zoom based on bounding box of tracked aircrafts and add padding
        const mapBounds = featureLongitudes.length > 1 ? {
          array: [Math.min(...featureLongitudes), Math.min(...featureLatitudes), Math.max(...featureLongitudes), Math.max(...featureLatitudes)]
        } : undefined;
        this.mapState.fit({
          point: {
            longitude: centroid[0],
            latitude: centroid[1]
          },
          bounds: mapBounds,
          padding: 2,
          duration: 200,
          animate: true
        });
      }
    }
    static {
      this.ɵfac = function InternetTrafficHighlightService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || InternetTrafficHighlightService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: InternetTrafficHighlightService,
        factory: InternetTrafficHighlightService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return InternetTrafficHighlightService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const INTERNET_TRAFFIC_TRACKED_BREADCRUMBS_SOURCE_ID = 'internet-traffic-tracked-breadcrumbs';
// This layer visualizes aircraft breadcrumbs(trajectory)
class InternetTrafficTrackedBreadcrumbsLayer extends SimpleLayer {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'internet-traffic-tracked-breadcrumbs';
    this.LAYER_PREFIX = 'internet-traffic-tracked-breadcrumbs';
    this.LAYER_PRIORITY = LayerPriority.InternetTrafficTrackedBreadcrumbs;
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [] // Initialize with an empty array
      }
    });
    return this.sourceId;
  }
  addLayer(map, sourceId, layerId) {
    map.addLayer(this.LAYER_PRIORITY, {
      id: layerId,
      type: 'line',
      source: sourceId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': 'white',
        'line-width': 3
      },
      filter: ['==', ['geometry-type'], 'LineString']
    });
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
}
let InternetTrafficTrackedBreadcrumbsService = /*#__PURE__*/(() => {
  class InternetTrafficTrackedBreadcrumbsService extends LayerService {
    constructor() {
      super();
      this.LAYER_INFO = 'InternetTraffic';
      this.aircraftTrackUri = null;
      this.currentFeatures = [];
      this.lastUpdateTime = 0;
      this.config = this.mapConfig.overlay.internetTraffic;
      this.info = this.mapInfo.overlay.internetTraffic;
      this.dependentSourceId = INTERNET_TRAFFIC_SOURCE_ID;
      this.updateSubject$ = new Subject();
      this.http = inject(HttpClient);
      this.destroyRef = inject(DestroyRef);
      this.showBreadcrumbs = computed(() => {
        return this.map() != null && this.mapInfo.overlay.internetTraffic.available() && this.mapConfig.overlay.internetTraffic.visible() && this.mapConfig.overlay.internetTraffic.breadcrumbsVisible();
      });
      this.onSourceData = ev => {
        const minUpdateInterval = 500; // ms
        if (ev.sourceId === this.dependentSourceId && Date.now() - this.lastUpdateTime > minUpdateInterval) {
          this.lastUpdateTime = Date.now();
          this.updateSubject$.next();
        }
      };
      this.updateSubject$.pipe(switchMap(() => this.update()), catchError(error => {
        console.error("Failure to get aircraft last position:", error);
        return EMPTY;
      }), takeUntilDestroyed()).subscribe();
      effect(() => {
        this.subscribeToSourceData(this.showBreadcrumbs());
      });
      effect(() => this.updateBreadcrumbs());
      effect(() => this.updateLayers());
      effect(() => {
        if (this.showBreadcrumbs()) {
          this.update().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        }
      });
    }
    subscribeToSourceData(isSubscribed) {
      const map = this.map();
      if (map != null) {
        if (isSubscribed) {
          map.on('sourcedata', this.onSourceData);
        } else {
          map.off('sourcedata', this.onSourceData);
        }
      }
    }
    createLayer(content) {
      return new InternetTrafficTrackedBreadcrumbsLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.info.content();
    }
    getVisible() {
      return this.config.visible();
    }
    update() {
      const trackedAircraft = this.mapHighlight.internetTraffic();
      if (!this.config.visible() || trackedAircraft.length == 0) {
        this.clearSource();
        return of(undefined);
      }
      this.currentFeatures = [];
      const concurrentNumber = 3;
      this.aircraftTrackUri = this.getAircraftTrackUri();
      return from(trackedAircraft).pipe(filter$1(input => input.id !== undefined), mergeMap(input => this.updateFeature(input.id), concurrentNumber), toArray(), tap(() => this.updateSource()), map(() => undefined), catchError(error => {
        console.error("Error in update process:", error);
        return of(undefined);
      }));
    }
    getAircraftTrackUri() {
      const content = this.getContent();
      if (content.length === 0 || !content[0].urlBase) {
        console.error('Invalid caps content');
        return null;
      }
      return `${content[0].urlBase}/track/last`;
    }
    updateFeature(id) {
      const url = `${this.aircraftTrackUri}/${id}`;
      return this.http.get(url, {
        withCredentials: true
      }).pipe(switchMap(data => {
        if (data != null && data.position != null) {
          const newFeature = this.createFeature(data);
          if (newFeature != null) {
            this.currentFeatures.push(newFeature);
          }
        }
        return of(undefined);
      }), catchError(error => {
        console.error('Error fetching aircraft data:', error);
        return of(undefined);
      }));
    }
    createFeature(breadcrumbs) {
      // Sort positions by timestamp to ensure a chronological order
      breadcrumbs.position.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      const features = [];
      let lastValidAltitude = null;
      const lastIndex = breadcrumbs.position.length - 1;
      let i = 0;
      while (i < lastIndex) {
        const start = breadcrumbs.position[i];
        let end = start;
        let j = i + 1;
        while (j <= lastIndex) {
          const nextPoint = breadcrumbs.position[j];
          if (!this.isShortDistance(start, nextPoint) || breadcrumbs.position.length < 10) {
            end = nextPoint;
            break;
          }
          j = j + 1;
        }
        // use the last point without further points
        if (j > lastIndex) {
          end = breadcrumbs.position[lastIndex];
        }
        // calculate the altitude
        let altitude = null;
        if (start.altitude != null && end.altitude != null) {
          altitude = (start.altitude + end.altitude) / 2;
        } else if (start.altitude != null) {
          altitude = start.altitude;
        } else if (end.altitude != null) {
          altitude = end.altitude;
        } else if (lastValidAltitude != null) {
          altitude = lastValidAltitude;
        }
        if (altitude != null) {
          lastValidAltitude = altitude;
        }
        // color breadcrumbs by the altitudes
        let color = Color.PureRed.toString();
        if (start.altitude > 18000 && start.altitude <= 29000) {
          color = Color.Orange.toString();
        } else if (start.altitude > 29000 && start.altitude <= 41000) {
          color = Color.Yellow.toString();
        } else if (start.altitude > 41000) {
          color = Color.Green.toString();
        }
        const lineFeature = turf.line([[start.lon, start.lat], [end.lon, end.lat]], {
          id: breadcrumbs.id,
          origin: breadcrumbs.origin,
          destination: breadcrumbs.destination,
          altitude: altitude,
          color: color
        });
        features.push(lineFeature);
        i = j;
      }
      return features;
    }
    isShortDistance(point1, point2) {
      // Reduce point density to avoid breadcrumbs appearing dashed when zoomed out.
      // Skip decluttering for low-speed and low-altitude positions, as they are typically near the ground
      // and require more details when zooming in.
      // Speed and altitude thresholds are based on empirical values.
      if (point1.speed > 100 || point1.altitude > 18000) {
        const minSpeed = 100;
        const maxSpeed = 200;
        const minThreshold = 0.05;
        const maxThreshold = 0.1;
        // Calculate threshold linearly between minThreshold and maxThreshold
        const threshold = minThreshold + (point1.speed - minSpeed) / (maxSpeed - minSpeed) * (maxThreshold - minThreshold);
        const clampedThreshold = Math.min(threshold, maxThreshold);
        const diff = Math.min(Math.abs(point1.lon - point2.lon), Math.abs(point1.lat - point2.lat));
        return diff < clampedThreshold;
      } else {
        return false;
      }
    }
    updateSource() {
      const map = this.map();
      if (map != null) {
        map.setGeoJsonSourceData(INTERNET_TRAFFIC_TRACKED_BREADCRUMBS_SOURCE_ID, {
          type: 'FeatureCollection',
          features: this.currentFeatures.flat()
        });
      }
    }
    clearSource() {
      this.currentFeatures = [];
      this.updateSource();
    }
    updateBreadcrumbs() {
      if (!this.config.breadcrumbsVisible()) {
        this.clearSource();
      } else {
        this.update();
      }
    }
    static {
      this.ɵfac = function InternetTrafficTrackedBreadcrumbsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || InternetTrafficTrackedBreadcrumbsService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: InternetTrafficTrackedBreadcrumbsService,
        factory: InternetTrafficTrackedBreadcrumbsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return InternetTrafficTrackedBreadcrumbsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let InternetTrafficService = /*#__PURE__*/(() => {
  class InternetTrafficService extends LayerService {
    constructor() {
      super();
      this.LAYER_INFO = 'InternetTraffic';
      this.config = this.mapConfig.overlay.internetTraffic;
      this.info = this.mapInfo.overlay.internetTraffic;
      effect(() => this.updateImages());
      effect(() => this.updateLayers(), {
        allowSignalWrites: true
      });
    }
    createLayer(content) {
      return new InternetTrafficLayer(this.injector, this.radialMenuMapService, content, this);
    }
    getContent() {
      return this.info.content();
    }
    getVisible() {
      return this.config.visible();
    }
    updateImages() {
      if (this.info.available() && this.config.visible()) {
        this.mapIcon.add(aircraftImages);
      } else {
        this.mapIcon.remove(aircraftImages);
      }
    }
    static {
      this.ɵfac = function InternetTrafficService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || InternetTrafficService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: InternetTrafficService,
        factory: InternetTrafficService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return InternetTrafficService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class LightningCircleLayer extends SubLayer {
  constructor(injector, radialMenuMapService, sourceLayerId, createdTime) {
    super(injector, radialMenuMapService, sourceLayerId);
    this.createdTime = createdTime;
    this.LAYER_PREFIX = 'lightning-circle';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Lightning, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'circle',
        'filter': this.getFilter(),
        'paint': {
          'circle-blur': 1,
          'circle-color': Color.White.toString(),
          'circle-opacity': .5,
          'circle-radius': 14
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  /**
   * Determine the maximum timestamp of lightning data to show.
   * Trying to show a later time than the most recent lightning data
   * will show the most recent lightning instead of nothing.
   */
  calculateMaxTime(displayTime) {
    if (this.createdTime == null || displayTime < this.createdTime) {
      return displayTime;
    }
    return this.createdTime;
  }
  calculateMinTime(maxTime, minutesPerStep) {
    return maxTime - minutesPerStep * 60;
  }
  getFilter() {
    const options = this.mapAnimation.options();
    const minutesPerStep = (options?.step ?? 60000) / (60 * 1000);
    const displayTime = this.mapAnimation.time();
    let maxTime = this.calculateMaxTime(displayTime);
    const minTime = this.calculateMinTime(maxTime, minutesPerStep);
    if (displayTime > (options?.now ?? 0)) {
      // lightning only works in the past
      maxTime = 0;
    }
    return this.timeRange(minTime, maxTime);
  }
  timeRange(minTime, maxTime) {
    return ['all', ['<=', ['get', 'time'], maxTime], ['>', ['get', 'time'], minTime]];
  }
}
const LIGHTNING_STEPS_TO_SHOW = 5;
class LightningSymbolLayer extends SubLayer {
  constructor(injector, radialMenuMapService, sourceLayerId, createdTime) {
    super(injector, radialMenuMapService, sourceLayerId);
    this.createdTime = createdTime;
    this.LAYER_PREFIX = 'lighting-symbol';
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error();
    }
    return super.setOpacity(this.getIconOpacity(this.calculateTiming(), opacity));
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const timing = this.calculateTiming();
      map.addLayer(LayerPriority.Lightning, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(timing),
        'paint': {
          'icon-color': this.getIconColor(timing)
        },
        'layout': {
          'icon-image': MapIcon.Lightning,
          'icon-allow-overlap': true,
          'icon-size': .5,
          'icon-padding': 0,
          'text-optional': true,
          'symbol-sort-key': ['get', 'time']
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const timing = this.calculateTiming();
      map.setLayerFilter(this.layerId, this.getFilter(timing));
      map.setLayerSymbolProperties(this.layerId, {
        iconColor: this.getIconColor(timing)
      });
    }
  }
  /**
   * Determine the maximum timestamp of lightning data to show.
   * Trying to show a later time than the most recent lightning data
   * will show the most recent lightning instead of nothing.
   */
  calculateMaxTime(displayTime) {
    if (this.createdTime == null || displayTime < this.createdTime) {
      return displayTime;
    }
    return this.createdTime;
  }
  calculateMinTime(maxTime, secondsPerStep) {
    return maxTime - LIGHTNING_STEPS_TO_SHOW * secondsPerStep;
  }
  calculateTiming() {
    const options = this.mapAnimation.options();
    const secondsPerStep = (options?.step ?? 60000) / 1000;
    const displayTime = this.mapAnimation.time();
    let maxTime = this.calculateMaxTime(displayTime);
    const minTime = this.calculateMinTime(maxTime, secondsPerStep);
    if (displayTime > (options?.now ?? 0)) {
      // lighting only works in the past
      maxTime = 0;
    }
    return {
      displayTime,
      maxTime,
      minTime,
      secondsPerStep
    };
  }
  getFilter(timing) {
    return this.timeRange(timing.minTime, timing.maxTime);
  }
  getIconColor(timing) {
    // New lightning strikes are white for one animation step
    // and then remain yellow.
    return ['case', this.timeRange(timing.displayTime - timing.secondsPerStep, timing.displayTime), Color.White.toString(), Color.LightningYellow.toString()];
  }
  getIconOpacity(timing, opacityScaleFactor) {
    // New lightning strikes have an initial opacity as defined by AvcMapsDisplayService.config.overlay.radar.opacity,
    // and linearly transition to an opacity of 0.0.
    return ['case', this.timeRange(timing.minTime, timing.maxTime), ['*', ['/', ['-', ['get', 'time'], timing.minTime], LIGHTNING_STEPS_TO_SHOW * timing.secondsPerStep], opacityScaleFactor], 0];
  }
  timeRange(minTime, maxTime) {
    return ['all', ['<=', ['get', 'time'], maxTime], ['>', ['get', 'time'], minTime]];
  }
}
const LIGHTNING_LENGTH_SECONDS = 7200;
class LightningLayer extends AnimationLayerGroup {
  constructor() {
    super(...arguments);
    this.SOURCE_LAYER = 'Lightning';
    this.SOURCE_PREFIX = 'lightning';
    this.groupInfo = [{
      layer: new LightningCircleLayer(this.injector, this.radialMenuMapService, this.SOURCE_LAYER, this.content.metadata?.createdTime)
    }, {
      layer: new LightningSymbolLayer(this.injector, this.radialMenuMapService, this.SOURCE_LAYER, this.content.metadata?.createdTime)
    }];
  }
  addAnimationSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      minzoom: 2,
      maxzoom: 9
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeAnimationSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
  updateAvailableRange() {
    const range = [];
    if (this.sourceId != null) {
      const time = this.content.metadata?.createdTime ?? 0;
      range.push([time - LIGHTNING_LENGTH_SECONDS, time]);
    }
    this.mapAnimation.availableRange.lightning.set(range);
  }
  updateLoadedRange() {
    const range = [];
    if (this.loaded) {
      const time = this.content.metadata?.createdTime ?? 0;
      range.push([time - LIGHTNING_LENGTH_SECONDS, time]);
    }
    this.mapAnimation.loadedRange.lightning.set(range);
  }
}
let LightningService = /*#__PURE__*/(() => {
  class LightningService extends LayerService {
    constructor() {
      super();
      this.images = [MapIcon.Lightning].map(x => MapIconDefinitions.get(x));
      this.config = this.mapConfig.overlay.radar;
      this.info = this.mapInfo.overlay.lightning;
      effect(() => this.updateImages());
      effect(() => this.updateInfo(), {
        allowSignalWrites: true
      });
      effect(() => this.updateLayers(), {
        allowSignalWrites: true
      });
    }
    createLayer(content) {
      return new LightningLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.info.content();
    }
    getVisible() {
      return this.config.visible() && this.config.lightning();
    }
    getOpacity() {
      return this.mapConfig.overlay.radar.opacity();
    }
    updateImages() {
      if (this.info.available() && this.getVisible()) {
        this.mapIcon.add(this.images);
      } else {
        this.mapIcon.remove(this.images);
      }
    }
    updateInfo() {
      const content = this.getContent();
      const visible = this.getVisible();
      const now = this.mapAnimation.options()?.now ?? 0;
      const available = [];
      if (visible) {
        for (const c of content) {
          if (c.metadata != null) {
            const time = c.metadata.createdTime;
            const minTime = time - LIGHTNING_LENGTH_SECONDS;
            const maxTime = Math.min(now, time);
            available.push([minTime, maxTime]);
          }
        }
      }
      this.mapAnimation.availableRange.lightning.set(available);
    }
    static {
      this.ɵfac = function LightningService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LightningService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LightningService,
        factory: LightningService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LightningService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const getObstacleIconImage = () => {
  return ['match', ['get', 'obstacleIcon'], 'LightedShort', MapIcon.Obstacle_Lighted_Short, 'UnlightedShort', MapIcon.Obstacle_Unlighted_Short, 'LightedTall', MapIcon.Obstacle_Lighted_Tall, MapIcon.Obstacle_Unlighted_Tall
  // Tall and unlighted is the worst case so it's the default
  ];
};
const getObstacleTestField = () => {
  return '{mslPlusAgl} ({agl})';
};
const getObstacleHighlightFilter = highlights => {
  if (highlights.length > 0) {
    return ['in', ['id'], ['literal', highlights.map(x => x.id)]];
  }
  return ['boolean', false];
};
class ObstacleHighlightSymbolLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'obstacle-highlight-symbol';
    this.theme = this.mapTheme;
  }
  _show(map) {
    if (this.layerId != null && this.sourceId != null) {
      map.addLayer(LayerPriority.HighlightObstacle, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(),
        'paint': {
          'text-color': this.theme.textColor(),
          'text-halo-color': this.theme.textHaloColor(),
          'text-halo-width': this.theme.textHaloWidth()
        },
        'layout': {
          'icon-anchor': 'bottom',
          'icon-image': getObstacleIconImage(),
          'text-field': getObstacleTestField(),
          'text-anchor': 'top',
          'text-font': ['Roboto'],
          'text-size': 9,
          'text-offset': this.getTextOffset()
        }
      });
    }
  }
  _update(map) {
    this.updateTheme(map);
    this.updateLayers(map);
  }
  getFilter() {
    return getObstacleHighlightFilter(this.mapHighlight.obstacle());
  }
  getTextOffset() {
    return ['array', 'number', 2, ['match', ['get', 'obstacleIcon'], 'LightedShort', ['literal', [0, -4.0]], 'UnlightedShort', ['literal', [0, -3.5]], 'LightedTall', ['literal', [0, -5.5]], ['literal', [0, -5.0]] // Tall and unlighted
    ]];
  }
  updateLayers(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  updateTheme(map) {
    const theme = this.mapTheme;
    if (this.layerId != null) {
      map.setLayerSymbolProperties(this.layerId, {
        textColor: theme.textColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
}
class ObstacleSymbolLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'obstacle-highlight';
    this.settings = this.mapSettings.obstacles;
    this.theme = this.mapTheme;
  }
  _show(map) {
    if (this.layerId != null && this.sourceId != null) {
      map.addLayer(LayerPriority.Obstacles, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(),
        'paint': {
          'text-color': this.theme.textColor(),
          'text-halo-color': this.theme.textHaloColor(),
          'text-halo-width': this.theme.textHaloWidth()
        },
        'layout': {
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom',
          'icon-image': getObstacleIconImage(),
          'text-field': getObstacleTestField(),
          'text-optional': true,
          'text-anchor': 'top',
          'text-font': ['Roboto'],
          'text-size': 9,
          'text-offset': this.getTextOffset(),
          'icon-ignore-placement': false,
          'icon-size': 0.4,
          // We want to show the tallest obstacles first as they'll hide
          // shorter obstacles. When overlapping is not allowed, the lower
          // number is higher priority. Therefore set the symbol-sort-key
          // to 0 - the MSL of the top of the obstacle. This isn't sorted
          // by AGL/height as a short obstacle on top of a cliff is probably
          // more important than a tall obstacle at the bottom of a cliff.
          'symbol-sort-key': ['-', 0, ['get', 'mslPlusAgl']],
          'symbol-z-order': 'source'
        }
      });
    }
  }
  _update(map) {
    this.updateTheme(map);
    this.updateLayers(map);
  }
  getFilter() {
    return ['all', ['!', getObstacleHighlightFilter(this.mapHighlight.obstacle())], ['>=', ['zoom'], this.settings.minimumZoom()], ['>=', ['get', 'agl'], this.settings.minimumHeightFt()]];
  }
  getTextOffset() {
    return ['array', 'number', 2, ['match', ['get', 'obstacleIcon'], 'LightedShort', ['literal', [0, -3.3]], 'UnlightedShort', ['literal', [0, -2.6]], 'LightedTall', ['literal', [0, -5]], ['literal', [0, -4.3]] // Tall and unlighted
    ]];
  }
  updateLayers(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  updateTheme(map) {
    const theme = this.mapTheme;
    if (this.layerId != null) {
      map.setLayerSymbolProperties(this.layerId, {
        textColor: theme.textColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
    }
  }
}

// export class ObstaclesLayer extends SimpleLayer {
class ObstacleLayer extends LayerGroup {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'obstacle';
    this.SOURCE_LAYER = 'Obstacle';
    this.groupInfo = [{
      layer: new ObstacleSymbolLayer(this.injector, this.radialMenuMapService, this.SOURCE_LAYER, LayerType.Obstacles)
    }, {
      layer: new ObstacleHighlightSymbolLayer(this.injector, this.radialMenuMapService, this.SOURCE_LAYER, LayerType.Obstacles)
    }];
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/index/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      minzoom: 8,
      maxzoom: 14
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
}
let ObstaclesService = /*#__PURE__*/(() => {
  class ObstaclesService extends LayerService {
    constructor() {
      super();
      this.config = this.mapConfig.overlay.obstacle;
      this.info = this.mapInfo.overlay.obstacle;
      this.settings = this.mapSettings.obstacles;
      this.images = [MapIcon.Obstacle_Lighted_Short, MapIcon.Obstacle_Unlighted_Short, MapIcon.Obstacle_Lighted_Tall, MapIcon.Obstacle_Unlighted_Tall].map(x => MapIconDefinitions.get(x));
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
      effect(() => this.updateConfig(), {
        allowSignalWrites: true
      });
    }
    createLayer(content) {
      return new ObstacleLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.info.content();
    }
    getVisible() {
      return this.config.visible();
    }
    getOpacity() {
      return this.config.opacity();
    }
    updateImages() {
      if (this.info.available() && this.config.visible()) {
        this.mapIcon.add(this.images);
      } else {
        this.mapIcon.remove(this.images);
      }
    }
    updateConfig() {
      const max = this.info.zoomMax();
      const min = this.info.zoomMin();
      const minimumZoom = this.settings.minimumZoom;
      if (minimumZoom() < min) {
        minimumZoom.set(min);
      }
      if (minimumZoom() > max) {
        minimumZoom.set(max);
      }
    }
    static {
      this.ɵfac = function ObstaclesService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ObstaclesService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ObstaclesService,
        factory: ObstaclesService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ObstaclesService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const PirepLineColor = {
  blue: new Color(0, 100, 255, 0.22),
  green: new Color(0, 255, 0, 0.18),
  orange: new Color(255, 152, 0, 0.26),
  red: new Color(255, 52, 0, 0.3)
};

/**
 * Get the expression for checking if the PIREP is not expired.
 * @param now The time to check against the expire time.
 * @returns An `ExpressionSpecification`.
 */
const isNotExpired = now => {
  return ['case', ['has', 'expireTime'], ['<=', now, ['get', 'expireTime']], true];
};
/**
 * Get the expression to get the altitude.
 * @returns An `ExpressionSpecification`.
 */
const getAltitude = () => {
  return ['get', 'altitude'];
};
/**
 * Get the expression to get the formatted altitude.
 * @returns An `ExpressionSpecification`.
 */
const getFormattedAltitude = () => {
  return ['case', ['has', 'altitude'], ['let', 'alt', ['/', getAltitude(), 100], ['concat', ['case', ['<', ['var', 'alt'], 10], '00', ['<', ['var', 'alt'], 100], '0', ''], ['to-string', ['var', 'alt']]]], ''];
};
/**
 * Get the expression to check if the PIREP is within the visible altitude range.
 * @param min The min altitude of the range.
 * @param max The max altitude of the range.
 * @returns An `ExpressionSpecification`.
 */
const withinAltitudeRange = (min, max) => {
  if (min == null && max == null) {
    return ['all'];
  }
  const expr = ['any', ['!', ['has', 'altitude']]];
  if (min != null && max != null) {
    expr.push(['all', ['<=', min, getAltitude()], ['<=', getAltitude(), max]]);
  } else if (min != null) {
    expr.push(['<=', min, getAltitude()]);
  } else if (max != null) {
    expr.push(['<=', getAltitude(), max]);
  }
  return expr;
};
/**
 * Get the expression to get the filename of the PIREP icon.
 * @returns An `ExpressionSpecification`.
 */
const getPirepIcon = () => {
  return ['concat', 'pirep_', ['get', 'icon'], ['case', ['has', 'altitude'], '_alt', '_no_alt']];
};
/**
 * Get the expression to get the PIREP line color based on the weather condition and its intensity.
 * @returns An `ExpressionSpecification`.
 */
const getPirepLineColor = () => {
  return ['match', ['get', 'icon'], PirepWeatherCondition.ICING_TR, PirepLineColor.green.toString(),
  // Icing
  PirepWeatherCondition.ICING_TR_LGT, PirepLineColor.green.toString(), PirepWeatherCondition.ICING_LGT, PirepLineColor.green.toString(), PirepWeatherCondition.ICING_LGT_MOD, PirepLineColor.orange.toString(), PirepWeatherCondition.ICING_MOD, PirepLineColor.orange.toString(), PirepWeatherCondition.ICING_MOD_SEV, PirepLineColor.red.toString(), PirepWeatherCondition.ICING_SEV, PirepLineColor.red.toString(), PirepWeatherCondition.SKYCOND_CLR, PirepLineColor.green.toString(),
  // Sky Conditions
  PirepWeatherCondition.SKYCOND_FEW, PirepLineColor.green.toString(), PirepWeatherCondition.SKYCOND_SCT, PirepLineColor.orange.toString(), PirepWeatherCondition.SKYCOND_BKN, PirepLineColor.orange.toString(), PirepWeatherCondition.SKYCOND_OVC, PirepLineColor.red.toString(), PirepWeatherCondition.SKYCOND_IMC, PirepLineColor.red.toString(), PirepWeatherCondition.TURBULENCE_LGT, PirepLineColor.green.toString(),
  // Turbulence
  PirepWeatherCondition.TURBULENCE_LGT_MOD, PirepLineColor.orange.toString(), PirepWeatherCondition.TURBULENCE_MOD, PirepLineColor.orange.toString(), PirepWeatherCondition.TURBULENCE_MOD_SEV, PirepLineColor.red.toString(), PirepWeatherCondition.TURBULENCE_SEV, PirepLineColor.red.toString(), PirepWeatherCondition.TURBULENCE_EXT, PirepLineColor.red.toString(), PirepLineColor.blue.toString()];
};
const getFilter = () => {
  const now = getUnixTime(new Date());
  const filter = [
  // do not show expired PIREPs
  'any', ['!', ['has', 'expireTime']], ['<=', now, ['get', 'expireTime']]];
  return filter;
};
class PirepLineLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'pirep-line';
    this.SOURCE_LAYER = 'PirepLine';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Pirep, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.SOURCE_LAYER,
        'type': 'line',
        'filter': getFilter(),
        'paint': {
          'line-color': getPirepLineColor(),
          'line-width': 3
        }
      });
    }
  }
  _update(_map) {
    // no action needed
  }
}
class PirepPointLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'pirep-point';
    this.SOURCE_LAYER = 'Pirep';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.Pirep, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.SOURCE_LAYER,
        'type': 'symbol',
        'filter': getFilter(),
        'paint': {
          'text-color': Color.White.toString()
        },
        'layout': {
          'icon-image': getPirepIcon(),
          'icon-text-fit': 'width',
          'text-field': getFormattedAltitude(),
          'text-font': ['Roboto'],
          'text-size': 12
        }
      });
    }
  }
  _update(_map) {
    // no action required
  }
}
class PirepLayer extends LayerGroup {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'pirep';
    this.groupInfo = [{
      layer: new PirepLineLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Pireps)
    }, {
      layer: new PirepPointLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Pireps)
    }];
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      minzoom: 4,
      maxzoom: 13
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
}
let PirepService = /*#__PURE__*/(() => {
  class PirepService extends LayerService {
    constructor() {
      super();
      this.images = [MapIcon.Pirep_Generic, MapIcon.Pirep_Icing_Negative, MapIcon.Pirep_Icing_Trace, MapIcon.Pirep_Icing_TraceToLight, MapIcon.Pirep_Icing_Light, MapIcon.Pirep_Icing_LightToModerate, MapIcon.Pirep_Icing_Moderate, MapIcon.Pirep_Icing_ModerateToSevere, MapIcon.Pirep_Icing_Severe, MapIcon.Pirep_Sky_Unknown, MapIcon.Pirep_Sky_Clear, MapIcon.Pirep_Sky_Few, MapIcon.Pirep_Sky_Scattered, MapIcon.Pirep_Sky_Broken, MapIcon.Pirep_Sky_Overcast, MapIcon.Pirep_Sky_InstrumentConditions, MapIcon.Pirep_Turbulence_Negative, MapIcon.Pirep_Turbulence_Light, MapIcon.Pirep_Turbulence_LightToModerate, MapIcon.Pirep_Turbulence_Moderate, MapIcon.Pirep_Turbulence_ModerateToSevere, MapIcon.Pirep_Turbulence_Severe, MapIcon.Pirep_Turbulence_Extreme, MapIcon.Pirep_Altitude_Generic, MapIcon.Pirep_Altitude_Icing_Negative, MapIcon.Pirep_Altitude_Icing_Trace, MapIcon.Pirep_Altitude_Icing_TraceToLight, MapIcon.Pirep_Altitude_Icing_Light, MapIcon.Pirep_Altitude_Icing_LightToModerate, MapIcon.Pirep_Altitude_Icing_Moderate, MapIcon.Pirep_Altitude_Icing_ModerateToSevere, MapIcon.Pirep_Altitude_Icing_Severe, MapIcon.Pirep_Altitude_Sky_Unknown, MapIcon.Pirep_Altitude_Sky_Clear, MapIcon.Pirep_Altitude_Sky_Few, MapIcon.Pirep_Altitude_Sky_Scattered, MapIcon.Pirep_Altitude_Sky_Broken, MapIcon.Pirep_Altitude_Sky_Overcast, MapIcon.Pirep_Altitude_Sky_InstrumentConditions, MapIcon.Pirep_Altitude_Turbulence_Negative, MapIcon.Pirep_Altitude_Turbulence_Light, MapIcon.Pirep_Altitude_Turbulence_LightToModerate, MapIcon.Pirep_Altitude_Turbulence_Moderate, MapIcon.Pirep_Altitude_Turbulence_ModerateToSevere, MapIcon.Pirep_Altitude_Turbulence_Severe, MapIcon.Pirep_Altitude_Turbulence_Extreme, MapIcon.Pirep_NoAltitude_Generic, MapIcon.Pirep_NoAltitude_Icing_Negative, MapIcon.Pirep_NoAltitude_Icing_Trace, MapIcon.Pirep_NoAltitude_Icing_TraceToLight, MapIcon.Pirep_NoAltitude_Icing_Light, MapIcon.Pirep_NoAltitude_Icing_LightToModerate, MapIcon.Pirep_NoAltitude_Icing_Moderate, MapIcon.Pirep_NoAltitude_Icing_ModerateToSevere, MapIcon.Pirep_NoAltitude_Icing_Severe, MapIcon.Pirep_NoAltitude_Sky_Unknown, MapIcon.Pirep_NoAltitude_Sky_Clear, MapIcon.Pirep_NoAltitude_Sky_Few, MapIcon.Pirep_NoAltitude_Sky_Scattered, MapIcon.Pirep_NoAltitude_Sky_Broken, MapIcon.Pirep_NoAltitude_Sky_Overcast, MapIcon.Pirep_NoAltitude_Sky_InstrumentConditions, MapIcon.Pirep_NoAltitude_Turbulence_Negative, MapIcon.Pirep_NoAltitude_Turbulence_Light, MapIcon.Pirep_NoAltitude_Turbulence_LightToModerate, MapIcon.Pirep_NoAltitude_Turbulence_Moderate, MapIcon.Pirep_NoAltitude_Turbulence_ModerateToSevere, MapIcon.Pirep_NoAltitude_Turbulence_Severe, MapIcon.Pirep_NoAltitude_Turbulence_Extreme].map(x => MapIconDefinitions.get(x));
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new PirepLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.overlay.pirep.content();
    }
    getVisible() {
      return this.mapConfig.overlay.pirep.visible();
    }
    updateImages() {
      const config = this.mapConfig.overlay.pirep;
      const info = this.mapInfo.overlay.pirep;
      if (info.available() && config.visible()) {
        this.mapIcon.add(this.images);
      } else {
        this.mapIcon.remove(this.images);
      }
    }
    static {
      this.ɵfac = function PirepService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PirepService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PirepService,
        factory: PirepService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PirepService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class WebglAnimationLayerService {
  constructor() {
    this.layer = null; // Only one layer for WebGL animation
    this.contentObserver = new ContentObserver();
    this.mapService = inject(MapService);
    this._updateProgress = effect(() => {
      const progress = this.mapService.animation.progress();
      this.layer?.webglAnimation?.setProgress(progress);
    });
    this._updateOpacity = effect(() => {
      const opacity = this.getOpacity();
      this.layer?.webglAnimation?.setOpacity(opacity);
    });
    this._updateLayer = effect(() => {
      const map = this.mapService.map();
      if (map == null) {
        return;
      }
      const content = this.getContent();
      const changes = this.contentObserver.update(content, this.getVisible());
      if (changes.removed.length > 0) {
        this.deleteLayer(map);
      }
      if (changes.added.length > 0) {
        this.addLayer(map, changes.added[0]);
      }
      this.updateAvailableRange().catch(error => {
        console.error('Failed to update animation range:', error);
      });
    });
  }
  getOpacity() {
    return MAX_OPACITY;
  }
  addLayer(map, content) {
    if (content.snapshots != null) {
      this.layer = this.createLayer(content);
      this.layer.add(map);
      this.layer.webglAnimation?.setOpacity(this.getOpacity());
    }
  }
  deleteLayer(map) {
    if (this.layer != null) {
      this.layer.remove(map);
      this.layer = null;
    }
  }
  async updateAvailableRange() {
    const content = this.getContent();
    if (this.layer == null || !this.getVisible() || content[0].snapshots == null) return;
    const options = this.mapService.animation.options();
    let maxTime = 0,
      minTime = 0;
    if (options == null) {
      // Return the 1st frame if there is no options info
      minTime = content[0].snapshots[0].time;
      maxTime = content[0].snapshots[0].time;
    } else {
      const range = getSnapshotAvailableRange(this.getContent(), options?.now ?? 0, this.MAXIMUM_AGE_SECONDS, this.DATA_VALIDITY_SECONDS, this.ONLY_PAST);
      minTime = range[0][0];
      maxTime = range[0][1];
    }
    try {
      const success = await this.layer.setupAnimation(minTime, maxTime);
      if (!success) {
        throw new Error('Failed to setup animation');
      }
      untracked(() => this.setAvailableLoadedRange([[minTime, maxTime]]));
    } catch (error) {
      throw new Error(`Animation setup failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
var WebglMapCoverageType = /*#__PURE__*/function (WebglMapCoverageType) {
  WebglMapCoverageType["RADAR"] = "radar";
  WebglMapCoverageType["DEFAULT"] = "default";
  return WebglMapCoverageType;
}(WebglMapCoverageType || {});
let WebglMapCoverage = /*#__PURE__*/(() => {
  class WebglMapCoverage {
    static {
      this.DEFAULT_MATRIX = [[0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]];
    }
    static {
      this.RADAR_MATRIX = [[0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [1, 1, 0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0], [1, 1, 1, 1, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0, 0], [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0], [1, 1, 1, 1, 1, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0, 0], [1, 1, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 1, 1, 1, 1], [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 1, 1, 1, 1], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]];
    }
    constructor(options) {
      this.coverageType = options?.type == null ? WebglMapCoverageType.DEFAULT : options.type;
      const matrix = this.getMatrixByType(this.coverageType);
      this.maskMatrixSize = matrix.length;
      this.bits = new Uint32Array(this.maskMatrixSize);
      this.baseZoom = Math.log2(this.maskMatrixSize);
      for (let y = 0; y < this.maskMatrixSize; y++) {
        let row = 0;
        for (let x = 0; x < this.maskMatrixSize; x++) {
          if (matrix[y][x]) {
            row |= 1 << x;
          }
        }
        this.bits[y] = row;
      }
    }
    hasDataAtBaseZoom(x, y) {
      if (x < 0 || x >= this.maskMatrixSize || y < 0 || y >= this.maskMatrixSize) return false;
      return (this.bits[y] & 1 << x) !== 0;
    }
    getMatrixByType(type) {
      switch (type) {
        case WebglMapCoverageType.RADAR:
          return WebglMapCoverage.RADAR_MATRIX;
        default:
          return WebglMapCoverage.DEFAULT_MATRIX;
      }
    }
    shouldDownloadTile(tileX, tileY, zoom) {
      const zoomDiff = zoom - this.baseZoom;
      if (zoomDiff <= 0) {
        const scale = 1 << -zoomDiff;
        const startX = tileX * scale;
        const startY = tileY * scale;
        const endX = Math.min(startX + scale, this.maskMatrixSize);
        const endY = Math.min(startY + scale, this.maskMatrixSize);
        for (let y = startY; y < endY; y++) {
          for (let x = startX; x < endX; x++) {
            if (this.hasDataAtBaseZoom(x, y)) return true;
          }
        }
        return false;
      } else {
        const scale = 1 << zoomDiff;
        const matrixX = Math.floor(tileX / scale);
        const matrixY = Math.floor(tileY / scale);
        return this.hasDataAtBaseZoom(matrixX, matrixY);
      }
    }
  }
  return WebglMapCoverage;
})();
const vertexShaderSource = `attribute vec2 a_position;attribute vec2 a_texCoord;varying vec2 v_texCoord;
  void main() {gl_Position = vec4(a_position, 0, 1); v_texCoord = a_texCoord;}`;
const fragmentShaderSource = `
  precision mediump float;
  uniform sampler2D u_image;
  uniform float u_opacity;
  varying vec2 v_texCoord;

  void main() {
    vec4 color = texture2D(u_image, v_texCoord);
    gl_FragColor = vec4(color.rgb * color.a, color.a * u_opacity);
  }
`;
class WebglAnimationRenderer {
  constructor(gl, textureManager, webglFrameManager) {
    this.gl = gl;
    this.textureManager = textureManager;
    this.webglFrameManager = webglFrameManager;
    this.positionAttributeLocation = 0;
    this.texCoordAttributeLocation = 0;
    this.opacity = 1.0;
    this.program = this.createProgram();
    this.positionBuffer = this.createPositionBuffer();
    this.texCoordBuffer = this.gl.createBuffer();
    this.texCoordData = new Float32Array(8);
    this.pausedTexture = null;
    this.positionAttributeLocation = this.gl.getAttribLocation(this.program, "a_position");
    this.texCoordAttributeLocation = this.gl.getAttribLocation(this.program, "a_texCoord");
    this.opacityLocation = this.gl.getUniformLocation(this.program, "u_opacity");
    this.setupGLState();
  }
  updateTextureCoords(currMapBounds, currentZoom, tileZoom, frameInfo) {
    const texCoords = this.getTextureRangeForViewport(currMapBounds, currentZoom, tileZoom, frameInfo);
    this.texCoordData.set(texCoords);
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.texCoordBuffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, this.texCoordData, this.gl.STATIC_DRAW);
  }
  render(time) {
    const timestamp = this.webglFrameManager.getFrameAtTime(time);
    if (timestamp === undefined) return;
    const texture = this.textureManager.getTexture(timestamp);
    if (texture == null) {
      return;
    }
    if (this.pausedTexture != null) {
      this.pausedTexture = null;
      this.resetPositionBuffer();
    }
    this.gl.useProgram(this.program);
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer);
    this.gl.vertexAttribPointer(this.positionAttributeLocation, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.positionAttributeLocation);
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.texCoordBuffer);
    this.gl.vertexAttribPointer(this.texCoordAttributeLocation, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.texCoordAttributeLocation);
    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
    const uImageLocation = this.gl.getUniformLocation(this.program, "u_image");
    this.gl.uniform1i(uImageLocation, 0);
    if (this.opacityLocation !== null) {
      this.gl.uniform1f(this.opacityLocation, this.opacity);
    }
    const {
      width,
      height
    } = this.gl.canvas;
    this.gl.viewport(0, 0, width, height);
    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }
  renderInteracting(time, hasBaseTiles, lastBounds, lastZoom, currBounds, currZoom) {
    const timestamp = this.webglFrameManager.getFrameAtTime(time);
    if (timestamp === undefined) {
      return;
    }
    if (this.pausedTexture == null || !hasBaseTiles) {
      const texture = this.textureManager.getTexture(timestamp);
      if (texture == null) {
        return;
      }
      this.pausedTexture = texture;
    }
    const newPositions = this.calculateInteractionPositions(lastBounds, currBounds, lastZoom, currZoom);
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, newPositions, this.gl.STATIC_DRAW);
    this.gl.useProgram(this.program);
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer);
    this.gl.vertexAttribPointer(this.positionAttributeLocation, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.positionAttributeLocation);
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.texCoordBuffer);
    this.gl.vertexAttribPointer(this.texCoordAttributeLocation, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.texCoordAttributeLocation);
    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.pausedTexture);
    const {
      width,
      height
    } = this.gl.canvas;
    this.gl.viewport(0, 0, width, height);
    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }
  cleanup() {
    this.gl.bindTexture(this.gl.TEXTURE_2D, null);
    this.gl.deleteProgram(this.program);
    this.gl.deleteBuffer(this.positionBuffer);
    this.gl.deleteBuffer(this.texCoordBuffer);
  }
  setOpacity(opacity) {
    this.opacity = Math.max(0, Math.min(1, opacity));
  }
  // private methods
  setupGLState() {
    this.gl.enable(this.gl.BLEND);
    this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
    this.gl.clearColor(0.0, 0.0, 0.0, 0.0);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
  }
  createShader(type, source) {
    const shader = this.gl.createShader(type);
    if (shader == null) {
      throw new Error('[WebglAnimationRenderer]Failed to create shader');
    }
    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);
    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      throw new Error(`[WebglAnimationRenderer]An error occurred compiling the shaders: ${this.gl.getShaderInfoLog(shader)}`);
    }
    return shader;
  }
  createProgram() {
    const vertexShader = this.createShader(this.gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = this.createShader(this.gl.FRAGMENT_SHADER, fragmentShaderSource);
    const program = this.gl.createProgram();
    if (program == null) {
      throw new Error('[WebglAnimationRenderer]Failed to create program');
    }
    this.gl.attachShader(program, vertexShader);
    this.gl.attachShader(program, fragmentShader);
    this.gl.linkProgram(program);
    if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
      throw new Error(`[WebglAnimationRenderer]Unable to initialize the shader program: ${this.gl.getProgramInfoLog(program)}`);
    }
    return program;
  }
  createPositionBuffer() {
    const buffer = this.gl.createBuffer();
    if (buffer == null) {
      throw new Error('[WebglAnimationRenderer]Failed to create position buffer');
    }
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffer);
    const positions = [-1.0, 1.0, 1.0, 1.0, -1.0, -1.0, 1.0, -1.0];
    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(positions), this.gl.STATIC_DRAW);
    return buffer;
  }
  getTextureRangeForViewport(viewportBounds, currentZoom, tileZoom, frameInfo) {
    const TILE_SIZE = 256;
    const zoomScale = Math.pow(2, currentZoom - tileZoom);
    const sw = viewportBounds.getSouthWest();
    const ne = viewportBounds.getNorthEast();
    const swTile = this.latLngToTilePoint(sw.lat, sw.lng, currentZoom);
    const neTile = this.latLngToTilePoint(ne.lat, ne.lng, currentZoom);
    const baseSwTile = {
      x: swTile.x / zoomScale,
      y: swTile.y / zoomScale
    };
    const baseNeTile = {
      x: neTile.x / zoomScale,
      y: neTile.y / zoomScale
    };
    const textureWidth = frameInfo.tilesX * TILE_SIZE;
    const textureHeight = frameInfo.tilesY * TILE_SIZE;
    const baseTileX = Math.floor(baseSwTile.x);
    const baseTileY = Math.floor(baseSwTile.y);
    const west = (baseSwTile.x - baseTileX) * TILE_SIZE;
    const east = (baseNeTile.x - baseTileX) * TILE_SIZE;
    const north = (baseNeTile.y - baseTileY) * TILE_SIZE;
    const south = (baseSwTile.y - baseTileY) * TILE_SIZE;
    const left = Math.max(0, west / textureWidth);
    const right = Math.min(1, east / textureWidth);
    const top = 1 - Math.min(1, north / textureHeight);
    const bottom = 1 - Math.max(0, south / textureHeight);
    return [left, top, right, top, left, bottom, right, bottom];
  }
  latLngToTilePoint(lat, lng, zoom) {
    const n = Math.pow(2, zoom);
    const latRad = lat * Math.PI / 180;
    const x = (lng + 180) / 360 * n;
    const mercY = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n;
    const y = n - mercY;
    return {
      x,
      y
    };
  }
  resetPositionBuffer() {
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array([-1.0, 1.0, 1.0, 1.0, -1.0, -1.0, 1.0, -1.0]), this.gl.STATIC_DRAW);
  }
  calculateInteractionPositions(prevBounds, currBounds, prevZoom, currZoom) {
    const prevPixels = {
      sw: this.latLngToTilePoint(prevBounds.getSouthWest().lat, prevBounds.getSouthWest().lng, currZoom),
      ne: this.latLngToTilePoint(prevBounds.getNorthEast().lat, prevBounds.getNorthEast().lng, currZoom)
    };
    const currPixels = {
      sw: this.latLngToTilePoint(currBounds.getSouthWest().lat, currBounds.getSouthWest().lng, currZoom),
      ne: this.latLngToTilePoint(currBounds.getNorthEast().lat, currBounds.getNorthEast().lng, currZoom)
    };
    const currWidth = currPixels.ne.x - currPixels.sw.x;
    const currHeight = currPixels.sw.y - currPixels.ne.y;
    const left = (prevPixels.sw.x - currPixels.sw.x) / currWidth * 2 - 1;
    const right = (prevPixels.ne.x - currPixels.sw.x) / currWidth * 2 - 1;
    const top = 1 - (prevPixels.ne.y - currPixels.ne.y) / currHeight * 2;
    const bottom = 1 - (prevPixels.sw.y - currPixels.ne.y) / currHeight * 2;
    return new Float32Array([left, top, right, top, left, bottom, right, bottom]);
  }
}
class WebglFrameManager {
  constructor() {
    this.frames = new Map();
    this.pendingFullFrames = new Map(); // Store fully loaded frames
  }
  addFrame(timestamp, zoom, tilesX, tilesY) {
    this.frames.set(timestamp, {
      timestamp,
      zoom,
      tilesX,
      tilesY
    });
  }
  reset() {
    this.frames.clear();
    this.pendingFullFrames.clear();
  }
  getFrameAtTime(time) {
    let closestTimestamp;
    let smallestDiff = Infinity;
    for (const [timestamp] of this.frames) {
      const diff = Math.abs(timestamp - time);
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestTimestamp = timestamp;
      }
    }
    return closestTimestamp;
  }
}
class WebglTextureManager {
  constructor(gl) {
    this.gl = gl;
    this.textures = new Map();
  }
  createTexture(timestamp, width, height) {
    const texture = this.gl.createTexture();
    if (texture == null) {
      throw new Error('[TextureManager]Failed to create texture');
    }
    this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
    this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, width, height, 0, this.gl.RGBA, this.gl.UNSIGNED_BYTE, null);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
    this.textures.set(timestamp, {
      texture,
      width,
      height
    });
  }
  updateTextureRegion(timestamp, x, y, data) {
    const textureInfo = this.textures.get(timestamp);
    if (textureInfo == null) {
      throw new Error(`[TextureManager]Texture for timestamp ${timestamp} not found`);
    }
    this.gl.bindTexture(this.gl.TEXTURE_2D, textureInfo.texture);
    this.gl.texSubImage2D(this.gl.TEXTURE_2D, 0, x, y, this.gl.RGBA, this.gl.UNSIGNED_BYTE, data);
  }
  getTexture(timestamp) {
    return this.textures.get(timestamp)?.texture;
  }
  cleanup() {
    for (const {
      texture
    } of this.textures.values()) {
      this.gl.deleteTexture(texture);
    }
    this.textures.clear();
  }
}
class WebglTileLoader {
  constructor(webglTextureManager) {
    this.webglTextureManager = webglTextureManager;
    this.maxParallelLoading = 84;
    this.activeRequests = new Map();
    this.newlyLoadedFrames = new Set();
    this.loadingCount = 0;
    this.tileQueue = [];
    this.stats = {
      total: 0,
      loaded: 0,
      failed: 0,
      percent: 0
    };
    this.loadingCallbacks = new Set();
    this.completeCallbacks = new Set();
    this.emptyTileImage = this.createTransparentBlackImage();
  }
  createTransparentBlackImage() {
    const SIZE = 256;
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    if (ctx == null) {
      throw new Error('[TileLoader]Could not get 2D context');
    }
    const imageData = ctx.createImageData(SIZE, SIZE);
    const pixels32 = new Uint32Array(imageData.data.buffer);
    pixels32.fill(0x40000000); // (0,0,0,64) in RGBA, transparent black
    ctx.putImageData(imageData, 0, 0);
    return canvas;
  }
  onLoadingProgress(callback) {
    this.loadingCallbacks.add(callback);
    return () => this.loadingCallbacks.delete(callback);
  }
  onLoadingComplete(callback) {
    this.completeCallbacks.add(callback);
    return () => this.completeCallbacks.delete(callback);
  }
  addTiles(tiles) {
    // Reset stats
    this.stats.total = tiles.length;
    this.stats.loaded = 0;
    this.stats.failed = 0;
    this.stats.percent = 0;
    this.newlyLoadedFrames.clear();
    this.tileQueue.push(...tiles);
    this.startLoadingTiles();
  }
  startLoadingTiles() {
    while (this.loadingCount < this.maxParallelLoading && this.tileQueue.length > 0) {
      const tile = this.tileQueue.shift();
      this.loadTile(tile);
    }
  }
  addEmptyTiles(tiles) {
    tiles.forEach(tile => {
      this.webglTextureManager.updateTextureRegion(tile.timestamp, tile.x, tile.y, this.emptyTileImage);
    });
  }
  loadTile(tile) {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.fetchPriority = "high";
    this.loadingCount = this.loadingCount + 1;
    const key = `${tile.timestamp}-${tile.x}-${tile.y}`;
    this.activeRequests.set(key, img);
    img.onload = () => this.handleTileLoaded(tile, img);
    img.onerror = () => this.handleTileError(tile);
    img.src = tile.url;
  }
  handleTileLoaded(tile, img) {
    try {
      if (img.width <= 1 || img.height <= 1) {
        this.webglTextureManager.updateTextureRegion(tile.timestamp, tile.x, tile.y, this.emptyTileImage);
      } else {
        this.webglTextureManager.updateTextureRegion(tile.timestamp, tile.x, tile.y, img);
      }
      this.stats.loaded = this.stats.loaded + 1;
      this.newlyLoadedFrames.add(tile.timestamp);
      this.updateProgress();
    } catch (error) {
      console.error('[TileLoader] Error updating texture:', error);
      this.stats.failed = this.stats.failed + 1;
    }
    this.cleanupAndContinue(tile);
  }
  handleTileError(tile) {
    console.warn('[TileLoader] Failed to load tile:', tile.url);
    this.stats.failed = this.stats.failed + 1;
    this.updateProgress();
    this.cleanupAndContinue(tile);
  }
  cleanupAndContinue(tile) {
    const key = `${tile.timestamp}-${tile.x}-${tile.y}`;
    this.activeRequests.delete(key);
    this.loadingCount = this.loadingCount - 1;
    this.startLoadingTiles();
    if (this.loadingCount === 0 && this.tileQueue.length === 0) {
      this.completeCallbacks.forEach(cb => cb());
    }
  }
  updateProgress() {
    const total = this.stats.total;
    const processed = this.stats.loaded + this.stats.failed;
    this.stats.percent = processed / total * 100;
    const stats = {
      ...this.stats,
      newlyLoadedFrames: Array.from(this.newlyLoadedFrames)
    };
    this.newlyLoadedFrames.clear();
    this.loadingCallbacks.forEach(cb => cb(stats));
  }
  cancelLoading() {
    this.activeRequests.forEach(img => {
      img.src = '';
      img.onload = null;
      img.onerror = null;
    });
    this.tileQueue = [];
    this.activeRequests.clear();
    this.loadingCount = 0;
    this.newlyLoadedFrames.clear();
  }
}
const TileLoadingStatus = {
  NONE: 'none',
  RELOAD: 'reload',
  PARTIAL_FRAME_LOADED: 'partialFrameLoaded',
  ALL_FRAME_LOADED: 'allFrameLoaded'
};
class WebglAnimation {
  /**
   * Creates a new WebGL animation instance.
   *
   * @param gl - The WebGL rendering context
   * @param map - The map wrapper instance
   * @param tileMaxZoom - Maximum zoom level for tiles
   * @param coverageType - Type of map coverage for tile loading
   */
  constructor(gl, map, tileMaxZoom, coverageType) {
    // Animation state
    this.visible = true;
    this.animationFrameId = null;
    this.startAnimationTime = null;
    this.progress = 1; // between 0 ~ 1
    this.lastZoom = null;
    this.lastBounds = null;
    this.isInteracting = false;
    this.hasBaseTiles = false;
    // Loading state
    this.tileLoadingStatus = TileLoadingStatus.NONE;
    this.isLoading = false;
    this.isPartiallyLoaded = false;
    this.lastLoadAttempt = null;
    this.partialTimestamps = [];
    this.allTimestamps = [];
    // Event handlers
    this.moveStartHandler = null;
    this.moveEndHandler = null;
    this.renderControl = () => {
      if (this.startAnimationTime == null) {
        return;
      }
      if (this.tileLoadingStatus === TileLoadingStatus.ALL_FRAME_LOADED) {
        const frameIndex = Math.floor(this.progress * (this.allTimestamps.length - 1));
        this.currentFrameTime = this.allTimestamps[frameIndex];
      } else if (this.tileLoadingStatus === TileLoadingStatus.PARTIAL_FRAME_LOADED) {
        const frameIndex = Math.floor(this.progress * (this.partialTimestamps.length - 1));
        this.currentFrameTime = this.partialTimestamps[frameIndex];
      }
      this.map.triggerRepaint();
      this.animationFrameId = requestAnimationFrame(this.renderControl);
    };
    this.map = map;
    this.tileMaxZoom = tileMaxZoom;
    this.tileZoom = Math.floor(this.map.getZoom());
    // Initialize managers and components
    this.webglTextureManager = new WebglTextureManager(gl);
    this.webglFrameManager = new WebglFrameManager();
    this.webglAnimationRenderer = new WebglAnimationRenderer(gl, this.webglTextureManager, this.webglFrameManager);
    this.webglTileLoader = new WebglTileLoader(this.webglTextureManager);
    this.webglMapCoverage = new WebglMapCoverage({
      type: coverageType
    });
    this.setupMouseEventHandlers();
  }
  // public methods
  async loadAllFrames(urlBase, fullTimestamps) {
    fullTimestamps.sort((a, b) => a - b); // old to new
    this.isLoading = true;
    this.isPartiallyLoaded = false;
    this.webglFrameManager.reset();
    this.lastLoadAttempt = {
      urlBase,
      fullTimestamps
    };
    if (this.isLoading) {
      this.cancelLoading();
    }
    this.pausedFrameTime = fullTimestamps[fullTimestamps.length - 1];
    this.hasBaseTiles = this.map.getZoom() <= this.tileMaxZoom;
    this.tileZoom = this.hasBaseTiles ? Math.floor(this.map.getZoom()) : this.tileMaxZoom;
    const bounds = this.map.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const minTile = this.lngLatToTile(sw.lng, ne.lat, this.tileZoom);
    const maxTile = this.lngLatToTile(ne.lng, sw.lat, this.tileZoom);
    const loadResults = [];
    this.partialTimestamps = fullTimestamps.filter((_, index) => index % 2 === 0);
    const resultPartial = await this.loadFrames(minTile, maxTile, urlBase, this.partialTimestamps);
    loadResults.push(resultPartial);
    this.tileLoadingStatus = TileLoadingStatus.PARTIAL_FRAME_LOADED;
    this.isPartiallyLoaded = true;
    this.startAnimation();
    const downloadRemainTimestamps = fullTimestamps.filter((_, index) => index % 2 === 1);
    this.allTimestamps = fullTimestamps;
    const resultFull = await this.loadFrames(minTile, maxTile, urlBase, downloadRemainTimestamps);
    loadResults.push(resultFull);
    this.tileLoadingStatus = TileLoadingStatus.ALL_FRAME_LOADED;
    this.isLoading = false;
    this.isPartiallyLoaded = !loadResults.includes(null);
  }
  getVisible() {
    return this.visible;
  }
  render() {
    if (this.isInteracting) {
      if (this.pausedFrameTime !== undefined && this.lastBounds != null && this.lastZoom != null) {
        this.webglAnimationRenderer.renderInteracting(this.pausedFrameTime, this.hasBaseTiles, this.lastBounds, this.lastZoom, this.map.getBounds(), this.map.getZoom());
      } else {
        console.error('Failed to renderInteracting. Missing required values:', {
          pausedFrameTime: this.pausedFrameTime !== undefined ? this.pausedFrameTime : 'undefined',
          lastBounds: this.lastBounds != null ? this.lastBounds : 'null',
          lastZoom: this.lastZoom != null ? this.lastZoom : 'null'
        });
      }
    } else if (this.currentFrameTime !== undefined) {
      if (this.hasBaseTiles) {
        this.webglAnimationRenderer.render(this.currentFrameTime);
      } else {
        if (this.lastBounds != null && this.lastZoom != null) {
          this.webglAnimationRenderer.renderInteracting(this.currentFrameTime, this.hasBaseTiles, this.lastBounds, this.lastZoom, this.map.getBounds(), this.map.getZoom());
        }
      }
    }
  }
  setVisible(isVisible) {
    this.visible = isVisible;
  }
  setProgress(progress) {
    this.progress = progress;
  }
  startAnimation() {
    const initialFrame = this.webglFrameManager.frames.values().next().value;
    if (initialFrame != null) {
      this.isInteracting = false;
      this.lastBounds = this.map.getBounds();
      this.lastZoom = this.map.getZoom();
      this.webglAnimationRenderer.updateTextureCoords(this.map.getBounds(), this.map.getZoom(), this.tileZoom, initialFrame);
    }
    this.startAnimationTime = performance.now();
    this.animationFrameId = requestAnimationFrame(this.renderControl);
  }
  stopAnimation() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.startAnimationTime = null;
  }
  cleanup() {
    this.stopAnimation();
    this.cancelLoading();
    this.webglTextureManager.cleanup();
    this.webglAnimationRenderer.cleanup();
    this.lastLoadAttempt = null;
    this.isPartiallyLoaded = false;
    this.removeMouseEventHandlers();
  }
  setOpacity(opacity) {
    this.webglAnimationRenderer.setOpacity(opacity);
  }
  //private methods
  removeMouseEventHandlers() {
    if (this.moveStartHandler != null) {
      this.map.off('movestart', this.moveStartHandler);
      this.moveStartHandler = null;
    }
    if (this.moveEndHandler != null) {
      this.map.off('moveend', this.moveEndHandler);
      this.moveEndHandler = null;
    }
  }
  async loadFrames(minTile, maxTile, urlBase, timeSlices) {
    const tilesX = maxTile.x - minTile.x + 1;
    const tilesY = maxTile.y - minTile.y + 1;
    const tileSize = 256;
    const textureWidth = tilesX * tileSize;
    const textureHeight = tilesY * tileSize;
    try {
      const tiles = [];
      const emptyTiles = [];
      for (const timestamp of timeSlices) {
        this.webglTextureManager.createTexture(timestamp, textureWidth, textureHeight);
        this.webglFrameManager.addFrame(timestamp, this.map.getZoom(), tilesX, tilesY);
        for (let x = minTile.x; x <= maxTile.x; x++) {
          for (let y = minTile.y; y <= maxTile.y; y++) {
            if (this.webglMapCoverage.shouldDownloadTile(x, y, this.tileZoom)) {
              const tmsY = (1 << this.tileZoom) - 1 - y;
              const url = `${urlBase}/${timestamp}/${this.tileZoom}/${x}/${tmsY}.png`;
              tiles.push({
                url,
                timestamp,
                x: (x - minTile.x) * tileSize,
                y: (y - minTile.y) * tileSize
              });
            } else {
              emptyTiles.push({
                url: '',
                timestamp,
                x: (x - minTile.x) * tileSize,
                y: (y - minTile.y) * tileSize
              });
            }
          }
        }
      }
      if (emptyTiles.length > 0) {
        this.webglTileLoader.addEmptyTiles(emptyTiles);
      }
      return new Promise(resolve => {
        if (tiles.length === 0) {
          resolve();
          return;
        }
        const unsubscribe = this.webglTileLoader.onLoadingComplete(() => {
          unsubscribe();
          resolve();
        });
        this.webglTileLoader.addTiles(tiles);
      });
    } catch (error) {
      console.error('[WebglAnimation] Error during frame loading:', error);
      return null;
    }
  }
  cancelLoading() {
    this.webglTileLoader.cancelLoading();
    this.isLoading = false;
  }
  setupMouseEventHandlers() {
    this.moveStartHandler = () => {
      if (this.visible) {
        this.cancelLoading();
        this.stopAnimation();
        this.isInteracting = true;
        this.pausedFrameTime = this.currentFrameTime;
        this.currentFrameTime = undefined;
      }
    };
    this.moveEndHandler = async () => {
      if (this.visible) {
        this.reloadTiles();
        this.currentFrameTime = this.pausedFrameTime;
      }
    };
    this.map.on('movestart', this.moveStartHandler);
    this.map.on('moveend', this.moveEndHandler);
  }
  async reloadTiles() {
    if (this.isMapRangeChanged()) {
      if (this.lastLoadAttempt != null) {
        const {
          urlBase,
          fullTimestamps
        } = this.lastLoadAttempt;
        try {
          this.tileLoadingStatus = TileLoadingStatus.RELOAD;
          await this.loadAllFrames(urlBase, fullTimestamps);
        } catch (error) {
          console.error('[WebglAnimation] Error reloading frames:', error);
        }
      }
    }
  }
  lngLatToTile(lng, lat, zoom) {
    const intZoom = Math.floor(zoom);
    const n = Math.pow(2, intZoom);
    const x = Math.floor((lng + 180) / 360 * n);
    const latRad = lat * Math.PI / 180;
    const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
    return {
      x,
      y
    };
  }
  isMapRangeChanged(zoomTolerance = 0.01, boundsTolerance = 0.0001) {
    // Early return if no previous values
    const currentZoom = this.map.getZoom();
    const currentBounds = this.map.getBounds();
    if (this.lastZoom == null || this.lastBounds == null) return true;
    // Check zoom change
    if (Math.abs(currentZoom - this.lastZoom) > zoomTolerance) return true;
    // Check each bounds dimension and return early if any has changed
    if (Math.abs(this.lastBounds.getWest() - currentBounds.getWest()) > boundsTolerance) return true;
    if (Math.abs(this.lastBounds.getEast() - currentBounds.getEast()) > boundsTolerance) return true;
    if (Math.abs(this.lastBounds.getNorth() - currentBounds.getNorth()) > boundsTolerance) return true;
    if (Math.abs(this.lastBounds.getSouth() - currentBounds.getSouth()) > boundsTolerance) return true;
    return false;
  }
}
class WebglAnimationLayer extends AnimationLayer {
  constructor() {
    super(...arguments);
    this.webglAnimation = null;
    this.timestamps = [];
  }
  add(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    this.layerId = map.layerIds.next(this.LAYER_PREFIX);
    map.addLayer(this.LAYER_PRIORITY, {
      id: this.layerId,
      type: 'custom',
      onAdd: async (_mapInstance, gl) => {
        this.gl = gl;
        this.webglAnimation = new WebglAnimation(gl, map, this.MAX_ZOOM, this.coverageType);
      },
      render: () => {
        this.gl?.enable(this.gl.BLEND);
        this.gl?.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
        this.webglAnimation?.render();
      },
      onRemove: (_map, _gl) => {
        this.webglAnimation?.cleanup();
        this.webglAnimation = null;
      }
    });
  }
  show(_map) {
    if (this.layerId != null) {
      this.webglAnimation?.startAnimation();
    }
  }
  remove(map) {
    if (this.layerId != null) {
      this.webglAnimation?.cleanup();
      this.webglAnimation = null;
      map.removeLayer(this.layerId);
      this.layerId = undefined;
    }
  }
  async setupAnimation(minTime, maxTime) {
    if (this.content == null || this.content.snapshots == null || this.webglAnimation == null) {
      console.error("Fail to setup animation");
      return false;
    }
    try {
      this.timestamps = this.content.snapshots.map(snapshot => snapshot.time).filter(time => time >= minTime && time <= maxTime);
      if (this.timestamps.length === 0) {
        throw new Error('No valid time slices found in the specified range');
      }
      await this.webglAnimation.loadAllFrames(this.content.urlBase, this.timestamps);
      this.webglAnimation.startAnimation();
      return true;
    } catch (error) {
      console.error('Failed to setup animation:', error);
      this.cleanup();
      return false;
    }
  }
  cleanup() {
    if (this.webglAnimation != null) {
      this.webglAnimation.cleanup();
      this.webglAnimation = null;
    }
    this.gl = undefined;
  }
}
class BaseRadarLayer extends WebglAnimationLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'base-radar';
    this.LAYER_PRIORITY = LayerPriority.Radar;
    this.SOURCE_PREFIX = 'base-radar';
    this.MIN_ZOOM = 0;
    this.MAX_ZOOM = 9;
    this.coverageType = WebglMapCoverageType.RADAR;
  }
}
let BaseRadarService = /*#__PURE__*/(() => {
  class BaseRadarService extends WebglAnimationLayerService {
    constructor() {
      super(...arguments);
      this.MAXIMUM_AGE_SECONDS = 2 * 60 * 60;
      this.DATA_VALIDITY_SECONDS = 15 * 60;
      this.ONLY_PAST = false;
    }
    createLayer(content) {
      return new BaseRadarLayer(this.mapService, content);
    }
    getContent() {
      return this.mapService.info.overlay.radar.baseContent();
    }
    getVisible() {
      return this.mapService.config.overlay.radar.visible() && this.mapService.config.overlay.radar.type() === RadarType.Base;
    }
    getOpacity() {
      return this.mapService.config.overlay.radar.opacity();
    }
    setAvailableLoadedRange(range) {
      this.mapService.animation.availableRange.radarBase.set(range);
      this.mapService.animation.loadedRange.radarBase.set(range);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵBaseRadarService_BaseFactory;
        return function BaseRadarService_Factory(__ngFactoryType__) {
          return (ɵBaseRadarService_BaseFactory || (ɵBaseRadarService_BaseFactory = i0.ɵɵgetInheritedFactory(BaseRadarService)))(__ngFactoryType__ || BaseRadarService);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BaseRadarService,
        factory: BaseRadarService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BaseRadarService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class CompositeRadarLayer extends WebglAnimationLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'composite-radar';
    this.LAYER_PRIORITY = LayerPriority.Radar;
    this.SOURCE_PREFIX = 'composite-radar';
    this.MIN_ZOOM = 0;
    this.MAX_ZOOM = 9;
    this.coverageType = WebglMapCoverageType.RADAR;
  }
}
let CompositeRadarService = /*#__PURE__*/(() => {
  class CompositeRadarService extends WebglAnimationLayerService {
    constructor() {
      super(...arguments);
      this.MAXIMUM_AGE_SECONDS = 2 * 60 * 60;
      this.DATA_VALIDITY_SECONDS = 15 * 60;
      this.ONLY_PAST = true;
    }
    createLayer(content) {
      return new CompositeRadarLayer(this.mapService, content);
    }
    getContent() {
      return this.mapService.info.overlay.radar.compositeContent();
    }
    getVisible() {
      return this.mapService.config.overlay.radar.visible() && this.mapService.config.overlay.radar.type() === RadarType.Composite;
    }
    getOpacity() {
      return this.mapService.config.overlay.radar.opacity();
    }
    setAvailableLoadedRange(range) {
      this.mapService.animation.availableRange.radarComposite.set(range);
      this.mapService.animation.loadedRange.radarComposite.set(range);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵCompositeRadarService_BaseFactory;
        return function CompositeRadarService_Factory(__ngFactoryType__) {
          return (ɵCompositeRadarService_BaseFactory || (ɵCompositeRadarService_BaseFactory = i0.ɵɵgetInheritedFactory(CompositeRadarService)))(__ngFactoryType__ || CompositeRadarService);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CompositeRadarService,
        factory: CompositeRadarService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CompositeRadarService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class Marker {
  constructor(map, position, properties, opacity) {
    this.map = map;
    this.position = position;
    this.properties = properties;
    this.opacity = opacity;
    this.visible = false;
    this.zoomLevel = this.map.getZoom() ?? 0;
  }
  destroy() {
    this.hide();
  }
  getVisible() {
    return this.visible;
  }
  hide() {
    if (this.visible) {
      this.visible = false;
      this.marker?.remove();
      this.marker = undefined;
    }
  }
  move(position) {
    if (this.position.lat !== position.lat || this.position.lng !== position.lng) {
      this.position = position;
      this.marker?.setLngLat(this.position);
      return true;
    }
    return false;
  }
  show() {
    if (!this.visible) {
      this.createOrUpdateMarker();
      if (this.marker != null) {
        this.map.addMarker(MarkerPriority.StormCells, this.marker);
      }
      this.visible = true;
    }
  }
  update(properties, position, zoomLevel, opacity) {
    const markerMoved = this.move(position);
    if (!isEqual(this.properties, properties) || zoomLevel != this.zoomLevel || opacity !== this.opacity) {
      this.properties = properties;
      this.zoomLevel = zoomLevel;
      this.opacity = opacity;
      this.onUpdate();
      if (this.visible) {
        this.hide();
        this.show();
      }
      return true;
    }
    return markerMoved;
  }
  createOrUpdateMarker() {
    if (this.marker == null) {
      this.marker = new ML__default.Marker({
        element: this.getElement(),
        offset: this.getOffset(),
        rotation: this.getRotation(),
        opacity: this.opacity.toString()
      });
    } else {
      this.marker.setOffset(this.getOffset());
      this.marker.setRotation(this.getRotation());
      this.marker.setOpacity(this.opacity.toString());
    }
    this.marker.setLngLat(this.position);
  }
}
class ScreenBoundingBox {
  toString(other) {
    let output;
    if (this.valid) {
      output = `${this.left} ${this.right} ${this.top} ${this.bottom}`;
      if (other != null) {
        output += `\n> other: ${other.toString()}\n`;
        output += `> L: ${this.intersectsLeft(other)} R: ${this.intersectsRight(other)}\n`;
        output += `> T: ${this.intersectsTop(other)} B: ${this.intersectsBottom(other)}\n`;
        output += `> H: ${this.containsHorizontal(other)} V: ${this.containsVertical(other)}`;
      }
    } else {
      output = 'null';
    }
    return output;
  }
  get valid() {
    return this._left != null && this._right != null && this._top != null && this._bottom != null;
  }
  get left() {
    if (this._left == null) {
      throw new Error('no value set to bounding box');
    }
    return this._left;
  }
  get right() {
    if (this._right == null) {
      throw new Error('no value set to bounding box');
    }
    return this._right;
  }
  get top() {
    if (this._top == null) {
      throw new Error('no value set to bounding box');
    }
    return this._top;
  }
  get bottom() {
    if (this._bottom == null) {
      throw new Error('no value set to bounding box');
    }
    return this._bottom;
  }
  get x() {
    return this.left;
  }
  get y() {
    return this.top;
  }
  get width() {
    return this.right - this.left;
  }
  get height() {
    return this.bottom - this.top;
  }
  constructor(x, y, width, height) {
    if (x != null) {
      this._left = x;
      this._right = x;
    }
    if (y != null) {
      this._top = y;
      this._bottom = y;
    }
    if (width != null && this._left != null) {
      this._right = this._left + width;
    }
    if (height != null && this._top != null) {
      this._bottom = this._top + height;
    }
  }
  addBoundingBox(value) {
    if (!value.valid) {
      return this;
    }
    return this.addPoints([new Point(value.left, value.top), new Point(value.right, value.bottom)]);
  }
  addBoundingBoxes(...values) {
    for (const value of values) {
      this.addBoundingBox(value);
    }
    return this;
  }
  addPoint(value, radius) {
    this.extendByPoint(value, radius);
    return this;
  }
  addPoints(value, radius) {
    for (const point of value) {
      this.extendByPoint(point, radius);
    }
    return this;
  }
  // eslint-disable-next-line complexity
  intersects(other) {
    if (!this.valid || !other.valid) {
      return false;
    }
    return this.intersectsLeft(other) && (this.intersectsTop(other) || this.intersectsBottom(other) || this.containsVertical(other)) || this.intersectsRight(other) && (this.intersectsTop(other) || this.intersectsBottom(other) || this.containsVertical(other)) || this.containsHorizontal(other) && (this.intersectsTop(other) || this.intersectsBottom(other) || this.containsVertical(other));
  }
  intersectsLeft(other) {
    return other.left <= this.left && other.right >= this.left;
  }
  intersectsRight(other) {
    return other.left <= this.right && other.right >= this.right;
  }
  intersectsTop(other) {
    return other.top <= this.top && other.bottom >= this.top;
  }
  intersectsBottom(other) {
    return other.top <= this.bottom && other.bottom >= this.bottom;
  }
  containsHorizontal(other) {
    return other.left >= this.left && other.right <= this.right;
  }
  containsVertical(other) {
    return other.top >= this.top && other.bottom <= this.bottom;
  }
  extendByPoint(value, radius) {
    let adjLeft = value.x;
    let adjRight = value.x;
    let adjTop = value.y;
    let adjBottom = value.y;
    if (radius != null) {
      adjLeft -= radius;
      adjRight += radius;
      adjTop -= radius;
      adjBottom += radius;
    }
    this._left = Math.min(this._left ?? Infinity, adjLeft);
    this._top = Math.min(this._top ?? Infinity, adjTop);
    this._right = Math.max(this._right ?? -Infinity, adjRight);
    this._bottom = Math.max(this._bottom ?? -Infinity, adjBottom);
  }
}
const SVG$1 = 'http://www.w3.org/2000/svg';
const HAIL_BACKGROUND_COLOR = '#0066F5';
const HAIL_HIGHLIGHT_COLOR = '#FFFFFF';
const HAIL_OUTLINE_COLOR = '#000000';
const HAIL_CLOUD_COLOR = '#F4F4F4';
const TORNADO_BACKGROUND_COLOR = '#DA1E28';
const TORNADO_HIGHLIGHT_COLOR = '#FFFFFF';
const TORNADO_OUTLINE_COLOR = '#000000';
const TORNADO_CLOUD_COLOR = '#F4F4F4';
class HailTornadoMarker extends Marker {
  getBoundingBox() {
    if (!(this.properties.isTornadic || this.properties.isHail)) {
      return new ScreenBoundingBox();
    }
    const offset = this.getOffset();
    const point = this.map.project(this.position);
    const bbox = new ScreenBoundingBox().addPoint(new Point(point.x + offset[0], point.y + offset[1]), 20);
    return bbox;
  }
  getElement() {
    return this.createMarkerSvg();
  }
  getOffset() {
    if (this.properties.direction >= 90 && this.properties.direction <= 270) {
      // Storm is going south so move icons north
      // Note: 0.5px in X-axis is to help center with the storm track. Some sort of subpixel madness.
      return [0.5, -25];
    }
    // Storm is going north so move icons south
    return [0.5, 25];
  }
  getRotation() {
    return 0;
  }
  onUpdate() {
    // nothing to do
  }
  createMarkerSvg() {
    const container = document.createElement('div');
    if (this.properties.isHail) {
      container.appendChild(this.createHailIcon());
    }
    if (this.properties.isTornadic) {
      container.appendChild(this.createTornado());
    }
    return container;
  }
  createHailIcon() {
    const svg = document.createElementNS(SVG$1, 'svg');
    svg.setAttribute('height', '20');
    svg.setAttribute('width', '20');
    svg.setAttribute('viewBox', '0 0 20 20');
    const circle = document.createElementNS(SVG$1, 'circle');
    circle.setAttribute('cx', '10');
    circle.setAttribute('cy', '10');
    circle.setAttribute('r', '9.5');
    circle.setAttribute('fill', HAIL_BACKGROUND_COLOR);
    circle.setAttribute('stroke', this.properties.highlighted ? HAIL_HIGHLIGHT_COLOR : HAIL_OUTLINE_COLOR);
    svg.appendChild(circle);
    const hailCircles = [[8, 11, 1], [9.5, 15.5, 1.5], [13, 12, 1]];
    for (const [x, y, r] of hailCircles) {
      const hailCircle = document.createElementNS(SVG$1, 'circle');
      hailCircle.setAttribute('cx', `${x}`);
      hailCircle.setAttribute('cy', `${y}`);
      hailCircle.setAttribute('r', `${r}`);
      hailCircle.setAttribute('fill', HAIL_CLOUD_COLOR);
      svg.appendChild(hailCircle);
    }
    const cloud = document.createElementNS(SVG$1, 'path');
    cloud.setAttribute('d',
    // eslint-disable-next-line max-len
    'M5.89474 9C4.8483 9 4 8.32843 4 7.5C4 6.67157 4.82952 5.89753 5.89474 6.32349C5.66014 4.93911 7.48113 3 9.68421 3C11.5723 3 13.2117 4.06959 13.5 5.5C14.5308 4.87522 16 5.89965 16 7.25C16 8.2165 15.0103 9 13.7895 9C13.7422 9 13.4737 9 13.4737 9H5.89474Z');
    cloud.setAttribute('fill', HAIL_CLOUD_COLOR);
    svg.appendChild(cloud);
    return svg;
  }
  createTornado() {
    const svg = document.createElementNS(SVG$1, 'svg');
    svg.setAttribute('height', '20');
    svg.setAttribute('width', '20');
    svg.setAttribute('viewBox', '0 0 20 20');
    const circle = document.createElementNS(SVG$1, 'circle');
    circle.setAttribute('cx', '10');
    circle.setAttribute('cy', '10');
    circle.setAttribute('r', '9.5');
    circle.setAttribute('fill', TORNADO_BACKGROUND_COLOR);
    circle.setAttribute('stroke', this.properties.highlighted ? TORNADO_HIGHLIGHT_COLOR : TORNADO_OUTLINE_COLOR);
    svg.appendChild(circle);
    const cloud = document.createElementNS(SVG$1, 'path');
    cloud.setAttribute('d',
    // eslint-disable-next-line max-len
    'M4.54027 8.96176C6.54715 6.57327 5.76073 5.15311 5.19059 5H16C15.3158 7.15885 11.952 9.22967 10.3556 9.99522C6.87082 11.5237 8.6436 13.1453 9.81459 13.7222C12.2837 14.9387 11.5529 16.4258 10.7547 17C10.8916 15.4383 8.89683 15.0084 7.9611 14.6459C3.21756 12.8086 3.70407 10.0909 4.54027 8.96176Z');
    cloud.setAttribute('fill', TORNADO_CLOUD_COLOR);
    svg.appendChild(cloud);
    return svg;
  }
}
let StormCellsConstants = /*#__PURE__*/(() => {
  class StormCellsConstants {
    // Hail probability is 0%-100%. This sets the threshold for "hail is very probable."
    static {
      this.HAIL_PROBABILITY_THRESHOLD = 70;
    }
    // How often the storm cell display updates
    static {
      this.UPDATE_MS = 200;
    }
    // The amount of time to consider a storm track "valid" for display.
    // Basically, process storm tracks between "now" and "now - window.""
    static {
      this.WINDOW_SIZE_S = 15 * 60;
    } // 15 minutes
    // Disable collision detection when you zoom in far enough to allow
    // storm cells to overlap.
    static {
      this.MAX_ZOOM_FOR_COLLISIONS = 8;
    }
    // The minimum change in echo tops to allow a non-visible storm cell
    // to override a visible storm cell.
    static {
      this.MIN_DELTA_ECHO_TOP_FT = 1000;
    }
    // THe minimum change in wind speed to allow a non-visible storm cell
    // to override a visible storm cell.
    static {
      this.MIN_DELTA_SPEED_KTS = 20;
    }
  }
  return StormCellsConstants;
})();
const PREFER_A = -1;
const PREFER_B = 1;
const NO_DIFF = 0;
const stormTrackIsPartiallyVisible = track => {
  return track?.hailTornadoMarker?.getVisible() === true || track?.stormTrackMarker?.getVisible() === true;
};
const stormTrackIsFullyVisible = track => {
  return track?.hailTornadoMarker?.getVisible() === true && track?.stormTrackMarker?.getVisible() === true;
};
const sortByVisibility = (trackA, trackB) => {
  const visibleA = stormTrackIsFullyVisible(trackA);
  const visibleB = stormTrackIsFullyVisible(trackB);
  if (visibleA && !visibleB) {
    return PREFER_A;
  } else if (!visibleA && visibleB) {
    return PREFER_B;
  }
  return NO_DIFF;
};
const sortByTornadic = (a, b) => {
  if (a.isTornadic && !b.isTornadic) {
    return PREFER_A;
  } else if (!a.isTornadic && b.isTornadic) {
    return PREFER_B;
  }
  return NO_DIFF;
};
const sortByHail = (a, b) => {
  const probHailA = a['HailIndex.probHail'];
  const probHailB = b['HailIndex.probHail'];
  const hailA = probHailA >= StormCellsConstants.HAIL_PROBABILITY_THRESHOLD;
  const hailB = probHailB >= StormCellsConstants.HAIL_PROBABILITY_THRESHOLD;
  if (hailA === hailB) {
    return probHailB - probHailA;
  } else if (hailA && !hailB) {
    return PREFER_A;
  } else if (!hailA && hailB) {
    return PREFER_B;
  }
  return NO_DIFF;
};
const sortByEchoTops = (a, b, minimumDelta) => {
  const echoTopA = a['EchoTop.echoTopFeetMSL'];
  const echoTopB = b['EchoTop.echoTopFeetMSL'];
  if (echoTopA != null && echoTopB != null) {
    let diff = echoTopB - echoTopA;
    if (minimumDelta != null && Math.abs(diff) < minimumDelta) {
      diff = 0;
    }
    return diff;
  } else if (echoTopA != null) {
    return PREFER_A;
  } else if (echoTopB != null) {
    return PREFER_B;
  }
  return NO_DIFF;
};
const sortBySpeedAndDirection = (a, b, minimumDelta) => {
  const dirA = a['CellTrackingInfo.dir'];
  const dirB = b['CellTrackingInfo.dir'];
  const speedA = a['CellTrackingInfo.speed'];
  const speedB = b['CellTrackingInfo.speed'];
  if (dirA != null && speedA != null && dirB != null && speedB != null) {
    let diff = speedB - speedA;
    if (minimumDelta != null && Math.abs(diff) < minimumDelta) {
      diff = 0;
    }
    return diff;
  } else if (dirA != null && speedA != null) {
    return PREFER_A;
  } else if (dirB != null && speedB != null) {
    return PREFER_B;
  }
  return NO_DIFF;
};
const sortByIssueTime = (a, b) => {
  const diff = b.issueTime - a.issueTime;
  return diff;
};
/**
 * Sorting functor for storm track priority in descending order.
 * 0 = high priority, N = low priority
 */
const sortStormTracks = ([_idA, trackA], [_idB, trackB]) => {
  const a = trackA.feature.properties;
  const b = trackB.feature.properties;
  const visibility = sortByVisibility(trackA, trackB);
  const difference =
  // prefer tornadic activity to no tornadic activity
  sortByTornadic(a, b) ||
  // prefer hail activity to no hail activity
  sortByHail(a, b) ||
  // prefer strong echo tops
  // if visibility is different, difference must be >= MIN_DELTA_ECHO_TOP
  sortByEchoTops(a, b, visibility !== 0 ? StormCellsConstants.MIN_DELTA_ECHO_TOP_FT : undefined) ||
  // prefer more complete wind direction and speed info
  // prefer stronger winds
  // if visibility is different, difference must be >= MIN_DELTA_SPEED
  sortBySpeedAndDirection(a, b, visibility !== 0 ? StormCellsConstants.MIN_DELTA_SPEED_KTS : undefined) || (
  // prefer more recent tracks
  // if visibility is different, ignored
  visibility === 0 ? sortByIssueTime(a, b) : 0) // keep the parens!
  ;
  if (difference === 0) {
    // no significant difference, sort by visibility
    return visibility;
  }
  return difference;
};
const isWithinStormCellWindow = displayTime => {
  return ['all', ['>=', ['get', 'issueTime'], displayTime - StormCellsConstants.WINDOW_SIZE_S], ['<=', ['get', 'issueTime'], displayTime]];
};
class StormCellsLinesLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'storm-cells-lines';
  }
  setOpacity(_opacity) {
    // NO-OP. Invisible layer. Opacity should always be 0.
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.StormCells, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'line',
        'filter': this.getFilter(),
        'paint': {
          // This layer is invisible and mostly exists just to load data
          // and have something for the radial menu to grab
          'line-opacity': 0
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  getFilter() {
    const displayTime = this.mapAnimation.time();
    return isWithinStormCellWindow(displayTime);
  }
}
class StormCellsPointsLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'storm-cells-points';
  }
  setOpacity(_opacity) {
    // NO-OP. Invisible layer. Opacity should always be 0.
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.StormCells, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'circle',
        'filter': this.getFilter(),
        'paint': {
          // This layer is invisible and mostly exists just to load data
          // and have something for the radial menu to grab.
          'circle-opacity': 0
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  getFilter() {
    const displayTime = this.mapAnimation.time();
    return isWithinStormCellWindow(displayTime);
  }
}
const STORM_CELL_LENGTH_SECONDS = 7200;
class StormCellsLayer extends AnimationLayerGroup {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'storm-cells';
    this.SOURCE_LAYER = 'stormcell';
    this.groupInfo = [{
      layer: new StormCellsPointsLayer(this.injector, this.radialMenuMapService, this.SOURCE_LAYER, LayerType.StormCells)
    }, {
      layer: new StormCellsLinesLayer(this.injector, this.radialMenuMapService, this.SOURCE_LAYER, LayerType.StormCells)
    }];
  }
  getLayerIds() {
    const results = [];
    for (const layerGroup of this.groupInfo) {
      if (layerGroup.layer.layerId != null) {
        results.push(layerGroup.layer.layerId);
      }
    }
    return results;
  }
  addAnimationSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      minzoom: 0,
      maxzoom: 9
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeAnimationSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
  updateAvailableRange() {
    const range = [];
    if (this.sourceId != null) {
      const time = this.content.metadata?.createdTime ?? 0;
      range.push([time - STORM_CELL_LENGTH_SECONDS, time]);
    }
    this.mapAnimation.availableRange.stormCells.set(range);
  }
  updateLoadedRange() {
    const range = [];
    if (this.loaded) {
      const time = this.content.metadata?.createdTime ?? 0;
      range.push([time - STORM_CELL_LENGTH_SECONDS, time]);
    }
    this.mapAnimation.loadedRange.stormCells.set(range);
  }
}
class SvgPosition {
  get height() {
    if (this.bottom == null || this.top == null) {
      return 0;
    }
    return this.bottom - this.top;
  }
  get width() {
    if (this.right == null || this.left == null) {
      return 0;
    }
    return this.right - this.left;
  }
  get viewPort() {
    if (this.left == null || this.right == null || this.top == null || this.bottom == null) {
      return '0 0 0 0';
    }
    return `${this.left} ${this.right} ${this.top} ${this.bottom}`;
  }
  reset() {
    this._x = undefined;
    this._y = undefined;
    this.left = undefined;
    this.right = undefined;
    this.top = undefined;
    this.bottom = undefined;
  }
  get x() {
    if (this._x == null) {
      return -1;
    }
    return this._x;
  }
  set x(value) {
    this._x = value;
    this.right = this.right == null ? this._x : Math.max(this._x, this.right);
    this.left = this.left == null ? this._x : Math.min(this._x, this.left);
  }
  get y() {
    if (this._y == null) {
      return -1;
    }
    return this._y;
  }
  set y(value) {
    this._y = value;
    this.bottom = this.bottom == null ? this._y : Math.max(this._y, this.bottom);
    this.top = this.top == null ? this._y : Math.min(this._y, this.top);
  }
}
const SVG = 'http://www.w3.org/2000/svg';
const OUTLINE_COLOR = '#000000';
const HIGHLIGHT_COLOR = '#FFFFFF';
const FILL_COLOR = '#FF7837';
const ACCENT_COLOR = '#F4F4F4';
const STROKE_WIDTH = 1;
const RADIUS = 6;
const BACKGROUND_RADIUS = RADIUS + STROKE_WIDTH;
const ACCENT_RADIUS = 2;
const STALK_WIDTH = 4;
const HALF_STALK_WIDTH = STALK_WIDTH / 2;
const ARROW_HEAD_WIDTH = 14;
const ARROW_HEAD_LENGTH = 14;
const ARROW_RADIUS = Math.max(ARROW_HEAD_WIDTH, ARROW_HEAD_LENGTH);
const STALK_OVERLAP = RADIUS / 4;
const PIP_WIDTH = 4;
const PIP_HALF_WIDTH = PIP_WIDTH / 2;
const PIP_HEIGHT = 4;
const MINIMUM_PIP_SPACING = 20;
const wrapRotation = value => {
  let result = value;
  while (result >= 360) {
    result -= 360;
  }
  while (result < 0) {
    result += 360;
  }
  return result;
};
class StormTrackMarker extends Marker {
  constructor() {
    super(...arguments);
    this.stalkLength = 0;
    this.trackLength = 0;
  }
  getBoundingBox() {
    const result = new ScreenBoundingBox();
    result.addPoint(this.map.project(this.position), RADIUS);
    if (this.endPosition == null) {
      this.endPosition = this.calculateEndPosition();
    }
    result.addPoint(this.map.project(this.endPosition), ARROW_RADIUS);
    return result;
  }
  setOpacity(opacity) {
    if (!isValidOpacity(opacity)) {
      throw new Error('Opacity is not valid.');
    }
    const elem = this.marker?.getElement();
    if (elem != null) {
      elem.style.opacity = opacity.toString();
    }
  }
  getElement() {
    return this.createMarkerSvg();
  }
  getOffset() {
    // If the icon is too small to have a stalk, it's just a circle. Center it.
    if (this.stalkLength <= 0) {
      return [0, 0];
    }
    // The icon is centered on the marker. We want the "start" circle of the track
    // to be centered on the marker instead. That's the white accent part.
    // Time to do some math!
    const rotationDeg = this.properties.direction;
    const rotation = rotationDeg * Math.PI / 180;
    const length = 0.5 * (this.trackLength - RADIUS);
    const offsetX = Math.sin(rotation) * length;
    const offsetY = Math.cos(rotation) * length;
    const offsetXAbs = Math.abs(offsetX);
    const offsetYAbs = Math.abs(offsetY);
    let result;
    if (rotationDeg <= 90) {
      result = [offsetXAbs, -offsetYAbs];
    } else if (rotationDeg <= 180) {
      result = [offsetXAbs, offsetYAbs];
    } else if (rotationDeg <= 270) {
      result = [-offsetXAbs, offsetYAbs];
    } else {
      result = [-offsetXAbs, -offsetYAbs];
    }
    // Note: Above logic may result in an offset containing "-0". This has no effect on the position.
    return result;
  }
  getRotation() {
    // The arrow is drawn to the east (90 degrees) so correct the rotation to north
    return wrapRotation(this.properties.direction - 90);
  }
  onUpdate() {
    this.endPosition = undefined;
  }
  createMarkerSvg() {
    const container = document.createElement('div');
    const svg = document.createElementNS(SVG, 'svg');
    const circleCenter = RADIUS + STROKE_WIDTH;
    this.calculateTrackLength();
    this.calculateStalkLength();
    const drawArrow = this.stalkLength > 0;
    const svgPosition = new SvgPosition();
    svg.appendChild(this.createCircle(circleCenter, circleCenter, BACKGROUND_RADIUS, this.properties.highlighted ? HIGHLIGHT_COLOR : OUTLINE_COLOR));
    if (drawArrow) {
      svg.appendChild(this.createPath(svgPosition, this.stalkLength));
    }
    svg.appendChild(this.createCircle(circleCenter, circleCenter, RADIUS, FILL_COLOR));
    svg.appendChild(this.createCircle(circleCenter, circleCenter, ACCENT_RADIUS, ACCENT_COLOR));
    // Modify position to account for circles
    svgPosition.x = 0;
    svgPosition.y = 0;
    svgPosition.x = 2 * RADIUS + 2 * STROKE_WIDTH;
    svgPosition.y = svgPosition.x;
    svg.setAttribute('height', `${svgPosition.height}`);
    svg.setAttribute('width', `${svgPosition.width}`);
    svg.setAttribute('viewPort', `${svgPosition.viewPort}`);
    container.appendChild(svg);
    return container;
  }
  calculateEndPosition() {
    const endPoint = turf.destination(this.position.toArray(), this.properties.speed, this.properties.direction,
    // TODO: check domain
    {
      units: 'nauticalmiles'
    });
    return ML__default.LngLat.convert([endPoint.geometry.coordinates[0], endPoint.geometry.coordinates[1]]);
  }
  calculateStalkLength() {
    this.stalkLength = this.trackLength - ARROW_HEAD_LENGTH - RADIUS;
  }
  calculateTrackLength() {
    if (this.properties.speed < 1) {
      this.trackLength = 0;
      return;
    }
    const startScreen = this.map.project(this.position);
    const endPt = turf.destination(this.position.toArray(), this.properties.speed, 90, {
      units: 'nauticalmiles'
    });
    const endPosition = ML__default.LngLat.convert([endPt.geometry.coordinates[0], endPt.geometry.coordinates[1]]);
    const endScreen = this.map.project(endPosition);
    const result = Math.abs(endScreen.x - startScreen.x);
    this.trackLength = result;
  }
  createCircle(x, y, r, fill) {
    const circle = document.createElementNS(SVG, 'circle');
    circle.setAttribute('cx', `${x}`);
    circle.setAttribute('cy', `${y}`);
    circle.setAttribute('r', `${r}`);
    circle.setAttribute('fill', `${fill}`);
    return circle;
  }
  createPath(svgPosition, stalkLength) {
    const d = [];
    // Set the initial location
    svgPosition.x = 2 * RADIUS + STROKE_WIDTH;
    svgPosition.y = RADIUS - HALF_STALK_WIDTH + STROKE_WIDTH;
    d.push(`M ${svgPosition.x} ${svgPosition.y}`);
    // Draw the top of the stalk
    const pipDeltaX = stalkLength / 4;
    const pipLocations = [svgPosition.x + pipDeltaX * 1, svgPosition.x + pipDeltaX * 2, svgPosition.x + pipDeltaX * 3];
    if (pipDeltaX >= MINIMUM_PIP_SPACING) {
      for (const location of pipLocations) {
        svgPosition.x = location - PIP_HALF_WIDTH;
        d.push(`H ${svgPosition.x}`);
        svgPosition.y -= PIP_HEIGHT;
        d.push(`V ${svgPosition.y}`);
        svgPosition.x += PIP_WIDTH;
        d.push(`H ${svgPosition.x}`);
        svgPosition.y += PIP_HEIGHT;
        d.push(`V ${svgPosition.y}`);
      }
      svgPosition.x += pipDeltaX - PIP_HALF_WIDTH;
      d.push(`H ${svgPosition.x}`);
    } else {
      svgPosition.x += stalkLength;
      d.push(`H ${svgPosition.x}`);
    }
    // Draw the top-left leg of the arrow
    svgPosition.y -= (ARROW_HEAD_WIDTH - STALK_WIDTH) / 2;
    d.push(`V ${svgPosition.y}`);
    // Draw the top-right leg of the arrow
    svgPosition.x += ARROW_HEAD_LENGTH;
    svgPosition.y += ARROW_HEAD_WIDTH / 2;
    d.push(`L ${svgPosition.x} ${svgPosition.y}`);
    // Draw the bottom-right leg of the arrow
    svgPosition.x -= ARROW_HEAD_LENGTH;
    svgPosition.y += ARROW_HEAD_WIDTH / 2;
    d.push(`L ${svgPosition.x} ${svgPosition.y}`);
    // Draw the bottom-left leg of the arrow
    svgPosition.y -= (ARROW_HEAD_WIDTH - STALK_WIDTH) / 2;
    d.push(`V ${svgPosition.y}`);
    // Draw the bottom of the stalk
    if (pipDeltaX >= MINIMUM_PIP_SPACING) {
      for (const location of pipLocations.reverse()) {
        svgPosition.x = location + PIP_HALF_WIDTH;
        d.push(`H ${svgPosition.x}`);
        svgPosition.y += PIP_HEIGHT;
        d.push(`V ${svgPosition.y}`);
        svgPosition.x -= PIP_WIDTH;
        d.push(`H ${svgPosition.x}`);
        svgPosition.y -= PIP_HEIGHT;
        d.push(`V ${svgPosition.y}`);
      }
      svgPosition.x -= pipDeltaX;
      d.push(`H ${svgPosition.x}`);
    } else {
      svgPosition.x -= stalkLength + STALK_OVERLAP;
      d.push(`H ${svgPosition.x}`);
    }
    // Draw the left of the stalk
    svgPosition.y -= STALK_WIDTH;
    d.push(`V ${svgPosition.y}`);
    // Close the path
    d.push('Z');
    const path = document.createElementNS(SVG, 'path');
    path.setAttribute('d', d.join(','));
    path.setAttribute('fill', FILL_COLOR);
    path.setAttribute('stroke', this.properties.highlighted ? HIGHLIGHT_COLOR : OUTLINE_COLOR);
    return path;
  }
}
let StormCellsService = /*#__PURE__*/(() => {
  class StormCellsService extends LayerService {
    constructor() {
      super();
      // private readonly animation = inject(AnimationService);
      this.config = this.mapConfig.overlay.radar;
      this.stormTracks = new Map();
      this.highlightStormTracks = [];
      this.updateMarkers = () => {
        if (this.timer == null) {
          return;
        }
        const map = this.map();
        if (map != null) {
          const highlights = this.mapHighlight.stormCell();
          const features = this.queryStormCells(map, highlights);
          const newTracks = this.createStormTracks(features);
          const oldTracks = this.stormTracks;
          this.createMarkers(map, oldTracks, newTracks);
          this.displayStormTracks(map, oldTracks, newTracks);
          this.stormTracks = newTracks;
          const newHighlightTracks = this.createHighlightStormTracks(features, highlights);
          const oldHighlightTracks = this.highlightStormTracks;
          this.createHighlightMarkers(map, newHighlightTracks);
          this.displayHighlightStormTracks(oldHighlightTracks, newHighlightTracks);
          this.highlightStormTracks = newHighlightTracks;
          if (this.timer.alive) {
            this.timer.handle = window.setTimeout(this.timer.fn, StormCellsConstants.UPDATE_MS);
          } else {
            this.timer = undefined;
          }
          this.updateMarkerOpacities();
        }
      };
      effect(() => this.updateAnimation());
      effect(() => this.updateInfo(), {
        allowSignalWrites: true
      });
      effect(() => this.updateLayers(), {
        allowSignalWrites: true
      });
      effect(() => this.updateTimers());
    }
    /**
     * Given a list of storm cell features, returns a list of the storm cell features
     * that are actually visible on the map.
     * @param features The storm cell features to filter
     */
    filterFeaturesByVisibility(features) {
      const results = [];
      for (const feature of features) {
        const trackId = feature.properties.id;
        const track = this.stormTracks.get(trackId);
        if (track != null) {
          const featureId = track.feature.id;
          if (feature.id === featureId && stormTrackIsPartiallyVisible(track)) {
            results.push(feature);
          }
        }
      }
      return results;
    }
    updateLayers() {
      super.updateLayers();
      this.updateMarkerOpacities();
    }
    createLayer(content) {
      return new StormCellsLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.overlay.stormCells.content();
    }
    getVisible() {
      return this.config.visible() && this.config.stormCells();
    }
    getOpacity() {
      return this.mapConfig.overlay.radar.opacity();
    }
    updateAnimation() {
      const displayTime = this.mapAnimation.time();
      let maximum = displayTime;
      let minimum = displayTime - StormCellsConstants.WINDOW_SIZE_S;
      const now = this.mapAnimation.options()?.now ?? 0;
      if (displayTime > now) {
        maximum = 0;
        minimum = 0;
      }
      this.selectedTime = {
        maximum,
        minimum
      };
    }
    updateInfo() {
      const content = this.getContent();
      const visible = this.getVisible();
      const available = [];
      const now = this.mapAnimation.options()?.now ?? 0;
      if (visible) {
        for (const c of content) {
          if (c.metadata != null) {
            const time = c.metadata.createdTime;
            const minTime = time - STORM_CELL_LENGTH_SECONDS;
            const maxTime = Math.min(now, time);
            available.push([minTime, maxTime]);
          }
        }
      }
      this.mapAnimation.availableRange.stormCells.set(available);
    }
    updateTimers() {
      if (this.getVisible()) {
        if (this.timer == null) {
          const fn = () => this.updateMarkers();
          const handle = window.setTimeout(fn, StormCellsConstants.UPDATE_MS);
          this.timer = {
            alive: true,
            fn,
            handle
          };
        }
      } else {
        if (this.timer != null) {
          this.timer.alive = false;
        }
      }
    }
    updateMarkerOpacities() {
      const opacity = untracked(() => this.getOpacity());
      for (const [_, track] of this.stormTracks) {
        track.stormTrackMarker?.setOpacity(opacity);
      }
    }
    createStormTracks(features) {
      // Find the most recent feature for each storm track
      // note: features should be sorted by issueTime descending
      const stormTracks = new Map();
      for (const feature of features) {
        const trackId = feature.properties.id;
        if (trackId != null && !stormTracks.has(trackId)) {
          stormTracks.set(trackId, {
            feature
          });
        }
      }
      return stormTracks;
    }
    createHighlightStormTracks(features, highlights) {
      const stormTracks = [];
      for (const feature of features) {
        const highlighted = highlights.some(x => x.id === feature.id);
        if (highlighted) {
          stormTracks.push({
            feature
          });
        }
      }
      return stormTracks;
    }
    createStormTrackMarker(properties, oldMarker) {
      if (oldMarker == null) {
        return new StormTrackMarker(properties.map, properties.position, properties.stormTrack, this.getOpacity());
      } else {
        oldMarker.update(properties.stormTrack, properties.position, properties.zoom, this.getOpacity());
        return oldMarker;
      }
    }
    createHailTornadoMarker(properties, oldMarker) {
      if (oldMarker == null) {
        return new HailTornadoMarker(properties.map, properties.position, properties.hailTornado, this.getOpacity());
      } else {
        oldMarker.update(properties.hailTornado, properties.position, properties.zoom, this.getOpacity());
        return oldMarker;
      }
    }
    createMarkers(map, oldTracks, newTracks) {
      for (const [id, track] of newTracks) {
        const properties = this.calculatePropertiesFromFeature(map, track.feature, false);
        const oldTrack = oldTracks.get(id);
        track.hailTornadoMarker = this.createHailTornadoMarker(properties, oldTrack?.hailTornadoMarker);
        track.stormTrackMarker = this.createStormTrackMarker(properties, oldTrack?.stormTrackMarker);
        track.boundingBox = new ScreenBoundingBox().addBoundingBoxes(track.stormTrackMarker.getBoundingBox(), track.hailTornadoMarker.getBoundingBox());
      }
    }
    createHighlightMarkers(map, highlights) {
      for (const track of highlights) {
        const properties = this.calculatePropertiesFromFeature(map, track.feature, true);
        track.hailTornadoMarker = this.createHailTornadoMarker(properties);
        track.stormTrackMarker = this.createStormTrackMarker(properties);
        track.boundingBox = new ScreenBoundingBox().addBoundingBoxes(track.stormTrackMarker.getBoundingBox(), track.hailTornadoMarker.getBoundingBox());
      }
    }
    displayStormTracks(map, oldTracks, newTracks) {
      const enableCollisions = map.getZoom() < StormCellsConstants.MAX_ZOOM_FOR_COLLISIONS;
      const newTracksByPriority = [...newTracks.entries()].sort(sortStormTracks);
      for (let i = 0; i < newTracksByPriority.length; i++) {
        const [_id, track] = newTracksByPriority[i];
        let conflicted = false;
        // Only run collision detection at low zoom levels.
        // Also skip if no bounding box exists cause no way to calculate collisions
        if (enableCollisions && track.boundingBox != null) {
          for (let j = 0; j < i; j++) {
            const [_otherId, otherTrack] = newTracksByPriority[j];
            // ignore not visible tracks and missing bounding boxes
            const otherVisible = stormTrackIsPartiallyVisible(otherTrack);
            if (!otherVisible || otherTrack.boundingBox == null) {
              continue;
            }
            if (otherTrack.boundingBox.intersects(track.boundingBox)) {
              conflicted = true;
              break;
            }
          }
        }
        if (conflicted) {
          track.hailTornadoMarker?.hide();
          track.stormTrackMarker?.hide();
        } else {
          track.hailTornadoMarker?.show();
          track.stormTrackMarker?.show();
        }
      }
      // Remove old tracks
      for (const [id, track] of oldTracks) {
        const found = newTracks.get(id) != null;
        if (!found) {
          track.hailTornadoMarker?.destroy();
          track.stormTrackMarker?.destroy();
        }
      }
    }
    displayHighlightStormTracks(oldHighlightTracks, newHighlightTracks) {
      // Remove the old highlights
      for (const track of oldHighlightTracks) {
        track.hailTornadoMarker?.destroy();
        track.stormTrackMarker?.destroy();
      }
      // Add the new highlights
      for (const track of newHighlightTracks) {
        track.hailTornadoMarker?.show();
        track.stormTrackMarker?.show();
      }
    }
    queryStormCells(map, highlights) {
      // Grab all the storm cell data
      const layerIds = [];
      for (const [_content, layer] of this.layers) {
        layerIds.push(...layer.getLayerIds());
      }
      const results = map.queryRenderedFeatures({
        layers: layerIds
      });
      // Filter the storm cell data
      const filteredResults = results.filter(feature => {
        // always include highlighted features
        const highlighted = highlights.some(x => x.id === feature.id);
        if (highlighted) {
          return true;
        }
        // don't include features outside the selected time (if one exists)
        const issueTime = feature.properties.issueTime;
        if (this.selectedTime != null && issueTime != null) {
          if (issueTime < this.selectedTime.minimum || issueTime > this.selectedTime.maximum) {
            return false;
          }
        }
        // include anything else
        return true;
      });
      // Sort the results by issue time descending
      filteredResults.sort((a, b) => {
        const issueTimeA = a.properties.issueTime;
        const issueTimeB = b.properties.issueTime;
        if (issueTimeA != null && issueTimeB != null) {
          return issueTimeB - issueTimeA;
        } else if (issueTimeA != null) {
          return 1;
        } else if (issueTimeB != null) {
          return -1;
        }
        return 0;
      });
      return filteredResults;
    }
    calculatePropertiesFromFeature(map, feature, highlight) {
      const props = feature.properties;
      const position = ML__default.LngLat.convert([props['Position.lon'], props['Position.lat']]);
      const isTornadic = props.isTornadic;
      const isHail = (props['HailIndex.probHail'] ?? 0) >= StormCellsConstants.HAIL_PROBABILITY_THRESHOLD;
      const speed = props['CellTrackingInfo.speed'] ?? 0;
      const direction = props['CellTrackingInfo.dir'] ?? 0;
      return {
        map,
        zoom: map.getZoom(),
        position,
        hailTornado: {
          direction,
          isHail,
          isTornadic,
          highlighted: highlight
        },
        stormTrack: {
          direction,
          speed,
          highlighted: highlight
        }
      };
    }
    static {
      this.ɵfac = function StormCellsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || StormCellsService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: StormCellsService,
        factory: StormCellsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return StormCellsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const color = now => {
  return ['case',
  // Currently active TFRs are red
  isActive(now), Color.Red.toString(),
  // Stadiums are blue unless they're within 24 hours of the start time
  ['all', isStadium(), ['!', startsWithin24Hours(now)]], Color.Blue.toString(),
  // Everything else is yellow by default
  Color.Yellow.toString()];
};
const lineWidth = () => {
  return [
  // perform linear interpolation based on the zoom level
  // to mitigate visual changes based on zoom level
  'interpolate', ['linear'], ['zoom'],
  // line width is 0.125 px at zoom level 4
  4, 0.125,
  // line width is 2 px at zoom level 8
  8, 2,
  // line width is 3 px at zoom level 12
  12, 3];
};
const lineWidthHighlight = () => {
  return [
  // perform linear interpolation based on the zoom level
  // to mitigate visual changes based on zoom level
  'interpolate', ['linear'], ['zoom'],
  // line width is 6 px at zoom level 4
  4, 6,
  // line width is 9 px at zoom level 8
  8, 9,
  // line width is 12 px at zoom level 12
  12, 12];
};
const text = () => {
  return ['let', 'restrictionType', ['case', ['all', ['has', 'restriction_type'], ['==', ['get', 'restriction_type'], 'Unknown']], 'Miscellaneous', ['get', 'restriction_type']],
  // Concatenate all objects into a string. There are two major formats here.
  // 1. For stadiums, "Fenway Park - NYC @ BST TFR: SFC - 3000 FT"
  // 2. For everything else, "Security TFR: 3000 FT - UNL"
  ['concat', ['case',
  // if the properties has both stadium_name and stadium_event_short_title
  ['all', ['has', 'stadium_name'], ['has', 'stadium_event_short_title']],
  // output 'stadium_name - stadium_event_short_title'
  ['concat', ['get', 'stadium_name'], ' - ', ['get', 'stadium_event_short_title']],
  // else if the properties has stadium_name
  ['has', 'stadium_name'],
  // output 'stadium_name'
  ['to-string', ['get', 'stadium_name']],
  // else output 'restriction_type' if it exists, 'restriction_subtype' if it exists
  // but restriction_type doesn't, or nothing if neither one exists
  ['to-string', ['coalesce', ['var', 'restrictionType'], ['get', 'restriction_subtype']]]], ' TFR', ['case',
  // if the properties has both minAltitude and maxAltitude
  ['all', ['has', 'minAltitude'], ['has', 'maxAltitude']], ['concat',
  // add a : after TFR as altitudes are being shown
  ': ', ['case',
  // if the minAltitude == 0
  ['==', ['get', 'minAltitude'], 0],
  // output 'SFC'
  'SFC',
  // else output 'minAltitude FT'
  ['concat', ['to-string', ['get', 'minAltitude']], 'FT']], ' - ', ['case',
  // if the maxAltitude == 80000
  ['==', ['get', 'maxAltitude'], 80000],
  // output 'UNL'
  'UNL',
  // else output 'maxAltitude FT'
  ['concat', ['to-string', ['get', 'maxAltitude']], 'FT']]], '']]];
};
const zOrder = (now, descending, highlighted) => {
  return ['case',
  // Highlighted TFRs always in front
  ['all', ['has', 'tfr_ident'], ['==', ['get', 'tfr_ident'], highlighted ?? '']], descending ? 100 : 0,
  // Currently active TFRs are red
  isActive(now), descending ? 4 : 1,
  // Stadiums are blue unless they're within 24 hours of the start time
  ['all', isStadium(), ['!', startsWithin24Hours(now)]], descending ? 2 : 3,
  // Everything else is yellow by default
  descending ? 3 : 2];
};
const isHighlighted = highlights => {
  if (highlights == null || highlights.length == 0) {
    return ['boolean', false];
  }
  const expr = ['any', ...highlights.map(tfr => ['==', ['get', 'tfr_ident'], tfr.ident])];
  return expr;
};
const filter = (now, highlights) => {
  return ['all', ['!', isHighlighted(highlights)], ['!', isExpired(now)]];
};
const isActive = now => {
  return ['all', ['has', 'start_time'], ['<=', ['get', 'start_time'], now], ['any', ['!', ['has', 'end_time']], ['<', now, ['get', 'end_time']]]];
};
const isExpired = now => {
  return ['all', ['has', 'expire_time'], ['<=', ['get', 'expire_time'], now]];
};
const isStadium = () => {
  return ['any', ['has', 'stadium_display_location'], ['has', 'stadium_event_estimated_start_time'], ['has', 'stadium_event_short_title'], ['has', 'stadium_event_title'], ['has', 'stadium_event_type'], ['has', 'stadium_name'], ['has', 'stadium_timezone_name']];
};
const startsWithin24Hours = now => {
  return ['all', ['has', 'start_time'], ['>=', ['get', 'start_time'], now], ['<=', ['get', 'start_time'], now + 24 * 60 * 60] // 24 hours in seconds
  ];
};
const listFilter = (now, highlights, tfrService) => {
  return ['all', ['!', isHighlighted(highlights)], ['!', isExpired(now)], ['in', ['get', 'ident'], ['literal', Array.from(tfrService.tfrList.keys())]]];
};
class TfrHighlightOutlineColorLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'tfr-highlight-outline-white';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const now = getUnixTime(new Date());
      map.addLayer(LayerPriority.HighlightTfr, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': TFR_SOURCE_LAYER,
        'type': 'line',
        'filter': isHighlighted(this.mapHighlight.tfr()),
        'paint': {
          'line-color': color(now),
          'line-width': lineWidth()
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const now = getUnixTime(new Date());
      map.setLayerFilter(this.layerId, isHighlighted(this.mapHighlight.tfr()));
      map.setLayerColor(this.layerId, color(now));
    }
  }
}
class TfrHighlightOutlineWhiteLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'tfr-highlight-outline-white';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(LayerPriority.HighlightTfr, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': TFR_SOURCE_LAYER,
        'type': 'line',
        'filter': isHighlighted(this.mapHighlight.tfr()),
        'paint': {
          'line-color': Color.White.toString(),
          'line-width': lineWidthHighlight()
        },
        'layout': {
          'line-join': 'round'
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, isHighlighted(this.mapHighlight.tfr()));
    }
  }
}
const OPACITY_SCALE_FACTOR$1 = 0.30;
class TfrHighlightPolygonLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'tfr-highlight-polygon';
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('TfrHighlightPolygonLayer only supports numeric opacity.');
    }
    super.setOpacity(opacity * OPACITY_SCALE_FACTOR$1);
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const now = getUnixTime(new Date());
      map.addLayer(LayerPriority.HighlightTfr, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': TFR_SOURCE_LAYER,
        'type': 'fill',
        'filter': isHighlighted(this.mapHighlight.tfr()),
        'paint': {
          'fill-color': color(now)
        },
        'layout': {
          'fill-sort-key': zOrder(now, true)
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const now = getUnixTime(new Date());
      map.setLayerFilter(this.layerId, isHighlighted(this.mapHighlight.tfr()));
      map.setLayerColor(this.layerId, color(now));
      map.setLayerSortKey(this.layerId, zOrder(now, true));
    }
  }
}
class TfrHighlightTextLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'tfr-highlight-text';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const now = getUnixTime(new Date());
      map.addLayer(LayerPriority.HighlightTfr, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': TFR_SOURCE_LAYER,
        'type': 'symbol',
        'filter': isHighlighted(this.mapHighlight.tfr()),
        'minzoom': 8,
        'paint': {
          'text-color': Color.White.toString(),
          'text-halo-color': color(now),
          'text-halo-width': 0.5,
          'text-translate': [0, -2]
        },
        'layout': {
          'symbol-placement': 'line',
          'symbol-sort-key': zOrder(now, false),
          'text-anchor': 'bottom',
          'text-field': text(),
          'text-font': ['Roboto'],
          'text-size': 14
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const now = getUnixTime(new Date());
      map.setLayerFilter(this.layerId, isHighlighted(this.mapHighlight.tfr()));
      map.setLayerSymbolProperties(this.layerId, {
        textHaloColor: color(now)
      });
      map.setLayerSortKey(this.layerId, zOrder(now, false));
    }
  }
}
class TfrOutlineLayer extends SubLayer {
  constructor(injector, radialMenuMapService, tfrService, sourceLayerId, layerType) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.injector = injector;
    this.radialMenuMapService = radialMenuMapService;
    this.tfrService = tfrService;
    this.sourceLayerId = sourceLayerId;
    this.layerType = layerType;
    this.LAYER_PREFIX = 'tfr-outline';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const now = getUnixTime(new Date());
      map.addLayer(LayerPriority.Tfr, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': TFR_SOURCE_LAYER,
        'type': 'line',
        'filter': listFilter(now, this.mapHighlight.tfr(), this.tfrService),
        'paint': {
          'line-color': color(now),
          'line-width': lineWidth()
        },
        'layout': {
          'line-join': 'round',
          'line-sort-key': zOrder(now, true)
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const now = getUnixTime(new Date());
      map.setLayerFilter(this.layerId, listFilter(now, this.mapHighlight.tfr(), this.tfrService));
      map.setLayerColor(this.layerId, color(now));
      map.setLayerSortKey(this.layerId, zOrder(now, true));
    }
  }
}
class TfrPolygonInvisibleLayer extends SubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'tfr-polygon-invisible';
  }
  setOpacity(_opacity) {
    // NO-OP. This is an invisible layer. Opacity should always be 0.
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const now = getUnixTime(new Date());
      map.addLayer(LayerPriority.Tfr, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': TFR_SOURCE_LAYER,
        'type': 'fill',
        'filter': filter(now, this.mapHighlight.tfr()),
        'paint': {
          'fill-color': color(now),
          'fill-opacity': 0
        },
        'layout': {
          'fill-sort-key': zOrder(now, true)
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const now = getUnixTime(new Date());
      map.setLayerFilter(this.layerId, filter(now, this.mapHighlight.tfr()));
      map.setLayerColor(this.layerId, color(now));
      map.setLayerSortKey(this.layerId, zOrder(now, true));
    }
  }
}
const OPACITY_SCALE_FACTOR = 0.30;
class TfrPolygonLayer extends SubLayer {
  constructor(injector, radialMenuMapService, tfrService, sourceLayerId, layerType) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.injector = injector;
    this.radialMenuMapService = radialMenuMapService;
    this.tfrService = tfrService;
    this.sourceLayerId = sourceLayerId;
    this.layerType = layerType;
    this.LAYER_PREFIX = 'tfr-polygon';
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('TfrPolygonLayer only supports numeric opacity.');
    }
    super.setOpacity(opacity * OPACITY_SCALE_FACTOR);
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const now = getUnixTime(new Date());
      map.addLayer(LayerPriority.Tfr, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': TFR_SOURCE_LAYER,
        'type': 'fill',
        'filter': listFilter(now, this.mapHighlight.tfr(), this.tfrService),
        'paint': {
          'fill-color': color(now)
        },
        'layout': {
          'fill-sort-key': zOrder(now, true)
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const now = getUnixTime(new Date());
      map.setLayerFilter(this.layerId, listFilter(now, this.mapHighlight.tfr(), this.tfrService));
      map.setLayerColor(this.layerId, color(now));
      map.setLayerSortKey(this.layerId, zOrder(now, true));
    }
  }
}
class TfrTextLayer extends SubLayer {
  constructor(injector, radialMenuMapService, tfrService, sourceLayerId, layerType) {
    super(injector, radialMenuMapService, sourceLayerId, layerType);
    this.injector = injector;
    this.radialMenuMapService = radialMenuMapService;
    this.tfrService = tfrService;
    this.sourceLayerId = sourceLayerId;
    this.layerType = layerType;
    this.LAYER_PREFIX = 'tfr-text';
  }
  _show(map) {
    if (this.sourceId != null && this.layerId != null) {
      const now = getUnixTime(new Date());
      map.addLayer(LayerPriority.Tfr, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': TFR_SOURCE_LAYER,
        'type': 'symbol',
        'filter': listFilter(now, this.mapHighlight.tfr(), this.tfrService),
        'minzoom': 8,
        'paint': {
          'text-color': Color.White.toString(),
          'text-halo-color': color(now),
          'text-halo-width': 0.5
        },
        'layout': {
          'symbol-placement': 'line',
          'symbol-sort-key': zOrder(now, false),
          'text-anchor': 'bottom',
          'text-field': text(),
          'text-font': ['Roboto'],
          'text-size': 14
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const now = getUnixTime(new Date());
      map.setLayerFilter(this.layerId, listFilter(now, this.mapHighlight.tfr(), this.tfrService));
      map.setLayerSymbolProperties(this.layerId, {
        textHaloColor: color(now)
      });
      map.setLayerSortKey(this.layerId, zOrder(now, false));
    }
  }
}
const TFR_SOURCE_LAYER = 'AviationTfr';
const SOURCE_PREFIX$1 = 'tfr';
class TfrLayer extends LayerGroup {
  constructor(injector, radialMenuMapService, content = EmptyContent, tfrService) {
    super(injector, radialMenuMapService, content);
    this.injector = injector;
    this.radialMenuMapService = radialMenuMapService;
    this.content = content;
    this.tfrService = tfrService;
    this.groupInfo = [{
      layer: new TfrHighlightPolygonLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Tfrs),
      shouldBeVisible: () => this.mapHighlight.tfr().length > 0
    }, {
      layer: new TfrHighlightOutlineWhiteLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Tfrs),
      shouldBeVisible: () => this.mapHighlight.tfr().length > 0
    }, {
      layer: new TfrHighlightOutlineColorLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Tfrs),
      shouldBeVisible: () => this.mapHighlight.tfr().length > 0
    }, {
      layer: new TfrHighlightTextLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Tfrs),
      shouldBeVisible: () => this.mapHighlight.tfr().length > 0
    }, {
      layer: new TfrPolygonLayer(this.injector, this.radialMenuMapService, this.tfrService, undefined, LayerType.Tfrs)
    }, {
      layer: new TfrOutlineLayer(this.injector, this.radialMenuMapService, this.tfrService, undefined, LayerType.Tfrs)
    }, {
      layer: new TfrTextLayer(this.injector, this.radialMenuMapService, this.tfrService, undefined, LayerType.Tfrs)
    }, {
      layer: new TfrPolygonInvisibleLayer(this.injector, this.radialMenuMapService, undefined, LayerType.Tfrs)
    }];
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(SOURCE_PREFIX$1);
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      minzoom: 0,
      maxzoom: 11
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
}
const INTERVAL_TIME_MS = 5000;
const DISTANCE_THRESHOLD_MILES = 1;
const AREA_THRESHOLD_SQUARE_METERS = 100;
const TWENTYFOUR_HOURS_SECONDS = 86400;
let TfrService = /*#__PURE__*/(() => {
  class TfrService extends LayerService {
    constructor() {
      super();
      this.tfrList = new Map();
      this.shouldQuery = false;
      this.initialDataSetDone = false;
      this.onSourceData = ev => {
        if (ev.sourceId.startsWith(SOURCE_PREFIX$1) && !this.shouldQuery) {
          this.sourceId = ev.sourceId;
          this.shouldQuery = true;
        }
      };
      this.intervals = () => {
        const visibility = this.mapConfig.overlay.tfr.visible();
        if (visibility) {
          if (!this.initialDataSetDone) {
            this.intervalId = window.setInterval(this.querySource, 100);
          } else {
            this.intervalId = window.setInterval(this.querySource, INTERVAL_TIME_MS);
          }
        } else if (!visibility && this.intervalId != null) {
          window.clearInterval(this.intervalId);
        }
      };
      this.querySource = () => {
        if (this.shouldQuery && this.sourceId != undefined) {
          const features = this.map()?.querySourceFeatures(this.sourceId, {
            sourceLayer: TFR_SOURCE_LAYER
          }) ?? [];
          this.processSourceData(features);
          if (!this.initialDataSetDone && features.length > 0) {
            this.initialDataSetDone = true;
            window.clearInterval(this.intervalId);
            this.intervalId = window.setInterval(this.querySource, INTERVAL_TIME_MS);
          }
          this.shouldQuery = false;
        }
      };
      this.processSourceData = features => {
        let changed = false;
        const now = getUnixTime(new Date());
        for (let i = 0; i < features.length; ++i) {
          const feature = features[i];
          if (Array.from(this.tfrList.keys()).includes(feature.properties.ident)) {
            continue;
          }
          const bboxPolygon = turf.bboxPolygon([feature.properties['featureMinLon'], feature.properties['featureMinLat'], feature.properties['featureMaxLon'], feature.properties['featureMaxLat']]);
          const center = turf.centerOfMass(bboxPolygon);
          let found1Key = '';
          let doUpdate = true;
          this.tfrList.forEach((value, key) => {
            if (found1Key === '') {
              if (turf.distance(turf.centerOfMass(value.bbox), center.geometry, {
                units: 'miles'
              }) < DISTANCE_THRESHOLD_MILES) {
                const foundArea = Math.abs(Math.abs(turf.area(value.bbox)) - Math.abs(turf.area(bboxPolygon)));
                if (foundArea < AREA_THRESHOLD_SQUARE_METERS) {
                  found1Key = key;
                  doUpdate = false;
                }
              }
            }
          });
          // New Center
          if (doUpdate) {
            changed = true;
            if (feature.properties.start_time <= now) {
              this.tfrList.set(feature.properties.ident, {
                active: 0 /* ActiveState.Active */,
                bbox: bboxPolygon
              });
            } else if (feature.properties.start_time > now && feature.properties.start_time - now < TWENTYFOUR_HOURS_SECONDS) {
              this.tfrList.set(feature.properties.ident, {
                active: 1 /* ActiveState.Upcoming */,
                bbox: bboxPolygon
              });
            } else {
              this.tfrList.set(feature.properties.ident, {
                active: 2 /* ActiveState.Inactive */,
                bbox: bboxPolygon
              });
            }
          }
          // Not a new Center - determine if we should override the previous one
          else {
            const tempChanged = this.determinePrecedence(found1Key, feature, bboxPolygon, now);
            if (!changed && tempChanged) {
              changed = true;
            }
          }
        }
        if (changed) {
          this.updateLayers();
        }
      };
      this.determinePrecedence = (oldFeatureId, newFeature, bbox, now) => {
        if (this.tfrList.get(oldFeatureId)?.active === 0 /* ActiveState.Active */) {
          return false;
        }
        if (newFeature.properties.start_time <= now) {
          this.tfrList.delete(oldFeatureId);
          this.tfrList.set(newFeature.properties.ident, {
            active: 0 /* ActiveState.Active */,
            bbox: bbox
          });
          return true;
        } else if (newFeature.properties.start_time > now && newFeature.properties.start_time - now < TWENTYFOUR_HOURS_SECONDS) {
          if (this.tfrList.get(oldFeatureId)?.active === 2 /* ActiveState.Inactive */) {
            this.tfrList.delete(oldFeatureId);
            this.tfrList.set(newFeature.properties.ident, {
              active: 1 /* ActiveState.Upcoming */,
              bbox: bbox
            });
            return true;
          }
        }
        return false;
      };
      effect(() => this.updateLayers());
      effect(() => this.subscribeToSourceData());
      effect(() => this.intervals());
    }
    createLayer(content) {
      return new TfrLayer(this.injector, this.radialMenuMapService, content, this);
    }
    getContent() {
      return this.mapInfo.overlay.tfr.content();
    }
    getVisible() {
      return this.mapConfig.overlay.tfr.visible();
    }
    getOpacity() {
      return this.mapConfig.overlay.tfr.opacity();
    }
    subscribeToSourceData() {
      const map = this.map();
      if (map != null) {
        map.on('sourcedata', this.onSourceData);
      }
    }
    static {
      this.ɵfac = function TfrService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TfrService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TfrService,
        factory: TfrService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TfrService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const SOURCE_PREFIX = 'tiled-temperature';
const LAYER_PREFIX$1 = 'tiled-temperature';
const LAYER_PRIORITY$1 = LayerPriority.TiledTemperature;
class TiledTemperatureLayer extends SnapshotLayer {
  add(map) {
    this.sourceId = map.sourceIds.next(SOURCE_PREFIX);
    const tileUrl = `${this.info.baseUrl}/${this.info.modelRunTime}/${this.info.elevation}/${this.info.offset}/{z}/{x}/{y}.png`;
    map.addSource(this.sourceId, {
      type: 'raster',
      tiles: [tileUrl],
      scheme: 'tms',
      bounds: [this.info.bounds.lonMin, this.info.bounds.latMin, this.info.bounds.lonMax, this.info.bounds.latMax],
      // The tiled temperature tiles are provided as a 256x256 image
      // (at the time of writing). We're purposefully using 512 instead.
      // This results in MapLibre using a more zoomed out tile but stretching
      // it to cover the same geographic area. This results in a slightly
      // blurrier image but with drastically fewer network requests.
      tileSize: 512
    });
    this.layerId = map.layerIds.next(LAYER_PREFIX$1);
    map.addLayer(LAYER_PRIORITY$1, {
      id: this.layerId,
      source: this.sourceId,
      type: 'raster'
    });
  }
  remove(map) {
    if (this.layerId != null) {
      map.removeLayer(this.layerId);
      this.layerId = undefined;
    }
    if (this.sourceId != null) {
      map.removeLayer(this.sourceId);
      this.sourceId = undefined;
    }
  }
  update(_map) {
    // nothing to do
  }
}
let TiledTemperatureService = /*#__PURE__*/(() => {
  class TiledTemperatureService extends SnapshotLayerService {
    constructor() {
      super(...arguments);
      this.MAXIMUM_AGE_SECONDS = undefined; // only future data
      this.MAXIMUM_ALTITUDE = 42000;
      this.MINIMUM_ALTITUDE = 3000;
      this.DATA_VALIDITY_SECONDS = 6 * 60 * 60;
      this.ONLY_PAST = false;
      this.config = this.mapConfig.overlay.tiledTemperature;
      this.info = this.mapInfo.overlay.tiledTemperature;
    }
    createLayer(info) {
      return new TiledTemperatureLayer(this.injector, info);
    }
    getContent() {
      return this.info.content();
    }
    getVisible() {
      return this.config.visible();
    }
    getOpacity() {
      return this.config.opacity();
    }
    setAvailableRange(range) {
      this.mapAnimation.availableRange.temps.set(range);
    }
    setLoadedRange(range) {
      this.mapAnimation.loadedRange.temps.set(range);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵTiledTemperatureService_BaseFactory;
        return function TiledTemperatureService_Factory(__ngFactoryType__) {
          return (ɵTiledTemperatureService_BaseFactory || (ɵTiledTemperatureService_BaseFactory = i0.ɵɵgetInheritedFactory(TiledTemperatureService)))(__ngFactoryType__ || TiledTemperatureService);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TiledTemperatureService,
        factory: TiledTemperatureService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TiledTemperatureService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var WeatherReportType = /*#__PURE__*/function (WeatherReportType) {
  WeatherReportType["Metar"] = "METAR";
  return WeatherReportType;
}(WeatherReportType || {});
class WeatherReportSubLayer extends SubLayer {
  constructor(injector, radialMenuMapService, type, layerType) {
    super(injector, radialMenuMapService, WeatherReportSubLayer.getSourceLayerId(type), layerType);
    this.type = type;
    this.priority = this.getPriority();
  }
  getFilter() {
    switch (this.type) {
      case WeatherReportType.Metar:
        {
          const now = getUnixTime(new Date());
          return ['<=', now, ['to-number', ['get', 'expireTime']]];
        }
      default:
        return ['boolean', true];
    }
  }
  _update(map) {
    if (this.layerId != null) {
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  getPriority() {
    switch (this.type) {
      case WeatherReportType.Metar:
      default:
        return LayerPriority.WeatherReport;
    }
  }
  static getSourceLayerId(type) {
    switch (type) {
      case WeatherReportType.Metar:
        return 'metar';
      default:
        throw new Error(`this layer is not supported by ${type}`);
    }
  }
}

// Fraction constants
const FractionDenominator = 16;
const FractionStep = 1 / FractionDenominator;
const FractionEpsilon = FractionStep / 2; // Small number used to avoid floating point errors
/**
 * MapLibre expression for converting a decimal to a fraction, e.g., 0.125 -> "1/8"
 */
const convertDecimalToFraction = value => {
  const output = ['case'];
  for (let index = 0, step = FractionEpsilon; index < FractionDenominator; index++, step += FractionStep) {
    // Add the comparison for everything but the one
    if (index < FractionDenominator - 1) {
      output.push(['<=', value, step]);
    }
    // Add the result
    if (index === 0) {
      // 0/X is just 0 so don't output anything
      output.push('');
    } else {
      const fraction = simplifyFractionFactor2([index, FractionDenominator]);
      output.push(`${fraction[0]}/${fraction[1]}`);
    }
  }
  return output;
};
/**
 * MapLibre expression for converting a number to a fraction, e.g., 3.125 -> "3 1/8"
 * NOTE: This expression uses expression variables so take care if combining with other expressions!
 */
const convertNumberToFraction = value => {
  return [
  // fractionRoundedNumber = value rounded to the nearest 1/X of a statute mile
  'let', 'fractionRoundedNumber', ['/', ['round', ['*', value, FractionDenominator]], FractionDenominator], [
  // fractionWholeNumber = the whole part of fractionRoundedNumber
  'let', 'fractionWholeNumber', ['floor', ['+', value, FractionEpsilon]], [
  // fractionRemainder = the decimal part of fractionWholeNumber
  'let', 'fractionRemainder', ['-', ['var', 'fractionRoundedNumber'], ['var', 'fractionWholeNumber']], ['case',
  // If the whole number is non-zero and there's a significant
  // remainder, print both separated by a space
  ['all', ['!=', ['var', 'fractionWholeNumber'], 0], ['>=', ['var', 'fractionRemainder'], FractionEpsilon]], ['concat', ['var', 'fractionWholeNumber'], ' ', convertDecimalToFraction(['var', 'fractionRemainder'])],
  // If the whole number is non-zero and there's no significant
  // remainder, print only the whole number
  ['all', ['!=', ['var', 'fractionWholeNumber'], 0], ['<', ['var', 'fractionRemainder'], FractionEpsilon]], ['to-string', ['var', 'fractionWholeNumber']],
  // If the whole number is zero and there's a significant
  // remainder, print only the remainder
  ['all', ['==', ['var', 'fractionWholeNumber'], 0], ['>=', ['var', 'fractionRemainder'], FractionEpsilon]], ['to-string', convertDecimalToFraction(['var', 'fractionRemainder'])],
  // Otherwise, just print a zero
  '0']]]];
};
const convertSecondsToMinutes = value => {
  return ['number-format', ['round', ['/', value, 60]], {}];
};
const convertTempCToF = value => {
  const TempCToFMult = 9 / 5;
  const TempCToFOffset = 32;
  return ['+', ['*', value, TempCToFMult], TempCToFOffset];
};
const convertVisibilityNumberToFraction = value => {
  // GP: only calculate fraction for value less than 3 for Visibility
  return ['let', 'integerValue', ['round', value], ['case', ['>=', ['var', 'integerValue'], 3], ['to-string', ['var', 'integerValue']], convertNumberToFraction(value)]];
};
const displayAltitude = prop => {
  return ['let', 'height', ['/', ['get', prop], 100], ['concat', ['case', ['<', ['var', 'height'], 10], '00', ['<', ['var', 'height'], 100], '0', ''], ['to-string', ['var', 'height']]]];
};
const displayTemp = value => {
  return ['format', ['number-format', ['round', value], {}], '°'];
};
const getTemporaryConditionText = (prop, op) => {
  return ['concat', '\n', ['get', 'tempoModifier'], ': ', op(prop)];
};
const hasTemporaryCondition = prop => {
  return ['all', ['has', 'tempoModifier'], ['has', prop]];
};
const limit = (min, max, value) => {
  return ['case', ['<=', value, min], min, ['>=', value, max], max, value];
};
const roundToNearest = (property, value) => {
  return ['*', ['round', ['/', ['get', property], value]], value];
};
/**
 * Simplifies fractions by factors of 2
 *  e.g., "4/16" becomes "1/4" but "3/6" stays "3/6"
 * @param value List with the numerator and denominator
 * @returns List with the simplified numerator and denominator
 */
const simplifyFractionFactor2 = value => {
  if (value[0] % 2 === 0 && value[1] % 2 === 0) {
    return simplifyFractionFactor2([value[0] / 2, value[1] / 2]);
  }
  return value;
};
const matchCloudCover = (property, other, skc, clr, few, sct, bkn, ovc, obs) => {
  return ['match', ['get', property], 'SKC', skc, 'CLR', clr, 'FEW', few, 'SCT', sct, 'BKN', bkn, 'OVC', ovc, 'OBS', obs, other];
};
const matchRating = (property, other, vfr, mvfr, ifr, lifr) => {
  return ['match', ['get', property], 'VFR', vfr, 'MVFR', mvfr, 'IFR', ifr, 'LIFR', lifr, other];
};
class FlightConditionsLayer extends WeatherReportSubLayer {
  constructor() {
    super(...arguments);
    this.LAYER_PREFIX = 'weather-report-flight-conditions-and-cloud-cover';
    this.property = this.getProperty();
  }
  _show(map) {
    const theme = this.mapTheme;
    if (this.sourceId != null && this.layerId != null) {
      map.addLayer(this.priority, {
        'id': this.layerId,
        'source': this.sourceId,
        'source-layer': this.sourceLayerId,
        'type': 'symbol',
        'filter': this.getFilter(),
        'paint': {
          'text-color': this.getTextColor(),
          'text-halo-color': theme.textHaloColor(),
          'text-halo-width': theme.textHaloWidth()
        },
        'layout': {
          'text-font': ['Roboto'],
          'text-field': ['step', ['zoom'], '', ZoomLevel['20NM'], ['get', 'station']],
          'text-offset': [0, -1.5],
          'icon-image': this.getIconImage(),
          'icon-size': .8,
          'symbol-sort-key': this.getSymbolSortKey()
        }
      });
    }
  }
  _update(map) {
    if (this.layerId != null) {
      const theme = this.mapTheme;
      map.setLayerSymbolProperties(this.layerId, {
        textColor: this.getTextColor(),
        textHaloColor: theme.textHaloColor(),
        textHaloWidth: theme.textHaloWidth()
      });
      map.setLayerFilter(this.layerId, this.getFilter());
    }
  }
  getProperty() {
    switch (this.type) {
      case WeatherReportType.Metar:
        return 'clouds';
      default:
        throw new Error(`this layer is not supported by ${this.type}`);
    }
  }
  /**
   * Image: Icons based on cloud coverage, color based on VFR/IFR rating
   */
  getIconImage() {
    return matchRating('airfieldRating', MapIcon.WeatherReport_CircleGrayMissing,
    // N/A-N/A
    matchCloudCover(this.property, MapIcon.WeatherReport_CircleGreenMissing,
    // VFR-Missing
    MapIcon.WeatherReport_CircleGreenClear,
    // VFR-SKC
    MapIcon.WeatherReport_CircleGreenClear,
    // VFR-CLR
    MapIcon.WeatherReport_CircleGreenFew,
    // VFR-FEW
    MapIcon.WeatherReport_CircleGreenScattered,
    // VFR-SCT
    MapIcon.WeatherReport_CircleGreenBroken,
    // VFR-BKN
    MapIcon.WeatherReport_CircleGreenOvercast,
    // VFR-OVC
    MapIcon.WeatherReport_CircleGreenObscured // VFR-OBS
    ), matchCloudCover(this.property, MapIcon.WeatherReport_CircleBlueMissing,
    // MVFR-Missing
    MapIcon.WeatherReport_CircleBlueClear,
    // MVFR-SKC
    MapIcon.WeatherReport_CircleBlueClear,
    // MVFR-CLR
    MapIcon.WeatherReport_CircleBlueFew,
    // MVFR-FEW
    MapIcon.WeatherReport_CircleBlueScattered,
    // MVFR-SCT
    MapIcon.WeatherReport_CircleBlueBroken,
    // MVFR-BKN
    MapIcon.WeatherReport_CircleBlueOvercast,
    // MVFR-OVC
    MapIcon.WeatherReport_CircleBlueObscured // MVFR-OBS
    ), matchCloudCover(this.property, MapIcon.WeatherReport_CircleRedMissing,
    // IFR-Missing
    MapIcon.WeatherReport_CircleRedClear,
    // IFR-SKC
    MapIcon.WeatherReport_CircleRedClear,
    // IFR-CLR
    MapIcon.WeatherReport_CircleRedFew,
    // IFR-FEW
    MapIcon.WeatherReport_CircleRedScattered,
    // IFR-SCT
    MapIcon.WeatherReport_CircleRedBroken,
    // IFR-BKN
    MapIcon.WeatherReport_CircleRedOvercast,
    // IFR-OVC
    MapIcon.WeatherReport_CircleRedObscured // IFR-OBS
    ), matchCloudCover(this.property, MapIcon.WeatherReport_CircleMagentaMissing,
    // LIFR-Missing
    MapIcon.WeatherReport_CircleMagentaClear,
    // LIFR-SKC
    MapIcon.WeatherReport_CircleMagentaClear,
    // LIFR-CLR
    MapIcon.WeatherReport_CircleMagentaFew,
    // LIFR-FEW
    MapIcon.WeatherReport_CircleMagentaScattered,
    // LIFR-SCT
    MapIcon.WeatherReport_CircleMagentaBroken,
    // LIFR-BKN
    MapIcon.WeatherReport_CircleMagentaOvercast,
    // LIFR-OVC
    MapIcon.WeatherReport_CircleMagentaObscured // LIFR-OBS
    ));
  }
  /**
   * Give priority to worse ratings and more cloud cover
   */
  getSymbolSortKey() {
    return matchRating('ceilingRating', 33, matchCloudCover(this.property, 32, 28, 27, 26, 25, 24, 23, 22), matchCloudCover(this.property, 31, 21, 20, 19, 18, 17, 16, 15), matchCloudCover(this.property, 30, 14, 13, 12, 11, 10, 9, 8), matchCloudCover(this.property, 29, 7, 6, 5, 4, 3, 2, 1));
  }
  getTextColor() {
    if (this.mapSettings.activeMapLayer() === MapOverlayLayer.VFR && this.mapSettings.vfrTopographyState() === TopographyState.ON) {
      return this.mapTheme.airportTextColor();
    }
    return this.mapTheme.textColor();
  }
}
class MetarLayer extends LayerGroup {
  constructor() {
    super(...arguments);
    this.SOURCE_PREFIX = 'metar';
    this.config = this.mapConfig.overlay.metar;
    this.groupInfo = [{
      layer: new FlightConditionsLayer(this.injector, this.radialMenuMapService, WeatherReportType.Metar, LayerType.Metars),
      shouldBeVisible: () => this.config.type() === MetarType.FlightConditions
    }];
  }
  addSource(map) {
    this.sourceId = map.sourceIds.next(this.SOURCE_PREFIX);
    map.addSource(this.sourceId, {
      type: 'vector',
      tiles: [`${this.content.urlBase}/index/{z}/{x}/{y}.mvt`],
      scheme: 'tms',
      minzoom: 2,
      maxzoom: 10
    });
    return this.sourceId;
  }
  getSource(_map) {
    return this.sourceId;
  }
  removeSource(map) {
    if (this.sourceId != null) {
      map.removeSource(this.sourceId);
      this.sourceId = undefined;
    }
  }
}
let MetarService = /*#__PURE__*/(() => {
  class MetarService extends LayerService {
    constructor() {
      super();
      this.images = [MapIcon.WeatherReport_CircleBlueBroken, MapIcon.WeatherReport_CircleBlueClear, MapIcon.WeatherReport_CircleBlueFew, MapIcon.WeatherReport_CircleBlueMissing, MapIcon.WeatherReport_CircleBlueObscured, MapIcon.WeatherReport_CircleBlueOvercast, MapIcon.WeatherReport_CircleBlueScattered, MapIcon.WeatherReport_CircleGrayMissing, MapIcon.WeatherReport_CircleGreenBroken, MapIcon.WeatherReport_CircleGreenClear, MapIcon.WeatherReport_CircleGreenFew, MapIcon.WeatherReport_CircleGreenMissing, MapIcon.WeatherReport_CircleGreenObscured, MapIcon.WeatherReport_CircleGreenOvercast, MapIcon.WeatherReport_CircleGreenScattered, MapIcon.WeatherReport_CircleMagentaBroken, MapIcon.WeatherReport_CircleMagentaClear, MapIcon.WeatherReport_CircleMagentaFew, MapIcon.WeatherReport_CircleMagentaMissing, MapIcon.WeatherReport_CircleMagentaObscured, MapIcon.WeatherReport_CircleMagentaOvercast, MapIcon.WeatherReport_CircleMagentaScattered, MapIcon.WeatherReport_CircleRedBroken, MapIcon.WeatherReport_CircleRedClear, MapIcon.WeatherReport_CircleRedFew, MapIcon.WeatherReport_CircleRedMissing, MapIcon.WeatherReport_CircleRedObscured, MapIcon.WeatherReport_CircleRedOvercast, MapIcon.WeatherReport_CircleRedScattered].map(x => MapIconDefinitions.get(x));
      effect(() => this.updateImages());
      effect(() => this.updateLayers());
    }
    createLayer(content) {
      return new MetarLayer(this.injector, this.radialMenuMapService, content);
    }
    getContent() {
      return this.mapInfo.overlay.metar.content();
    }
    getVisible() {
      return this.mapConfig.overlay.metar.visible();
    }
    updateImages() {
      if (this.getVisible()) {
        this.mapIcon.add(this.images);
      } else {
        this.mapIcon.remove(this.images);
      }
    }
    static {
      this.ɵfac = function MetarService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MetarService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MetarService,
        factory: MetarService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MetarService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// glsl
const vertexShader = `
    precision mediump float;

    attribute float a_index;

    uniform sampler2D u_particles;
    uniform float u_particles_res;

    varying vec2 v_particle_pos;

    void main() {
        vec4 color = texture2D(u_particles, vec2(
            fract(a_index / u_particles_res),
            floor(a_index / u_particles_res) / u_particles_res));

        // decode current particle position from the pixel's RGBA value
        v_particle_pos = vec2(
            color.r / 255.0 + color.b,
            color.g / 255.0 + color.a);

        gl_PointSize = 2.2;
        gl_Position = vec4(2.0 * v_particle_pos.x - 1.0, 1.0 - 2.0 * v_particle_pos.y, 0, 1);
    }`;
const fragmentShader = `
    precision mediump float;

    uniform sampler2D u_vector;
    uniform vec2 u_vector_min;
    uniform vec2 u_vector_max;
    uniform sampler2D u_color_ramp;
    uniform float u_opacity;

    uniform vec4 u_bounds;
    uniform vec4 u_data_bounds;

    varying vec2 v_particle_pos;

    vec2 returnLonLat(float x_domain, float y_domain, vec2 pos) {

        //need value between 0 and 1, which fract accomplishes
        float mercator_x = fract(u_bounds.x + pos.x * x_domain);
        float mercator_y = u_bounds.y + pos.y * y_domain;

        float lon = mercator_x * 360.0 - 180.0;
        float lat2 = 180.0 - mercator_y * 360.0;
        float lat = 360.0 / 3.141592654 * atan(exp(lat2 * 3.141592654/180.0)) - 90.0;

        return vec2(lon, lat);
    }

    void main() {

        //convert from 0-1 to degrees for proper texture value lookup
        float x_domain = abs(u_bounds.x - u_bounds.z);
        float y_domain = abs(u_bounds.y - u_bounds.w);

        vec2 coordinate = returnLonLat(x_domain, y_domain, v_particle_pos);
        float lon = coordinate.x;
        float lat = coordinate.y;

        //discard if out of bounds
        if (
            lat > u_data_bounds.w || lat < u_data_bounds.y ||
            lon > u_data_bounds.z || lon < u_data_bounds.x
        ) {
            discard;
        }

        //color ramp is encoded in a 16x16(colorTextureSize) texture
        //Enable color particles when needed
        //vec2 velocity = mix(u_vector_min, u_vector_max, texture2D(u_vector, v_particle_pos).rg);
        //float speed_t = length(velocity) / length(u_vector_max);
        //vec2 ramp_pos = vec2(
        //    fract(16.0 * speed_t),
        //    floor(16.0 * speed_t) / 16.0);
        //gl_FragColor = texture2D(u_color_ramp, ramp_pos);

        // use fixed colors
        vec4 color = vec4(0.7, 1.0, 1.0, 0.8);
        gl_FragColor = vec4(floor(255.0 * color * u_opacity) / 255.0);
    }`;
const vsQuad = `
    precision mediump float;

    attribute vec2 a_pos;

    varying vec2 v_tex_pos;

    void main() {
        v_tex_pos = a_pos;
        gl_Position = vec4(1.0 - 2.0 * a_pos, 0, 1);
    }`;
const fsScreen = `
    precision mediump float;

    uniform sampler2D u_screen;
    uniform float u_opacity;

    varying vec2 v_tex_pos;

    void main() {
        vec4 color = texture2D(u_screen, 1.0 - v_tex_pos);
        // a hack to guarantee opacity fade out even with a value close to 1.0
        gl_FragColor = vec4(floor(255.0 * color * u_opacity) / 255.0);
    }`;
const fsUpdate = `
    precision mediump float;

    uniform sampler2D u_particles;
    uniform sampler2D u_vector;
    uniform vec2 u_vector_res;
    uniform vec2 u_vector_min;
    uniform vec2 u_vector_max;
    uniform float u_rand_seed;
    uniform float u_speed_factor;
    uniform float u_drop_rate;
    uniform float u_drop_rate_bump;
    uniform vec4 u_bounds;
    uniform vec4 u_data_bounds;

    varying vec2 v_tex_pos;

    // pseudo-random generator
    const vec3 rand_constants = vec3(12.9898, 78.233, 4375.85453);
    float rand(const vec2 co) {
        float t = dot(rand_constants.xy, co);
        return fract(sin(t) * (rand_constants.z + t));
    }

    // vector magnitude lookup; use manual bilinear filtering based on 4 adjacent pixels for smooth interpolation
    vec2 lookup_vector(const vec2 uv) {
        vec2 px = 1.0 / u_vector_res;
        vec2 vc = (floor(uv * u_vector_res)) * px;
        vec2 f = fract(uv * u_vector_res);
        vec2 tl = texture2D(u_vector, vc).rg;
        vec2 tr = texture2D(u_vector, vc + vec2(px.x, 0)).rg;
        vec2 bl = texture2D(u_vector, vc + vec2(0, px.y)).rg;
        vec2 br = texture2D(u_vector, vc + px).rg;
        return mix(mix(tl, tr, f.x), mix(bl, br, f.x), f.y);
    }

    vec2 returnLonLat(float x_domain, float y_domain, vec2 pos) {

        //need value between 0 and 1, which fract accomplishes
        float mercator_x = fract(u_bounds.x + pos.x * x_domain);
        float mercator_y = u_bounds.y + pos.y * y_domain;

        float lon = mercator_x * 360.0 - 180.0;
        float lat2 = 180.0 - mercator_y * 360.0;
        float lat = 360.0 / 3.141592654 * atan(exp(lat2 * 3.141592654/180.0)) - 90.0;

        return vec2(lon, lat);
    }

    void main() {
        vec4 color = texture2D(u_particles, v_tex_pos);
        vec2 pos = vec2(
            color.r / 255.0 + color.b,
            color.g / 255.0 + color.a); // decode particle position from pixel RGBA

        //convert from 0-1 to degrees for proper texture value lookup
        float x_domain = abs(u_bounds.x - u_bounds.z);
        float y_domain = abs(u_bounds.y - u_bounds.w);

        vec2 coordinate = returnLonLat(x_domain, y_domain, pos);
        float lon = coordinate.x;
        float lat = coordinate.y;

        float lon_domain = u_data_bounds.z - u_data_bounds.x;
        float lat_domain = u_data_bounds.y - u_data_bounds.w;

        vec2 pos_lookup = vec2(
            (lon - u_data_bounds.x) / lon_domain,
            (lat - u_data_bounds.y) / lat_domain
        );

        vec2 velocity = mix(u_vector_min, u_vector_max, lookup_vector(pos_lookup));
        float speed_t = length(velocity) / length(u_vector_max);

        vec2 offset = vec2(velocity.x , -velocity.y) * 0.0001 * u_speed_factor;

        // update particle position, wrapping around the date line
        pos = fract(1.0 + pos + offset);

        // a random seed to use for the particle drop
        vec2 seed = (pos + v_tex_pos) * u_rand_seed;

        // drop rate is a chance a particle will restart at random position, to avoid degeneration
        float drop_rate = u_drop_rate + speed_t * u_drop_rate_bump;
        float drop = step(1.0 - drop_rate, rand(seed));

        vec2 random_pos = vec2(
            rand(seed + 1.3),
            rand(seed + 2.1));
        pos = mix(pos, random_pos, drop);

        // encode the new particle position back into RGBA
        gl_FragColor = vec4(
            fract(pos * 255.0),
            floor(pos * 255.0) / 255.0);
    }`;
class WindGL {
  constructor(map, gl) {
    this.map = map;
    this.gl = gl;
    this.state = 'PAUSED';
    this.animationId = null;
    this.defaultParticleNum = 6400;
    this.colorTextureSize = 16;
    this.minParticleNum = 1;
    this.defaultParticleOpacity = 0.8;
    this.map = map;
    this.gl = gl;
    this.numParticles = this.defaultParticleNum;
    this.particleOpacity = this.defaultParticleOpacity;
  }
  setBounds(bounds) {
    const nw = bounds.getNorthWest();
    const se = bounds.getSouthEast();
    const nwMercator = ML.MercatorCoordinate.fromLngLat(nw);
    const seMercator = ML.MercatorCoordinate.fromLngLat(se);
    //[minx, miny, maxx, maxy]
    this.mapBounds = [nwMercator.x, nwMercator.y, seMercator.x, seMercator.y];
  }
  setData(dataObject) {
    if (dataObject != null) {
      // set windGL data and bounds of data, and range of vector components
      ({
        data: this.data,
        bounds: this.bounds,
        range: this.range
      } = dataObject);
      // initialize settings, programs, buffers
      this.initialize();
      this.startAnimation();
    }
  }
  getColorRamp(colors) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx == null) {
      throw new Error('Unable to get 2D rendering context');
    }
    canvas.width = this.colorTextureSize;
    canvas.height = this.colorTextureSize;
    const gradient = ctx.createLinearGradient(0, 0, 0, this.colorTextureSize);
    Object.entries(colors).forEach(([stop, color]) => {
      gradient.addColorStop(parseFloat(stop), color);
    });
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, this.colorTextureSize, this.colorTextureSize);
    return ctx.getImageData(0, 0, this.colorTextureSize, this.colorTextureSize).data;
  }
  setParticles(num) {
    this.particleRes = Math.ceil(Math.sqrt(num));
    this.numParticles = this.particleRes * this.particleRes;
    const particleState = new Uint8Array(this.numParticles * 4);
    for (let i = 0; i < particleState.length; i++) {
      particleState[i] = Math.floor(Math.random() * 256);
    }
    this.particleTextures = twgl.createTextures(this.gl, {
      particleTexture0: {
        mag: this.gl.NEAREST,
        min: this.gl.NEAREST,
        width: this.particleRes,
        height: this.particleRes,
        format: this.gl.RGBA,
        src: particleState,
        wrap: this.gl.CLAMP_TO_EDGE
      },
      particleTexture1: {
        mag: this.gl.NEAREST,
        min: this.gl.NEAREST,
        width: this.particleRes,
        height: this.particleRes,
        format: this.gl.RGBA,
        src: particleState,
        wrap: this.gl.CLAMP_TO_EDGE
      }
    });
    this.particleIndices = new Float32Array(this.numParticles);
    for (let i = 0; i < this.numParticles; i++) {
      this.particleIndices[i] = i;
    }
  }
  setParticleOpacity(opacity) {
    this.particleOpacity = opacity;
  }
  initialize() {
    this.fadeOpacity = 0.96;
    this.speedFactor = 0.4;
    this.dropRate = 0.004;
    this.dropRateBump = 0.05;
    const defaultRampColors = {
      0.0: '#3288bd',
      0.1: '#66c2a5',
      0.2: '#abdda4',
      0.3: '#e6f598',
      0.4: '#fee08b',
      0.5: '#fdae61',
      0.6: '#f46d43',
      1.0: '#d53e4f'
    };
    this.colorRampData = this.getColorRamp(defaultRampColors);
    this.programInfo = twgl.createProgramInfo(this.gl, [vertexShader, fragmentShader]);
    this.screenProgramInfo = twgl.createProgramInfo(this.gl, [vsQuad, fsScreen]);
    this.updateProgramInfo = twgl.createProgramInfo(this.gl, [vsQuad, fsUpdate]);
    //initial setting of particle positions
    this.setParticles(this.defaultParticleNum);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context == null) {
      throw new Error('2D context not supported');
    }
    canvas.width = this.data.width;
    canvas.height = this.data.height;
    context.drawImage(this.data, 0, 0);
    const myData = context.getImageData(0, 0, this.data.width, this.data.height);
    const emptyPixels = new Uint8Array(this.gl.canvas.width * this.gl.canvas.height * 4);
    this.textures = twgl.createTextures(this.gl, {
      u_image: {
        mag: this.gl.LINEAR,
        min: this.gl.LINEAR,
        width: myData.width,
        height: myData.height,
        format: this.gl.RGBA,
        src: myData.data
      },
      backgroundTexture: {
        mag: this.gl.NEAREST,
        min: this.gl.NEAREST,
        width: this.gl.canvas.width,
        height: this.gl.canvas.height,
        format: this.gl.RGBA,
        src: emptyPixels,
        wrap: this.gl.CLAMP_TO_EDGE
      },
      screenTexture: {
        mag: this.gl.NEAREST,
        min: this.gl.NEAREST,
        width: this.gl.canvas.width,
        height: this.gl.canvas.height,
        format: this.gl.RGBA,
        src: emptyPixels,
        wrap: this.gl.CLAMP_TO_EDGE
      },
      colorRampTexture: {
        mag: this.gl.LINEAR,
        min: this.gl.LINEAR,
        width: this.colorTextureSize,
        height: this.colorTextureSize,
        format: this.gl.RGBA,
        src: new Uint8Array(this.colorRampData)
      }
    });
    this.framebuffer = this.gl.createFramebuffer();
  }
  drawParticles() {
    this.gl.useProgram(this.programInfo.program);
    const arrays = {
      a_index: {
        numComponents: 1,
        data: this.particleIndices
      }
    };
    const bufferInfo = twgl.createBufferInfoFromArrays(this.gl, arrays);
    const uniforms = {
      u_vector: this.textures.u_image,
      u_particles: this.particleTextures.particleTexture0,
      u_particles_res: this.particleRes,
      u_vector_min: [this.range[0], this.range[0]],
      u_vector_max: [this.range[1], this.range[1]],
      u_bounds: this.mapBounds,
      u_data_bounds: this.bounds,
      u_color_ramp: this.textures.colorRampTexture,
      u_opacity: this.particleOpacity
    };
    twgl.setBuffersAndAttributes(this.gl, this.programInfo, bufferInfo);
    twgl.setUniforms(this.programInfo, uniforms);
    twgl.drawBufferInfo(this.gl, bufferInfo, this.gl.POINTS);
  }
  drawTexture(texture, opacity) {
    this.gl.useProgram(this.screenProgramInfo.program);
    const arrays = {
      a_pos: {
        numComponents: 2,
        data: new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1])
      }
    };
    const uniforms = {
      u_screen: texture,
      u_opacity: opacity
    };
    const bufferInfo = twgl.createBufferInfoFromArrays(this.gl, arrays);
    twgl.setBuffersAndAttributes(this.gl, this.screenProgramInfo, bufferInfo);
    twgl.setUniforms(this.screenProgramInfo, uniforms);
    twgl.drawBufferInfo(this.gl, bufferInfo);
  }
  drawScreen() {
    //bind framebuffer
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.framebuffer);
    //draw to screenTexture
    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.textures.screenTexture, 0);
    //set viewport to size of canvas
    this.gl.viewport(0, 0, this.gl.canvas.width, this.gl.canvas.height);
    //first disable blending
    this.gl.disable(this.gl.BLEND);
    //draw backgroundTexture to screenTexture target
    this.drawTexture(this.textures.backgroundTexture, this.fadeOpacity);
    //draw particles to screentexture
    this.drawParticles();
    //target normal canvas by setting FRAMEBUFFER to null
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
    //enable blending for final render to map
    this.gl.enable(this.gl.BLEND);
    this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
    this.drawTexture(this.textures.screenTexture, 1.0);
    this.gl.disable(this.gl.BLEND);
    //swap background with screen
    const temp = this.textures.backgroundTexture;
    this.textures.backgroundTexture = this.textures.screenTexture;
    this.textures.screenTexture = temp;
  }
  updateParticles() {
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.framebuffer);
    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.particleTextures.particleTexture1, 0);
    this.gl.viewport(0, 0, this.particleRes, this.particleRes);
    this.gl.useProgram(this.updateProgramInfo.program);
    const arrays = {
      a_pos: {
        numComponents: 2,
        data: new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1])
      }
    };
    const uniforms = {
      u_vector: this.textures.u_image,
      u_particles: this.particleTextures.particleTexture0,
      u_vector_min: [this.range[0], this.range[0]],
      u_vector_max: [this.range[1], this.range[1]],
      u_rand_seed: Math.random(),
      u_vector_res: [this.data.width, this.data.height],
      u_speed_factor: this.speedFactor,
      u_drop_rate: this.dropRate,
      u_drop_rate_bump: this.dropRateBump,
      u_bounds: this.mapBounds,
      u_data_bounds: this.bounds,
      u_color_ramp: this.textures.colorRampTexture
    };
    const bufferInfo = twgl.createBufferInfoFromArrays(this.gl, arrays);
    twgl.setBuffersAndAttributes(this.gl, this.updateProgramInfo, bufferInfo);
    twgl.setUniforms(this.updateProgramInfo, uniforms);
    twgl.drawBufferInfo(this.gl, bufferInfo);
    const temp = this.particleTextures.particleTexture0;
    this.particleTextures.particleTexture0 = this.particleTextures.particleTexture1;
    this.particleTextures.particleTexture1 = temp;
  }
  draw() {
    if (this.state !== 'ANIMATING') return;
    this.gl.disable(this.gl.DEPTH_TEST);
    this.gl.disable(this.gl.STENCIL_TEST);
    this.drawScreen();
    this.updateParticles();
  }
  frame() {
    this.map.triggerRepaint();
    this.animationId = requestAnimationFrame(this.frame.bind(this));
  }
  startAnimation() {
    this.state = 'ANIMATING';
    this.setBounds(this.map.getBounds());
    this.frame();
  }
  stopAnimation() {
    this.state = 'PAUSED';
    this.clear();
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
    }
  }
  clear() {
    if (this.gl == null) {
      console.error('WebGL context is not initialized');
      return;
    }
    if (this.textures == null) {
      console.error('screenTexture is not initialized');
      return;
    }
    if (this.framebuffer == null) {
      console.error('framebuffer is not initialized');
      return;
    }
    this.gl.clearColor(0.0, 0.0, 0.0, 0.0);
    //clear framebuffer textures
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.framebuffer);
    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.textures.screenTexture, 0);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.textures.backgroundTexture, 0);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    //generate new random particle positions
    this.setParticles(this.minParticleNum);
    //target normal canvas
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
    //clear canvas
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
  }
  resize(width, height) {
    this.gl.canvas.width = width;
    this.gl.canvas.height = height;
    this.gl.viewport(0, 0, width, height);
    const emptyPixels = new Uint8Array(width * height * 4);
    // adjust texture size
    twgl.setTextureFromArray(this.gl, this.textures?.backgroundTexture, emptyPixels, {
      width: width,
      height: height
    });
    twgl.setTextureFromArray(this.gl, this.textures?.screenTexture, emptyPixels, {
      width: width,
      height: height
    });
    if (this.framebuffer != null) {
      this.framebuffer = this.gl.createFramebuffer();
    }
    this.clear();
  }
}
const LAYER_PRIORITY = LayerPriority.WindAnimation;
const LAYER_PREFIX = 'wind-animation';
class WindAnimationLayer extends SnapshotLayer {
  constructor(injector, snapshotLayerInfo, onLoadedChange) {
    super(injector, snapshotLayerInfo);
    this.onLoadedChange = onLoadedChange;
    this.animationStartTime = 0;
    this.isInitialParticleNum = true;
    this.renderFreqControlFlag = true;
    this.particleNum = 6400;
    this.wind_vector_max = 55; // This value is from backend wind u/v component configuration
    this.wind_vector_min = -45; // This value is from backend wind u/v component configuration
    this.initialTime = 150; // ms
    this.http = this.injector.get(HttpClient);
    this.config = this.injector.get(AVC_MAPS_DISPLAY_CONFIG);
  }
  add(map) {
    this.layerId = map.layerIds.next(LAYER_PREFIX);
    this.sourceId = this.layerId;
    map.addLayer(LAYER_PRIORITY, {
      id: this.layerId,
      type: 'custom',
      onAdd: async (mapInstance, gl) => {
        try {
          await this.loadVectorData();
          if (this.vectorData != null) {
            this.windGL = new WindGL(mapInstance, gl);
            this.windGL.setData(this.vectorData);
            this.windGL.setParticleOpacity(this.mapConfig.overlay.windAnimation.opacity());
          } else {
            console.error("WindAnimation: vectorData is null");
          }
        } catch (error) {
          console.error("WindAnimation: fails to load vector data:", error);
        }
      },
      render: () => {
        if (this.windGL != null) {
          if (this.renderFreqControlFlag) {
            // Use half render frequency
            this.renderFreqControlFlag = false;
            return;
          }
          if (this.isInitialParticleNum) {
            if (Date.now() - this.animationStartTime > this.initialTime) {
              // Render fewer particles in the initial and then increase particles for the issue in AVPILOTWEB-4121
              this.windGL.setParticles(this.particleNum);
              this.isInitialParticleNum = false;
            }
          }
          this.windGL.setParticleOpacity(this.mapConfig.overlay.windAnimation.opacity());
          this.windGL.draw();
        }
      }
    });
    map.on('movestart', () => {
      this.windGL?.stopAnimation();
    });
    map.on('moveend', () => {
      this.windGL?.startAnimation();
      this.isInitialParticleNum = true;
      this.animationStartTime = Date.now();
    });
    map.on('resize', () => {
      if (this.windGL != null) {
        const width = map.getCanvas().width;
        const height = map.getCanvas().height;
        this.windGL.resize(width, height);
        this.windGL.startAnimation();
      }
    });
  }
  setOpacity(opacity) {
    if (Array.isArray(opacity)) {
      throw new Error('WindAnimationLayer only supports numeric opacity.');
    }
    if (this.windGL != null) {
      this.windGL.setParticleOpacity(opacity);
    }
  }
  async loadVectorData() {
    const url = `${this.info.baseUrl}/${this.info.modelRunTime}/${this.info.elevation}/${this.info.offset}/05deg.png`;
    try {
      const response = await lastValueFrom(this.http.get(url, {
        ...this.getHttpGetOptions(),
        responseType: 'blob'
      }));
      const img = await createImageBitmap(response);
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx != null) {
        ctx.drawImage(img, 0, 0);
        this.vectorData = {
          data: canvas,
          width: img.width,
          height: img.height,
          bounds: [-180, -90, 180, 90],
          range: [this.wind_vector_min, this.wind_vector_max]
        };
        this.loaded = true;
        this.onLoadedChange();
      } else {
        console.error("Failed to create 2D context");
      }
    } catch (error) {
      console.error("Failed to load the wind image from:", url, error);
    }
  }
  remove(map) {
    if (this.layerId != null) {
      this.stopAnimation();
      map.removeLayer(this.layerId);
      this.layerId = undefined;
    }
    this.loaded = false;
    this.onLoadedChange();
  }
  update(_map) {
    // no action
  }
  stopAnimation() {
    this.windGL?.stopAnimation();
  }
  getHttpGetOptions() {
    const options = {};
    const integrationTestKey = this.config.integrationTestKey;
    if (integrationTestKey != null && integrationTestKey.length > 0) {
      options.headers = {
        'X-Garmin-Integration-Test': integrationTestKey
      };
    } else {
      options.withCredentials = true;
    }
    return options;
  }
}
let WindAnimationService = /*#__PURE__*/(() => {
  class WindAnimationService extends SnapshotLayerService {
    constructor() {
      super(...arguments);
      this.MAXIMUM_AGE_SECONDS = undefined; // only future data
      this.MAXIMUM_ALTITUDE = 42000;
      this.MINIMUM_ALTITUDE = 3000;
      this.DATA_VALIDITY_SECONDS = 6 * 60 * 60;
      this.ONLY_PAST = false;
      this.DISABLE_ON_SOURCE_DATA = true;
    }
    createLayer(info) {
      return new WindAnimationLayer(this.injector, info, () => this.updateLoadedRange());
    }
    getContent() {
      return this.mapInfo.overlay.windAnimation.content();
    }
    getVisible() {
      return this.mapConfig.overlay.windAnimation.visible();
    }
    getOpacity() {
      return this.mapConfig.overlay.windAnimation.opacity();
    }
    setAvailableRange(range) {
      this.mapAnimation.availableRange.winds.set(range);
    }
    setLoadedRange(range) {
      this.mapAnimation.loadedRange.winds.set(range);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵWindAnimationService_BaseFactory;
        return function WindAnimationService_Factory(__ngFactoryType__) {
          return (ɵWindAnimationService_BaseFactory || (ɵWindAnimationService_BaseFactory = i0.ɵɵgetInheritedFactory(WindAnimationService)))(__ngFactoryType__ || WindAnimationService);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: WindAnimationService,
        factory: WindAnimationService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return WindAnimationService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const RADIAL_MENU_EXCEPTION = 'radial-menu-exception';
let PopupService = /*#__PURE__*/(() => {
  class PopupService {
    constructor(_locale) {
      this._locale = _locale;
      this.radialMenuMapService = inject(RadialMenuMapService);
      this.mapService = inject(MapService);
      this.decimal = new DecimalPipe(this._locale);
      this.notamRunwayService = inject(NotamRunwayService);
      this.gatherNotams = features => {
        // NOTAMs can come from many different layers. This turns them into actual usable NOTAM features.
        const notams = [];
        for (const feature of features) {
          let notam;
          // Runway NOTAMs
          if (this.notamRunwayService.isRunwayNotam(feature)) {
            notam = this.notamRunwayService.getNotam(feature);
          }
          // normal NOTAMs
          if (notam == null) {
            notam = feature;
          }
          notams.push(notam);
        }
        return notams;
      };
    }
    close() {
      this.mapService.search.airmetsSigmets.set([]);
      this.mapService.search.airports.set([]);
      this.mapService.search.airspaces.set([]);
      this.mapService.search.airways.set([]);
      this.mapService.search.fuelPrices.set([]);
      this.mapService.search.internetTraffic.set([]);
      this.mapService.search.metars.set([]);
      this.mapService.search.ndbs.set([]);
      this.mapService.search.notams.set([]);
      this.mapService.search.obstacles.set([]);
      this.mapService.search.pireps.set([]);
      this.mapService.search.stormCells.set([]);
      this.mapService.search.tfrs.set([]);
      this.mapService.search.vors.set([]);
      this.mapService.search.wpts.set([]);
    }
    showAllFeatures(features, currentPosition) {
      this.close();
      const layers = this.radialMenuMapService.getAllLayers();
      for (const [layerType, layerIds] of layers) {
        const filteredFeatures = features.filter(x => layerIds.includes(x.layer.id));
        if (filteredFeatures.length > 0) {
          this.populateFeaturesByLayerType(layerType, filteredFeatures, currentPosition);
        }
      }
    }
    showFeatures(featureType, features, currentPosition) {
      this.close();
      this.populateFeaturesByFeatureType(featureType, features, currentPosition);
    }
    // eslint-disable-next-line complexity
    populateFeaturesByFeatureType(featureType, features, currentPosition) {
      switch (featureType) {
        case FeatureType$1.AirmetsSigmets:
          this.mapService.search.airmetsSigmets.set(features.map(packAirmetSigmet));
          break;
        case FeatureType$1.Airports:
          this.mapService.search.airports.set(features.map(packAirport));
          break;
        case FeatureType$1.Airspaces:
          {
            const notams = this.queryAirspaceNotams();
            this.mapService.search.airspaces.set(features.map(feature => packAirspace(feature, currentPosition, notams, this.decimal)));
            break;
          }
        case FeatureType$1.Airways:
          this.mapService.search.airways.set(features.flatMap(packAirway));
          break;
        case FeatureType$1.Fuel:
          this.mapService.search.fuelPrices.set(features.map(packFuel));
          break;
        case FeatureType$1.InternetTraffic:
          this.mapService.search.internetTraffic.set(features.map(packInternetTraffic));
          break;
        case FeatureType$1.NAVAIDs_NDB:
          this.mapService.search.ndbs.set(features.map(packNavaid));
          break;
        case FeatureType$1.NAVAIDs_VOR:
          this.mapService.search.vors.set(features.map(packNavaid));
          break;
        case FeatureType$1.NAVAIDs_WPT:
          this.mapService.search.wpts.set(features.map(packNavaid));
          break;
        case FeatureType$1.NOTAMs:
          this.mapService.search.notams.set(deduplicateNotams(this.gatherNotams(features).map(packNotam)));
          break;
        case FeatureType$1.Obstacles:
          this.mapService.search.obstacles.set(features.map(packObstacle));
          break;
        case FeatureType$1.PIREPs:
          this.mapService.search.pireps.set(features.map(packPirep));
          break;
        case FeatureType$1.StormCells:
          this.mapService.search.stormCells.set(features.map(packStormCell));
          break;
        case FeatureType$1.TFRs:
          this.mapService.search.tfrs.set(deduplicateTfrs(features.map(packTfr)));
          break;
        case FeatureType$1.UserWaypoints:
          if (features.length >= 1) {
            this.mapService.search.userWaypoints.set(features.map(packUserWaypoint));
          } else if (currentPosition != null) {
            this.mapService.search.userWaypoints.set([{
              exists: false,
              point: packPoint(currentPosition.lat, currentPosition.lng)
            }]);
          }
          break;
        case FeatureType$1.WeatherReport:
          this.mapService.search.metars.set(features.map(feature => packMetar(feature, currentPosition)));
          break;
        case FeatureType$1.MoreInfo:
        default:
          this.showAllFeatures(features);
          break;
      }
    }
    // eslint-disable-next-line complexity
    populateFeaturesByLayerType(layerType, features, currentPosition) {
      switch (layerType) {
        case LayerType.AirmetsSigmets:
          this.mapService.search.airmetsSigmets.set(features.map(packAirmetSigmet));
          break;
        case LayerType.Airports:
          this.mapService.search.airports.set(features.map(packAirport));
          break;
        case LayerType.Airspaces:
          {
            const notams = this.queryAirspaceNotams();
            this.mapService.search.airspaces.set(features.map(feature => packAirspace(feature, currentPosition, notams, this.decimal)));
            break;
          }
        case LayerType.Airways:
          this.mapService.search.airways.set(features.flatMap(packAirway));
          break;
        case LayerType.FuelPrices:
          this.mapService.search.fuelPrices.set(features.map(packFuel));
          break;
        case LayerType.InternetTraffic:
          this.mapService.search.internetTraffic.set(features.map(packInternetTraffic));
          break;
        case LayerType.Metars:
          // TODO
          break;
        case LayerType.Navaids_NDB:
          this.mapService.search.ndbs.set(features.map(packNavaid));
          break;
        case LayerType.Navaids_VOR:
          this.mapService.search.vors.set(features.map(packNavaid));
          break;
        case LayerType.Navaids_WPT:
          this.mapService.search.wpts.set(features.map(packNavaid));
          break;
        case LayerType.Notams:
          this.mapService.search.notams.set(deduplicateNotams(features.map(packNotam)));
          break;
        case LayerType.Obstacles:
          this.mapService.search.obstacles.set(features.map(packObstacle));
          break;
        case LayerType.Pireps:
          this.mapService.search.pireps.set(features.map(packPirep));
          break;
        case LayerType.StormCells:
          this.mapService.search.stormCells.set(features.map(packStormCell));
          break;
        case LayerType.Tfrs:
          this.mapService.search.tfrs.set(deduplicateTfrs(features.map(packTfr)));
          break;
        case LayerType.UserWaypoints:
          if (features.length >= 1) {
            this.mapService.search.userWaypoints.set(features.map(packUserWaypoint));
          } else if (currentPosition != null) {
            this.mapService.search.userWaypoints.set([{
              exists: false,
              point: packPoint(currentPosition.lat, currentPosition.lng)
            }]);
          }
          break;
        default:
          break;
      }
    }
    queryAirspaceNotams() {
      const map = this.mapService.map();
      if (map == null) {
        return [];
      }
      const layerIds = this.radialMenuMapService.getLayerData(LayerType.Notams);
      const sources = [];
      for (const id of layerIds) {
        const source = map.getSourceForLayerId(id);
        const isNew = sources.find(x => {
          return x.source === source.source && x.sourceLayer === source.sourceLayer;
        }) == null;
        if (isNew) {
          sources.push(source);
        }
      }
      const features = [];
      for (const source of sources) {
        if (source.source != null) {
          features.push(...map.querySourceFeatures(source.source, {
            sourceLayer: source.sourceLayer,
            filter: ['==', ['get', 'notamClass'], NotamClass.Airspace]
          }));
        }
      }
      return features;
    }
    static {
      this.ɵfac = function PopupService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PopupService)(i0.ɵɵinject(LOCALE_ID));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PopupService,
        factory: PopupService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PopupService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const DEBUG = false;
let SearchService = /*#__PURE__*/(() => {
  class SearchService {
    constructor() {
      this.markers = []; // debugging
    }
    /**
     * Searches around a given coordinate for currently rendered map features
     * whose geometries are within the specified radius (in meters) from the
     * given coordinate.
     *
     * @example
     * ```ts
     * const coord = [-122.420679, 37.772537];
     * const radius = 2000; // 2 km
     * const results = searchService.radialSearch(coord, radius);
     * ```
     *
     * @param coord The center coordinate to search around.
     * @param radius The radius (in meters) around the center coordinate.
     * @param options A set of query options. See https://maplibre.org/maplibre-gl-js-docs/api/map/#map#queryrenderedfeatures
     * @returns An array of MapGeoJSONFeature results.
     */
    radialSearch(coord, radius, map, options) {
      const bboxResults = this.bboxCollisionSearch(ML__default.LngLatBounds.fromLngLat(coord, radius), map, options);
      const result = bboxResults.filter(feature => {
        switch (feature.geometry.type) {
          case 'Point':
            {
              const [lng, lat] = feature.geometry.coordinates;
              return coord.distanceTo(new ML__default.LngLat(lng, lat)) <= radius;
            }
          case 'MultiPoint':
            {
              for (const point of feature.geometry.coordinates) {
                const [lng, lat] = point;
                if (coord.distanceTo(new ML__default.LngLat(lng, lat)) <= radius) {
                  return true;
                }
              }
              return false;
            }
          case 'LineString':
            {
              const line = feature.geometry.coordinates;
              return isPolygonWithinRadius(line, coord, radius);
            }
          case 'MultiLineString':
            {
              for (const line of feature.geometry.coordinates) {
                if (isPolygonWithinRadius(line, coord, radius)) {
                  return true;
                }
              }
              return false;
            }
          case 'Polygon':
            {
              // a polygon geometry can have inner polygons representing holes in
              // the outer polygon - for now, we only care about the outer polygon
              const polygon = feature.geometry.coordinates[0];
              return isPolygonWithinRadius(polygon, coord, radius);
            }
          case 'MultiPolygon':
            {
              for (const polygons of feature.geometry.coordinates) {
                const polygon = polygons[0]; // see comment in Polygon case
                if (isPolygonWithinRadius(polygon, coord, radius)) {
                  return true;
                }
              }
              return false;
            }
          default:
            console.warn('Unsupported geometry type:', feature.geometry.type);
            return false;
        }
      });
      return result;
    }
    /**
     * Searches for currently rendered map features whose collision boxes overlap
     * with the given search bounds.
     *
     * @example
     * ```ts
     * const coord = LngLat.convert([-122.420679, 37.772537]);
     * const radius = 2000; // 2 km
     * const results = searchService.bboxCollisionSearch(coord.toBounds(radius));
     * ```
     *
     * @param searchBounds The search bounding box.
     * @param options A set of query options. See https://maplibre.org/maplibre-gl-js-docs/api/map/#map#queryrenderedfeatures
     * @returns An array of MapGeoJSONFeature results.
     */
    bboxCollisionSearch(searchBounds, map, options) {
      if (map == null) {
        return [];
      }
      return map.queryRenderedFeatures([map.project(searchBounds.getSouthWest()), map.project(searchBounds.getNorthEast())], options);
    }
    debugClearMarkers() {
      if (DEBUG) {
        for (const marker of this.markers) {
          marker.remove();
        }
        this.markers = [];
      }
    }
    debugAddMarkers() {
      if (DEBUG) {
        for (const _marker of this.markers) {
          //this.mapService.addMarker(marker);
        }
      }
    }
    debugPointMarker(hit, coord, options) {
      if (DEBUG && hit) {
        this.markers.push(new ML__default.Marker(options).setLngLat(coord));
      }
    }
    debugPolygonMarkers(hit, coords) {
      if (DEBUG && hit) {
        const colors = ['red', 'green', 'blue'];
        let index = 0;
        for (const [lng, lat] of coords) {
          this.debugPointMarker(hit, [lng, lat], {
            color: colors[index]
          });
          index = (index + 1) % colors.length;
        }
      }
    }
    static {
      this.ɵfac = function SearchService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SearchService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SearchService,
        factory: SearchService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SearchService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const wrapLng = lng => {
  let newLng = lng;
  while (newLng < -180) {
    newLng += 360;
  }
  while (newLng > 180) {
    newLng -= 360;
  }
  return newLng;
};
const projection = ([start, end], coord) => {
  const a = [coord.lng - start[0], coord.lat - start[1]]; // a = coord - start
  a[0] = wrapLng(a[0]);
  const b = [end[0] - start[0], end[1] - start[1]]; // b = end - start
  b[0] = wrapLng(b[0]);
  const d = a[0] * b[0] + a[1] * b[1]; // d = a . b
  const t = d / (b[0] ** 2 + b[1] ** 2); // t = d / |b|^2
  const p = new ML__default.LngLat(start[0] + b[0] * t, start[1] + b[1] * t); // start + b * t
  return [p, t];
};
const clockwiseAngle = ([start, end], coord) => {
  const a = [start[0] - coord.lng, start[1] - coord.lat];
  a[0] = wrapLng(a[0]);
  const b = [end[0] - coord.lng, end[1] - coord.lat];
  b[0] = wrapLng(b[0]);
  const d = a[0] * b[0] + a[1] * b[1];
  const m = a[0] * b[1] - a[1] * b[0];
  return -Math.atan2(m, d); // negative because we want the clockwise angle
};
const samePos = (a, b) => a[0] === b[0] && a[1] === b[1];
const isPolygonWithinRadius = (polygon, coord, radius) => {
  const poly = polygon.map((value, index, array) => [value, array[index + 1]]) // make pairs
  .filter(([a, b]) => b !== undefined && !samePos(a, b)); // get rid of the last one and 0-length segments
  let totalAngle = 0;
  for (const [start, end] of poly) {
    // case 1: one of the points of the line segment is inside the radius
    if (coord.distanceTo(new ML__default.LngLat(start[0], start[1])) <= radius || coord.distanceTo(new ML__default.LngLat(end[0], end[1])) <= radius) {
      return true;
    }
    // case 2: the projection of coord is on the line segment and is within the radius
    const [proj, t] = projection([start, end], coord);
    if (coord.distanceTo(proj) <= radius && t > 0 && t < 1) {
      return true;
    }
    // case 3: all points of the polygon completely surround the coord
    totalAngle += clockwiseAngle([start, end], coord);
  }
  // theoretically this check could be either:
  // - totalAngle == 2 * Math.Pi
  // - totalAngle != 0
  // however, floating point errors make these checks inconsistent so we just
  // check that our angle is sufficiently greater than 0
  return totalAngle > TO_RAD;
};
let TerrainService = /*#__PURE__*/(() => {
  class TerrainService {
    constructor() {
      this.trgb = new TerrainRGB();
      this.mapService = inject(MapService);
      this._update = effect(() => {
        const content = this.mapService.info.content.hillshade.content()[0];
        if (content != null) {
          this.trgb.configure(`${content.urlBase}/{z}/{x}/{y}.webp`, 1, 10);
        }
      });
    }
    available() {
      return this.mapService.info.content.hillshade.available();
    }
    /**
     * Determines the elevation of the terrain at the given location.
     * @param point LngLat to get the terrain from
     * @returns The elevation (in meters) or undefined
     */
    async getElevation(point, mapDisplay) {
      if (this.trgb == null) {
        return Promise.reject(new Error('TerrainRGB class is not initialized'));
      }
      return this.trgb.getElevation(point, mapDisplay);
    }
    static {
      this.ɵfac = function TerrainService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TerrainService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TerrainService,
        factory: TerrainService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TerrainService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * The RadialMenu has two main parts:
 *  1) An interactive SVG - the thing we think of as the actual radial menu (with buttons and animations and all that)
 *  2) A MapLibre Marker - this is what attaches the SVG to the map and provides dragging and location functionality
 */
class RadialMenu {
  constructor(injector) {
    this.injector = injector;
    this.searchRadius = 0; // in meters
    this.shouldToggle = true;
    this.airportService = this.injector.get(AirportService);
    this.mapService = this.injector.get(MapService);
    this.searchService = this.injector.get(SearchService);
    this.stormCellService = this.injector.get(StormCellsService);
    this.terrainService = this.injector.get(TerrainService);
    this.radialMenuService = this.injector.get(AvcMapRadialMenuService);
    this.radialMenuMapService = this.injector.get(RadialMenuMapService);
    this.popupService = this.injector.get(PopupService);
    this.onClick = target => {
      const element = target.target;
      if (this.svg != null && this.svg.visible) {
        if (!element.classList.contains(RADIAL_MENU_EXCEPTION)) {
          if (!this.isInside(element)) {
            if (element.classList.contains('maplibregl-canvas')) {
              this.shouldToggle = false;
            }
            this.close();
          }
        }
      }
    };
    this.isInside = elementToCheck => {
      const svgElement = document.getElementById('radial-menu');
      return elementToCheck === svgElement || svgElement.contains(elementToCheck);
    };
    effect(() => {
      const map = this.mapService.map();
      if (map != null) {
        this.map = map;
        if (this.marker == null) {
          this.createNewMarker(this.map);
        }
        if (this.marker != null && this.svg != null) {
          this.svg.popupConnect(this.popupService, this.marker.getLngLat.bind(this.marker));
          this.svg.waypointConnect(this.popupService, this.marker.getLngLat.bind(this.marker), this.mapService.search.enableEditUserWaypoints);
        }
      }
    }, {
      injector
    });
    effect(() => {
      // Close the radial menu if it's open when disabling the radial menu
      if (this.mapService.config.interactions() !== MapInteractions.RadialMenu) {
        this.close();
      }
    }, {
      injector
    });
    window.addEventListener('click', this.onClick);
  }
  createNewMarker(map) {
    this.svg = new RadialSvg(map, this.mapService, this.airportService, this.stormCellService, this.terrainService, this.radialMenuMapService, this.radialMenuService);
    this.marker = new Marker$2({
      element: this.svg.container,
      draggable: true
    });
    this.marker.setLngLat([0.0, 0.0]); // this needs to be set or else adding it to the map causes problems
    this.onMarkerDrag = () => {
      this.moved(map);
    };
    this.onMarkerDragStart = () => {
      this.svg?.dragstart();
    };
    this.onMarkerDragEnd = () => {
      this.svg?.dragend();
    };
    this.marker.on('drag', this.onMarkerDrag);
    this.marker.on('dragend', this.onMarkerDragEnd);
    this.marker.on('dragstart', this.onMarkerDragStart);
    map.addMarker(MarkerPriority.RadialMenu, this.marker);
    map.registerMapClickHandler(e => {
      if (this.mapService.config.interactions() === MapInteractions.RadialMenu) {
        if (this.svg != null) {
          if (this.shouldToggle && !this.radialMenuService.getOverrideToggle()) {
            this.toggle(map, e.lngLat);
          } else {
            if (!this.shouldToggle) {
              this.shouldToggle = true;
            } else if (this.radialMenuService.getOverrideToggle()) {
              this.radialMenuService.setOverrideToggle(false);
            }
          }
        }
      }
    });
    this.onDragEnd = () => {
      this.hideIfOffScreen(map);
    };
    map.on('dragend', this.onDragEnd);
  }
  destroy(map) {
    if (this.onMarkerDrag != null) {
      this.marker?.off('drag', this.onMarkerDrag);
    }
    if (this.onMarkerDragEnd != null) {
      this.marker?.off('dragend', this.onMarkerDragEnd);
    }
    if (this.onMarkerDragStart != null) {
      this.marker?.off('dragstart', this.onMarkerDragStart);
    }
    if (this.onDragEnd != null) {
      map.off('dragend', this.onDragEnd);
    }
    this.marker?.remove();
    delete this.marker;
    this.svg?.destroy();
    delete this.svg;
  }
  /**
   * Get the center location (x, y) of the Radial Menu in Map canvas coordinates.
   * @returns A `PointLike` coordinate if available.
   */
  canvasPosition(map) {
    if (this.marker == null) {
      return undefined;
    }
    return map.project(this.marker.getLngLat().wrap());
  }
  /**
   * Forces the Radial Menu to close.
   */
  close() {
    if (this.svg != null) {
      if (this.svg.visible) {
        this.svg.toggle();
      }
    }
  }
  /**
   * Hides the Radial Menu if all of its buttons and their labels are completely off screen. This
   * behavior matches Garmin Pilot.
   */
  hideIfOffScreen(map) {
    if (this.svg?.visible !== true) {
      return;
    }
    // radial menu
    const canvas = map.getCanvas().getBoundingClientRect();
    if (canvas == null) {
      return;
    }
    const svg = this.svg.buttons_group.getBoundingClientRect();
    const m = {
      x: canvas.left + canvas.width / 2,
      y: canvas.top + canvas.height / 2
    };
    const c = {
      x: svg.left + SIZE / 2,
      y: svg.top + SIZE / 2
    };
    // distance from edge of canvas to center point of radial menu
    const d = {
      x: Math.abs(c.x - m.x) - canvas.width / 2,
      y: Math.abs(c.y - m.y) - canvas.height / 2
    };
    const radial_menu_offscreen = d.x > RADIUS_BTN_OUTER || d.y > RADIUS_BTN_OUTER || d.x > 0 && d.y > 0 && d.x * d.x + d.y * d.y > RADIUS_BTN_OUTER * RADIUS_BTN_OUTER;
    // labels
    const labels_offscreen = this.svg.dynamic_buttons.find(btn => {
      const label_rect = btn.label.getBoundingClientRect();
      if (label_rect.width === 0) {
        return false;
      }
      return label_rect.left <= canvas.right && label_rect.right >= canvas.left && label_rect.top <= canvas.bottom && label_rect.bottom >= canvas.top;
    }) === undefined;
    if (radial_menu_offscreen && labels_offscreen) {
      this.toggle(map);
    }
  }
  getMap() {
    return this.map;
  }
  /**
   * Toggles the Radial Menu on and off at the given location.
   * @param lngLat The location to set the Radial Menu at. If undefined then leave the Radial Menu
   * where it is.
   */
  toggle(map, lngLat) {
    if (this.svg != null && this.marker != null) {
      if (!this.svg.visible && lngLat !== undefined) {
        // if we're not currently visible, we're about to be, so get ready
        this.marker.setLngLat(lngLat);
        this.moved(map);
      }
      this.svg.toggle();
      if (this.svg.visible) {
        this.radialMenuService.setIsOpen(true);
      } else {
        this.radialMenuService.setIsOpen(false);
      }
    }
  }
  /**
   * Updates the search radius of the Radial Menu which changes based on the map zoom level.
   */
  updateSearchRadius(map) {
    if (this.marker != null) {
      const mkrLoc = this.marker.getLngLat().wrap();
      const radPro = map.project(mkrLoc);
      radPro.x += RADIUS_APERTURE; // move the projected x to the edge of the aperture
      const radLoc = map.unproject(radPro);
      if (radLoc != null) {
        this.searchRadius = mkrLoc.distanceTo(radLoc);
      }
    }
  }
  /**
   * Retrieves the latest set of features from within the Radial Menu aperture and updates the
   * Radial Menu buttons accordingly.
   */
  updateButtons() {
    if (this.svg != null && this.marker != null) {
      const layersToSearch = [];
      this.radialMenuMapService.getAllLayers().forEach(value => {
        value.forEach(layer => {
          layersToSearch.push(layer);
        });
      });
      const results = this.searchService.radialSearch(this.marker.getLngLat().wrap(), this.searchRadius, this.map, {
        layers: Array.from(layersToSearch)
      });
      this.svg.updateDynamicButtons(results);
      this.svg.updateStaticButtons(results);
    }
  }
  /**
   * Makes the necessary updates to the Radial Menu in response to moving it to a different location
   * on the map.
   */
  moved(map) {
    if (this.svg != null && this.marker != null) {
      this.svg.info_circle.update(this.marker.getLngLat().wrap(), map);
      this.updateSearchRadius(map);
      this.updateButtons();
      const aE = document.activeElement;
      aE.blur();
      this.radialMenuService.setOverrideToggle(false);
      this.radialMenuService.setOpenException(true);
    }
  }
}
let AvcMapRadialMenuService = /*#__PURE__*/(() => {
  class AvcMapRadialMenuService {
    constructor() {
      this.mapService = inject(MapService);
      this.injector = inject(Injector);
      // Booleans used to determine radial menu behavior
      this.overrideToggle = false;
      this.isOpen = false;
      this.openException = false;
    }
    createRadialMenu() {
      this.radialMenu = new RadialMenu(this.injector);
    }
    destroyRadialMenu() {
      if (this.radialMenu != null) {
        const map = this.mapService.map();
        if (map != null) {
          this.radialMenu.destroy(map);
          this.overrideToggle = false;
        }
      }
    }
    setOverrideToggle(toggle) {
      this.overrideToggle = toggle;
    }
    setIsOpen(open) {
      this.isOpen = open;
    }
    setOpenException(exception) {
      this.openException = exception;
    }
    getOverrideToggle() {
      return this.overrideToggle;
    }
    searchHideContent() {
      if (!this.openException && this.isOpen || !this.isOpen) {
        this.overrideToggle = true;
      } else {
        this.openException = false;
      }
    }
    searchShowContent() {
      if (this.isOpen) {
        this.openException = false;
      }
    }
    closeIfOpen() {
      if (this.isOpen) {
        if (this.radialMenu != undefined) {
          const map = this.radialMenu.getMap();
          if (map != undefined) {
            this.radialMenu.toggle(map);
          }
        }
      }
    }
    static {
      this.ɵfac = function AvcMapRadialMenuService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AvcMapRadialMenuService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AvcMapRadialMenuService,
        factory: AvcMapRadialMenuService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AvcMapRadialMenuService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const TIMEOUT_5_MIN = 5 * 1000 * 60;
const TIMEOUT_10_SEC = 10 * 1000;
let CapsService = /*#__PURE__*/(() => {
  class CapsService {
    constructor(http, mapService, config) {
      this.http = http;
      this.mapService = mapService;
      this.config = config;
      this.contentSetInterval = 0;
      this.metadataInterval = 0;
      this.snapshotInterval = 0;
      this.nextContentSetUpdateTime = 0;
      this.nextMetadataUpdateTime = 0;
      this.nextSnapshotUpdateTime = 0;
      this.metadataDescriptors = [];
      this.snapshotDescriptors = [];
      this.isLoggedIn = inject(OptionalAuthService).loggedIn;
      this.fetchContent$ = new Subject();
      this.fetchMetadata$ = new Subject();
      this.fetchSnapshots$ = new Subject();
      this.checkContentSetUpdate = () => {
        if (Date.now() > this.nextContentSetUpdateTime) {
          this.nextContentSetUpdateTime = Date.now() + TIMEOUT_10_SEC;
          this.fetchContent$.next();
        }
      };
      this.checkMetadataUpdate = () => {
        if (Date.now() > this.nextMetadataUpdateTime) {
          this.nextMetadataUpdateTime = Date.now() + TIMEOUT_5_MIN;
          this.fetchMetadata$.next(this.metadataDescriptors);
        }
      };
      this.checkSnapshotUpdate = () => {
        if (Date.now() > this.nextSnapshotUpdateTime) {
          this.nextSnapshotUpdateTime = Date.now() + TIMEOUT_5_MIN;
          this.fetchSnapshots$.next(this.snapshotDescriptors);
        }
      };
      if (this.config.capsUrl == null) {
        throw new Error('CapsService should not be instantiated without a defined capsUrl!');
      }
      this.contentSetInterval = window.setInterval(this.checkContentSetUpdate, 100);
      this.metadataInterval = window.setInterval(this.checkMetadataUpdate, 100);
      this.snapshotInterval = window.setInterval(this.checkSnapshotUpdate, 100);
      this.fetchContent$.pipe(switchMap(() => {
        // this uses switchMap instead of mergeMap so that future fetch requests will
        // cancel previous fetch requests
        const url = `${this.config.capsUrl}/contentSet`;
        return this.http.get(url, this.getHttpGetOptions()).pipe(tap(response => {
          this.processContentSetResponse(response);
        }), catchError(errorResponse => {
          console.error('failure to get CAPS ContentSet', errorResponse);
          this.nextContentSetUpdateTime = this.nextContentSetUpdateTime + TIMEOUT_10_SEC;
          return EMPTY;
        }));
      }), takeUntilDestroyed()).subscribe();
      this.fetchMetadata$.pipe(switchMap(descriptors => {
        this.metadataDescriptors = descriptors;
        // this uses switchMap instead of tap so that future fetch requests will
        // cancel previous fetch requests
        return of(descriptors);
      }), mergeAll(), mergeMap(descriptor => {
        const url = `${descriptor.urlBase}/metadata.json`;
        return this.http.get(url, this.getHttpGetOptions()).pipe(tap(response => {
          this.processMetadata(descriptor, response);
        }), catchError(errorResponse => {
          console.error('failure to get CAPS metadata', errorResponse);
          this.nextMetadataUpdateTime = this.nextMetadataUpdateTime + TIMEOUT_10_SEC;
          return EMPTY;
        }));
      }), takeUntilDestroyed()).subscribe();
      this.fetchSnapshots$.pipe(switchMap(descriptors => {
        this.snapshotDescriptors = descriptors;
        // this uses switchMap instead of tap so that future fetch requests will
        // cancel previous fetch requests
        return of(descriptors);
      }), mergeAll(), mergeMap(descriptor => {
        const url = `${descriptor.urlBase}/index.json`;
        return this.http.get(url, this.getHttpGetOptions()).pipe(tap(response => {
          this.processSnapshots(descriptor, response);
        }), catchError(errorResponse => {
          console.error('failure to get CAPS Snapshot', errorResponse);
          this.nextSnapshotUpdateTime = this.nextSnapshotUpdateTime + TIMEOUT_10_SEC;
          return EMPTY;
        }));
      }), takeUntilDestroyed()).subscribe();
      effect(() => {
        const _isLoggedIn = this.isLoggedIn(); // refetch when auth state changes
        this.fetchContent$.next();
      });
    }
    ngOnDestroy() {
      window.clearInterval(this.contentSetInterval);
      window.clearInterval(this.metadataInterval);
      window.clearInterval(this.snapshotInterval);
    }
    convertFormat(format) {
      switch (format) {
        case 'Image':
          return ContentFormat.Image;
        case 'Mvt':
          return ContentFormat.MVT;
        default:
          return ContentFormat.None;
      }
    }
    splitDescriptorsOnBounds(content) {
      const results = [];
      // MapLibre can't handle bounds that cross the international
      // date line so split them up into two separate domains.
      if (content.ContentDescriptor.Bounds.lonMax > 180.0) {
        results.push({
          type: content.ContentDescriptor.contentType,
          domain: `${content.ContentDescriptor.contentDomain}-E`,
          urlBase: content.urlBase,
          bounds: {
            ...content.ContentDescriptor.Bounds,
            lonMax: 180.0
          },
          format: this.convertFormat(content.ContentDescriptor.format)
        });
        results.push({
          type: content.ContentDescriptor.contentType,
          domain: `${content.ContentDescriptor.contentDomain}-W`,
          urlBase: content.urlBase,
          bounds: {
            ...content.ContentDescriptor.Bounds,
            lonMin: -180.0,
            lonMax: 180.0 - content.ContentDescriptor.Bounds.lonMax
          },
          format: this.convertFormat(content.ContentDescriptor.format)
        });
      } else {
        results.push({
          type: content.ContentDescriptor.contentType,
          domain: content.ContentDescriptor.contentDomain,
          urlBase: content.urlBase,
          bounds: content.ContentDescriptor.Bounds,
          format: this.convertFormat(content.ContentDescriptor.format)
        });
      }
      return results;
    }
    processContent(contentSet) {
      const metadataDescriptors = [];
      const snapshotDescriptors = [];
      for (const content of contentSet) {
        if (this.contentIsBlocked(content)) {
          continue;
        }
        const descriptors = this.splitDescriptorsOnBounds(content);
        for (const descriptor of descriptors) {
          if (this.hasMetadata(descriptor)) {
            metadataDescriptors.push(descriptor);
          }
          if (this.hasSnapshotContent(descriptor)) {
            snapshotDescriptors.push(descriptor);
          } else {
            this.updateContent(descriptor);
          }
        }
      }
      this.fetchMetadata$.next(metadataDescriptors);
      this.fetchSnapshots$.next(snapshotDescriptors);
    }
    processContentSetResponse(response) {
      const now = new Date();
      const expire = fromUnixTime(response.ContentSet.expireTime);
      if (expire <= now) {
        // This content set has already expired??? Try again in 10 seconds.
        this.nextContentSetUpdateTime = now.getTime() + TIMEOUT_10_SEC;
      } else {
        this.nextContentSetUpdateTime = expire.getTime() + TIMEOUT_10_SEC;
        this.processContent(response.ContentSet.Content);
      }
    }
    processMetadata(descriptor, metadata) {
      descriptor.metadata = metadata;
      this.updateContent(descriptor);
    }
    processSnapshots(descriptor, snapshots) {
      descriptor.snapshots = snapshots.Index;
      // logic elsewhere depends on the snapshots being in "newest to oldest" order
      descriptor.snapshots.sort((a, b) => b.time - a.time);
      this.updateContent(descriptor);
    }
    updateContent(descriptor) {
      for (const signal of this.getSignals(descriptor.type)) {
        signal.update(descriptors => {
          // Remove old descriptors matching the type and domain
          const results = descriptors.filter(x => x.type !== descriptor.type || x.domain !== descriptor.domain);
          results.push(descriptor);
          return results;
        });
      }
    }
    // eslint-disable-next-line complexity
    getSignals(type) {
      const info = this.mapService.info;
      switch (type) {
        case 'Airmet':
          return [info.overlay.airmetSigmet.setAirmetContent];
        case 'AviationTfr':
          return [info.overlay.tfr.setContent];
        case 'Fuel':
          return [info.overlay.fuelPrices.setContent];
        case 'Gairmet':
          return [info.overlay.airmetSigmet.setGraphicalAirmetContent];
        case 'Radar':
          return [info.overlay.radar.setBaseContent];
        case 'RadarComp':
          return [info.overlay.radar.setCompositeContent];
        case 'GroundSatellite':
          return [info.content.groundSatellite.setContent];
        case 'Icing':
          return [info.overlay.icing.setContent];
        case 'InternetTraffic':
          return [info.overlay.internetTraffic.setContent];
        case 'IRSat':
          return [info.overlay.clouds.setContent];
        case 'Lightning':
          return [info.overlay.lightning.setContent];
        case 'Metar':
          return [info.overlay.metar.setContent];
        case 'Notam':
          return [info.overlay.notam.setContent];
        case 'Obstacle':
          return [info.overlay.obstacle.setContent];
        case 'Pirep':
          return [info.overlay.pirep.setContent];
        case 'Safetaxi':
          return [info.content.safeTaxi.setContent];
        case 'Sigmet':
          return [info.overlay.airmetSigmet.setSigmetContent];
        case 'StormCell':
          return [info.overlay.stormCells.setContent];
        case 'Terrain':
          return [info.content.hillshade.setContent, info.content.topography.setContent];
        case 'TiledTemperature':
          return [info.overlay.tiledTemperature.setContent];
        case 'UVWind':
          return [info.overlay.windAnimation.setContent];
        case 'MvtCycleContent':
          return [info.content.airport.setContent, info.content.airspace.setContent, info.content.airway.setContent, info.content.baseMap.setContent, info.content.navaid.setContent];
        default:
          return [];
      }
    }
    hasMetadata(descriptor) {
      switch (descriptor.format) {
        case ContentFormat.MVT:
          // only MVT sources have metadata
          switch (descriptor.type) {
            // The MVT cycle content is an aggregator and doesn't have
            // its own metadata. If the information becomes useful at
            // some point, the metadata from the individual content
            // sources (e.g., airport, navaids, etc.) should be combined.
            case 'MvtCycleContent':
              return false;
            // Icing uses snapshots so doesn't have a seperate metadata file
            case 'Icing':
              return false;
            default:
              // only get metadata for stuff we're using
              return this.getSignals(descriptor.type).length > 0;
          }
        default:
          return false;
      }
    }
    hasSnapshotContent(descriptor) {
      switch (descriptor.type) {
        case 'Radar':
        case 'RadarComp':
        case 'IRSat':
        case 'TiledTemperature':
        case 'UVWind': // for wind animation
        case 'Icing':
          // only get snapshots for stuff we're using
          return this.getSignals(descriptor.type).length > 0;
        default:
          return false;
      }
    }
    contentIsBlocked(descriptor) {
      switch (descriptor.ContentDescriptor.contentType) {
        case 'Radar':
        case 'RadarComp':
        case 'IRSat':
          // These layers have a 'GLOBAL' domain and individual region domains.
          // We just want the 'GLOBAL' domain.
          return descriptor.ContentDescriptor.contentDomain !== 'GLOBAL';
        default:
          return false;
      }
    }
    getHttpGetOptions() {
      const options = {};
      const integrationTestKey = this.config.integrationTestKey;
      if (integrationTestKey != null && integrationTestKey.length > 0) {
        options.headers = {
          'X-Garmin-Integration-Test': integrationTestKey
        };
      } else {
        options.withCredentials = true;
      }
      return options;
    }
    static {
      this.ɵfac = function CapsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CapsService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(MapService), i0.ɵɵinject(AVC_MAPS_DISPLAY_CONFIG));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CapsService,
        factory: CapsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CapsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var PredefinedChartGroupIds = /*#__PURE__*/function (PredefinedChartGroupIds) {
  PredefinedChartGroupIds["HelicopterOverlays"] = "HELI_OVERLAYS";
  return PredefinedChartGroupIds;
}(PredefinedChartGroupIds || {});
const BOUNDS_MINIMUM_LATITUDE = -85.051129;
const BOUNDS_MAXIMUM_LATITUDE = 85.051129;
const BOUNDS_MINIMUM_LONGITUDE = -180;
const BOUNDS_MAXIMUM_LONGITUDE = 180;
const DEFAULT_BOUNDS = [BOUNDS_MINIMUM_LONGITUDE, BOUNDS_MINIMUM_LATITUDE, BOUNDS_MAXIMUM_LONGITUDE, BOUNDS_MAXIMUM_LATITUDE];
const boundsAreValid = bounds => {
  if (!Array.isArray(bounds) || bounds.length !== 4) {
    return false;
  }
  const [leftLng, lowerLat, rightLng, upperLat] = bounds;
  return leftLng >= BOUNDS_MINIMUM_LONGITUDE && leftLng <= BOUNDS_MAXIMUM_LONGITUDE && rightLng >= BOUNDS_MINIMUM_LONGITUDE && rightLng <= BOUNDS_MAXIMUM_LONGITUDE && leftLng <= rightLng && lowerLat >= BOUNDS_MINIMUM_LATITUDE && lowerLat <= BOUNDS_MAXIMUM_LATITUDE && upperLat >= BOUNDS_MINIMUM_LATITUDE && upperLat <= BOUNDS_MAXIMUM_LATITUDE && lowerLat <= upperLat;
};
const sanitizeBounds = bounds => {
  if (bounds != null && boundsAreValid(bounds)) {
    return bounds;
  } else {
    return DEFAULT_BOUNDS;
  }
};
const INVALID_ORDER = 9999;
const MINIMUM_ZOOM_TAC_CHART = ZoomLevel['0.5NM'];
const DEFAULT_GROUPS = new Map([['HELI', {
  id: PredefinedChartGroupIds.HelicopterOverlays,
  name: 'Helicopter Overlays',
  fullName: 'Helicopter Overlays',
  regions: [],
  chartType: 'HELI',
  abbreviation: 'HELI',
  order: 9000,
  children: []
}]]);
let ChartsInfoService = /*#__PURE__*/(() => {
  class ChartsInfoService {
    constructor() {
      this.config = inject(AVC_MAPS_DISPLAY_CONFIG);
      this.http = inject(HttpClient);
      this.mapService = inject(MapService);
      this.isLoggedIn = inject(OptionalAuthService).loggedIn;
      this.fetchContent$ = new Subject();
      if (this.config.chartsUrl == null) {
        throw new Error('ChartsInfoService should not be instantiated without a defined chartsUrl!');
      }
      this.fetchContent$.pipe(takeUntilDestroyed(), switchMap(() => {
        const url = `${this.config.chartsUrl}/contentSet`;
        return this.http.get(url, this.getHttpGetOptions()).pipe(tap(response => {
          this.processContentSetResponse(response);
        }), catchError(errorResponse => {
          console.error('failure to get charts ContentSet', errorResponse);
          return EMPTY;
        }));
      })).subscribe();
      effect(() => {
        const _isLoggedIn = this.isLoggedIn(); // refetch when auth state changes
        this.fetchContent$.next();
      });
    }
    getHttpGetOptions() {
      const options = {};
      const integrationTestKey = this.config.integrationTestKey;
      if (integrationTestKey != null && integrationTestKey.length > 0) {
        options.headers = {
          'X-Garmin-Integration-Test': integrationTestKey
        };
      } else {
        options.withCredentials = true;
      }
      return options;
    }
    createChart(url) {
      let minZoom = url.minZoom ?? 0;
      if ((url.name?.includes('TAC') ?? false) && minZoom < MINIMUM_ZOOM_TAC_CHART) {
        minZoom = MINIMUM_ZOOM_TAC_CHART;
      }
      return {
        url: url.url ?? 'UNKNOWN',
        name: url.name ?? 'UNKNOWN',
        bounds: sanitizeBounds(url.bounds),
        minZoom,
        maxZoom: url.maxZoom ?? 24
      };
    }
    createMetadata(metadata, children) {
      return {
        id: metadata.id ?? 'UNKNOWN',
        name: metadata.name ?? 'UNKNOWN',
        fullName: metadata.fullName ?? 'UNKNOWN',
        abbreviation: metadata.abbreviation ?? 'UNK',
        order: metadata.order ?? INVALID_ORDER,
        regions: cloneDeep(metadata.regions) ?? [],
        children
      };
    }
    createChartGroup(metadata, children) {
      return {
        id: metadata.id ?? 'UNKNOWN',
        name: metadata.name ?? 'UNKNOWN',
        fullName: metadata.fullName ?? 'UNKNOWN',
        abbreviation: metadata.fullName ?? 'UNK',
        order: metadata.order ?? INVALID_ORDER,
        chartType: metadata.chartType ?? 'UNKNOWN',
        children
      };
    }
    groupUngroupedCharts(response) {
      const results = [];
      const newGroups = new Map();
      for (const group of response) {
        if (group.children != null) {
          results.push(group);
        } else if (group.urls != null) {
          const key = group.chartType ?? 'UNKNOWN';
          const parentGroup = newGroups.get(key) ?? DEFAULT_GROUPS.get(key);
          if (parentGroup?.children != null && group.urls != null) {
            parentGroup.children.push(group);
            newGroups.set(key, parentGroup);
          }
        }
      }
      for (const [_key, newGroup] of newGroups) {
        results.push(newGroup);
      }
      return results;
    }
    /**
     * Most charts are grouped together in a 3-layer structure:
     *    ChartMetadata -> ChartMetadata[] -> ChartUrl[]
     *
     * Internally, these are named ChartGroup, ChartMetadata, and Chart.
     *
     * Some charts come through with just 2-layers:
     *    ChartMetadata -> ChartUrl[]
     *
     * These are grouped together into artificial ChartGroups based on
     * the type of chart.
     */
    processContentSetResponse(response) {
      const groupedResponse = this.groupUngroupedCharts(response);
      const groups = groupedResponse.map(chartGroup => {
        const metadata = chartGroup.children?.map(chart => {
          const charts = chart.urls?.map(url => this.createChart(url)) ?? [];
          return this.createMetadata(chart, charts);
        }) ?? [];
        metadata.sort((a, b) => a.order - b.order);
        return this.createChartGroup(chartGroup, metadata);
      });
      groups.sort((a, b) => a.order - b.order);
      this.mapService.info.charts.setGroups.set(groups);
    }
    static {
      this.ɵfac = function ChartsInfoService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ChartsInfoService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ChartsInfoService,
        factory: ChartsInfoService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ChartsInfoService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DebugService = /*#__PURE__*/(() => {
  class DebugService {
    constructor() {
      this.mapService = inject(MapService);
      this.updateShowTileBoundaries = () => {
        const map = this.mapService.map();
        const showTileBoundaries = this.mapService.debug.showTileBoundaries();
        if (map != null) {
          map.showTileBoundaries = showTileBoundaries;
          map.triggerRepaint();
        }
      };
      effect(this.updateShowTileBoundaries);
    }
    showFeatures(features) {
      const map = this.mapService.map();
      if (map != null) {
        const container = map.getCanvasContainer();
        if (this.featureWindow != null) {
          this.featureWindow.remove();
          this.featureWindow = undefined;
        }
        this.featureWindow = document.createElement('div');
        this.featureWindow.style.position = 'absolute';
        this.featureWindow.style.inset = '1rem';
        this.featureWindow.style.width = 'auto';
        this.featureWindow.style.height = 'auto';
        this.featureWindow.style.background = 'rgba(0 0 0 / 70%)';
        this.featureWindow.style.overflowY = 'auto';
        this.featureWindow.style.display = 'flex';
        this.featureWindow.style.flexDirection = 'column';
        this.featureWindow.addEventListener('click', event => {
          this.featureWindow?.remove();
          this.featureWindow = undefined;
          event.stopPropagation();
        });
        container.appendChild(this.featureWindow);
        for (const feature of features) {
          const table = document.createElement('div');
          table.style.display = 'grid';
          table.style.grid = 'auto-flow / 1fr 1fr 1fr';
          table.style.boxShadow = '0 2px 0 white';
          this.featureWindow.appendChild(table);
          const appendText = x => {
            const span = document.createElement('span');
            span.innerText = x;
            table.appendChild(span);
          };
          appendText('source');
          appendText(typeof feature.source);
          appendText(`${feature.source ?? 'N/A'}`);
          appendText('source-layer');
          appendText(typeof feature.sourceLayer);
          appendText(`${feature.sourceLayer ?? 'N/A'}`);
          appendText('layer');
          appendText(typeof feature.layer.id);
          appendText(`${feature.layer.id ?? 'N/A'}`);
          appendText('id');
          appendText(typeof feature.id);
          appendText(`${feature.id ?? 'N/A'}`);
          for (const [k, v] of Object.entries(feature.properties)) {
            appendText(`${k}`);
            appendText(typeof v);
            appendText(`${v}`);
          }
        }
      }
    }
    static {
      this.ɵfac = function DebugService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DebugService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DebugService,
        factory: DebugService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DebugService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const MINIMUM_SAMPLE_MS = 500;
class FramesPerSecondMonitor {
  constructor() {
    this.frames = 0;
    this.lastTick = 0;
    this.firstTick = 0;
    this.mapService = inject(MapService);
    this.onRender = () => {
      this.frames += 1;
      const tick = now();
      const duration = tick - this.lastTick;
      if (duration > MINIMUM_SAMPLE_MS) {
        const rawFps = this.frames / (duration / 1000);
        const fps = Math.round(rawFps * 10) / 10; // round to 1 decimal
        const time = Math.round(tick - this.firstTick); // round to 1ms
        this.mapService.debug.fpsReport.set({
          time,
          frames: this.frames,
          duration,
          fps
        });
        this.lastTick = tick;
        this.frames = 0;
      }
    };
  }
  start(map) {
    this.reset();
    const tick = now();
    this.firstTick = tick;
    this.lastTick = tick;
    this.oldRepaint = map.repaint;
    map.repaint = true;
    map.on('render', this.onRender);
  }
  stop(map) {
    map.off('render', this.onRender);
    if (this.oldRepaint != null) {
      map.repaint = this.oldRepaint;
      this.oldRepaint = undefined;
    }
    this.reset();
  }
  reset() {
    this.frames = 0;
    this.lastTick = now();
    this.firstTick = 0;
  }
}
const REPORT_PERIOD_MS = 100;
class SourceLoadReporter {
  constructor() {
    this.mapService = inject(MapService);
  }
  start(map) {
    this.reset(map);
    if (this.timer == null) {
      this.timer = window.setInterval(() => {
        this.publishReports(map);
      }, REPORT_PERIOD_MS);
    }
  }
  stop(map) {
    if (this.timer != null) {
      window.clearInterval(this.timer);
      this.timer = undefined;
    }
    this.reset(map);
  }
  publishReports(map) {
    const reports = new Map();
    const sourceIds = map.getSourceIds();
    for (const sourceId of sourceIds) {
      let report = reports.get(sourceId);
      if (report == null) {
        report = {
          isLoaded: true,
          sourceId,
          totalLoadTime: 0
        };
        reports.set(sourceId, report);
      }
      report.isLoaded = report.isLoaded && map.sourceLoadingMonitor.getSourceIsLoaded(sourceId);
      report.totalLoadTime = Math.round(Math.max(report.totalLoadTime, map.sourceLoadingMonitor.getTotalLoadTime(sourceId)));
    }
    this.mapService.debug.sourceLoadReports.set(Array.from(reports.values()));
  }
  reset(map) {
    map.sourceLoadingMonitor.reset();
    this.mapService.debug.sourceLoadReports.set([]);
  }
}
let PerformanceService = /*#__PURE__*/(() => {
  class PerformanceService {
    constructor() {
      this.mapService = inject(MapService);
      this.fpsMonitor = new FramesPerSecondMonitor();
      this.sourceLoadReporter = new SourceLoadReporter();
      this.currentMode = PerformanceMode.Off;
      this.updatePerformanceMode = () => {
        const mode = this.mapService.debug.performanceMode();
        const map = this.mapService.map();
        if (map != null) {
          // Disable old stuff
          switch (this.currentMode) {
            case PerformanceMode.FramesPerSecond:
              this.fpsMonitor.stop(map);
              break;
            case PerformanceMode.SourceLoading:
              this.sourceLoadReporter.stop(map);
              break;
            case PerformanceMode.Off:
            default:
            // nothing to do
          }
          // Enable new stuff
          switch (mode) {
            case PerformanceMode.FramesPerSecond:
              this.fpsMonitor.start(map);
              break;
            case PerformanceMode.SourceLoading:
              this.sourceLoadReporter.start(map);
              break;
            case PerformanceMode.Off:
            default:
            // nothing to do
          }
          this.currentMode = mode;
        } else {
          this.currentMode = PerformanceMode.Off;
        }
      };
      effect(this.updatePerformanceMode, {
        allowSignalWrites: true
      });
    }
    static {
      this.ɵfac = function PerformanceService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PerformanceService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PerformanceService,
        factory: PerformanceService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PerformanceService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const CLICK_SIZE = 10; // pixels
/**
 * This service is responsible for handling direct clicks on the map,
 * searching the area around the click for map features, and populating
 * the search API with those features.
 */
let MapClickInteractionService = /*#__PURE__*/(() => {
  class MapClickInteractionService {
    constructor() {
      this.debugService = inject(DebugService);
      this.mapService = inject(MapService);
      this.popupService = inject(PopupService);
      this.searchService = inject(SearchService);
      effect(() => this.registerClickHandler());
    }
    registerClickHandler() {
      const map = this.mapService.map();
      if (map != null) {
        map.registerMapClickHandler(event => this.clickHandler(map, event));
      }
    }
    clickHandler(map, event) {
      // remove all highlighted locations when the map is clicked
      this.mapService.highlight.location.set([]);
      switch (this.mapService.config.interactions()) {
        case MapInteractions.Click:
          this.popupService.showAllFeatures(this.getFeatures(map, event), event.lngLat);
          break;
        case MapInteractions.ClickDebug:
          this.debugService.showFeatures(this.getFeatures(map, event));
          break;
        case MapInteractions.None:
        default:
        // no action needed
      }
    }
    getFeatures(map, event) {
      const halfClickSize = CLICK_SIZE / 2;
      const bounds = new ML__default.LngLatBounds(event.target.unproject([event.point.x - halfClickSize, event.point.y - halfClickSize]), event.target.unproject([event.point.x + halfClickSize, event.point.y + halfClickSize]));
      return this.searchService.bboxCollisionSearch(bounds, map);
    }
    static {
      this.ɵfac = function MapClickInteractionService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapClickInteractionService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapClickInteractionService,
        factory: MapClickInteractionService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MapClickInteractionService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * This class exists to ensure that all map services are
 * instantiated when AvcMapsDisplayService is used
 */
let LifetimeService = /*#__PURE__*/(() => {
  class LifetimeService {
    constructor() {
      this.moduleConfig = inject(AVC_MAPS_DISPLAY_CONFIG);
      this.services = [];
      // services that don't require any HTTP requests
      this.services.push(inject(AvcMapRadialMenuService), inject(BackgroundService), inject(DebugService), inject(FlightService), inject(MapClickInteractionService), inject(PerformanceService), inject(PopupService), inject(ProcedureService), inject(RouteService), inject(WaypointService), inject(LocationHighlightService));
      // services that require the CAPS worker
      if (this.moduleConfig.capsUrl != null) {
        this.services.push(inject(CapsService), inject(AirmetService), inject(AirportService), inject(AirspacesService), inject(AirwayService), inject(BaseMapService), inject(BaseRadarService), inject(CloudsService), inject(CompositeRadarService), inject(FuelPricesService), inject(GraphicalAirmetService), inject(GroundSatelliteService), inject(HillshadeService), inject(IcingLayerService), inject(InternetTrafficHighlightService), inject(InternetTrafficService), inject(InternetTrafficTrackedBreadcrumbsService), inject(LightningService), inject(MetarService), inject(NavaidsService), inject(NotamRunwayService), inject(NotamService), inject(ObstaclesService), inject(OpenStreetMapService), inject(PirepService), inject(SafetaxiService), inject(SigmetService), inject(StormCellsService), inject(TfrService), inject(TiledTemperatureService), inject(TopographyService), inject(WindAnimationService));
      }
      // services that require the Charts worker
      if (this.moduleConfig.chartsUrl != null) {
        this.services.push(inject(ChartsService), inject(ChartsInfoService));
      }
    }
    static {
      this.ɵfac = function LifetimeService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LifetimeService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LifetimeService,
        factory: LifetimeService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LifetimeService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * This class is used to provide easier console access to certain
 * debugging and/or performance related features.
 *
 * CHANGE THIS FILE WITH CARE!
 *
 * Changing this file may affect QA workflows so any major changes
 * should be communicated with them.
 */
class ConsoleDebugger {
  constructor() {
    this.mapService = inject(MapService);
    this.overrideMapInteractionsWithClickDebug = false;
    this._updateFps = effect(() => {
      const fpsReport = this.mapService.debug.fpsReport();
      if (this.fpsText != null) {
        this.fpsText.innerText = fpsReport?.fps.toFixed(1) ?? '---';
      }
    });
    this.overrideMapInteractions = () => {
      const interactions = this.mapService.config.interactions();
      if (this.overrideMapInteractionsWithClickDebug) {
        if (interactions !== MapInteractions.ClickDebug) {
          this.overriddenMapInteractions = interactions;
          untracked(() => this.mapService.config.interactions.set(MapInteractions.ClickDebug));
        }
      }
    };
    this._updateInteractions = effect(this.overrideMapInteractions);
  }
  disableDebugClick() {
    this.overrideMapInteractionsWithClickDebug = false;
    if (this.overriddenMapInteractions != null) {
      this.mapService.config.interactions.set(this.overriddenMapInteractions);
    }
  }
  enableDebugClick() {
    this.overrideMapInteractionsWithClickDebug = true;
    this.overrideMapInteractions();
  }
  hideFps() {
    this.removeFpsElements();
    this.mapService.debug.performanceMode.set(PerformanceMode.Off);
  }
  hideTileBoundaries() {
    this.mapService.debug.showTileBoundaries.set(false);
  }
  showFps() {
    this.createFpsElement();
    this.mapService.debug.performanceMode.set(PerformanceMode.FramesPerSecond);
  }
  showTileBoundaries() {
    this.mapService.debug.showTileBoundaries.set(true);
  }
  createFpsElement() {
    this.removeFpsElements();
    const map = this.mapService.map();
    if (map == null) {
      return;
    }
    const container = map.getCanvasContainer();
    this.fpsContainer = document.createElement('div');
    this.fpsContainer.style.position = 'absolute';
    this.fpsContainer.style.width = '3rem';
    this.fpsContainer.style.height = '3rem';
    this.fpsContainer.style.top = '50%';
    this.fpsContainer.style.left = '1rem';
    this.fpsContainer.style.bottom = '50%';
    this.fpsContainer.style.background = 'rgba(0 0 0 / 70%)';
    this.fpsContainer.style.display = 'flex';
    this.fpsContainer.style.justifyContent = 'center';
    this.fpsContainer.style.alignItems = 'center';
    container.appendChild(this.fpsContainer);
    this.fpsText = document.createElement('span');
    this.fpsText.innerText = '---';
    this.fpsContainer.appendChild(this.fpsText);
  }
  removeFpsElements() {
    if (this.fpsContainer != null) {
      this.fpsContainer.remove();
      this.fpsContainer = undefined;
    }
    if (this.fpsText != null) {
      this.fpsText.remove();
      this.fpsText = undefined;
    }
  }
}
let AvcMapsDisplayService = /*#__PURE__*/(() => {
  class AvcMapsDisplayService {
    constructor() {
      // Inject all map services to ensure they're created
      this._serviceLifetime = inject(LifetimeService);
      // Inject API service for forwarding
      this.apiService = inject(MapService);
      // Forwarding external API properties
      this.animation = this.apiService.animation;
      this.config = this.apiService.config;
      this.data = this.apiService.data;
      this.debug = this.apiService.debug;
      this.highlight = this.apiService.highlight;
      this.icon = this.apiService.icon;
      this.info = this.apiService.info;
      this.interactions = this.apiService.interactions;
      this.preset = this.apiService.preset;
      this.search = this.apiService.search;
      this.settings = this.apiService.settings;
      this.state = this.apiService.state;
      this.theme = this.apiService.theme;
      // Set up the MapLibre tuning.
      // These figures were chosen by A-B testing with our performance test.
      // NOTE: ML.setWorkerCount is not touched as any change reduces MVT performance with
      //       very little gain to raster performance.
      ML__default.setMaxParallelImageRequests(128);
      // Create the MapLibre workers and other shared resources
      ML__default.prewarm();
      // Create the console debugger and attach it to the window
      window.garminMapDebugger = new ConsoleDebugger();
    }
    ngOnDestroy() {
      ML__default.clearPrewarmedResources();
    }
    // Forwarding external API functions
    attachMap(container) {
      return this.apiService.attachMap(container);
    }
    save() {
      return {
        config: this.config.save(),
        data: this.data.save(),
        state: this.state.save(),
        theme: this.theme.save()
      };
    }
    restore(state) {
      if (state.config != null) {
        this.config.restore(state.config);
      }
      if (state.data != null) {
        this.data.restore(state.data);
      }
      if (state.state != null) {
        this.state.restore(state.state);
      }
      if (state.theme != null) {
        this.theme.restore(state.theme);
      }
    }
    static {
      this.ɵfac = function AvcMapsDisplayService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AvcMapsDisplayService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AvcMapsDisplayService,
        factory: AvcMapsDisplayService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AvcMapsDisplayService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AvcMapsDisplayModule = /*#__PURE__*/(() => {
  class AvcMapsDisplayModule {
    static forRoot(config) {
      return {
        ngModule: AvcMapsDisplayModule,
        providers: [{
          provide: AVC_MAPS_DISPLAY_CONFIG,
          useValue: config
        }]
      };
    }
    static {
      this.ɵfac = function AvcMapsDisplayModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AvcMapsDisplayModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AvcMapsDisplayModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [provideHttpClient(withInterceptorsFromDi())]
      });
    }
  }
  return AvcMapsDisplayModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const DefaultAircraft = {
  path: '',
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none'
};
const DefaultIcon = {
  path: '',
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none'
};
const DefaultSmallIcon = {
  path: '',
  width: 16,
  height: 16,
  viewBox: '0 0 16 16',
  fill: 'none'
};
const DefaultInternetTrafficIcon = {
  path: '',
  width: 24,
  height: 24,
  viewBox: '0 0 155 155',
  fill: 'none'
};
const Icons = {
  NAVAID: {
    IFR: {
      DME: {
        ...DefaultAircraft,
        path: 'navaid/ifr/dme.svg#dme'
      },
      NDB: {
        ...DefaultAircraft,
        path: 'navaid/ifr/ndb.svg#ndb'
      },
      NDB_MARKER: {
        ...DefaultAircraft,
        path: 'navaid/ifr/ndb-marker.svg#ndb-marker'
      },
      TACAN: {
        ...DefaultAircraft,
        path: 'navaid/ifr/tacan.svg#tacan'
      },
      VOR: {
        ...DefaultAircraft,
        path: 'navaid/ifr/vor.svg#vor'
      },
      VOR_DME: {
        ...DefaultAircraft,
        path: 'navaid/ifr/vor-dme.svg#vor-dme'
      },
      VORTAC: {
        ...DefaultAircraft,
        path: 'navaid/ifr/vortac.svg#vortac'
      },
      WPT: {
        ...DefaultIcon,
        path: 'navaid/ifr/wpt.svg#wpt'
      },
      WPT_RNAV: {
        ...DefaultIcon,
        path: 'navaid/ifr/wpt-rnav.svg#wpt-rnav'
      },
      SMALL: {
        DME: {
          ...DefaultSmallIcon,
          path: 'navaid/ifr/small/dme_16.svg#dme'
        },
        NDB: {
          ...DefaultSmallIcon,
          path: 'navaid/ifr/small/ndb_16.svg#ndb'
        },
        NDB_MARKER: {
          ...DefaultSmallIcon,
          path: 'navaid/ifr/small/ndb-marker_16.svg#ndb-marker'
        },
        TACAN: {
          ...DefaultSmallIcon,
          path: 'navaid/ifr/small/tacan_16.svg#tacan'
        },
        VOR: {
          ...DefaultSmallIcon,
          path: 'navaid/ifr/small/vor_16.svg#vor'
        },
        VOR_DME: {
          ...DefaultSmallIcon,
          path: 'navaid/ifr/small/vor-dme_16.svg#vor-dme'
        },
        VORTAC: {
          ...DefaultSmallIcon,
          path: 'navaid/ifr/small/vortac_16.svg#vortac'
        }
      }
    },
    VFR: {
      DME: {
        ...DefaultAircraft,
        path: 'navaid/vfr/dme.svg#dme'
      },
      NDB: {
        ...DefaultAircraft,
        path: 'navaid/vfr/ndb.svg#ndb'
      },
      NDB_MARKER: {
        ...DefaultAircraft,
        path: 'navaid/vfr/ndb-marker.svg#ndb-marker'
      },
      TACAN: {
        ...DefaultAircraft,
        path: 'navaid/vfr/tacan.svg#tacan'
      },
      VOR: {
        ...DefaultAircraft,
        path: 'navaid/vfr/vor.svg#vor'
      },
      VOR_DME: {
        ...DefaultAircraft,
        path: 'navaid/vfr/vor-dme.svg#vor-dme'
      },
      VORTAC: {
        ...DefaultAircraft,
        path: 'navaid/vfr/vortac.svg#vortac'
      },
      WPT: {
        ...DefaultIcon,
        path: 'navaid/vfr/wpt.svg#wpt'
      },
      WPT_RNAV: {
        ...DefaultIcon,
        path: 'navaid/vfr/wpt-rnav.svg#wpt-rnav'
      },
      SMALL: {
        DME: {
          ...DefaultSmallIcon,
          path: 'navaid/vfr/small/dme_16.svg#dme'
        },
        NDB: {
          ...DefaultSmallIcon,
          path: 'navaid/vfr/small/ndb_16.svg#ndb'
        },
        NDB_MARKER: {
          ...DefaultSmallIcon,
          path: 'navaid/vfr/small/ndb-marker_16.svg#ndb-marker'
        },
        TACAN: {
          ...DefaultSmallIcon,
          path: 'navaid/vfr/small/tacan_16.svg#tacan'
        },
        VOR: {
          ...DefaultSmallIcon,
          path: 'navaid/vfr/small/vor_16.svg#vor'
        },
        VOR_DME: {
          ...DefaultSmallIcon,
          path: 'navaid/vfr/small/vor-dme_16.svg#vor-dme'
        },
        VORTAC: {
          ...DefaultSmallIcon,
          path: 'navaid/vfr/small/vortac_16.svg#vortac'
        }
      }
    }
  },
  NOTAM: {
    IFR: {
      DME_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/ifr/dme-caution.svg#dme'
      },
      DME_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/ifr/dme-upcoming.svg#dme'
      },
      DME_WARNING: {
        ...DefaultAircraft,
        path: 'notam/ifr/dme-warning.svg#dme'
      },
      NDB_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/ifr/ndb-caution.svg#ndb'
      },
      NDB_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/ifr/ndb-upcoming.svg#ndb'
      },
      NDB_WARNING: {
        ...DefaultAircraft,
        path: 'notam/ifr/ndb-warning.svg#ndb'
      },
      NDB_MARKER_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/ifr/ndb-marker-caution.svg#ndb-marker'
      },
      NDB_MARKER_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/ifr/ndb-marker-upcoming.svg#ndb-marker'
      },
      NDB_MARKER_WARNING: {
        ...DefaultAircraft,
        path: 'notam/ifr/ndb-marker-warning.svg#ndb-marker'
      },
      TACAN_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/ifr/tacan-caution.svg#tacan'
      },
      TACAN_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/ifr/tacan-upcoming.svg#tacan'
      },
      TACAN_WARNING: {
        ...DefaultAircraft,
        path: 'notam/ifr/tacan-warning.svg#tacan'
      },
      VOR_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/ifr/vor-caution.svg#vor'
      },
      VOR_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/ifr/vor-upcoming.svg#vor'
      },
      VOR_WARNING: {
        ...DefaultAircraft,
        path: 'notam/ifr/vor-warning.svg#vor'
      },
      VOR_DME_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/ifr/vor-dme-caution.svg#vor-dme'
      },
      VOR_DME_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/ifr/vor-dme-upcoming.svg#vor-dme'
      },
      VOR_DME_WARNING: {
        ...DefaultAircraft,
        path: 'notam/ifr/vor-dme-warning.svg#vor-dme'
      },
      VORTAC_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/ifr/vortac-caution.svg#vortac'
      },
      VORTAC_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/ifr/vortac-upcoming.svg#vortac'
      },
      VORTAC_WARNING: {
        ...DefaultAircraft,
        path: 'notam/ifr/vortac-warning.svg#vortac'
      },
      SMALL: {
        DME_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/dme-caution_16.svg#dme'
        },
        DME_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/dme-warning_16.svg#dme'
        },
        NDB_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/ndb-caution_16.svg#ndb'
        },
        NDB_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/ndb-warning_16.svg#ndb'
        },
        NDB_MARKER_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/ndb-marker-caution_16.svg#ndb-marker'
        },
        NDB_MARKER_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/ndb-marker-warning_16.svg#ndb-marker'
        },
        TACAN_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/tacan-caution_16.svg#tacan'
        },
        TACAN_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/tacan-warning_16.svg#tacan'
        },
        VOR_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/vor-caution_16.svg#vor'
        },
        VOR_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/vor-warning_16.svg#vor'
        },
        VOR_DME_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/vor-dme-caution_16.svg#vor-dme'
        },
        VOR_DME_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/vor-dme-warning_16.svg#vor-dme'
        },
        VORTAC_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/vortac-caution_16.svg#vortac'
        },
        VORTAC_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/ifr/small/vortac-warning_16.svg#vortac'
        }
      }
    },
    VFR: {
      DME_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/vfr/dme-caution.svg#dme'
      },
      DME_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/vfr/dme-upcoming.svg#dme'
      },
      DME_WARNING: {
        ...DefaultAircraft,
        path: 'notam/vfr/dme-warning.svg#dme'
      },
      NDB_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/vfr/ndb-caution.svg#ndb'
      },
      NDB_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/vfr/ndb-upcoming.svg#ndb'
      },
      NDB_WARNING: {
        ...DefaultAircraft,
        path: 'notam/vfr/ndb-warning.svg#ndb'
      },
      NDB_MARKER_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/vfr/ndb-marker-caution.svg#ndb-marker'
      },
      NDB_MARKER_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/vfr/ndb-marker-upcoming.svg#ndb-marker'
      },
      NDB_MARKER_WARNING: {
        ...DefaultAircraft,
        path: 'notam/vfr/ndb-marker-warning.svg#ndb-marker'
      },
      TACAN_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/vfr/tacan-caution.svg#tacan'
      },
      TACAN_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/vfr/tacan-upcoming.svg#tacan'
      },
      TACAN_WARNING: {
        ...DefaultAircraft,
        path: 'notam/vfr/tacan-warning.svg#tacan'
      },
      VOR_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/vfr/vor-caution.svg#vor'
      },
      VOR_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/vfr/vor-upcoming.svg#vor'
      },
      VOR_WARNING: {
        ...DefaultAircraft,
        path: 'notam/vfr/vor-warning.svg#vor'
      },
      VOR_DME_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/vfr/vor-dme-caution.svg#vor-dme'
      },
      VOR_DME_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/vfr/vor-dme-upcoming.svg#vor-dme'
      },
      VOR_DME_WARNING: {
        ...DefaultAircraft,
        path: 'notam/vfr/vor-dme-warning.svg#vor-dme'
      },
      VORTAC_CAUTION: {
        ...DefaultAircraft,
        path: 'notam/vfr/vortac-caution.svg#vortac'
      },
      VORTAC_UPCOMING: {
        ...DefaultAircraft,
        path: 'notam/vfr/vortac-upcoming.svg#vortac'
      },
      VORTAC_WARNING: {
        ...DefaultAircraft,
        path: 'notam/vfr/vortac-warning.svg#vortac'
      },
      SMALL: {
        DME_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/dme-caution_16.svg#dme'
        },
        DME_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/dme-warning_16.svg#dme'
        },
        NDB_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/ndb-caution_16.svg#ndb'
        },
        NDB_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/ndb-warning_16.svg#ndb'
        },
        NDB_MARKER_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/ndb-marker-caution_16.svg#ndb-marker'
        },
        NDB_MARKER_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/ndb-marker-warning_16.svg#ndb-marker'
        },
        TACAN_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/tacan-caution_16.svg#tacan'
        },
        TACAN_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/tacan-warning_16.svg#tacan'
        },
        VOR_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/vor-caution_16.svg#vor'
        },
        VOR_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/vor-warning_16.svg#vor'
        },
        VOR_DME_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/vor-dme-caution_16.svg#vor-dme'
        },
        VOR_DME_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/vor-dme-warning_16.svg#vor-dme'
        },
        VORTAC_CAUTION: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/vortac-caution_16.svg#vortac'
        },
        VORTAC_WARNING: {
          ...DefaultSmallIcon,
          path: 'notam/vfr/small/vortac-warning_16.svg#vortac'
        }
      }
    }
  },
  INTERNET_TRAFFIC: {
    internet_traffic_biplane: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/biplane.svg#biplane'
    },
    internet_traffic_business_jet: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/business-jet.svg#business-jet'
    },
    internet_traffic_commercial_dual_engine: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/commercial-dual-engine.svg#commercial-dual-engine'
    },
    internet_traffic_commercial_four_engine: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/commercial-four-engine.svg#commercial-four-engine'
    },
    internet_traffic_commercial_rear_engine: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/commercial-rear-engine.svg#commercial-rear-engine'
    },
    internet_traffic_fighter: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/fighter.svg#fighter'
    },
    internet_traffic_float_plane: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/float-plane.svg#float-plane'
    },
    internet_traffic_generic: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/generic.svg#generic'
    },
    internet_traffic_helicopter: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/helicopter.svg#helicopter'
    },
    internet_traffic_high_wind_twin: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/high-wind-twin.svg#high-wind-twin'
    },
    internet_traffic_high_wing: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/high-wing.svg#high-wing'
    },
    internet_traffic_low_wing_twin: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/low-wing-twin.svg#low-wing-twin'
    },
    internet_traffic_low_wing: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/low-wing.svg#low-wing'
    },
    internet_traffic_military_transport: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/military-transport.svg#military-transport'
    },
    internet_traffic_sr: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/sr.svg#sr'
    },
    internet_traffic_war_bird: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/warbird.svg#warbird'
    },
    internet_traffic_x_plane: {
      ...DefaultInternetTrafficIcon,
      path: 'internet-traffic/x-plane.svg#x-plane'
    }
  }
};
const createMapIcons = assetsPath => {
  const addAssetsPath = value => {
    if (value.path != null) {
      value.path = `${assetsPath}/${value.path}`;
    } else {
      for (const [_key, subValue] of Object.entries(value)) {
        addAssetsPath(subValue);
      }
    }
  };
  const clone = cloneDeep(Icons);
  if (assetsPath != null) {
    addAssetsPath(clone);
  }
  return clone;
};
var WeatherReportInfoType = /*#__PURE__*/function (WeatherReportInfoType) {
  WeatherReportInfoType["Metar"] = "METAR";
  return WeatherReportInfoType;
}(WeatherReportInfoType || {});
class MockMapAnimationHandler {
  constructor() {
    this.active = signal(false);
    this.date = signal(new Date(0));
    this.progress = signal(0);
    this.status = {
      airmet: signal(MapAnimationStatus.OK),
      clouds: signal(MapAnimationStatus.OK),
      graphicalAirmet: signal(MapAnimationStatus.OK),
      icing: signal(MapAnimationStatus.OK),
      lightning: signal(MapAnimationStatus.OK),
      radarBase: signal(MapAnimationStatus.OK),
      radarComposite: signal(MapAnimationStatus.OK),
      sigmet: signal(MapAnimationStatus.OK),
      stormCells: signal(MapAnimationStatus.OK),
      temps: signal(MapAnimationStatus.OK),
      winds: signal(MapAnimationStatus.OK)
    };
    this.time = signal(0);
  }
  configure(_options) {
    // no action
  }
  seek(_dateTime) {
    // no action
  }
  start() {
    // no action
  }
  stop() {
    // no action
  }
}
class MockMapIconHandler {
  get(_id) {
    return undefined;
  }
  getPath(_id) {
    return undefined;
  }
}
class MockNotamInfo {
  constructor() {
    this.available = signal(false);
    this.content = signal([]);
  }
  isMapDisplayable(_notam) {
    return true;
  }
}
class MockOverlayInfo {
  constructor() {
    this.airmetSigmet = new AirmetSigmetInfo();
    this.clouds = new CloudsInfo();
    this.fuelPrices = new FuelPricesInfo();
    this.icing = new IcingInfo();
    this.internetTraffic = new InternetTrafficInfo();
    this.lightning = new LightningInfo();
    this.metar = new MetarInfo();
    this.notam = new MockNotamInfo();
    this.obstacle = new ObstacleInfo();
    this.pirep = new PirepInfo();
    this.radar = new RadarInfo();
    this.stormCells = new StormCellsInfo();
    this.tfr = new TfrInfo();
    this.tiledTemperature = new TiledTemperatureInfo();
    this.windAnimation = new WindAnimationInfo();
  }
}
class MockMapInfo {
  constructor() {
    this.charts = new ChartsInfo();
    this.content = new ContentInfo();
    this.overlay = new MockOverlayInfo();
  }
  getMapImageData() {
    // not implemented
    return Promise.resolve(null);
  }
}
class MockMapInteractionHandler {
  constructor() {
    this.hoverEvents = new Subject();
    this.contextEvents = new Subject();
    this.clickEvents = new Subject();
    this.dragStartEvents = new Subject();
    this.dragEndEvents = new Subject();
    this.zoomStartEvents = new Subject();
    this.zoomEndEvents = new Subject();
  }
  dispatchEvent(_e) {
    // NO-OP.
  }
}
class MockMapPresetHandler {
  constructor(configHandler) {
    this.configHandler = configHandler;
  }
  apply(preset) {
    const config = this.get(preset);
    this.configHandler.restore(config);
  }
  get(preset) {
    let config;
    switch (preset) {
      case MapPreset.Blank:
        config = BlankPreset;
        break;
      case MapPreset.VFR:
        config = VfrPreset;
        break;
      case MapPreset.IFR:
        config = IfrPreset;
        break;
      case MapPreset.Satellite:
        config = SatellitePreset;
        break;
      case MapPreset.OpenStreetMap:
        config = OpenStreetMapPreset;
        break;
      default:
        config = VfrPreset;
        break;
    }
    return cloneDeep(config);
  }
  isAvailable(_preset) {
    return false;
  }
}
class MockMapSearch {
  constructor() {
    this.airmetsSigmets = signal([]);
    this.airports = signal([]);
    this.airspaces = signal([]);
    this.airways = signal([]);
    this.fuelPrices = signal([]);
    this.internetTraffic = signal([]);
    this.ndbs = signal([]);
    this.notams = signal([]);
    this.metars = signal([]);
    this.obstacles = signal([]);
    this.pireps = signal([]);
    this.stormCells = signal([]);
    this.tfrs = signal([]);
    this.vors = signal([]);
    this.weatherReports = signal([]);
    this.wpts = signal([]);
    this.enableEditUserWaypoints = signal(false);
    this.userWaypoints = signal([]);
  }
  changeTopElementCount(_val) {
    // do nothing
  }
  clearSearch() {
    this.airmetsSigmets.set([]);
    this.airports.set([]);
    this.airspaces.set([]);
    this.airways.set([]);
    this.fuelPrices.set([]);
    this.internetTraffic.set([]);
    this.ndbs.set([]);
    this.notams.set([]);
    this.metars.set([]);
    this.obstacles.set([]);
    this.pireps.set([]);
    this.stormCells.set([]);
    this.tfrs.set([]);
    this.vors.set([]);
    this.weatherReports.set([]);
    this.wpts.set([]);
    this.userWaypoints.set([]);
  }
}

/**
 * This is a mock of the real map service. The idea behind this is
 * leaving most of the API intact but disabling any internal mechanics
 * of the map service. No internal services will be created.
 *
 * Note this is for unit testing purposes only. Functionality is not
 * guaranteed to be complete or make any sense.
 *
 * To use this, add this to the testing module's providers:
 * `{ provide: AvcMapsDisplayService, useClass: MockAvcMapsDisplayService }`
 */
class MockAvcMapsDisplayService {
  constructor() {
    this.animation = new MockMapAnimationHandler();
    this.config = new MapConfigHandler();
    this.data = new MapDataHandler();
    this.highlight = new MapHighlight();
    this.icon = new MockMapIconHandler();
    this.info = new MockMapInfo();
    this.interactions = new MockMapInteractionHandler();
    this.preset = new MockMapPresetHandler(this.config);
    this.search = new MockMapSearch();
    this.settings = new MapSettingsHandler();
    this.state = new MapStateHandler();
    this.theme = new MapThemeHandler();
  }
  attachMap(_container) {
    // do nothing
  }
  restore(state) {
    if (state?.config != null) {
      this.config.restore(state.config);
    }
    if (state?.data != null) {
      this.data.restore(state.data);
    }
    if (state?.state != null) {
      this.state.restore(state.state);
    }
    if (state?.theme != null) {
      this.theme.restore(state.theme);
    }
  }
  save() {
    return {
      config: this.config.save(),
      data: this.data.save(),
      state: this.state.save(),
      theme: this.theme.save()
    };
  }
}

/*
 * Public API Surface of avc-maps-display
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AVC_MAPS_DISPLAY_CONFIG, AerodromeSubClass, AirspaceSubClass, AirwayLayerVisibility, AirwayType$1 as AirwayType, AllFeatureTypes, AltimeterUnits, ApronSubClass, ArtccAirspace, AvcMapRadialMenuService, AvcMapsDisplayModule, AvcMapsDisplayService, ContentFormat, CoordinatesFormat, CtrlAirspace, DistanceUnits, EmptyContent, EnrouteState, FeatureType$1 as FeatureType, FlightRule, FuelPriceType, IcingType, MapAnimationStatus, MapIcon, MapInteractions, MapOverlayLayer, MapPreset, MapThemePreset, MapWaypointType, MetarType, MockAvcMapsDisplayService, NavQCode, NavigationSubClass, NotamClass, NotamSource, NotamStatus, NotamWarningStatuses, ObstacleSubClass, PerformanceMode, PredefinedChartGroupIds, PressureUnits, RADIAL_MENU_EXCEPTION, RadarType, RunwayLengthUnits, RunwaySubClass, SuaAirspace, TaxiwaySubClass, TemperatureUnits, TimeFormat, TimeZone, TopographyState, VelocityUnits, VisibilityUnits, WeatherReportInfoType, WindSpeedUnits, WorldBounds, ZoomLevel, ZoomLevelKM, ZoomLevelSM, ZoomLevelTable, ZoomLevelTableKM, ZoomLevelTableSM, aircraftImages, aircraftMappingTable, createMapIcons };
